import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { supabase } from '../src/supabaseClient';
import {
    Task, Level, ResourceItem, BudgetFolder, BudgetItem, ApuItem, PhysicalUnit,
    Responsible, ProjectInfo, ProtocolConfig, WeeklyAllocation, WeeklyPlan, Constraint, LookaheadPlan,
    ProtocolState, ResourceProject, CNCCause, CNCGroup, ConstraintDefinition, ConstraintGroup, GanttBaseline, Profile, Activity,
    SubcontractType, ProjectProgressSnapshot, GlobalUser, Subcontractor, Contract, SubcontractorEvaluationConfig, CompanyConfig,
    WorkGroupTemplate, PresentationTemplate
} from '../src/types';
import { SubcontractorEvaluationModal } from '../src/components/payments/SubcontractorEvaluationModal';
import { WorkGroupManagerModal } from '../src/components/planning/WorkGroupManagerModal';
import PresentationEditor from '../src/components/presentations/PresentationEditor';
import { PresentationListModal } from '../src/components/presentations/PresentationListModal';
import { fetchTemplates } from '../src/services/presentationService';
import { AttendanceModal } from '../src/components/planning/AttendanceModal';
import { MeetingMinutesModal } from '../src/components/planning/MeetingMinutesModal';
import { PullToRefresh } from '../src/components/mobile/PullToRefresh';
import { KlSheet, KlDialog, KlMenu, KlMenuItem } from '../src/components/mobile/modal';
import { DEFAULT_EVALUATION_RANGES } from '../src/components/payments/EvaluationConfigModal';
import {
    calculateSCurve, calculateDelayDays, truncateProgress, roundToTwoDecimals, formatTwoDecimals,
    calculateProjectProgress, calculateTaskProgress, isProtocolProgressed,
    calculateCriticalPath, findTaskById, findTaskName, flattenTasks, findParentTask,
    calculateCriticalPathDelay, calculateCriticalPathDelayAtDate, calculateWeightedProgress, calculateProgrammedProgressAtDate,
    getLeafTasksOnly, isMatrixValueApplies, isTaskUnitAssigned, getTaskMatrixValue, getTaskUnitAccumulatedCompliance, cleanTaskName,
    getTaskGanttRowNumber, getAllocationCompliancePct, parseDependencyLink, findSavedProtocol, findSavedProtocolEntry
} from '../src/utils/planningUtils';
import { mergeWeeklyPlans } from '../src/utils/weeklyPlanMerge';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { GraphicProgressViewer } from '../src/components/graphic-progress/GraphicProgressViewer';
import { triggerAppNotification, resolveRecipientsForEvent } from '../src/services/notificationService';
import { MobileTitleBar } from '../src/components/mobile/MobileTitleBar';
import { MobileProtocolModal } from '../src/components/mobile/MobileProtocolModal';
import { BufferedTextArea } from '../src/components/ui/BufferedTextArea';

interface ContextMenuState {
    visible: boolean;
    x: number;
    y: number;
    taskId: string | null;
}

export interface AdditionalTaskRowDraft {
    id: string;
    levelId: string;
    unitId: string;
    additionalTaskName: string;
    responsibleId: string;
    days: Record<string, boolean>;
    selectedLevelIds?: string[];
    selectedUnitIds?: string[];
}

export const isRevisionStatus = (status?: string) => {
    if (!status) return false;
    const s = status.trim().toLowerCase();
    return s === 'en revisión' || s === 'en revision' || s === 'revisión' || s === 'revision' || s.includes('revis');
};

export const isPlanningStatus = (status?: string) => {
    if (!status) return false;
    const s = status.trim().toLowerCase();
    return s === 'en planificación' || s === 'en planificacion' || s === 'planificación' || s === 'planificacion' || s.includes('planificac');
};

export const isExecutionStatus = (status?: string) => {
    if (!status) return false;
    const s = status.trim().toLowerCase();
    return s === 'en ejecución' || s === 'en ejecucion' || s === 'ejecución' || s === 'ejecucion' || s.includes('ejecuc');
};

export const isClosedStatus = (status?: string) => {
    if (!status) return false;
    const s = status.trim().toLowerCase();
    return s === 'cerrada' || s === 'cerrado';
};

// --- Custom Components ---

const SearchableCNCSelect = ({ value, onChange, groups, disabled }: { value: string, onChange: (val: string) => void, groups: CNCGroup[], disabled?: boolean }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const dropdownRef = React.useRef<HTMLDivElement>(null);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Find selected cause name
    let selectedName = "Seleccionar Causa...";
    if (value) {
        for (const g of groups) {
            const found = g.causes.find(c => c.id === value);
            if (found) {
                selectedName = found.name;
                break;
            }
        }
    }

    // Filter groups and causes based on search
    const filteredGroups = groups.map(g => {
        const filteredCauses = g.causes.filter(c =>
            c.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        return { ...g, causes: filteredCauses };
    }).filter(g => g.causes.length > 0);

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className={`w-full text-xs border border-slate-300 dark:border-slate-600 rounded p-1.5 bg-white dark:bg-slate-800 outline-none focus:ring-1 focus:ring-amber-500 text-left flex items-center justify-between ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
                <span className="truncate">{selectedName}</span>
                <span className="material-symbols-outlined text-[14px]">expand_more</span>
            </button>

            {isOpen && (
                <div className="absolute z-[9999] top-full left-0 w-[280px] mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-xl max-h-60 overflow-hidden flex flex-col">
                    <div className="p-2 border-b border-slate-100 dark:border-slate-700">
                        <input
                            type="text"
                            className="w-full text-xs p-1.5 border border-slate-200 dark:border-slate-600 rounded bg-slate-50 dark:bg-slate-900 focus:outline-none focus:border-amber-500"
                            placeholder="Buscar causa..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            onClick={e => e.stopPropagation()}
                            autoFocus
                        />
                    </div>
                    <div className="overflow-y-auto flex-1 p-1">
                        <div
                            className="px-2 py-1.5 text-xs cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 italic"
                            onClick={() => { onChange(''); setIsOpen(false); }}
                        >
                            Seleccionar Causa...
                        </div>
                        {filteredGroups.length === 0 ? (
                            <div className="px-2 py-3 text-xs text-center text-slate-400">No hay coincidencias</div>
                        ) : (
                            filteredGroups.map(group => (
                                <div key={group.id} className="mb-1">
                                    <div className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50 dark:bg-slate-900/50">
                                        {group.name}
                                    </div>
                                    {group.causes.map(c => (
                                        <div
                                            key={c.id}
                                            className={`px-3 py-1.5 text-xs cursor-pointer truncate ${value === c.id ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400 font-medium' : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'}`}
                                            onClick={() => { onChange(c.id); setIsOpen(false); }}
                                            title={c.name}
                                        >
                                            {c.name}
                                        </div>
                                    ))}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Componente blindado para input de Justificación:
// Mantiene estado local mientras el usuario escribe para evitar que los re-renderizados globales de React
// hagan perder la posición del cursor en teclados móviles (Samsung Keyboard, Gboard, etc.) o inviertan texto.
const JustificationInput = ({
    value,
    onChange,
    disabled,
    className,
    placeholder = "Justificación (obligatoria)"
}: {
    value?: string;
    onChange: (val: string) => void;
    disabled?: boolean;
    className?: string;
    placeholder?: string;
}) => {
    const [localVal, setLocalVal] = useState(value || '');

    useEffect(() => {
        setLocalVal(value || '');
    }, [value]);

    return (
        <input
            type="text"
            dir="ltr"
            autoCapitalize="sentences"
            autoCorrect="on"
            spellCheck="true"
            placeholder={placeholder}
            disabled={disabled}
            className={className}
            value={localVal}
            onChange={(e) => {
                const newVal = e.target.value;
                setLocalVal(newVal);
                onChange(newVal);
            }}
            onBlur={() => {
                if (localVal !== (value || '')) {
                    onChange(localVal);
                }
            }}
        />
    );
};

export const PlanStatusDot: React.FC<{ status?: string; className?: string; title?: string }> = ({
    status,
    className = 'w-2.5 h-2.5',
    title
}) => {
    let colorClass = 'bg-slate-300';
    if (status === 'En Planificación') colorClass = 'bg-blue-500';
    else if (status === 'En Ejecución') colorClass = 'bg-emerald-500';
    else if (status === 'En Revisión') colorClass = 'bg-amber-400';
    else if (status === 'Cerrada') colorClass = 'bg-slate-400';

    return (
        <span
            className={`inline-block ${className} rounded-full ${colorClass} shrink-0 ring-2 ring-white dark:ring-slate-800 shadow-sm`}
            title={title || status}
        />
    );
};

// --- Helper Functions ---
const calculateDaysBetween = (startStr: string, endStr: string): number => {
    const s = new Date(startStr + "T12:00:00");
    const e = new Date(endStr + "T12:00:00");
    const diffTime = e.getTime() - s.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 ? diffDays + 1 : 0;
};

export interface DynamicDayColumn {
    dayLetter: string;
    dateNum: string;
    fullDate: string;
    key: string;
}

export const generateDynamicDayColumns = (startDateStr?: string, endDateStr?: string): DynamicDayColumn[] => {
    if (!startDateStr || !endDateStr) {
        const defaults = [
            { letter: 'L', num: '--' },
            { letter: 'M', num: '--' },
            { letter: 'X', num: '--' },
            { letter: 'J', num: '--' },
            { letter: 'V', num: '--' },
            { letter: 'S', num: '--' },
            { letter: 'D', num: '--' },
        ];
        return defaults.map((d, i) => ({
            dayLetter: d.letter,
            dateNum: d.num,
            fullDate: '',
            key: `default_${d.letter}_${i}`
        }));
    }

    const DAY_LETTERS = ['D', 'L', 'M', 'X', 'J', 'V', 'S']; // 0 = Domingo, 1 = Lunes, 2 = Martes, 3 = Miércoles ('X'), 4 = Jueves, 5 = Viernes, 6 = Sábado
    const result: DynamicDayColumn[] = [];

    const current = new Date(startDateStr + "T12:00:00");
    const end = new Date(endDateStr + "T12:00:00");

    let idx = 0;
    while (current <= end) {
        const dayIndex = current.getDay(); // 0-6
        const dayLetter = DAY_LETTERS[dayIndex];
        const dateNum = String(current.getDate()).padStart(2, '0');
        const fullDate = current.toISOString().split('T')[0];

        result.push({
            dayLetter,
            dateNum,
            fullDate,
            key: `${fullDate}_${dayLetter}_${idx++}`
        });

        current.setDate(current.getDate() + 1);
    }

    return result;
};

const generateTimelineWeeks = (startDate: Date, numWeeks: number) => {
    let current = new Date(startDate);
    const day = current.getDay();
    const diff = current.getDate() - day + (day === 0 ? -6 : 1);
    current.setDate(diff);

    const weeks = [];
    for (let i = 0; i < numWeeks; i++) {
        weeks.push(new Date(current));
        current.setDate(current.getDate() + 7);
    }
    return weeks;
};

const groupWeeksByMonth = (weeks: Date[]) => {
    const months: { label: string; colSpan: number }[] = [];
    let currentMonthLabel = "";
    let spanCount = 0;
    const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

    weeks.forEach((date, index) => {
        const label = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
        if (label !== currentMonthLabel) {
            if (currentMonthLabel !== "") {
                months.push({ label: currentMonthLabel, colSpan: spanCount });
            }
            currentMonthLabel = label;
            spanCount = 1;
        } else {
            spanCount++;
        }
        if (index === weeks.length - 1) {
            months.push({ label: currentMonthLabel, colSpan: spanCount });
        }
    });
    return months;
};

const getDatesForWeekday = (startDate: string, endDate: string, weekday: number) => {
    if (!startDate || !endDate) return [];
    const start = new Date(startDate + "T12:00:00");
    const end = new Date(endDate + "T12:00:00");
    const dates: string[] = [];

    let current = new Date(start);
    // Find first occurrence of the weekday
    while (current.getDay() !== (weekday % 7)) {
        current.setDate(current.getDate() + 1);
    }

    while (current <= end) {
        dates.push(current.toISOString().split('T')[0]);
        current.setDate(current.getDate() + 7);
    }
    return dates;
};

const getSyncDates = (projectStartDate: string, projectEndDate: string, syncDay: number, progressHistory: ProjectProgressSnapshot[]) => {
    // 1. Get already synchronized dates in chronological order
    const sortedHistory = [...progressHistory].sort((a, b) => a.date.localeCompare(b.date));
    const historyDates = sortedHistory.map(r => r.date);

    // 2. Generate remaining dates starting after the last synchronized date
    const remainingDates: string[] = [];
    const end = new Date(projectEndDate + "T12:00:00");

    let current: Date;
    if (historyDates.length > 0) {
        // Start the day after the last synchronized date
        const lastSyncDate = historyDates[historyDates.length - 1];
        current = new Date(lastSyncDate + "T12:00:00");
        current.setDate(current.getDate() + 1);
    } else {
        // Start from project start date
        current = new Date(projectStartDate + "T12:00:00");
    }

    // Find the next occurrence of syncDay
    while (current.getDay() !== (syncDay % 7)) {
        current.setDate(current.getDate() + 1);
    }

    // Generate dates up to the end of the project
    while (current <= end) {
        remainingDates.push(current.toISOString().split('T')[0]);
        current.setDate(current.getDate() + 7);
    }

    return [...historyDates, ...remainingDates];
};


const getPlannedProgressAtDate = (flatTasks: Task[], dateStr: string): number => {
    const totalProjectDuration = flatTasks.reduce((sum, t) => sum + (t.duration || 0), 0);
    const compareTime = new Date(dateStr + "T12:00:00").getTime();

    let plannedDurationAccum = 0;
    flatTasks.forEach(t => {
        if (!t.start) return;
        const tStart = new Date(t.start + "T12:00:00").getTime();
        const tEnd = new Date((t.end || t.start) + "T12:00:00").getTime();
        const tDur = t.duration || 0;

        if (compareTime >= tEnd) {
            plannedDurationAccum += tDur;
        } else if (compareTime > tStart) {
            const totalDays = Math.max(86400000, tEnd - tStart);
            const elapsedDays = compareTime - tStart;
            plannedDurationAccum += (elapsedDays / totalDays) * tDur;
        }
    });

    return totalProjectDuration > 0
        ? Number(((plannedDurationAccum / totalProjectDuration) * 100).toFixed(2))
        : 0;
};

const generateHistoricalWeeks = (projectStartDate: string, cutOffDate: string, tasks: Task[], syncDay: number = 5) => {
    let startStr = projectStartDate;
    if (tasks && tasks.length > 0) {
        const flat = flattenTasks(tasks).filter(t => t.type === 'task' && !t.isWeeklySubtask);
        let minDate = "";
        flat.forEach(t => {
            if (t.start && (!minDate || t.start < minDate)) {
                minDate = t.start;
            }
        });
        if (minDate) {
            startStr = minDate;
        }
    }
    if (!startStr) startStr = new Date().toISOString().split('T')[0];

    // Límite Superior Estricto: Fecha de Hoy (Semana Actual)
    const todayStr = new Date().toISOString().split('T')[0];
    const endStr = todayStr;

    const start = new Date(startStr + "T12:00:00");
    const cutOff = new Date(endStr + "T12:00:00");

    // First Friday or syncDay (default Friday = 5) on or after start
    const current = new Date(start);
    while (current.getDay() !== syncDay) {
        current.setDate(current.getDate() + 1);
    }

    const weeks: { index: number; label: string; syncDateStr: string; plannedProgress: number; plannedWeekly: number }[] = [];
    let idx = 1;
    let accumulatedPlanned = 0;

    // Flatten tasks to compute planned progress
    const flat = flattenTasks(tasks).filter(t => !t.isWeeklySubtask);

    const oneDay = 24 * 60 * 60 * 1000;
    while (current.getTime() - 6 * oneDay < cutOff.getTime()) {
        const syncDateStr = current.toISOString().split('T')[0];
        const plannedProgress = getPlannedProgressAtDate(flat, syncDateStr);
        const plannedWeekly = Math.max(0, plannedProgress - accumulatedPlanned);
        accumulatedPlanned = plannedProgress;

        // Find week start (6 days before syncDate)
        const weekStart = new Date(current);
        weekStart.setDate(weekStart.getDate() - 6);
        const startStrVal = weekStart.toISOString().split('T')[0];

        weeks.push({
            index: idx,
            label: `Semana ${idx}: ${startStrVal} al ${syncDateStr}`,
            syncDateStr,
            plannedProgress,
            plannedWeekly
        });

        idx++;
        current.setDate(current.getDate() + 7);
    }

    return weeks;
};

const autoDistributeProportionally = (totalImportedProgress: number, weeks: any[]) => {
    const N = weeks.length;
    if (N === 0) return [];

    const plannedGains = weeks.map((w, idx) => {
        if (idx === 0) return w.plannedProgress;
        return Math.max(0, w.plannedProgress - weeks[idx - 1].plannedProgress);
    });

    const sumPlannedGains = plannedGains.reduce((sum, val) => sum + val, 0);

    let distributedGains: number[] = [];
    if (sumPlannedGains > 0) {
        distributedGains = plannedGains.map(gain => {
            return Number(((gain / sumPlannedGains) * totalImportedProgress).toFixed(2));
        });
    } else {
        // Distribute equally
        distributedGains = weeks.map(() => Number((totalImportedProgress / N).toFixed(2)));
    }

    const sumOfFirstNMinus1 = distributedGains.slice(0, -1).reduce((sum, val) => sum + val, 0);
    const lastVal = Number((totalImportedProgress - sumOfFirstNMinus1).toFixed(2));
    distributedGains[N - 1] = Math.max(0, lastVal);

    return distributedGains;
};

const normalizeTaskName = (name: string): string => {
    let clean = name.replace(/^\[[\d\.]+\]\s*/, '');
    return clean
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, ""); // Remove accents
};

const getTaskUniqueKey = (t: Task): string => {
    return normalizeTaskName(t.name);
};

interface PlanningScreenProps {
    tasks: Task[];
    setTasks: React.Dispatch<React.SetStateAction<Task[]>>;
    responsibles: Responsible[];
    setResponsibles: React.Dispatch<React.SetStateAction<Responsible[]>>;
    globalUsers: GlobalUser[];
    setGlobalUsers: React.Dispatch<React.SetStateAction<GlobalUser[]>>;
    matrixData: Record<string, string>;
    setMatrixData?: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    units: PhysicalUnit[];
    levels: Level[];
    savedProtocols: Record<string, ProtocolState>;
    protocolConfigs: Record<string, ProtocolConfig>;
    weeklyPlans: WeeklyPlan[];
    setWeeklyPlans: React.Dispatch<React.SetStateAction<WeeklyPlan[]>>;
    lookaheadPlans: LookaheadPlan[];
    setLookaheadPlans: React.Dispatch<React.SetStateAction<LookaheadPlan[]>>;
    projectInfo: ProjectInfo;
    subcontractTypes: SubcontractType[];
    setSubcontractTypes: React.Dispatch<React.SetStateAction<SubcontractType[]>>;
    cncGroups: CNCGroup[];
    setCncGroups: React.Dispatch<React.SetStateAction<CNCGroup[]>>;
    constraintGroups: ConstraintGroup[];
    setConstraintGroups: React.Dispatch<React.SetStateAction<ConstraintGroup[]>>;
    globalCNCGroups: CNCGroup[];
    globalConstraintGroups: ConstraintGroup[];
    baselines: GanttBaseline[];
    setBaselines: React.Dispatch<React.SetStateAction<GanttBaseline[]>>;
    forceSyncTasks?: (overriddenState?: Task[]) => Promise<void>;
    profiles: Profile[];
    addActivity: (activity: Omit<Activity, 'id' | 'timestamp'>) => void;
    progressHistory: ProjectProgressSnapshot[];
    setProgressHistory: React.Dispatch<React.SetStateAction<ProjectProgressSnapshot[]>>;
    notifications: any[];
    setNotifications: React.Dispatch<React.SetStateAction<any[]>>;
    currentUser?: any;
    hasPermission: (module: string, action: 'view' | 'edit') => boolean;
    subcontractors?: Subcontractor[];
    contracts?: Contract[];
    companyConfig?: CompanyConfig;
    setSavedProtocols?: React.Dispatch<React.SetStateAction<Record<string, ProtocolState>>>;
    setProtocolConfigs?: React.Dispatch<React.SetStateAction<Record<string, ProtocolConfig>>>;
    workGroupTemplates?: WorkGroupTemplate[];
    setWorkGroupTemplates?: React.Dispatch<React.SetStateAction<WorkGroupTemplate[]>>;
}


// --- New Component: Dependency Arrows ---

const DependencyArrows: React.FC<{
    viewStartDate: Date;
    visibleTasks: Task[];
    zoomLevel: string;
    workingDays: boolean[];
    holidays: { date: string; name: string }[];
    timelineColumns: Date[];
    selectedGanttTaskId: string | null;
    taskPathInfo: {
        selectedId: string;
        selectedOrigId: number | string | undefined;
        predecessors: Set<number | string>;
        successors: Set<number | string>;
    } | null;
}> = ({
    viewStartDate,
    visibleTasks,
    zoomLevel,
    workingDays,
    holidays,
    timelineColumns,
    selectedGanttTaskId,
    taskPathInfo
}) => {
        const uniqueLinks = new Map<string, { fromOrigId: number | string, toOrigId: number | string, type: string }>();

        const getLocalDateStr = (date: Date): string => {
            const yyyy = date.getFullYear();
            const mm = String(date.getMonth() + 1).padStart(2, '0');
            const dd = String(date.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        };

        const getDayAfter = (dateStr: string): string => {
            const date = new Date(dateStr + "T12:00:00");
            date.setDate(date.getDate() + 1);
            return getLocalDateStr(date);
        };

        const isWorkingDay = (date: Date): boolean => {
            const dayOfWeek = date.getDay();
            if (!workingDays[dayOfWeek]) return false;
            const dateStr = getLocalDateStr(date);
            return !holidays.some(h => h.date === dateStr);
        };

        const getDateX = (dateStrOrObj: string | Date): number => {
            const date = typeof dateStrOrObj === 'string' ? new Date(dateStrOrObj + 'T12:00:00') : dateStrOrObj;
            if (isNaN(date.getTime())) return 0;

            if (zoomLevel === 'days') {
                const diffTime = date.getTime() - viewStartDate.getTime();
                const diffDays = diffTime / (1000 * 60 * 60 * 24);
                return diffDays * 30;
            } else if (zoomLevel === 'weeks') {
                const diffTime = date.getTime() - viewStartDate.getTime();
                const diffDays = diffTime / (1000 * 60 * 60 * 24);
                return (diffDays / 7) * 100;
            } else if (zoomLevel === 'months') {
                const monthDiff = (date.getFullYear() - viewStartDate.getFullYear()) * 12 + (date.getMonth() - viewStartDate.getMonth());
                const day = date.getDate();
                const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
                return monthDiff * 120 + ((day - 1) / daysInMonth) * 120;
            } else { // 'years'
                const yearDiff = date.getFullYear() - viewStartDate.getFullYear();
                const startOfYear = new Date(date.getFullYear(), 0, 1);
                const endOfYear = new Date(date.getFullYear() + 1, 0, 1);
                const pct = (date.getTime() - startOfYear.getTime()) / (endOfYear.getTime() - startOfYear.getTime());
                return yearDiff * 250 + pct * 250;
            }
        };

        // Collect and deduplicate links from both predecessors and successors of all visible tasks
        visibleTasks.forEach(task => {
            const taskIdOriginal = task.id_original_project;
            if (taskIdOriginal === undefined || task.isWeeklySubtask) return;

            // 1. Process predecessors (incoming links: B is predecessor of A)
            if (task.predecessors) {
                const allPreds: string[] = [];
                task.predecessors.forEach(p => {
                    if (typeof p === 'string' && p.includes(',')) {
                        p.split(',').forEach(s => {
                            const trimmed = s.trim();
                            if (trimmed) allPreds.push(trimmed);
                        });
                    } else if (typeof p === 'string') {
                        const trimmed = p.trim();
                        if (trimmed) allPreds.push(trimmed);
                    }
                });

                allPreds.forEach(predStr => {
                    const parsed = parseDependencyLink(predStr);
                    if (!parsed) return;
                    const predOriginalId = parsed.id;
                    const type = parsed.type;

                    const key = `${predOriginalId}-${taskIdOriginal}-${type}`;
                    uniqueLinks.set(key, { fromOrigId: predOriginalId, toOrigId: taskIdOriginal, type });
                });
            }

            // 2. Process successors (outgoing links: B is successor of A)
            if (task.successors) {
                const allSuccs: string[] = [];
                task.successors.forEach(s => {
                    if (typeof s === 'string' && s.includes(',')) {
                        s.split(',').forEach(sub => {
                            const trimmed = sub.trim();
                            if (trimmed) allSuccs.push(trimmed);
                        });
                    } else if (typeof s === 'string') {
                        const trimmed = s.trim();
                        if (trimmed) allSuccs.push(trimmed);
                    }
                });

                allSuccs.forEach(succStr => {
                    const parsed = parseDependencyLink(succStr);
                    if (!parsed) return;
                    const succOriginalId = parsed.id;
                    const type = parsed.type;

                    const key = `${taskIdOriginal}-${succOriginalId}-${type}`;
                    uniqueLinks.set(key, { fromOrigId: taskIdOriginal, toOrigId: succOriginalId, type });
                });
            }
        });

        const arrowElements: { element: React.ReactNode, isHighlighted: boolean }[] = [];

        // Draw the deduplicated links using start/end bar anchors
        uniqueLinks.forEach((link, key) => {
            const predTask = visibleTasks.find(t => t.id_original_project === link.fromOrigId);
            const task = visibleTasks.find(t => t.id_original_project === link.toOrigId);

            if (!predTask || !predTask.start || !task || !task.start) return;

            const predEndVal = predTask.end || predTask.start;
            const taskEndVal = task.end || task.start;

            // Anchors (Left = Start, Right = End)
            const taskStart = getDateX(task.start);
            const taskEnd = getDateX(getDayAfter(taskEndVal));
            const predStart = getDateX(predTask.start);
            const predEnd = getDateX(getDayAfter(predEndVal));

            const fromIdx = visibleTasks.findIndex(t => t.id_original_project === link.fromOrigId);
            const toIdx = visibleTasks.findIndex(t => t.id_original_project === link.toOrigId);

            if (fromIdx === -1 || toIdx === -1) return;

            const y1 = fromIdx * 44 + 18;
            const y2 = toIdx * 44 + 18;

            let x1 = predEnd;
            let x2 = taskStart;
            let isEntryFromLeft = true; // enters start of task bar (FS, SS)
            let d = "";
            const yMid = y1 + (y2 - y1) / 2;

            if (link.type === "FS") {
                x1 = predEnd;
                x2 = taskStart;
                isEntryFromLeft = true;

                if (x2 >= x1 + 10) {
                    const midX = x1 + 10;
                    d = `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2} ${y2}`;
                } else {
                    d = `M ${x1} ${y1} L ${x1 + 10} ${y1} L ${x1 + 10} ${yMid} L ${x2 - 10} ${yMid} L ${x2 - 10} ${y2} L ${x2} ${y2}`;
                }
            } else if (link.type === "SS") {
                x1 = predStart;
                x2 = taskStart;
                isEntryFromLeft = true;

                if (x2 >= x1) {
                    const midX = x1 - 10;
                    d = `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2} ${y2}`;
                } else {
                    const midX = x1 - 10;
                    d = `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${yMid} L ${x2 - 10} ${yMid} L ${x2 - 10} ${y2} L ${x2} ${y2}`;
                }
            } else if (link.type === "FF") {
                x1 = predEnd;
                x2 = taskEnd;
                isEntryFromLeft = false;

                const midX = Math.max(x1, x2) + 10;
                d = `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2} ${y2}`;
            } else if (link.type === "SF") {
                x1 = predStart;
                x2 = taskEnd;
                isEntryFromLeft = false;

                if (x2 > x1 - 10) {
                    d = `M ${x1} ${y1} L ${x1 - 10} ${y1} L ${x1 - 10} ${yMid} L ${x2 + 10} ${yMid} L ${x2 + 10} ${y2} L ${x2} ${y2}`;
                } else {
                    const midX = x1 - 10;
                    d = `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2} ${y2}`;
                }
            }

            // Highlight paths (Direct relations only)
            const isPredecessorLink = !!(taskPathInfo &&
                link.toOrigId === taskPathInfo.selectedOrigId &&
                taskPathInfo.predecessors.has(link.fromOrigId)
            );

            const isSuccessorLink = !!(taskPathInfo &&
                link.fromOrigId === taskPathInfo.selectedOrigId &&
                taskPathInfo.successors.has(link.toOrigId)
            );

            const isCriticalLink = !!(predTask.isCritical && task.isCritical);

            let color = "#475569";
            let strokeWidth = "1.5";
            let opacityClass = "opacity-60";

            if (selectedGanttTaskId) {
                if (isPredecessorLink) {
                    color = "#eab308"; // Amarillo brillante
                    strokeWidth = "2.5";
                    opacityClass = "opacity-100";
                } else if (isSuccessorLink) {
                    color = "#d946ef"; // Rosado/Fucsia
                    strokeWidth = "2.5";
                    opacityClass = "opacity-100";
                } else {
                    opacityClass = "opacity-25"; // Atenuado
                }
            } else {
                if (isCriticalLink) {
                    color = "#ef4444"; // Rojo brillante
                    strokeWidth = "2";
                    opacityClass = "opacity-90";
                }
            }

            const arrowHeadPath = isEntryFromLeft
                ? `M ${x2 - 4} ${y2 - 3} L ${x2} ${y2} L ${x2 - 4} ${y2 + 3}`  // pointing right (into start)
                : `M ${x2 + 4} ${y2 - 3} L ${x2} ${y2} L ${x2 + 4} ${y2 + 3}`; // pointing left (into end)

            arrowElements.push({
                isHighlighted: !!((selectedGanttTaskId && (isPredecessorLink || isSuccessorLink)) || (!selectedGanttTaskId && isCriticalLink)),
                element: (
                    <g key={key} className="group/arrow">
                        <path
                            d={d}
                            fill="none"
                            stroke={color}
                            strokeWidth={strokeWidth}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className={`${opacityClass} group-hover/arrow:opacity-100 transition-all`}
                        />
                        <path
                            d={arrowHeadPath}
                            fill="none"
                            stroke={color}
                            strokeWidth={strokeWidth}
                            className={`${opacityClass} group-hover/arrow:opacity-100 transition-all`}
                        />
                    </g>
                )
            });
        });

        // Separate normal and highlighted arrows so highlighted ones are drawn last (on top)
        const normalArrows = arrowElements.filter(a => !a.isHighlighted).map(a => a.element);
        const highlightedArrows = arrowElements.filter(a => a.isHighlighted).map(a => a.element);
        const lines = [...normalArrows, ...highlightedArrows];

        const totalHeight = visibleTasks.length * 44;
        const bgElements: React.ReactNode[] = [];

        // Draw background shading and grid lines
        if (zoomLevel === 'days') {
            timelineColumns.forEach((date) => {
                const x = getDateX(date);
                if (!isWorkingDay(date)) {
                    bgElements.push(
                        <rect
                            key={`shade-${date.getTime()}`}
                            x={x}
                            y={0}
                            width={30}
                            height={totalHeight}
                            fill="currentColor"
                            className="text-slate-200/90 dark:text-slate-800/75"
                        />
                    );
                }
                bgElements.push(
                    <line
                        key={`line-${date.getTime()}`}
                        x1={x + 30}
                        y1={0}
                        x2={x + 30}
                        y2={totalHeight}
                        stroke="currentColor"
                        className="text-slate-200/60 dark:text-slate-850/30"
                        strokeWidth="1"
                    />
                );
            });
        } else if (zoomLevel === 'weeks') {
            if (timelineColumns.length > 0) {
                const startLimit = new Date(timelineColumns[0]);
                const endLimit = new Date(timelineColumns[timelineColumns.length - 1]);
                endLimit.setDate(endLimit.getDate() + 7);

                let curr = new Date(startLimit);
                const dayWidth = 100 / 7;
                while (curr < endLimit) {
                    if (!isWorkingDay(curr)) {
                        const x = getDateX(curr);
                        bgElements.push(
                            <rect
                                key={`shade-${curr.getTime()}`}
                                x={x}
                                y={0}
                                width={dayWidth}
                                height={totalHeight}
                                fill="currentColor"
                                className="text-slate-200/90 dark:text-slate-800/75"
                            />
                        );
                    }
                    curr.setDate(curr.getDate() + 1);
                }
            }
            timelineColumns.forEach((date) => {
                const x = getDateX(date);
                bgElements.push(
                    <line
                        key={`line-${date.getTime()}`}
                        x1={x + 100}
                        y1={0}
                        x2={x + 100}
                        y2={totalHeight}
                        stroke="currentColor"
                        className="text-slate-200/60 dark:text-slate-850/30"
                        strokeWidth="1"
                    />
                );
            });
        } else if (zoomLevel === 'months') {
            timelineColumns.forEach((date) => {
                const x = getDateX(date);
                bgElements.push(
                    <line
                        key={`line-${date.getTime()}`}
                        x1={x + 120}
                        y1={0}
                        x2={x + 120}
                        y2={totalHeight}
                        stroke="currentColor"
                        className="text-slate-200/60 dark:text-slate-850/30"
                        strokeWidth="1"
                    />
                );
            });
        } else { // 'years'
            timelineColumns.forEach((date) => {
                const x = getDateX(date);
                bgElements.push(
                    <line
                        key={`line-${date.getTime()}`}
                        x1={x + 250}
                        y1={0}
                        x2={x + 250}
                        y2={totalHeight}
                        stroke="currentColor"
                        className="text-slate-200/60 dark:text-slate-850/30"
                        strokeWidth="1"
                    />
                );
            });
        }

        return (
            <svg className="absolute top-0 left-0 w-full h-full pointer-events-none z-0 overflow-visible">
                {bgElements}
                {lines}
            </svg>
        );
    };

interface PredecessorInputCellProps {
    task: Task;
    onUpdate: (taskId: string, newPreds: string[]) => void;
}

const PredecessorInputCell: React.FC<PredecessorInputCellProps> = ({ task, onUpdate }) => {
    const [val, setVal] = useState(task.predecessors?.join(", ") || "");

    useEffect(() => {
        setVal(task.predecessors?.join(", ") || "");
    }, [task.predecessors]);

    const handleConfirm = () => {
        const rawParts = val.split(",").map(s => s.trim()).filter(s => s !== "");

        const cleaned: string[] = [];
        rawParts.forEach(part => {
            const parsed = parseDependencyLink(part);
            if (parsed) {
                cleaned.push(parsed.normalized);
            }
        });

        onUpdate(task.id, cleaned);
    };

    return (
        <input
            type="text"
            className="w-full text-xs border border-slate-300 rounded p-1 dark:bg-slate-800 dark:border-slate-600"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onBlur={handleConfirm}
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    handleConfirm();
                }
            }}
            placeholder="ej. 1FC+2, 2CC"
        />
    );
};

interface SuccessorInputCellProps {
    task: Task;
    onUpdate: (taskId: string, newSuccs: string[]) => void;
}

const SuccessorInputCell: React.FC<SuccessorInputCellProps> = ({ task, onUpdate }) => {
    const [val, setVal] = useState(task.successors?.join(", ") || "");

    useEffect(() => {
        setVal(task.successors?.join(", ") || "");
    }, [task.successors]);

    const handleConfirm = () => {
        const rawParts = val.split(",").map(s => s.trim()).filter(s => s !== "");

        const cleaned: string[] = [];
        rawParts.forEach(part => {
            const parsed = parseDependencyLink(part);
            if (parsed) {
                cleaned.push(parsed.normalized);
            }
        });

        onUpdate(task.id, cleaned);
    };

    return (
        <input
            type="text"
            className="w-full text-xs border border-slate-300 rounded p-1 dark:bg-slate-800 dark:border-slate-600"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onBlur={handleConfirm}
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    handleConfirm();
                }
            }}
            placeholder="ej. 3FC+2, 4FF"
        />
    );
};

interface PlanTaskRowProps {
    alloc: WeeklyAllocation & {
        selected: boolean;
        isRestricted: boolean;
        isPreviouslyExecuted?: boolean;
        isReprogrammed?: boolean;
        isSuggestedByGantt?: boolean;
        accumulatedCompliance?: number;
        remainingBalance?: number;
    };
    idx: number;
    unitName: string;
    allocLevelName: string;
    taskName: string;
    dynamicDayColumns: DynamicDayColumn[];
    responsibleOptions: React.ReactNode;
    onToggleRow: (idx: number) => void;
    onToggleDay: (idx: number, dayKey: string) => void;
    onUpdateField: (idx: number, field: keyof WeeklyAllocation, value: any) => void;
}

const PlanTaskRow = React.memo<PlanTaskRowProps>(({
    alloc,
    idx,
    unitName,
    allocLevelName,
    taskName,
    dynamicDayColumns,
    responsibleOptions,
    onToggleRow,
    onToggleDay,
    onUpdateField
}) => {
    const accumulatedCompliance = alloc.accumulatedCompliance ?? 0;
    const maxComp = alloc.remainingBalance !== undefined ? alloc.remainingBalance : Math.max(0, 100 - accumulatedCompliance);

    return (
        <tr className={`hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors ${(!alloc.selected && !alloc.isRestricted && !alloc.isPreviouslyExecuted && !alloc.isSuggestedByGantt) ? 'opacity-60' : ''} ${alloc.isRestricted ? 'bg-red-50/30 dark:bg-red-900/10' : ''} ${alloc.isPreviouslyExecuted ? 'bg-green-50/20 dark:bg-green-900/10' : ''} ${(alloc.isSuggestedByGantt && !alloc.isPreviouslyExecuted) ? 'bg-amber-50/80 dark:bg-amber-950/20' : ''}`}>
            <td className={`px-4 py-3 text-center ${(alloc.isSuggestedByGantt && !alloc.isPreviouslyExecuted) ? 'border-l-4 border-amber-400' : ''}`}>
                {alloc.isPreviouslyExecuted ? (
                    <div className="flex items-center justify-center cursor-not-allowed" title="Tarea Ejecutada">
                        <span className="material-symbols-outlined text-green-500 text-[20px]">check_circle</span>
                    </div>
                ) : alloc.isRestricted ? (
                    <div className="flex items-center justify-center cursor-help" title="Cuenta con restricciones Activas">
                        <span className="material-symbols-outlined text-amber-500 text-[20px] animate-pulse">warning</span>
                    </div>
                ) : (
                    <input
                        type="checkbox"
                        checked={alloc.selected}
                        onChange={() => onToggleRow(idx)}
                        className="rounded border-slate-300 text-primary focus:ring-primary size-4 cursor-pointer"
                    />
                )}
            </td>
            <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{allocLevelName}</td>
            <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300 font-medium">{unitName}</td>
            <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                <div className="flex items-center gap-2">
                    {taskName}
                    {alloc.isReprogrammed && !alloc.isPreviouslyExecuted && (
                        <span className="material-symbols-outlined text-[16px] text-blue-500" title="Reprogramable">update</span>
                    )}
                    {alloc.isSuggestedByGantt && !alloc.isPreviouslyExecuted && (
                        <span className="text-[10px] uppercase font-bold text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400 px-2 py-0.5 rounded border border-amber-200 dark:border-amber-800">Sugerido por Gantt</span>
                    )}
                    {alloc.isPreviouslyExecuted && (
                        <span className="text-[10px] uppercase font-bold text-green-600 bg-green-100 px-2 py-0.5 rounded border border-green-200">Ejecutado</span>
                    )}
                </div>
            </td>
            <td className="px-4 py-3">
                <select
                    className="w-full text-xs border border-slate-300 rounded p-1.5 bg-white dark:bg-slate-800 dark:border-slate-600 outline-none focus:border-primary disabled:bg-slate-100 dark:disabled:bg-slate-900 disabled:cursor-not-allowed"
                    value={alloc.responsibleId}
                    onChange={(e) => onUpdateField(idx, 'responsibleId', e.target.value)}
                    disabled={!alloc.selected || alloc.isPreviouslyExecuted}
                >
                    <option value="">Seleccionar...</option>
                    {responsibleOptions}
                </select>
            </td>
            <td className="px-4 py-3 text-center">
                <input
                    type="number"
                    min="0"
                    max={maxComp}
                    step="0.01"
                    disabled={!alloc.selected || alloc.isPreviouslyExecuted}
                    className="w-20 text-center text-xs p-1.5 border border-slate-300 rounded dark:bg-slate-800 dark:border-slate-600 dark:text-white disabled:opacity-50 font-bold"
                    value={roundToTwoDecimals(Math.min(alloc.percentComprometido ?? maxComp, maxComp))}
                    onChange={(e) => {
                        const rawVal = parseFloat(e.target.value) || 0;
                        const val = roundToTwoDecimals(rawVal);
                        if (val > maxComp) {
                            alert(`El porcentaje comprometido no puede superar el saldo restante (${maxComp}%).`);
                            onUpdateField(idx, 'percentComprometido', roundToTwoDecimals(maxComp));
                        } else if (val < 0) {
                            onUpdateField(idx, 'percentComprometido', 0);
                        } else {
                            onUpdateField(idx, 'percentComprometido', val);
                        }
                    }}
                />
            </td>
            <td className="px-4 py-3 text-center text-sm font-semibold text-slate-500 dark:text-slate-400">
                {`${accumulatedCompliance}%`}
            </td>
            {dynamicDayColumns.map(col => {
                const dayKey = col.dayLetter;
                const isChecked = !!alloc.days[dayKey];
                const isDisabled = !alloc.selected || alloc.isPreviouslyExecuted;

                return (
                    <td
                        key={col.key}
                        className={`px-1 py-2.5 text-center select-none ${!isDisabled ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                        onClick={() => !isDisabled && onToggleDay(idx, dayKey)}
                        title={`${col.fullDate || dayKey} - ${unitName}`}
                    >
                        <button
                            type="button"
                            disabled={isDisabled}
                            className={`w-8 h-9 rounded-lg mx-auto flex flex-col items-center justify-center font-black text-xs transition-all ${isChecked
                                    ? (alloc.isPreviouslyExecuted
                                        ? 'bg-green-500 text-white shadow-sm border border-green-600 opacity-60'
                                        : 'bg-purple-600 text-white shadow-md shadow-purple-500/20 scale-105')
                                    : 'bg-white dark:bg-slate-800 text-slate-400 dark:text-slate-550 border border-slate-200 dark:border-slate-700 hover:border-purple-400 disabled:hover:border-slate-200'
                                } ${alloc.isPreviouslyExecuted ? 'opacity-60' : ''}`}
                        >
                            <span className="text-[11px] leading-none">{col.dayLetter}</span>
                            <span className="text-[9px] font-medium leading-none opacity-80 mt-0.5">{col.dateNum}</span>
                        </button>
                    </td>
                );
            })}
        </tr>
    );
}, (prevProps, nextProps) => {
    // Only re-render if this row's data changed
    if (prevProps.alloc !== nextProps.alloc) return false;
    if (prevProps.idx !== nextProps.idx) return false;
    if (prevProps.unitName !== nextProps.unitName) return false;
    if (prevProps.allocLevelName !== nextProps.allocLevelName) return false;
    if (prevProps.taskName !== nextProps.taskName) return false;
    if (prevProps.dynamicDayColumns !== nextProps.dynamicDayColumns) return false;
    if (prevProps.responsibleOptions !== nextProps.responsibleOptions) return false;
    return true;
});

interface PlanTaskModalProps {
    isOpen: boolean;
    planningTaskId: string | null;
    initialAllocations: (WeeklyAllocation & {
        selected: boolean;
        isRestricted: boolean;
        isPreviouslyExecuted?: boolean;
        isReprogrammed?: boolean;
        isSuggestedByGantt?: boolean;
        accumulatedCompliance?: number;
        remainingBalance?: number;
    })[];
    activePlanId: number | null;
    showGanttSuggestions: boolean;
    units: PhysicalUnit[];
    levels: any[];
    tasks: Task[];
    sortedResponsibles: any[];
    dynamicDayColumns: DynamicDayColumn[];
    weeklyPlans: WeeklyPlan[];
    protocolConfigs: Record<string, ProtocolConfig>;
    savedProtocols: Record<string, ProtocolState>;
    onClose: () => void;
    onSave: (allocations: (WeeklyAllocation & { selected: boolean; isRestricted: boolean; isPreviouslyExecuted?: boolean; isReprogrammed?: boolean; isSuggestedByGantt?: boolean })[]) => void;
    onExportExcel: (planId: number) => void;
}

const PlanTaskModal: React.FC<PlanTaskModalProps> = React.memo(({
    isOpen,
    planningTaskId,
    initialAllocations,
    activePlanId,
    showGanttSuggestions,
    units,
    levels,
    tasks,
    sortedResponsibles,
    dynamicDayColumns,
    weeklyPlans,
    protocolConfigs,
    savedProtocols,
    onClose,
    onSave,
    onExportExcel
}) => {
    const [localAllocations, setLocalAllocations] = useState(initialAllocations);
    const [bulkResponsibleId, setBulkResponsibleId] = useState('');

    useEffect(() => {
        setLocalAllocations(initialAllocations);
        setBulkResponsibleId('');
    }, [initialAllocations]);

    if (!isOpen) return null;

    const unitMap = new Map(units.map(u => [u.id, u]));
    const levelMap = new Map(levels.map(l => [l.id, l]));
    const taskNameMap = new Map<string, string>();
    const getFastTaskName = (id: string) => {
        if (!taskNameMap.has(id)) {
            taskNameMap.set(id, findTaskName(id, tasks) || '[Partida Obsoleta/Fuera de Gantt]');
        }
        return taskNameMap.get(id)!;
    };

    const responsibleOptions = sortedResponsibles.map(r => (
        <option key={r.id} value={r.id}>{r.name} ({r.companyName || 'Sin Asignar'})</option>
    ));

    const suggestedRows = localAllocations.filter(alloc => alloc.isSuggestedByGantt && !alloc.isPreviouslyExecuted && !alloc.isRestricted);
    const areAllSuggestedSelected = suggestedRows.length > 0 && suggestedRows.every(alloc => alloc.selected);

    const toggleRowSelection = (index: number) => {
        setLocalAllocations(prev => {
            const alloc = prev[index];
            if (!alloc || alloc.isRestricted || alloc.isPreviouslyExecuted) return prev;
            const newAllocations = [...prev];
            const newSelected = !alloc.selected;
            newAllocations[index] = {
                ...alloc,
                selected: newSelected,
                ...(newSelected && bulkResponsibleId ? { responsibleId: bulkResponsibleId } : {})
            };
            return newAllocations;
        });
    };

    const toggleDay = (index: number, day: string) => {
        setLocalAllocations(prev => {
            const alloc = prev[index];
            if (!alloc || !alloc.selected || alloc.isPreviouslyExecuted) return prev;
            const newAllocations = [...prev];
            newAllocations[index] = {
                ...alloc,
                days: { ...alloc.days, [day]: !alloc.days[day] }
            };
            return newAllocations;
        });
    };

    const updateAllocationField = (index: number, field: keyof WeeklyAllocation, value: any) => {
        setLocalAllocations(prev => {
            const alloc = prev[index];
            if (!alloc) return prev;
            const newAllocations = [...prev];
            newAllocations[index] = { ...alloc, [field]: value };
            return newAllocations;
        });
    };

    const handleSuggestByGanttToggle = () => {
        if (areAllSuggestedSelected) {
            setLocalAllocations(prev => prev.map(alloc => {
                if (alloc.isSuggestedByGantt && !alloc.isPreviouslyExecuted && !alloc.isRestricted) {
                    return { ...alloc, selected: false };
                }
                return alloc;
            }));
        } else {
            setLocalAllocations(prev => prev.map(alloc => {
                if (alloc.isSuggestedByGantt && !alloc.isPreviouslyExecuted && !alloc.isRestricted) {
                    const newResp = (!alloc.selected && bulkResponsibleId) ? bulkResponsibleId : alloc.responsibleId;
                    const accumulatedCompliance = getTaskUnitAccumulatedCompliance(alloc.taskId, alloc.unitId, tasks, weeklyPlans, protocolConfigs, savedProtocols, undefined, activePlanId);
                    const remainingBalance = Math.max(0, Number((100 - accumulatedCompliance).toFixed(2)));
                    return {
                        ...alloc,
                        selected: true,
                        responsibleId: newResp,
                        percentComprometido: Math.min(alloc.percentComprometido ?? remainingBalance, remainingBalance)
                    };
                }
                return alloc;
            }));
        }
    };

    const handleSave = () => {
        onSave(localAllocations);
    };

    const selectableRows = localAllocations.filter(a => !a.isRestricted && !a.isPreviouslyExecuted);
    const allSelected = selectableRows.length > 0 && selectableRows.every(a => a.selected);
    const someSelected = selectableRows.some(a => a.selected);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-6xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[90vh]">
                <div className="flex justify-between items-center p-4 border-b border-slate-100 dark:border-slate-800">
                    <h3 className="text-lg font-bold text-slate-800 dark:text-white">
                        {planningTaskId === 'ALL_REPROGRAMMED' ? 'Re-Programar Tareas' : 'Programar Tarea'}
                    </h3>
                    <div className="flex items-center gap-4">
                        {activePlanId && (
                            <>
                                {showGanttSuggestions && (
                                    <button
                                        onClick={handleSuggestByGanttToggle}
                                        className={`border px-3 py-1.5 rounded text-sm font-bold transition-all flex items-center gap-1 ${areAllSuggestedSelected
                                                ? 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200'
                                                : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
                                            }`}
                                    >
                                        <span className="material-symbols-outlined text-[18px]">
                                            {areAllSuggestedSelected ? 'remove_done' : 'tips_and_updates'}
                                        </span>
                                        <span>{areAllSuggestedSelected ? 'Desmarcar Sugeridas' : 'Sugerir por Gantt'}</span>
                                    </button>
                                )}
                                <button
                                    onClick={() => onExportExcel(activePlanId)}
                                    className="bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 px-3 py-1.5 rounded text-sm font-bold transition-colors flex items-center gap-1"
                                >
                                    <span className="material-symbols-outlined text-[18px]">download</span>
                                    <span>Exportar las Tareas</span>
                                </button>
                            </>
                        )}
                        <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>
                </div>

                {/* General/Bulk selector panel */}
                <div className="px-6 py-3 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                        <span className="material-symbols-outlined text-slate-500 text-[20px]">assignment_ind</span>
                        <div className="flex items-center gap-2">
                            <label className="text-xs font-bold text-slate-700 dark:text-slate-350 uppercase tracking-wider">Asignar Responsable en Lote:</label>
                            <select
                                value={bulkResponsibleId}
                                onChange={(e) => setBulkResponsibleId(e.target.value)}
                                className="text-xs p-1.5 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 font-bold text-primary outline-none focus:ring-1 focus:ring-primary/50"
                            >
                                <option value="">Seleccionar...</option>
                                {sortedResponsibles.map(r => (
                                    <option key={r.id} value={r.id}>{r.name} ({r.companyName || 'Sin Asignar'})</option>
                                ))}
                            </select>
                        </div>
                        <span className="text-[10px] text-slate-400 dark:text-slate-550 italic mt-0.5">(Inyecta automáticamente al marcar casillas de unidades)</span>
                    </div>
                </div>

                <div className="px-6 pb-6 pt-0 overflow-y-auto relative">
                    <table className="w-full text-left border-collapse">
                        <thead className="sticky top-0 z-[50] bg-slate-50 dark:bg-slate-800 shadow-md border-b dark:border-slate-700">
                            <tr>
                                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase w-10 text-center bg-slate-50 dark:bg-slate-800">
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                                        onChange={(e) => {
                                            const check = e.target.checked;
                                            setLocalAllocations(prev => prev.map(a => {
                                                if (a.isRestricted || a.isPreviouslyExecuted) return a;
                                                const newResp = (check && !a.selected && bulkResponsibleId) ? bulkResponsibleId : a.responsibleId;
                                                return { ...a, selected: check, responsibleId: newResp };
                                            }));
                                        }}
                                        className="rounded border-slate-300 text-primary focus:ring-primary size-4 cursor-pointer"
                                        title={allSelected ? "Desmarcar todas" : "Seleccionar todas"}
                                    />
                                </th>
                                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase bg-slate-50 dark:bg-slate-800">Nivel</th>
                                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase bg-slate-50 dark:bg-slate-800">Unidades Físicas</th>
                                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase bg-slate-50 dark:bg-slate-800">Tarea</th>
                                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase w-48 bg-slate-50 dark:bg-slate-800">Responsable</th>
                                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase w-28 text-center bg-slate-50 dark:bg-slate-800">% Comprometido</th>
                                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase w-28 text-center bg-slate-50 dark:bg-slate-800">% Cumplimiento Acum.</th>
                                {dynamicDayColumns.map(col => (
                                    <th key={col.key} className="px-1.5 py-2.5 text-xs font-bold text-slate-600 dark:text-slate-300 text-center w-10 bg-slate-50 dark:bg-slate-800 border-l border-slate-200/50 dark:border-slate-700/50">
                                        <div className="flex flex-col items-center">
                                            <span className="text-[11px] font-black leading-none">{col.dayLetter}</span>
                                            <span className="text-[9px] font-medium text-slate-400 dark:text-slate-550 leading-none mt-0.5">{col.dateNum}</span>
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {localAllocations.length === 0 ? (
                                <tr>
                                    <td colSpan={7 + dynamicDayColumns.length} className="px-4 py-8 text-center text-slate-400 italic">
                                        {planningTaskId === 'ALL_REPROGRAMMED'
                                            ? 'No hay tareas no ejecutadas pendientes de reprogramar.'
                                            : 'No hay Unidades Físicas asignadas a esta tarea en el EDT (Marcadas con valor distinto de 0).'}
                                    </td>
                                </tr>
                            ) : (
                                localAllocations.map((alloc, idx) => {
                                    const allocUnit = unitMap.get(alloc.unitId);
                                    const unitName = allocUnit?.name || '';
                                    const allocLevelName = allocUnit?.levelId ? (levelMap.get(allocUnit.levelId)?.name || '-') : '-';
                                    const currentTaskId = alloc.taskId || planningTaskId;
                                    const taskName = currentTaskId ? getFastTaskName(currentTaskId) : "";

                                    return (
                                        <PlanTaskRow
                                            key={`${alloc.taskId}_${alloc.unitId}`}
                                            alloc={alloc}
                                            idx={idx}
                                            unitName={unitName}
                                            allocLevelName={allocLevelName}
                                            taskName={taskName}
                                            dynamicDayColumns={dynamicDayColumns}
                                            responsibleOptions={responsibleOptions}
                                            onToggleRow={toggleRowSelection}
                                            onToggleDay={toggleDay}
                                            onUpdateField={updateAllocationField}
                                        />
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3 border-t border-slate-200 dark:border-slate-700">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-200 rounded">Cancelar</button>
                    <button onClick={handleSave} className="px-6 py-2 text-sm font-bold text-white bg-primary hover:bg-blue-600 rounded shadow-sm">Guardar Programación</button>
                </div>
            </div>
        </div>
    );
});

interface AdditionalTaskRowItemProps {
    row: AdditionalTaskRowDraft;
    idx: number;
    isEditing: boolean;
    isRowChecked: boolean;
    levels: any[];
    availableUnits: any[];
    sortedResponsibles: any[];
    dynamicDayColumns: any[];
    canDelete: boolean;
    isLevelPickerOpen: boolean;
    isUnitPickerOpen: boolean;
    onToggleSelectRow: (id: string) => void;
    onToggleLevelPicker: (id: string) => void;
    onToggleUnitPicker: (id: string) => void;
    onUpdateRow: (id: string, field: keyof AdditionalTaskRowDraft, value: any) => void;
    onToggleDay: (id: string, dayLetter: string) => void;
    onRemoveRow: (id: string) => void;
    onDeployMultiLevel: (id: string) => void;
    onDeployMultiUnit: (id: string) => void;
    onSelectLevelsBatch: (id: string, levelIds: string[]) => void;
    onSelectUnitsBatch: (id: string, unitIds: string[]) => void;
}

const AdditionalTaskRowItem: React.FC<AdditionalTaskRowItemProps> = React.memo(({
    row,
    idx,
    isEditing,
    isRowChecked,
    levels = [],
    availableUnits = [],
    sortedResponsibles = [],
    dynamicDayColumns = [],
    canDelete,
    isLevelPickerOpen,
    isUnitPickerOpen,
    onToggleSelectRow,
    onToggleLevelPicker,
    onToggleUnitPicker,
    onUpdateRow,
    onToggleDay,
    onRemoveRow,
    onDeployMultiLevel,
    onDeployMultiUnit,
    onSelectLevelsBatch,
    onSelectUnitsBatch
}) => {
    const isMultiLevel = !!(row?.selectedLevelIds && row.selectedLevelIds.length > 1);
    const isMultiUnit = !!(!isMultiLevel && row?.selectedUnitIds && row.selectedUnitIds.length > 1);
    const rowDays = row?.days || {};
    const safeLevels = levels || [];
    const safeUnits = availableUnits || [];
    const safeResponsibles = sortedResponsibles || [];
    const safeDayColumns = dynamicDayColumns || [];

    const levelBtnRef = React.useRef<HTMLButtonElement>(null);
    const unitBtnRef = React.useRef<HTMLButtonElement>(null);
    const [levelDropdownPos, setLevelDropdownPos] = React.useState<{ top: number; left: number; openUpward: boolean }>({ top: 0, left: 0, openUpward: false });
    const [unitDropdownPos, setUnitDropdownPos] = React.useState<{ top: number; left: number; openUpward: boolean }>({ top: 0, left: 0, openUpward: false });

    React.useEffect(() => {
        if (isLevelPickerOpen && levelBtnRef.current) {
            const rect = levelBtnRef.current.getBoundingClientRect();
            const winH = typeof window !== 'undefined' ? window.innerHeight : 800;
            const winW = typeof window !== 'undefined' ? window.innerWidth : 1200;
            const spaceBelow = winH - rect.bottom;
            const openUpward = spaceBelow < 250 && rect.top > 250;
            setLevelDropdownPos({
                top: openUpward ? rect.top - 8 : rect.bottom + 6,
                left: Math.max(10, Math.min(rect.left, winW - 300)),
                openUpward
            });
        }
    }, [isLevelPickerOpen]);

    React.useEffect(() => {
        if (isUnitPickerOpen && unitBtnRef.current) {
            const rect = unitBtnRef.current.getBoundingClientRect();
            const winH = typeof window !== 'undefined' ? window.innerHeight : 800;
            const winW = typeof window !== 'undefined' ? window.innerWidth : 1200;
            const spaceBelow = winH - rect.bottom;
            const openUpward = spaceBelow < 250 && rect.top > 250;
            setUnitDropdownPos({
                top: openUpward ? rect.top - 8 : rect.bottom + 6,
                left: Math.max(10, Math.min(rect.left, winW - 300)),
                openUpward
            });
        }
    }, [isUnitPickerOpen]);

    const winHeight = typeof window !== 'undefined' ? window.innerHeight : 800;

    return (
        <tr className={`hover:bg-slate-50/70 dark:hover:bg-slate-800/50 transition-colors ${isRowChecked ? 'bg-purple-50/40 dark:bg-purple-950/20' : ''}`}>
            {/* Checkbox for Bulk Actions */}
            {!isEditing && (
                <td className="py-2.5 px-3 text-center">
                    <input
                        type="checkbox"
                        checked={isRowChecked}
                        onChange={() => onToggleSelectRow(row.id)}
                        className="rounded border-slate-300 text-purple-600 focus:ring-purple-500 size-4 cursor-pointer"
                    />
                </td>
            )}

            {/* Index */}
            <td className="py-2.5 px-2 text-center text-xs font-bold text-slate-400">
                {idx + 1}
            </td>

            {/* Level Select */}
            <td className="py-2.5 px-2">
                {isEditing ? (
                    <select
                        value={row?.levelId || ''}
                        onChange={(e) => onUpdateRow(row.id, 'levelId', e.target.value)}
                        className="w-full text-xs p-2 border border-slate-300 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 text-slate-800 dark:text-white focus:ring-2 focus:ring-purple-500/50 outline-none font-medium"
                    >
                        <option value="">Seleccionar Nivel...</option>
                        {safeLevels.map(l => (
                            <option key={l.id} value={l.id}>{l.name}</option>
                        ))}
                    </select>
                ) : (
                    <div className="relative">
                        <div className="flex items-center gap-1.5">
                            <button
                                ref={levelBtnRef}
                                type="button"
                                onClick={() => onToggleLevelPicker(row.id)}
                                className={`flex-1 text-left text-xs p-2 border rounded-xl bg-white dark:bg-slate-800 font-medium flex items-center justify-between transition-all ${isMultiLevel
                                        ? 'border-purple-400 text-purple-700 dark:text-purple-300 bg-purple-50/50 dark:bg-purple-950/30'
                                        : row?.levelId
                                            ? 'border-slate-300 dark:border-slate-700 text-slate-800 dark:text-white'
                                            : 'border-slate-300 dark:border-slate-700 text-slate-400'
                                    }`}
                            >
                                <span className="truncate">
                                    {isMultiLevel
                                        ? `${row.selectedLevelIds?.length} Niveles Seleccionados`
                                        : row?.levelId
                                            ? (safeLevels.find(l => l.id === row.levelId)?.name || 'Nivel Seleccionado')
                                            : 'Seleccionar Nivel(es)...'}
                                </span>
                                <span className="material-symbols-outlined text-[16px] text-slate-400 ml-1">
                                    {isLevelPickerOpen ? 'expand_less' : 'expand_more'}
                                </span>
                            </button>

                            {isMultiLevel && (
                                <button
                                    type="button"
                                    onClick={() => onDeployMultiLevel(row.id)}
                                    className="p-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl shadow-md shadow-purple-500/20 flex items-center justify-center transition-all animate-pulse hover:animate-none flex-shrink-0"
                                    title={`Desplegar en ${row.selectedLevelIds?.length} tareas (una por cada nivel seleccionado)`}
                                >
                                    <span className="material-symbols-outlined text-[18px]">call_split</span>
                                </button>
                            )}
                        </div>

                        {/* Floating Portal-Style Dropdown Menu */}
                        {isLevelPickerOpen && (
                            <>
                                <div
                                    className="fixed inset-0 z-[120]"
                                    onClick={() => onToggleLevelPicker(row.id)}
                                />
                                <div
                                    style={{
                                        position: 'fixed',
                                        top: levelDropdownPos.openUpward ? undefined : `${levelDropdownPos.top}px`,
                                        bottom: levelDropdownPos.openUpward ? `${winHeight - levelDropdownPos.top}px` : undefined,
                                        left: `${levelDropdownPos.left}px`,
                                        width: '280px',
                                        maxHeight: '260px'
                                    }}
                                    className="bg-white dark:bg-[#1e293b] border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl z-[130] p-2 space-y-1 overflow-y-auto animate-in fade-in zoom-in-95 duration-150"
                                >
                                    <div className="px-2 py-1 text-[10px] font-black uppercase text-slate-400 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center">
                                        <span>Niveles de la Obra</span>
                                        <span className="text-purple-600 font-bold">Selección Múltiple</span>
                                    </div>
                                    {safeLevels.map(l => {
                                        const isSelected = (row?.selectedLevelIds && row.selectedLevelIds.length > 0)
                                            ? row.selectedLevelIds.includes(l.id)
                                            : row?.levelId === l.id;
                                        return (
                                            <label
                                                key={l.id}
                                                className="flex items-center gap-2 p-1.5 hover:bg-purple-50 dark:hover:bg-purple-950/40 rounded-lg cursor-pointer text-xs text-slate-700 dark:text-slate-200"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => {
                                                        const current = (row?.selectedLevelIds && row.selectedLevelIds.length > 0)
                                                            ? [...row.selectedLevelIds]
                                                            : (row?.levelId ? [row.levelId] : []);
                                                        let updated: string[];
                                                        if (current.includes(l.id)) {
                                                            updated = current.filter(id => id !== l.id);
                                                        } else {
                                                            updated = [...current, l.id];
                                                        }
                                                        onSelectLevelsBatch(row.id, updated);
                                                    }}
                                                    className="rounded border-slate-300 text-purple-600 focus:ring-purple-500 size-3.5"
                                                />
                                                <span className="font-medium truncate">{l.name}</span>
                                            </label>
                                        );
                                    })}
                                    {isMultiLevel && (
                                        <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                                            <button
                                                type="button"
                                                onClick={() => onDeployMultiLevel(row.id)}
                                                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold text-[11px] py-1.5 px-2 rounded-lg flex items-center justify-center gap-1.5 shadow-sm"
                                            >
                                                <span className="material-symbols-outlined text-[15px]">call_split</span>
                                                <span>Desplegar {row.selectedLevelIds?.length} tareas</span>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </td>

            {/* Unit Select (Sitio) */}
            <td className="py-2.5 px-2">
                {isMultiLevel ? (
                    <div className="w-full text-xs p-2 border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-100 dark:bg-slate-800/50 text-slate-400 italic font-medium flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-[15px]">lock</span>
                        <span className="truncate">Se definirá al desplegar</span>
                    </div>
                ) : isEditing ? (
                    <select
                        value={row?.unitId || ''}
                        onChange={(e) => onUpdateRow(row.id, 'unitId', e.target.value)}
                        disabled={!row?.levelId}
                        className="w-full text-xs p-2 border border-slate-300 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 text-slate-800 dark:text-white focus:ring-2 focus:ring-purple-500/50 outline-none font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <option value="">Seleccionar Sitio...</option>
                        {safeUnits.map(u => (
                            <option key={u.id} value={u.id}>{u.name}</option>
                        ))}
                    </select>
                ) : (
                    <div className="relative">
                        <div className="flex items-center gap-1.5">
                            <button
                                ref={unitBtnRef}
                                type="button"
                                disabled={!row?.levelId}
                                onClick={() => onToggleUnitPicker(row.id)}
                                className={`flex-1 text-left text-xs p-2 border rounded-xl bg-white dark:bg-slate-800 font-medium flex items-center justify-between transition-all disabled:opacity-50 disabled:cursor-not-allowed ${isMultiUnit
                                        ? 'border-purple-400 text-purple-700 dark:text-purple-300 bg-purple-50/50 dark:bg-purple-950/30'
                                        : row?.unitId
                                            ? 'border-slate-300 dark:border-slate-700 text-slate-800 dark:text-white'
                                            : 'border-slate-300 dark:border-slate-700 text-slate-400'
                                    }`}
                            >
                                <span className="truncate">
                                    {isMultiUnit
                                        ? `${row.selectedUnitIds?.length} Sitios Seleccionados`
                                        : row?.unitId
                                            ? (safeUnits.find(u => u.id === row.unitId)?.name || 'Sitio Seleccionado')
                                            : 'Seleccionar Sitio(s)...'}
                                </span>
                                <span className="material-symbols-outlined text-[16px] text-slate-400 ml-1">
                                    {isUnitPickerOpen ? 'expand_less' : 'expand_more'}
                                </span>
                            </button>

                            {isMultiUnit && (
                                <button
                                    type="button"
                                    onClick={() => onDeployMultiUnit(row.id)}
                                    className="p-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl shadow-md shadow-purple-500/20 flex items-center justify-center transition-all animate-pulse hover:animate-none flex-shrink-0"
                                    title={`Desplegar en ${row.selectedUnitIds?.length} tareas (una por cada sitio seleccionado)`}
                                >
                                    <span className="material-symbols-outlined text-[18px]">call_split</span>
                                </button>
                            )}
                        </div>

                        {/* Floating Portal-Style Dropdown Menu */}
                        {isUnitPickerOpen && (
                            <>
                                <div
                                    className="fixed inset-0 z-[120]"
                                    onClick={() => onToggleUnitPicker(row.id)}
                                />
                                <div
                                    style={{
                                        position: 'fixed',
                                        top: unitDropdownPos.openUpward ? undefined : `${unitDropdownPos.top}px`,
                                        bottom: unitDropdownPos.openUpward ? `${winHeight - unitDropdownPos.top}px` : undefined,
                                        left: `${unitDropdownPos.left}px`,
                                        width: '280px',
                                        maxHeight: '260px'
                                    }}
                                    className="bg-white dark:bg-[#1e293b] border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl z-[130] p-2 space-y-1 overflow-y-auto animate-in fade-in zoom-in-95 duration-150"
                                >
                                    <div className="px-2 py-1 text-[10px] font-black uppercase text-slate-400 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center">
                                        <span>Unidades Físicas (Sitios)</span>
                                        <span className="text-purple-600 font-bold">Selección Múltiple</span>
                                    </div>
                                    {safeUnits.length === 0 ? (
                                        <div className="p-3 text-xs text-slate-400 italic text-center">
                                            No hay unidades físicas en este nivel.
                                        </div>
                                    ) : (
                                        safeUnits.map(u => {
                                            const isSelected = (row?.selectedUnitIds && row.selectedUnitIds.length > 0)
                                                ? row.selectedUnitIds.includes(u.id)
                                                : row?.unitId === u.id;
                                            return (
                                                <label
                                                    key={u.id}
                                                    className="flex items-center gap-2 p-1.5 hover:bg-purple-50 dark:hover:bg-purple-950/40 rounded-lg cursor-pointer text-xs text-slate-700 dark:text-slate-200"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => {
                                                            const current = (row?.selectedUnitIds && row.selectedUnitIds.length > 0)
                                                                ? [...row.selectedUnitIds]
                                                                : (row?.unitId ? [row.unitId] : []);
                                                            let updated: string[];
                                                            if (current.includes(u.id)) {
                                                                updated = current.filter(id => id !== u.id);
                                                            } else {
                                                                updated = [...current, u.id];
                                                            }
                                                            onSelectUnitsBatch(row.id, updated);
                                                        }}
                                                        className="rounded border-slate-300 text-purple-600 focus:ring-purple-500 size-3.5"
                                                    />
                                                    <span className="font-medium truncate">{u.name}</span>
                                                </label>
                                            );
                                        })
                                    )}
                                    {isMultiUnit && (
                                        <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                                            <button
                                                type="button"
                                                onClick={() => onDeployMultiUnit(row.id)}
                                                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold text-[11px] py-1.5 px-2 rounded-lg flex items-center justify-center gap-1.5 shadow-sm"
                                            >
                                                <span className="material-symbols-outlined text-[15px]">call_split</span>
                                                <span>Desplegar {row.selectedUnitIds?.length} tareas</span>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </td>

            {/* Task Name */}
            <td className="py-2.5 px-2">
                <input
                    type="text"
                    value={row?.additionalTaskName || ''}
                    onChange={(e) => onUpdateRow(row.id, 'additionalTaskName', e.target.value)}
                    placeholder="Escriba el nombre de la tarea..."
                    className="w-full text-xs p-2 border border-slate-300 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 text-slate-800 dark:text-white focus:ring-2 focus:ring-purple-500/50 outline-none font-medium"
                />
            </td>

            {/* Responsible Select */}
            <td className="py-2.5 px-2">
                <select
                    value={row?.responsibleId || ''}
                    onChange={(e) => onUpdateRow(row.id, 'responsibleId', e.target.value)}
                    className="w-full text-xs p-2 border border-slate-300 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 text-slate-800 dark:text-white focus:ring-2 focus:ring-purple-500/50 outline-none font-medium truncate"
                >
                    <option value="">Seleccionar Responsable...</option>
                    {safeResponsibles.map(r => (
                        <option key={r.id} value={r.id}>
                            {r.name} ({r.companyName || 'Sin Asignar'})
                        </option>
                    ))}
                </select>
            </td>

            {/* Days Buttons */}
            <td className="py-2.5 px-2">
                <div className="flex items-center justify-center gap-1.5">
                    {safeDayColumns.map(col => {
                        const dayKey = col.dayLetter;
                        const isChecked = !!rowDays[dayKey];
                        return (
                            <button
                                key={col.key}
                                type="button"
                                onClick={() => onToggleDay(row.id, dayKey)}
                                className={`flex flex-col items-center justify-center w-8 h-9 rounded-lg text-xs font-black transition-all ${isChecked
                                        ? 'bg-purple-600 text-white shadow-md shadow-purple-500/20 scale-105'
                                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:border-purple-400'
                                    }`}
                                title={col.fullDate || col.dayLetter}
                            >
                                <span className="text-[11px] leading-none">{col.dayLetter}</span>
                                <span className="text-[9px] font-medium leading-none opacity-80 mt-0.5">{col.dateNum}</span>
                            </button>
                        );
                    })}
                </div>
            </td>

            {/* Actions */}
            {!isEditing && (
                <td className="py-2.5 px-2 text-center">
                    <button
                        type="button"
                        onClick={() => onRemoveRow(row.id)}
                        disabled={!canDelete}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all disabled:opacity-30 disabled:pointer-events-none"
                        title="Eliminar fila"
                    >
                        <span className="material-symbols-outlined text-[18px]">delete</span>
                    </button>
                </td>
            )}
        </tr>
    );
});

interface ConstraintModalProps {
    isOpen: boolean;
    editingConstraintId: string | null;
    initialData: {
        typeId: string;
        responsibleId: string;
        description: string;
        requiredDate: string;
        unitsType: 'na' | 'all' | 'custom';
        unitIds: string[];
    };
    constraintGroups: ConstraintGroup[];
    sortedResponsibles: any[];
    onClose: () => void;
    onSave: (data: {
        typeId: string;
        responsibleId: string;
        description: string;
        requiredDate: string;
        unitsType: 'na' | 'all' | 'custom';
        unitIds: string[];
    }) => void;
    onOpenUnitSelection: (currentData: {
        typeId: string;
        responsibleId: string;
        description: string;
        requiredDate: string;
        unitsType: 'na' | 'all' | 'custom';
        unitIds: string[];
    }) => void;
}

const ConstraintModal: React.FC<ConstraintModalProps> = React.memo(({
    isOpen,
    editingConstraintId,
    initialData,
    constraintGroups,
    sortedResponsibles,
    onClose,
    onSave,
    onOpenUnitSelection
}) => {
    const [formData, setFormData] = useState(initialData);

    useEffect(() => {
        if (isOpen) {
            setFormData(initialData);
        }
    }, [isOpen, initialData]);

    if (!isOpen) return null;

    const handleSubmit = () => {
        onSave(formData);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-900 rounded-lg shadow-2xl w-[500px] border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col">
                <div className="flex justify-between items-center p-4 border-b border-slate-100 dark:border-slate-800">
                    <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                        <span className="material-symbols-outlined text-amber-500">
                            {editingConstraintId ? 'edit_note' : 'warning'}
                        </span>
                        {editingConstraintId ? 'Editar Restricción' : 'Restricción'}
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>
                <div className="p-6 flex flex-col gap-4">
                    <div>
                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Tipo de Restricción</label>
                        <select
                            className="w-full rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white p-2.5 border focus:ring-1 focus:ring-purple-500 outline-none text-sm"
                            value={formData.typeId}
                            onChange={(e) => setFormData(prev => ({ ...prev, typeId: e.target.value }))}
                        >
                            <option value="">Seleccionar Tipo...</option>
                            {constraintGroups.map(group => (
                                <optgroup key={group.id} label={group.name}>
                                    {group.definitions.map(def => (
                                        <option key={def.id} value={def.id}>{def.name}</option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Responsable</label>
                        <select
                            className="w-full rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white p-2.5 border focus:ring-1 focus:ring-purple-500 outline-none text-sm"
                            value={formData.responsibleId}
                            onChange={(e) => setFormData(prev => ({ ...prev, responsibleId: e.target.value }))}
                        >
                            <option value="">Seleccionar Responsable...</option>
                            {sortedResponsibles.map(r => (
                                <option key={r.id} value={r.id}>{r.name} ({r.companyName || 'Sin Asignar'})</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Fecha Requerida</label>
                        <input
                            type="date"
                            className="w-full rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white p-2.5 border focus:ring-1 focus:ring-purple-500 outline-none text-sm"
                            value={formData.requiredDate}
                            onChange={(e) => setFormData(prev => ({ ...prev, requiredDate: e.target.value }))}
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Restricción (Detalle)</label>
                        <textarea
                            className="w-full rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white p-3 border focus:ring-1 focus:ring-purple-500 outline-none text-sm resize-none h-24"
                            placeholder="Describa el detalle de la restricción..."
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Unidades físicas</label>
                        <button
                            type="button"
                            onClick={() => onOpenUnitSelection(formData)}
                            className="w-full rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white p-2.5 border hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-sm text-left flex items-center justify-between"
                        >
                            <span className={formData.unitsType === 'na' ? 'text-slate-400' : 'text-slate-700 dark:text-slate-200'}>
                                {formData.unitsType === 'na' ? 'No aplica Unidades físicas' :
                                    formData.unitsType === 'all' ? 'Todas las unidades (Seleccionado)' :
                                        `Si aplica (${formData.unitIds.length} unidades)`}
                            </span>
                            <span className="material-symbols-outlined text-[18px]">open_in_new</span>
                        </button>
                    </div>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3 border-t border-slate-200 dark:border-slate-700">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        className="px-6 py-2 text-sm font-bold text-white bg-purple-600 hover:bg-purple-700 rounded transition-colors shadow-sm"
                    >
                        Guardar Restricción
                    </button>
                </div>
            </div>
        </div>
    );
});

interface UnitSelectionModalProps {
    isOpen: boolean;
    unitsType: 'na' | 'all' | 'custom';
    unitIds: string[];
    activeConstraintTask: string | null;
    units: PhysicalUnit[];
    levels: Level[];
    matrixData: any;
    tasks: Task[];
    onClose: () => void;
    onConfirm: (unitsType: 'na' | 'all' | 'custom', unitIds: string[]) => void;
}

const UnitSelectionModal: React.FC<UnitSelectionModalProps> = React.memo(({
    isOpen,
    unitsType: initialUnitsType,
    unitIds: initialUnitIds,
    activeConstraintTask,
    units,
    levels,
    matrixData,
    tasks,
    onClose,
    onConfirm
}) => {
    const [unitsType, setUnitsType] = useState(initialUnitsType);
    const [selectedUnitIds, setSelectedUnitIds] = useState<string[]>(initialUnitIds);

    useEffect(() => {
        if (isOpen) {
            setUnitsType(initialUnitsType);
            setSelectedUnitIds(initialUnitIds);
        }
    }, [isOpen, initialUnitsType, initialUnitIds]);

    if (!isOpen) return null;

    const availableUnitsForTask = activeConstraintTask ? units.filter(u => isTaskUnitAssigned(activeConstraintTask, u.id, matrixData, tasks)) : [];

    const handleSelectAll = () => {
        setUnitsType('all');
        setSelectedUnitIds(availableUnitsForTask.map(u => u.id));
    };

    const handleSelectNone = () => {
        setUnitsType('na');
        setSelectedUnitIds([]);
    };

    const handleToggleUnit = (unitId: string) => {
        let newUnits = [...selectedUnitIds];
        if (newUnits.includes(unitId)) {
            newUnits = newUnits.filter(id => id !== unitId);
        } else {
            newUnits.push(unitId);
        }
        setSelectedUnitIds(newUnits);
        setUnitsType(newUnits.length > 0 ? 'custom' : 'na');
    };

    const handleConfirm = () => {
        onConfirm(unitsType, selectedUnitIds);
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-[500px] border border-slate-200 dark:border-slate-800 flex flex-col max-h-[80vh]">
                <div className="flex justify-between items-center p-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                    <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                        <span className="material-symbols-outlined text-primary">account_tree</span>
                        Seleccionar Unidades Físicas
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>
                <div className="p-4 flex gap-2 border-b border-slate-100 dark:border-slate-800">
                    <button
                        type="button"
                        onClick={handleSelectNone}
                        className={`flex-1 py-2 px-3 rounded-lg border text-sm font-bold transition-all ${unitsType === 'na' ? 'bg-slate-100 border-slate-400 text-slate-800' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                    >
                        No Aplica
                    </button>
                    <button
                        type="button"
                        onClick={handleSelectAll}
                        className={`flex-1 py-2 px-3 rounded-lg border text-sm font-bold transition-all ${unitsType === 'all' ? 'bg-primary border-primary text-white' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                    >
                        Seleccionar Todo
                    </button>
                </div>
                <div className="overflow-x-auto p-4">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-slate-200 dark:border-slate-700">
                                <th className="px-3 py-2 w-10 text-center"></th>
                                <th className="px-3 py-2 text-xs font-bold text-slate-500 uppercase">Nivel</th>
                                <th className="px-3 py-2 text-xs font-bold text-slate-500 uppercase">Sitio</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {availableUnitsForTask.map(unit => {
                                const level = levels.find(l => l.id === unit.levelId);
                                const isChecked = selectedUnitIds.includes(unit.id);
                                return (
                                    <tr
                                        key={unit.id}
                                        onClick={() => handleToggleUnit(unit.id)}
                                        className={`cursor-pointer transition-colors ${isChecked ? 'bg-primary/5 hover:bg-primary/10' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                                    >
                                        <td className="px-3 py-3 text-center">
                                            <div className={`size-5 mx-auto rounded flex items-center justify-center border ${isChecked ? 'bg-primary border-primary text-white' : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800'}`}>
                                                {isChecked && <span className="material-symbols-outlined text-[14px] font-bold">check</span>}
                                            </div>
                                        </td>
                                        <td className="px-3 py-3 text-sm text-slate-500 font-medium">
                                            {level ? level.name : '-'}
                                        </td>
                                        <td className={`px-3 py-3 text-sm ${isChecked ? 'text-primary font-bold' : 'text-slate-700 dark:text-slate-300'}`}>
                                            {unit.name}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3 border-t border-slate-200 dark:border-slate-700">
                    <button type="button" onClick={handleConfirm} className="px-6 py-2 text-sm font-bold text-white bg-primary hover:bg-blue-600 rounded shadow-sm">Confirmar</button>
                </div>
            </div>
        </div>
    );
});
interface WeeklyMobileFilterState {
    levels: string[];
    units: string[];
    responsibles: string[];
    tasks: string[];
    statuses: string[];
}

const initialWeeklyMobileFilters: WeeklyMobileFilterState = {
    levels: [],
    units: [],
    responsibles: [],
    tasks: [],
    statuses: []
};

const PlanningScreen: React.FC<PlanningScreenProps> = ({
    tasks,
    setTasks,
    responsibles,
    setResponsibles,
    globalUsers,
    setGlobalUsers,
    matrixData,
    setMatrixData,
    units,
    levels,
    savedProtocols,
    protocolConfigs,
    weeklyPlans,
    setWeeklyPlans,
    lookaheadPlans,
    setLookaheadPlans,
    projectInfo,
    subcontractTypes,
    setSubcontractTypes,
    cncGroups,
    setCncGroups,
    constraintGroups,
    setConstraintGroups,
    globalCNCGroups,
    globalConstraintGroups,
    baselines,
    setBaselines,
    forceSyncTasks,
    profiles,
    addActivity,
    progressHistory,
    setProgressHistory,
    notifications,
    setNotifications,
    currentUser,
    hasPermission,
    subcontractors = [],
    contracts = [],
    companyConfig,
    setSavedProtocols,
    setProtocolConfigs,
    workGroupTemplates = [],
    setWorkGroupTemplates
}) => {
    // --- State and Logic ---
    const navigate = useNavigate();
    const location = useLocation();

    const checkIsMobileEnv = useCallback((): boolean => {
        if (typeof window === 'undefined') return false;
        if (Capacitor.isNativePlatform() || (typeof document !== 'undefined' && document.documentElement.classList.contains('is-native'))) return true;
        const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
            (navigator.maxTouchPoints > 1 && window.matchMedia('(pointer: coarse)').matches);
        if (isMobileDevice) return true;
        return window.innerWidth < 768;
    }, []);

    const [isMobile, setIsMobile] = React.useState<boolean>(() => checkIsMobileEnv());

    const canReviewPlan = hasPermission('movil_ps_revisar', 'view') || hasPermission('movil_ps_revisar', 'edit') || hasPermission('ps_revisar', 'view') || hasPermission('ps_revisar', 'edit');

    const isPlanVisibleInMobile = useCallback((_planStatus?: string) => {
        // En la pestaña Global, todos los usuarios asignados a la obra ven todas las planificaciones sin importar su perfil
        return true;
    }, []);

    // Mobile Filters for Active Weekly Plan
    const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
    const [mobileFilters, setMobileFilters] = useState<{
        levelId?: string;
        unitId?: string;
        responsibleId?: string;
        taskSearch?: string;
        status?: 'all' | 'executed' | 'pending' | 'protocol';
    }>({
        levelId: '',
        unitId: '',
        responsibleId: '',
        taskSearch: '',
        status: 'all'
    });

    const mobileActiveFiltersCount = useMemo(() => {
        let count = 0;
        if (mobileFilters.levelId) count++;
        if (mobileFilters.unitId) count++;
        if (mobileFilters.responsibleId) count++;
        if (mobileFilters.taskSearch && mobileFilters.taskSearch.trim() !== '') count++;
        if (mobileFilters.status && mobileFilters.status !== 'all') count++;
        return count;
    }, [mobileFilters]);

    // Helper único y canónico para número secuencial de Planificación Intermedia
    // Regla: la más antigua es la #1 y la más reciente tiene el número mayor.
    const getLookaheadPlanNumber = useCallback((planId?: string) => {
        if (!planId) return 1;
        const sorted = [...lookaheadPlans].sort((a, b) => {
            const timeA = new Date(a.startDate || 0).getTime();
            const timeB = new Date(b.startDate || 0).getTime();
            if (timeA !== timeB) return timeA - timeB;
            return (a.id || '').localeCompare(b.id || '');
        });
        const idx = sorted.findIndex(p => p.id === planId);
        return idx !== -1 ? idx + 1 : 1;
    }, [lookaheadPlans]);

    // Helper for lookahead plan sequential title (Web / Escritorio)
    // Regla pre-2.0.8 (a6c6330): orden descendente donde la más reciente es la #1
    const getLookaheadPlanTitle = useCallback((planId?: string) => {
        if (!planId) return 'Planificación Intermedia';
        const sorted = [...lookaheadPlans].sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
        const idx = sorted.findIndex(p => p.id === planId);
        return `Planificación #${idx !== -1 ? idx + 1 : 1}`;
    }, [lookaheadPlans]);


    // Master Work Group Templates Manager Modal
    const [isWorkGroupModalOpen, setIsWorkGroupModalOpen] = useState(false);

    // Subcontractor Evaluation Modal from Weekly Planning History
    const [evalModalOpen, setEvalModalOpen] = useState(false);
    const [evalModalPlanId, setEvalModalPlanId] = useState<number | null>(null);

    // Attendance Sheet Modal from Weekly Planning History
    const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
    const [attendanceModalPlan, setAttendanceModalPlan] = useState<WeeklyPlan | null>(null);

    // Meeting Minutes (Acta de Reunión) Modal from Weekly Planning History
    const [meetingMinutesModalOpen, setMeetingMinutesModalOpen] = useState(false);
    const [meetingMinutesModalPlan, setMeetingMinutesModalPlan] = useState<WeeklyPlan | null>(null);

    // Modal when unexecuting a task with an existing Quality Protocol
    const [protocolReviewPromptOpen, setProtocolReviewPromptOpen] = useState(false);
    const [protocolPromptTarget, setProtocolPromptTarget] = useState<{
        planId: number;
        allocationIndex: number;
        protocolId: string;
        taskId: string;
        unitId: string;
        taskName: string;
        unitName: string;
    } | null>(null);
    const [rejectionObservation, setRejectionObservation] = useState('');

    // Reactive trigger to recheck which Additionals are completed (gray -> green)
    const [additionalsUpdateTrigger, setAdditionalsUpdateTrigger] = useState(0);

    // --- Weekly Table Sorting & Filtering States ---
    type WeeklyAllocColKey = 'level' | 'unit' | 'task' | 'responsible' | 'comprometido' | 'estado' | 'cumplimiento' | 'cnc';
    const [weeklySortColumn, setWeeklySortColumn] = useState<WeeklyAllocColKey | null>(null);
    const [weeklySortDirection, setWeeklySortDirection] = useState<'asc' | 'desc' | null>(null);
    const [weeklyColFilters, setWeeklyColFilters] = useState<Record<string, { search?: string; selected?: string[] }>>({});
    const [weeklyActiveFilterDropdown, setWeeklyActiveFilterDropdown] = useState<WeeklyAllocColKey | null>(null);

    const handleWeeklySortClick = (colKey: WeeklyAllocColKey, direction?: 'asc' | 'desc') => {
        if (direction) {
            if (weeklySortColumn === colKey && weeklySortDirection === direction) {
                setWeeklySortColumn(null);
                setWeeklySortDirection(null);
            } else {
                setWeeklySortColumn(colKey);
                setWeeklySortDirection(direction);
            }
            return;
        }

        if (weeklySortColumn === colKey) {
            if (weeklySortDirection === 'asc') {
                setWeeklySortDirection('desc');
            } else if (weeklySortDirection === 'desc') {
                setWeeklySortColumn(null);
                setWeeklySortDirection(null);
            }
        } else {
            setWeeklySortColumn(colKey);
            setWeeklySortDirection('asc');
        }
    };

    const handleWeeklyColSearchChange = (colKey: WeeklyAllocColKey, searchVal: string) => {
        setWeeklyColFilters(prev => ({
            ...prev,
            [colKey]: {
                ...(prev[colKey] || {}),
                search: searchVal
            }
        }));
    };

    const handleWeeklyColCheckboxToggle = (colKey: WeeklyAllocColKey, val: string, isChecked: boolean, allValues: string[]) => {
        setWeeklyColFilters(prev => {
            const currentSelected = prev[colKey]?.selected ?? allValues;
            let updated: string[];
            if (isChecked) {
                updated = [...currentSelected, val];
            } else {
                updated = currentSelected.filter(item => item !== val);
            }
            return {
                ...prev,
                [colKey]: {
                    ...(prev[colKey] || {}),
                    selected: updated
                }
            };
        });
    };

    const handleWeeklyColSelectAll = (colKey: WeeklyAllocColKey, selectAll: boolean, allValues: string[]) => {
        setWeeklyColFilters(prev => ({
            ...prev,
            [colKey]: {
                ...(prev[colKey] || {}),
                selected: selectAll ? allValues : []
            }
        }));
    };

    const clearWeeklyColFilter = (colKey: WeeklyAllocColKey) => {
        setWeeklyColFilters(prev => {
            const copy = { ...prev };
            delete copy[colKey];
            return copy;
        });
    };

    const renderWeeklyFilterDropdown = (colKey: WeeklyAllocColKey) => {
        if (weeklyActiveFilterDropdown !== colKey || !activePlan) return null;

        const currentFilter = weeklyColFilters[colKey] || {};
        const searchTerm = currentFilter.search || '';
        const isFilterActive = !!searchTerm || (currentFilter.selected !== undefined && currentFilter.selected.length < (activePlan.allocations?.length || 0));

        // Obtener valores únicos disponibles para esta columna
        const allDistinctValues = Array.from(new Set(
            activePlan.allocations.map(alloc => {
                if (colKey === 'level') {
                    const unit = units.find(u => u.id === alloc.unitId);
                    return levels.find(l => l.id === unit?.levelId)?.name || '-';
                }
                if (colKey === 'unit') {
                    const unit = units.find(u => u.id === alloc.unitId);
                    return unit?.name || 'N/A';
                }
                if (colKey === 'task') {
                    return alloc.isAdditional ? (alloc.additionalTaskName || 'Tarea Adicional') : (findTaskName(alloc.taskId, tasks) || '[Partida Obsoleta]');
                }
                if (colKey === 'responsible') {
                    return responsibles.find(r => r.id === alloc.responsibleId)?.name || '-';
                }
                if (colKey === 'comprometido') {
                    const pct = alloc.percentComprometido ?? (alloc.executed ? 100 : 100);
                    return `${pct}%`;
                }
                if (colKey === 'estado') {
                    const st = getStatusValue(alloc);
                    return st === 'si' ? 'Ejecutado' : st === 'no' ? 'No Ejecutado' : 'Pendiente';
                }
                if (colKey === 'cumplimiento') {
                    const pct = getAllocationCompliancePct(alloc);
                    return `${pct}%`;
                }
                if (colKey === 'cnc') {
                    if (getStatusValue(alloc) === 'si') return 'No Aplica';
                    const cause = cncGroups.flatMap(g => g.causes).find(c => c.id === alloc.cncId);
                    return cause?.name || 'Sin C.N.C';
                }
                return '-';
            })
        )).sort((a, b) => a.localeCompare(b, 'es', { numeric: true, sensitivity: 'base' }));

        const selectedList = currentFilter.selected ?? allDistinctValues;

        const filteredValues = searchTerm
            ? allDistinctValues.filter(val => val.toLowerCase().includes(searchTerm.toLowerCase()))
            : allDistinctValues;

        const isAllSelected = allDistinctValues.length > 0 && allDistinctValues.every(val => selectedList.includes(val));
        const isSomeSelected = allDistinctValues.some(val => selectedList.includes(val));

        return (
            <>
                <div className="fixed inset-0 z-40" onClick={() => setWeeklyActiveFilterDropdown(null)}></div>
                <div
                    onClick={(e) => e.stopPropagation()}
                    className="absolute top-full left-0 mt-1.5 w-64 bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 py-3 px-3.5 z-50 text-slate-700 dark:text-slate-200 normal-case font-normal select-text animate-in fade-in zoom-in-95 duration-150"
                >
                    {/* Header con Limpiar */}
                    <div className="flex justify-between items-center mb-2.5 pb-2 border-b border-slate-100 dark:border-slate-700/80">
                        <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Opciones de Columna</span>
                        {(isFilterActive || weeklySortColumn === colKey) && (
                            <button
                                onClick={() => {
                                    clearWeeklyColFilter(colKey);
                                    if (weeklySortColumn === colKey) {
                                        setWeeklySortColumn(null);
                                        setWeeklySortDirection(null);
                                    }
                                }}
                                className="text-[10px] font-bold text-red-500 hover:text-red-600 transition-colors uppercase cursor-pointer"
                            >
                                Reestablecer
                            </button>
                        )}
                    </div>

                    {/* Acciones de Ordenamiento */}
                    <div className="flex gap-1.5 mb-2.5">
                        <button
                            type="button"
                            onClick={() => handleWeeklySortClick(colKey, 'asc')}
                            className={`flex-1 flex items-center justify-center gap-1 py-1.5 px-2 text-xs font-semibold rounded-lg border transition-all cursor-pointer ${weeklySortColumn === colKey && weeklySortDirection === 'asc'
                                    ? 'bg-primary/10 border-primary text-primary dark:bg-primary/20 font-bold shadow-sm'
                                    : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-750'
                                }`}
                        >
                            <span className="material-symbols-outlined text-[15px]">arrow_upward</span>
                            <span>Ascendente</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => handleWeeklySortClick(colKey, 'desc')}
                            className={`flex-1 flex items-center justify-center gap-1 py-1.5 px-2 text-xs font-semibold rounded-lg border transition-all cursor-pointer ${weeklySortColumn === colKey && weeklySortDirection === 'desc'
                                    ? 'bg-primary/10 border-primary text-primary dark:bg-primary/20 font-bold shadow-sm'
                                    : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-750'
                                }`}
                        >
                            <span className="material-symbols-outlined text-[15px]">arrow_downward</span>
                            <span>Descendente</span>
                        </button>
                    </div>

                    {/* Buscador de Valores */}
                    <div className="relative mb-2">
                        <span className="material-symbols-outlined absolute left-2.5 top-2 text-slate-400 text-[15px]">search</span>
                        <input
                            type="text"
                            placeholder="Buscar en columna..."
                            value={searchTerm}
                            onChange={(e) => handleWeeklyColSearchChange(colKey, e.target.value)}
                            className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/50 text-xs outline-none focus:ring-2 focus:ring-primary focus:bg-white text-slate-800 dark:text-slate-200 transition-all placeholder:text-slate-400"
                            autoFocus
                        />
                    </div>

                    {/* Seleccionar Todos */}
                    <div className="flex items-center justify-between py-1 px-1.5 mb-1 bg-slate-50 dark:bg-slate-900/40 rounded-md border border-slate-100 dark:border-slate-700/60">
                        <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer text-slate-700 dark:text-slate-300 select-none">
                            <input
                                type="checkbox"
                                checked={isAllSelected}
                                ref={(el) => {
                                    if (el) el.indeterminate = !isAllSelected && isSomeSelected;
                                }}
                                onChange={(e) => handleWeeklyColSelectAll(colKey, e.target.checked, allDistinctValues)}
                                className="rounded border-slate-300 dark:border-slate-600 text-primary focus:ring-primary h-3.5 w-3.5 cursor-pointer"
                            />
                            <span>(Seleccionar Todo)</span>
                        </label>
                        <span className="text-[10px] text-slate-400 font-mono">
                            {selectedList.length}/{allDistinctValues.length}
                        </span>
                    </div>

                    {/* Lista de Checkboxes */}
                    <div className="max-h-48 overflow-y-auto space-y-0.5 pr-1 py-0.5 border border-slate-100 dark:border-slate-700/50 rounded-lg">
                        {filteredValues.length === 0 ? (
                            <div className="text-xs text-slate-400 italic py-3 text-center">No hay coincidencias</div>
                        ) : (
                            filteredValues.map(val => {
                                const isChecked = selectedList.includes(val);
                                return (
                                    <label key={val} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 py-1 px-2 rounded transition-all select-none">
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={(e) => handleWeeklyColCheckboxToggle(colKey, val, e.target.checked, allDistinctValues)}
                                            className="rounded border-slate-300 dark:border-slate-600 text-primary focus:ring-primary h-3.5 w-3.5 cursor-pointer"
                                        />
                                        <span className="truncate flex-1 text-slate-700 dark:text-slate-300" title={val}>{val}</span>
                                    </label>
                                );
                            })
                        )}
                    </div>
                </div>
            </>
        );
    };

    const renderWeeklyHeaderCell = (colKey: WeeklyAllocColKey, label: string, extraClasses = "") => {
        const isSorted = weeklySortColumn === colKey;
        const currentFilter = weeklyColFilters[colKey];
        const isFilterActive = !!currentFilter?.search || (currentFilter?.selected !== undefined);

        return (
            <th className={`px-4 py-2 text-xs font-semibold text-slate-500 uppercase relative select-none ${extraClasses}`}>
                <div className="flex items-center justify-between gap-1">
                    <span
                        onClick={() => handleWeeklySortClick(colKey)}
                        className="hover:text-slate-800 dark:hover:text-white cursor-pointer flex items-center transition-colors truncate group"
                        title={`Ordenar por ${label} (${isSorted ? (weeklySortDirection === 'asc' ? 'Ascendente' : 'Descendente') : 'Click para ordenar'})`}
                    >
                        <span>{label}</span>
                        <span className={`material-symbols-outlined text-[14px] ml-1 shrink-0 transition-opacity ${isSorted ? 'opacity-100 text-primary font-bold' : 'opacity-40 group-hover:opacity-100'
                            }`}>
                            {isSorted
                                ? (weeklySortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward')
                                : 'unfold_more'}
                        </span>
                    </span>

                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setWeeklyActiveFilterDropdown(prev => prev === colKey ? null : colKey);
                        }}
                        className={`p-1 rounded-md transition-all shrink-0 cursor-pointer ${isFilterActive
                                ? 'text-primary font-bold bg-primary/10 dark:bg-primary/20 ring-1 ring-primary/40'
                                : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700'
                            }`}
                        title={`Filtrar y opciones de ${label}`}
                    >
                        <span className="material-symbols-outlined text-[15px] leading-none">
                            {isFilterActive ? 'filter_alt' : 'filter_list'}
                        </span>
                    </button>
                </div>
                {renderWeeklyFilterDropdown(colKey)}
            </th>
        );
    };

    const isPlanEvaluationDone = (planId: number): boolean => {
        try {
            const pId = projectInfo?.name || 'default';
            const saved = localStorage.getItem(`klydapp_sc_evaluations_${pId}`);
            if (saved) {
                const evals = JSON.parse(saved);
                const planEvals = evals[planId];
                if (planEvals && Object.keys(planEvals).length > 0) {
                    return Object.values(planEvals).some((v: any) => v && v.scores && Object.keys(v.scores).length > 0);
                }
            }
        } catch { }
        return false;
    };

    const isPlanAttendanceDone = (planId: number): boolean => {
        try {
            const pId = projectInfo?.name || 'default';
            const saved = localStorage.getItem(`klydapp_attendance_${pId}_plan_${planId}`);
            if (saved) {
                const sheet = JSON.parse(saved);
                return sheet?.rows && sheet.rows.some((r: any) => (r.name && r.name.trim() !== '') || r.signature);
            }
        } catch { }
        return false;
    };

    const isPlanMeetingMinutesDone = (planId: number): boolean => {
        try {
            const pId = projectInfo?.name || 'default';
            const saved = localStorage.getItem(`klydapp_meeting_minutes_${pId}_plan_${planId}`);
            if (saved) {
                const sheet = JSON.parse(saved);
                return sheet?.rows && sheet.rows.some((r: any) =>
                    (r.observation && r.observation.trim() !== '') ||
                    (r.responsibleName && r.responsibleName.trim() !== '') ||
                    (r.followUp && r.followUp.trim() !== '')
                );
            }
        } catch { }
        return false;
    };

    const isPlanPresentationDone = (planId: number): boolean => {
        try {
            const pId = projectInfo?.name || projectInfo?.id || 'default';
            const saved = localStorage.getItem(`klydapp_presentation_done_${pId}_plan_${planId}`);
            return saved === 'true';
        } catch { }
        return false;
    };

    const markPlanPresentationDone = (planId: number) => {
        try {
            const pId = projectInfo?.name || projectInfo?.id || 'default';
            localStorage.setItem(`klydapp_presentation_done_${pId}_plan_${planId}`, 'true');
        } catch { }
    };

    const evaluationConfig = useMemo<SubcontractorEvaluationConfig>(() => {
        try {
            const saved = localStorage.getItem('klydapp_sc_evaluation_config');
            if (saved) return JSON.parse(saved);
        } catch (e) {
            console.error(e);
        }
        return {
            areas: [],
            ranges: DEFAULT_EVALUATION_RANGES
        };
    }, [evalModalOpen]);
    React.useEffect(() => {
        const handleResize = () => setIsMobile(checkIsMobileEnv());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [checkIsMobileEnv]);

    // Responsables ordenados alfabéticamente A-Z para todas las listas desplegables
    const sortedResponsibles = useMemo(() => {
        return [...(responsibles || [])].sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })
        );
    }, [responsibles]);

    // Retroactive normalization for milestones (duration 0) in already imported projects
    React.useEffect(() => {
        if (!tasks || tasks.length === 0) return;

        let needsNormalize = false;
        const normalizeRecursive = (list: Task[]): Task[] => {
            return list.map(t => {
                let updated = { ...t };
                const hasChildren = t.children && t.children.length > 0;

                if (t.type === 'task' && !hasChildren) {
                    if (t.start && (t.duration === undefined || t.duration === null || !t.end)) {
                        updated.duration = 0;
                        updated.end = t.start;
                        needsNormalize = true;
                    }
                }

                if (t.children && t.children.length > 0) {
                    updated.children = normalizeRecursive(t.children);
                }
                return updated;
            });
        };

        const normalized = normalizeRecursive(tasks);
        if (needsNormalize) {
            console.log("[Retroactive Normalization] Fixing duration 0 milestones.");
            setTasks(normalized);
            if (forceSyncTasks) {
                forceSyncTasks(normalized).catch(err => {
                    console.error("Error syncing retroactively normalized tasks:", err);
                });
            }
        }
    }, [tasks, forceSyncTasks, setTasks]);



    // --- State ---
    const [activeTab, setActiveTab] = useState<'maestra' | 'intermedia' | 'semanal'>(() => {
        const isNativeOrMobile = typeof window !== 'undefined' && (
            Capacitor.isNativePlatform() ||
            document.documentElement.classList.contains('is-native') ||
            window.innerWidth < 768 ||
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
        );
        const saved = typeof window !== 'undefined' ? (sessionStorage.getItem('planning_activeTab') as any) : null;
        if (isNativeOrMobile) {
            // En móvil, la pestaña por defecto siempre es 'semanal'
            if (!saved || saved === 'maestra') return 'semanal';
            return saved;
        }
        return saved || 'maestra';
    });
    const [mobilePlanFilter, setMobilePlanFilter] = useState<'Todas' | 'En ejecución' | 'En revisión' | 'En planificación' | 'Cerrado'>('Todas');

    const matchesMobileStatusFilter = useCallback((status?: string, filter: string = 'Todas') => {
        if (filter === 'Todas') return true;
        if (!status) return false;
        const s = status.trim().toLowerCase();
        const f = filter.trim().toLowerCase();
        if (f.includes('ejecu')) return s.includes('ejecu');
        if (f.includes('revis')) return s.includes('revis');
        if (f.includes('planif')) return s.includes('planif');
        if (f.includes('cerrad')) return s.includes('cerrad');
        return s === f;
    }, []);

    const getMobileStatusBadge = useCallback((status?: string) => {
        const s = (status || '').trim().toLowerCase();
        if (s.includes('ejecu')) {
            return { label: 'En Ejecución', bg: '#DCFCE7', color: '#166534' };
        }
        if (s.includes('revis')) {
            return { label: 'En Revisión', bg: '#FEF3C7', color: '#92400E' };
        }
        if (s.includes('planif')) {
            return { label: 'En Planificación', bg: '#DBEAFE', color: '#1E40AF' };
        }
        return { label: 'Cerrado', bg: '#E2E8F0', color: '#475569' };
    }, []);

    const formatMobilePlanDateRange = useCallback((start?: string, end?: string): string => {
        if (!start && !end) return '-';
        const formatD = (dStr?: string) => {
            if (!dStr) return null;
            const parts = dStr.split('-');
            if (parts.length === 3) {
                const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
                const [y, m, d] = parts;
                const mIdx = parseInt(m, 10) - 1;
                const monthName = months[mIdx] || m;
                return { day: parseInt(d, 10), month: monthName, year: y };
            }
            return null;
        };
        const s = formatD(start);
        const e = formatD(end);
        if (s && e) {
            if (s.year === e.year) {
                return `${s.day} ${s.month} → ${e.day} ${e.month} ${e.year}`;
            }
            return `${s.day} ${s.month} ${s.year} → ${e.day} ${e.month} ${e.year}`;
        }
        return `${start || ''} al ${end || ''}`;
    }, []);

    const chronologicalLookaheads = useMemo(() => {
        // Orden canónico de lista: de la más reciente arriba a la más antigua abajo
        return [...lookaheadPlans].sort((a, b) => {
            const timeA = new Date(a.startDate || 0).getTime();
            const timeB = new Date(b.startDate || 0).getTime();
            if (timeB !== timeA) return timeB - timeA;
            return (b.id || '').localeCompare(a.id || '');
        });
    }, [lookaheadPlans]);

    const filteredLookaheadPlans = useMemo(() => {
        return chronologicalLookaheads.filter(p => matchesMobileStatusFilter(p.status, mobilePlanFilter));
    }, [chronologicalLookaheads, mobilePlanFilter, matchesMobileStatusFilter]);

    const chronologicalWeekly = useMemo(() => {
        return [...weeklyPlans].sort((a, b) => new Date(b.startDate || 0).getTime() - new Date(a.startDate || 0).getTime());
    }, [weeklyPlans]);

    const filteredWeeklyPlans = useMemo(() => {
        return chronologicalWeekly.filter(p => matchesMobileStatusFilter(p.status, mobilePlanFilter));
    }, [chronologicalWeekly, mobilePlanFilter, matchesMobileStatusFilter]);

    const [isGanttMenuOpen, setIsGanttMenuOpen] = useState(false);
    const [isAdicionalesMenuOpen, setIsAdicionalesMenuOpen] = useState(false);
    const [showPresentationEditor, setShowPresentationEditor] = useState(false);
    const [showPresentationListModal, setShowPresentationListModal] = useState(false);
    const [presentationTargetPlan, setPresentationTargetPlan] = useState<WeeklyPlan | null>(null);
    const [presentationInitialTemplate, setPresentationInitialTemplate] = useState<PresentationTemplate | undefined>(undefined);
    const [presentationEditorMode, setPresentationEditorMode] = useState<'config' | 'presentation'>('config');

    const handleOpenPresentationForPlan = React.useCallback(async (plan: WeeklyPlan) => {
        setPresentationTargetPlan(plan);
        setPresentationEditorMode('presentation');
        markPlanPresentationDone(plan.id);
        try {
            const templates = await fetchTemplates(projectInfo?.id || '');
            // Find template assigned specifically to 'semanal'
            const assignedTemplate = templates.find(t => t.category === 'semanal');
            setPresentationInitialTemplate(assignedTemplate || undefined);
        } catch (e) {
            console.error('Error fetching presentation template for weekly plan:', e);
            setPresentationInitialTemplate(undefined);
        }
        setShowPresentationEditor(true);
    }, [projectInfo?.id]);

    React.useEffect(() => {
        sessionStorage.setItem('planning_activeTab', activeTab);
    }, [activeTab]);

    React.useEffect(() => {
        if (activeTab === 'maestra' && !hasPermission('pm_gantt_edit', 'view') && !hasPermission('pm_reprog', 'view')) {
            if (hasPermission('pi_planificaciones', 'view')) setActiveTab('intermedia');
            else if (hasPermission('ps_planificaciones', 'view')) setActiveTab('semanal');
        }

        // Mobile redirect: if on mobile/native and activeTab is 'maestra', go to 'semanal' automatically.
        const isNativeOrMobile = Capacitor.isNativePlatform() || (typeof document !== 'undefined' && document.documentElement.classList.contains('is-native')) || window.innerWidth < 768;
        if (typeof window !== 'undefined' && isNativeOrMobile && activeTab === 'maestra') {
            setActiveTab('semanal');
        }
    }, [hasPermission, activeTab]);

    // Modes
    const [isMasterEditing, setIsMasterEditing] = useState<boolean>(() => {
        const stored = sessionStorage.getItem('planning_isMasterEditing');
        return stored === 'true';
    });
    const [activeTooltipAllocIdx, setActiveTooltipAllocIdx] = useState<number | null>(null);
    const [isWeeklyEditing, setIsWeeklyEditing] = useState<boolean>(() => {
        const stored = sessionStorage.getItem('planning_isWeeklyEditing');
        if (stored !== null) return stored === 'true';
        const storedPlanId = sessionStorage.getItem('planning_activePlanId');
        return storedPlanId !== null;
    });

    const [weeklyTaskSearchTerm, setWeeklyTaskSearchTerm] = useState<string>('');

    React.useEffect(() => {
        sessionStorage.setItem('planning_isMasterEditing', isMasterEditing ? 'true' : 'false');
    }, [isMasterEditing]);

    React.useEffect(() => {
        sessionStorage.setItem('planning_isWeeklyEditing', isWeeklyEditing ? 'true' : 'false');
    }, [isWeeklyEditing]);

    // Zoom and Calendar states
    const [zoomLevel, setZoomLevel] = useState<'days' | 'weeks' | 'months' | 'years'>('weeks');

    // Calendar Settings
    const [workingDays, setWorkingDays] = useState<boolean[]>(() => {
        const saved = localStorage.getItem(`gantt_working_days_${projectInfo?.id || 'default'}`);
        return saved ? JSON.parse(saved) : [false, true, true, true, true, true, false]; // Default Mon-Fri
    });

    const [holidays, setHolidays] = useState<{ date: string; name: string }[]>(() => {
        const saved = localStorage.getItem(`gantt_holidays_${projectInfo?.id || 'default'}`);
        return saved ? JSON.parse(saved) : [];
    });

    const [hoursPerDay, setHoursPerDay] = useState<number>(() => {
        const saved = localStorage.getItem(`gantt_hours_per_day_${projectInfo?.id || 'default'}`);
        return saved ? parseFloat(saved) : 8; // Default 8 hours/day
    });

    const [calendarModalOpen, setCalendarModalOpen] = useState(false);
    const [tempWorkingDays, setTempWorkingDays] = useState<boolean[]>([]);
    const [tempHolidays, setTempHolidays] = useState<{ date: string; name: string }[]>([]);
    const [tempHoursPerDay, setTempHoursPerDay] = useState<number>(8);

    useEffect(() => {
        if (calendarModalOpen) {
            setTempWorkingDays([...workingDays]);
            setTempHolidays(JSON.parse(JSON.stringify(holidays)));
            setTempHoursPerDay(hoursPerDay);
        }
    }, [calendarModalOpen, workingDays, holidays, hoursPerDay]);

    const zoomFocusRef = React.useRef<{ date: Date; mouseX: number } | null>(null);

    // Process tasks with Critical Path Method (CPM) dynamically, ensuring subtasks inherit parent dates
    const processedTasks = useMemo(() => {
        const cpmTasks = calculateCriticalPath(tasks, workingDays, holidays);
        // Regla: TODAS las subtareas semanales heredan las fechas de inicio y termino de la tarea madre
        const inheritDatesRecursive = (list: Task[], parentStart?: string, parentEnd?: string, parentDuration?: number): Task[] => {
            return list.map(t => {
                const currentStart = t.isWeeklySubtask && parentStart ? parentStart : t.start;
                const currentEnd = t.isWeeklySubtask && parentEnd ? parentEnd : t.end;
                const currentDuration = t.isWeeklySubtask && parentDuration !== undefined ? parentDuration : t.duration;
                const updatedChildren = t.children ? inheritDatesRecursive(t.children, currentStart, currentEnd, currentDuration) : [];
                return {
                    ...t,
                    start: currentStart,
                    end: currentEnd,
                    duration: currentDuration,
                    children: updatedChildren
                };
            });
        };
        return inheritDatesRecursive(cpmTasks);
    }, [tasks, workingDays, holidays]);

    const flattenedTasks = useMemo(() => flattenTasks(processedTasks), [processedTasks]);

    const getCriticalPathDelayAtDate = React.useCallback((dateStr: string): number => {
        return calculateCriticalPathDelayAtDate(
            tasks,
            dateStr,
            units,
            matrixData,
            protocolConfigs,
            projectInfo,
            savedProtocols,
            weeklyPlans,
            workingDays,
            holidays
        );
    }, [tasks, units, matrixData, protocolConfigs, projectInfo, savedProtocols, weeklyPlans, workingDays, holidays]);

    const projectStatus = useMemo(() => {
        if (!progressHistory || progressHistory.length === 0) return { label: 'Al día', color: 'bg-[#52be80]', textColor: 'text-green-300', icon: 'check_circle', delay: 0 };
        const lastRecord = [...progressHistory].sort((a, b) => b.date.localeCompare(a.date))[0];
        const liveDelayRC = getCriticalPathDelayAtDate(lastRecord.date);
        const delay = Math.max(liveDelayRC, lastRecord.delayCurvaS || 0);

        if (delay <= 0) {
            return { label: 'Al día', color: 'bg-[#52be80] border-[#229954] text-white', icon: 'check_circle', delay: 0 };
        } else if (delay <= 9) {
            return { label: 'En Riesgo', color: 'bg-[#f4d03f] border-[#d4ac0d] text-slate-900', icon: 'warning', delay };
        } else {
            return { label: 'Atrasado', color: 'animate-pulse bg-[#ff0000] border-[#cc0000] text-yellow-300', icon: 'error', delay };
        }
    }, [progressHistory, getCriticalPathDelayAtDate]);

    const visibleFlattenedTasks = useMemo(() => {
        const getVisible = (list: Task[]): Task[] => {
            let res: Task[] = [];
            list.forEach(t => {
                if (t.isDeleted) return;
                if (activeTab !== 'semanal' && t.isWeeklySubtask) return;
                res.push(t);
                if (t.expanded && t.children) {
                    res = res.concat(getVisible(t.children));
                }
            });
            return res;
        };
        return getVisible(processedTasks);
    }, [processedTasks, activeTab]);

    const filterTasksBySearch = (taskList: Task[], query: string): Task[] => {
        if (!query.trim()) return taskList;
        const lowerQuery = query.toLowerCase().trim();

        const filterTree = (list: Task[]): Task[] => {
            return list.reduce((acc: Task[], t: Task) => {
                const nameMatch = t.name ? t.name.toLowerCase().includes(lowerQuery) : false;
                const idMatch = t.id ? t.id.toLowerCase().includes(lowerQuery) : false;
                const origIdMatch = t.id_original_project ? String(t.id_original_project).toLowerCase().includes(lowerQuery) : false;
                const selfMatches = nameMatch || idMatch || origIdMatch;
                const matchingChildren = t.children ? filterTree(t.children) : [];

                if (selfMatches || matchingChildren.length > 0) {
                    acc.push({
                        ...t,
                        expanded: true,
                        children: selfMatches ? t.children : matchingChildren
                    });
                }
                return acc;
            }, []);
        };

        return filterTree(taskList);
    };

    const tasksToDisplayInLeftTable = useMemo(() => {
        if (activeTab === 'semanal' && weeklyTaskSearchTerm.trim() !== '') {
            return filterTasksBySearch(processedTasks, weeklyTaskSearchTerm);
        }
        return processedTasks;
    }, [processedTasks, activeTab, weeklyTaskSearchTerm]);

    // Timeline Config (Dynamic Range by Zoom Level)
    const timelineConfig = useMemo(() => {
        const allTasks = flattenedTasks;
        const today = new Date();
        if (allTasks.length === 0) {
            const start = new Date(today.getFullYear(), today.getMonth(), 1);
            const end = new Date(today.getFullYear(), today.getMonth() + 2, 1);
            return { start, end };
        }

        const startDates = allTasks
            .map(t => t.start ? new Date(t.start + "T12:00:00") : null)
            .filter((d): d is Date => d !== null && !isNaN(d.getTime()));

        const endDates = allTasks
            .map(t => t.end ? new Date(t.end + "T12:00:00") : null)
            .filter((d): d is Date => d !== null && !isNaN(d.getTime()));

        if (startDates.length === 0) {
            const start = new Date(today.getFullYear(), today.getMonth(), 1);
            const end = new Date(today.getFullYear(), today.getMonth() + 2, 1);
            return { start, end };
        }

        const minDate = new Date(Math.min(...startDates.map(d => d.getTime())));
        const maxDate = new Date(Math.max(...(endDates.length > 0 ? endDates : startDates).map(d => d.getTime())));

        const viewStart = new Date(minDate);
        const viewEnd = new Date(maxDate);

        if (zoomLevel === 'days') {
            viewStart.setDate(viewStart.getDate() - 7);
            viewEnd.setDate(viewEnd.getDate() + 7);
            const diffDays = Math.ceil((viewEnd.getTime() - viewStart.getTime()) / (1000 * 60 * 60 * 24));
            if (diffDays < 15) {
                viewEnd.setDate(viewEnd.getDate() + (15 - diffDays));
            }
        } else if (zoomLevel === 'weeks') {
            viewStart.setMonth(viewStart.getMonth() - 1);
            viewEnd.setMonth(viewEnd.getMonth() + 1);
        } else if (zoomLevel === 'months') {
            viewStart.setMonth(viewStart.getMonth() - 3);
            viewEnd.setMonth(viewEnd.getMonth() + 3);
        } else { // 'years'
            viewStart.setFullYear(viewStart.getFullYear() - 1);
            viewEnd.setFullYear(viewEnd.getFullYear() + 1);
        }

        // Normalize starts
        if (zoomLevel === 'days' || zoomLevel === 'weeks') {
            const day = viewStart.getDay();
            const diff = viewStart.getDate() - day + (day === 0 ? -6 : 1);
            viewStart.setDate(diff);
        } else if (zoomLevel === 'months') {
            viewStart.setDate(1);
        } else { // 'years'
            viewStart.setMonth(0, 1);
        }

        return { start: viewStart, end: viewEnd };
    }, [flattenedTasks, zoomLevel]);

    const gridStartDate = timelineConfig.start;
    const viewStartDate = timelineConfig.start;
    const viewEndDate = timelineConfig.end;

    const timelineColumns = useMemo(() => {
        const cols = [];
        let curr = new Date(viewStartDate);
        if (zoomLevel === 'days') {
            while (curr <= viewEndDate) {
                cols.push(new Date(curr));
                curr.setDate(curr.getDate() + 1);
            }
        } else if (zoomLevel === 'weeks') {
            while (curr <= viewEndDate) {
                cols.push(new Date(curr));
                curr.setDate(curr.getDate() + 7);
            }
        } else if (zoomLevel === 'months') {
            while (curr <= viewEndDate) {
                cols.push(new Date(curr));
                curr.setMonth(curr.getMonth() + 1);
            }
        } else { // 'years'
            while (curr <= viewEndDate) {
                cols.push(new Date(curr));
                curr.setFullYear(curr.getFullYear() + 1);
            }
        }
        return cols;
    }, [viewStartDate, viewEndDate, zoomLevel]);

    const timelineHeaders = useMemo(() => {
        const headers: { label: string; width: number }[] = [];
        if (zoomLevel === 'days') {
            let currentMonthLabel = "";
            let dayCount = 0;
            const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

            timelineColumns.forEach((date, idx) => {
                const label = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
                if (label !== currentMonthLabel) {
                    if (currentMonthLabel !== "") {
                        headers.push({ label: currentMonthLabel, width: dayCount * 30 });
                    }
                    currentMonthLabel = label;
                    dayCount = 1;
                } else {
                    dayCount++;
                }
                if (idx === timelineColumns.length - 1) {
                    headers.push({ label: currentMonthLabel, width: dayCount * 30 });
                }
            });
        } else if (zoomLevel === 'weeks') {
            let currentMonthLabel = "";
            let weekCount = 0;
            const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

            timelineColumns.forEach((date, idx) => {
                const label = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
                if (label !== currentMonthLabel) {
                    if (currentMonthLabel !== "") {
                        headers.push({ label: currentMonthLabel, width: weekCount * 100 });
                    }
                    currentMonthLabel = label;
                    weekCount = 1;
                } else {
                    weekCount++;
                }
                if (idx === timelineColumns.length - 1) {
                    headers.push({ label: currentMonthLabel, width: weekCount * 100 });
                }
            });
        } else if (zoomLevel === 'months') {
            let currentYearLabel = "";
            let monthCount = 0;

            timelineColumns.forEach((date, idx) => {
                const label = `${date.getFullYear()}`;
                if (label !== currentYearLabel) {
                    if (currentYearLabel !== "") {
                        headers.push({ label: currentYearLabel, width: monthCount * 120 });
                    }
                    currentYearLabel = label;
                    monthCount = 1;
                } else {
                    monthCount++;
                }
                if (idx === timelineColumns.length - 1) {
                    headers.push({ label: currentYearLabel, width: monthCount * 120 });
                }
            });
        } else { // 'years'
            headers.push({ label: "Línea de Tiempo Macro", width: timelineColumns.length * 250 });
        }
        return headers;
    }, [timelineColumns, zoomLevel]);

    // --- Calendar & Date Math Helpers ---
    const getLocalDateStr = (date: Date): string => {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const formatDateToShow = (dateStr: string | undefined): string => {
        if (!dateStr) return "-";
        if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) return dateStr;
        const parts = dateStr.split('-');
        if (parts.length === 3) {
            const [year, month, day] = parts;
            if (year.length === 4) {
                return `${day}-${month}-${year}`;
            }
        }
        return dateStr;
    };

    const getDayAfter = (dateStr: string): string => {
        const date = new Date(dateStr + "T12:00:00");
        date.setDate(date.getDate() + 1);
        return getLocalDateStr(date);
    };

    const isWorkingDay = (date: Date, currentWorkingDays = workingDays, currentHolidays = holidays): boolean => {
        const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
        if (!currentWorkingDays[dayOfWeek]) return false;

        const dateStr = getLocalDateStr(date);
        return !currentHolidays.some(h => h.date === dateStr);
    };

    const calculateEndDate = (start: string, duration: number, currentWorkingDays = workingDays, currentHolidays = holidays): string => {
        let currDate = new Date(start + "T12:00:00");
        while (!isWorkingDay(currDate, currentWorkingDays, currentHolidays)) {
            currDate.setDate(currDate.getDate() + 1);
        }

        let remaining = duration - 1;
        while (remaining > 0) {
            currDate.setDate(currDate.getDate() + 1);
            if (isWorkingDay(currDate, currentWorkingDays, currentHolidays)) {
                remaining--;
            }
        }
        return getLocalDateStr(currDate);
    };

    const calculateStartDate = (end: string, duration: number, currentWorkingDays = workingDays, currentHolidays = holidays): string => {
        let currDate = new Date(end + "T12:00:00");
        while (!isWorkingDay(currDate, currentWorkingDays, currentHolidays)) {
            currDate.setDate(currDate.getDate() - 1);
        }

        let remaining = duration - 1;
        while (remaining > 0) {
            currDate.setDate(currDate.getDate() - 1);
            if (isWorkingDay(currDate, currentWorkingDays, currentHolidays)) {
                remaining--;
            }
        }
        return getLocalDateStr(currDate);
    };

    const calculateWorkingDaysDuration = (start: string, end: string, currentWorkingDays = workingDays, currentHolidays = holidays): number => {
        const startD = new Date(start + "T12:00:00");
        const endD = new Date(end + "T12:00:00");
        if (startD > endD) return 0;

        let count = 0;
        let curr = new Date(startD);
        while (curr <= endD) {
            if (isWorkingDay(curr, currentWorkingDays, currentHolidays)) {
                count++;
            }
            curr.setDate(curr.getDate() + 1);
        }
        return count;
    };

    const getNextWorkingDay = (dateStr: string, currentWorkingDays = workingDays, currentHolidays = holidays): string => {
        const date = new Date(dateStr + "T12:00:00");
        date.setDate(date.getDate() + 1);
        while (!isWorkingDay(date, currentWorkingDays, currentHolidays)) {
            date.setDate(date.getDate() + 1);
        }
        return getLocalDateStr(date);
    };

    const shiftWorkingDays = (dateStr: string, days: number, currentWorkingDays = workingDays, currentHolidays = holidays): string => {
        let currDate = new Date(dateStr + "T12:00:00");
        if (days === 0) return getLocalDateStr(currDate);
        const step = days > 0 ? 1 : -1;
        let remaining = Math.abs(days);
        while (remaining > 0) {
            currDate.setDate(currDate.getDate() + step);
            if (isWorkingDay(currDate, currentWorkingDays, currentHolidays)) {
                remaining--;
            }
        }
        return getLocalDateStr(currDate);
    };

    const adjustTaskToCalendar = (task: Task, currentWorkingDays = workingDays, currentHolidays = holidays): Task => {
        if (task.type === 'folder') {
            if (task.children) {
                task.children = task.children.map(child => adjustTaskToCalendar(child, currentWorkingDays, currentHolidays));
            }
            return task;
        }

        if (!task.start || !task.end) return task;

        let startD = new Date(task.start + "T12:00:00");
        let shifted = false;
        while (!isWorkingDay(startD, currentWorkingDays, currentHolidays)) {
            startD.setDate(startD.getDate() + 1);
            shifted = true;
        }
        if (shifted) {
            task.start = getLocalDateStr(startD);
        }

        let duration = (task.duration !== undefined && task.duration !== null)
            ? task.duration
            : calculateWorkingDaysDuration(task.start, task.end, currentWorkingDays, currentHolidays);
        if (duration < 0) duration = 0;
        task.duration = duration;

        if (duration === 0) {
            task.end = task.start;
        } else {
            task.end = calculateEndDate(task.start, duration, currentWorkingDays, currentHolidays);
        }
        return task;
    };

    const realignTasksToCalendar = (list: Task[], currentWorkingDays: boolean[], currentHolidays: { date: string; name: string }[]): Task[] => {
        const adjustRecursive = (tasksList: Task[]): Task[] => {
            return tasksList.map(t => {
                if (t.type === 'folder') {
                    return {
                        ...t,
                        children: t.children ? adjustRecursive(t.children) : undefined
                    };
                }
                if (!t.start || !t.end) return t;

                let sDate = new Date(t.start + "T12:00:00");
                while (!isWorkingDay(sDate, currentWorkingDays, currentHolidays)) {
                    sDate.setDate(sDate.getDate() + 1);
                }
                const newStart = getLocalDateStr(sDate);
                const duration = t.duration || 1;
                const newEnd = calculateEndDate(newStart, duration, currentWorkingDays, currentHolidays);

                return {
                    ...t,
                    start: newStart,
                    end: newEnd
                };
            });
        };

        const adjusted = adjustRecursive(list);
        return syncDependencies(adjusted, undefined, currentWorkingDays, currentHolidays);
    };

    // --- Coordinate Math Helpers ---
    const getDateX = (dateStrOrObj: string | Date, currentZoomLevel = zoomLevel, currentGridStartDate?: Date): number => {
        const refStartDate = currentGridStartDate || gridStartDate;
        const date = typeof dateStrOrObj === 'string' ? new Date(dateStrOrObj + 'T12:00:00') : dateStrOrObj;
        if (isNaN(date.getTime())) return 0;

        if (currentZoomLevel === 'days') {
            const diffTime = date.getTime() - refStartDate.getTime();
            const diffDays = diffTime / (1000 * 60 * 60 * 24);
            return diffDays * 30;
        } else if (currentZoomLevel === 'weeks') {
            const diffTime = date.getTime() - refStartDate.getTime();
            const diffDays = diffTime / (1000 * 60 * 60 * 24);
            return (diffDays / 7) * 100;
        } else if (currentZoomLevel === 'months') {
            const monthDiff = (date.getFullYear() - refStartDate.getFullYear()) * 12 + (date.getMonth() - refStartDate.getMonth());
            const day = date.getDate();
            const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
            return monthDiff * 120 + ((day - 1) / daysInMonth) * 120;
        } else { // 'years'
            const yearDiff = date.getFullYear() - refStartDate.getFullYear();
            const startOfYear = new Date(date.getFullYear(), 0, 1);
            const endOfYear = new Date(date.getFullYear() + 1, 0, 1);
            const pct = (date.getTime() - startOfYear.getTime()) / (endOfYear.getTime() - startOfYear.getTime());
            return yearDiff * 250 + pct * 250;
        }
    };

    const getXDate = (x: number, currentZoomLevel = zoomLevel, currentGridStartDate?: Date): Date => {
        const refStartDate = currentGridStartDate || gridStartDate;
        if (currentZoomLevel === 'days') {
            const diffDays = x / 30;
            return new Date(refStartDate.getTime() + diffDays * 24 * 60 * 60 * 1000);
        } else if (currentZoomLevel === 'weeks') {
            const diffDays = (x / 100) * 7;
            return new Date(refStartDate.getTime() + diffDays * 24 * 60 * 60 * 1000);
        } else if (currentZoomLevel === 'months') {
            const monthDiff = Math.floor(x / 120);
            const pct = (x % 120) / 120;
            const targetMonth = new Date(refStartDate.getFullYear(), refStartDate.getMonth() + monthDiff, 1);
            const daysInMonth = new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0).getDate();
            const day = Math.max(1, Math.min(daysInMonth, 1 + Math.round(pct * daysInMonth)));
            return new Date(targetMonth.getFullYear(), targetMonth.getMonth(), day, 12, 0, 0);
        } else { // 'years'
            const yearDiff = Math.floor(x / 250);
            const pct = (x % 250) / 250;
            const targetYear = refStartDate.getFullYear() + yearDiff;
            const startOfYear = new Date(targetYear, 0, 1).getTime();
            const endOfYear = new Date(targetYear + 1, 0, 1).getTime();
            const dateTime = startOfYear + pct * (endOfYear - startOfYear);
            return new Date(dateTime);
        }
    };

    React.useEffect(() => {
        const container = rightScrollRef.current;
        if (!container) return;

        const handleWheel = (e: WheelEvent) => {
            if (e.ctrlKey) {
                e.preventDefault(); // Bloquea por completo el zoom nativo del navegador, manteniendo fijo el tamaño de la interfaz general al 100%

                const rect = container.getBoundingClientRect();
                const mouseX = e.clientX - rect.left;
                const contentX = mouseX + container.scrollLeft;

                const focusDate = getXDate(contentX, zoomLevel, gridStartDate);

                const isZoomIn = e.deltaY < 0;
                const zoomOrder: ('days' | 'weeks' | 'months' | 'years')[] = ['days', 'weeks', 'months', 'years'];
                const currentIndex = zoomOrder.indexOf(zoomLevel);

                let nextIndex = currentIndex;
                if (isZoomIn && currentIndex > 0) {
                    nextIndex = currentIndex - 1;
                } else if (!isZoomIn && currentIndex < zoomOrder.length - 1) {
                    nextIndex = currentIndex + 1;
                }

                const nextZoomLevel = zoomOrder[nextIndex];
                if (nextZoomLevel !== zoomLevel) {
                    zoomFocusRef.current = { date: focusDate, mouseX };
                    setZoomLevel(nextZoomLevel);
                }
            }
        };

        container.addEventListener('wheel', handleWheel, { passive: false });
        return () => {
            container.removeEventListener('wheel', handleWheel);
        };
    }, [zoomLevel, gridStartDate]);

    React.useLayoutEffect(() => {
        if (zoomFocusRef.current && rightScrollRef.current) {
            const container = rightScrollRef.current;
            const { date, mouseX } = zoomFocusRef.current;
            const newContentX = getDateX(date, zoomLevel, gridStartDate);
            const newScrollLeft = newContentX - mouseX;
            container.scrollLeft = Math.max(0, newScrollLeft);
            zoomFocusRef.current = null;
        }
    }, [zoomLevel, gridStartDate]);

    const handleSaveCalendar = () => {
        setWorkingDays(tempWorkingDays);
        setHolidays(tempHolidays);
        setHoursPerDay(tempHoursPerDay);

        localStorage.setItem(`gantt_working_days_${projectInfo?.id || 'default'}`, JSON.stringify(tempWorkingDays));
        localStorage.setItem(`gantt_holidays_${projectInfo?.id || 'default'}`, JSON.stringify(tempHolidays));
        localStorage.setItem(`gantt_hours_per_day_${projectInfo?.id || 'default'}`, tempHoursPerDay.toString());

        setTasks(prev => {
            return realignTasksToCalendar(prev, tempWorkingDays, tempHolidays);
        });

        addActivity({
            type: 'planning',
            title: 'Calendario laboral actualizado',
            description: `Se ha modificado la jornada estándar (${tempHoursPerDay} hrs/día) y la lista de días festivos del proyecto.`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });

        setCalendarModalOpen(false);
    };

    const handleExportToMSProject = () => {
        interface ExportTaskItem {
            uid: number;
            id: number;
            name: string;
            start: string;
            end: string;
            duration: number;
            progress: number;
            isSummary: boolean;
            outlineLevel: number;
            outlineNumber: string;
            predecessors: { fromUid: number; type: number; lag: number }[];
        }

        const list: ExportTaskItem[] = [];
        let nextUid = 1;
        const origIdToUidMap = new Map<number | string, number>();

        const buildExportList = (tasksList: Task[], level = 1, parentPrefix = "") => {
            tasksList.forEach((t, index) => {
                const currentIndexStr = parentPrefix ? `${parentPrefix}.${index + 1}` : `${index + 1}`;
                const currentUid = typeof t.id_original_project === 'number' ? t.id_original_project : nextUid++;
                if (t.id_original_project !== undefined) {
                    origIdToUidMap.set(t.id_original_project, currentUid);
                } else {
                    origIdToUidMap.set(currentUid, currentUid);
                }

                const hasChildren = t.children && t.children.length > 0;
                const startStr = t.start || getLocalDateStr(new Date());
                const endStr = t.end || startStr;

                const item: ExportTaskItem = {
                    uid: currentUid,
                    id: currentUid,
                    name: t.name,
                    start: startStr,
                    end: endStr,
                    duration: t.duration || 1,
                    progress: t.progress || 0,
                    isSummary: t.type === 'folder' || hasChildren,
                    outlineLevel: level,
                    outlineNumber: currentIndexStr,
                    predecessors: []
                };

                (t.predecessors || []).forEach(pStr => {
                    const parsed = parseDependencyLink(pStr);
                    if (parsed) {
                        const predOrigId = parsed.id;
                        const typeStr = parsed.type;

                        let typeCode = 1; // FS
                        if (typeStr === "FF") typeCode = 0;
                        if (typeStr === "SF") typeCode = 2;
                        if (typeStr === "SS") typeCode = 3;

                        const mappedFromUid = origIdToUidMap.get(predOrigId) || nextUid;
                        item.predecessors.push({ fromUid: mappedFromUid, type: typeCode, lag: parsed.lag });
                    }
                });

                list.push(item);

                if (t.children && t.children.length > 0) {
                    buildExportList(t.children, level + 1, currentIndexStr);
                }
            });
        };

        buildExportList(tasks);

        list.forEach(item => {
            item.predecessors = item.predecessors.map(p => {
                const resolvedUid = origIdToUidMap.get(p.fromUid);
                return {
                    fromUid: resolvedUid || p.fromUid,
                    type: p.type,
                    lag: p.lag
                };
            }).filter(p => origIdToUidMap.has(p.fromUid));
        });

        const weekDaysXml = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'].map((dayName, idx) => {
            const isWorking = workingDays[idx];
            const dayNum = idx + 1;
            if (isWorking) {
                return `
        <WeekDay>
            <DayType>${dayNum}</DayType>
            <DayWorking>1</DayWorking>
            <WorkingTimes>
                <WorkingTime>
                    <FromTime>08:00:00</FromTime>
                    <ToTime>12:00:00</ToTime>
                </WorkingTime>
                <WorkingTime>
                    <FromTime>13:00:00</FromTime>
                    <ToTime>17:00:00</ToTime>
                </WorkingTime>
            </WorkingTimes>
        </WeekDay>`;
            } else {
                return `
        <WeekDay>
            <DayType>${dayNum}</DayType>
            <DayWorking>0</DayWorking>
        </WeekDay>`;
            }
        }).join('');

        const exceptionsXml = holidays.map((h) => `
        <Exception>
            <EnteredByOccurrences>0</EnteredByOccurrences>
            <TimePeriod>
                <From>${h.date}T08:00:00</From>
                <To>${h.date}T17:00:00</To>
            </TimePeriod>
            <Occurrences>1</Occurrences>
            <Name>${escapeXml(h.name)}</Name>
            <Type>1</Type>
            <DayWorking>0</DayWorking>
        </Exception>`).join('');

        const tasksXml = list.map(item => {
            const durationHours = item.duration * 8;
            const durationFormat = `PT${durationHours}H0M0S`;

            const predecessorLinksXml = item.predecessors.map(p => `
            <PredecessorLink>
                <PredecessorUID>${p.fromUid}</PredecessorUID>
                <Type>${p.type}</Type>
                <LinkLag>${(p.lag || 0) * 4800}</LinkLag>
                <LagFormat>7</LagFormat>
            </PredecessorLink>`).join('');

            return `
        <Task>
            <UID>${item.uid}</UID>
            <ID>${item.id}</ID>
            <Name>${escapeXml(item.name)}</Name>
            <Type>0</Type>
            <Manual>0</Manual>
            <PercentComplete>${item.progress}</PercentComplete>
            <Start>${item.start}T08:00:00</Start>
            <Finish>${item.end}T17:00:00</Finish>
            <Duration>${durationFormat}</Duration>
            <DurationFormat>7</DurationFormat>
            <OutlineLevel>${item.outlineLevel}</OutlineLevel>
            <OutlineNumber>${item.outlineNumber}</OutlineNumber>
            <Summary>${item.isSummary ? 1 : 0}</Summary>
            ${predecessorLinksXml}
        </Task>`;
        }).join('');

        const projectXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Project xmlns="http://schemas.microsoft.com/project">
    <Name>${escapeXml(projectInfo.name)}</Name>
    <Title>${escapeXml(projectInfo.name)}</Title>
    <StartDate>${gridStartDate ? getLocalDateStr(gridStartDate) : getLocalDateStr(new Date())}T08:00:00</StartDate>
    <CalendarUID>1</CalendarUID>
    <Calendars>
        <Calendar>
            <UID>1</UID>
            <Name>Calendario Laboral</Name>
            <IsBaseCalendar>1</IsBaseCalendar>
            <WeekDays>
                ${weekDaysXml}
            </WeekDays>
            <Exceptions>
                ${exceptionsXml}
            </Exceptions>
        </Calendar>
    </Calendars>
    <Tasks>
        ${tasksXml}
    </Tasks>
</Project>`;

        function escapeXml(unsafe: string): string {
            return unsafe.replace(/[<>&'"]/g, (c) => {
                switch (c) {
                    case '<': return '&lt;';
                    case '>': return '&gt;';
                    case '&': return '&amp;';
                    case '\'': return '&apos;';
                    case '"': return '&quot;';
                    default: return c;
                }
            });
        }

        const blob = new Blob([projectXml], { type: 'application/xml' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Carta_Gantt_${projectInfo.name.replace(/\s+/g, '_')}.xml`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        addActivity({
            type: 'planning',
            title: 'Carta Gantt Exportada',
            description: 'Se exportó la planificación maestra en formato XML compatible con Microsoft Project (MSPDI).',
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });

        setExportModalOpen(false);
    };

    // Tarea column resizing and left pane splitter states
    const [tareaColumnWidth, setTareaColumnWidth] = useState<number | null>(null);
    const [leftPaneWidth, setLeftPaneWidth] = useState<number | null>(null);

    const handleResizeMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        const headerElement = document.getElementById('planning-tarea-header');
        const startWidth = headerElement ? headerElement.getBoundingClientRect().width : 250;
        const startX = e.clientX;

        const handleMouseMove = (moveEvent: MouseEvent) => {
            const newWidth = Math.max(150, Math.min(800, startWidth + (moveEvent.clientX - startX)));
            setTareaColumnWidth(newWidth);
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleSplitterMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        const paneElement = document.getElementById('planning-left-pane');
        const startWidth = paneElement ? paneElement.getBoundingClientRect().width : 480;
        const startX = e.clientX;

        const handleMouseMove = (moveEvent: MouseEvent) => {
            const maxAllowedWidth = Math.max(800, window.innerWidth * 0.75);
            const newWidth = Math.max(250, Math.min(maxAllowedWidth, startWidth + (moveEvent.clientX - startX)));
            setLeftPaneWidth(newWidth);
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    // States for Warning on Exit without Saving
    const [showExitWarningModal, setShowExitWarningModal] = useState(false);
    const [exitWarningTarget, setExitWarningTarget] = useState<'semanal' | 'intermedia' | null>(null);
    const [weeklyPlansBackup, setWeeklyPlansBackup] = useState<WeeklyPlan[] | null>(null);
    const [lookaheadPlansBackup, setLookaheadPlansBackup] = useState<LookaheadPlan[] | null>(null);
    const [isBreakdownMode, setIsBreakdownMode] = useState(false);
    const [isITEView, setIsITEView] = useState(false); // NEW: ITE View State
    const [showGanttSuggestions, setShowGanttSuggestions] = useState(false);
    const [showWeeklyDates, setShowWeeklyDates] = useState(false); // Toggle to show start and end dates next to % Avance in Weekly mode

    // Data Store for Weekly Plans is now in props (weeklyPlans)
    const [graphicMode, setGraphicMode] = useState(false);
    const [weeklyGraphicMode, setWeeklyGraphicMode] = useState(false);
    const [weeklyGraphicViewType, setWeeklyGraphicViewType] = useState<'task' | 'planning'>('planning');
    const [isWeeklyGraphicExpanded, setIsWeeklyGraphicExpanded] = useState(false);
    const [isWeeklyGraphicDropdownOpen, setIsWeeklyGraphicDropdownOpen] = useState(false);
    const [isWeeklyHeaderDropdownOpen, setIsWeeklyHeaderDropdownOpen] = useState(false);
    const [graphicSelectedTaskId, setGraphicSelectedTaskId] = useState<string | null>(null);
    const [selectedGanttTaskId, setSelectedGanttTaskId] = useState<string | null>(null);

    // Calculate Task Path dependencies (Predecessors and Successors) - Direct relationships only (No recursion/cascading)
    const taskPathInfo = useMemo(() => {
        if (!selectedGanttTaskId) return null;
        if (activeTab !== 'maestra') return null;
        const selectedTask = flattenedTasks.find(t => t.id === selectedGanttTaskId);
        if (!selectedTask) return null;

        const selectedOrigId = selectedTask.id_original_project;
        const predecessors = new Set<number | string>();
        const successors = new Set<number | string>();

        const parseIds = (arr: any[] | undefined): (number | string)[] => {
            if (!arr) return [];
            const ids: (number | string)[] = [];
            arr.forEach(val => {
                if (typeof val === 'string') {
                    val.split(',').forEach(part => {
                        const match = part.trim().match(/^([\d.]+)/);
                        if (match) {
                            const rawId = match[1];
                            ids.push(/^\d+$/.test(rawId) ? parseInt(rawId, 10) : rawId);
                        }
                    });
                }
            });
            return ids;
        };

        // Populate direct predecessors from the selected task
        parseIds(selectedTask.predecessors).forEach(id => predecessors.add(id));

        // Populate direct successors from the selected task
        parseIds(selectedTask.successors).forEach(id => successors.add(id));

        return {
            selectedId: selectedTask.id,
            selectedOrigId,
            predecessors,
            successors
        };
    }, [selectedGanttTaskId, flattenedTasks]);
    const [activePlanId, setActivePlanId] = useState<number | null>(() => {
        const stored = sessionStorage.getItem('planning_activePlanId');
        return stored ? parseInt(stored) : null;
    });

    React.useEffect(() => {
        if (activePlanId !== null) {
            sessionStorage.setItem('planning_activePlanId', activePlanId.toString());
            const plan = weeklyPlans.find(p => p.id === activePlanId);
            if (plan && plan.status !== 'Cerrada') {
                const storedEditing = sessionStorage.getItem('planning_isWeeklyEditing');
                if (storedEditing === null) {
                    setIsWeeklyEditing(true);
                }
            }
        } else {
            sessionStorage.removeItem('planning_activePlanId');
            setIsWeeklyEditing(false);
            sessionStorage.removeItem('planning_isWeeklyEditing');
        }
    }, [activePlanId, weeklyPlans]);

    // Limpieza automática de grupos de restricciones por defecto legados ('Ingeniería' / 'Abastecimiento' con IDs 'cg1' / 'cg2')
    React.useEffect(() => {
        if (constraintGroups && constraintGroups.length > 0) {
            const hasLegacyDefaults = constraintGroups.some(g => g.id === 'cg1' || g.id === 'cg2' || (g.name === 'Ingeniería' && g.definitions.some(d => d.id === 'cd1')) || (g.name === 'Abastecimiento' && g.definitions.some(d => d.id === 'cd3')));
            if (hasLegacyDefaults) {
                const cleaned = constraintGroups.filter(g => g.id !== 'cg1' && g.id !== 'cg2' && !(g.name === 'Ingeniería' && g.definitions.some(d => d.id === 'cd1')) && !(g.name === 'Abastecimiento' && g.definitions.some(d => d.id === 'cd3')));
                setConstraintGroups(cleaned);
            }
        }
    }, [constraintGroups, setConstraintGroups]);

    // Helper to create empty additional row draft
    const createEmptyAdditionalRow = React.useCallback((): AdditionalTaskRowDraft => ({
        id: 'draft_' + Math.random().toString(36).substring(2, 9),
        levelId: '',
        unitId: '',
        additionalTaskName: '',
        responsibleId: '',
        days: { L: false, M: false, X: false, J: false, V: false, S: false, D: false }
    }), []);

    // States for Additional Tasks Modal
    const [additionalModalOpen, setAdditionalModalOpen] = useState(false);
    const [editingAdditionalIndex, setEditingAdditionalIndex] = useState<number | null>(null);
    const [additionalTaskRows, setAdditionalTaskRows] = useState<AdditionalTaskRowDraft[]>([
        { id: 'draft_init', levelId: '', unitId: '', additionalTaskName: '', responsibleId: '', days: { L: false, M: false, X: false, J: false, V: false, S: false, D: false } }
    ]);
    const [additionalBulkResponsibleId, setAdditionalBulkResponsibleId] = useState<string>('');
    const [selectedAdditionalRowIds, setSelectedAdditionalRowIds] = useState<Set<string>>(new Set());
    const [activeLevelPickerRowId, setActiveLevelPickerRowId] = useState<string | null>(null);
    const [activeUnitPickerRowId, setActiveUnitPickerRowId] = useState<string | null>(null);

    // States for Move Modal
    const [moveModalOpen, setMoveModalOpen] = useState(false);
    const [movingItemId, setMovingItemId] = useState<string | null>(null);

    const handleAddAdditionalRow = useCallback(() => {
        setAdditionalTaskRows(prev => [...prev, createEmptyAdditionalRow()]);
    }, [createEmptyAdditionalRow]);

    const handleRemoveAdditionalRow = useCallback((rowId: string) => {
        setAdditionalTaskRows(prev => {
            if (prev.length <= 1) return prev;
            return prev.filter(r => r.id !== rowId);
        });
        setSelectedAdditionalRowIds(prev => {
            const next = new Set(prev);
            next.delete(rowId);
            return next;
        });
    }, []);

    // Toggle row selection for bulk actions
    const handleToggleSelectAdditionalRow = useCallback((rowId: string) => {
        setSelectedAdditionalRowIds(prev => {
            const next = new Set(prev);
            if (next.has(rowId)) {
                next.delete(rowId);
            } else {
                next.add(rowId);
            }
            return next;
        });
    }, []);

    const handleToggleSelectAllAdditionalRows = useCallback(() => {
        setAdditionalTaskRows(currentRows => {
            setSelectedAdditionalRowIds(prevSelected => {
                if (prevSelected.size === currentRows.length) {
                    return new Set();
                } else {
                    return new Set(currentRows.map(r => r.id));
                }
            });
            return currentRows;
        });
    }, []);

    // Apply bulk responsible
    const handleApplyAdditionalBulkResponsible = useCallback((respIdToApply?: string) => {
        const respId = respIdToApply || additionalBulkResponsibleId;
        if (!respId) return;

        setAdditionalTaskRows(prev => prev.map(r => {
            if (selectedAdditionalRowIds.size === 0 || selectedAdditionalRowIds.has(r.id)) {
                return { ...r, responsibleId: respId };
            }
            return r;
        }));
    }, [additionalBulkResponsibleId, selectedAdditionalRowIds]);

    // Deploy / expand multi-level row into individual rows
    const handleDeployMultiLevelRow = useCallback((rowId: string) => {
        setAdditionalTaskRows(prev => {
            const targetRow = prev.find(r => r.id === rowId);
            if (!targetRow || !targetRow.selectedLevelIds || targetRow.selectedLevelIds.length <= 1) return prev;

            const newRows: AdditionalTaskRowDraft[] = targetRow.selectedLevelIds.map((lvlId, i) => ({
                id: i === 0 ? targetRow.id : 'draft_' + Math.random().toString(36).substring(2, 9),
                levelId: lvlId,
                unitId: '', // User must pick the unit for each level!
                additionalTaskName: targetRow.additionalTaskName,
                responsibleId: targetRow.responsibleId,
                days: { ...targetRow.days },
                selectedLevelIds: undefined,
                selectedUnitIds: undefined
            }));

            const rowIndex = prev.findIndex(r => r.id === rowId);
            const copy = [...prev];
            copy.splice(rowIndex, 1, ...newRows);
            return copy;
        });
        setActiveLevelPickerRowId(null);
    }, []);

    // Deploy / expand multi-unit row into individual rows
    const handleDeployMultiUnitRow = useCallback((rowId: string) => {
        setAdditionalTaskRows(prev => {
            const targetRow = prev.find(r => r.id === rowId);
            if (!targetRow || !targetRow.levelId || !targetRow.selectedUnitIds || targetRow.selectedUnitIds.length <= 1) return prev;

            const newRows: AdditionalTaskRowDraft[] = targetRow.selectedUnitIds.map((uId, i) => ({
                id: i === 0 ? targetRow.id : 'draft_' + Math.random().toString(36).substring(2, 9),
                levelId: targetRow.levelId,
                unitId: uId,
                additionalTaskName: targetRow.additionalTaskName,
                responsibleId: targetRow.responsibleId,
                days: { ...targetRow.days },
                selectedLevelIds: undefined,
                selectedUnitIds: undefined
            }));

            const rowIndex = prev.findIndex(r => r.id === rowId);
            const copy = [...prev];
            copy.splice(rowIndex, 1, ...newRows);
            return copy;
        });
        setActiveUnitPickerRowId(null);
    }, []);

    const handleUpdateAdditionalRow = useCallback((rowId: string, field: keyof AdditionalTaskRowDraft, value: any) => {
        setAdditionalTaskRows(prev => prev.map(r => {
            if (r.id === rowId) {
                if (field === 'levelId') {
                    return { ...r, levelId: value, unitId: '', selectedLevelIds: value ? [value] : [], selectedUnitIds: [] };
                }
                return { ...r, [field]: value };
            }
            return r;
        }));
    }, []);

    const handleToggleAdditionalRowDay = useCallback((rowId: string, dayLetter: string) => {
        setAdditionalTaskRows(prev => prev.map(r => {
            if (r.id === rowId) {
                return {
                    ...r,
                    days: { ...r.days, [dayLetter]: !r.days[dayLetter] }
                };
            }
            return r;
        }));
    }, []);

    const handleSelectLevelsBatch = useCallback((rowId: string, levelIds: string[]) => {
        setAdditionalTaskRows(prev => prev.map(r => {
            if (r.id !== rowId) return r;
            if (levelIds.length === 0) {
                return { ...r, levelId: '', selectedLevelIds: [], unitId: '', selectedUnitIds: [] };
            } else if (levelIds.length === 1) {
                return { ...r, levelId: levelIds[0], selectedLevelIds: [levelIds[0]], unitId: '', selectedUnitIds: [] };
            } else {
                return { ...r, levelId: levelIds[0], selectedLevelIds: levelIds, unitId: '', selectedUnitIds: [] };
            }
        }));
    }, []);

    const handleSelectUnitsBatch = useCallback((rowId: string, unitIds: string[]) => {
        setAdditionalTaskRows(prev => prev.map(r => {
            if (r.id !== rowId) return r;
            if (unitIds.length === 0) {
                return { ...r, unitId: '', selectedUnitIds: [] };
            } else if (unitIds.length === 1) {
                return { ...r, unitId: unitIds[0], selectedUnitIds: [unitIds[0]] };
            } else {
                return { ...r, unitId: unitIds[0], selectedUnitIds: unitIds };
            }
        }));
    }, []);

    const handleToggleLevelPicker = useCallback((rowId: string) => {
        setActiveUnitPickerRowId(null);
        setActiveLevelPickerRowId(prev => prev === rowId ? null : rowId);
    }, []);

    const handleToggleUnitPicker = useCallback((rowId: string) => {
        setActiveLevelPickerRowId(null);
        setActiveUnitPickerRowId(prev => prev === rowId ? null : rowId);
    }, []);

    const handleSaveAdditionalTasksBatch = () => {
        if (activePlanId === null) return;

        // Validate rows
        for (let i = 0; i < additionalTaskRows.length; i++) {
            const row = additionalTaskRows[i];
            const rowNum = i + 1;

            if (row.selectedLevelIds && row.selectedLevelIds.length > 1) {
                alert(`Fila #${rowNum}: Ha seleccionado múltiples niveles (${row.selectedLevelIds.length}). Debe presionar la flecha de despliegue para generar las tareas por cada nivel y luego seleccionar su unidad física.`);
                return;
            }

            if (row.selectedUnitIds && row.selectedUnitIds.length > 1) {
                alert(`Fila #${rowNum}: Ha seleccionado múltiples unidades físicas (${row.selectedUnitIds.length}). Debe presionar el botón de despliegue para generar una tarea por cada unidad física.`);
                return;
            }

            if (!row.levelId) {
                alert(`Fila #${rowNum}: Debe seleccionar un Nivel.`);
                return;
            }
            if (!row.unitId) {
                alert(`Fila #${rowNum}: Debe seleccionar una Unidad Física en el nivel correspondiente.`);
                return;
            }
            if (!row.additionalTaskName.trim()) {
                alert(`Fila #${rowNum}: Debe ingresar el nombre de la tarea.`);
                return;
            }
            if (!row.responsibleId) {
                alert(`Fila #${rowNum}: Debe seleccionar un Responsable.`);
                return;
            }
            if (!Object.values(row?.days || {}).some(d => d)) {
                alert(`Fila #${rowNum}: Debe seleccionar al menos un día de ejecución.`);
                return;
            }
        }

        if (editingAdditionalIndex !== null) {
            // Editing a single existing allocation
            const singleRow = additionalTaskRows[0];
            setWeeklyPlans(prevPlans => prevPlans.map(plan => {
                if (plan.id === activePlanId) {
                    const newAllocations = [...plan.allocations];
                    newAllocations[editingAdditionalIndex] = {
                        ...newAllocations[editingAdditionalIndex],
                        unitId: singleRow.unitId,
                        additionalTaskName: singleRow.additionalTaskName,
                        responsibleId: singleRow.responsibleId,
                        days: singleRow.days
                    };
                    return { ...plan, allocations: newAllocations };
                }
                return plan;
            }));
        } else {
            // Adding 1 or more new additional task allocations
            setWeeklyPlans(prevPlans => prevPlans.map(plan => {
                if (plan.id === activePlanId) {
                    const newAllocations = [...plan.allocations];
                    additionalTaskRows.forEach((row, idx) => {
                        const newAlloc: WeeklyAllocation = {
                            taskId: 'additional_' + Date.now() + '_' + idx + '_' + Math.random().toString(36).substr(2, 4),
                            unitId: row.unitId,
                            responsibleId: row.responsibleId,
                            days: row.days,
                            executed: null,
                            isAdditional: true,
                            additionalTaskName: row.additionalTaskName
                        };
                        newAllocations.push(newAlloc);
                    });
                    return { ...plan, allocations: newAllocations };
                }
                return plan;
            }));
        }

        setAdditionalModalOpen(false);
        setEditingAdditionalIndex(null);
        setAdditionalTaskRows([createEmptyAdditionalRow()]);
        setSelectedAdditionalRowIds(new Set());
        setAdditionalBulkResponsibleId('');
        setActiveLevelPickerRowId(null);
        setActiveUnitPickerRowId(null);
    };

    const handleAddResponsible = async () => {
        if (!newRespName.trim() || !newRespEmail.trim() || !newRespProfileId || !newRespCompanyName) {
            alert('Por favor completa todos los campos requeridos, incluyendo la empresa.');
            return;
        }
        setIsInvitingResponsible(true);
        try {
            const roleName = profiles.find(p => p.id === newRespProfileId)?.name || newRespProfileId;
            const activeProjectId = projectInfo.id;
            if (!activeProjectId) {
                alert('No se pudo determinar el ID de la obra activa.');
                setIsInvitingResponsible(false);
                return;
            }

            // Invocar la Edge Function para despachar el correo e invitar en auth
            const { data, error } = await supabase.functions.invoke('invite-user', {
                body: {
                    email: newRespEmail.trim(),
                    name: newRespName.trim(),
                    role: roleName,
                    projectId: activeProjectId
                }
            });

            if (error || (data && data.error)) {
                const actualError = error?.message || data?.error;
                console.error("Function Error:", actualError);
                if (actualError && (actualError.includes('422') || actualError.includes('already_registered') || actualError.includes('registered'))) {
                    alert(`El correo ${newRespEmail} ya se encuentra registrado o invitado previamente.`);
                } else {
                    alert("No pudimos enviar la invitación. Por favor intenta más tarde.\nDetalle del Servidor: " + actualError);
                }
                setIsInvitingResponsible(false);
                return;
            }

            if (data && data.email_status === 'failed') {
                alert(`⚠️ El responsable fue creado exitosamente en el sistema, PERO el correo de invitación falló al enviarse.\n\nMotivo del servidor de correo: ${data.email_error}\n\nPor favor, comparte el enlace de acceso manualmente:\n${data.action_link}`);
            }

            // Actualizar el listado global de usuarios (globalUsers)
            const exists = globalUsers.find(u => u.email.toLowerCase() === newRespEmail.toLowerCase().trim());
            if (exists) {
                // Si ya existe, le asignamos la obra actual y actualizamos empresa si procede
                const updatedUsers = globalUsers.map(u => {
                    if (u.email.toLowerCase() === newRespEmail.toLowerCase().trim()) {
                        return {
                            ...u,
                            status: 'Active' as const,
                            companyName: newRespCompanyName || u.companyName || companyConfig?.name || 'Constructora LyD S.A.',
                            assignedProjects: [
                                ...u.assignedProjects.filter(ap => ap.projectId !== activeProjectId),
                                { projectId: activeProjectId, profileId: newRespProfileId, profileName: roleName }
                            ]
                        };
                    }
                    return u;
                });
                setGlobalUsers(updatedUsers);
            } else {
                // Si es nuevo, lo creamos
                const newUser: GlobalUser = {
                    id: data?.user?.id || 'temp-' + Date.now().toString(),
                    email: newRespEmail.toLowerCase().trim(),
                    name: newRespName.trim(),
                    globalRole: 'Usuario',
                    status: 'Pending',
                    companyName: newRespCompanyName || companyConfig?.name || 'Constructora LyD S.A.',
                    assignedProjects: [{ projectId: activeProjectId, profileId: newRespProfileId, profileName: roleName }]
                };
                setGlobalUsers([...globalUsers, newUser]);
            }

            // Doble Persistencia: Vincular a la lista de responsables de la obra activa
            const newLocalResp: Responsible = {
                id: data?.user?.id || 'temp-' + Date.now().toString(),
                name: newRespName.trim(),
                rut: '',
                email: newRespEmail.toLowerCase().trim(),
                role: roleName,
                profileId: newRespProfileId
            };
            setResponsibles(prev => {
                const existsLocal = prev.some(r => r.email.toLowerCase() === newLocalResp.email.toLowerCase());
                if (existsLocal) return prev;
                return [...prev, newLocalResp];
            });

            // Registrar actividad
            addActivity({
                type: 'planning',
                title: 'Responsable añadido',
                description: `Se añadió a ${newRespName.trim()} (${roleName}) de ${newRespCompanyName} a la obra`,
                user: currentUser?.name || currentUser?.email || 'Usuario'
            });

            // Limpiar estados y cerrar modal
            setNewRespName('');
            setNewRespEmail('');
            setNewRespProfileId('');
            setNewRespCompanyName('');
            setIsAddResponsibleModalOpen(false);
            alert(`¡Listo! Hemos enviado la invitación de acceso a ${newRespEmail} y se ha asignado al proyecto.`);

        } catch (err: any) {
            console.error("Network Error:", err);
            alert("Error de red al invitar al responsable. Por favor verifica tu conexión.");
        } finally {
            setIsInvitingResponsible(false);
        }
    };

    const [draftWeeklyPlan, setDraftWeeklyPlan] = useState<WeeklyPlan | null>(null);
    const [editingWeeklyPlanId, setEditingWeeklyPlanId] = useState<number | null>(null);

    // Data Store for Lookahead Plans
    const [activeLookaheadId, setActiveLookaheadId] = useState<string | null>(null);

    // Get currently active plan data (moved up to avoid hoisting/TS2448 error)
    const _baseActivePlan = weeklyPlans.find(p => p.id === activePlanId);
    const activePlan = isRevisionStatus(_baseActivePlan?.status) && draftWeeklyPlan?.id === activePlanId ? draftWeeklyPlan : _baseActivePlan;

    // Auto-clear active planning in mobile if in 'En Planificación' and user lacks permission
    useEffect(() => {
        if (isMobile && activePlanId !== null) {
            const currentPlan = weeklyPlans.find(p => p.id === activePlanId);
            if (currentPlan && !isPlanVisibleInMobile(currentPlan.status)) {
                setActivePlanId(null);
            }
        }
    }, [isMobile, activePlanId, weeklyPlans, isPlanVisibleInMobile]);

    useEffect(() => {
        if (isMobile && activeLookaheadId !== null) {
            const currentPlan = lookaheadPlans.find(p => p.id === activeLookaheadId);
            if (currentPlan && !isPlanVisibleInMobile(currentPlan.status)) {
                setActiveLookaheadId(null);
            }
        }
    }, [isMobile, activeLookaheadId, lookaheadPlans, isPlanVisibleInMobile]);

    // Restauración de scroll y resaltado al volver a la lista de planificaciones (Intermedia y Semanal)
    const lastViewedPlanRef = useRef<{ type: 'intermedia' | 'semanal'; id: string; scrollTop: number } | null>(null);
    const [highlightedPlanId, setHighlightedPlanId] = useState<string | null>(null);

    const recordPlanScroll = (type: 'intermedia' | 'semanal', id: string | number) => {
        const mobileContainer = document.getElementById('mobile-planning-scroll-container');
        const scrollTop = mobileContainer ? mobileContainer.scrollTop : window.scrollY;
        const planIdStr = String(id);
        lastViewedPlanRef.current = { type, id: planIdStr, scrollTop };
        try {
            sessionStorage.setItem('planning_last_viewed_plan', JSON.stringify({ type, id: planIdStr, scrollTop }));
        } catch (e) { }
    };

    useEffect(() => {
        if (activePlanId === null && activeLookaheadId === null) {
            let saved = lastViewedPlanRef.current;
            if (!saved) {
                try {
                    const raw = sessionStorage.getItem('planning_last_viewed_plan');
                    if (raw) saved = JSON.parse(raw);
                } catch (e) { }
            }
            if (saved) {
                const { type, id, scrollTop } = saved;
                setHighlightedPlanId(id);

                const timer = setTimeout(() => {
                    const container = document.getElementById('mobile-planning-scroll-container');
                    if (container && typeof scrollTop === 'number') {
                        container.scrollTop = scrollTop;
                    }
                    const targetCard = document.getElementById(`plan-card-${type}-${id}`) || document.getElementById(`plan-card-${id}`);
                    if (targetCard) {
                        targetCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 80);

                const clearTimer = setTimeout(() => {
                    setHighlightedPlanId(null);
                    lastViewedPlanRef.current = null;
                    try {
                        sessionStorage.removeItem('planning_last_viewed_plan');
                    } catch (e) { }
                }, 1200);

                return () => {
                    clearTimeout(timer);
                    clearTimeout(clearTimer);
                };
            }
        }
    }, [activePlanId, activeLookaheadId]);

    // Dynamic Day Columns derived from active weekly plan date range
    const dynamicDayColumns = useMemo(() => {
        return generateDynamicDayColumns(activePlan?.startDate, activePlan?.endDate);
    }, [activePlan?.startDate, activePlan?.endDate]);

    const closedComplianceMap = useMemo(() => {
        const map = new Map<string, number>();
        if (!activePlan) return map;
        weeklyPlans
            .filter(p => p.status === 'Cerrada' && p.id !== activePlan.id)
            .forEach(p => {
                p.allocations.forEach(a => {
                    const key = `${a.taskId}_${a.unitId}`;
                    const current = map.get(key) || 0;
                    map.set(key, current + getAllocationCompliancePct(a));
                });
            });
        return map;
    }, [weeklyPlans, activePlan]);

    // Get currently active lookahead plan (moved up)
    const activeLookahead = lookaheadPlans.find(p => p.id === activeLookaheadId);

    React.useEffect(() => {
        if (location.state?.activeTab) {
            setActiveTab(location.state.activeTab);
        }
        if (location.state?.activePlanId) {
            setActivePlanId(location.state.activePlanId);
        }
        if (location.state?.activeLookaheadId) {
            setActiveLookaheadId(location.state.activeLookaheadId);
        }
        if (location.state) {
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    const [lookaheadModalOpen, setLookaheadModalOpen] = useState(false);
    const [lookaheadRange, setLookaheadRange] = useState({ start: '', end: '' });
    const [editingLookaheadId, setEditingLookaheadId] = useState<string | null>(null);

    // Add/Edit Constraint Modal State
    const [constraintModalOpen, setConstraintModalOpen] = useState(false);
    const [editingConstraintId, setEditingConstraintId] = useState<string | null>(null);
    const [activeConstraintTask, setActiveConstraintTask] = useState<string | null>(null); // Task ID being constrained
    const [newConstraintData, setNewConstraintData] = useState({
        typeId: '',
        responsibleId: '',
        description: '',
        requiredDate: '',
        unitsType: 'na' as 'na' | 'all' | 'custom',
        unitIds: [] as string[]
    });

    // Unit selection modal state
    const [unitSelectionModalOpen, setUnitSelectionModalOpen] = useState(false);
    const [unitsViewModalOpen, setUnitsViewModalOpen] = useState(false);
    const [activeConstraintForUnits, setActiveConstraintForUnits] = useState<string | null>(null);

    // Modals State
    const [newPlanModalOpen, setNewPlanModalOpen] = useState(false);
    const [weeklyPlanDates, setWeeklyPlanDates] = useState({ start: '', end: '' });
    const [mobileProtocolTarget, setMobileProtocolTarget] = useState<{
        taskId: string;
        unitId: string;
        taskName?: string;
        taskCode?: string;
    } | null>(null);

    // Import/Export Modals
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [missingTasksModalOpen, setMissingTasksModalOpen] = useState(false);
    const [missingTasksList, setMissingTasksList] = useState<{ id: string; name: string; key: string }[]>([]);
    const [pendingImportData, setPendingImportData] = useState<{
        finalStoredTasks: Task[];
        isReprogramming: boolean;
        comment: string;
        parsedWorkingDays?: boolean[] | null;
        parsedHolidays?: { date: string; name: string }[];
        parsedHoursPerDay?: number | null;
        updatedCount: number;
        newCount: number;
        deletedCount: number;
    } | null>(null);

    // Reconciliation Modal States
    const [reconciliationModalOpen, setReconciliationModalOpen] = useState(false);
    const [unmatchedOldTasks, setUnmatchedOldTasks] = useState<Task[]>([]);
    const [reconciliationNewTasks, setReconciliationNewTasks] = useState<Task[]>([]);
    const [reconciliationMappings, setReconciliationMappings] = useState<Record<string, string>>({}); // oldId -> newId
    const [selectedOldTaskId, setSelectedOldTaskId] = useState<string | null>(null);
    const [selectedNewTaskId, setSelectedNewTaskId] = useState<string | null>(null);
    const [reconciliationSearch, setReconciliationSearch] = useState('');

    // Document Manager Integration Mock
    const [docManagerModalOpen, setDocManagerModalOpen] = useState(false);
    const [docManagerModalType, setDocManagerModalType] = useState<'import' | 'reprogram'>('import');

    // NEW: Sync Progress Modal State
    const [syncProgressModalOpen, setSyncProgressModalOpen] = useState(false);
    const [syncDay, setSyncDay] = useState<number>(() => {
        const pId = projectInfo?.id || 'default';
        const savedProject = localStorage.getItem(`gantt_sync_day_${pId}`);
        if (savedProject !== null) {
            const parsed = parseInt(savedProject, 10);
            if (!isNaN(parsed)) return parsed;
        }
        const savedGlobal = localStorage.getItem('gantt_sync_day');
        if (savedGlobal !== null) {
            const parsed = parseInt(savedGlobal, 10);
            if (!isNaN(parsed)) return parsed;
        }
        return 5; // default Friday (5)
    });

    // Keep syncDay in sync if active project changes
    useEffect(() => {
        const pId = projectInfo?.id || 'default';
        const savedProject = localStorage.getItem(`gantt_sync_day_${pId}`);
        if (savedProject !== null) {
            const parsed = parseInt(savedProject, 10);
            if (!isNaN(parsed) && parsed !== syncDay) {
                setSyncDay(parsed);
                return;
            }
        }
        const savedGlobal = localStorage.getItem('gantt_sync_day');
        if (savedGlobal !== null) {
            const parsed = parseInt(savedGlobal, 10);
            if (!isNaN(parsed) && parsed !== syncDay) {
                setSyncDay(parsed);
            }
        }
    }, [projectInfo?.id]);

    // Self-healing check to auto-align progressHistory when project progress is updated or differs from snapshots
    React.useEffect(() => {
        if (!tasks || tasks.length === 0) return;

        const currentProgress = calculateProjectProgress(
            processedTasks,
            units,
            matrixData,
            protocolConfigs,
            projectInfo,
            savedProtocols,
            weeklyPlans
        );

        if (currentProgress > 0) {
            const lastRecord = (progressHistory && progressHistory.length > 0)
                ? progressHistory[progressHistory.length - 1]
                : null;

            const needsRealign = !progressHistory || progressHistory.length === 0 ||
                (lastRecord && Math.abs(lastRecord.progress - currentProgress) > 0.3);

            if (needsRealign) {
                console.log("[Self-Healing] Re-aligning progress history to live project progress:", currentProgress);

                // Get earliest task start date
                let minStart = "";
                const flat = flattenTasks(processedTasks).filter(t => t.type === 'task' && !t.isWeeklySubtask);
                flat.forEach(t => {
                    if (t.start && (!minStart || t.start < minStart)) minStart = t.start;
                });
                const projectStartDate = minStart || projectInfo.startDate;

                // Cutoff is today's date or last snapshot date if beyond today
                const todayStr = new Date().toISOString().split('T')[0];
                const cutoffDate = (lastRecord && lastRecord.date > todayStr) ? lastRecord.date : todayStr;

                const weeksList = generateHistoricalWeeks(projectStartDate, cutoffDate, processedTasks, syncDay);
                if (weeksList.length > 0) {
                    const distributed = autoDistributeProportionally(currentProgress, weeksList);
                    const tempCurveData = calculateSCurve(processedTasks, [], syncDay);
                    let accumulatedReal = 0;
                    const generatedHistory = weeksList.map((w, idx) => {
                        const realWeekly = distributed[idx] || 0;
                        accumulatedReal += realWeekly;
                        const delayCS = calculateDelayDays(tempCurveData, accumulatedReal, w.syncDateStr);
                        const delayRC = getCriticalPathDelayAtDate(w.syncDateStr);
                        return {
                            id: Date.now().toString() + "_" + idx + "_" + Math.random().toString(36).substr(2, 5),
                            date: w.syncDateStr,
                            progress: Number(accumulatedReal.toFixed(2)),
                            delayCurvaS: delayCS,
                            delayRutaCritica: delayRC > 0 ? delayRC : delayCS
                        };
                    });
                    if (generatedHistory.length > 0) {
                        generatedHistory[generatedHistory.length - 1].progress = Number(currentProgress.toFixed(2));
                    }
                    console.log("[Self-Healing] Saving re-aligned progress history:", generatedHistory);
                    setProgressHistory(generatedHistory);
                }
            }
        }
    }, [tasks, processedTasks, progressHistory, units, matrixData, protocolConfigs, projectInfo, savedProtocols, weeklyPlans, syncDay, setProgressHistory]);

    const [manualCriticalPathDelays, setManualCriticalPathDelays] = useState<Record<string, number>>({});
    const [syncConfirmOpen, setSyncConfirmOpen] = useState(false);
    const [syncConfirmData, setSyncConfirmData] = useState<{ date: string; delayCS: number; delayRC: number; weekIndex?: number } | null>(null);
    const [extraWeeksCount, setExtraWeeksCount] = useState<number>(0);
    const [newHolidayDate, setNewHolidayDate] = useState("");
    const [newHolidayName, setNewHolidayName] = useState("");

    const handleMigrateSyncDayHistory = (newSyncDay: number) => {
        const oldSyncDay = syncDay;
        setSyncDay(newSyncDay);

        const pId = projectInfo?.id || 'default';
        try {
            localStorage.setItem(`gantt_sync_day_${pId}`, newSyncDay.toString());
            localStorage.setItem('gantt_sync_day', newSyncDay.toString());
        } catch (e) {
            console.error("Error saving syncDay to localStorage:", e);
        }

        let minStart = "";
        const flat = flattenTasks(processedTasks).filter(t => t.type === 'task' && !t.isWeeklySubtask);
        flat.forEach(t => {
            if (t.start && (!minStart || t.start < minStart)) minStart = t.start;
        });
        const projectStartDate = minStart || projectInfo.startDate;
        if (!projectStartDate) return;

        // 1. Re-map draftWeeklyGains if present
        const startStr = tasks[0]?.start || projectInfo.startDate;
        const endStr = tasks[0]?.end || projectInfo.endDate;
        if (startStr && endStr && draftWeeklyGains && Object.keys(draftWeeklyGains).length > 0) {
            const limitDate = new Date(endStr + "T12:00:00");

            const oldCurrent = new Date(startStr + "T12:00:00");
            while (oldCurrent.getDay() !== (oldSyncDay % 7)) {
                oldCurrent.setDate(oldCurrent.getDate() + 1);
            }
            const oldDates: Record<number, string> = {};
            let idx = 1;
            while (oldCurrent <= limitDate) {
                oldDates[idx++] = oldCurrent.toISOString().split('T')[0];
                oldCurrent.setDate(oldCurrent.getDate() + 7);
            }
            for (let i = 0; i < extraWeeksCount; i++) {
                oldDates[idx++] = oldCurrent.toISOString().split('T')[0];
                oldCurrent.setDate(oldCurrent.getDate() + 7);
            }

            const newCurrent = new Date(startStr + "T12:00:00");
            while (newCurrent.getDay() !== (newSyncDay % 7)) {
                newCurrent.setDate(newCurrent.getDate() + 1);
            }
            const newDates: Record<number, string> = {};
            idx = 1;
            while (newCurrent <= limitDate) {
                newDates[idx++] = newCurrent.toISOString().split('T')[0];
                newCurrent.setDate(newCurrent.getDate() + 7);
            }
            for (let i = 0; i < extraWeeksCount; i++) {
                newDates[idx++] = newCurrent.toISOString().split('T')[0];
                newCurrent.setDate(newCurrent.getDate() + 7);
            }

            const newDrafts: Record<string, string> = {};
            Object.keys(oldDates).forEach(wIndexStr => {
                const wIdx = parseInt(wIndexStr);
                const oldDateStr = oldDates[wIdx];
                const newDateStr = newDates[wIdx];
                if (oldDateStr && newDateStr && draftWeeklyGains[oldDateStr] !== undefined) {
                    newDrafts[newDateStr] = draftWeeklyGains[oldDateStr];
                }
            });
            setDraftWeeklyGains(newDrafts);
        }

        // 2. Re-map progressHistory if present
        if (progressHistory && progressHistory.length > 0) {
            let updatedHistory = [...progressHistory];
            updatedHistory.sort((a, b) => (a.weekIndex && b.weekIndex ? a.weekIndex - b.weekIndex : a.date.localeCompare(b.date)));

            updatedHistory = updatedHistory.map((record, index) => {
                const weekNum = record.weekIndex || (index + 1);

                const current = new Date(projectStartDate + "T12:00:00");
                while (current.getDay() !== (newSyncDay % 7)) {
                    current.setDate(current.getDate() + 1);
                }
                current.setDate(current.getDate() + (weekNum - 1) * 7);
                const newDateStr = current.toISOString().split('T')[0];

                return {
                    ...record,
                    weekIndex: weekNum,
                    date: newDateStr
                };
            });

            console.log(`[SyncDay Migration] Updated ${updatedHistory.length} snapshots for new syncDay ${newSyncDay}:`, updatedHistory);
            setProgressHistory(updatedHistory);
        }
    };

    // NEW: Global Table Edit Mode States (Admin only)
    const [isTableEditMode, setIsTableEditMode] = useState<boolean>(false);
    const [draftWeeklyGains, setDraftWeeklyGains] = useState<Record<string, string>>({});

    const handleEnterTableEditMode = (allWeeksList: { index: number; dateStr: string }[]) => {
        const initialDrafts: Record<string, string> = {};

        allWeeksList.forEach((w, index) => {
            const record = progressHistory.find(r => (r.weekIndex ? r.weekIndex === w.index : r.date === w.dateStr));
            const prevWeek = allWeeksList[index - 1];
            const prevRecord = prevWeek ? progressHistory.find(r => (r.weekIndex ? r.weekIndex === prevWeek.index : r.date === prevWeek.dateStr)) : null;

            let weeklyGain = 0;
            if (record) {
                const prevProgress = prevRecord ? prevRecord.progress : 0;
                weeklyGain = Math.max(0, record.progress - prevProgress);
            } else {
                // Unsynchronized future weeks start at 0% (Pendiente)
                weeklyGain = 0;
            }

            initialDrafts[w.dateStr] = Number(weeklyGain.toFixed(2)).toString();
        });

        setDraftWeeklyGains(initialDrafts);
        setIsTableEditMode(true);
    };

    const handleSaveGlobalTableEdit = (allWeeksList: { index: number; dateStr: string }[], maxAllowedProgress?: number) => {
        const maxLimit = maxAllowedProgress !== undefined ? maxAllowedProgress : calculateTotalProjectProgress();
        let accum = 0;

        // 1. Calculate cumulative progress for each week based on entered draft gains
        const weekProgresses: { index: number; dateStr: string; gain: number; accum: number }[] = [];
        allWeeksList.forEach(w => {
            const dStr = w.dateStr;
            const inputVal = draftWeeklyGains[dStr];
            const numVal = inputVal !== undefined && inputVal !== '' ? parseFloat(inputVal) : 0;
            const validVal = !isNaN(numVal) ? numVal : 0;
            accum += validVal;
            const newAccum = Number(Math.min(maxLimit, Math.max(0, accum)).toFixed(2));

            weekProgresses.push({
                index: w.index,
                dateStr: dStr,
                gain: validVal,
                accum: newAccum
            });
        });

        // 2. Find the last week index that has a real positive gain (> 0)
        let lastSyncedIndex = -1;
        for (let i = weekProgresses.length - 1; i >= 0; i--) {
            if (weekProgresses[i].gain > 0) {
                lastSyncedIndex = i;
                break;
            }
        }

        // 3. Save history records ONLY up to lastSyncedIndex
        let updatedHistory: ProjectProgressSnapshot[] = [];

        if (lastSyncedIndex >= 0) {
            const tempCurveData = calculateSCurve(processedTasks, [], syncDay);
            for (let i = 0; i <= lastSyncedIndex; i++) {
                const wp = weekProgresses[i];
                const existingRecord = progressHistory.find(r => (r.weekIndex ? r.weekIndex === wp.index : r.date === wp.dateStr));
                const delayCS = calculateDelayDays(tempCurveData, wp.accum, wp.dateStr);
                const delayRC = getCriticalPathDelayAtDate(wp.dateStr);

                updatedHistory.push({
                    weekIndex: wp.index,
                    date: wp.dateStr,
                    progress: wp.accum,
                    delayCurvaS: delayCS > 0 ? delayCS : (existingRecord?.delayCurvaS || 0),
                    delayRutaCritica: delayRC > 0 ? delayRC : (existingRecord?.delayRutaCritica || (delayCS > 0 ? delayCS : 0))
                });
            }
        }

        updatedHistory.sort((a, b) => a.date.localeCompare(b.date));
        setProgressHistory(updatedHistory);
        setIsTableEditMode(false);

        addActivity({
            type: 'planning',
            title: 'Tabla de Sincronización Editada',
            description: `Se actualizaron manualmente los porcentajes de avance semanal real (Límite Máximo: ${maxLimit.toFixed(2)}%).`,
            user: currentUser?.name || currentUser?.email || 'Administrador'
        });
    };

    // States for Gantt Import Wizard (Step 1: Progress Distribution / Step 2: Calendar Settings)
    const [importWizardModalOpen, setImportWizardModalOpen] = useState(false);
    const [wizardStep, setWizardStep] = useState<number>(1);
    const [selectedReconcileParentId, setSelectedReconcileParentId] = useState<string | null>(null);
    const [isWizardFullscreen, setIsWizardFullscreen] = useState(false);
    const [expandedSubtasks, setExpandedSubtasks] = useState<Record<string, boolean>>({});
    const toggleSubtaskExpanded = (subId: string) => {
        setExpandedSubtasks(prev => ({
            ...prev,
            [subId]: !prev[subId]
        }));
    };
    const [wizardData, setWizardData] = useState<{
        tasks: Task[];
        importedProgress: number;
        activeWorkingDays: boolean[];
        activeHolidays: any[];
        activeHoursPerDay: number;
        isReprogramming: boolean;
        comment: string;
        weeklyProgress: Record<number, string>;
        cutOffDate: string;
        workingDays: boolean[];
        hoursPerDay: number;
        tempHolidays: { date: string; name: string }[];
        reconciliationTargets: Record<string, number>;
        reconciliationProgress: Record<string, number>;
        reconciliationUnitProgress: Record<string, Record<string, number>>;
        syncDay?: number;
    } | null>(null);

    const tasksToReconcile = useMemo(() => {
        if (!wizardData) return [];
        const list: { parentTask: Task; existingSubtasks: Task[]; targetProgress: number }[] = [];

        const findParentTasks = (taskList: Task[]) => {
            taskList.forEach(t => {
                const weeklySubtasks = t.children ? t.children.filter(c => c.isWeeklySubtask && !c.isDeleted) : [];
                if (weeklySubtasks.length > 0 && wizardData.reconciliationTargets && wizardData.reconciliationTargets[t.id] !== undefined) {
                    list.push({
                        parentTask: t,
                        existingSubtasks: weeklySubtasks,
                        targetProgress: wizardData.reconciliationTargets[t.id]
                    });
                }
                if (t.children) findParentTasks(t.children);
            });
        };
        findParentTasks(wizardData.tasks);
        return list;
    }, [wizardData]);

    const isReconciliationBalanced = useMemo(() => {
        if (tasksToReconcile.length === 0) return true;
        return tasksToReconcile.every(item => {
            const target = item.targetProgress;
            const achieved = item.existingSubtasks.reduce((sum, sub) => {
                const progress = wizardData?.reconciliationProgress[sub.id] ?? sub.progress ?? 0;
                const weight = sub.weight !== undefined ? sub.weight : (100 / item.existingSubtasks.length);
                return sum + progress * (weight / 100);
            }, 0);
            return Math.abs(target - achieved) < 0.01;
        });
    }, [tasksToReconcile, wizardData?.reconciliationProgress]);

    const areWeightsValid = useMemo(() => {
        if (tasksToReconcile.length === 0) return true;
        return tasksToReconcile.every(item => {
            if (item.existingSubtasks.length === 0) return true;
            const sumWeights = item.existingSubtasks.reduce((sum, sub) => {
                const w = sub.weight !== undefined ? sub.weight : (100 / item.existingSubtasks.length);
                return sum + w;
            }, 0);
            return Math.abs(sumWeights - 100) < 0.01;
        });
    }, [tasksToReconcile, wizardData?.tasks]);

    const getTaskPath = (taskId: string, list: Task[]): string => {
        let path = "";
        const findAncestors = (tasks: Task[], currentPath: string[]): boolean => {
            for (const t of tasks) {
                if (t.id === taskId) {
                    path = currentPath.join(" > ");
                    return true;
                }
                if (t.children && t.children.length > 0) {
                    const nameLabel = t.id_original_project !== undefined ? `[${t.id_original_project}] ${t.name}` : t.name;
                    if (findAncestors(t.children, [...currentPath, nameLabel])) {
                        return true;
                    }
                }
            }
            return false;
        };
        findAncestors(list, []);
        return path;
    };

    const wizardSteps = useMemo(() => {
        if (!wizardData) return [];
        const steps = [];
        if (wizardData.importedProgress > 0) {
            steps.push('distribution');
        }
        const targets: Record<string, number> = {};
        const findTargets = (list: Task[]) => {
            list.forEach(t => {
                const weeklySubtasks = t.children ? t.children.filter(c => c.isWeeklySubtask && !c.isDeleted) : [];
                if (weeklySubtasks.length > 0 && t.progress !== undefined && t.progress > 0 && t.progress < 100) {
                    targets[t.id] = t.progress;
                }
                if (t.children) findTargets(t.children);
            });
        };
        findTargets(wizardData.tasks);
        if (Object.keys(targets).length > 0) {
            steps.push('reconciliation');
        }
        steps.push('calendar');
        return steps;
    }, [wizardData]);

    const activeStepType = wizardSteps[wizardStep - 1];

    useEffect(() => {
        if (activeStepType === 'reconciliation' && tasksToReconcile.length > 0) {
            if (!selectedReconcileParentId || !tasksToReconcile.some(t => t.parentTask.id === selectedReconcileParentId)) {
                setSelectedReconcileParentId(tasksToReconcile[0].parentTask.id);
            }
        }
    }, [activeStepType, tasksToReconcile, selectedReconcileParentId]);

    // States for Re-import conflict handling (Incompatibility, Mode Change, Changes Detected)
    const [conflictModalOpen, setConflictModalOpen] = useState(false);
    const [conflictType, setConflictType] = useState<'incompatibility' | 'mode_change' | 'changes_detected' | null>(null);
    const [conflictData, setConflictData] = useState<{
        processed: Task[];
        activeWorkingDays: boolean[];
        activeHolidays: any[];
        activeHoursPerDay: number;
        isReprogramming: boolean;
        comment: string;
        currentProjectName?: string;
        importedProjectName?: string;
    } | null>(null);

    // States for Reprogramming comparison view (Added vs Deleted tasks)
    const [reprogramCompareModalOpen, setReprogramCompareModalOpen] = useState(false);
    const [reprogramCompareData, setReprogramCompareData] = useState<{
        addedTasks: Task[];
        deletedTasks: Task[];
        mergedTasks: Task[];
        activeWorkingDays: boolean[];
        activeHolidays: any[];
        activeHoursPerDay: number;
        comment: string;
    } | null>(null);

    const [modalOpen, setModalOpen] = useState(false); // Generic Input Modal
    const [inputName, setInputName] = useState("");
    const [inputWeight, setInputWeight] = useState("");
    const [targetParentId, setTargetParentId] = useState<string | null>(null);
    const [editingConstraint, setEditingConstraint] = useState<string | null>(null);
    const [editingItemId, setEditingItemId] = useState<string | null>(null);

    // Subtasks Weight Management Modal State
    const [weightModalOpen, setWeightModalOpen] = useState(false);
    const [weightModalParentId, setWeightModalParentId] = useState<string | null>(null);
    const [draftWeights, setDraftWeights] = useState<Record<string, string>>({});

    // Multi-Subtask Breakdown & Weight Management State
    const [breakdownModalOpen, setBreakdownModalOpen] = useState(false);
    const [breakdownParentTaskId, setBreakdownParentTaskId] = useState<string | null>(null);
    const [draftSubtasks, setDraftSubtasks] = useState<Array<{
        id: string;
        name: string;
        weight: string;
        id_original_project?: string | number;
        isNew?: boolean;
    }>>([]);
    const [newSubtaskName, setNewSubtaskName] = useState("");
    const [newSubtaskWeight, setNewSubtaskWeight] = useState("");
    const [isAddingSubtask, setIsAddingSubtask] = useState(false);
    const [draggedSubtaskIndex, setDraggedSubtaskIndex] = useState<number | null>(null);
    const newSubtaskInputRef = useRef<HTMLInputElement>(null);

    // Baseline & Reprogramming State
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [reprogrammingModalOpen, setReprogrammingModalOpen] = useState(false);
    const [reprogrammingComment, setReprogrammingComment] = useState('');
    const [comparisonBaselineId, setComparisonBaselineId] = useState<string | null>(null);
    const [showComparison, setShowComparison] = useState(false);
    const [showCriticalPath, setShowCriticalPath] = useState(false);

    const comparisonBaseline = useMemo(() => {
        if (!comparisonBaselineId) return null;
        return baselines.find(b => b.id === comparisonBaselineId) || null;
    }, [comparisonBaselineId, baselines]);
    const [modalMode, setModalMode] = useState<'folder' | 'subtask'>('folder');

    // Bi-directional Scroll Sync
    const leftScrollRef = React.useRef<HTMLDivElement>(null);
    const rightScrollRef = React.useRef<HTMLDivElement>(null);
    const isScrollingLeft = React.useRef(false);
    const isScrollingRight = React.useRef(false);

    const handleLeftScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (!leftScrollRef.current || !rightScrollRef.current) return;
        if (isScrollingRight.current) {
            isScrollingRight.current = false;
            return;
        }
        isScrollingLeft.current = true;
        rightScrollRef.current.scrollTop = leftScrollRef.current.scrollTop;
    };

    const handleRightScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (!leftScrollRef.current || !rightScrollRef.current) return;
        if (isScrollingLeft.current) {
            isScrollingLeft.current = false;
            return;
        }
        isScrollingRight.current = true;
        leftScrollRef.current.scrollTop = rightScrollRef.current.scrollTop;
    };

    // Responsibles Modals
    const [responsiblesMenuOpen, setResponsiblesMenuOpen] = useState(false);
    const [isAddResponsibleModalOpen, setIsAddResponsibleModalOpen] = useState(false);
    const [isResponsiblesListModalOpen, setIsResponsiblesListModalOpen] = useState(false);
    const [newRespName, setNewRespName] = useState('');
    const [newRespEmail, setNewRespEmail] = useState('');
    const [newRespProfileId, setNewRespProfileId] = useState('');
    const [newRespCompanyName, setNewRespCompanyName] = useState('');
    const [isInvitingResponsible, setIsInvitingResponsible] = useState(false);

    // CNC (Causas de No Cumplimiento) State
    const [cncMenuOpen, setCncMenuOpen] = useState(false);
    const [cncModalOpen, setCncModalOpen] = useState(false);
    // REMOVED LOCAL STATE cncGroups
    const [newCNCGroupName, setNewCNCGroupName] = useState('');
    const [activeGroupForNewCNC, setActiveGroupForNewCNC] = useState<string | null>(null);
    const [newCNCCauseName, setNewCNCCauseName] = useState('');

    // NEW CRUD State for CNC
    const [editingCNCGroupId, setEditingCNCGroupId] = useState<string | null>(null);
    const [editCNCGroupName, setEditCNCGroupName] = useState('');
    const [editingCNCCauseId, setEditingCNCCauseId] = useState<string | null>(null);
    const [editCNCCauseName, setEditCNCCauseName] = useState('');

    // Constraints Master Management State
    const [constraintsMenuOpen, setConstraintsMenuOpen] = useState(false);
    const [constraintsModalOpen, setConstraintsModalOpen] = useState(false);
    // REMOVED LOCAL STATE constraintGroups
    const [newConstraintGroupName, setNewConstraintGroupName] = useState('');
    const [activeGroupForNewConstraint, setActiveGroupForNewConstraint] = useState<string | null>(null);
    const [newConstraintName, setNewConstraintName] = useState('');

    // NEW CRUD State for Constraints
    const [editingConstraintGroupId, setEditingConstraintGroupId] = useState<string | null>(null);
    const [editConstraintGroupName, setEditConstraintGroupName] = useState('');
    const [editingConstraintDefId, setEditingConstraintDefId] = useState<string | null>(null);
    const [editConstraintDefName, setEditConstraintDefName] = useState('');

    // Plan Task Modal State
    const [planTaskModalOpen, setPlanTaskModalOpen] = useState(false);
    const [planningTaskId, setPlanningTaskId] = useState<string | null>(null);
    const [currentAllocations, setCurrentAllocations] = useState<(WeeklyAllocation & {
        selected: boolean;
        isRestricted: boolean;
        isPreviouslyExecuted?: boolean;
        isReprogrammed?: boolean;
        isSuggestedByGantt?: boolean;
        accumulatedCompliance?: number;
        remainingBalance?: number;
    })[]>([]);

    // NEW: Mobile Filters and Obs Modals
    const [mobileFilterITE, setMobileFilterITE] = useState<'all' | 'mine'>('all');
    const [mobileFilterWeekly, setMobileFilterWeekly] = useState<'all' | 'mine'>(() => {
        return (sessionStorage.getItem('planning_mobileFilterWeekly') as any) || 'all';
    });

    React.useEffect(() => {
        sessionStorage.setItem('planning_mobileFilterWeekly', mobileFilterWeekly);
    }, [mobileFilterWeekly]);

    // Auto-inicializar Rev 0 - Línea Base Original si hay tareas pero no hay líneas base
    React.useEffect(() => {
        if (tasks && tasks.length > 0 && baselines && baselines.length === 0) {
            const currentStart = tasks.length > 0 ? tasks[0].start : "";
            const currentEnd = tasks.length > 0 ? tasks[0].end : "";
            const rev0Baseline: GanttBaseline = {
                id: 'baseline-rev0-' + Date.now(),
                versionNumber: 0,
                uploadDate: new Date().toISOString(),
                tasksSnapshot: JSON.parse(JSON.stringify(tasks)),
                comment: "Línea Base Original",
                startDate: currentStart || "N/A",
                endDate: currentEnd || "N/A",
                vigente: true,
                selectedForChart: true
            };
            setBaselines([rev0Baseline]);
        }
    }, [tasks, baselines, setBaselines]);
    const [constraintObsModalOpen, setConstraintObsModalOpen] = useState(false);
    const [constraintObsData, setConstraintObsData] = useState<{ planId: string, constraintId: string, currentStatus: string, observations: string, description: string } | null>(null);

    // Mobile Planning States (Puntos 0, 2, 3)
    const [mobileActiveConstraintMenuId, setMobileActiveConstraintMenuId] = useState<string | null>(null);
    const [mobileActiveConstraintMenuAnchor, setMobileActiveConstraintMenuAnchor] = useState<HTMLElement | null>(null);
    const [mobileCardActionTarget, setMobileCardActionTarget] = useState<any | null>(null);
    const [mobileCardActionAnchor, setMobileCardActionAnchor] = useState<HTMLElement | null>(null);
    const [mobileConstraintDetailTarget, setMobileConstraintDetailTarget] = useState<{ task: Task; constraint: Constraint; respName?: string; typeName?: string; levelName?: string; ganttNumber?: number | string } | null>(null);

    // Weekly Mobile Filters & Modals (Punto 3)
    const [weeklyMobileFilters, setWeeklyMobileFilters] = useState<WeeklyMobileFilterState>(initialWeeklyMobileFilters);
    const [draftWeeklyMobileFilters, setDraftWeeklyMobileFilters] = useState<WeeklyMobileFilterState>(initialWeeklyMobileFilters);
    const [isWeeklyFilterSheetOpen, setIsWeeklyFilterSheetOpen] = useState(false);
    const [weeklyFilterCategorySearch, setWeeklyFilterCategorySearch] = useState<Record<string, string>>({});
    const [expandedFilterCategories, setExpandedFilterCategories] = useState<Record<string, boolean>>({});

    const [mobileActiveWeeklyMenuKey, setMobileActiveWeeklyMenuKey] = useState<string | null>(null);
    const [mobileActiveWeeklyMenuAnchor, setMobileActiveWeeklyMenuAnchor] = useState<HTMLElement | null>(null);
    const [mobileWeeklyCardActionTarget, setMobileWeeklyCardActionTarget] = useState<any | null>(null);
    const [mobileWeeklyCardActionAnchor, setMobileWeeklyCardActionAnchor] = useState<HTMLElement | null>(null);
    const [mobileWeeklyDetailTarget, setMobileWeeklyDetailTarget] = useState<any | null>(null);

    const [mobileUnexecutedModalData, setMobileUnexecutedModalData] = useState<{
        allocIndex: number;
        alloc: WeeklyAllocation;
        cncId: string;
        comentario: string;
        pctCumplimiento: number;
        pctStr: string;
        taskName: string;
    } | null>(null);

    const [mobileQualityBlockedModalData, setMobileQualityBlockedModalData] = useState<{
        alloc: WeeklyAllocation;
        task: Task;
    } | null>(null);

    // Edit Single Allocation State
    const [isEditingAllocation, setIsEditingAllocation] = useState(false);
    const [editingAllocationIndex, setEditingAllocationIndex] = useState<number>(-1);
    const [editingAllocationData, setEditingAllocationData] = useState<WeeklyAllocation | null>(null);

    // Gantt Autoprogram Mass Assignment State
    const [ganttAutoPlanModalOpen, setGanttAutoPlanModalOpen] = useState(false);
    const [ganttAutoPlanSelectedRespId, setGanttAutoPlanSelectedRespId] = useState('');
    const [bulkResponsibleId, setBulkResponsibleId] = useState('');
    const [ganttSuggestionsData, setGanttSuggestionsData] = useState<any[]>([]);
    const [expandedTaskIdx, setExpandedTaskIdx] = useState<number | null>(null);

    // Context Menu
    const [contextMenu, setContextMenu] = useState<ContextMenuState>({ visible: false, x: 0, y: 0, taskId: null });
    const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
    const [itemToDeleteId, setItemToDeleteId] = useState<string | null>(null);

    // Dragging state for Gantt bars
    const [draggingTask, setDraggingTask] = useState<{ taskId: string; startX: number; originalStart: string } | null>(null);



    // Dragging Logic Effect
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!draggingTask) return;

            const deltaX = e.clientX - draggingTask.startX;
            const daysToShift = Math.round(deltaX / (100 / 7)); // 100px = 7 days

            if (daysToShift === 0) return;

            const updateTaskDates = (list: Task[], tid: string, shift: number, origStart: string): Task[] => {
                return list.map(t => {
                    if (t.id === tid) {
                        const sDate = new Date(origStart + "T12:00:00");
                        sDate.setDate(sDate.getDate() + shift);

                        // Ensure start lands on a working day
                        while (!isWorkingDay(sDate, workingDays, holidays)) {
                            sDate.setDate(sDate.getDate() + 1);
                        }
                        const newStart = getLocalDateStr(sDate);
                        const newEnd = calculateEndDate(newStart, t.duration || 1, workingDays, holidays);

                        return { ...t, start: newStart, end: newEnd };
                    }
                    if (t.children) return { ...t, children: updateTaskDates(t.children, tid, shift, origStart) };
                    return t;
                });
            };

            setTasks(prev => {
                const updated = updateTaskDates(prev, draggingTask.taskId, daysToShift, draggingTask.originalStart);
                return syncDependencies(updated, draggingTask.taskId);
            });
        };

        const handleMouseUp = () => {
            setDraggingTask(null);
        };

        if (draggingTask) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [draggingTask]);

    // Generic Task Logic Helper: Sync Dependencies using a Fixed-Point Iteration Solver
    const syncDependencies = (
        currentTasks: Task[],
        changedTaskId?: string,
        currentWorkingDays = workingDays,
        currentHolidays = holidays
    ): Task[] => {
        let updated = JSON.parse(JSON.stringify(currentTasks));

        let changed = true;
        let iterations = 0;
        const maxIterations = 50;

        while (changed && iterations < maxIterations) {
            changed = false;
            iterations++;

            const flat = flattenTasks(updated);

            flat.forEach(task => {
                if (task.type === 'folder' || !task.predecessors || task.predecessors.length === 0) return;

                let latestStartStr = "";
                let latestEndStr = "";

                task.predecessors.forEach(predStr => {
                    const parsed = parseDependencyLink(predStr);
                    if (!parsed) return;

                    const predOriginalId = parsed.id;
                    const type = parsed.type;
                    const lag = parsed.lag || 0;

                    const predTask = flat.find(pt => pt.id_original_project === predOriginalId);
                    if (!predTask || !predTask.start || !predTask.end) return;

                    if (type === "FS") {
                        const baseStart = getNextWorkingDay(predTask.end, currentWorkingDays, currentHolidays);
                        const candidateStart = lag !== 0 ? shiftWorkingDays(baseStart, lag, currentWorkingDays, currentHolidays) : baseStart;
                        if (!latestStartStr || candidateStart > latestStartStr) {
                            latestStartStr = candidateStart;
                        }
                    } else if (type === "SS") {
                        const candidateStart = lag !== 0 ? shiftWorkingDays(predTask.start, lag, currentWorkingDays, currentHolidays) : predTask.start;
                        if (!latestStartStr || candidateStart > latestStartStr) {
                            latestStartStr = candidateStart;
                        }
                    } else if (type === "FF") {
                        const candidateEnd = lag !== 0 ? shiftWorkingDays(predTask.end, lag, currentWorkingDays, currentHolidays) : predTask.end;
                        if (!latestEndStr || candidateEnd > latestEndStr) {
                            latestEndStr = candidateEnd;
                        }
                    } else if (type === "SF") {
                        const candidateEnd = lag !== 0 ? shiftWorkingDays(predTask.start, lag, currentWorkingDays, currentHolidays) : predTask.start;
                        if (!latestEndStr || candidateEnd > latestEndStr) {
                            latestEndStr = candidateEnd;
                        }
                    }
                });

                const duration = task.duration !== undefined && task.duration >= 0 ? task.duration : 1;

                let targetStart = "";
                if (latestStartStr && latestEndStr) {
                    const startFromEnd = calculateStartDate(latestEndStr, duration, currentWorkingDays, currentHolidays);
                    targetStart = latestStartStr > startFromEnd ? latestStartStr : startFromEnd;
                } else if (latestStartStr) {
                    targetStart = latestStartStr;
                } else if (latestEndStr) {
                    targetStart = calculateStartDate(latestEndStr, duration, currentWorkingDays, currentHolidays);
                }

                if (targetStart) {
                    let tStartD = new Date(targetStart + "T12:00:00");
                    while (!isWorkingDay(tStartD, currentWorkingDays, currentHolidays)) {
                        tStartD.setDate(tStartD.getDate() + 1);
                    }
                    const normalizedStart = getLocalDateStr(tStartD);
                    const targetEnd = calculateEndDate(normalizedStart, duration, currentWorkingDays, currentHolidays);

                    if (task.start !== normalizedStart || task.end !== targetEnd) {
                        task.start = normalizedStart;
                        task.end = targetEnd;
                        changed = true;
                    }
                }
            });
        }

        return calculateCriticalPath(updated, currentWorkingDays, currentHolidays);
    };

    // --- Actions ---

    // Lookahead Calculation Logic
    const calculatedWeeksAhead = useMemo(() => {
        if (!lookaheadRange.start || !lookaheadRange.end) return 0;
        const s = new Date(lookaheadRange.start);
        const e = new Date(lookaheadRange.end);
        const diffTime = e.getTime() - s.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays <= 0) return 0;
        return Math.max(1, Math.round(diffDays / 7));
    }, [lookaheadRange]);

    const handleOpenLookaheadModal = () => {
        if (isMobile) {
            alert("La creación de planificaciones debe realizarse exclusivamente desde la interfaz Web.");
            return;
        }
        // Default: Next Monday + 4 weeks (standard lookahead)
        const today = new Date();
        const nextMonday = new Date(today);
        nextMonday.setDate(today.getDate() + ((1 + 7 - today.getDay()) % 7 || 7)); // Calculate next Monday

        const fourWeeksLater = new Date(nextMonday);
        fourWeeksLater.setDate(nextMonday.getDate() + 27); // 4 weeks window (approx 28 days - 1 day to end on Sunday)

        setLookaheadRange({
            start: nextMonday.toISOString().split('T')[0],
            end: fourWeeksLater.toISOString().split('T')[0]
        });
        setLookaheadModalOpen(true);
    };

    const handleCreateLookaheadPlan = () => {
        if (isMobile) {
            alert("La creación de planificaciones debe realizarse exclusivamente desde la interfaz Web.");
            return;
        }
        if (!lookaheadRange.start || !lookaheadRange.end) {
            alert("Por favor seleccione ambas fechas.");
            return;
        }

        if (editingLookaheadId) {
            setLookaheadPlans(prev => prev.map(p => {
                if (p.id === editingLookaheadId) {
                    return {
                        ...p,
                        weeksAhead: calculatedWeeksAhead,
                        startDate: lookaheadRange.start,
                        endDate: lookaheadRange.end,
                    };
                }
                return p;
            }));
            addActivity({
                type: 'planning',
                title: 'Planificación Intermedia Editada',
                description: `Se han modificado las fechas del plan ${lookaheadRange.start} al ${lookaheadRange.end}`,
                user: currentUser?.name || currentUser?.email || 'Usuario'
            });
            setLookaheadModalOpen(false);
            setEditingLookaheadId(null);
            return;
        }

        const carriedConstraints: Constraint[] = [];
        const previouslyClearedTaskIds: string[] = [];
        if (lookaheadPlans.length > 0) {
            const sorted = [...lookaheadPlans].sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
            const latestPlan = sorted[0];

            if (latestPlan) {
                const pending = latestPlan.constraints.filter(c => c.status !== 'Liberado');
                pending.forEach(c => {
                    const isOverdue = c.requiredDate < lookaheadRange.start;
                    carriedConstraints.push({
                        ...c,
                        isReprogrammed: true,
                        isOverdue,
                        originPlanId: latestPlan.id,
                        originWeekName: `Plan del ${latestPlan.startDate} al ${latestPlan.endDate}`
                    });
                });

                if (latestPlan.clearedTaskIds) {
                    const newPlanTasks = getTasksForLookahead(lookaheadRange.start, lookaheadRange.end);
                    const newPlanTaskIds = new Set(newPlanTasks.map(t => t.id));
                    latestPlan.clearedTaskIds.forEach(tid => {
                        if (newPlanTaskIds.has(tid)) {
                            previouslyClearedTaskIds.push(tid);
                        }
                    });
                }
            }
        }

        const newPlan: LookaheadPlan = {
            id: Math.random().toString(36).substr(2, 9),
            weeksAhead: calculatedWeeksAhead, // Calculated from range
            startDate: lookaheadRange.start,
            endDate: lookaheadRange.end,
            status: 'En Planificación',
            constraints: carriedConstraints,
            clearedTaskIds: previouslyClearedTaskIds
        };
        setLookaheadPlansBackup(JSON.parse(JSON.stringify(lookaheadPlans)));
        setLookaheadPlans([...lookaheadPlans, newPlan]);
        setActiveLookaheadId(newPlan.id);

        addActivity({
            type: 'planning',
            title: 'Nueva Planificación Intermedia',
            description: `Se ha creado una nueva planificación intermedia del ${newPlan.startDate} al ${newPlan.endDate}`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });

        triggerAppNotification({
            title: 'Nueva Planificación Intermedia',
            description: `Se ha creado la Planificación Intermedia #${newPlan.id} para el período del ${newPlan.startDate} al ${newPlan.endDate}.`,
            type: 'planning',
            eventType: 'planning_created_assigned',
            companyConfig,
            dedupKey: `lookahead_create_${newPlan.id}`
        });

        setLookaheadModalOpen(false);
    };

    const getTasksForLookahead = (startDate: string, endDate: string, planConstraints: Constraint[] = []) => {
        const taskIdsWithConstraints = new Set(planConstraints.map(c => c.taskId));

        // Use processedTasks where dates are inherited by weekly subtasks
        const result: Task[] = [];

        const collectTasks = (list: Task[]) => {
            list.forEach(t => {
                if (t.type === 'folder') {
                    if (t.children && t.children.length > 0) {
                        collectTasks(t.children);
                    }
                    return;
                }

                const overlaps = !!(t.start && t.end && t.start <= endDate && t.end >= startDate);
                const hasConstraint = taskIdsWithConstraints.has(t.id);
                const weeklyChildren = (t.children || []).filter(c => c.isWeeklySubtask && !c.isDeleted);
                const hasBreakdown = weeklyChildren.length > 0;

                // Si tiene desgloses, chequear si alguno de sus desgloses o ella misma solapa o tiene restricciones
                const childOverlapsOrHasConstraint = weeklyChildren.some(c =>
                    (c.start && c.end && c.start <= endDate && c.end >= startDate) || taskIdsWithConstraints.has(c.id)
                );

                if (overlaps || hasConstraint || childOverlapsOrHasConstraint) {
                    result.push(t);
                    if (hasBreakdown) {
                        weeklyChildren.forEach(child => {
                            const cOverlaps = !!(child.start && child.end && child.start <= endDate && child.end >= startDate);
                            const cHasConstraint = taskIdsWithConstraints.has(child.id);
                            if (cOverlaps || cHasConstraint || overlaps) {
                                result.push(child);
                            }
                        });
                    }
                }
            });
        };

        collectTasks(processedTasks);
        return result;
    };

    // Toggle Task Cleared Status (No Aplica Restricción)
    const handleToggleTaskCleared = (planId: string, taskId: string) => {
        setLookaheadPlans(prev => prev.map(p => {
            if (p.id === planId) {
                const currentCleared = p.clearedTaskIds || [];
                const isCleared = currentCleared.includes(taskId);
                let newCleared: string[];

                if (isCleared) {
                    newCleared = currentCleared.filter(id => id !== taskId);
                } else {
                    newCleared = [...currentCleared, taskId];
                }

                return { ...p, clearedTaskIds: newCleared };
            }
            return p;
        }));
    };

    // New / Edit Constraint Logic
    const handleOpenAddConstraintModal = (taskId: string) => {
        setEditingConstraintId(null);
        setActiveConstraintTask(taskId);
        setNewConstraintData({
            typeId: '',
            responsibleId: '',
            description: '',
            requiredDate: activeLookahead?.startDate || '',
            unitsType: 'na',
            unitIds: []
        });
        setConstraintModalOpen(true);
    };

    const handleOpenEditConstraintModal = (constraint: Constraint) => {
        setEditingConstraintId(constraint.id);
        setActiveConstraintTask(constraint.taskId);
        setNewConstraintData({
            typeId: constraint.typeId || '',
            responsibleId: constraint.responsibleId || '',
            description: constraint.description || '',
            requiredDate: constraint.requiredDate || '',
            unitsType: constraint.unitsType || 'na',
            unitIds: constraint.unitIds || []
        });
        setConstraintModalOpen(true);
    };

    const handleSaveConstraint = (dataToSave = newConstraintData) => {
        if (!activeLookaheadId || !activeConstraintTask) return;
        if (!dataToSave.typeId || !dataToSave.responsibleId || !dataToSave.description) {
            alert("Por favor complete todos los campos obligatorios.");
            return;
        }

        setLookaheadPlans(prev => prev.map(p => {
            if (p.id === activeLookaheadId) {
                if (editingConstraintId) {
                    // Update existing constraint
                    return {
                        ...p,
                        constraints: p.constraints.map(c => {
                            if (c.id === editingConstraintId) {
                                return {
                                    ...c,
                                    typeId: dataToSave.typeId,
                                    responsibleId: dataToSave.responsibleId,
                                    description: dataToSave.description,
                                    requiredDate: dataToSave.requiredDate,
                                    unitsType: dataToSave.unitsType,
                                    unitIds: dataToSave.unitIds
                                };
                            }
                            return c;
                        })
                    };
                }

                // Add new constraint
                const currentCleared = p.clearedTaskIds || [];
                const updatedCleared = currentCleared.filter(id => id !== activeConstraintTask);
                return {
                    ...p,
                    clearedTaskIds: updatedCleared,
                    constraints: [...p.constraints, {
                        id: Math.random().toString(36).substr(2, 9),
                        taskId: activeConstraintTask,
                        typeId: dataToSave.typeId,
                        description: dataToSave.description,
                        responsibleId: dataToSave.responsibleId,
                        requiredDate: dataToSave.requiredDate,
                        status: 'Pendiente',
                        unitsType: dataToSave.unitsType,
                        unitIds: dataToSave.unitIds
                    }]
                };
            }
            return p;
        }));
        setConstraintModalOpen(false);
        setEditingConstraintId(null);
    };

    const dispatchPlanningNotifications = async (planName: string, planType: 'Semanal' | 'Intermedia', assignments: { taskId: string, responsibleId: string }[]) => {
        try {
            const flat = flattenTasks(tasks);
            const assignmentsGrouped: Record<string, { userName: string, tasks: any[] }> = {};

            assignments.forEach(a => {
                if (!a.responsibleId || !a.taskId) return;
                const responsible = responsibles.find(r => r.id === a.responsibleId);
                if (!responsible || !responsible.email) return;

                const task = flat.find(t => t.id === a.taskId);
                if (!task) return;

                if (!assignmentsGrouped[responsible.email]) {
                    assignmentsGrouped[responsible.email] = {
                        userName: responsible.name,
                        tasks: []
                    };
                }
                assignmentsGrouped[responsible.email].tasks.push({
                    name: task.name,
                    startDate: task.start || 'N/D',
                    endDate: task.end || 'N/D'
                });
            });

            const recipients = responsibles
                .filter(r => r.email)
                .map(r => r.email);

            // Create in-app notifications
            if (setNotifications) {
                const newNotifications = Object.keys(assignmentsGrouped).map(email => {
                    const responsible = responsibles.find(r => r.email === email);
                    if (!responsible) return null;
                    const tasksQty = assignmentsGrouped[email].tasks.length;
                    return {
                        id: Math.random().toString(36).substr(2, 9),
                        userId: responsible.id,
                        title: `Nuevas tareas asignadas: Planificación ${planType}`,
                        description: `Se te han asignado ${tasksQty} nueva(s) tarea(s) en la Planificación ${planType} "${planName}".`,
                        date: new Date().toISOString(),
                        isRead: false,
                        type: 'planning'
                    };
                }).filter(Boolean);

                if (newNotifications.length > 0) {
                    setNotifications((prev: any) => [...newNotifications, ...prev]);
                }
            }

            if (recipients.length === 0) return;

            const { error } = await supabase.functions.invoke('notify-planning', {
                body: {
                    projectId: projectInfo.name,
                    planName,
                    planType,
                    assignmentsGrouped,
                    recipients,
                    authorName: 'Coordinador / Administrador'
                }
            });
            if (error) console.error("Notification Error:", error);
        } catch (e) {
            console.error("Error dispatching planning notifications:", e);
        }
    };

    const handleSaveLookaheadPlan = () => {
        const plan = lookaheadPlans.find(p => p.id === activeLookaheadId);
        addActivity({
            type: 'planning',
            title: 'Planificación Intermedia Guardada',
            description: `Se han guardado cambios en la planificación ${plan?.startDate} - ${plan?.endDate}`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });

        if (plan && plan.constraints) {
            dispatchPlanningNotifications(
                `${plan.startDate} al ${plan.endDate}`,
                'Intermedia',
                plan.constraints // Assuming they contain taskId and responsibleId
            );
        }

        // Just closes the editing view, effectively "saving" the state
        setActiveLookaheadId(null);
        setIsITEView(false); // Reset ITE view
    };

    const handleUpdateConstraint = (planId: string, constraintId: string, field: keyof Constraint, value: any) => {
        setLookaheadPlans(prev => prev.map(p => {
            if (p.id === planId) {
                return {
                    ...p,
                    constraints: p.constraints.map(c => c.id === constraintId ? { ...c, [field]: value } : c)
                };
            }
            return p;
        }));
    };

    const handleReleaseConstraintWithObservation = (planId: string, constraintId: string, observations: string, status: 'Pendiente' | 'Liberado' | 'NO LIBERADO' = 'Liberado') => {
        setLookaheadPlans(prev => prev.map(p => {
            if (p.id === planId) {
                return {
                    ...p,
                    constraints: p.constraints.map(c => c.id === constraintId ? { ...c, status, observations } : c)
                };
            }
            return p;
        }));
        setConstraintObsModalOpen(false);
    };

    const handleDeleteConstraint = (planId: string, constraintId: string) => {
        setLookaheadPlans(prev => prev.map(p => {
            if (p.id === planId) {
                return {
                    ...p,
                    constraints: p.constraints.filter(c => c.id !== constraintId)
                };
            }
            return p;
        }));
    };

    // Constraint Master Logic
    const handleAddConstraintGroup = () => {
        if (!newConstraintGroupName.trim()) return;
        const newGroup: ConstraintGroup = {
            id: Math.random().toString(36).substr(2, 9),
            name: newConstraintGroupName,
            definitions: []
        };
        setConstraintGroups([...constraintGroups, newGroup]);
        setNewConstraintGroupName('');
    };

    const handleAddConstraintDefinition = (groupId: string) => {
        if (!newConstraintName.trim()) return;
        const newDef: ConstraintDefinition = {
            id: Math.random().toString(36).substr(2, 9),
            name: newConstraintName
        };
        setConstraintGroups(prev => prev.map(g => {
            if (g.id === groupId) {
                return { ...g, definitions: [...g.definitions, newDef] };
            }
            return g;
        }));
        setNewConstraintName('');
        setActiveGroupForNewConstraint(null);
    };

    const handleEditConstraintGroup = (groupId: string, newName: string) => {
        if (!newName.trim()) return;
        setConstraintGroups(prev => prev.map(g => g.id === groupId ? { ...g, name: newName } : g));
        setEditingConstraintGroupId(null);
    };

    const handleDeleteConstraintGroup = (groupId: string) => {
        if (window.confirm("¿Seguro que desea eliminar este grupo y todas sus definiciones?")) {
            setConstraintGroups(prev => prev.filter(g => g.id !== groupId));
        }
    };

    const handleEditConstraintDefinition = (groupId: string, defId: string, newName: string) => {
        if (!newName.trim()) return;
        setConstraintGroups(prev => prev.map(g => {
            if (g.id === groupId) {
                return {
                    ...g,
                    definitions: g.definitions.map(d => d.id === defId ? { ...d, name: newName } : d)
                };
            }
            return g;
        }));
        setEditingConstraintDefId(null);
    };

    const handleDeleteConstraintDefinition = (groupId: string, defId: string) => {
        if (window.confirm("¿Seguro que desea eliminar esta definición de restricción?")) {
            setConstraintGroups(prev => prev.map(g => {
                if (g.id === groupId) {
                    return { ...g, definitions: g.definitions.filter(d => d.id !== defId) };
                }
                return g;
            }));
        }
    };

    const isProtocolProgressedLocal = (taskId: string, unitId: string) => {
        return isProtocolProgressed(taskId, unitId, savedProtocols, protocolConfigs);
    };

    const calculateTaskProgressLocal = (task: Task): number => {
        if (task.children && task.children.length > 0) {
            const weeklySubtasks = task.children.filter(c => c.isWeeklySubtask);
            const masterSubtasks = task.children.filter(c => !c.isWeeklySubtask);

            if (masterSubtasks.length > 0) {
                // Carpeta contenedora -> Ponderado por Duración de Tareas Terminales Hojas únicamente (excluyendo carpetas intermedias)
                const leafTasks = getLeafTasksOnly([task]);
                if (leafTasks.length === 0) return 0;

                let sumaDuracionTotal = 0;
                let sumaDuracionEjecutada = 0;

                leafTasks.forEach(leaf => {
                    const leafProgress = calculateTaskProgressLocal(leaf);
                    const dur = leaf.duration || 0;
                    if (dur > 0) {
                        sumaDuracionTotal += dur;
                        sumaDuracionEjecutada += dur * (leafProgress / 100);
                    }
                });

                if (sumaDuracionTotal > 0) {
                    return (sumaDuracionEjecutada / sumaDuracionTotal) * 100;
                } else {
                    const sum = leafTasks.reduce((acc, leaf) => acc + calculateTaskProgressLocal(leaf), 0);
                    return sum / leafTasks.length;
                }
            } else if (weeklySubtasks.length > 0) {
                // Tarea hoja desglosada semanalmente -> Suma ponderada de sus desgloses semanales
                const subtaskProgresses = weeklySubtasks.map(child => {
                    const weight = child.weight !== undefined ? child.weight : (100 / weeklySubtasks.length);
                    return calculateTaskProgressLocal(child) * (weight / 100);
                });
                return Math.min(100, subtaskProgresses.reduce((sum, p) => sum + p, 0));
            }
        }
        const physicalProgress = calculateTaskProgress(task.id, units, matrixData, protocolConfigs, projectInfo, savedProtocols, weeklyPlans, tasks);
        return Math.min(100, (task.baseProgress !== undefined ? task.baseProgress : (task.progress || 0)) + physicalProgress);
    };

    const calculateWeeklyProgrammedProgress = React.useCallback((task: Task): number => {
        if (!activePlan) return 0;

        if (task.children && task.children.length > 0) {
            const weeklySubtasks = task.children.filter(c => c.isWeeklySubtask);
            const masterSubtasks = task.children.filter(c => !c.isWeeklySubtask);

            if (masterSubtasks.length > 0) {
                // Carpeta contenedora -> Ponderado por Duración de Tareas Terminales Hojas únicamente
                const leafTasks = getLeafTasksOnly([task]);
                if (leafTasks.length === 0) return 0;

                let sumaDuracionTotal = 0;
                let sumaDuracionEjecutada = 0;

                leafTasks.forEach(leaf => {
                    const leafProgress = calculateWeeklyProgrammedProgress(leaf);
                    const dur = leaf.duration || 0;
                    if (dur > 0) {
                        sumaDuracionTotal += dur;
                        sumaDuracionEjecutada += dur * (leafProgress / 100);
                    }
                });

                if (sumaDuracionTotal > 0) {
                    return (sumaDuracionEjecutada / sumaDuracionTotal) * 100;
                } else {
                    const sum = leafTasks.reduce((acc, leaf) => acc + calculateWeeklyProgrammedProgress(leaf), 0);
                    return sum / leafTasks.length;
                }
            } else if (weeklySubtasks.length > 0) {
                // Tarea hoja desglosada semanalmente -> Suma ponderada de sus desgloses semanales
                const subtaskProgresses = weeklySubtasks.map(child => {
                    const weight = child.weight !== undefined ? child.weight : (100 / weeklySubtasks.length);
                    return calculateWeeklyProgrammedProgress(child) * (weight / 100);
                });
                return Math.min(100, subtaskProgresses.reduce((sum, p) => sum + p, 0));
            }
        }

        // Leaf task / weekly subtask: calculate programmed progress in active weekly plan
        const parentTask = findParentTask(task.id, tasks);
        const targetTaskId = (task.isWeeklySubtask && parentTask) ? parentTask.id : task.id;

        const assignedUnits = units.filter(u => isTaskUnitAssigned(task.id, u.id, matrixData, tasks));
        const totalUnits = assignedUnits.length;
        if (totalUnits === 0) return 0;

        // Find allocations in the active plan for this task
        const allocations = activePlan.allocations.filter(a => a.taskId === task.id);
        let totalComprometido = 0;
        allocations.forEach(a => {
            totalComprometido += a.percentComprometido ?? 100;
        });

        return totalComprometido / totalUnits;
    }, [activePlan, tasks, units, matrixData]);

    const calculateWeeklyGlobalProgrammedProgress = React.useCallback((): number => {
        if (!activePlan) return 0;

        const flatTasks = flattenTasks(tasks);
        const masterLeafTasks = flatTasks.filter(t =>
            !t.isWeeklySubtask &&
            t.type === 'task' &&
            (!t.children || t.children.length === 0 || t.children.every(c => c.isWeeklySubtask))
        );

        let totalWeightedProgrammedProgress = 0;
        let totalProjectDuration = 0;

        masterLeafTasks.forEach(t => {
            const prog = calculateWeeklyProgrammedProgress(t);
            const duration = t.duration || 0;
            totalWeightedProgrammedProgress += prog * duration;
            totalProjectDuration += duration;
        });

        return totalProjectDuration > 0 ? (totalWeightedProgrammedProgress / totalProjectDuration) : 0;
    }, [activePlan, tasks, calculateWeeklyProgrammedProgress]);

    const calculateWeeklyGlobalGanttMeta = React.useCallback((): number => {
        if (!activePlan) return 0;

        const weekStartStr = activePlan.startDate;
        const weekEndStr = activePlan.endDate;
        if (!weekStartStr || !weekEndStr) return 0;

        // Step 1: Find working days in the active week
        const weekWorkingDays: string[] = [];
        let curr = new Date(weekStartStr + "T12:00:00");
        const end = new Date(weekEndStr + "T12:00:00");
        while (curr <= end) {
            if (isWorkingDay(curr)) {
                weekWorkingDays.push(getLocalDateStr(curr));
            }
            curr.setDate(curr.getDate() + 1);
        }

        if (weekWorkingDays.length === 0) return 0;

        // Step 2: Get master leaf tasks
        const flatTasks = flattenTasks(tasks);
        const masterLeafTasks = flatTasks.filter(t =>
            !t.isWeeklySubtask &&
            t.type === 'task' &&
            (!t.children || t.children.length === 0 || t.children.every(c => c.isWeeklySubtask))
        );

        // Step 3: Compute total master project duration
        const totalProjectDuration = masterLeafTasks.reduce((sum, t) => sum + (t.duration || 0), 0);
        if (totalProjectDuration === 0) return 0;

        // Step 4: Calculate quota of days for each task in this week
        let totalDaysInWeek = 0;
        masterLeafTasks.forEach(t => {
            if (t.start && t.end && (t.duration || 0) > 0) {
                // Count overlap working days
                let overlapCount = 0;
                weekWorkingDays.forEach(dayStr => {
                    if (dayStr >= t.start! && dayStr <= t.end!) {
                        overlapCount++;
                    }
                });
                totalDaysInWeek += overlapCount;
            }
        });

        return (totalDaysInWeek / totalProjectDuration) * 100;
    }, [activePlan, tasks, workingDays, holidays]);

    const calculateWeeklyRecoveryMeta = React.useCallback((metaGantt: number): number => {
        if (!activePlan || !projectStatus.delay || projectStatus.delay <= 0) return metaGantt;

        const weekStartStr = activePlan.startDate;
        if (!weekStartStr) return metaGantt;

        // Compute remaining duration in working days from weekStartStr to end of project
        const flatTasks = flattenTasks(tasks);
        const masterLeafTasks = flatTasks.filter(t =>
            !t.isWeeklySubtask &&
            t.type === 'task' &&
            (!t.children || t.children.length === 0 || t.children.every(c => c.isWeeklySubtask))
        );

        let remainingDuration = 0;
        masterLeafTasks.forEach(t => {
            if (t.start && t.end) {
                if (t.end < weekStartStr) {
                    // Task already finished before this week
                } else if (t.start >= weekStartStr) {
                    // Task starts in this week or in future, count full duration
                    remainingDuration += (t.duration || 0);
                } else {
                    // Task started in past but ends in this week or future, calculate remaining working days
                    remainingDuration += calculateWorkingDaysDuration(weekStartStr, t.end);
                }
            }
        });

        if (remainingDuration <= 0) return metaGantt;

        const delayDays = projectStatus.delay;
        const multiplier = (remainingDuration + delayDays) / remainingDuration;
        return metaGantt * multiplier;
    }, [activePlan, tasks, projectStatus, workingDays, holidays]);

    const getAccumulatedCompliance = React.useCallback((taskId: string, unitId: string) => {
        const appliesProtocol = protocolConfigs[taskId]?.applies ?? false;
        if (appliesProtocol && isProtocolProgressedLocal(taskId, unitId)) {
            return 100;
        }
        let accumulated = 0;
        const closedPlans = weeklyPlans.filter(p => p.status === 'Cerrada');
        closedPlans.forEach(cp => {
            cp.allocations.forEach(a => {
                if (a.taskId === taskId && a.unitId === unitId) {
                    accumulated += getAllocationCompliancePct(a);
                }
            });
        });
        return Math.min(100, accumulated);
    }, [protocolConfigs, savedProtocols, weeklyPlans]);

    const getTaskDurationWeeks = React.useCallback((t: Task): number => {
        if (!t.start || !t.end) return 1;
        const start = new Date(t.start);
        const end = new Date(t.end);
        const diffTime = Math.abs(end.getTime() - start.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        return Math.max(1, Math.ceil(diffDays / 7));
    }, []);

    const getGanttSuggestedTasks = React.useCallback(() => {
        if (!activePlan) return [];
        const weekStart = activePlan.startDate;
        const weekEnd = activePlan.endDate;
        if (!weekStart || !weekEnd) return [];

        const result: Task[] = [];

        flattenedTasks.forEach(t => {
            // Regla AGENTS.md #1, #2 y #10:
            // Solo procesamos tareas terminales de la Gantt maestra (hojas maestras)
            if (t.isWeeklySubtask) return;
            if (t.type !== 'task') return;
            const hasRealChildren = t.children && t.children.length > 0 && t.children.some(c => !c.isWeeklySubtask);
            if (hasRealChildren) return;
            if (!t.start || !t.end) return;

            // Verificar si la tarea madre o sus fechas aplican para la semana activa
            const isCurrent = t.start <= weekEnd && t.end >= weekStart;
            const isPast = t.end < weekStart;
            if (!isCurrent && !isPast) return;

            const weeklySubtasks = (t.children || []).filter(c => c.isWeeklySubtask && !c.isDeleted);

            if (weeklySubtasks.length > 0) {
                // Tarea madre desglosada: la tarea madre NO es programable.
                // En su lugar, sugerimos cada una de sus subtareas desglosadas que tenga avance < 100%.
                weeklySubtasks.forEach(sub => {
                    const subProgress = calculateTaskProgressLocal(sub);
                    if (subProgress < 100) {
                        result.push({
                            ...sub,
                            start: t.start || sub.start || '',
                            end: t.end || sub.end || '',
                            id_original_project: sub.id_original_project ?? t.id_original_project
                        });
                    }
                });
            } else {
                // Tarea madre sin desgloses: es programable directamente
                const progress = calculateTaskProgressLocal(t);
                if (progress < 100) {
                    result.push(t);
                }
            }
        });

        // Sort by sequential row index (id_original_project) ascending
        return result.sort((a, b) => {
            const aVal = a.id_original_project !== undefined && a.id_original_project !== null ? Number(a.id_original_project) : 99999;
            const bVal = b.id_original_project !== undefined && b.id_original_project !== null ? Number(b.id_original_project) : 99999;
            return aVal - bVal;
        });
    }, [activePlan, flattenedTasks, calculateTaskProgressLocal]);

    const calculateTotalProjectProgress = () => {
        return calculateProjectProgress(processedTasks, units, matrixData, protocolConfigs, projectInfo, savedProtocols, weeklyPlans);
    };

    const handleSyncProgressToDate = (date: string, delayCS: number = 0, delayRC: number = 0, weekIndex?: number) => {
        // 1. Calculate global progress (High precision)
        const globalProgress = calculateTotalProjectProgress();

        // 2. Add to progressHistory
        const newRecord: ProjectProgressSnapshot = {
            weekIndex,
            date,
            progress: globalProgress,
            delayCurvaS: delayCS,
            delayRutaCritica: delayRC
        };

        // Check if date or weekIndex already exists and replace it, or add new
        setProgressHistory(prev => {
            const filtered = prev.filter(r => (weekIndex && r.weekIndex ? r.weekIndex !== weekIndex : r.date !== date));
            return [...filtered, newRecord].sort((a, b) => a.date.localeCompare(b.date));
        });

        // 3. Update tasks in Master Plan with current calculated progress
        const syncRecursive = (list: Task[]): Task[] => {
            return list.map(t => {
                const updatedTask = { ...t };
                if (t.children && t.children.length > 0) {
                    updatedTask.children = syncRecursive(t.children);
                    updatedTask.progress = calculateTaskProgressLocal(updatedTask);
                } else if (t.type === 'task') {
                    if (updatedTask.baseProgress === undefined) {
                        updatedTask.baseProgress = t.progress || 0;
                    }
                    updatedTask.progress = calculateTaskProgressLocal(updatedTask);
                }
                return updatedTask;
            });
        };
        const updatedTasks = syncRecursive(tasks);
        setTasks(updatedTasks);
        if (forceSyncTasks) forceSyncTasks(updatedTasks);

        addActivity({
            type: 'planning',
            title: 'Avance Semanal Sincronizado',
            description: `Se ha sincronizado el avance del proyecto (${globalProgress}%) para la fecha ${date}`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });

        alert(`Sincronización completada para el ${date}: ${globalProgress}% de avance global.`);
    };

    const handleCreateFolderClick = (parentId: string | null = null) => {
        setTargetParentId(parentId);
        setEditingItemId(null);
        setInputName("");
        setInputWeight("");
        setModalMode('folder');
        setModalOpen(true);
        setContextMenu({ ...contextMenu, visible: false });
    };

    const openBreakdownModal = (parentTaskId: string) => {
        const parentTask = findTaskById(parentTaskId, tasks);
        if (!parentTask) return;
        const weeklySubtasks = (parentTask.children || []).filter(c => c.isWeeklySubtask && !c.isDeleted);
        setDraftSubtasks(weeklySubtasks.map((sub, idx) => ({
            id: sub.id,
            name: sub.name,
            weight: sub.weight !== undefined ? String(sub.weight) : "",
            id_original_project: sub.id_original_project || (parentTask.id_original_project ? `${parentTask.id_original_project}.${idx + 1}` : String(idx + 1)),
            isNew: false
        })));
        setNewSubtaskName("");
        setNewSubtaskWeight("");
        setIsAddingSubtask(false);
        setDraggedSubtaskIndex(null);
        setBreakdownParentTaskId(parentTaskId);
        setBreakdownModalOpen(true);
        setContextMenu({ ...contextMenu, visible: false });
    };

    const handleAddDraftSubtask = () => {
        if (!newSubtaskName.trim()) return;
        const parentTask = breakdownParentTaskId ? findTaskById(breakdownParentTaskId, tasks) : null;
        const parentCode = parentTask?.id_original_project !== undefined ? String(parentTask.id_original_project) : '';
        const nextIndex = draftSubtasks.length + 1;
        const subtaskCorrelative = parentCode ? `${parentCode}.${nextIndex}` : String(nextIndex);

        const newId = 'wsub_' + Math.random().toString(36).substr(2, 9);
        const enteredWeight = newSubtaskWeight.trim();

        setDraftSubtasks(prev => [
            ...prev,
            {
                id: newId,
                name: newSubtaskName.trim(),
                weight: enteredWeight,
                id_original_project: subtaskCorrelative,
                isNew: true
            }
        ]);
        setNewSubtaskName("");
        setNewSubtaskWeight("");
        setIsAddingSubtask(false);
    };

    const handleDeleteDraftSubtask = (id: string) => {
        setDraftSubtasks(prev => prev.filter(s => s.id !== id));
    };

    const handleUpdateDraftWeight = (id: string, weight: string) => {
        setDraftSubtasks(prev => prev.map(s => s.id === id ? { ...s, weight } : s));
    };

    const handleUpdateDraftName = (id: string, name: string) => {
        setDraftSubtasks(prev => prev.map(s => s.id === id ? { ...s, name } : s));
    };

    const handleSaveBreakdownModal = () => {
        if (!breakdownParentTaskId) return;

        const parentTask = findTaskById(breakdownParentTaskId, tasks);
        if (!parentTask) return;

        const parentCode = parentTask.id_original_project !== undefined ? String(parentTask.id_original_project) : '';
        const newSubtasksToInherit: Task[] = [];

        const updateRecursive = (list: Task[]): Task[] => {
            return list.map(t => {
                if (t.id === breakdownParentTaskId) {
                    const nonWeeklyChildren = (t.children || []).filter(c => !c.isWeeklySubtask);
                    const weeklyChildren: Task[] = draftSubtasks.map((d, idx) => {
                        const existing = (t.children || []).find(c => c.id === d.id);
                        const weightNum = d.weight.trim() !== '' ? parseFloat(d.weight) : 0;
                        const validWeight = !isNaN(weightNum) ? Math.round(weightNum * 100) / 100 : 0;
                        const subtaskCorrelative = d.id_original_project || (parentCode ? `${parentCode}.${idx + 1}` : String(idx + 1));

                        if (existing) {
                            return {
                                ...existing,
                                name: d.name,
                                id_original_project: subtaskCorrelative,
                                weight: validWeight,
                                start: t.start || existing.start || '',
                                end: t.end || existing.end || '',
                                duration: t.duration !== undefined ? t.duration : existing.duration
                            };
                        } else {
                            const newTaskObj: Task = {
                                id: d.id,
                                name: d.name,
                                type: 'task',
                                children: [],
                                expanded: true,
                                progress: 0,
                                start: t.start || '',
                                end: t.end || '',
                                duration: t.duration || 0,
                                id_original_project: subtaskCorrelative,
                                isWeeklySubtask: true,
                                weight: validWeight
                            };
                            newSubtasksToInherit.push(newTaskObj);
                            return newTaskObj;
                        }
                    });

                    return {
                        ...t,
                        expanded: true,
                        children: [...nonWeeklyChildren, ...weeklyChildren]
                    };
                }
                if (t.children && t.children.length > 0) {
                    return { ...t, children: updateRecursive(t.children) };
                }
                return t;
            });
        };

        const updatedTree = updateRecursive(tasks);
        setTasks(updatedTree);
        if (forceSyncTasks) forceSyncTasks(updatedTree);

        // Herencia y Traspaso Automático de Cubicaciones de Tarea Madre a Subtarea (Regla 1 & 3 Matriz EDT)
        if (newSubtasksToInherit.length > 0 && matrixData && setMatrixData && units && units.length > 0) {
            const newEntries: Record<string, string> = {};
            let inheritedCount = 0;
            newSubtasksToInherit.forEach(newItem => {
                units.forEach(u => {
                    const parentPlanVal = matrixData[`planning_${breakdownParentTaskId}_${u.id}`];
                    const parentQualVal = matrixData[`quality_${breakdownParentTaskId}_${u.id}`];
                    const parentPayVal = matrixData[`payment_${breakdownParentTaskId}_${u.id}`];
                    const activeVal = parentPlanVal || parentQualVal || parentPayVal;
                    if (activeVal && activeVal !== '-' && activeVal !== '0') {
                        newEntries[`planning_${newItem.id}_${u.id}`] = parentPlanVal || activeVal;
                        newEntries[`quality_${newItem.id}_${u.id}`] = parentQualVal || parentPlanVal || activeVal;
                        if (parentPayVal) newEntries[`payment_${newItem.id}_${u.id}`] = parentPayVal;
                        inheritedCount++;
                    }
                });
            });
            if (inheritedCount > 0) {
                setMatrixData(prev => ({ ...prev, ...newEntries }));
            }
        }

        setBreakdownModalOpen(false);
        setBreakdownParentTaskId(null);
    };

    const handleCreateSubtaskClick = (parentId: string) => {
        if (isBreakdownMode) {
            openBreakdownModal(parentId);
            return;
        }
        setTargetParentId(parentId);
        setEditingItemId(null);
        setInputName("");
        setInputWeight("");
        setModalMode('subtask'); // Used for Master tasks inside folders
        setModalOpen(true);
        setContextMenu({ ...contextMenu, visible: false });
    };

    const handleEditNameClick = (task: Task) => {
        setTargetParentId(null);
        setEditingItemId(task.id);
        setInputName(task.name);
        setInputWeight(task.weight !== undefined ? task.weight.toString() : "");
        setModalMode(task.type === 'folder' ? 'folder' : 'subtask');
        setModalOpen(true);
        setContextMenu({ ...contextMenu, visible: false });
    };

    const openWeightManagementModal = (parentTaskId: string) => {
        openBreakdownModal(parentTaskId);
    };

    const handleSaveSubtasksWeights = () => {
        // Kept for backward compatibility if called
        handleSaveBreakdownModal();
    };

    const adjustSubtasksWeights = (subtasks: Task[], currentId: string, currentWeight: number): Task[] => {
        return subtasks.map(c => {
            if (c.id === currentId) {
                return { ...c, weight: currentWeight };
            }
            return c;
        });
    };

    const handleDeleteItemClick = (taskId: string) => {
        const taskObj = findTaskById(taskId, tasks);
        if (taskObj && !taskObj.isWeeklySubtask && isBreakdownMode) {
            alert("Las tareas madre de la carta Gantt no se pueden eliminar desde el desglose.");
            return;
        }

        setItemToDeleteId(taskId);
        setDeleteConfirmModalOpen(true);
        setContextMenu({ ...contextMenu, visible: false });
    };

    const confirmDeleteTask = () => {
        if (!itemToDeleteId) return;

        const targetId = itemToDeleteId;

        const removeTaskFromTree = (list: Task[]): Task[] => {
            return list
                .filter(t => t.id !== targetId)
                .map(t => {
                    if (!t.children || t.children.length === 0) return t;

                    const updatedChildren = removeTaskFromTree(t.children);

                    return { ...t, children: updatedChildren };
                });
        };

        const updatedTree = removeTaskFromTree(tasks);
        setTasks(updatedTree);
        if (forceSyncTasks) forceSyncTasks(updatedTree);
        setDeleteConfirmModalOpen(false);
        setItemToDeleteId(null);
    };

    const handleMoveItemClick = (itemId: string) => {
        setMovingItemId(itemId);
        setMoveModalOpen(true);
        setContextMenu({ ...contextMenu, visible: false });
    };

    // Recursive helper to check if a folder is a descendant of another item
    const isDescendant = (parentList: Task[], childId: string): boolean => {
        const findChild = (list: Task[]): boolean => {
            return list.some(t => {
                if (t.id === childId) return true;
                if (t.children) return findChild(t.children);
                return false;
            });
        };
        return findChild(parentList);
    };

    // Recursive helper to build a list of all folders (excluding moving item and its descendants)
    const getValidTargetFolders = (list: Task[], movingId: string): { id: string; name: string; level: number }[] => {
        let result: { id: string; name: string; level: number }[] = [];

        const traverse = (items: Task[], currentLevel: number) => {
            items.forEach(item => {
                if (item.type === 'folder' && item.id !== movingId) {
                    const movingItem = findTaskById(movingId, tasks);
                    const isDesc = movingItem?.children ? isDescendant(movingItem.children, item.id) : false;

                    if (!isDesc) {
                        result.push({ id: item.id, name: item.name, level: currentLevel });
                        if (item.children) {
                            traverse(item.children, currentLevel + 1);
                        }
                    }
                }
            });
        };

        traverse(list, 0);
        return result;
    };

    const handleMoveItem = (targetParentId: string | null) => {
        if (!movingItemId) return;

        let itemToMove: Task | null = null;

        // 1. Remove the item from the tree and capture it
        const removeRecursive = (list: Task[]): Task[] => {
            return list.filter(t => {
                if (t.id === movingItemId) {
                    itemToMove = t;
                    return false;
                }
                return true;
            }).map(t => {
                if (t.children) {
                    return { ...t, children: removeRecursive(t.children) };
                }
                return t;
            });
        };

        const treeWithoutItem = removeRecursive(tasks);

        if (!itemToMove) {
            alert("No se pudo encontrar el elemento a mover.");
            setMoveModalOpen(false);
            return;
        }

        let updatedTree: Task[];

        // 2. Insert it at the target destination
        if (targetParentId === null) {
            // Move to root level
            updatedTree = [...treeWithoutItem, itemToMove];
        } else {
            // Move inside target folder
            const insertRecursive = (list: Task[]): Task[] => {
                return list.map(t => {
                    if (t.id === targetParentId) {
                        return {
                            ...t,
                            children: [...(t.children || []), itemToMove!]
                        };
                    }
                    if (t.children) {
                        return { ...t, children: insertRecursive(t.children) };
                    }
                    return t;
                });
            };
            updatedTree = insertRecursive(treeWithoutItem);
        }

        // 3. Update tasks state
        const processedTree = calculateCriticalPath(updatedTree, workingDays, holidays);
        setTasks(processedTree);
        if (forceSyncTasks) forceSyncTasks(processedTree);

        addActivity({
            type: 'planning',
            title: 'Elemento movido',
            description: `Se ha movido el elemento "${(itemToMove as Task).name}" a una nueva ubicación.`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });

        setMoveModalOpen(false);
        setMovingItemId(null);
    };

    const getTasksToReconcile = (importedTasks: Task[], currentTasks: Task[]): { parentTask: Task; existingSubtasks: Task[]; targetProgress: number }[] => {
        const existingTaskMap = new Map<string, Task>();
        const flatten = (list: Task[]) => {
            list.forEach(t => {
                existingTaskMap.set(t.id, t);
                if (t.children) flatten(t.children);
            });
        };
        flatten(currentTasks);

        const result: { parentTask: Task; existingSubtasks: Task[]; targetProgress: number }[] = [];

        const checkImported = (list: Task[]) => {
            list.forEach(t => {
                if (t.type === 'task' && t.progress !== undefined && t.progress > 0 && t.progress < 100) {
                    const existing = existingTaskMap.get(t.id);
                    if (existing && existing.children && existing.children.some(c => c.isWeeklySubtask)) {
                        result.push({
                            parentTask: t,
                            existingSubtasks: existing.children.filter(c => c.isWeeklySubtask && !c.isDeleted),
                            targetProgress: t.progress
                        });
                    }
                }
                if (t.children) checkImported(t.children);
            });
        };
        checkImported(importedTasks);
        return result;
    };

    const getSubtaskUnitInfo = (subtaskName: string, subtaskId: string) => {
        for (const plan of weeklyPlans) {
            const alloc = plan.allocations.find(a => a.taskId === subtaskId);
            if (alloc) {
                const unit = units.find(u => u.id === alloc.unitId);
                if (unit) {
                    const levelName = levels.find(l => l.id === unit.levelId)?.name || '-';
                    return { level: levelName, unit: unit.name };
                }
            }
        }
        const match = subtaskName.match(/(Manzana\s*\d+)\s*[-_]?\s*(Sitio\s*\d+)/i) ||
            subtaskName.match(/(Nivel\s*\d+)\s*[-_]?\s*(Unidad\s*\d+)/i) ||
            subtaskName.match(/(M\s*\d+)\s*[-_]?\s*(S\s*\d+)/i);
        if (match) {
            return { level: match[1], unit: match[2] };
        }
        return { level: '-', unit: '-' };
    };

    const applyReconciliationProgress = (
        list: Task[],
        progressMap: Record<string, number>,
        unitProgressMap: Record<string, Record<string, number>>
    ): Task[] => {
        return list.map(t => {
            let updatedTask = { ...t };
            if (t.isWeeklySubtask) {
                if (progressMap[t.id] !== undefined) {
                    updatedTask.progress = progressMap[t.id];
                    updatedTask.baseProgress = progressMap[t.id];
                }
                if (unitProgressMap[t.id] !== undefined) {
                    updatedTask.reconciliationProgressUnits = { ...unitProgressMap[t.id] };
                }
            }
            if (t.children && t.children.length > 0) {
                updatedTask.children = applyReconciliationProgress(t.children, progressMap, unitProgressMap);
                const weeklySubtasks = updatedTask.children.filter(c => c.isWeeklySubtask && !c.isDeleted);
                if (weeklySubtasks.length > 0) {
                    const sum = weeklySubtasks.reduce((acc, c) => {
                        const weight = c.weight !== undefined ? c.weight : (100 / weeklySubtasks.length);
                        return acc + (c.progress || 0) * (weight / 100);
                    }, 0);
                    updatedTask.progress = Math.round(Math.min(100, sum) * 100) / 100;
                    updatedTask.baseProgress = updatedTask.progress;
                }
            }
            return updatedTask;
        });
    };

    const handleUpdateSubtaskProgress = (subtaskId: string, newProgress: number) => {
        if (!wizardData) return;
        const progress = Math.max(0, Math.min(100, isNaN(newProgress) ? 0 : newProgress));
        const updatedProgressMap = {
            ...wizardData.reconciliationProgress,
            [subtaskId]: progress
        };
        const updatedTasks = applyReconciliationProgress(wizardData.tasks, updatedProgressMap, wizardData.reconciliationUnitProgress);
        setWizardData({
            ...wizardData,
            reconciliationProgress: updatedProgressMap,
            tasks: updatedTasks
        });
    };

    const getSubtaskUnits = (subtaskId: string, subtaskName: string, parentTaskId: string) => {
        const flatTasks = flattenTasks(tasks);

        // 1. Try to find database tasks whose name matches the subtask name (since desgloses often copy the EDT leaf task name)
        const normalizedSubName = normalizeTaskName(subtaskName);
        let matchedTasks = normalizedSubName ? flatTasks.filter(t => normalizeTaskName(t.name) === normalizedSubName) : [];

        let targetDbTask: Task | null = null;

        if (matchedTasks.length > 0) {
            if (matchedTasks.length === 1) {
                targetDbTask = matchedTasks[0];
            } else {
                // If there are multiple matches (due to old and new reprogrammed branches),
                // pick the one under the parent task with the LATEST end/start date.
                const getParentTask = (targetId: string): Task | null => {
                    const findInList = (list: Task[], parent: Task | null): Task | null => {
                        for (const t of list) {
                            if (t.id === targetId) return parent;
                            if (t.children) {
                                const res = findInList(t.children, t);
                                if (res) return res;
                            }
                        }
                        return null;
                    };
                    return findInList(tasks, null);
                };

                matchedTasks.sort((a, b) => {
                    const pA = getParentTask(a.id);
                    const pB = getParentTask(b.id);
                    const dateA = pA?.end || pA?.start || '';
                    const dateB = pB?.end || pB?.start || '';
                    return dateB.localeCompare(dateA);
                });

                targetDbTask = matchedTasks[0];
            }
        }

        // 2. If not found by name, try to find the database task whose children contains this weekly subtask
        if (!targetDbTask) {
            targetDbTask = flatTasks.find(t => t.children && t.children.some(c => c.id === subtaskId));
        }

        // 3. Fallback to parentTaskId if still not found
        const actualTaskId = targetDbTask ? targetDbTask.id : parentTaskId;

        const parentMatrixUnits = units.filter(u => isTaskUnitAssigned(actualTaskId, u.id, matrixData, tasks));

        const allocatedUnitIds = new Set(
            weeklyPlans
                .flatMap(p => p.allocations)
                .filter(a => a.taskId === subtaskId)
                .map(a => a.unitId)
        );

        if (allocatedUnitIds.size > 0) {
            return parentMatrixUnits.filter(u => allocatedUnitIds.has(u.id));
        }

        const matchedUnits = parentMatrixUnits.filter(u => {
            const levelName = levels.find(l => l.id === u.levelId)?.name || '';
            const uName = u.name || '';
            const sName = subtaskName.toLowerCase();

            const levelMatch = levelName === '' || sName.includes(levelName.toLowerCase());
            const unitMatch = sName.includes(uName.toLowerCase());

            if (levelMatch && unitMatch) return true;

            const mSub = sName.match(/m\s*(\d+)/);
            const mLevel = levelName.toLowerCase().match(/manzana\s*(\d+)/) || levelName.toLowerCase().match(/nivel\s*(\d+)/);
            const sSub = sName.match(/s\s*(\d+)/);
            const sUnit = uName.toLowerCase().match(/sitio\s*(\d+)/) || uName.toLowerCase().match(/unidad\s*(\d+)/);

            if (mSub && mLevel && mSub[1] === mLevel[1] && sSub && sUnit && sSub[1] === sUnit[1]) {
                return true;
            }
            return false;
        });

        if (matchedUnits.length > 0) {
            return matchedUnits;
        }

        return parentMatrixUnits;
    };

    const getInitialUnitProgress = (sub: Task, unitId: string) => {
        if (unitId === 'general') return 0;
        let accumulatedCompliance = 0;
        weeklyPlans
            .filter(p => p.status === 'Cerrada')
            .flatMap(p => p.allocations)
            .forEach(a => {
                if (a.taskId === sub.id && a.unitId === unitId) {
                    accumulatedCompliance += getAllocationCompliancePct(a);
                }
            });
        accumulatedCompliance = Math.min(100, accumulatedCompliance);
        if (accumulatedCompliance > 0) return accumulatedCompliance;
        return 0;
    };

    const handleUpdateUnitProgress = (subtaskId: string, unitId: string, newProgress: number) => {
        if (!wizardData) return;
        const progress = Math.max(0, Math.min(100, isNaN(newProgress) ? 0 : newProgress));

        const subUnitProgress = {
            ...(wizardData.reconciliationUnitProgress[subtaskId] || {}),
            [unitId]: progress
        };

        const updatedUnitProgressMap = {
            ...wizardData.reconciliationUnitProgress,
            [subtaskId]: subUnitProgress
        };

        let foundSub: Task | null = null;
        let foundParent: Task | null = null;
        const search = (list: Task[], parent: Task | null) => {
            for (const t of list) {
                if (t.id === subtaskId) {
                    foundSub = t;
                    foundParent = parent;
                    return;
                }
                if (t.children) search(t.children, t);
            }
        };
        search(wizardData.tasks, null);

        const subUnits = getSubtaskUnits(subtaskId, foundSub ? foundSub.name : '', foundParent ? foundParent.id : '');
        let subtaskProgress = 0;
        if (subUnits.length > 0) {
            let sum = 0;
            subUnits.forEach(u => {
                const pVal = subUnitProgress[u.id] ?? (foundSub ? getInitialUnitProgress(foundSub, u.id) : 0);
                sum += pVal;
            });
            subtaskProgress = Math.round((sum / subUnits.length) * 100) / 100;
        }

        const updatedProgressMap = {
            ...wizardData.reconciliationProgress,
            [subtaskId]: subtaskProgress
        };

        const updatedTasks = applyReconciliationProgress(wizardData.tasks, updatedProgressMap, updatedUnitProgressMap);

        setWizardData({
            ...wizardData,
            reconciliationUnitProgress: updatedUnitProgressMap,
            reconciliationProgress: updatedProgressMap,
            tasks: updatedTasks
        });
    };

    const handleReconcileSequentially = (parentId: string) => {
        if (!wizardData) return;
        const targetItem = tasksToReconcile.find(t => t.parentTask.id === parentId);
        if (!targetItem) return;

        const target = targetItem.targetProgress;
        const subtasks = targetItem.existingSubtasks;

        const newProgressMap = { ...wizardData.reconciliationProgress };
        const newUnitProgressMap = { ...wizardData.reconciliationUnitProgress };
        let remaining = target;

        subtasks.forEach((sub) => {
            const weight = sub.weight !== undefined ? sub.weight : (100 / subtasks.length);
            const subUnits = getSubtaskUnits(sub.id, sub.name, parentId);
            newUnitProgressMap[sub.id] = { ...(newUnitProgressMap[sub.id] || {}) };

            if (subUnits.length > 0) {
                if (remaining >= weight) {
                    newProgressMap[sub.id] = 100;
                    subUnits.forEach(u => {
                        newUnitProgressMap[sub.id][u.id] = 100;
                    });
                    remaining -= weight;
                } else if (remaining > 0) {
                    const subProgress = Math.round((remaining / weight) * 100 * 100) / 100;
                    newProgressMap[sub.id] = subProgress;

                    let pool = subProgress * subUnits.length;
                    subUnits.forEach(u => {
                        if (pool >= 100) {
                            newUnitProgressMap[sub.id][u.id] = 100;
                            pool -= 100;
                        } else if (pool > 0) {
                            newUnitProgressMap[sub.id][u.id] = Math.round(pool * 100) / 100;
                            pool = 0;
                        } else {
                            newUnitProgressMap[sub.id][u.id] = 0;
                        }
                    });
                    remaining = 0;
                } else {
                    newProgressMap[sub.id] = 0;
                    subUnits.forEach(u => {
                        newUnitProgressMap[sub.id][u.id] = 0;
                    });
                }
            } else {
                newProgressMap[sub.id] = 0;
            }
        });

        const updatedTasks = applyReconciliationProgress(wizardData.tasks, newProgressMap, newUnitProgressMap);
        setWizardData({
            ...wizardData,
            reconciliationProgress: newProgressMap,
            reconciliationUnitProgress: newUnitProgressMap,
            tasks: updatedTasks
        });
    };

    const handleUpdateSubtaskWeight = (parentId: string, subtaskId: string, newWeight: number) => {
        if (!wizardData) return;

        const updateTaskWeights = (list: Task[]): Task[] => {
            return list.map(t => {
                if (t.id === parentId && t.children) {
                    const updatedChildren = t.children.map(child => {
                        if (child.id === subtaskId) {
                            return { ...child, weight: newWeight };
                        }
                        return child;
                    });
                    return { ...t, children: updatedChildren };
                }
                if (t.children && t.children.length > 0) {
                    return { ...t, children: updateTaskWeights(t.children) };
                }
                return t;
            });
        };

        const updatedTasks = updateTaskWeights(wizardData.tasks);
        setWizardData({
            ...wizardData,
            tasks: updatedTasks
        });
    };

    const startImportWizard = (
        processedTasks: Task[],
        activeWorkingDays: boolean[],
        activeHolidays: any[],
        activeHoursPerDay: number,
        isReprogramming: boolean,
        comment: string
    ) => {
        let minStart = "";
        let maxEnd = "";
        const flat = flattenTasks(processedTasks).filter(t => t.type === 'task' && !t.isWeeklySubtask);
        flat.forEach(t => {
            if (t.start && (!minStart || t.start < minStart)) minStart = t.start;
            if (t.end && (!maxEnd || t.end > maxEnd)) maxEnd = t.end;
        });

        const projectStartDate = minStart || projectInfo.startDate;
        const totalImportedProgress = calculateWeightedProgress(processedTasks);

        const initialWeeklyProgress: Record<number, string> = {};
        const tempHols = activeHolidays.map((h: any) => ({
            date: typeof h === 'string' ? h : h.date,
            name: typeof h === 'string' ? 'Feriado' : h.name
        }));

        const initialReconciliationTargets: Record<string, number> = {};
        const initialReconciliationProgress: Record<string, number> = {};
        const initialReconciliationUnitProgress: Record<string, Record<string, number>> = {};
        const tasksToReconcile = getTasksToReconcile(processedTasks, tasks);
        tasksToReconcile.forEach(item => {
            initialReconciliationTargets[item.parentTask.id] = item.targetProgress;
            item.existingSubtasks.forEach(sub => {
                initialReconciliationUnitProgress[sub.id] = {};
                const subUnits = getSubtaskUnits(sub.id, sub.name, item.parentTask.id);
                let sum = 0;
                subUnits.forEach(u => {
                    const initProg = getInitialUnitProgress(sub, u.id);
                    initialReconciliationUnitProgress[sub.id][u.id] = initProg;
                    sum += initProg;
                });
                initialReconciliationProgress[sub.id] = subUnits.length > 0 ? Math.round((sum / subUnits.length) * 100) / 100 : 0;
            });
        });

        const initialTasksTree = applyReconciliationProgress(processedTasks, initialReconciliationProgress, initialReconciliationUnitProgress);

        setIsWizardFullscreen(false);
        setExpandedSubtasks({});
        setWizardData({
            tasks: initialTasksTree,
            importedProgress: totalImportedProgress,
            activeWorkingDays,
            activeHolidays,
            activeHoursPerDay,
            isReprogramming,
            comment,
            weeklyProgress: initialWeeklyProgress,
            cutOffDate: maxEnd || projectStartDate,
            workingDays: [...activeWorkingDays],
            hoursPerDay: activeHoursPerDay,
            tempHolidays: tempHols,
            reconciliationTargets: initialReconciliationTargets,
            reconciliationProgress: initialReconciliationProgress,
            reconciliationUnitProgress: initialReconciliationUnitProgress,
            syncDay: 1
        });
        setWizardStep(1);
        setImportWizardModalOpen(true);
    };

    const processImportFlow = (
        processedTasks: Task[],
        activeWorkingDays: boolean[],
        activeHolidays: any[],
        activeHoursPerDay: number,
        isReprogramming: boolean,
        comment: string
    ): boolean => {
        // If a Gantt is already loaded, check for conflicts
        if (tasks.length > 0) {
            // Caso 1: Incompatibility
            const currentName = projectInfo?.name || "";
            // Find root/first task name in new Gantt
            const importedName = processedTasks[0]?.name || "";
            if (currentName && importedName && currentName.toLowerCase().trim() !== importedName.toLowerCase().trim()) {
                setConflictData({
                    processed: processedTasks,
                    activeWorkingDays,
                    activeHolidays,
                    activeHoursPerDay,
                    isReprogramming,
                    comment,
                    currentProjectName: currentName,
                    importedProjectName: importedName
                });
                setConflictType('incompatibility');
                setConflictModalOpen(true);
                setImportModalOpen(false);
                return true;
            }

            // Caso 2: Mode Change (database has 0% progress but import has progress)
            const currentProgress = calculateWeightedProgress(tasks);
            const importedProgress = calculateWeightedProgress(processedTasks);
            if (currentProgress === 0 && importedProgress > 0) {
                setConflictData({
                    processed: processedTasks,
                    activeWorkingDays,
                    activeHolidays,
                    activeHoursPerDay,
                    isReprogramming,
                    comment
                });
                setConflictType('mode_change');
                setConflictModalOpen(true);
                setImportModalOpen(false);
                return true;
            }

            // Caso 3: Changes Detected (structural changes)
            const flatOld = flattenTasks(tasks).filter(t => !t.isWeeklySubtask);
            const flatNew = flattenTasks(processedTasks);

            const oldKeys = new Set(flatOld.map(t => getTaskUniqueKey(t)));
            const newKeys = new Set(flatNew.map(t => getTaskUniqueKey(t)));

            const added = flatNew.filter(t => !oldKeys.has(getTaskUniqueKey(t)));
            const deleted = flatOld.filter(t => !newKeys.has(getTaskUniqueKey(t)));
            const modified = flatNew.filter(nt => {
                const ot = flatOld.find(o => getTaskUniqueKey(o) === getTaskUniqueKey(nt));
                if (!ot) return false;
                return ot.start !== nt.start || ot.end !== nt.end || ot.duration !== nt.duration;
            });

            if (added.length > 0 || deleted.length > 0 || modified.length > 0) {
                setConflictData({
                    processed: processedTasks,
                    activeWorkingDays,
                    activeHolidays,
                    activeHoursPerDay,
                    isReprogramming,
                    comment
                });
                setConflictType('changes_detected');
                setConflictModalOpen(true);
                setImportModalOpen(false);
                return true;
            }

            // If no conflicts/changes, perform normal merge
            return false;
        }

        // First import: open wizard
        startImportWizard(processedTasks, activeWorkingDays, activeHolidays, activeHoursPerDay, isReprogramming, comment);
        return true;
    };

    const handleConfirmImportWizard = async () => {
        if (!wizardData) return;
        const { tasks: processedTasks, workingDays: finalWorkingDays, tempHolidays: finalHolidays, hoursPerDay: finalHoursPerDay, isReprogramming, comment, importedProgress, weeklyProgress, cutOffDate, syncDay } = wizardData;

        let generatedHistory: ProjectProgressSnapshot[] = [];
        if (importedProgress > 0) {
            let minStart = "";
            const flat = flattenTasks(processedTasks).filter(t => t.type === 'task' && !t.isWeeklySubtask);
            flat.forEach(t => {
                if (t.start && (!minStart || t.start < minStart)) minStart = t.start;
            });
            const projectStartDate = minStart || projectInfo.startDate;
            const weeksList = generateHistoricalWeeks(projectStartDate, cutOffDate, processedTasks, syncDay);

            const parsedWeeklyVals = Object.keys(weeklyProgress).reduce((acc, key) => {
                const val = parseFloat(weeklyProgress[Number(key)]);
                acc[Number(key)] = isNaN(val) ? 0 : val;
                return acc;
            }, {} as Record<number, number>);

            let accumulatedReal = 0;
            generatedHistory = weeksList.map((w, idx) => {
                const realWeekly = parsedWeeklyVals[w.index] || 0;
                accumulatedReal += realWeekly;
                return {
                    id: Date.now().toString() + "_" + idx,
                    date: w.syncDateStr,
                    progress: accumulatedReal,
                    delayCurvaS: 0,
                    delayRutaCritica: 0
                };
            });
        }

        // Persist calendar settings
        setWorkingDays(finalWorkingDays);
        localStorage.setItem(`gantt_working_days_${projectInfo?.id || 'default'}`, JSON.stringify(finalWorkingDays));

        setHolidays(finalHolidays);
        localStorage.setItem(`gantt_holidays_${projectInfo?.id || 'default'}`, JSON.stringify(finalHolidays));

        setHoursPerDay(finalHoursPerDay);
        localStorage.setItem(`gantt_hours_per_day_${projectInfo?.id || 'default'}`, finalHoursPerDay.toString());

        if (syncDay !== undefined && syncDay !== null) {
            setSyncDay(syncDay);
            const pId = projectInfo?.id || 'default';
            localStorage.setItem(`gantt_sync_day_${pId}`, syncDay.toString());
            localStorage.setItem('gantt_sync_day', syncDay.toString());
        }

        // Update progressHistory & reset old plans if New Complete Import
        if (!isReprogramming || generatedHistory.length > 0) {
            setProgressHistory(generatedHistory);
        }

        if (!isReprogramming) {
            setWeeklyPlans([]);
            setLookaheadPlans([]);
        }

        // Compute critical path and save tasks
        const finalTasks = calculateCriticalPath(processedTasks, finalWorkingDays, finalHolidays);
        setTasks(finalTasks);
        if (forceSyncTasks) {
            await forceSyncTasks(finalTasks).catch(err => {
                console.error("Error forced syncing tasks:", err);
            });
        }

        // Generate baseline
        const newStart = finalTasks.length > 0 ? finalTasks[0].start : "";
        const newEnd = finalTasks.length > 0 ? finalTasks[0].end : "";
        const baseline: GanttBaseline = {
            id: Date.now().toString(),
            versionNumber: isReprogramming ? baselines.length : 0,
            uploadDate: new Date().toISOString(),
            tasksSnapshot: JSON.parse(JSON.stringify(finalTasks)),
            comment: comment || (isReprogramming ? "Reprogramación de Obra" : "Línea Base Inicial"),
            startDate: newStart || "N/A",
            endDate: newEnd || "N/A",
            vigente: true,
            selectedForChart: false
        };

        if (!isReprogramming) {
            setBaselines([baseline]);
        } else {
            const updatedBaselines = baselines.map(b => ({ ...b, vigente: false }));
            setBaselines([...updatedBaselines, baseline]);
        }

        addActivity({
            type: 'planning',
            title: isReprogramming ? 'Nueva Reprogramación de Obra' : 'Nueva Línea Base Importada',
            description: `Se importó y configuró un nuevo cronograma de ${finalTasks.length} partidas.`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });

        setImportWizardModalOpen(false);
        setWizardData(null);
        setImportModalOpen(false);
    };

    const handleReprogramConfirm = async () => {
        if (!reprogramCompareData) return;
        const { mergedTasks, comment } = reprogramCompareData;

        // Save merged tasks directly
        setTasks(mergedTasks);

        const newStart = mergedTasks.length > 0 ? mergedTasks[0].start : "";
        const newEnd = mergedTasks.length > 0 ? mergedTasks[0].end : "";
        const repBaseline: GanttBaseline = {
            id: Date.now().toString(),
            versionNumber: baselines.length,
            uploadDate: new Date().toISOString(),
            tasksSnapshot: JSON.parse(JSON.stringify(mergedTasks)),
            comment: comment || "Reprogramación oficial",
            startDate: newStart || "N/A",
            endDate: newEnd || "N/A",
            vigente: true,
            selectedForChart: false
        };
        const updatedBaselines = baselines.map(b => ({ ...b, vigente: false }));
        setBaselines([...updatedBaselines, repBaseline]);

        if (forceSyncTasks) {
            await forceSyncTasks(mergedTasks).catch(err => {
                console.error("Error forced syncing reprogrammed tasks:", err);
            });
        }

        addActivity({
            type: 'planning',
            title: 'Reprogramación de Obra Oficial',
            description: `Se reprogramó la estructura conservando los avances de terreno ingresados en K-LyDapp.`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });

        setReprogramCompareModalOpen(false);
        setReprogramCompareData(null);
    };

    const handleProceedIncompatibility = () => {
        if (!conflictData) return;
        const { processed, activeWorkingDays, activeHolidays, activeHoursPerDay, isReprogramming, comment } = conflictData;

        // Check if there is a mode change conflict next
        const currentProgress = calculateWeightedProgress(tasks);
        const importedProgress = calculateWeightedProgress(processed);
        if (currentProgress === 0 && importedProgress > 0) {
            setConflictType('mode_change');
            return;
        }

        // Check structural changes next
        const flatOld = flattenTasks(tasks).filter(t => !t.isWeeklySubtask);
        const flatNew = flattenTasks(processed);
        const oldKeys = new Set(flatOld.map(t => getTaskUniqueKey(t)));
        const newKeys = new Set(flatNew.map(t => getTaskUniqueKey(t)));
        const added = flatNew.filter(t => !oldKeys.has(getTaskUniqueKey(t)));
        const deleted = flatOld.filter(t => !newKeys.has(getTaskUniqueKey(t)));
        const modified = flatNew.filter(nt => {
            const ot = flatOld.find(o => getTaskUniqueKey(o) === getTaskUniqueKey(nt));
            if (!ot) return false;
            return ot.start !== nt.start || ot.end !== nt.end || ot.duration !== nt.duration;
        });

        if (added.length > 0 || deleted.length > 0 || modified.length > 0) {
            setConflictType('changes_detected');
            return;
        }

        // Complete normal merge
        setConflictModalOpen(false);
        setTasks(processed);
    };

    const handleModeChangeChoice = (importWithProgress: boolean) => {
        if (!conflictData) return;
        const { processed, activeWorkingDays, activeHolidays, activeHoursPerDay, isReprogramming, comment } = conflictData;

        if (importWithProgress) {
            // Open wizard step 1
            setConflictModalOpen(false);
            startImportWizard(processed, activeWorkingDays, activeHolidays, activeHoursPerDay, isReprogramming, comment);
        } else {
            // Import without progress: strip progress from imported tasks
            const strippedTasks = JSON.parse(JSON.stringify(processed));
            const stripProgress = (list: Task[]) => {
                list.forEach(t => {
                    t.progress = 0;
                    if (t.children) stripProgress(t.children);
                });
            };
            stripProgress(strippedTasks);

            // Now check if structural changes exist
            const flatOld = flattenTasks(tasks).filter(t => !t.isWeeklySubtask);
            const flatNew = flattenTasks(strippedTasks);
            const oldKeys = new Set(flatOld.map(t => getTaskUniqueKey(t)));
            const newKeys = new Set(flatNew.map(t => getTaskUniqueKey(t)));
            const added = flatNew.filter(t => !oldKeys.has(getTaskUniqueKey(t)));
            const deleted = flatOld.filter(t => !newKeys.has(getTaskUniqueKey(t)));
            const modified = flatNew.filter(nt => {
                const ot = flatOld.find(o => getTaskUniqueKey(o) === getTaskUniqueKey(nt));
                if (!ot) return false;
                return ot.start !== nt.start || ot.end !== nt.end || ot.duration !== nt.duration;
            });

            if (added.length > 0 || deleted.length > 0 || modified.length > 0) {
                setConflictData({
                    ...conflictData,
                    processed: strippedTasks
                });
                setConflictType('changes_detected');
            } else {
                // Perfect match: save and close
                setConflictModalOpen(false);
                setTasks(strippedTasks);
                if (forceSyncTasks) forceSyncTasks(strippedTasks);
            }
        }
    };

    const handleChangesDetectedChoice = (choice: 'full_import' | 'reprogram') => {
        if (!conflictData) return;
        const { processed, activeWorkingDays, activeHolidays, activeHoursPerDay, comment } = conflictData;

        if (choice === 'full_import') {
            setConflictModalOpen(false);
            startImportWizard(processed, activeWorkingDays, activeHolidays, activeHoursPerDay, false, comment);
        } else {
            // Reprogramming flow
            // 1. Ignore advances in XML: strip progress from processed
            const strippedTasks = JSON.parse(JSON.stringify(processed));
            const stripProgress = (list: Task[]) => {
                list.forEach(t => {
                    t.progress = 0;
                    if (t.children) stripProgress(t.children);
                });
            };
            stripProgress(strippedTasks);

            // 2. Perform smart merge preserving terrain progress
            const existingTaskMap: Record<string, Task> = {};
            const flattenAll = (list: Task[]) => {
                list.forEach(t => {
                    existingTaskMap[`key-${t.id}`] = t;
                    if (t.children) flattenAll(t.children);
                });
            };
            flattenAll(tasks);

            const mergeRecursive = (newList: Task[]): Task[] => {
                return newList.map(newTask => {
                    const key = `key-${newTask.id}`;
                    const existing = existingTaskMap[key];
                    if (existing) {
                        const existingWeeklySubtasks = existing.children
                            ? existing.children.filter(c => c.isWeeklySubtask)
                            : [];

                        let updatedWeeklySubtasks = [...existingWeeklySubtasks];
                        let finalProgress = existing.progress;
                        let finalBaseProgress = existing.baseProgress;

                        if (newTask.progress !== undefined && newTask.progress > 0) {
                            finalProgress = newTask.progress;
                            finalBaseProgress = newTask.progress;
                            updatedWeeklySubtasks = existingWeeklySubtasks.map(c => ({
                                ...c,
                                progress: newTask.progress,
                                baseProgress: newTask.progress
                            }));
                        }

                        const newMasterChildrenMerged = newTask.children
                            ? mergeRecursive(newTask.children)
                            : [];

                        const combinedChildren = [...updatedWeeklySubtasks, ...newMasterChildrenMerged];

                        return {
                            ...existing,
                            name: newTask.name,
                            start: newTask.start,
                            end: newTask.end,
                            duration: newTask.duration,
                            predecessors: newTask.predecessors,
                            successors: newTask.successors,
                            isCritical: newTask.isCritical,
                            progress: finalProgress,
                            baseProgress: finalBaseProgress,
                            children: combinedChildren.length > 0 ? combinedChildren : undefined
                        };
                    } else {
                        if (newTask.children) {
                            newTask.children = mergeRecursive(newTask.children);
                        }
                        return newTask;
                    }
                });
            };

            const merged = mergeRecursive(strippedTasks);

            // 3. Compute structural differences for compare modal
            const flatOld = flattenTasks(tasks).filter(t => !t.isWeeklySubtask);
            const flatNew = flattenTasks(strippedTasks);

            const oldKeys = new Set(flatOld.map(t => `key-${t.id}`));
            const newKeys = new Set(flatNew.map(t => `key-${t.id}`));

            const addedTasks = flatNew.filter(t => !oldKeys.has(`key-${t.id}`));
            const deletedTasks = flatOld.filter(t => !newKeys.has(`key-${t.id}`));

            // 4. Open Reprogram Compare Modal
            setConflictModalOpen(false);
            setReprogramCompareData({
                addedTasks,
                deletedTasks,
                mergedTasks: merged,
                activeWorkingDays,
                activeHolidays,
                activeHoursPerDay,
                comment
            });
            setReprogramCompareModalOpen(true);
        }
    };

    const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const fileName = file.name.toLowerCase();
        if (fileName.endsWith('.mpp')) {
            alert("Los archivos nativos de Microsoft Project (.mpp) son formatos binarios protegidos. Para una importación exitosa en la web, por favor exporte su proyecto desde MS Project como 'XML de Microsoft Project' (.xml) y suba ese archivo.");
            return;
        }

        const reader = new FileReader();
        reader.onload = async (event) => {
            const xmlText = event.target?.result as string;
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xmlText, "text/xml");

            // Helper to handle potential tag casing mismatches from different DOM implementations
            const getTags = (parent: Element | Document, tag: string) => {
                const found = parent.getElementsByTagName(tag);
                if (found.length > 0) return found;
                return parent.getElementsByTagName(tag.toLowerCase());
            };

            // Extract MinutesPerDay from the XML, defaulting to 480 (8 hours)
            const minutesPerDayEl = getTags(xmlDoc, "MinutesPerDay")[0];
            const minutesPerDay = minutesPerDayEl ? parseInt(minutesPerDayEl.textContent || "480", 10) : 480;

            // Parse Calendar Settings (working days and holidays) from XML if present
            let parsedWorkingDays: boolean[] | null = null;
            let parsedHolidays: { date: string; name: string }[] = [];
            let calendarHoursPerDay: number | null = null;

            const calendars = getTags(xmlDoc, "Calendar");
            let baseCalendar: Element | null = null;
            for (let i = 0; i < calendars.length; i++) {
                const cal = calendars[i];
                const isBase = (getTags(cal, "IsBaseCalendar")[0]?.textContent === "1");
                if (isBase) {
                    baseCalendar = cal;
                    break;
                }
            }
            if (!baseCalendar && calendars.length > 0) {
                baseCalendar = calendars[0];
            }

            if (baseCalendar) {
                const weekDays = getTags(baseCalendar, "WeekDay");
                if (weekDays.length > 0) {
                    const tempDays = [false, false, false, false, false, false, false];
                    let hasWorkingDaysInfo = false;
                    const dailyHours: number[] = [];

                    for (let j = 0; j < weekDays.length; j++) {
                        const wd = weekDays[j];
                        const dayTypeAttr = getTags(wd, "DayType")[0]?.textContent;
                        const dayWorkingAttr = getTags(wd, "DayWorking")[0]?.textContent;

                        if (dayTypeAttr && dayWorkingAttr) {
                            const dayType = parseInt(dayTypeAttr, 10); // 1 = Sunday, 2 = Monday, ..., 7 = Saturday
                            const dayWorking = dayWorkingAttr === "1";
                            if (dayType >= 1 && dayType <= 7) {
                                tempDays[dayType - 1] = dayWorking;
                                hasWorkingDaysInfo = true;
                            }

                            if (dayWorking) {
                                let dayHours = 0;
                                const wt = getTags(wd, "WorkingTime");
                                for (let k = 0; k < wt.length; k++) {
                                    const from = getTags(wt[k], "FromTime")[0]?.textContent || "";
                                    const to = getTags(wt[k], "ToTime")[0]?.textContent || "";
                                    if (from && to) {
                                        const [fH, fM] = from.split(":").map(Number);
                                        const [tH, tM] = to.split(":").map(Number);
                                        const diffMin = (tH * 60 + tM) - (fH * 60 + fM);
                                        dayHours += diffMin / 60;
                                    }
                                }
                                if (dayHours > 0) {
                                    dailyHours.push(dayHours);
                                }
                            }
                        }
                    }
                    if (hasWorkingDaysInfo) {
                        parsedWorkingDays = tempDays;
                    }

                    if (dailyHours.length > 0) {
                        // Calculate the mode (most frequent daily hours)
                        const counts: Record<number, number> = {};
                        let maxCount = 0;
                        let modeHours = 8;
                        dailyHours.forEach(h => {
                            counts[h] = (counts[h] || 0) + 1;
                            if (counts[h] > maxCount) {
                                maxCount = counts[h];
                                modeHours = h;
                            }
                        });
                        calendarHoursPerDay = modeHours;
                    }
                }

                // Parse exceptions (holidays)
                const exceptions = getTags(baseCalendar, "Exception");
                for (let j = 0; j < exceptions.length; j++) {
                    const exc = exceptions[j];
                    const dayWorking = getTags(exc, "DayWorking")[0]?.textContent === "1";
                    if (!dayWorking) {
                        const name = getTags(exc, "Name")[0]?.textContent || "Feriado XML";
                        const timePeriodEl = getTags(exc, "TimePeriod")[0];
                        const fromDateText = (timePeriodEl ? getTags(timePeriodEl, "FromDate")[0]?.textContent : null)
                            || getTags(exc, "FromDate")[0]?.textContent;
                        if (fromDateText) {
                            const dateStr = fromDateText.split("T")[0];
                            if (dateStr && !parsedHolidays.some(h => h.date === dateStr)) {
                                parsedHolidays.push({ date: dateStr, name });
                            }
                        }
                    }
                }
            }

            const activeWorkingDays = parsedWorkingDays || workingDays;
            const activeHolidays = parsedHolidays.length > 0 ? parsedHolidays : holidays;
            const activeHoursPerDay = calendarHoursPerDay || (minutesPerDay / 60) || hoursPerDay || 8;

            const projectTasks = getTags(xmlDoc, "Task");
            const tempTasks: any[] = [];
            const taskMap: Record<string, Task> = {};

            let rootXmlProgress: number | null = null;

            // First pass: Create task objects
            for (let i = 0; i < projectTasks.length; i++) {
                const xmlTask = projectTasks[i];

                // Helper to get element by tag name (prioritizing direct children)
                const getEl = (tag: string) => {
                    // 1. Try direct children first (avoids baseline confusion)
                    const directMatch = Array.from(xmlTask.children).find(c =>
                        c.localName?.toLowerCase() === tag.toLowerCase()
                    );
                    if (directMatch) return directMatch;

                    // 2. Fallback to any descendant
                    const found = getTags(xmlTask, tag);
                    if (found.length > 0) return found[0];
                    return null;
                };

                const uid = getEl("UID")?.textContent || "";
                const idOriginalText = getEl("ID")?.textContent || "";
                const idOriginal = parseInt(idOriginalText) || (i + 1); // physical row index (1-based)
                const name = getEl("Name")?.textContent || "";
                const type = getEl("Summary")?.textContent === "1" ? "folder" : "task";
                let start = (getEl("Start")?.textContent || getEl("StartDate")?.textContent || "")?.split("T")[0] || "";
                let end = (getEl("Finish")?.textContent || getEl("FinishDate")?.textContent || "")?.split("T")[0] || "";
                const isCritical = getEl("Critical")?.textContent === "1";

                // Parse Duration (handles ISO 8601 duration format like PT40H0M0S, P2D, etc.)
                const durationText = getEl("Duration")?.textContent || "0";
                let duration = 0;
                if (durationText.startsWith('PT') || durationText.startsWith('P') || durationText.startsWith('-PT') || durationText.startsWith('-P')) {
                    const isNegative = durationText.startsWith('-');
                    const cleanText = durationText.replace(/^-/, '');

                    const dMatch = cleanText.match(/(\d+)D/i);
                    const hMatch = cleanText.match(/(\d+)H/i);
                    const mMatch = cleanText.match(/(\d+)M/i);
                    const sMatch = cleanText.match(/(\d+)S/i);

                    const days = dMatch ? parseInt(dMatch[1], 10) : 0;
                    const hours = hMatch ? parseInt(hMatch[1], 10) : 0;
                    const minutes = mMatch ? parseInt(mMatch[1], 10) : 0;
                    const seconds = sMatch ? parseInt(sMatch[1], 10) : 0;

                    // Convert everything to working hours (using the XML's hoursPerDay)
                    const totalHours = (days * activeHoursPerDay) + hours + (minutes / 60) + (seconds / 3600);
                    duration = Math.ceil(totalHours / activeHoursPerDay);
                    if (isNegative) duration = -duration;
                } else if (durationText !== "0") {
                    // Non-ISO duration format in MS Project is in tenths of a minute
                    duration = Math.ceil(parseInt(durationText, 10) / (activeHoursPerDay * 600));
                } else if (start && end) {
                    // Fallback: calculate duration based on working days if duration is 0/missing but dates are present
                    duration = calculateWorkingDaysDuration(start, end, activeWorkingDays, activeHolidays);
                }

                const outlineLevel = parseInt(getEl("OutlineLevel")?.textContent || "1");

                // Fallback: If end is missing but start exists, calculate it or default it to start
                if (!end && start) {
                    if (duration > 0) {
                        try {
                            const sDate = new Date(start + "T12:00:00"); // Use noon to avoid TZ shifts
                            if (!isNaN(sDate.getTime())) {
                                end = calculateEndDate(start, duration, activeWorkingDays, activeHolidays);
                            }
                        } catch (e) {
                            console.error("Error calculating fallback end date", e);
                        }
                    } else {
                        end = start;
                    }
                }

                // Predecessors (raw extraction from XML, using UID references first)
                const rawPredecessors: string[] = [];
                const links = xmlTask.getElementsByTagName("PredecessorLink");
                for (let j = 0; j < links.length; j++) {
                    const link = links[j];
                    const predUid = Array.from(link.children).find(c => c.localName === "PredecessorUID")?.textContent || link.getElementsByTagName("PredecessorUID")[0]?.textContent;
                    const linkType = Array.from(link.children).find(c => c.localName === "Type")?.textContent || link.getElementsByTagName("Type")[0]?.textContent || "1";
                    const linkLagText = Array.from(link.children).find(c => c.localName === "LinkLag")?.textContent || link.getElementsByTagName("LinkLag")[0]?.textContent || "0";

                    if (predUid) {
                        // Project XML Types: 0:FF, 1:FS, 2:SS, 3:SF
                        let suffix = "FS";
                        if (linkType === "0") suffix = "FF";
                        else if (linkType === "2") suffix = "SS";
                        else if (linkType === "3") suffix = "SF";

                        // MS Project LinkLag is in tenths of a minute (activeHoursPerDay * 600 = 1 working day)
                        let lagDays = 0;
                        const rawLagVal = parseInt(linkLagText, 10);
                        if (!isNaN(rawLagVal) && rawLagVal !== 0) {
                            lagDays = Math.round(rawLagVal / (activeHoursPerDay * 600));
                        }
                        const lagSuffix = lagDays > 0 ? `+${lagDays}` : lagDays < 0 ? `${lagDays}` : "";

                        rawPredecessors.push(`${predUid}${suffix}${lagSuffix}`);
                    }
                }

                const containsCyrillic = /[\u0400-\u04FF]/.test(name);
                const isSyntheticRoot =
                    uid === "0" ||
                    uid === "-1" ||
                    idOriginal === 0 ||
                    idOriginal === -1 ||
                    outlineLevel === 0 ||
                    name.toLowerCase().includes("proyecto raíz") ||
                    name.toLowerCase() === "proyecto" ||
                    name.toLowerCase().includes("project summary") ||
                    name.toLowerCase().includes("duración total del proyecto") ||
                    name.toLowerCase().includes("общая продолжительность");

                // Prioridad de Ingesta desde el XML: % Av. Real (Texto8) o PercentComplete del nodo raíz (Tarea ID 0)
                const texto8El = xmlTask.getElementsByTagName("Texto8")[0] ||
                    xmlTask.getElementsByTagName("Text8")[0] ||
                    xmlTask.getElementsByTagName("texto8")[0] ||
                    xmlTask.getElementsByTagName("text8")[0];
                if (texto8El && texto8El.textContent) {
                    const val = parseFloat(texto8El.textContent.trim().replace(',', '.'));
                    if (!isNaN(val) && val >= 0) {
                        rootXmlProgress = val;
                    }
                }

                if (isSyntheticRoot && rootXmlProgress === null) {
                    const extAttrs = xmlTask.getElementsByTagName("ExtendedAttribute");
                    for (let j = 0; j < extAttrs.length; j++) {
                        const ext = extAttrs[j];
                        const valEl = ext.getElementsByTagName("Value")[0];
                        if (valEl && valEl.textContent) {
                            const val = parseFloat(valEl.textContent.trim().replace(',', '.'));
                            if (!isNaN(val) && val >= 0) {
                                rootXmlProgress = val;
                                break;
                            }
                        }
                    }
                    if (rootXmlProgress === null) {
                        const pcEl = getEl("PercentComplete");
                        if (pcEl && pcEl.textContent) {
                            const val = parseFloat(pcEl.textContent.trim().replace(',', '.'));
                            if (!isNaN(val) && val >= 0) {
                                rootXmlProgress = val;
                            }
                        }
                    }
                }

                if (!name || containsCyrillic || isSyntheticRoot) continue;

                const percentCompleteText = getEl("PercentComplete")?.textContent || "0";
                const parsedProgress = parseFloat(percentCompleteText) || 0;

                if (duration === 0 && start) {
                    end = start;
                }

                const task: Task = {
                    id: uid,
                    name,
                    type,
                    start: start || undefined,
                    end: end || undefined,
                    duration: duration >= 0 ? duration : undefined,
                    children: [],
                    expanded: true,
                    progress: parsedProgress,
                    predecessors: rawPredecessors.length > 0 ? rawPredecessors : undefined,
                    id_original_project: idOriginal,
                    isCritical: isCritical
                };

                taskMap[uid] = task;
                tempTasks.push({ task, outlineLevel, uid });
            }

            // Reindex correlativos (id_original_project) starting at 1 sequentially
            tempTasks.forEach((item, idx) => {
                item.task.id_original_project = idx + 1;
            });

            // Map XML UID predecessor references to id_original_project values and build successors
            const uidToOriginalIdMap: Record<string, number> = {};
            tempTasks.forEach(({ task, uid }) => {
                if (uid && task.id_original_project !== undefined) {
                    uidToOriginalIdMap[uid] = task.id_original_project;
                }
            });

            const rawPredecessorsMap: Record<string, string[]> = {};
            tempTasks.forEach(({ task }) => {
                if (task.predecessors) {
                    rawPredecessorsMap[task.id] = [...task.predecessors];
                    task.predecessors = []; // Clear to rebuild with original IDs
                }
                task.successors = []; // Initialize successors array
            });

            tempTasks.forEach(({ task }) => {
                const rawPreds = rawPredecessorsMap[task.id];
                if (rawPreds) {
                    const translatedPreds: string[] = [];
                    rawPreds.forEach(predStr => {
                        const parsed = parseDependencyLink(predStr);
                        if (parsed) {
                            const predUid = String(parsed.rawId);
                            const type = parsed.type;
                            const lagSuffix = parsed.lagSuffix;
                            const predOriginalId = uidToOriginalIdMap[predUid];
                            if (predOriginalId !== undefined) {
                                translatedPreds.push(`${predOriginalId}${type}${lagSuffix}`);

                                // Find predecessor task to add the inverse successor relation
                                const predTaskObj = tempTasks.find(t => t.uid === predUid);
                                if (predTaskObj) {
                                    predTaskObj.task.successors = predTaskObj.task.successors || [];
                                    const successorLink = `${task.id_original_project}${type}${lagSuffix}`;
                                    if (!predTaskObj.task.successors.includes(successorLink)) {
                                        predTaskObj.task.successors.push(successorLink);
                                    }
                                }
                            }
                        }
                    });
                    if (translatedPreds.length > 0) {
                        task.predecessors = translatedPreds;
                    } else {
                        delete task.predecessors;
                    }
                }
            });

            // Re-scale outline levels so that the minimum level starts at 1
            if (tempTasks.length > 0) {
                const minLevel = Math.min(...tempTasks.map(t => t.outlineLevel));
                const offset = minLevel - 1;
                tempTasks.forEach(item => {
                    item.outlineLevel = Math.max(1, item.outlineLevel - offset);
                });
            }

            // Second pass: Reconstruct hierarchy
            const rootTasks: Task[] = [];
            const stack: { level: number; task: Task }[] = [];

            tempTasks.forEach(({ task, outlineLevel }) => {
                while (stack.length > 0 && stack[stack.length - 1].level >= outlineLevel) {
                    stack.pop();
                }

                if (stack.length === 0) {
                    rootTasks.push(task);
                } else {
                    const parent = stack[stack.length - 1].task;
                    parent.children = parent.children || [];
                    parent.children.push(task);
                    parent.type = 'folder'; // Ensure parent is a folder if it has children
                }

                stack.push({ level: outlineLevel, task });
            });

            // Clean up empty fields to save Firestore space
            const optimizeTasks = (list: Task[]): Task[] => {
                return list.map(t => {
                    const cleaned: any = { ...t };
                    if (cleaned.children && cleaned.children.length === 0) delete cleaned.children;
                    if (cleaned.predecessors && cleaned.predecessors.length === 0) delete cleaned.predecessors;
                    if (cleaned.successors && cleaned.successors.length === 0) delete cleaned.successors;
                    if (cleaned.children) cleaned.children = optimizeTasks(cleaned.children);
                    return cleaned as Task;
                });
            };

            const optimizedRootTasks = optimizeTasks(rootTasks);

            if (rootXmlProgress !== null && optimizedRootTasks.length > 0) {
                if (optimizedRootTasks.length === 1 && optimizedRootTasks[0].type === 'folder') {
                    optimizedRootTasks[0].progress = rootXmlProgress;
                }
            }

            let finalStoredTasks = optimizedRootTasks;

            const isReprogramming = !!(window as any).__REPROGRAMMING_COMMENT;
            let newBaseline: GanttBaseline | null = null;
            let comment = "";

            if (isReprogramming) {
                comment = (window as any).__REPROGRAMMING_COMMENT || "Reprogramación";
            }

            // --- AUTO-MAPPING & RECONCILIATION FLOW ---
            if (tasks.length > 0) {
                // 1. Flatten old tasks
                const flatOld = flattenTasks(tasks).filter(t => !t.isWeeklySubtask);

                // 2. Map old tasks by normalized name, keeping them in an array
                const oldTasksByName: Record<string, Task[]> = {};
                flatOld.forEach(t => {
                    const norm = normalizeTaskName(t.name);
                    if (!oldTasksByName[norm]) oldTasksByName[norm] = [];
                    oldTasksByName[norm].push(t);
                });

                // 3. Match new tasks by name sequentially
                const matchedOldIds = new Set<string>();
                const mapNewTaskIds = (list: Task[]) => {
                    list.forEach(newTask => {
                        const norm = normalizeTaskName(newTask.name);
                        const candidates = oldTasksByName[norm] || [];
                        const match = candidates.find(c => !matchedOldIds.has(c.id));
                        if (match) {
                            newTask.id = match.id;
                            matchedOldIds.add(match.id);
                        }
                        if (newTask.children) mapNewTaskIds(newTask.children);
                    });
                };
                mapNewTaskIds(optimizedRootTasks);

                // 4. Find unmatched old tasks with protocols or weekly progress
                const unmatchedOldWithProgress = flatOld.filter(ot => {
                    const wasMatched = matchedOldIds.has(ot.id);
                    if (wasMatched) return false;
                    const hasProt = Object.values(savedProtocols).some(p => p.taskId === ot.id && p.status !== 'Inactivo');
                    const hasPlan = weeklyPlans.some(wp => wp.allocations.some(alloc => alloc.taskId === ot.id));
                    return hasProt || hasPlan;
                });

                if (unmatchedOldWithProgress.length > 0) {
                    // Open Reconciliation Modal and halt flow
                    setUnmatchedOldTasks(unmatchedOldWithProgress);
                    setReconciliationNewTasks(flattenTasks(optimizedRootTasks).filter(t => !t.isWeeklySubtask));
                    setReconciliationMappings({});
                    setSelectedOldTaskId(null);
                    setSelectedNewTaskId(null);

                    setPendingImportData({
                        finalStoredTasks: optimizedRootTasks,
                        isReprogramming: isReprogramming,
                        comment: comment,
                        parsedWorkingDays,
                        parsedHolidays,
                        parsedHoursPerDay: activeHoursPerDay,
                        updatedCount: 0,
                        newCount: 0,
                        deletedCount: 0
                    });
                    setReconciliationModalOpen(true);
                    return; // Pause the flow
                }

                // Proceed with Smart Merge directly using ID matching (key-${t.id})
                const getLocalUniqueKey = (t: Task): string => {
                    return `key-${t.id}`;
                };

                const existingTaskMap: Record<string, Task> = {};
                const existingMasterKeys = new Set<string>();
                const existingMasterTasks: Task[] = [];
                const flattenIntoMap = (list: Task[]) => {
                    list.forEach(t => {
                        const key = getLocalUniqueKey(t);
                        existingTaskMap[key] = t;
                        if (!t.isWeeklySubtask) {
                            existingMasterKeys.add(key);
                            existingMasterTasks.push(t);
                        }
                        if (t.children) flattenIntoMap(t.children);
                    });
                };
                flattenIntoMap(tasks);

                const newMasterKeys = new Set<string>();
                const collectNewMasterKeys = (list: Task[]) => {
                    list.forEach(t => {
                        const key = getLocalUniqueKey(t);
                        if (!t.isWeeklySubtask) {
                            newMasterKeys.add(key);
                        }
                        if (t.children) collectNewMasterKeys(t.children);
                    });
                };
                collectNewMasterKeys(optimizedRootTasks);

                const missing = existingMasterTasks.filter(t => !newMasterKeys.has(getLocalUniqueKey(t)));

                let updatedCount = 0;
                let newCount = 0;

                const mergeRecursive = (newList: Task[]): Task[] => {
                    return newList.map(newTask => {
                        const key = getLocalUniqueKey(newTask);
                        const existing = existingTaskMap[key];
                        if (existing) {
                            if (!newTask.isWeeklySubtask) updatedCount++;
                            const existingWeeklySubtasks = existing.children
                                ? existing.children.filter(c => c.isWeeklySubtask)
                                : [];

                            let updatedWeeklySubtasks = [...existingWeeklySubtasks];
                            let finalProgress = existing.progress;
                            let finalBaseProgress = existing.baseProgress;

                            if (newTask.progress !== undefined && newTask.progress > 0) {
                                finalProgress = newTask.progress;
                                finalBaseProgress = newTask.progress;
                                updatedWeeklySubtasks = existingWeeklySubtasks.map(c => ({
                                    ...c,
                                    progress: newTask.progress,
                                    baseProgress: newTask.progress
                                }));
                            }

                            const newMasterChildrenMerged = newTask.children
                                ? mergeRecursive(newTask.children)
                                : [];
                            const combinedChildren = [...updatedWeeklySubtasks, ...newMasterChildrenMerged];
                            return {
                                ...existing,
                                name: newTask.name,
                                start: newTask.start,
                                end: newTask.end,
                                duration: newTask.duration,
                                predecessors: newTask.predecessors,
                                successors: newTask.successors,
                                isCritical: newTask.isCritical,
                                progress: finalProgress,
                                baseProgress: finalBaseProgress,
                                children: combinedChildren.length > 0 ? combinedChildren : undefined
                            };
                        } else {
                            if (!newTask.isWeeklySubtask) newCount++;
                            if (newTask.children) newTask.children = mergeRecursive(newTask.children);
                            return newTask;
                        }
                    });
                };

                const mergedTasks = mergeRecursive(optimizedRootTasks);
                const deletedCount = missing.length;

                if (missing.length > 0) {
                    setPendingImportData({
                        finalStoredTasks: mergedTasks,
                        isReprogramming: isReprogramming,
                        comment: comment,
                        parsedWorkingDays,
                        parsedHolidays,
                        parsedHoursPerDay: activeHoursPerDay,
                        updatedCount,
                        newCount,
                        deletedCount
                    });
                    setMissingTasksList(missing.map(m => ({
                        id: m.id,
                        name: m.name,
                        key: getLocalUniqueKey(m)
                    })));
                    setMissingTasksModalOpen(true);
                    return;
                } else {
                    finalStoredTasks = mergedTasks;
                }
            } else {
                if (!confirm("Esta acción inicializará la Carta Gantt. ¿Desea continuar?")) return;
            }

            if (finalStoredTasks.length > 0) {
                const processed = calculateCriticalPath(finalStoredTasks, activeWorkingDays, activeHolidays);

                if (processImportFlow(processed, activeWorkingDays, activeHolidays, calendarHoursPerDay || activeHoursPerDay, isReprogramming, comment)) {
                    return;
                }

                if (parsedWorkingDays) {
                    setWorkingDays(parsedWorkingDays);
                    localStorage.setItem(`gantt_working_days_${projectInfo?.id || 'default'}`, JSON.stringify(parsedWorkingDays));
                }
                if (parsedHolidays.length > 0) {
                    setHolidays(parsedHolidays);
                    localStorage.setItem(`gantt_holidays_${projectInfo?.id || 'default'}`, JSON.stringify(parsedHolidays));
                }
                if (activeHoursPerDay) {
                    setHoursPerDay(activeHoursPerDay);
                    localStorage.setItem(`gantt_hours_per_day_${projectInfo?.id || 'default'}`, activeHoursPerDay.toString());
                }

                setTasks(processed);
                if (forceSyncTasks) forceSyncTasks(processed);
                setImportModalOpen(false);
            } else {
                alert("No se encontraron tareas válidas en el archivo.");
            }
        };
        reader.readAsText(file, "UTF-8");
    };

    const confirmMissingTasksImport = () => {
        if (!pendingImportData) return;
        const { finalStoredTasks, isReprogramming, comment, parsedWorkingDays, parsedHolidays, parsedHoursPerDay } = pendingImportData;

        const activeWorkingDays = parsedWorkingDays || workingDays;
        const activeHolidays = (parsedHolidays && parsedHolidays.length > 0) ? parsedHolidays : holidays;

        if (finalStoredTasks.length > 0) {
            const processed = calculateCriticalPath(finalStoredTasks, activeWorkingDays, activeHolidays);

            if (processImportFlow(processed, activeWorkingDays, activeHolidays, parsedHoursPerDay || hoursPerDay, isReprogramming, comment)) {
                setPendingImportData(null);
                setMissingTasksList([]);
                setMissingTasksModalOpen(false);
                return;
            }

            if (parsedWorkingDays) {
                setWorkingDays(parsedWorkingDays);
                localStorage.setItem(`gantt_working_days_${projectInfo?.id || 'default'}`, JSON.stringify(parsedWorkingDays));
            }
            if (parsedHolidays && parsedHolidays.length > 0) {
                setHolidays(parsedHolidays);
                localStorage.setItem(`gantt_holidays_${projectInfo?.id || 'default'}`, JSON.stringify(parsedHolidays));
            }
            if (parsedHoursPerDay) {
                setHoursPerDay(parsedHoursPerDay);
                localStorage.setItem(`gantt_hours_per_day_${projectInfo?.id || 'default'}`, parsedHoursPerDay.toString());
            }

            setTasks(processed);
            if (forceSyncTasks) forceSyncTasks(processed);
        }

        setPendingImportData(null);
        setMissingTasksList([]);
        setMissingTasksModalOpen(false);
    };

    const handleConfirmReconciliation = () => {
        if (!pendingImportData) return;
        const { finalStoredTasks, isReprogramming, comment, parsedWorkingDays, parsedHolidays, parsedHoursPerDay } = pendingImportData;

        // Apply manual mappings to the new tasks tree
        const applyMappings = (list: Task[]) => {
            list.forEach(newTask => {
                const mappedOldId = Object.keys(reconciliationMappings).find(
                    oldId => reconciliationMappings[oldId] === newTask.id
                );
                if (mappedOldId) {
                    newTask.id = mappedOldId;
                }
                if (newTask.children) applyMappings(newTask.children);
            });
        };
        applyMappings(finalStoredTasks);

        // Proceed to Smart Merge with new task IDs (which now match old IDs)
        const getLocalUniqueKey = (t: Task): string => {
            return `key-${t.id}`;
        };

        const existingTaskMap: Record<string, Task> = {};
        const existingMasterKeys = new Set<string>();
        const existingMasterTasks: Task[] = [];
        const flattenIntoMap = (list: Task[]) => {
            list.forEach(t => {
                const key = getLocalUniqueKey(t);
                existingTaskMap[key] = t;
                if (!t.isWeeklySubtask) {
                    existingMasterKeys.add(key);
                    existingMasterTasks.push(t);
                }
                if (t.children) flattenIntoMap(t.children);
            });
        };
        flattenIntoMap(tasks);

        const newMasterKeys = new Set<string>();
        const collectNewMasterKeys = (list: Task[]) => {
            list.forEach(t => {
                const key = getLocalUniqueKey(t);
                if (!t.isWeeklySubtask) {
                    newMasterKeys.add(key);
                }
                if (t.children) collectNewMasterKeys(t.children);
            });
        };
        collectNewMasterKeys(finalStoredTasks);

        const missing = existingMasterTasks.filter(t => !newMasterKeys.has(getLocalUniqueKey(t)));

        let updatedCount = 0;
        let newCount = 0;

        const mergeRecursive = (newList: Task[]): Task[] => {
            return newList.map(newTask => {
                const key = getLocalUniqueKey(newTask);
                const existing = existingTaskMap[key];
                if (existing) {
                    if (!newTask.isWeeklySubtask) updatedCount++;
                    const existingWeeklySubtasks = existing.children
                        ? existing.children.filter(c => c.isWeeklySubtask)
                        : [];

                    let updatedWeeklySubtasks = [...existingWeeklySubtasks];
                    let finalProgress = existing.progress;
                    let finalBaseProgress = existing.baseProgress;

                    if (newTask.progress !== undefined && newTask.progress > 0) {
                        finalProgress = newTask.progress;
                        finalBaseProgress = newTask.progress;
                        updatedWeeklySubtasks = existingWeeklySubtasks.map(c => ({
                            ...c,
                            progress: newTask.progress,
                            baseProgress: newTask.progress
                        }));
                    }

                    const newMasterChildrenMerged = newTask.children
                        ? mergeRecursive(newTask.children)
                        : [];
                    const combinedChildren = [...updatedWeeklySubtasks, ...newMasterChildrenMerged];
                    return {
                        ...existing,
                        name: newTask.name,
                        start: newTask.start,
                        end: newTask.end,
                        duration: newTask.duration,
                        predecessors: newTask.predecessors,
                        successors: newTask.successors,
                        isCritical: newTask.isCritical,
                        progress: finalProgress,
                        baseProgress: finalBaseProgress,
                        children: combinedChildren.length > 0 ? combinedChildren : undefined
                    };
                } else {
                    if (!newTask.isWeeklySubtask) newCount++;
                    if (newTask.children) newTask.children = mergeRecursive(newTask.children);
                    return newTask;
                }
            });
        };

        const mergedTasks = mergeRecursive(finalStoredTasks);
        const deletedCount = missing.length;

        // Reset reconciliation states
        setReconciliationModalOpen(false);
        setUnmatchedOldTasks([]);
        setReconciliationNewTasks([]);
        setReconciliationMappings({});

        if (missing.length > 0) {
            setPendingImportData({
                finalStoredTasks: mergedTasks,
                isReprogramming,
                comment,
                parsedWorkingDays,
                parsedHolidays,
                parsedHoursPerDay,
                updatedCount,
                newCount,
                deletedCount
            });
            setMissingTasksList(missing.map(m => ({
                id: m.id,
                name: m.name,
                key: getLocalUniqueKey(m)
            })));
            setMissingTasksModalOpen(true);
        } else {
            // Finalize Import
            const activeWorkingDays = parsedWorkingDays || workingDays;
            const activeHolidays = (parsedHolidays && parsedHolidays.length > 0) ? parsedHolidays : holidays;
            const processed = calculateCriticalPath(mergedTasks, activeWorkingDays, activeHolidays);

            if (processImportFlow(processed, activeWorkingDays, activeHolidays, parsedHoursPerDay || hoursPerDay, isReprogramming, comment)) {
                setPendingImportData(null);
                return;
            }

            if (parsedWorkingDays) {
                setWorkingDays(parsedWorkingDays);
                localStorage.setItem(`gantt_working_days_${projectInfo?.id || 'default'}`, JSON.stringify(parsedWorkingDays));
            }
            if (parsedHolidays && parsedHolidays.length > 0) {
                setHolidays(parsedHolidays);
                localStorage.setItem(`gantt_holidays_${projectInfo?.id || 'default'}`, JSON.stringify(parsedHolidays));
            }
            if (parsedHoursPerDay) {
                setHoursPerDay(parsedHoursPerDay);
                localStorage.setItem(`gantt_hours_per_day_${projectInfo?.id || 'default'}`, parsedHoursPerDay.toString());
            }

            setTasks(processed);
            if (forceSyncTasks) forceSyncTasks(processed);
            setPendingImportData(null);
        }
    };

    const handleCancelReconciliation = () => {
        setPendingImportData(null);
        setUnmatchedOldTasks([]);
        setReconciliationNewTasks([]);
        setReconciliationMappings({});
        setReconciliationModalOpen(false);
    };

    const invertRelationType = (type: string): string => {
        if (type === "FS") return "SF";
        if (type === "SF") return "FS";
        return type; // SS remains SS, FF remains FF
    };

    const handleUpdatePredecessors = (taskId: string, newPreds: string[]) => {
        setTasks(prev => {
            const flat = flattenTasks(prev);
            const target = flat.find(t => t.id === taskId);
            if (!target) return prev;

            const targetOrigId = target.id_original_project;
            if (targetOrigId === undefined) return prev;

            // Normalize relationship inputs (e.g., 7fc+2 -> 7FS+2, 8cc -> 8SS)
            const normalizedPreds = newPreds.map(predStr => {
                const parsed = parseDependencyLink(predStr);
                return parsed ? parsed.normalized : predStr;
            });

            const oldPreds = target.predecessors || [];

            // 1. Remove old mirror relations
            oldPreds.forEach(oldPredStr => {
                if (normalizedPreds.includes(oldPredStr)) return; // Still present
                const parsed = parseDependencyLink(oldPredStr);
                if (parsed) {
                    const predOrigId = parsed.id;
                    const type = parsed.type;
                    const lagSuffix = parsed.lagSuffix;
                    const predTask = flat.find(t => t.id_original_project === predOrigId);
                    if (predTask && predTask.successors) {
                        const mirrorLinkWithLag = `${targetOrigId}${invertRelationType(type)}${lagSuffix}`;
                        const mirrorLinkSameType = `${targetOrigId}${type}${lagSuffix}`;
                        predTask.successors = predTask.successors.filter(s => s !== mirrorLinkWithLag && s !== mirrorLinkSameType);
                    }
                }
            });

            // 2. Add new mirror relations
            normalizedPreds.forEach(newPredStr => {
                if (oldPreds.includes(newPredStr)) return; // Already present
                const parsed = parseDependencyLink(newPredStr);
                if (parsed) {
                    const predOrigId = parsed.id;
                    const type = parsed.type;
                    const lagSuffix = parsed.lagSuffix;
                    const predTask = flat.find(t => t.id_original_project === predOrigId);
                    if (predTask) {
                        predTask.successors = predTask.successors || [];
                        const mirrorLink = `${targetOrigId}${type}${lagSuffix}`;
                        if (!predTask.successors.includes(mirrorLink)) {
                            predTask.successors.push(mirrorLink);
                        }
                    }
                }
            });

            // Update target predecessors
            target.predecessors = normalizedPreds.length > 0 ? normalizedPreds : undefined;

            // Clean up empty arrays across all tasks
            flat.forEach(t => {
                if (t.successors && t.successors.length === 0) delete t.successors;
                if (t.predecessors && t.predecessors.length === 0) delete t.predecessors;
            });

            return syncDependencies(prev, taskId);
        });
    };

    const handleUpdateSuccessors = (taskId: string, newSuccs: string[]) => {
        setTasks(prev => {
            const flat = flattenTasks(prev);
            const target = flat.find(t => t.id === taskId);
            if (!target) return prev;

            const targetOrigId = target.id_original_project;
            if (targetOrigId === undefined) return prev;

            // Normalize relationship inputs (e.g., 7fc+2 -> 7FS+2, 8cc -> 8SS)
            const normalizedSuccs = newSuccs.map(succStr => {
                const parsed = parseDependencyLink(succStr);
                return parsed ? parsed.normalized : succStr;
            });

            const oldSuccs = target.successors || [];

            // 1. Remove old mirror relations
            oldSuccs.forEach(oldSuccStr => {
                if (normalizedSuccs.includes(oldSuccStr)) return; // Still present
                const parsed = parseDependencyLink(oldSuccStr);
                if (parsed) {
                    const succOrigId = parsed.id;
                    const type = parsed.type;
                    const lagSuffix = parsed.lagSuffix;
                    const succTask = flat.find(t => t.id_original_project === succOrigId);
                    if (succTask && succTask.predecessors) {
                        const mirrorLinkWithLag = `${targetOrigId}${invertRelationType(type)}${lagSuffix}`;
                        const mirrorLinkSameType = `${targetOrigId}${type}${lagSuffix}`;
                        succTask.predecessors = succTask.predecessors.filter(p => p !== mirrorLinkWithLag && p !== mirrorLinkSameType);
                    }
                }
            });

            // 2. Add new mirror relations
            normalizedSuccs.forEach(newSuccStr => {
                if (oldSuccs.includes(newSuccStr)) return; // Already present
                const parsed = parseDependencyLink(newSuccStr);
                if (parsed) {
                    const succOrigId = parsed.id;
                    const type = parsed.type;
                    const lagSuffix = parsed.lagSuffix;
                    const succTask = flat.find(t => t.id_original_project === succOrigId);
                    if (succTask) {
                        succTask.predecessors = succTask.predecessors || [];
                        const mirrorLink = `${targetOrigId}${type}${lagSuffix}`;
                        if (!succTask.predecessors.includes(mirrorLink)) {
                            succTask.predecessors.push(mirrorLink);
                        }
                    }
                }
            });

            // Update target successors
            target.successors = normalizedSuccs.length > 0 ? normalizedSuccs : undefined;

            // Clean up empty arrays across all tasks
            flat.forEach(t => {
                if (t.successors && t.successors.length === 0) delete t.successors;
                if (t.predecessors && t.predecessors.length === 0) delete t.predecessors;
            });

            return syncDependencies(prev, taskId);
        });
    };

    const confirmInputModal = () => {
        if (!inputName.trim()) return;
        const weightValue = (isBreakdownMode && modalMode === 'subtask') ? (parseFloat(inputWeight) || 0) : undefined;

        if (editingItemId) {
            const updateRecursive = (list: Task[]): Task[] => {
                return list.map(t => {
                    if (t.id === editingItemId) {
                        return { ...t, name: inputName, weight: weightValue };
                    }
                    if (t.children && t.children.some(c => c.id === editingItemId)) {
                        const updatedChildren = t.children.map(c => c.id === editingItemId ? { ...c, name: inputName, weight: weightValue } : c);
                        return { ...t, children: updatedChildren };
                    }
                    if (t.children) return { ...t, children: updateRecursive(t.children) };
                    return t;
                });
            };
            const updated = updateRecursive(tasks);
            setTasks(updated);
            if (forceSyncTasks) forceSyncTasks(updated);
        } else {
            const nextOriginalId = flattenTasks(tasks).reduce((max, t) => {
                const idVal = t.id_original_project;
                if (typeof idVal === 'number') {
                    return Math.max(max, idVal);
                }
                if (typeof idVal === 'string' && /^\d+$/.test(idVal)) {
                    return Math.max(max, parseInt(idVal, 10));
                }
                return max;
            }, 0) + 1;

            let assignedOriginalId: string | number = nextOriginalId;
            let subtaskStart = '2026-01-01';
            let subtaskEnd = '2026-01-15';
            let subtaskDuration = 14;

            if (isBreakdownMode && targetParentId !== null) {
                const parentTask = findTaskById(targetParentId, tasks);
                if (parentTask) {
                    const parentCode = parentTask.id_original_project !== undefined ? parentTask.id_original_project : '';
                    let nextChildCorrelative = 1;
                    if (parentTask.children && parentTask.children.length > 0) {
                        const parentCodeStr = String(parentCode);
                        let maxCorr = 0;
                        parentTask.children.forEach(child => {
                            const childCodeStr = String(child.id_original_project || '');
                            if (childCodeStr.startsWith(parentCodeStr + '.')) {
                                const parts = childCodeStr.split('.');
                                const corr = parseInt(parts[parts.length - 1], 10);
                                if (!isNaN(corr) && corr > maxCorr) {
                                    maxCorr = corr;
                                }
                            }
                        });
                        nextChildCorrelative = maxCorr > 0 ? maxCorr + 1 : parentTask.children.length + 1;
                    }
                    assignedOriginalId = parentCode ? `${parentCode}.${nextChildCorrelative}` : String(nextChildCorrelative);
                    if (parentTask.start) subtaskStart = parentTask.start;
                    if (parentTask.end) subtaskEnd = parentTask.end;
                    if (parentTask.duration !== undefined) subtaskDuration = parentTask.duration;
                }
            }

            const newItem: Task = {
                id: Math.random().toString(36).substr(2, 9),
                name: inputName,
                type: modalMode === 'folder' ? 'folder' : 'task',
                children: [],
                expanded: true,
                progress: 0,
                start: isBreakdownMode ? subtaskStart : '2026-01-01',
                end: isBreakdownMode ? subtaskEnd : '2026-01-15',
                duration: isBreakdownMode ? subtaskDuration : 14,
                id_original_project: assignedOriginalId,
                isWeeklySubtask: isBreakdownMode,
                weight: weightValue
            };

            if (targetParentId === null) {
                const updated = [...tasks, newItem];
                setTasks(updated);
                if (forceSyncTasks) forceSyncTasks(updated);
            } else {
                const addRecursive = (list: Task[]): Task[] => {
                    return list.map(t => {
                        if (t.id === targetParentId) {
                            const newChildren = [...(t.children || []), newItem];
                            return { ...t, children: newChildren };
                        }
                        if (t.children) return { ...t, children: addRecursive(t.children) };
                        return t;
                    });
                };
                const updated = addRecursive(tasks);
                setTasks(updated);
                if (forceSyncTasks) forceSyncTasks(updated);

                // REGLA 1 & 3: Herencia y Traspaso Automático de Cubicaciones de Tarea Madre a Subtarea
                if (matrixData && setMatrixData && units && units.length > 0) {
                    const newEntries: Record<string, string> = {};
                    let inheritedCount = 0;
                    units.forEach(u => {
                        const parentPlanVal = matrixData[`planning_${targetParentId}_${u.id}`];
                        const parentQualVal = matrixData[`quality_${targetParentId}_${u.id}`];
                        const parentPayVal = matrixData[`payment_${targetParentId}_${u.id}`];
                        const activeVal = parentPlanVal || parentQualVal || parentPayVal;
                        if (activeVal && activeVal !== '-' && activeVal !== '0') {
                            newEntries[`planning_${newItem.id}_${u.id}`] = parentPlanVal || activeVal;
                            newEntries[`quality_${newItem.id}_${u.id}`] = parentQualVal || parentPlanVal || activeVal;
                            if (parentPayVal) newEntries[`payment_${newItem.id}_${u.id}`] = parentPayVal;
                            inheritedCount++;
                        }
                    });
                    if (inheritedCount > 0) {
                        setMatrixData(prev => ({ ...prev, ...newEntries }));
                    }
                }
            }
        }
        setModalOpen(false);
    };

    const handleContextMenu = (e: React.MouseEvent, task: Task) => {
        if (activeTab === 'maestra' && !isMasterEditing) return;
        if (activeTab === 'semanal' && !isWeeklyEditing && !isBreakdownMode) return;
        if (activeTab === 'intermedia') return;

        e.preventDefault();
        e.stopPropagation();

        const menuHeight = 220; // Estimated height of context menu
        const menuWidth = 210;  // Width of context menu

        let top = e.clientY;
        let left = e.clientX;

        // If opening near bottom of viewport, adjust Y to pop upward!
        if (e.clientY + menuHeight > window.innerHeight) {
            top = Math.max(10, e.clientY - menuHeight);
        }

        // If opening near right edge of viewport, adjust X to stay on screen
        if (e.clientX + menuWidth > window.innerWidth) {
            left = Math.max(10, window.innerWidth - menuWidth - 10);
        }

        setContextMenu({
            visible: true,
            x: left,
            y: top,
            taskId: task.id
        });
    };

    const handleToggleCritical = (taskId: string) => {
        const toggleRecursive = (list: Task[]): Task[] => {
            return list.map(t => {
                if (t.id === taskId) {
                    const newCritical = !t.isCritical;
                    // Also toggle all children
                    const toggleChildren = (children: Task[]): Task[] => {
                        return children.map(c => ({
                            ...c,
                            isCritical: newCritical,
                            children: c.children ? toggleChildren(c.children) : undefined
                        }));
                    };
                    return {
                        ...t,
                        isCritical: newCritical,
                        children: t.children ? toggleChildren(t.children) : undefined
                    };
                }
                if (t.children) return { ...t, children: toggleRecursive(t.children) };
                return t;
            });
        };
        setTasks(toggleRecursive(tasks));
        setContextMenu({ visible: false, x: 0, y: 0, taskId: null });
    };

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            // Si el clic ocurrió dentro del menú contextual, dejamos que el evento del botón se ejecute
            if (target && target.closest('.fixed.z-\\[100\\]')) {
                return;
            }
            // Si el clic ocurrió dentro de un dropdown del header, no cerrar los dropdowns
            // (cada dropdown maneja su propio cierre internamente)
            if (target && target.closest('.header-dropdown-area')) {
                setContextMenu(prev => prev.visible ? { ...prev, visible: false, taskId: null } : prev);
                return;
            }
            setContextMenu(prev => prev.visible ? { ...prev, visible: false, taskId: null } : prev);
            setResponsiblesMenuOpen(false);
            setCncMenuOpen(false);
            setConstraintsMenuOpen(false);
            setIsAdicionalesMenuOpen(false);
        };
        // Use capture phase (true) so it intercepts clicks on table rows and outside areas
        window.addEventListener('click', handleClick, true);
        window.addEventListener('contextmenu', (e) => {
            // If right-clicked outside any custom handler target, dismiss context menu
            const target = e.target as HTMLElement;
            if (!target.closest('tr') && !target.closest('.fixed.z-\\[100\\]')) {
                setContextMenu(prev => prev.visible ? { ...prev, visible: false, taskId: null } : prev);
            }
        });
        return () => {
            window.removeEventListener('click', handleClick, true);
        };
    }, []);

    // Helper robusto para descarga de archivos Excel en navegadores
    const triggerDownloadExcel = (buffer: any, filename: string) => {
        // En navegadores modernos ExcelJS puede retornar Buffer (Node polyfill) o ArrayBuffer.
        // Convertirlo a Uint8Array asegura que el Blob binario no se corrompa ni pierda bytes.
        const uint8Array = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
        const blob = new Blob([uint8Array], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
            if (link.parentNode) {
                link.parentNode.removeChild(link);
            }
            window.URL.revokeObjectURL(url);
        }, 1000);
    };

    // --- Exportación a Excel ---
    const exportLookaheadToExcel = async (lookaheadId: string) => {
        try {
            const lookahead = lookaheadPlans.find(p => p.id === lookaheadId);
            if (!lookahead) return;

            const tasksInWindow = getTasksForLookahead(lookahead.startDate, lookahead.endDate, lookahead.constraints);
            const rowsToExport: any[] = [];

            tasksInWindow.forEach(task => {
                const constraints = lookahead.constraints.filter(c => c.taskId === task.id);
                const isCleared = lookahead.clearedTaskIds?.includes(task.id);

                const hasBreakdown = task.children?.some(c => c.isWeeklySubtask && !c.isDeleted);
                const isSubtask = !!task.isWeeklySubtask;
                const formattedName = isSubtask
                    ? `   ↳ ${cleanTaskName(task.name)}${task.weight !== undefined ? ` (Peso: ${task.weight}%)` : ''}`
                    : cleanTaskName(task.name);

                if (hasBreakdown) {
                    rowsToExport.push({
                        id_original_project: task.id_original_project || '-',
                        taskName: formattedName,
                        startDate: formatDateToShow(task.start),
                        endDate: formatDateToShow(task.end),
                        type: '-',
                        responsible: '-',
                        requiredDate: '-',
                        description: 'Desglosada en Subtareas',
                        units: '-',
                        status: 'Desglosada',
                        isReprogrammed: false,
                        isOverdue: false,
                        observations: '-'
                    });
                } else if (constraints.length > 0) {
                    constraints.forEach(c => {
                        let typeName = 'General';
                        constraintGroups.forEach(g => {
                            const def = g.definitions.find(d => d.id === c.typeId);
                            if (def) typeName = def.name;
                        });
                        const respName = responsibles.find(r => r.id === c.responsibleId)?.name || 'Sin asignar';

                        // Resolver unidades físicas asociadas
                        let unidadesFisicasText = 'No Aplica Unidades Físicas';
                        if (c.unitsType === 'all' || (c.unitsType === 'custom' && c.unitIds && c.unitIds.length > 0)) {
                            unidadesFisicasText = 'Aplica Unidades Físicas';
                        }

                        rowsToExport.push({
                            id_original_project: task.id_original_project || '-',
                            taskName: formattedName,
                            startDate: formatDateToShow(task.start),
                            endDate: formatDateToShow(task.end),
                            type: typeName,
                            responsible: respName,
                            requiredDate: c.requiredDate ? formatDateToShow(c.requiredDate) : '-',
                            description: c.description,
                            units: unidadesFisicasText,
                            status: c.status,
                            isReprogrammed: !!c.isReprogrammed,
                            isOverdue: !!c.isOverdue,
                            observations: c.observations?.trim() || '-'
                        });
                    });
                } else {
                    // Tarea programada en ventana sin restricciones o con No Aplica
                    rowsToExport.push({
                        id_original_project: task.id_original_project || '-',
                        taskName: formattedName,
                        startDate: formatDateToShow(task.start),
                        endDate: formatDateToShow(task.end),
                        type: '-',
                        responsible: '-',
                        requiredDate: '-',
                        description: isCleared ? 'No Aplica Restricción' : 'Sin Restricciones Identificadas',
                        units: '-',
                        status: isCleared ? 'Liberado' : 'Sin Restricción',
                        isReprogrammed: false,
                        isOverdue: false,
                        observations: '-'
                    });
                }
            });

            if (rowsToExport.length === 0) {
                alert("No hay tareas en el periodo de esta planificación intermedia.");
                return;
            }

            const workbook = new ExcelJS.Workbook();
            workbook.creator = 'K-LyDApp / ConstructOS';
            workbook.created = new Date();

            const worksheet = workbook.addWorksheet(`Plan Intermedio`);

            // Table Column Headers (Row 2)
            const headers = [
                '#',
                'Tarea Vigente',
                'Fecha Inicio',
                'Fecha Término',
                'Tipo de Restricción',
                'Responsable',
                'Fecha Requerida',
                'Detalle de Restricción',
                'Unidades Físicas',
                'Estado',
                'Observaciones'
            ];

            // Dynamic helper to compute Excel column letter for exact banner merging
            const getColumnLetter = (colIdx: number): string => {
                let letter = '';
                while (colIdx > 0) {
                    const temp = (colIdx - 1) % 26;
                    letter = String.fromCharCode(65 + temp) + letter;
                    colIdx = Math.floor((colIdx - temp) / 26);
                }
                return letter;
            };

            const lastColLetter = getColumnLetter(headers.length);

            // Title Header Banner (Row 1)
            worksheet.mergeCells(`A1:${lastColLetter}1`);
            const titleCell = worksheet.getCell('A1');
            const lookaheadStatusText = (lookahead.status || 'EN EJECUCIÓN').toUpperCase();
            titleCell.value = `PLANIFICACIÓN INTERMEDIA (${lookahead.startDate} al ${lookahead.endDate}) - ESTADO: ${lookaheadStatusText}`;
            titleCell.font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
            titleCell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF312E81' } // Dark Indigo / Purple
            };
            titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
            worksheet.getRow(1).height = 30;

            const headerRow = worksheet.addRow(headers);
            headerRow.height = 26;
            headerRow.eachCell((cell) => {
                cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FF1E293B' } // Slate 800
                };
                cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FF475569' } },
                    left: { style: 'thin', color: { argb: 'FF475569' } },
                    bottom: { style: 'medium', color: { argb: 'FF0F172A' } },
                    right: { style: 'thin', color: { argb: 'FF475569' } }
                };
            });

            // Add Data Rows
            rowsToExport.forEach((item, rowIdx) => {
                let statusDisplay = item.status;
                if (item.isReprogrammed) statusDisplay += ' (Reprogramada)';
                if (item.isOverdue) statusDisplay += ' (Vencida)';

                const rowValues = [
                    item.id_original_project,
                    item.taskName,
                    item.startDate,
                    item.endDate,
                    item.type,
                    item.responsible,
                    item.requiredDate,
                    item.description,
                    item.units,
                    statusDisplay,
                    item.observations
                ];

                const row = worksheet.addRow(rowValues);
                row.height = 22;

                const isEven = rowIdx % 2 === 0;
                const rowBgColor = isEven ? 'FFFFFFFF' : 'FFF8FAFC'; // Alternating zebra light slate

                row.eachCell((cell, colNum) => {
                    cell.font = { name: 'Calibri', size: 10, color: { argb: 'FF1E293B' } };
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: rowBgColor }
                    };
                    cell.border = {
                        top: { style: 'thin', color: { argb: 'FFE2E8F0' } },
                        left: { style: 'thin', color: { argb: 'FFE2E8F0' } },
                        bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } },
                        right: { style: 'thin', color: { argb: 'FFE2E8F0' } }
                    };
                    cell.alignment = { vertical: 'middle', horizontal: 'left' };

                    // Column 1: # (Número de Tarea)
                    if (colNum === 1) {
                        cell.alignment = { vertical: 'middle', horizontal: 'center' };
                        cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FF64748B' } };
                    }

                    // Column 3 & 4: Fechas Inicio / Término
                    if (colNum === 3 || colNum === 4) {
                        cell.alignment = { vertical: 'middle', horizontal: 'center' };
                    }

                    // Column 5: Tipo de Restricción
                    if (colNum === 5) {
                        cell.alignment = { vertical: 'middle', horizontal: 'center' };
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3E8FF' } }; // Soft Purple
                        cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FF6B21A8' } };
                    }

                    // Column 7: Fecha Requerida
                    if (colNum === 7) {
                        cell.alignment = { vertical: 'middle', horizontal: 'center' };
                    }

                    // Column 9: Unidades Físicas
                    if (colNum === 9) {
                        cell.alignment = { vertical: 'middle', horizontal: 'center' };
                    }

                    // Column 10: Estado
                    if (colNum === 10) {
                        cell.alignment = { vertical: 'middle', horizontal: 'center' };
                        if (item.status === 'Liberado') {
                            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDCFCE7' } }; // Soft Green
                            cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FF15803D' } };
                        } else if (item.status === 'NO LIBERADO' || item.isOverdue) {
                            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEE2E2' } }; // Soft Red
                            cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFB91C1C' } };
                        } else if (item.status === 'Sin Restricción') {
                            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } }; // Soft Slate / Gris
                            cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FF64748B' } };
                        } else {
                            // Pendiente (Amarillo claro destacado)
                            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF08A' } }; // Yellow 200
                            cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FF854D0E' } }; // Yellow 800
                        }
                    }

                    // Column 11: Observaciones
                    if (colNum === 11 && item.observations !== '-') {
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF3C7' } }; // Light amber background
                        cell.font = { name: 'Calibri', size: 10, italic: true, color: { argb: 'FF92400E' } };
                    }
                });
            });

            // Auto-fit Column Widths
            worksheet.columns.forEach((column) => {
                let maxLen = 12;
                column.eachCell?.({ includeEmpty: false }, (cell) => {
                    const cellLen = cell.value ? String(cell.value).length : 0;
                    if (cellLen > maxLen) maxLen = cellLen;
                });
                column.width = Math.min(45, maxLen + 4);
            });

            // Generate Excel File Download
            const buffer = await workbook.xlsx.writeBuffer();
            triggerDownloadExcel(buffer, `Planificacion_Intermedia_${lookahead.startDate}.xlsx`);
        } catch (error) {
            console.error("Error al exportar Planificación Intermedia a Excel:", error);
            alert("Ocurrió un error al generar el archivo Excel de Planificación Intermedia.");
        }
    };

    const exportWeeklyPlanToExcel = async (planId: string | number) => {
        try {
            const plan = weeklyPlans.find(p => String(p.id) === String(planId));
            if (!plan || !plan.allocations || plan.allocations.length === 0) {
                alert("No hay tareas programadas en este plan semanal.");
                return;
            }

            const workbook = new ExcelJS.Workbook();
            workbook.creator = 'K-LyDApp / ConstructOS';
            workbook.created = new Date();

            const worksheet = workbook.addWorksheet(`Plan Semanal ${plan.id}`);

            // Table Column Headers (Row 2)
            const headers = [
                'Nivel',
                'Unidad Física (Sitio)',
                'Tarea / Partida',
                'Responsable',
                ...dynamicDayColumns.map(col => `${col.dayLetter} (${col.dateNum})`),
                '% Comprometido',
                '% Cumplimiento',
                'Estado',
                'C.N.C (Causa No Cumplimiento)',
                'Justificación de Incumplimiento'
            ];

            // Dynamic helper to compute Excel column letter for exact banner merging
            const getColumnLetter = (colIdx: number): string => {
                let letter = '';
                while (colIdx > 0) {
                    const temp = (colIdx - 1) % 26;
                    letter = String.fromCharCode(65 + temp) + letter;
                    colIdx = Math.floor((colIdx - temp) / 26);
                }
                return letter;
            };

            const lastColLetter = getColumnLetter(headers.length);

            // Title Header Banner (Row 1)
            worksheet.mergeCells(`A1:${lastColLetter}1`);
            const titleCell = worksheet.getCell('A1');
            titleCell.value = `PLANIFICACIÓN SEMANAL #${plan.id} (${plan.startDate} al ${plan.endDate}) - ESTADO: ${plan.status.toUpperCase()}`;
            titleCell.font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
            titleCell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF312E81' } // Dark Indigo / Purple
            };
            titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
            worksheet.getRow(1).height = 30;

            const headerRow = worksheet.addRow(headers);
            headerRow.height = 26;
            headerRow.eachCell((cell) => {
                cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FF1E293B' } // Slate 800
                };
                cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FF475569' } },
                    left: { style: 'thin', color: { argb: 'FF475569' } },
                    bottom: { style: 'medium', color: { argb: 'FF0F172A' } },
                    right: { style: 'thin', color: { argb: 'FF475569' } }
                };
            });

            // Add Data Rows
            plan.allocations.forEach((alloc, rowIdx) => {
                const unit = units.find(u => u.id === alloc.unitId);
                const unitName = unit?.name || 'N/A';
                const levelName = levels.find(l => l.id === unit?.levelId)?.name || '-';
                const rawTaskName = alloc.isAdditional
                    ? (alloc.additionalTaskName || 'Tarea Adicional')
                    : (findTaskName(alloc.taskId, tasks) || '[Partida Obsoleta/Fuera de Gantt]');
                const taskName = cleanTaskName(rawTaskName);
                const responsibleName = responsibles.find(r => r.id === alloc.responsibleId)?.name || '-';

                const isExecuted = alloc.executed;

                const pctComprometido = alloc.percentComprometido ?? 100;
                const pctCumplimiento = getAllocationCompliancePct(alloc);

                const statusVal = getStatusValue(alloc);
                let statusStr = 'Pendiente';
                if (statusVal === 'si') {
                    statusStr = 'Ejecutado';
                } else if (statusVal === 'no') {
                    statusStr = 'No Ejecutado';
                } else {
                    statusStr = 'Pendiente';
                }

                const cncName = alloc.cncId ? (cncGroups.flatMap(g => g.causes).find(c => c.id === alloc.cncId)?.name || alloc.cncId) : '-';
                const justificationText = alloc.comentario?.trim() || '-';

                const dayValues = dynamicDayColumns.map(col => alloc.days[col.dayLetter] ? 'X' : '');

                const rowValues = [
                    levelName,
                    unitName,
                    taskName,
                    responsibleName,
                    ...dayValues,
                    `${pctComprometido}%`,
                    `${pctCumplimiento}%`,
                    statusStr,
                    cncName,
                    justificationText
                ];

                const row = worksheet.addRow(rowValues);
                row.height = 22;

                const isEven = rowIdx % 2 === 0;
                const rowBgColor = isEven ? 'FFFFFFFF' : 'FFF8FAFC'; // Alternating zebra light slate

                row.eachCell((cell, colNum) => {
                    cell.font = { name: 'Calibri', size: 10, color: { argb: 'FF1E293B' } };
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: rowBgColor }
                    };
                    cell.border = {
                        top: { style: 'thin', color: { argb: 'FFE2E8F0' } },
                        left: { style: 'thin', color: { argb: 'FFE2E8F0' } },
                        bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } },
                        right: { style: 'thin', color: { argb: 'FFE2E8F0' } }
                    };
                    cell.alignment = { vertical: 'middle', horizontal: 'left' };

                    // Column specific formatting & conditional styling:
                    if (colNum === 1 || colNum === 2 || colNum === 3 || colNum === 4) {
                        cell.alignment = { vertical: 'middle', horizontal: 'left' };
                    }

                    // Highlight additional tasks in Tarea column
                    if (colNum === 3 && alloc.isAdditional) {
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3E8FF' } }; // Soft Purple
                        cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FF6B21A8' } };
                    }

                    // Day Columns (X markers)
                    const dayColsStart = 5;
                    const dayColsEnd = 4 + dynamicDayColumns.length;
                    if (colNum >= dayColsStart && colNum <= dayColsEnd) {
                        cell.alignment = { vertical: 'middle', horizontal: 'center' };
                        if (cell.value === 'X') {
                            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDCFCE7' } }; // Soft Green
                            cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FF166534' } };
                        }
                    }

                    // % Comprometido Column
                    const pctComprometidoCol = dayColsEnd + 1;
                    if (colNum === pctComprometidoCol) {
                        cell.alignment = { vertical: 'middle', horizontal: 'center' };
                    }

                    // % Cumplimiento Column
                    const pctCumplimientoCol = dayColsEnd + 2;
                    if (colNum === pctCumplimientoCol) {
                        cell.alignment = { vertical: 'middle', horizontal: 'center' };
                        if (pctCumplimiento >= 100) {
                            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDCFCE7' } }; // Green 100%
                            cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FF15803D' } };
                        } else if (pctCumplimiento > 0) {
                            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF3C7' } }; // Amber partial
                            cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFB45309' } };
                        } else if (plan.status === 'Cerrada') {
                            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEE2E2' } }; // Red 0%
                            cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFB91C1C' } };
                        }
                    }

                    // Estado Column (Pendiente | No Ejecutado | Ejecutado)
                    const estadoCol = dayColsEnd + 3;
                    if (colNum === estadoCol) {
                        cell.alignment = { vertical: 'middle', horizontal: 'center' };
                        if (statusVal === 'si') {
                            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDCFCE7' } }; // Soft Green
                            cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FF15803D' } };
                        } else if (statusVal === 'no') {
                            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEE2E2' } }; // Soft Red
                            cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFB91C1C' } };
                        } else {
                            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF3C7' } }; // Soft Amber
                            cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFB45309' } };
                        }
                    }

                    // C.N.C Column
                    const cncCol = dayColsEnd + 4;
                    if (colNum === cncCol && cncName !== '-') {
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEE2E2' } }; // Light red warning
                        cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FF991B1B' } };
                    }

                    // Justificación Column
                    const justifCol = dayColsEnd + 5;
                    if (colNum === justifCol && justificationText !== '-') {
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF3C7' } }; // Light amber background
                        cell.font = { name: 'Calibri', size: 10, italic: true, color: { argb: 'FF92400E' } };
                    }
                });
            });

            // Auto-fit Column Widths
            worksheet.columns.forEach((column) => {
                let maxLen = 12;
                column.eachCell?.({ includeEmpty: false }, (cell) => {
                    const cellLen = cell.value ? String(cell.value).length : 0;
                    if (cellLen > maxLen) maxLen = cellLen;
                });
                column.width = Math.min(45, maxLen + 4);
            });

            // Generate Excel File Download
            const buffer = await workbook.xlsx.writeBuffer();
            triggerDownloadExcel(buffer, `Planificacion_Semanal_${plan.id}_${plan.startDate}.xlsx`);
        } catch (error) {
            console.error("Error al exportar Planificación Semanal a Excel:", error);
            alert("Ocurrió un error al generar el archivo Excel de Planificación Semanal.");
        }
    };

    // --- Specific Mode Logic ---

    const handleCreateWeeklyPlan = () => {
        if (isMobile) {
            alert("La creación de planificaciones debe realizarse exclusivamente desde la interfaz Web.");
            return;
        }
        if (!weeklyPlanDates.start || !weeklyPlanDates.end) {
            alert("Ingrese fechas validas");
            return;
        }

        if (editingWeeklyPlanId) {
            setWeeklyPlans(prev => prev.map(p => {
                if (p.id === editingWeeklyPlanId) {
                    return {
                        ...p,
                        startDate: weeklyPlanDates.start,
                        endDate: weeklyPlanDates.end,
                    };
                }
                return p;
            }));
            addActivity({
                type: 'planning',
                title: 'Planificación Semanal Editada',
                description: `Se han modificado las fechas de la planificación semanal #${editingWeeklyPlanId} del ${weeklyPlanDates.start} al ${weeklyPlanDates.end}`,
                user: currentUser?.name || currentUser?.email || 'Usuario'
            });
            setNewPlanModalOpen(false);
            setEditingWeeklyPlanId(null);
            return;
        }

        const newId = weeklyPlans.length > 0 ? Math.max(...weeklyPlans.map(p => p.id)) + 1 : 1;
        const newPlan: WeeklyPlan = {
            id: newId,
            startDate: weeklyPlanDates.start,
            endDate: weeklyPlanDates.end,
            status: 'En Planificación',
            allocations: []
        };

        setWeeklyPlansBackup(JSON.parse(JSON.stringify(weeklyPlans)));
        setWeeklyPlans([...weeklyPlans, newPlan]);
        setActivePlanId(newId);

        addActivity({
            type: 'planning',
            title: 'Nueva Planificación Semanal',
            description: `Se ha creado la planificación semanal #${newId} del ${newPlan.startDate} al ${newPlan.endDate}`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });

        triggerAppNotification({
            title: 'Nueva Planificación Semanal',
            description: `Se ha creado la Planificación Semanal #${newId} (${newPlan.startDate} al ${newPlan.endDate}).`,
            type: 'planning',
            eventType: 'planning_created_assigned',
            companyConfig,
            dedupKey: `weekly_create_${newId}`
        });

        setNewPlanModalOpen(false);
        setIsWeeklyEditing(true);
        setIsBreakdownMode(false);
    };

    const handleSaveWeeklyPlanFromToolbar = () => {
        const plan = weeklyPlans.find(p => p.id === activePlanId);

        if (plan?.status === 'En Revisión' && draftWeeklyPlan && draftWeeklyPlan.id === activePlanId) {
            setWeeklyPlans(prev => prev.map(p => p.id === activePlanId ? draftWeeklyPlan : p));
        }
        setDraftWeeklyPlan(null);

        addActivity({
            type: 'planning',
            title: 'Planificación Semanal Guardada',
            description: `Se han guardado cambios en la planificación semanal #${activePlanId} del ${plan?.startDate} al ${plan?.endDate}`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });

        if (plan && plan.allocations && plan.allocations.length > 0) {
            dispatchPlanningNotifications(
                `${plan.startDate} al ${plan.endDate}`,
                'Semanal',
                plan.allocations // They contain taskId and responsibleId
            );
        }

        setWeeklyPlansBackup(null);
        setIsWeeklyEditing(false);
        setActivePlanId(null); // Return to History View
    };

    const handleExitWithoutSaving = () => {
        if (exitWarningTarget === 'semanal') {
            const plan = weeklyPlans.find(p => p.id === activePlanId);
            if (plan?.status === 'En Revisión') {
                setDraftWeeklyPlan(null);
            } else if (weeklyPlansBackup) {
                // Safely merge backup with current state so terrain edits made while viewing this plan are never wiped
                setWeeklyPlans(prev => mergeWeeklyPlans(weeklyPlansBackup, prev));
            }
            setIsWeeklyEditing(false);
            setActivePlanId(null);
        } else if (exitWarningTarget === 'intermedia') {
            if (lookaheadPlansBackup) {
                setLookaheadPlans(lookaheadPlansBackup);
            }
            setActiveLookaheadId(null);
            setIsITEView(false);
        }
        setShowExitWarningModal(false);
        setExitWarningTarget(null);
    };

    const handleExitWithSaving = () => {
        if (exitWarningTarget === 'semanal') {
            handleSaveWeeklyPlanFromToolbar();
        } else if (exitWarningTarget === 'intermedia') {
            handleSaveLookaheadPlan();
        }
        setShowExitWarningModal(false);
        setExitWarningTarget(null);
    };

    const handlePublishPlan = (planId: number) => {
        if (!hasPermission('ps_publicar', 'edit')) {
            alert('No tienes permisos para publicar la planificación.');
            return;
        }
        const nowIso = new Date().toISOString();
        const userName = currentUser?.name || currentUser?.email || 'Usuario';
        setWeeklyPlans(prev => prev.map(p => p.id === planId ? { ...p, status: 'En Ejecución', statusUpdatedAt: nowIso, updatedAt: nowIso, updatedBy: userName } : p));
        addActivity({
            type: 'planning',
            title: 'Planificación Semanal Publicada',
            description: `La planificación semanal #${planId} ha sido publicada y ahora está En Ejecución.`,
            user: userName
        });
        const recipients = resolveRecipientsForEvent({
            eventType: 'planning_published',
            projectId: projectInfo?.id,
            globalUsers
        });

        triggerAppNotification({
            eventType: 'planning_published',
            title: 'Planificación Semanal Publicada',
            type: 'planning',
            projectId: projectInfo?.id,
            projectName: projectInfo?.name,
            targetUserIds: recipients,
            variables: {
                obra: projectInfo?.name,
                planificacion: `Planificación Semanal #${planId}`
            },
            companyConfig,
            dedupKey: `weekly_publish_${planId}`,
            link: '/planning',
            requiresAttention: false
        });
        setIsWeeklyEditing(false);
    };

    const handleRevertToDraft = (planId: number) => {
        if (isMobile) {
            return;
        }
        const nowIso = new Date().toISOString();
        const userName = currentUser?.name || currentUser?.email || 'Usuario';
        setWeeklyPlans(prev => prev.map(p => p.id === planId ? { ...p, status: 'En Planificación', statusUpdatedAt: nowIso, updatedAt: nowIso, updatedBy: userName } : p));
        setIsWeeklyEditing(true);
        addActivity({
            type: 'planning',
            title: 'Planificación Semanal en Edición',
            description: `La planificación semanal #${planId} ha sido devuelta al estado En Planificación.`,
            user: userName
        });
    };

    const handlePublishLookahead = (planId: string) => {
        if (!hasPermission('ps_publicar', 'edit') && !hasPermission('pi_generar', 'edit')) {
            alert('No tienes permisos para publicar la planificación intermedia.');
            return;
        }
        const nowIso = new Date().toISOString();
        setLookaheadPlans(prev => prev.map(p => p.id === planId ? { ...p, status: 'En Ejecución', statusUpdatedAt: nowIso, updatedAt: nowIso } : p));
        addActivity({
            type: 'planning',
            title: 'Planificación Intermedia Publicada',
            description: `La planificación intermedia ha sido publicada y ahora está En Ejecución.`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });
        const recipients = resolveRecipientsForEvent({
            eventType: 'planning_published',
            projectId: projectInfo?.id,
            globalUsers
        });

        triggerAppNotification({
            eventType: 'planning_published',
            title: 'Planificación Intermedia Publicada',
            type: 'planning',
            projectId: projectInfo?.id,
            projectName: projectInfo?.name,
            targetUserIds: recipients,
            variables: {
                obra: projectInfo?.name,
                planificacion: `Planificación Intermedia #${planId}`
            },
            companyConfig,
            dedupKey: `lookahead_publish_${planId}`,
            link: '/planning',
            requiresAttention: false
        });
        setActiveLookaheadId(null);
        setIsITEView(false);
    };

    const handleRevertLookaheadToDraft = (planId: string) => {
        if (isMobile) {
            return;
        }
        const nowIso = new Date().toISOString();
        setLookaheadPlans(prev => prev.map(p => p.id === planId ? { ...p, status: 'En Planificación', statusUpdatedAt: nowIso, updatedAt: nowIso } : p));
        addActivity({
            type: 'planning',
            title: 'Planificación Intermedia en Edición',
            description: `La planificación intermedia ha sido devuelta al estado En Planificación.`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });
    };

    const handleStartLookaheadReview = (planId: string) => {
        const nowIso = new Date().toISOString();
        setLookaheadPlans(prev => prev.map(p => p.id === planId ? { ...p, status: 'En Revisión', statusUpdatedAt: nowIso, updatedAt: nowIso } : p));
        addActivity({
            type: 'planning',
            title: 'Revisión de Planificación Intermedia',
            description: `La planificación intermedia ha sido enviada a revisión.`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });
    };

    const handleCloseLookahead = (planId: string) => {
        const nowIso = new Date().toISOString();
        setLookaheadPlans(prev => prev.map(p => p.id === planId ? { ...p, status: 'Cerrada', statusUpdatedAt: nowIso, updatedAt: nowIso } : p));
        addActivity({
            type: 'planning',
            title: 'Planificación Intermedia Cerrada',
            description: `La planificación intermedia ha sido cerrada.`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });
        setActiveLookaheadId(null);
    };

    const handleBackToLookaheadExecution = (planId: string) => {
        setLookaheadPlans(prev => prev.map(p => p.id === planId ? { ...p, status: 'En Ejecución' } : p));
        addActivity({
            type: 'planning',
            title: 'Planificación Intermedia Re-abierta',
            description: `Se ha devuelto la planificación intermedia al estado En Ejecución.`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });
    };

    const handleReopenLookahead = (planId: string) => {
        if (window.confirm("¿Seguro que desea volver a abrir esta planificación intermedia?")) {
            setLookaheadPlans(prev => prev.map(p => p.id === planId ? { ...p, status: 'En Revisión' } : p));
            addActivity({
                type: 'planning',
                title: 'Planificación Intermedia Reabierta',
                description: `Se ha reabierto la planificación intermedia para revisión`,
                user: currentUser?.name || currentUser?.email || 'Usuario'
            });
        }
    };

    const isLookaheadFullyResolved = (plan: LookaheadPlan | null): boolean => {
        if (!plan) return false;
        const tasksInWindow = getTasksForLookahead(plan.startDate, plan.endDate, plan.constraints);
        for (const task of tasksInWindow) {
            const isCleared = plan.clearedTaskIds?.includes(task.id);
            if (isCleared) continue; // Resolved: No Aplica Restricción

            const taskConstraints = plan.constraints.filter(c => c.taskId === task.id);
            for (const c of taskConstraints) {
                const isReleased = c.status === 'Liberado';
                const hasComment = c.observations && c.observations.trim().length > 0;
                if (!isReleased && !hasComment) {
                    return false; // Not resolved!
                }
            }
        }
        return true;
    };

    const handleDeleteLookahead = (planId: string) => {
        if (!hasPermission('pi_eliminar', 'edit')) return;
        const plan = lookaheadPlans.find(p => p.id === planId);
        if (!plan) return;

        if (window.confirm(`¿Estás completamente seguro de eliminar la Planificación Intermedia del ${plan.startDate} al ${plan.endDate}?\n\nEsta acción es irreversible y eliminará todas sus restricciones y datos asociados.`)) {
            setLookaheadPlans(prev => prev.filter(p => p.id !== planId));
            addActivity({
                type: 'planning',
                title: 'Planificación Intermedia Eliminada',
                description: `Se ha eliminado la planificación intermedia del ${plan.startDate} al ${plan.endDate}`,
                user: currentUser?.name || currentUser?.email || 'Usuario'
            });
        }
    };

    const handleDeleteWeeklyPlan = (planId: number) => {
        if (!hasPermission('ps_eliminar', 'edit')) return;
        const plan = weeklyPlans.find(p => p.id === planId);
        if (!plan) return;

        if (window.confirm(`¿Estás completamente seguro de eliminar la Planificación Semanal #${planId} (${plan.startDate} al ${plan.endDate})?\n\nEsta acción es irreversible y eliminará todo el desglose de tareas, asignaciones e historial de avance.`)) {
            setWeeklyPlans(prev => prev.filter(p => p.id !== planId));
            if (activePlanId === planId) {
                setActivePlanId(null);
            }
            addActivity({
                type: 'planning',
                title: 'Planificación Semanal Eliminada',
                description: `Se ha eliminado la planificación semanal #${planId} del ${plan.startDate} al ${plan.endDate}`,
                user: currentUser?.name || currentUser?.email || 'Usuario'
            });
        }
    };

    // Updated to handle "Review" state instead of closing immediately
    const handleStartReview = (planId: number) => {
        const plan = weeklyPlans.find(p => p.id === planId);
        if (plan) {
            const pendingAllocations = plan.allocations.filter(a => getStatusValue(a) === 'pendiente');
            if (pendingAllocations.length > 0) {
                alert(`No se puede iniciar la revisión: Faltan tareas que no se han revisado y están en estado pendiente (${pendingAllocations.length} pendiente${pendingAllocations.length > 1 ? 's' : ''}). Debe completar todas las tareas declarándolas como 'Ejecutado' o 'No Ejecutado'.`);
                return;
            }

            const hasNoWithoutComment = plan.allocations.some(a => {
                const status = getStatusValue(a);
                return status === 'no' && !a.comentario?.trim();
            });
            if (hasNoWithoutComment) {
                alert("No se puede iniciar la revisión: Existen tareas declaradas como 'No' que no tienen una justificación de incumplimiento. Por favor complete todas las justificaciones.");
                return;
            }
            setDraftWeeklyPlan(JSON.parse(JSON.stringify({ ...plan, status: 'En Revisión' })));
        }
        const nowIso = new Date().toISOString();
        const userName = currentUser?.name || currentUser?.email || 'Usuario';

        setWeeklyPlans(prev => prev.map(p => p.id === planId ? { ...p, status: 'En Revisión', statusUpdatedAt: nowIso, updatedAt: nowIso, updatedBy: userName } : p));
        addActivity({
            type: 'planning',
            title: 'Revisión de Planificación Iniciada',
            description: `La planificación semanal #${planId} (${plan?.startDate} al ${plan?.endDate}) ha sido enviada a revisión`,
            user: userName
        });

        const reviewers = resolveRecipientsForEvent({
            eventType: 'review_requested',
            projectId: projectInfo?.id,
            globalUsers
        });

        triggerAppNotification({
            eventType: 'review_requested',
            title: 'Solicitud de Revisión de Planificación',
            type: 'planning',
            projectId: projectInfo?.id,
            projectName: projectInfo?.name,
            targetUserIds: reviewers,
            variables: {
                usuario: 'Revisor',
                obra: projectInfo?.name,
                tarea: `Planificación Semanal #${planId}`,
                planificacion: `Semana #${planId} (${plan?.startDate || ''} al ${plan?.endDate || ''})`
            },
            companyConfig,
            dedupKey: `weekly_review_req_${planId}`,
            link: '/planning',
            requiresAttention: true
        });

        setIsWeeklyEditing(false); // Stop standard editing
    };

    const handleSaveReview = (planId: number) => {
        if (draftWeeklyPlan && draftWeeklyPlan.id === planId) {
            setWeeklyPlans(prev => prev.map(p => p.id === planId ? draftWeeklyPlan : p));
        }
        setDraftWeeklyPlan(null);
        addActivity({
            type: 'planning',
            title: 'Revisión Guardada',
            description: `Se han guardado los cambios en la revisión del Plan #${planId}`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });
        setActivePlanId(null);
    };

    const handleBackToExecution = (planId: number) => {
        setDraftWeeklyPlan(null);
        const nowIso = new Date().toISOString();
        const userName = currentUser?.name || currentUser?.email || 'Usuario';
        setWeeklyPlans(currentPlans => currentPlans.map(p => {
            if (p.id === planId) {
                return { ...p, status: 'En Ejecución', statusUpdatedAt: nowIso, updatedAt: nowIso, updatedBy: userName };
            }
            return p;
        }));
        setIsWeeklyEditing(false);
        addActivity({
            type: 'planning',
            title: 'Planificación re-abierta para edición',
            description: `Se ha vuelto al modo de edición para la Planificación Semanal #${planId}`,
            user: userName
        });
    };

    const handleFinalClosePlan = (planId: number) => {
        const plan = (draftWeeklyPlan && draftWeeklyPlan.id === planId) ? draftWeeklyPlan : weeklyPlans.find(p => p.id === planId);
        if (plan) {
            const hasPendientes = plan.allocations.some(a => {
                const status = getStatusValue(a);
                return status === 'pendiente';
            });
            if (hasPendientes) {
                alert("No se puede finalizar la revisión: Existen tareas en estado 'Pendiente'. Debe declararlas como 'Ejecutado' o 'No Ejecutado'.");
                return;
            }
            const hasNoWithoutComment = plan.allocations.some(a => {
                const status = getStatusValue(a);
                return status === 'no' && !a.comentario?.trim();
            });
            if (hasNoWithoutComment) {
                alert("No se puede finalizar la revisión: Existen tareas declaradas como 'No Ejecutado' sin justificación.");
                return;
            }
        }

        const nowIso = new Date().toISOString();
        const userName = currentUser?.name || currentUser?.email || 'Usuario';

        // Close Plan Immediately (No Confirmation)
        setWeeklyPlans(currentPlans => currentPlans.map(p => {
            if (p.id === planId) {
                const baseObj = (draftWeeklyPlan && draftWeeklyPlan.id === planId) ? draftWeeklyPlan : p;
                return {
                    ...baseObj,
                    status: 'Cerrada',
                    statusUpdatedAt: nowIso,
                    closedAt: nowIso,
                    closedBy: userName,
                    updatedAt: nowIso,
                    updatedBy: userName
                };
            }
            return p;
        }));
        setDraftWeeklyPlan(null);

        const planObj = weeklyPlans.find(p => p.id === planId);
        addActivity({
            type: 'planning',
            title: 'Planificación Semanal Cerrada',
            description: `La planificación semanal #${planId} (${planObj?.startDate} al ${planObj?.endDate}) ha sido cerrada finalizando la semana`,
            user: userName
        });

        setActivePlanId(null);
        setIsWeeklyEditing(false);
    };

    const handleReopenPlan = (planId: number) => {
        if (window.confirm("¿Seguro que desea volver a abrir esta planificación?")) {
            const nowIso = new Date().toISOString();
            const userName = currentUser?.name || currentUser?.email || 'Usuario';
            setWeeklyPlans(currentPlans => currentPlans.map(p => {
                if (p.id === planId) {
                    return {
                        ...p,
                        status: 'En Revisión',
                        statusUpdatedAt: nowIso,
                        reopenedAt: nowIso,
                        reopenedBy: userName,
                        updatedAt: nowIso,
                        updatedBy: userName
                    };
                }
                return p;
            }));
            const plan = weeklyPlans.find(p => p.id === planId);
            addActivity({
                type: 'planning',
                title: 'Planificación Semanal Reabierta',
                description: `Se ha reabierto la planificación semanal #${planId} del ${plan?.startDate} al ${plan?.endDate} para revisión`,
                user: currentUser?.name || currentUser?.email || 'Usuario'
            });
        }
    };

    const handleBreakdownClick = () => {
        setIsBreakdownMode(true);
        setIsWeeklyEditing(false); // Exclusive
        setNewPlanModalOpen(false);
    };

    // Update allocation field in the main view (for CNC during review)
    const updateMainAllocationField = (planId: number, allocationIndex: number, field: keyof WeeklyAllocation, value: any) => {
        // Resolver la fila por clave (tarea + unidad física) para no escribir en otra fila si el orden difiere
        const sourcePlan = (draftWeeklyPlan && draftWeeklyPlan.id === planId)
            ? draftWeeklyPlan
            : weeklyPlans.find(p => p.id === planId);
        if (!sourcePlan) return;

        // Si la planificación ya está Cerrada, queda estrictamente blindada contra modificaciones accidentales
        if (sourcePlan.status === 'Cerrada') {
            console.warn(`[Planning] Intento de modificar asignación en Plan #${planId} que ya está Cerrado. Operación bloqueada.`);
            return;
        }

        const target = sourcePlan?.allocations[allocationIndex];
        if (!target) return;
        const matches = (a: WeeklyAllocation) => a.taskId === target.taskId && a.unitId === target.unitId;

        // Sello de cambio manual: solo los cambios explícitos del usuario actualizan el estado
        const isStatusField = field === 'executed' || field === 'percentCumplimiento' || field === 'cncId' || field === 'comentario';
        const nowIso = new Date().toISOString();
        const userName = currentUser?.name || currentUser?.email || 'Usuario';
        const stamp: Partial<WeeklyAllocation> = {
            updatedAt: nowIso,
            updatedBy: userName,
            ...(isStatusField ? { statusUpdatedAt: nowIso, statusUpdatedBy: userName } : {})
        };

        const apply = (allocs: WeeklyAllocation[]) =>
            allocs.map(a => {
                if (!matches(a)) return a;
                let extra: Partial<WeeklyAllocation> = {};
                if (field === 'executed' && value === true) {
                    extra = { percentCumplimiento: 100 };
                } else if (field === 'executed' && value === null) {
                    extra = {
                        percentCumplimiento: 0,
                        cncId: undefined,
                        comentario: a.comentario ? (a.comentario.startsWith('[Anterior]:') ? a.comentario : `[Anterior]: ${a.comentario}`) : ''
                    };
                }
                return { ...a, [field]: value, ...extra, ...stamp };
            });

        if (draftWeeklyPlan && draftWeeklyPlan.id === planId) {
            setDraftWeeklyPlan(prev => prev ? { ...prev, allocations: apply(prev.allocations), updatedAt: nowIso, updatedBy: userName } : prev);
        }
        // Always update weeklyPlans so that changes to executed/progress are immediately persisted to Supabase and visible across clients
        setWeeklyPlans(prev => prev.map(p => p.id !== planId ? p : { ...p, allocations: apply(p.allocations), updatedAt: nowIso, updatedBy: userName }));
    };

    // Handler when user marks a task as 'No Ejecutado'
    const handleSetUnexecuted = (planId: number, allocationIndex: number, alloc: WeeklyAllocation) => {
        // Resolve targetTaskId (in case of weekly subtasks, quality configs reside on parent or task)
        const taskObj = findTaskById(alloc.taskId, tasks);
        const parentTask = findParentTask(alloc.taskId, tasks);
        const targetTaskId = (taskObj?.isWeeklySubtask && parentTask) ? parentTask.id : alloc.taskId;

        const protocolKey = `${targetTaskId}_${alloc.unitId}`;
        const resolvedEntry = findSavedProtocolEntry(savedProtocols, protocolKey, targetTaskId, alloc.unitId);
        const existingProtocol = resolvedEntry?.state || (savedProtocols[alloc.taskId] && savedProtocols[alloc.taskId].unitId === alloc.unitId ? savedProtocols[alloc.taskId] : undefined);

        // Check if there is an active/sent protocol with signatures or status !== 'Inactivo'
        const hasActiveOrSentProtocol = existingProtocol &&
            existingProtocol.status !== 'Inactivo' &&
            (Object.keys(existingProtocol.signatures || {}).length > 0 || existingProtocol.status === 'Abierto' || existingProtocol.status === 'Resuelto' || existingProtocol.status === 'Cerrado');

        if (hasActiveOrSentProtocol && existingProtocol) {
            const allocUnit = units.find(u => u.id === alloc.unitId);
            const uName = allocUnit?.name || alloc.unitId;
            const tName = taskObj?.name || findTaskName(alloc.taskId, tasks) || 'Partida';

            setProtocolPromptTarget({
                planId,
                allocationIndex,
                protocolId: resolvedEntry?.key || existingProtocol.id || protocolKey,
                taskId: targetTaskId,
                unitId: alloc.unitId,
                taskName: tName,
                unitName: uName
            });
            setRejectionObservation('');
            setProtocolReviewPromptOpen(true);
            return;
        }

        // Normal unexecution without prompt
        const currentPct = alloc.percentCumplimiento ?? 0;
        updateMainAllocationField(planId, allocationIndex, 'executed', false);
        updateMainAllocationField(planId, allocationIndex, 'percentCumplimiento', currentPct >= 100 ? 0 : currentPct);
    };

    // Confirm Option 1: Reject protocol with observation
    const handleConfirmRejectProtocol = () => {
        if (!protocolPromptTarget) return;
        const { planId, allocationIndex, protocolId, taskName, unitName } = protocolPromptTarget;

        const currentPct = (weeklyPlans.find(p => p.id === planId)?.allocations[allocationIndex]?.percentCumplimiento) ?? 0;
        updateMainAllocationField(planId, allocationIndex, 'executed', false);
        updateMainAllocationField(planId, allocationIndex, 'percentCumplimiento', currentPct >= 100 ? 0 : currentPct);

        if (setSavedProtocols) {
            setSavedProtocols(prev => {
                const entry = findSavedProtocolEntry(prev, protocolId, protocolPromptTarget.taskId, protocolPromptTarget.unitId);
                const current = entry?.state || prev[protocolId];
                if (!current) return prev;

                const newReviewerIdx = Math.max(0, current.currentReviewerIndex - 1);
                const updatedHistory = [...(current.history || [])];
                updatedHistory.push({
                    date: new Date().toLocaleString(),
                    action: 'rejected',
                    user: currentUser?.name || currentUser?.email || 'Jefe de Terreno / Revisor',
                    comment: rejectionObservation.trim() || 'Rechazado desde Revisión Semanal al marcar No Ejecutado.'
                });

                const targetKey = entry?.key || protocolId;
                const nextState: ProtocolState = {
                    ...current,
                    status: 'Rechazado',
                    currentReviewerIndex: newReviewerIdx,
                    history: updatedHistory,
                    updatedAt: new Date().toISOString()
                };
                const updated = {
                    ...prev,
                    [targetKey]: nextState
                };
                if (entry) {
                    entry.allKeys.forEach(k => {
                        updated[k] = nextState;
                    });
                }
                return updated;
            });
        }

        addActivity({
            type: 'quality',
            title: 'Protocolo Rechazado desde Revisión Semanal',
            description: `Protocolo ${protocolId} (${taskName} - ${unitName}) fue rechazado al cambiar tarea a No Ejecutada. Obs: ${rejectionObservation.substring(0, 40)}`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });

        triggerAppNotification({
            eventType: 'review_rejected',
            title: 'Revisión Rechazada',
            type: 'planning',
            projectId: projectInfo?.id,
            projectName: projectInfo?.name,
            variables: {
                usuario: 'Responsable',
                obra: projectInfo?.name,
                tarea: `${taskName} (${unitName})`,
                planificacion: `Semana #${planId}`,
                protocolo: `Protocolo #${protocolId}`
            },
            companyConfig,
            dedupKey: `plan_reject_${protocolId}_${planId}`,
            link: '/planning',
            requiresAttention: true
        });

        setProtocolReviewPromptOpen(false);
        setProtocolPromptTarget(null);
        setRejectionObservation('');
    };

    // Confirm Option 2: Only adjust production progress, keep protocol state
    const handleConfirmAdjustProductionOnly = () => {
        if (!protocolPromptTarget) return;
        const { planId, allocationIndex } = protocolPromptTarget;

        const currentPct = (weeklyPlans.find(p => p.id === planId)?.allocations[allocationIndex]?.percentCumplimiento) ?? 0;
        updateMainAllocationField(planId, allocationIndex, 'executed', false);
        updateMainAllocationField(planId, allocationIndex, 'percentCumplimiento', currentPct >= 100 ? 0 : currentPct);

        setProtocolReviewPromptOpen(false);
        setProtocolPromptTarget(null);
        setRejectionObservation('');
    };

    const handleDeleteAllocation = (planId: number, allocationIndex: number) => {
        if (!confirm("¿Estás seguro de eliminar esta actividad de la planificación semanal?")) return;
        const nowIso = new Date().toISOString();
        const userName = currentUser?.name || currentUser?.email || 'Usuario';

        setWeeklyPlans(prev => prev.map(p => {
            if (p.id !== planId) return p;
            const targetAlloc = p.allocations[allocationIndex];
            const newAllocations = p.allocations.filter((_, i) => i !== allocationIndex);
            const delKey = targetAlloc ? `${targetAlloc.taskId}__${targetAlloc.unitId}` : null;
            const updatedDeletedAllocations = {
                ...(p.deletedAllocations || {}),
                ...(delKey ? { [delKey]: nowIso } : {})
            };
            return {
                ...p,
                allocations: newAllocations,
                deletedAllocations: updatedDeletedAllocations,
                updatedAt: nowIso,
                updatedBy: userName
            };
        }));
        addActivity({
            type: 'planning',
            title: 'Actividad eliminada',
            description: `Se ha eliminado una actividad de la Planificación #${planId}`,
            user: userName
        });
    };

    // CNC Logic
    const handleAddCNCGroup = () => {
        if (!newCNCGroupName.trim()) return;
        const newGroup: CNCGroup = {
            id: Math.random().toString(36).substr(2, 9),
            name: newCNCGroupName,
            causes: []
        };
        setCncGroups([...cncGroups, newGroup]);
        setNewCNCGroupName('');
    };

    const handleAddCNCCause = (groupId: string) => {
        if (!newCNCCauseName.trim()) return;
        const newCause: CNCCause = {
            id: Math.random().toString(36).substr(2, 9),
            name: newCNCCauseName
        };
        setCncGroups(prev => prev.map(g => {
            if (g.id === groupId) {
                return { ...g, causes: [...g.causes, newCause] };
            }
            return g;
        }));
        setNewCNCCauseName('');
        setActiveGroupForNewCNC(null);
    };

    const handleEditCNCGroup = (groupId: string, newName: string) => {
        if (!newName.trim()) return;
        setCncGroups(prev => prev.map(g => g.id === groupId ? { ...g, name: newName } : g));
        setEditingCNCGroupId(null);
    };

    const handleDeleteCNCGroup = (groupId: string) => {
        if (window.confirm("¿Seguro que desea eliminar este grupo y todas sus causas?")) {
            setCncGroups(prev => prev.filter(g => g.id !== groupId));
        }
    };

    const handleEditCNCCause = (groupId: string, causeId: string, newName: string) => {
        if (!newName.trim()) return;
        setCncGroups(prev => prev.map(g => {
            if (g.id === groupId) {
                return {
                    ...g,
                    causes: g.causes.map(c => c.id === causeId ? { ...c, name: newName } : c)
                };
            }
            return g;
        }));
        setEditingCNCCauseId(null);
    };

    const handleDeleteCNCCause = (groupId: string, causeId: string) => {
        if (window.confirm("¿Seguro que desea eliminar esta causa?")) {
            setCncGroups(prev => prev.map(g => {
                if (g.id === groupId) {
                    return { ...g, causes: g.causes.filter(c => c.id !== causeId) };
                }
                return g;
            }));
        }
    };

    // Plan Task Modal Logic
    const openPlanTaskModal = (taskId: string) => {
        setPlanningTaskId(taskId);
        setContextMenu({ ...contextMenu, visible: false });
        setBulkResponsibleId('');

        const taskObj = findTaskById(taskId, tasks);
        const parentTask = findParentTask(taskId, tasks);
        const targetTaskId = (taskObj?.isWeeklySubtask && parentTask) ? parentTask.id : taskId;

        if (taskObj) {
            // Regla AGENTS.md #10: Tareas madre con desgloses NO son programables directamente en planificación semanal
            const hasWeeklySubtasks = taskObj.children && taskObj.children.some(c => c.isWeeklySubtask && !c.isDeleted);
            if (hasWeeklySubtasks) {
                alert("Esta partida está desglosada en subtareas. Debe programar sus subtareas individuales.");
                return;
            }

            const currentProgress = calculateTaskProgressLocal(taskObj);
            if (currentProgress >= 100) {
                alert("Esta tarea ya se encuentra al 100% de avance y no se puede volver a planificar.");
                return;
            }
        }

        // Check for pending constraints (ITE Restrictions)
        const pendingConstraints = lookaheadPlans.flatMap(p => p.constraints).filter(c => c.taskId === targetTaskId && c.status !== 'Liberado');

        const restrictedUnitIds = new Set<string>();
        pendingConstraints.forEach(c => {
            if (c.unitsType === 'all' || c.unitsType === 'na' || !c.unitsType) {
                // If restriction applies to all units or is general, mark all assigned units as restricted
                units.filter(u => isTaskUnitAssigned(taskId, u.id, matrixData, tasks)).forEach(u => restrictedUnitIds.add(u.id));
            } else if (c.unitsType === 'custom' && c.unitIds) {
                c.unitIds.forEach(uid => restrictedUnitIds.add(uid));
            }
        });

        // 1. Find all units marked for this task in Matrix
        const matrixUnits = units.filter(u => isTaskUnitAssigned(taskId, u.id, matrixData, tasks));

        // 2. Determine execution and scheduling states
        const appliesProtocol = protocolConfigs[targetTaskId]?.applies ?? false;

        // Check if we already have allocations for this task in the CURRENT active plan
        const activePlan = weeklyPlans.find(p => p.id === activePlanId);
        const existingAllocations = activePlan?.allocations.filter(a => a.taskId === taskId) || [];

        // Helper to check working days using states workingDays and holidays
        const getLocalDateStr = (date: Date): string => {
            const yyyy = date.getFullYear();
            const mm = String(date.getMonth() + 1).padStart(2, '0');
            const dd = String(date.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        };

        const checkIsWorkingDay = (date: Date): boolean => {
            const dayOfWeek = date.getDay();
            if (!workingDays[dayOfWeek]) return false;
            const dateStr = getLocalDateStr(date);
            return !holidays.some(h => h.date === dateStr);
        };

        const ganttTask = findTaskById(targetTaskId, tasks);
        let ratio = 0;
        if (activePlan && ganttTask && ganttTask.start && ganttTask.end) {
            if (ganttTask.end < activePlan.startDate) {
                // Rezagada: contractual end is in the past, so Gantt requires 100% of the task to be completed.
                ratio = 1.0;
            } else if (ganttTask.start > activePlan.endDate) {
                // Futura: task hasn't started contractually yet.
                ratio = 0.0;
            } else {
                // Vigente: calculate the overlap working days.
                let overlapDays = 0;
                let curr = new Date(activePlan.startDate + "T12:00:00");
                const end = new Date(activePlan.endDate + "T12:00:00");
                while (curr <= end) {
                    if (checkIsWorkingDay(curr)) {
                        const dateStr = getLocalDateStr(curr);
                        if (dateStr >= ganttTask.start && dateStr <= ganttTask.end) {
                            overlapDays++;
                        }
                    }
                    curr.setDate(curr.getDate() + 1);
                }
                ratio = Math.min(1, overlapDays / (ganttTask.duration || 1));
            }
        }
        const totalUnitsForTask = matrixUnits.length;
        const N = Math.ceil(ratio * totalUnitsForTask);

        const taskObjForModal = findTaskById(taskId, tasks);
        const taskProgressForModal = taskObjForModal ? calculateTaskProgressLocal(taskObjForModal) : 0;

        // Pre-compute compliance once per unit to avoid repeated O(U * W) calculations
        const complianceMap = new Map<string, number>();
        matrixUnits.forEach(u => {
            complianceMap.set(u.id, getTaskUnitAccumulatedCompliance(taskId, u.id, tasks, weeklyPlans, protocolConfigs, savedProtocols, taskProgressForModal, activePlanId));
        });

        // Pre-compute units scheduled in closed plans once
        const scheduledUnitIds = new Set<string>();
        weeklyPlans
            .filter(p => p.status === 'Cerrada')
            .flatMap(p => p.allocations)
            .forEach(a => {
                if (a.taskId === taskId) {
                    scheduledUnitIds.add(a.unitId);
                }
            });

        const pendingUnits = matrixUnits.filter(u => (complianceMap.get(u.id) ?? 0) < 100);
        const suggestedUnitIds = new Set(pendingUnits.slice(0, N).map(u => u.id));

        let displayAllocations: (WeeklyAllocation & {
            selected: boolean;
            isRestricted: boolean;
            isPreviouslyExecuted?: boolean;
            isReprogrammed?: boolean;
            isSuggestedByGantt?: boolean;
            accumulatedCompliance?: number;
            remainingBalance?: number;
        })[] = [];

        // For each unit, check status
        matrixUnits.forEach(u => {
            const isRestricted = restrictedUnitIds.has(u.id);
            const accumulatedCompliance = complianceMap.get(u.id) ?? 0;
            const remainingBalance = Math.max(0, Number((100 - accumulatedCompliance).toFixed(2)));
            const isPreviouslyExecuted = accumulatedCompliance >= 100;
            const isReprogrammed = scheduledUnitIds.has(u.id) && !isPreviouslyExecuted;
            const existing = existingAllocations.find(a => a.unitId === u.id);
            const isSuggestedByGantt = suggestedUnitIds.has(u.id);

            if (existing) {
                displayAllocations.push({
                    ...existing,
                    selected: true,
                    isRestricted,
                    isPreviouslyExecuted,
                    isReprogrammed,
                    isSuggestedByGantt,
                    accumulatedCompliance,
                    remainingBalance,
                    percentComprometido: Math.min(existing.percentComprometido ?? remainingBalance, remainingBalance),
                    percentCumplimiento: getAllocationCompliancePct(existing)
                });
            } else {
                const config = protocolConfigs[targetTaskId];
                const group = config?.reviewerGroups?.find(g => g.unitIds.includes(u.id));
                const defaultResponsible = group?.reviewers?.[0]?.id || '';

                displayAllocations.push({
                    taskId: taskId,
                    unitId: u.id,
                    responsibleId: defaultResponsible,
                    days: { L: false, M: false, X: false, J: false, V: false, S: false },
                    executed: null,
                    selected: false,
                    isRestricted,
                    isPreviouslyExecuted,
                    isReprogrammed,
                    isSuggestedByGantt,
                    accumulatedCompliance,
                    remainingBalance,
                    percentComprometido: remainingBalance,
                    percentCumplimiento: 0
                });
            }
        });

        setCurrentAllocations(displayAllocations);
        setPlanTaskModalOpen(true);
    };

    const handleSaveAllocations = (savedAllocations: (WeeklyAllocation & { selected: boolean; isRestricted: boolean; isPreviouslyExecuted?: boolean; isReprogrammed?: boolean; isSuggestedByGantt?: boolean })[]) => {
        if (activePlanId === null) return;

        const currentActivePlan = weeklyPlans.find(p => p.id === activePlanId);
        if (currentActivePlan?.status === 'Cerrada') {
            alert("No se puede modificar la planificación: Esta semana ya ha sido 'Cerrada' y finalizada.");
            setPlanTaskModalOpen(false);
            return;
        }

        const selectedAllocations = savedAllocations.filter(a => a.selected);

        // Validation
        for (const alloc of selectedAllocations) {
            if (!alloc.responsibleId) {
                alert(`Debe asignar un responsable para la unidad: ${units.find(u => u.id === alloc.unitId)?.name}`);
                return;
            }
            const hasDay = Object.values(alloc.days).some(d => d);
            if (!hasDay) {
                alert(`Debe seleccionar al menos un día para la unidad: ${units.find(u => u.id === alloc.unitId)?.name}`);
                return;
            }
        }

        setWeeklyPlans(prevPlans => prevPlans.map(plan => {
            if (plan.id === activePlanId) {
                // Remove old allocations for this task / these tasks and add current ones (sanitized)
                let otherAllocations: WeeklyAllocation[];
                if (planningTaskId === 'ALL_REPROGRAMMED') {
                    const currentAllocKeys = new Set(savedAllocations.map(a => `${a.taskId}_${a.unitId}`));
                    otherAllocations = plan.allocations.filter(a => !currentAllocKeys.has(`${a.taskId}_${a.unitId}`));
                } else {
                    otherAllocations = plan.allocations.filter(a => a.taskId !== planningTaskId);
                }

                const nowIso = new Date().toISOString();
                const userName = currentUser?.name || currentUser?.email || 'Usuario';
                const newAllocationsToSave = selectedAllocations.map(({ selected, isRestricted, isPreviouslyExecuted, isReprogrammed, isSuggestedByGantt, ...rest }) => ({
                    ...rest,
                    updatedAt: rest.updatedAt || nowIso,
                    updatedBy: rest.updatedBy || userName
                }));

                const updatedDeletedAllocations = { ...(plan.deletedAllocations || {}) };
                newAllocationsToSave.forEach(a => {
                    delete updatedDeletedAllocations[`${a.taskId}__${a.unitId}`];
                });

                return {
                    ...plan,
                    allocations: [...otherAllocations, ...newAllocationsToSave],
                    deletedAllocations: updatedDeletedAllocations,
                    updatedAt: nowIso,
                    updatedBy: userName
                };
            }
            return plan;
        }));

        // Notificaciones automáticas de asignación de tareas
        selectedAllocations.forEach(alloc => {
            if (alloc.responsibleId) {
                const unit = units.find(u => u.id === alloc.unitId);
                const taskName = alloc.isAdditional ? (alloc.additionalTaskName || 'Tarea Adicional') : (findTaskName(alloc.taskId, tasks) || 'Partida');
                const respUser = globalUsers?.find(u => u.id === alloc.responsibleId || u.email === alloc.responsibleId);
                triggerAppNotification({
                    eventType: 'task_assigned',
                    title: 'Nueva Tarea Asignada',
                    type: 'planning',
                    projectId: projectInfo?.id,
                    projectName: projectInfo?.name,
                    targetUserIds: [alloc.responsibleId],
                    variables: {
                        usuario: respUser?.name || 'Responsable',
                        obra: projectInfo?.name,
                        tarea: `${cleanTaskName(taskName)} (${unit?.name || 'Unidad'})`,
                        planificacion: `Semana #${activePlanId}`
                    },
                    companyConfig,
                    dedupKey: `task_assign_${activePlanId}_${alloc.taskId}_${alloc.unitId}_${alloc.responsibleId}`,
                    link: '/planning',
                    requiresAttention: false
                });
            }
        });

        setPlanTaskModalOpen(false);
    };

    const toggleGanttSuggestions = () => {
        if (!hasPermission('ps_gantt_avance', 'view') && !hasPermission('ps_gantt_avance', 'edit')) return;
        setShowGanttSuggestions(prev => !prev);
    };

    const handleOpenAutoPlanModal = () => {
        if (!hasPermission('ps_gantt_avance', 'edit')) return;
        if (!activePlanId) return;
        const plan = weeklyPlans.find(p => p.id === activePlanId);
        if (!plan) return;

        // Compute suggested tasks and their units to schedule (utiliza getGanttSuggestedTasks que ya contiene subtareas y tareas sin desglose)
        const suggestedTasks = getGanttSuggestedTasks();
        const initialSuggestions = suggestedTasks.map(t => {
            const taskId = t.id;
            const isRezagada = t.end && activePlan && t.end < activePlan.startDate;

            // Get all units assigned to this task in Matrix
            const assignedUnits = units.filter(u => isTaskUnitAssigned(taskId, u.id, matrixData, tasks));

            // Calculate pending units & unitsToSuggest
            const pendingUnits = assignedUnits.filter(u => getAccumulatedCompliance(taskId, u.id) < 100);
            const pendingUnitsCount = pendingUnits.length;
            const durationWeeks = getTaskDurationWeeks(t);
            const unitsToSuggest = Math.ceil(pendingUnitsCount / durationWeeks);

            // Filter available units: not already in active plan and at 0% compliance
            const availableUnits = assignedUnits.filter(u => {
                const exists = plan.allocations.some(a => a.taskId === taskId && a.unitId === u.id);
                if (exists) return false;
                return getAccumulatedCompliance(taskId, u.id) === 0;
            });

            // Select sequential first X units
            const unitsToSchedule = availableUnits.slice(0, unitsToSuggest);

            const taskObj = findTaskById(taskId, tasks);
            const taskProgress = taskObj ? calculateTaskProgressLocal(taskObj) : 0;

            const scheduledUnitsWithResp = unitsToSchedule.map(u => {
                const config = protocolConfigs[taskId];
                const group = config?.reviewerGroups?.find(g => g.unitIds.includes(u.id));
                const rev1Id = group?.reviewers?.[0]?.id;

                const levelName = levels.find(l => l.id === u.levelId)?.name || '-';

                const accumulatedCompliance = getTaskUnitAccumulatedCompliance(
                    taskId,
                    u.id,
                    tasks,
                    weeklyPlans,
                    protocolConfigs,
                    savedProtocols,
                    taskProgress,
                    activePlanId
                );
                const remainingBalance = Math.max(0, Number((100 - accumulatedCompliance).toFixed(2)));

                // Default days: Monday to Friday active
                const initialDays: Record<string, boolean> = { L: true, M: true, X: true, J: true, V: true, S: false };
                if (dynamicDayColumns && dynamicDayColumns.length > 0) {
                    dynamicDayColumns.forEach(c => {
                        if (c.dayLetter !== 'S' && c.dayLetter !== 'D') {
                            initialDays[c.dayLetter] = true;
                        }
                    });
                }

                return {
                    unitId: u.id,
                    unitName: u.name,
                    levelName: levelName,
                    hasInheritedResp: !!rev1Id,
                    responsibleId: rev1Id || '', // Autocomplete with Quality or empty
                    selected: true, // Individual unit checked state defaults to true
                    accumulatedCompliance,
                    percentComprometido: remainingBalance,
                    days: initialDays
                };
            });

            return {
                taskId: t.id,
                taskName: t.name,
                isRezagada,
                unitsToSuggest, // total count
                selected: true, // Parent task checked state defaults to true
                scheduledUnits: scheduledUnitsWithResp // array of units details
            };
        }).filter(s => s.unitsToSuggest > 0 && s.scheduledUnits.length > 0);

        setGanttSuggestionsData(initialSuggestions);
        setExpandedTaskIdx(null); // Reset accordion
        setGanttAutoPlanModalOpen(true);
    };

    const handleToggleTaskSelection = (taskIdx: number) => {
        setGanttSuggestionsData(prev => {
            const updated = [...prev];
            const task = { ...updated[taskIdx] };
            const newSelected = !task.selected;
            task.selected = newSelected;

            // Set all units inside to match parent selection
            task.scheduledUnits = task.scheduledUnits.map((u: any) => ({
                ...u,
                selected: newSelected
            }));

            updated[taskIdx] = task;

            // If deselecting, collapse accordion if expanded
            if (!newSelected && expandedTaskIdx === taskIdx) {
                setExpandedTaskIdx(null);
            }
            return updated;
        });
    };

    const handleToggleUnitSelection = (taskIdx: number, unitIdx: number) => {
        setGanttSuggestionsData(prev => {
            const updated = [...prev];
            const task = { ...updated[taskIdx] };
            const unitsList = [...task.scheduledUnits];

            unitsList[unitIdx] = {
                ...unitsList[unitIdx],
                selected: !unitsList[unitIdx].selected
            };

            task.scheduledUnits = unitsList;

            // Re-evaluate parent task selected state: true if at least one unit is selected
            const hasAnySelected = unitsList.some((u: any) => u.selected);
            task.selected = hasAnySelected;

            updated[taskIdx] = task;
            return updated;
        });
    };

    const handleIndividualRespChange = (taskIdx: number, unitIdx: number, newRespId: string) => {
        setGanttSuggestionsData(prev => {
            const updated = [...prev];
            const task = { ...updated[taskIdx] };
            const unitsList = [...task.scheduledUnits];
            unitsList[unitIdx] = { ...unitsList[unitIdx], responsibleId: newRespId };
            task.scheduledUnits = unitsList;
            updated[taskIdx] = task;
            return updated;
        });
    };

    const handleUnitPercentComprometidoChange = (taskIdx: number, unitIdx: number, val: number) => {
        setGanttSuggestionsData(prev => {
            const updated = [...prev];
            const task = { ...updated[taskIdx] };
            const unitsList = [...task.scheduledUnits];
            const u = unitsList[unitIdx];
            const maxVal = Math.max(0, 100 - (u.accumulatedCompliance ?? 0));
            const clampedVal = Math.max(0, Math.min(maxVal, isNaN(val) ? 0 : val));
            unitsList[unitIdx] = { ...u, percentComprometido: clampedVal };
            task.scheduledUnits = unitsList;
            updated[taskIdx] = task;
            return updated;
        });
    };

    const handleToggleUnitDay = (taskIdx: number, unitIdx: number, dayKey: string) => {
        setGanttSuggestionsData(prev => {
            const updated = [...prev];
            const task = { ...updated[taskIdx] };
            const unitsList = [...task.scheduledUnits];
            const u = unitsList[unitIdx];
            const currentDays = u.days || {};
            unitsList[unitIdx] = {
                ...u,
                days: {
                    ...currentDays,
                    [dayKey]: !currentDays[dayKey]
                }
            };
            task.scheduledUnits = unitsList;
            updated[taskIdx] = task;
            return updated;
        });
    };

    const handleAutoPlanGanttTasks = () => {
        if (!hasPermission('ps_gantt_avance', 'edit')) return;
        if (!activePlanId) return;
        const plan = weeklyPlans.find(p => p.id === activePlanId);
        if (!plan) return;

        if (plan.status === 'Cerrada') {
            alert("No se puede auto-programar: Esta planificación ya ha sido 'Cerrada' y finalizada.");
            setGanttAutoPlanModalOpen(false);
            return;
        }

        // 1. Validation: check that every selected unit in selected tasks has a responsible and at least one day
        for (const task of ganttSuggestionsData) {
            if (!task.selected) continue;
            for (const u of task.scheduledUnits) {
                if (!u.selected) continue;
                if (!u.responsibleId) {
                    alert(`Debe asignar un responsable para la unidad: ${u.unitName} en la tarea: ${task.taskName}`);
                    return;
                }
                const hasDay = u.days && Object.values(u.days).some(Boolean);
                if (!hasDay) {
                    alert(`Debe seleccionar al menos un día de trabajo para la unidad: ${u.unitName} en la tarea: ${task.taskName}`);
                    return;
                }
            }
        }

        // 2. Perform scheduling for active units
        let addedCount = 0;
        const newAllocations = [...plan.allocations];

        const nowIso = new Date().toISOString();
        const userName = currentUser?.name || currentUser?.email || 'Usuario';

        ganttSuggestionsData.forEach(task => {
            if (!task.selected) return;
            task.scheduledUnits.forEach((u: any) => {
                if (!u.selected) return;
                newAllocations.push({
                    taskId: task.taskId,
                    unitId: u.unitId,
                    responsibleId: u.responsibleId,
                    days: u.days || { L: true, M: true, X: true, J: true, V: true, S: false },
                    executed: null,
                    percentComprometido: u.percentComprometido ?? 100,
                    percentCumplimiento: 0,
                    updatedAt: nowIso,
                    updatedBy: userName
                });
                addedCount++;
            });
        });

        if (addedCount === 0) {
            alert("No hay unidades sugeridas seleccionadas para auto-programar.");
            return;
        }

        // Save updated plan allocations
        setWeeklyPlans(prevPlans => prevPlans.map(p => {
            if (p.id === activePlanId) {
                return { ...p, allocations: newAllocations, updatedAt: nowIso, updatedBy: userName };
            }
            return p;
        }));

        addActivity({
            type: 'planning',
            title: 'Auto-programación por Carta Gantt',
            description: `Se auto-programaron ${addedCount} unidades físicas de partidas sugeridas/atrasadas por Gantt`,
            user: currentUser?.name || currentUser?.email || 'Usuario'
        });

        alert(`¡Listo! Se han auto-programado automáticamente ${addedCount} unidades físicas.`);
    };

    // --- Renderers ---

    // Calculate if there are missing observations for lookahead
    const hasMissingLookaheadComments = useMemo(() => {
        if (!activeLookahead || activeLookahead.status !== 'En Revisión') return false;
        return !isLookaheadFullyResolved(activeLookahead);
    }, [activeLookahead, lookaheadPlans, tasks]);

    // Calculate if there are missing CNCs
    const hasMissingCNC = useMemo(() => {
        if (!activePlan || activePlan.status !== 'En Revisión') return false;

        return activePlan.allocations.some(alloc => {
            const config = protocolConfigs[alloc.taskId];
            const appliesProtocol = config?.applies ?? false;

            // Calculate accumulated compliance from closed plans
            let accumulatedCompliance = 0;
            weeklyPlans
                .filter(p => p.status === 'Cerrada')
                .flatMap(p => p.allocations)
                .forEach(a => {
                    if (a.taskId === alloc.taskId && a.unitId === alloc.unitId) {
                        accumulatedCompliance += getAllocationCompliancePct(a);
                    }
                });
            accumulatedCompliance = Math.min(100, accumulatedCompliance);

            // Check if protocol was closed (meaning it is 100% completed)
            const wasProtocolExecuted = appliesProtocol && isProtocolProgressedLocal(alloc.taskId, alloc.unitId);

            // Effective comprometido and cumplimiento for this week
            const pctComprometido = alloc.percentComprometido ?? (alloc.executed ? 100 : 100);
            const pctCumplimiento = getAllocationCompliancePct(alloc);

            // If we didn't achieve the commitment, and no CNC is selected, it's missing CNC
            const isFitted = pctCumplimiento >= pctComprometido;
            return !isFitted && !alloc.cncId;
        });
    }, [activePlan, weeklyPlans, savedProtocols, protocolConfigs]);

    // User profile/role helpers for Jefatura / Oficina Técnica
    const isJefaturaOrOficina = useMemo(() => {
        if (!currentUser) return false;
        const isGlobalAdmin = currentUser.globalRole === 'Administrador';
        const isProjectAdmin = currentUser.role === 'admin' || currentUser.role === 'manager';
        if (isGlobalAdmin || isProjectAdmin) return true;

        const projectId = projectInfo?.id;
        if (!projectId) return false;

        const assignment = currentUser.assignedProjects?.find((ap: any) => ap.projectId === projectId);
        if (!assignment) return false;

        const profileId = assignment.profileId;
        return profileId === 'prof-jefe' || profileId === 'prof-oficina';
    }, [currentUser, projectInfo]);

    // Helper to get status string ("si" | "no" | "pendiente")
    const getStatusValue = (alloc: WeeklyAllocation) => {
        if (alloc.executed === true) return "si";
        if (alloc.executed === false) return "no";
        return "pendiente";
    };

    // Helper to check if a weekly allocation row's inputs should be disabled
    const getDisabledStatus = (alloc: WeeklyAllocation) => {
        if (isClosedStatus(activePlan?.status)) return true;
        if (isPlanningStatus(activePlan?.status)) return true;

        const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager' || currentUser?.globalRole === 'Administrador';

        if (isExecutionStatus(activePlan?.status)) {
            const isAssigned = responsibles.find(r => r.id === alloc.responsibleId)?.email === currentUser?.email;
            return !(isAdmin || isAssigned);
        }

        if (isRevisionStatus(activePlan?.status)) {
            return !isJefaturaOrOficina;
        }

        return true;
    };

    // Helper to get all unexecuted allocations from past closed plans eligible for reprogramming
    const getReprogrammedAllocations = React.useCallback(() => {
        const closedPlans = weeklyPlans.filter(p => isClosedStatus(p.status));
        if (closedPlans.length === 0) return [];

        const activePlan = weeklyPlans.find(p => p.id === activePlanId);
        const activeAllocSet = new Set(
            (activePlan?.allocations || []).map(a => `${a.taskId}_${a.unitId}`)
        );

        // Map latest unexecuted allocation per task-unit key
        const latestUnexecutedMap = new Map<string, WeeklyAllocation & { planStartDate?: string }>();

        // Sort closed plans chronologically
        const sortedClosed = [...closedPlans].sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''));

        for (const plan of sortedClosed) {
            for (const alloc of plan.allocations) {
                const isUnexecuted = alloc.executed === false || getStatusValue(alloc) === 'no';
                const key = `${alloc.taskId}_${alloc.unitId}`;
                if (isUnexecuted) {
                    latestUnexecutedMap.set(key, { ...alloc, planStartDate: plan.startDate });
                } else if (alloc.executed === true || getStatusValue(alloc) === 'si') {
                    // If later it was executed in a subsequent closed plan, it's no longer pending
                    latestUnexecutedMap.delete(key);
                }
            }
        }

        const result: (WeeklyAllocation & { taskName: string; unitName: string; levelName: string; remainingBalance: number; isRestricted: boolean; planStartDate?: string })[] = [];

        // Build restriction lookup across lookaheadPlans
        const pendingConstraints = lookaheadPlans.flatMap(p => p.constraints).filter(c => c.status !== 'Liberado');

        latestUnexecutedMap.forEach((alloc) => {
            const key = `${alloc.taskId}_${alloc.unitId}`;
            // If already scheduled in the current active plan, don't show as pending
            if (activeAllocSet.has(key)) return;

            // Determine accumulated compliance for this unexecuted unit
            // If in the previous closed week where it was not executed:
            // - If percentCumplimiento was 0 (or undefined with executed=false), accumulated compliance is 0%, allowing 100% comprometido.
            // - If it had partial progress (e.g., 40%), accumulated compliance is that progress (e.g., 40%), allowing remaining balance (e.g., 60%).
            const lastWeekCumplimiento = alloc.percentCumplimiento !== undefined
                ? alloc.percentCumplimiento
                : (alloc.executed ? 100 : 0);

            // Also check quality protocol and general accumulated compliance, but prioritize the unexecuted week's actual progress
            const taskObj = findTaskById(alloc.taskId, tasks);
            const parentTask = findParentTask(alloc.taskId, tasks);
            const targetTaskId = (taskObj?.isWeeklySubtask && parentTask) ? parentTask.id : alloc.taskId;

            // Check quality protocol completion
            const appliesProtocol = protocolConfigs[targetTaskId]?.applies ?? false;
            let isProtocolDone = false;
            if (appliesProtocol) {
                const protocolKey = `${targetTaskId}_${alloc.unitId}`;
                const pState = findSavedProtocol(savedProtocols, protocolKey, targetTaskId, alloc.unitId);
                if (pState && (pState.status === 'Resuelto' || pState.status === 'Cerrado')) {
                    isProtocolDone = true;
                }
            }
            if (isProtocolDone) return;

            const generalAccumulated = getTaskUnitAccumulatedCompliance(
                alloc.taskId,
                alloc.unitId,
                tasks,
                weeklyPlans,
                protocolConfigs,
                savedProtocols,
                undefined,
                activePlanId
            );
            const accumulatedCompliance = Math.min(100, Math.max(0, Number(Math.max(lastWeekCumplimiento, generalAccumulated).toFixed(2))));
            if (accumulatedCompliance >= 100) return;

            const remainingBalance = Math.max(0, Number((100 - accumulatedCompliance).toFixed(2)));
            const allocUnit = units.find(u => u.id === alloc.unitId);
            const unitName = allocUnit?.name || alloc.unitId;
            const levelName = levels.find(l => l.id === allocUnit?.levelId)?.name || '-';
            const taskName = taskObj ? taskObj.name : (findTaskName(alloc.taskId, tasks) || '[Partida Obsoleta/Fuera de Gantt]');

            // Check restriction
            const taskConstraints = pendingConstraints.filter(c => c.taskId === targetTaskId);
            let isRestricted = false;
            for (const c of taskConstraints) {
                if (c.unitsType === 'all' || c.unitsType === 'na' || !c.unitsType) {
                    isRestricted = true;
                    break;
                } else if (c.unitsType === 'custom' && c.unitIds && c.unitIds.includes(alloc.unitId)) {
                    isRestricted = true;
                    break;
                }
            }

            result.push({
                ...alloc,
                percentCumplimiento: accumulatedCompliance,
                taskName,
                unitName,
                levelName,
                remainingBalance,
                isRestricted
            });
        });

        return result;
    }, [weeklyPlans, activePlanId, tasks, units, levels, protocolConfigs, savedProtocols, lookaheadPlans]);

    const handlePlanAllReprogrammed = () => {
        const items = getReprogrammedAllocations();
        if (items.length === 0) {
            alert("No hay tareas no ejecutadas pendientes de reprogramar.");
            return;
        }

        const displayAllocations: (WeeklyAllocation & { selected: boolean; isRestricted: boolean; isPreviouslyExecuted?: boolean; isReprogrammed?: boolean; isSuggestedByGantt?: boolean })[] = items.map(item => {
            const hasDays = item.days && Object.values(item.days).some(d => d);
            const daysToUse = hasDays ? { ...item.days } : { L: true, M: true, X: true, J: true, V: true, S: false };

            return {
                taskId: item.taskId,
                unitId: item.unitId,
                responsibleId: item.responsibleId || bulkResponsibleId || '',
                days: daysToUse,
                executed: null,
                selected: false,
                isRestricted: item.isRestricted,
                isPreviouslyExecuted: false,
                isReprogrammed: true,
                isSuggestedByGantt: false,
                accumulatedCompliance: item.percentCumplimiento ?? 0,
                remainingBalance: item.remainingBalance,
                percentComprometido: item.remainingBalance,
                percentCumplimiento: item.percentCumplimiento ?? 0
            };
        });

        setPlanningTaskId('ALL_REPROGRAMMED');
        setCurrentAllocations(displayAllocations);
        setPlanTaskModalOpen(true);
    };

    // Recursive Gantt Bar Renderer
    const renderGanttBars = (list: Task[], level = 0): React.ReactNode[] => {
        let rows: React.ReactNode[] = [];
        list.forEach((task) => {
            // Logic to hide weekly subtasks if not in weekly or intermedia mode (matches left table logic)
            const visibleChildren = (task.children || []).filter(child => {
                if (activeTab !== 'semanal' && activeTab !== 'intermedia' && child.isWeeklySubtask) return false;
                return true;
            });
            const hasChildren = visibleChildren.length > 0;

            // Calculate Position
            const left = getDateX(task.start || '2026-01-01');
            const taskEnd = getDateX(getDayAfter(task.end || task.start || '2026-01-01'));
            const width = taskEnd - left;

            const isSelected = selectedGanttTaskId === task.id;
            const isPredecessor = taskPathInfo?.predecessors.has(task.id_original_project || -9999);
            const isSuccessor = taskPathInfo?.successors.has(task.id_original_project || -9999);

            let customBarClass = "";
            let customBarStyles: React.CSSProperties = {};

            if (selectedGanttTaskId) {
                if (isSelected) {
                    customBarClass = "ring-2 ring-blue-500 dark:ring-blue-400 shadow-lg scale-y-105 z-25";
                } else if (activeTab === 'maestra') {
                    if (isPredecessor) {
                        customBarClass = "z-20";
                        customBarStyles.backgroundColor = "#eab308"; // Amarillo brillante
                    } else if (isSuccessor) {
                        customBarClass = "z-20";
                        customBarStyles.backgroundColor = "#d946ef"; // Rosado/Fucsia
                    } else {
                        customBarClass = "opacity-35"; // Atenuado
                    }
                }
            }

            let folderColor = "#1e293b";
            if (showCriticalPath && activeTab === 'maestra') {
                if (task.isCritical) {
                    folderColor = "#ef4444";
                } else {
                    folderColor = "#cbd5e1";
                }
            } else {
                if (task.isCritical) {
                    folderColor = "#ef4444";
                }
                if (taskPathInfo) {
                    const isPredecessor = task.id_original_project !== undefined && taskPathInfo.predecessors.has(task.id_original_project);
                    const isSuccessor = task.id_original_project !== undefined && taskPathInfo.successors.has(task.id_original_project);
                    if (isPredecessor) {
                        folderColor = "#eab308";
                    } else if (isSuccessor) {
                        folderColor = "#d946ef";
                    }
                }
            }

            rows.push(
                <div
                    key={task.id}
                    id={`gantt-row-${task.id}`}
                    className="h-[44px] border-b border-slate-200 dark:border-slate-700 relative group cursor-pointer"
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedGanttTaskId(prev => prev === task.id ? null : task.id);
                    }}
                >
                    {/* Bar */}
                    {task.type === 'folder' ? (
                        <div
                            id={`gantt-bar-${task.id}`}
                            className={`absolute top-[16px] h-[12px] transition-all z-10 ${customBarClass}`}
                            style={{
                                left: `${left}px`,
                                width: `${Math.max(width, 2)}px`,
                                userSelect: 'none',
                            }}
                            title={`${task.name}: ${formatDateToShow(task.start)} - ${formatDateToShow(task.end)} (${task.duration} días)`}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedGanttTaskId(prev => prev === task.id ? null : task.id);
                            }}
                        >
                            {/* Thin horizontal line */}
                            <div
                                className="absolute top-[2px] left-0 right-0 h-[4px]"
                                style={{ backgroundColor: folderColor }}
                            />
                            {/* Left Hook SVG */}
                            <svg
                                className="absolute left-0 top-[2px] w-[8px] h-[10px]"
                                viewBox="0 0 8 10"
                                style={{ color: folderColor }}
                            >
                                <polygon points="0,0 8,0 8,3 4,7 0,3" fill="currentColor" />
                            </svg>
                            {/* Right Hook SVG */}
                            <svg
                                className="absolute right-0 top-[2px] w-[8px] h-[10px]"
                                viewBox="0 0 8 10"
                                style={{ color: folderColor }}
                            >
                                <polygon points="0,0 8,0 8,3 4,7 0,3" fill="currentColor" />
                            </svg>
                        </div>
                    ) : (
                        <div
                            id={`gantt-bar-${task.id}`}
                            className={`absolute top-2 h-5 rounded-md shadow-sm text-[10px] text-white flex items-center px-2 overflow-hidden whitespace-nowrap transition-all z-10
                                ${showCriticalPath && activeTab === 'maestra'
                                    ? (task.isCritical
                                        ? 'bg-red-500 dark:bg-red-600 hover:bg-red-600 shadow-lg shadow-red-500/20'
                                        : 'bg-blue-300/30 dark:bg-blue-900/10 text-blue-400 dark:text-blue-600 border border-slate-250/20 dark:border-slate-800/40 opacity-30 hover:opacity-60')
                                    : (task.isCritical
                                        ? 'bg-red-500 dark:bg-red-600 hover:bg-red-600'
                                        : 'bg-blue-500 dark:bg-blue-600 hover:bg-blue-600')
                                }
                                ${(isMasterEditing) ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'}
                                ${customBarClass}
                            `}
                            style={{
                                left: `${left}px`,
                                width: `${Math.max(width, 2)}px`,
                                userSelect: 'none',
                                ...customBarStyles
                            }}
                            title={`${task.name}: ${formatDateToShow(task.start)} - ${formatDateToShow(task.end)} (${task.duration} días)`}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedGanttTaskId(prev => prev === task.id ? null : task.id);
                            }}
                            onMouseDown={(e) => {
                                if (!isMasterEditing || !task.start) return;
                                setDraggingTask({
                                    taskId: task.id,
                                    startX: e.clientX,
                                    originalStart: task.start
                                });
                            }}
                        >
                            {width > 30 && <span>{truncateProgress(calculateTaskProgressLocal(task))}%</span>}
                        </div>
                    )}

                    {/* Baseline / Ghost Bar */}
                    {showComparison && comparisonBaseline && (
                        (() => {
                            const findInSnapshot = (id: string, list: Task[]): Task | null => {
                                for (const t of list) {
                                    if (t.id === id) return t;
                                    if (t.children) {
                                        const found = findInSnapshot(id, t.children);
                                        if (found) return found;
                                    }
                                }
                                return null;
                            };

                            const baselineTask = findInSnapshot(task.id, comparisonBaseline.tasksSnapshot);
                            if (!baselineTask || !baselineTask.start) return null;

                            const bLeft = getDateX(baselineTask.start);
                            const bEnd = getDateX(getDayAfter(baselineTask.end || baselineTask.start));
                            const bWidth = bEnd - bLeft;

                            return (
                                <div
                                    className={`absolute top-7 h-1.5 rounded-sm opacity-40 z-0 pointer-events-none
                                        ${task.type === 'folder' ? 'bg-slate-300' : 'bg-slate-400 border border-dashed border-slate-500'}
                                    `}
                                    style={{
                                        left: `${bLeft}px`,
                                        width: `${Math.max(bWidth, 2)}px`
                                    }}
                                    title={`Base: ${baselineTask.start} - ${baselineTask.end}`}
                                />
                            );
                        })()
                    )}
                </div>
            );

            if (task.expanded && hasChildren) {
                rows = [...rows, ...renderGanttBars(visibleChildren, level + 1)];
            }
        });
        return rows;
    };

    const renderTableRows = (list: Task[], level = 0, indexPrefix = ""): React.ReactNode[] => {
        let rows: React.ReactNode[] = [];
        list.forEach((task, index) => {
            const currentIndex = indexPrefix ? `${indexPrefix}.${index + 1}` : `${index + 1}`;
            const paddingLeft = level * 20 + 10;
            const isEditing = activeTab === 'maestra' ? isMasterEditing : isWeeklyEditing;

            // Determine visible children for recursion
            const visibleChildren = (task.children || []).filter(child => {
                // If not in Weekly or Intermedia mode, hide any items flagged as "Weekly Subtasks"
                if (activeTab !== 'semanal' && activeTab !== 'intermedia' && child.isWeeklySubtask) return false;
                return true;
            });
            const hasChildren = visibleChildren.length > 0;

            // I.T.E Visualization Logic
            let rowBackgroundClass = "";
            let rowBorderClass = "";

            if (activeTab === 'intermedia' && isITEView && task.type === 'task') {
                // Find all constraints for this task across ALL plans
                const allConstraints = lookaheadPlans.flatMap(p => p.constraints).filter(c => c.taskId === task.id);
                // Check if task was ever cleared in any plan
                const isExplicitlyClearedAnywhere = lookaheadPlans.some(p => p.clearedTaskIds?.includes(task.id));

                // Only color if there is some history (constraint exists or was cleared)
                if (allConstraints.length > 0 || isExplicitlyClearedAnywhere) {
                    const hasPending = allConstraints.some(c => c.status !== 'Liberado');

                    if (hasPending) {
                        rowBackgroundClass = "bg-[#fee2e2] dark:bg-red-900/30"; // Soft Red
                    } else {
                        // Green if no pending constraints
                        rowBackgroundClass = "bg-[#f0fdf4] dark:bg-green-900/30"; // Soft Green
                    }
                }
            }

            if (activeTab === 'semanal' && showGanttSuggestions && activePlan && (hasPermission('ps_gantt_avance', 'view') || hasPermission('ps_gantt_avance', 'edit'))) {
                const parentOfSub = task.isWeeklySubtask ? findParentTask(task.id, tasks) : null;
                const taskStart = task.start || parentOfSub?.start;
                const taskEnd = task.end || parentOfSub?.end;
                const weekStart = activePlan.startDate;
                const weekEnd = activePlan.endDate;

                // Solo resaltar elementos programables (tareas hoja sin desglose o subtareas semanales)
                const isProgrammableLeaf = (task.type === 'task' && !task.isWeeklySubtask && (!task.children || !task.children.some(c => c.isWeeklySubtask && !c.isDeleted))) || task.isWeeklySubtask;

                if (isProgrammableLeaf && taskStart && taskEnd && weekStart && weekEnd) {
                    const overlaps = taskStart <= weekEnd && taskEnd >= weekStart;
                    if (overlaps) {
                        rowBackgroundClass = "bg-blue-50/80 dark:bg-blue-950/40 font-bold";
                        rowBorderClass = "border-l-4 border-l-blue-500";
                    } else if (taskEnd < weekStart) {
                        const progress = calculateTaskProgressLocal(task);
                        if (progress < 100) {
                            rowBackgroundClass = "bg-red-50/70 dark:bg-red-950/20 font-bold";
                            rowBorderClass = "border-l-4 border-l-red-500 border-red-200 dark:border-red-900";
                        }
                    }
                }
            }

            const isSelected = selectedGanttTaskId === task.id;
            const isPredecessor = taskPathInfo?.predecessors.has(task.id_original_project || -9999);
            const isSuccessor = taskPathInfo?.successors.has(task.id_original_project || -9999);

            let customRowClass = "";
            if (selectedGanttTaskId) {
                if (isSelected) {
                    customRowClass = "bg-blue-100/60 dark:bg-blue-950/40 border-l-4 border-l-blue-500 font-semibold";
                } else if (activeTab === 'maestra') {
                    if (isPredecessor) {
                        customRowClass = "bg-yellow-50/70 dark:bg-yellow-950/25 border-l-4 border-l-yellow-500";
                    } else if (isSuccessor) {
                        customRowClass = "bg-fuchsia-50/70 dark:bg-fuchsia-950/25 border-l-4 border-l-fuchsia-500";
                    } else {
                        customRowClass = "opacity-35";
                    }
                }
            } else if (showCriticalPath && activeTab === 'maestra') {
                if (task.isCritical) {
                    customRowClass = "bg-red-50/50 dark:bg-red-950/10 border-l-4 border-l-red-500 font-semibold";
                } else {
                    customRowClass = "opacity-25";
                }
            }

            const isGraphicSelected = graphicMode && graphicSelectedTaskId === task.id;

            rows.push(
                <tr
                    key={task.id}
                    className={`
                        border-b border-slate-200 dark:border-slate-700 
                        ${task.type === 'folder' ? 'bg-slate-50 dark:bg-slate-800/50' : 'bg-white dark:bg-slate-900'}
                        ${rowBackgroundClass}
                        ${rowBorderClass}
                        hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors h-[44px]
                        ${isBreakdownMode || (activeTab === 'maestra' && isMasterEditing) || graphicMode || true ? 'cursor-pointer' : ''}
                        ${isGraphicSelected ? 'bg-blue-100 dark:bg-blue-900/30 border-l-4 border-l-blue-500 border-b-blue-200 dark:border-b-blue-800' : ''}
                        ${customRowClass}
                    `}
                    onClick={(e) => {
                        e.stopPropagation();
                        const isLeaf = task.type === 'task' && !hasChildren;
                        const clickedOnText = (e.target as HTMLElement).closest('.task-name-text');

                        if (isLeaf || clickedOnText) {
                            setSelectedGanttTaskId(prev => prev === task.id ? null : task.id);
                            if ((graphicMode || weeklyGraphicMode) && task.type === 'task' && !hasChildren) {
                                setGraphicSelectedTaskId(prev => prev === task.id ? null : task.id);
                            }
                        } else if (task.type === 'folder' || hasChildren) {
                            const toggleRecursive = (list: Task[]): Task[] => {
                                return list.map(t => {
                                    if (t.id === task.id) return { ...t, expanded: !t.expanded };
                                    if (t.children) return { ...t, children: toggleRecursive(t.children) };
                                    return t;
                                });
                            };
                            setTasks(toggleRecursive(tasks));
                        }
                    }}
                    onContextMenu={(e) => handleContextMenu(e, task)}
                >
                    <td className={`px-4 py-2 text-xs font-mono w-12 text-center transition-colors ${showCriticalPath && task.isCritical && activeTab === 'maestra' ? 'text-red-600 dark:text-red-400 font-bold bg-red-100/50 dark:bg-red-950/30' : 'text-slate-500'}`}>{task.id_original_project || "-"}</td>
                    <td
                        className="px-4 py-2 border-r border-slate-200 dark:border-slate-700"
                        style={tareaColumnWidth !== null ? { width: `${tareaColumnWidth}px`, minWidth: `${tareaColumnWidth}px`, maxWidth: `${tareaColumnWidth}px`, overflow: 'hidden' } : undefined}
                    >
                        <div style={{ paddingLeft: `${paddingLeft}px` }} className="flex items-center gap-2 overflow-hidden w-full">
                            {(task.type === 'folder' || hasChildren) && (
                                <button onClick={(e) => {
                                    e.stopPropagation();
                                    const toggleRecursive = (list: Task[]): Task[] => {
                                        return list.map(t => {
                                            if (t.id === task.id) return { ...t, expanded: !t.expanded };
                                            if (t.children) return { ...t, children: toggleRecursive(t.children) };
                                            return t;
                                        });
                                    };
                                    setTasks(toggleRecursive(tasks));
                                }} className="text-slate-400 hover:text-primary">
                                    <span className="material-symbols-outlined text-[18px]">
                                        {task.expanded ? 'folder_open' : 'folder'}
                                    </span>
                                </button>
                            )}
                            {task.type === 'task' && !hasChildren && (
                                <span className={`material-symbols-outlined text-[18px] transition-all ${showCriticalPath && task.isCritical && activeTab === 'maestra' ? 'text-red-500 animate-pulse' : 'text-slate-400'}`}>
                                    {showCriticalPath && task.isCritical && activeTab === 'maestra' ? 'warning' : 'check_circle'}
                                </span>
                            )}
                            <span className={`task-name-text text-sm truncate flex-1 flex items-center gap-1.5 ${task.type === 'folder' ? 'font-bold text-slate-800 dark:text-slate-200' : 'text-slate-600 dark:text-slate-300'} ${showCriticalPath && task.isCritical && activeTab === 'maestra' ? 'text-red-600 dark:text-red-400 font-semibold' : ''}`}>
                                <span>{cleanTaskName(task.name)}</span>

                                {(() => {
                                    const weeklySubtasks = (task.children || []).filter(c => c.isWeeklySubtask && !c.isDeleted);
                                    if (weeklySubtasks.length > 0) {
                                        const sumWeight = weeklySubtasks.reduce((sum, c) => sum + (c.weight !== undefined ? Number(c.weight) : 0), 0);
                                        const roundedSum = Math.round(sumWeight * 100) / 100;
                                        if (roundedSum < 100) {
                                            return (
                                                <span
                                                    className="text-[10px] bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400 px-1.5 py-0.5 rounded ml-1.5 font-bold"
                                                    title="Falta distribuir ponderaciones (Saldo pendiente)"
                                                >
                                                    Peso: {roundedSum}%
                                                </span>
                                            );
                                        }
                                    }
                                    return null;
                                })()}
                                {task.isWeeklySubtask && task.weight !== undefined && (
                                    <span className="text-[10px] bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-400 px-1.5 py-0.5 rounded ml-1.5 font-bold">
                                        Peso: {task.weight}%
                                    </span>
                                )}
                            </span>
                            {activeTab === 'semanal' && task.type === 'task' && !hasChildren && (() => {
                                const pendingConstraints = lookaheadPlans.flatMap(p => p.constraints).filter(c => c.taskId === task.id && c.status !== 'Liberado');
                                if (pendingConstraints.length > 0) {
                                    return (
                                        <span className="material-symbols-outlined text-[18px] text-amber-500 animate-pulse cursor-help" title="Esta tarea cuenta con restricciones activas">
                                            warning
                                        </span>
                                    );
                                }
                                return null;
                            })()}
                        </div>
                    </td>

                    {activeTab === 'semanal' || activeTab === 'intermedia' || (activeTab === 'maestra' && !isMasterEditing) ? (
                        <td className={`px-4 py-2 text-center border-r border-slate-200 dark:border-slate-700 ${activeTab === 'semanal' ? 'w-28 shrink-0' : 'w-32'}`}>
                            {(task.type === 'task' || (task.children && task.children.length > 0)) ? (
                                (() => {
                                    const accumulatedProgress = calculateTaskProgressLocal(task);
                                    const programmedProgress = (activeTab === 'semanal' && activePlan) ? calculateWeeklyProgrammedProgress(task) : 0;
                                    const hasWeeklySubtasks = task.children && task.children.some(c => c.isWeeklySubtask);
                                    const isSubtask = task.isWeeklySubtask;

                                    let textColorClass = 'text-slate-700 dark:text-slate-300 font-bold';
                                    let barColorClass = 'bg-blue-500';

                                    if (hasWeeklySubtasks) {
                                        textColorClass = 'text-black dark:text-white font-bold';
                                        barColorClass = 'bg-slate-950 dark:bg-white';
                                    } else if (isSubtask) {
                                        textColorClass = 'text-green-600 dark:text-green-400 font-bold';
                                        barColorClass = 'bg-green-500';
                                    } else if (accumulatedProgress === 100) {
                                        textColorClass = 'text-green-600 dark:text-green-400 font-bold';
                                        barColorClass = 'bg-green-500';
                                    }

                                    return (
                                        <div className="flex flex-col gap-1 px-1">
                                            <div className="flex items-center justify-center gap-1 flex-wrap">
                                                <span className={`text-[10px] ${textColorClass}`}>
                                                    {truncateProgress(accumulatedProgress)}%
                                                </span>
                                                {activeTab === 'semanal' && programmedProgress > 0 && (
                                                    <span className="text-[10px] text-green-600 dark:text-green-400 font-bold ml-1">
                                                        (+{truncateProgress(programmedProgress)}%)
                                                    </span>
                                                )}
                                            </div>
                                            <div className="h-1.5 w-full bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden flex">
                                                <div className={`h-full ${barColorClass}`} style={{ width: `${accumulatedProgress}%` }} />
                                                {activeTab === 'semanal' && programmedProgress > 0 && (
                                                    <div
                                                        className="h-full bg-green-500"
                                                        style={{ width: `${Math.min(100 - accumulatedProgress, programmedProgress)}%` }}
                                                        title={`+${truncateProgress(programmedProgress)}% programado esta semana`}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    );
                                })()
                            ) : (
                                <span className="text-xs text-slate-400 font-medium">-</span>
                            )}
                        </td>
                    ) : null}
                    {activeTab === 'semanal' && showWeeklyDates && (
                        <>
                            <td className="px-2 py-1 text-center border-r border-slate-200 dark:border-slate-700 w-24">
                                <span className="text-xs text-slate-600 dark:text-slate-300 block whitespace-nowrap font-medium">
                                    {formatDateToShow(task.start)}
                                </span>
                            </td>
                            <td className="px-2 py-1 text-center border-r border-slate-200 dark:border-slate-700 w-24">
                                <span className="text-xs text-slate-600 dark:text-slate-300 block whitespace-nowrap font-medium">
                                    {formatDateToShow(task.end)}
                                </span>
                            </td>
                        </>
                    )}
                    {activeTab === 'maestra' && (
                        <>
                            <td className="px-4 py-2 text-center border-r border-slate-200 dark:border-slate-700 w-24">
                                {isMasterEditing && task.type !== 'folder' ? (
                                    <input
                                        type="number"
                                        className="w-full text-xs border border-slate-300 rounded p-1 dark:bg-slate-800 dark:border-slate-600 text-center"
                                        value={task.duration ?? 0}
                                        onChange={(e) => {
                                            const newDuration = parseInt(e.target.value) || 0;
                                            const updateRecursive = (list: Task[]): Task[] => {
                                                return list.map(t => {
                                                    if (t.id === task.id) {
                                                        const updatedTask = { ...t, duration: newDuration };
                                                        // Sync End Date
                                                        if (t.start && newDuration >= 0) {
                                                            updatedTask.end = calculateEndDate(t.start, newDuration, workingDays, holidays);
                                                        }
                                                        return updatedTask;
                                                    }
                                                    if (t.children) return { ...t, children: updateRecursive(t.children) };
                                                    return t;
                                                });
                                            };
                                            setTasks(prev => {
                                                const updated = updateRecursive(prev);
                                                return syncDependencies(updated, task.id);
                                            });
                                        }}
                                    />
                                ) : (
                                    <span className="text-sm text-slate-600 dark:text-slate-400">
                                        {task.duration !== undefined ? `${task.duration} días` : '-'}
                                    </span>
                                )}
                            </td>
                            <td className="px-2 py-1 border-r border-slate-200 dark:border-slate-700 w-36">
                                {isMasterEditing && task.type !== 'folder' ? (
                                    <input
                                        type="date"
                                        className="w-full text-xs border border-slate-300 rounded p-1 dark:bg-slate-800 dark:border-slate-600"
                                        value={task.start || ""}
                                        onChange={(e) => {
                                            const newStart = e.target.value;
                                            const updateRecursive = (list: Task[]): Task[] => {
                                                return list.map(t => {
                                                    if (t.id === task.id) {
                                                        let sDate = new Date(newStart + "T12:00:00");
                                                        while (!isWorkingDay(sDate, workingDays, holidays)) {
                                                            sDate.setDate(sDate.getDate() + 1);
                                                        }
                                                        const normalizedStart = getLocalDateStr(sDate);
                                                        const updatedTask = { ...t, start: normalizedStart };

                                                        if (t.duration === 0) {
                                                            updatedTask.end = normalizedStart;
                                                        }
                                                        // Priority 1: Sync Duration (if End exists)
                                                        else if (normalizedStart && t.end) {
                                                            let duration = calculateWorkingDaysDuration(normalizedStart, t.end, workingDays, holidays);
                                                            if (duration < 1) duration = 1;
                                                            updatedTask.duration = duration;
                                                            updatedTask.end = calculateEndDate(normalizedStart, duration, workingDays, holidays);
                                                        }
                                                        // Priority 2: Sync End (if Duration exists but no End)
                                                        else if (normalizedStart && t.duration) {
                                                            updatedTask.end = calculateEndDate(normalizedStart, t.duration, workingDays, holidays);
                                                        }
                                                        return updatedTask;
                                                    }
                                                    if (t.children) return { ...t, children: updateRecursive(t.children) };
                                                    return t;
                                                });
                                            };
                                            setTasks(prev => {
                                                const updated = updateRecursive(prev);
                                                return syncDependencies(updated, task.id);
                                            });
                                        }}
                                    />
                                ) : (
                                    <span className="text-xs text-slate-600 dark:text-slate-300 block px-2 py-1 whitespace-nowrap">{formatDateToShow(task.start)}</span>
                                )}
                            </td>
                            <td className="px-2 py-1 border-r border-slate-200 dark:border-slate-700 w-36">
                                {isMasterEditing && task.type !== 'folder' ? (
                                    <input
                                        type="date"
                                        className="w-full text-xs border border-slate-300 rounded p-1 dark:bg-slate-800 dark:border-slate-600"
                                        value={task.end || ""}
                                        onChange={(e) => {
                                            const newEnd = e.target.value;
                                            const updateRecursive = (list: Task[]): Task[] => {
                                                return list.map(t => {
                                                    if (t.id === task.id) {
                                                        let eDate = new Date(newEnd + "T12:00:00");
                                                        while (!isWorkingDay(eDate, workingDays, holidays)) {
                                                            eDate.setDate(eDate.getDate() - 1);
                                                        }
                                                        const normalizedEnd = getLocalDateStr(eDate);
                                                        const updatedTask = { ...t, end: normalizedEnd };

                                                        if (t.duration === 0) {
                                                            updatedTask.start = normalizedEnd;
                                                        }
                                                        // Sync Duration
                                                        else if (t.start && normalizedEnd) {
                                                            let duration = calculateWorkingDaysDuration(t.start, normalizedEnd, workingDays, holidays);
                                                            if (duration < 1) {
                                                                duration = 1;
                                                                updatedTask.end = calculateEndDate(t.start, 1, workingDays, holidays);
                                                            } else {
                                                                updatedTask.duration = duration;
                                                            }
                                                        }
                                                        return updatedTask;
                                                    }
                                                    if (t.children) return { ...t, children: updateRecursive(t.children) };
                                                    return t;
                                                });
                                            };
                                            setTasks(prev => {
                                                const updated = updateRecursive(prev);
                                                return syncDependencies(updated, task.id);
                                            });
                                        }}
                                    />
                                ) : (
                                    <span className="text-xs text-slate-600 dark:text-slate-300 block px-2 py-1 whitespace-nowrap">{formatDateToShow(task.end)}</span>
                                )}
                            </td>
                            {(isMasterEditing || flattenedTasks.some(t => (t.predecessors && t.predecessors.length > 0) || (t.successors && t.successors.length > 0))) && (
                                <>
                                    <td className="px-2 py-1 w-24 border-r border-slate-200 dark:border-slate-700">
                                        {isMasterEditing ? (
                                            <PredecessorInputCell
                                                task={task}
                                                onUpdate={handleUpdatePredecessors}
                                            />
                                        ) : (
                                            <span className="text-xs text-slate-600 dark:text-slate-300 block px-2 py-1 whitespace-nowrap">{task.predecessors?.join(", ") || "-"}</span>
                                        )}
                                    </td>
                                    <td className="px-2 py-1 w-24">
                                        {isMasterEditing ? (
                                            <SuccessorInputCell
                                                task={task}
                                                onUpdate={handleUpdateSuccessors}
                                            />
                                        ) : (
                                            <span className="text-xs text-slate-600 dark:text-slate-300 block px-2 py-1 whitespace-nowrap">{task.successors?.join(", ") || "-"}</span>
                                        )}
                                    </td>
                                </>
                            )}
                        </>
                    )}
                </tr>
            );

            // Recurse if expanded and has visible children
            if (task.expanded && hasChildren) {
                rows = [...rows, ...renderTableRows(visibleChildren, level + 1, currentIndex)];
            }
        });
        return rows;
    };

    return (
        <div
            className="flex flex-col h-full bg-slate-100 dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-sans overflow-hidden"
        >
            {/* ======================================================== */}
            {/* VISTA MÓVIL (02-plan-intermedia.html / 03-plan-semanal.html) */}
            {/* ======================================================== */}
            <PullToRefresh id="mobile-planning-scroll-container" className="block md:hidden flex-1" style={{ background: 'var(--kl-bg, #F1F5F9)' }}>
                {activeLookaheadId ? (
                    // VISTA DETALLE LOOKAHEAD
                    <div style={{ paddingBottom: '80px' }}>
                        <div style={{ background: '#2563EB', color: '#fff', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <button
                                type="button"
                                onClick={() => setActiveLookaheadId(null)}
                                style={{ border: 0, background: 'none', color: '#fff', padding: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                aria-label="Volver a lista"
                            >
                                <svg className="i" viewBox="0 0 24 24" style={{ width: '22px', height: '22px' }}><path d="M15 18l-6-6 6-6" /></svg>
                            </button>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '15px', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {`Planificación #${getLookaheadPlanNumber(activeLookahead?.id)}`}
                                </div>
                                <div style={{ fontSize: '11px', opacity: 0.9 }}>
                                    {formatDateToShow(activeLookahead?.startDate)} al {formatDateToShow(activeLookahead?.endDate)}
                                </div>
                            </div>
                            <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 8px', borderRadius: '10px', background: 'rgba(255,255,255,0.2)', whiteSpace: 'nowrap' }}>
                                {activeLookahead?.status}
                            </span>
                        </div>
                        <div className="p-4 space-y-4">
                            <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
                                <button
                                    onClick={() => setMobileFilterITE('all')}
                                    className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-colors ${mobileFilterITE === 'all' ? 'bg-white dark:bg-slate-700 shadow text-slate-900 dark:text-white flex items-center justify-center gap-1' : 'text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700/50 flex items-center justify-center gap-1'}`}
                                >
                                    <span className="material-symbols-outlined text-[14px]">public</span> Global
                                </button>
                                <button
                                    onClick={() => setMobileFilterITE('mine')}
                                    className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-colors ${mobileFilterITE === 'mine' ? 'bg-primary text-white shadow flex items-center justify-center gap-1' : 'text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700/50 flex items-center justify-center gap-1'}`}
                                >
                                    <span className="material-symbols-outlined text-[14px]">person</span> Mis Restricciones
                                </button>
                            </div>
                            {(() => {
                                if (!activeLookahead) return null;
                                let tasksInWindow = getTasksForLookahead(activeLookahead.startDate, activeLookahead.endDate, activeLookahead.constraints);
                                if (tasksInWindow.length === 0) {
                                    return <div className="text-center text-slate-400 italic py-8 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">No hay tareas programadas para esta ventana.</div>;
                                }
                                let mobileConstraints = tasksInWindow.flatMap(task => {
                                    const constraints = activeLookahead.constraints.filter(c => c.taskId === task.id);
                                    return constraints.map(c => ({ task, constraint: c }));
                                });
                                if (mobileFilterITE === 'mine' && currentUser) {
                                    mobileConstraints = mobileConstraints.filter(mc => {
                                        const resp = responsibles.find(r => r.id === mc.constraint.responsibleId);
                                        return resp && resp.email === currentUser.email;
                                    });
                                }
                                if (mobileConstraints.length === 0) {
                                    return <div className="text-center text-slate-400 italic py-8 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">No hay restricciones vigentes.</div>;
                                }
                                return mobileConstraints.map(({ task, constraint: c }) => {
                                    let typeName = 'General';
                                    [...constraintGroups, ...(globalConstraintGroups || [])].forEach(g => {
                                        const def = g.definitions?.find(d => d.id === c.typeId);
                                        if (def) typeName = def.name;
                                    });
                                    const respName = responsibles.find(r => r.id === c.responsibleId)?.name || 'Sin asignar';
                                    const levelName = levels.find(l => l.id === (task as any)?.levelId)?.name || 'General';
                                    const isLiberado = c.status === 'Liberado';
                                    const isNoLiberado = c.status === 'NO LIBERADO';
                                    const statusClass = isLiberado ? 'st-done' : isNoLiberado ? 'st-fail' : 'st-pending';
                                    const displayStatus = c.status || 'Pendiente';

                                    const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager' || currentUser?.globalRole === 'Administrador';
                                    const isAssigned = responsibles.find(r => r.id === c.responsibleId)?.email === currentUser?.email;
                                    const canEditConstraint = activeLookahead.status !== 'Cerrada' && (isAdmin || isAssigned);
                                    const ganttNumber = getTaskGanttRowNumber(task, tasks);
                                    const hasProtocol = !!protocolConfigs[task.id]?.applies;

                                    return (
                                        <div key={c.id} className={`kl-task-card ${statusClass}`} style={{ padding: '10px 12px', marginBottom: '8px' }}>
                                            {/* Línea 1: nombre de la tarea a 14px/700 (hasta 2 líneas). A la derecha, recuadro gris con tipo de restricción real */}
                                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                                                <div style={{
                                                    fontSize: '14px',
                                                    fontWeight: 700,
                                                    color: '#0F172A',
                                                    lineHeight: '1.3',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    flex: 1
                                                }}>
                                                    {cleanTaskName(task.name)}
                                                </div>
                                                <span style={{
                                                    fontSize: '11px',
                                                    fontWeight: 700,
                                                    background: '#F1F5F9',
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: '6px',
                                                    padding: '2px 8px',
                                                    color: '#334155',
                                                    whiteSpace: 'nowrap',
                                                    flexShrink: 0
                                                }}>
                                                    {typeName}
                                                </span>
                                            </div>

                                            {/* Línea 2: Responsable a la izquierda y Fecha a la derecha */}
                                            <div style={{
                                                fontSize: '12px',
                                                color: '#64748B',
                                                marginTop: '2px',
                                                marginBottom: '6px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: '8px'
                                            }}>
                                                <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {respName}
                                                </span>
                                                <span style={{ fontSize: '11px', flexShrink: 0 }}>
                                                    {c.requiredDate ? formatDateToShow(c.requiredDate) : (c.requiredDate || 'Sin fecha')}
                                                </span>
                                            </div>

                                            {/* Línea 3, de izquierda a derecha y con 6px de separación */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                {/* 1. A la izquierda, botón Protocolo */}
                                                <button
                                                    type="button"
                                                    className="kl-proto-btn"
                                                    onClick={() => {
                                                        if (hasProtocol) {
                                                            setMobileProtocolTarget({
                                                                taskId: task.id,
                                                                unitId: '',
                                                                taskName: task.name,
                                                                taskCode: ganttNumber ? String(ganttNumber) : undefined
                                                            });
                                                        } else {
                                                            alert('Esta partida no tiene protocolo de calidad configurado.');
                                                        }
                                                    }}
                                                    style={{
                                                        opacity: hasProtocol ? 1 : 0.6
                                                    }}
                                                >
                                                    <span className="material-symbols-outlined" style={{ fontSize: '15px' }}>fact_check</span>
                                                    <span>Protocolo</span>
                                                </button>

                                                {/* 2. Botón ⋮ de 28x28 (área táctil de 40x40), borde #E2E8F0, radio 8 */}
                                                <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', margin: '0 -6px' }}>
                                                    <button
                                                        type="button"
                                                        style={{
                                                            width: '28px',
                                                            height: '28px',
                                                            borderRadius: '8px',
                                                            border: '1px solid #E2E8F0',
                                                            background: '#ffffff',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: 'pointer',
                                                            padding: 0
                                                        }}
                                                        aria-label="Acciones de restricción"
                                                        onClick={(e) => {
                                                            setMobileCardActionTarget({ task, constraint: c, respName, typeName, levelName, ganttNumber });
                                                            setMobileCardActionAnchor(e.currentTarget);
                                                        }}
                                                    >
                                                        <span className="material-symbols-outlined" style={{ fontSize: '18px', color: '#64748B' }}>more_vert</span>
                                                    </button>
                                                </div>

                                                {/* 3. ID correlativo de la fila de la carta Gantt (#65) - Oculto si no tiene número */}
                                                {ganttNumber ? (
                                                    <span style={{
                                                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                                        fontSize: '11px',
                                                        fontWeight: 700,
                                                        color: '#64748B',
                                                        background: '#F8FAFC',
                                                        border: '1px solid #E2E8F0',
                                                        borderRadius: '6px',
                                                        padding: '2px 6px',
                                                        lineHeight: '1.4',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        #{ganttNumber}
                                                    </span>
                                                ) : null}

                                                {/* 4. A la derecha, Insignia de estado ▾ */}
                                                <div style={{ position: 'relative', marginLeft: 'auto' }}>
                                                    <div
                                                        className="kl-st-badge"
                                                        onClick={(e) => {
                                                            if (canEditConstraint) {
                                                                setMobileActiveConstraintMenuId(c.id);
                                                                setMobileActiveConstraintMenuAnchor(e.currentTarget);
                                                            } else {
                                                                alert('No tienes permisos para modificar el estado de esta restricción o el plan está cerrado.');
                                                            }
                                                        }}
                                                    >
                                                        <span>{displayStatus}</span>
                                                        <svg viewBox="0 0 24 24" style={{ width: '12px', height: '12px', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5 }}>
                                                            <path d="M6 9l6 6 6-6" />
                                                        </svg>
                                                    </div>

                                                    <KlMenu
                                                        open={mobileActiveConstraintMenuId === c.id}
                                                        onClose={() => setMobileActiveConstraintMenuId(null)}
                                                        anchorRef={{ current: mobileActiveConstraintMenuAnchor }}
                                                        items={[
                                                            {
                                                                id: 'pendiente',
                                                                label: 'Pendiente',
                                                                iconName: 'schedule',
                                                                iconColor: '#D97706',
                                                                onClick: () => {
                                                                    setMobileActiveConstraintMenuId(null);
                                                                    handleUpdateConstraint(activeLookahead.id, c.id, 'status', 'Pendiente');
                                                                    handleUpdateConstraint(activeLookahead.id, c.id, 'observations', '');
                                                                }
                                                            },
                                                            {
                                                                id: 'liberado',
                                                                label: 'Liberado',
                                                                iconName: 'check_circle',
                                                                iconColor: '#16A34A',
                                                                onClick: () => {
                                                                    setMobileActiveConstraintMenuId(null);
                                                                    setConstraintObsData({
                                                                        planId: activeLookahead.id,
                                                                        constraintId: c.id,
                                                                        currentStatus: 'Liberado',
                                                                        observations: c.observations || '',
                                                                        description: c.description
                                                                    });
                                                                    setConstraintObsModalOpen(true);
                                                                }
                                                            },
                                                            {
                                                                id: 'no_liberado',
                                                                label: 'NO LIBERADO',
                                                                iconName: 'cancel',
                                                                iconColor: '#DC2626',
                                                                onClick: () => {
                                                                    setMobileActiveConstraintMenuId(null);
                                                                    setConstraintObsData({
                                                                        planId: activeLookahead.id,
                                                                        constraintId: c.id,
                                                                        currentStatus: 'NO LIBERADO',
                                                                        observations: c.observations || '',
                                                                        description: c.description
                                                                    });
                                                                    setConstraintObsModalOpen(true);
                                                                }
                                                            }
                                                        ]}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                    </div>
                ) : activePlanId ? (
                    // VISTA DETALLE SEMANAL
                    <div style={{ paddingBottom: '80px' }}>
                        {/* 1. Header barra azul con botón volver y título */}
                        <div style={{ background: '#2563EB', color: '#fff', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <button
                                type="button"
                                onClick={() => {
                                    setActivePlanId(null);
                                    setWeeklyMobileFilters(initialWeeklyMobileFilters);
                                }}
                                style={{ border: 0, background: 'none', color: '#fff', padding: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                aria-label="Volver a lista"
                            >
                                <svg className="i" viewBox="0 0 24 24" style={{ width: '22px', height: '22px' }}><path d="M15 18l-6-6 6-6" /></svg>
                            </button>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '15px', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    Planificación Semanal #{activePlan?.id || ''}
                                </div>
                                <div style={{ fontSize: '11px', opacity: 0.9 }}>
                                    {formatMobilePlanDateRange(activePlan?.startDate, activePlan?.endDate)}
                                </div>
                            </div>
                            <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 8px', borderRadius: '10px', background: 'rgba(255,255,255,0.2)', whiteSpace: 'nowrap' }}>
                                {activePlan?.status}
                            </span>
                            <button
                                type="button"
                                onClick={() => {
                                    window.dispatchEvent(new CustomEvent('klyd_sync_refresh'));
                                }}
                                style={{ border: 0, background: 'rgba(255,255,255,0.2)', color: '#fff', padding: '4px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                title="Sincronizar ahora con la nube"
                                aria-label="Sincronizar ahora"
                            >
                                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>sync</span>
                            </button>
                        </div>

                        {/* 2. Barra de Herramientas: Pestañas Global / Mis Tareas + Botón Filtro */}
                        <div style={{ padding: '8px 12px 10px', background: '#fff', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #E2E8F0' }}>
                            {/* Switcher Global / Mis Tareas */}
                            <div style={{ flex: 1, display: 'flex', background: '#F1F5F9', borderRadius: '10px', padding: '3px' }}>
                                <button
                                    type="button"
                                    onClick={() => setMobileFilterWeekly('all')}
                                    style={{
                                        flex: 1,
                                        height: '32px',
                                        border: 0,
                                        borderRadius: '8px',
                                        background: mobileFilterWeekly === 'all' ? '#ffffff' : 'none',
                                        boxShadow: mobileFilterWeekly === 'all' ? '0 1px 3px rgba(0,0,0,.08)' : 'none',
                                        fontSize: '12px',
                                        fontWeight: mobileFilterWeekly === 'all' ? 700 : 500,
                                        color: mobileFilterWeekly === 'all' ? '#0F172A' : '#64748B',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <span className="material-symbols-outlined" style={{ fontSize: '15px' }}>public</span>
                                    <span>Global</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setMobileFilterWeekly('mine')}
                                    style={{
                                        flex: 1,
                                        height: '32px',
                                        border: 0,
                                        borderRadius: '8px',
                                        background: mobileFilterWeekly === 'mine' ? '#2563EB' : 'none',
                                        boxShadow: mobileFilterWeekly === 'mine' ? '0 1px 3px rgba(37,99,235,.25)' : 'none',
                                        fontSize: '12px',
                                        fontWeight: mobileFilterWeekly === 'mine' ? 700 : 500,
                                        color: mobileFilterWeekly === 'mine' ? '#ffffff' : '#64748B',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <span className="material-symbols-outlined" style={{ fontSize: '15px' }}>person</span>
                                    <span>Mis Tareas</span>
                                </button>
                            </div>

                            {/* Botón Filtro */}
                            {(() => {
                                const activeCount = (
                                    weeklyMobileFilters.levels.length +
                                    weeklyMobileFilters.units.length +
                                    weeklyMobileFilters.responsibles.length +
                                    weeklyMobileFilters.tasks.length +
                                    weeklyMobileFilters.statuses.length
                                );

                                return (
                                    <button
                                        type="button"
                                        aria-label="Filtrar"
                                        onClick={() => {
                                            setDraftWeeklyMobileFilters(weeklyMobileFilters);
                                            setIsWeeklyFilterSheetOpen(true);
                                        }}
                                        style={{
                                            position: 'relative',
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '10px',
                                            border: activeCount > 0 ? '1.5px solid #2563EB' : '1px solid #CBD5E1',
                                            background: activeCount > 0 ? '#EFF6FF' : '#ffffff',
                                            color: activeCount > 0 ? '#2563EB' : '#475569',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <svg viewBox="0 0 24 24" style={{ width: '18px', height: '18px', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }}>
                                            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                                        </svg>
                                        {activeCount > 0 && (
                                            <span style={{
                                                position: 'absolute',
                                                top: '-4px',
                                                right: '-4px',
                                                minWidth: '18px',
                                                height: '18px',
                                                borderRadius: '9px',
                                                background: '#2563EB',
                                                color: '#fff',
                                                fontSize: '10px',
                                                fontWeight: 700,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: '0 4px',
                                                boxShadow: '0 1px 2px rgba(0,0,0,.2)'
                                            }}>
                                                {activeCount}
                                            </span>
                                        )}
                                    </button>
                                );
                            })()}
                        </div>

                        {/* 3. Lista de Tarjetas */}
                        <div className="p-3">
                            {(() => {
                                const allRawAllocs = activePlan?.allocations || [];
                                if (allRawAllocs.length === 0) {
                                    return (
                                        <div style={{ background: '#fff', borderRadius: '12px', padding: '32px 16px', textAlign: 'center', color: '#64748B', fontSize: '13px', boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>
                                            No hay tareas asignadas en esta planificación semanal.
                                        </div>
                                    );
                                }

                                // Filtros aplicados
                                let filtered = allRawAllocs
                                    .map((alloc, idx) => ({ alloc, idx }))
                                    .filter(({ alloc }) => !!alloc);

                                if (mobileFilterWeekly === 'mine' && currentUser) {
                                    filtered = filtered.filter(({ alloc }) => {
                                        const resp = responsibles.find(r => r.id === alloc.responsibleId);
                                        return resp && resp.email === currentUser.email;
                                    });
                                }

                                if (weeklyMobileFilters.levels.length > 0) {
                                    filtered = filtered.filter(({ alloc }) => {
                                        const task = findTaskById(alloc.taskId, tasks);
                                        const lvl = levels.find(l => l.id === (task as any)?.levelId)?.name || 'General';
                                        return weeklyMobileFilters.levels.includes(lvl);
                                    });
                                }

                                if (weeklyMobileFilters.units.length > 0) {
                                    filtered = filtered.filter(({ alloc }) => {
                                        const unit = units.find(u => u.id === alloc.unitId);
                                        const isRepeated = unit && units.filter(u2 => u2.name && u2.name.trim().toLowerCase() === unit.name.trim().toLowerCase()).length > 1;
                                        const uDisplayName = isRepeated ? `${unit.name} · ${levels.find(l => l.id === unit.levelId)?.name || 'General'}` : (unit?.name || alloc.unitId);
                                        return weeklyMobileFilters.units.includes(uDisplayName) ||
                                            (unit && weeklyMobileFilters.units.includes(unit.name)) ||
                                            weeklyMobileFilters.units.includes(alloc.unitId);
                                    });
                                }

                                if (weeklyMobileFilters.responsibles.length > 0) {
                                    filtered = filtered.filter(({ alloc }) => {
                                        const resp = responsibles.find(r => r.id === alloc.responsibleId);
                                        const rName = resp?.name || 'Sin asignar';
                                        return weeklyMobileFilters.responsibles.includes(rName);
                                    });
                                }

                                if (weeklyMobileFilters.tasks.length > 0) {
                                    filtered = filtered.filter(({ alloc }) => {
                                        const task = findTaskById(alloc.taskId, tasks);
                                        const rowNum = getTaskGanttRowNumber(task, tasks);
                                        const rawName = cleanTaskName(task?.name || (alloc.isAdditional ? (alloc.additionalTaskName || 'Tarea Adicional') : ''));
                                        const taskLabel = rowNum ? `#${rowNum} ${rawName}`.trim() : (rawName || 'Tarea');
                                        return weeklyMobileFilters.tasks.includes(taskLabel) || (rawName && weeklyMobileFilters.tasks.includes(rawName));
                                    });
                                }

                                if (weeklyMobileFilters.statuses.length > 0) {
                                    filtered = filtered.filter(({ alloc }) => {
                                        const st = alloc.executed === true ? 'Cumplida' : alloc.executed === false ? 'No cumplida' : 'Pendiente';
                                        return weeklyMobileFilters.statuses.includes(st);
                                    });
                                }

                                if (filtered.length === 0) {
                                    return (
                                        <div style={{ background: '#fff', borderRadius: '14px', padding: '36px 16px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>
                                            <div style={{ fontSize: '15px', fontWeight: 700, color: '#0F172A' }}>No hay tareas con estos filtros</div>
                                            <div style={{ fontSize: '12px', color: '#64748B', marginTop: '4px' }}>Intenta ajustar o limpiar los filtros seleccionados.</div>
                                            <button
                                                type="button"
                                                onClick={() => setWeeklyMobileFilters(initialWeeklyMobileFilters)}
                                                className="b b-o"
                                                style={{ margin: '16px auto 0', height: '36px', fontSize: '12px', padding: '0 16px' }}
                                            >
                                                Limpiar filtros
                                            </button>
                                        </div>
                                    );
                                }

                                return filtered.map(({ alloc, idx }) => {
                                    const task = findTaskById(alloc.taskId, tasks);
                                    const unit = units.find(u => u.id === alloc.unitId);
                                    const config = !alloc.isAdditional ? protocolConfigs[alloc.taskId] : null;
                                    const appliesProtocol = config?.applies ?? false;
                                    const isProtocolClosed = !alloc.isAdditional && appliesProtocol ? isProtocolProgressedLocal(alloc.taskId, alloc.unitId) : false;

                                    const isCumplida = alloc.executed === true;
                                    const isNoCumplida = alloc.executed === false;
                                    const isPendiente = alloc.executed === null || alloc.executed === undefined;
                                    const statusClass = isCumplida ? 'st-done' : isNoCumplida ? 'st-fail' : 'st-pending';
                                    const displayStatus = isCumplida ? 'Cumplida' : isNoCumplida ? 'No cumplida' : 'Pendiente';

                                    const ganttNumber = getTaskGanttRowNumber(task, tasks);
                                    const taskDisplayName = cleanTaskName(task?.name || (alloc.isAdditional ? (alloc.additionalTaskName || 'Tarea Adicional') : 'Tarea'));
                                    const unitLevelName = levels.find(l => l.id === unit?.levelId)?.name;
                                    const taskLevelName = levels.find(l => l.id === (task as any)?.levelId)?.name;
                                    const levelName = unitLevelName || taskLevelName || 'General';
                                    const unitName = unit?.name || alloc.unitId;
                                    const respName = responsibles.find(r => r.id === alloc.responsibleId)?.name || 'Sin asignar';

                                    const activeDays = dynamicDayColumns
                                        .filter(col => alloc.days?.[col.dayLetter] || alloc.days?.[col.fullDate])
                                        .map(col => col.dayLetter);

                                    const rawComprometido = (alloc as any).pctComprometido ?? ((alloc as any).comprometido ? ((alloc as any).customPercentComprometido ?? 100) : (alloc.percentComprometido ?? 0));
                                    const pctComprometido = Number(Number(rawComprometido).toFixed(2));
                                    const accumulatedCompliance = !alloc.isAdditional
                                        ? getTaskUnitAccumulatedCompliance(alloc.taskId, alloc.unitId, tasks, weeklyPlans, protocolConfigs, savedProtocols, undefined, activePlan?.id)
                                        : 0;

                                    const canEditWeekly = (activePlan?.status === 'En Ejecución' || activePlan?.status === 'En Revisión' || activePlan?.status === 'En Planificación') &&
                                        (currentUser?.role === 'admin' || currentUser?.role === 'manager' || currentUser?.globalRole === 'Administrador' || hasPermission('ps_gantt_avance', 'edit') || hasPermission('movil_ps_gantt_avance', 'edit') || responsibles.find(r => r.id === alloc.responsibleId)?.email === currentUser?.email);

                                    const itemKey = `${alloc.taskId}_${alloc.unitId}`;

                                    return (
                                        <div key={itemKey} className={`kl-task-card ${statusClass}`} style={{ padding: '10px 12px', marginBottom: '8px' }}>
                                            {/* Línea 1: nombre de la tarea y días programados (L, M, X, J, V, S, D). A la derecha, etiqueta de la unidad física */}
                                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
                                                        <span style={{
                                                            fontSize: '14px',
                                                            fontWeight: 700,
                                                            color: '#0F172A',
                                                            lineHeight: '1.3'
                                                        }}>
                                                            {taskDisplayName}
                                                        </span>
                                                        {dynamicDayColumns && dynamicDayColumns.length > 0 && (
                                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', flexWrap: 'wrap' }}>
                                                                {dynamicDayColumns.map((col, dIdx) => {
                                                                    const isChecked = !!(alloc.days?.[col.dayLetter] || alloc.days?.[col.fullDate]);
                                                                    return (
                                                                        <span
                                                                            key={col.key || dIdx}
                                                                            title={`${col.dayLetter} (${col.dateNum}) - ${isChecked ? 'Programado' : 'No programado'}`}
                                                                            style={{
                                                                                fontSize: '10px',
                                                                                fontWeight: isChecked ? 800 : 600,
                                                                                lineHeight: 1,
                                                                                background: isChecked ? '#2563EB' : '#F1F5F9',
                                                                                border: isChecked ? '1px solid #1D4ED8' : '1px solid #E2E8F0',
                                                                                color: isChecked ? '#FFFFFF' : '#94A3B8',
                                                                                borderRadius: '4px',
                                                                                padding: '2px 4px',
                                                                                minWidth: '16px',
                                                                                textAlign: 'center',
                                                                                display: 'inline-block'
                                                                            }}
                                                                        >
                                                                            {col.dayLetter}
                                                                        </span>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <span style={{
                                                    fontSize: '11px',
                                                    fontWeight: 700,
                                                    background: '#F1F5F9',
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: '6px',
                                                    padding: '2px 8px',
                                                    color: '#334155',
                                                    whiteSpace: 'nowrap',
                                                    flexShrink: 0
                                                }}>
                                                    {unitName}
                                                </span>
                                            </div>

                                            {/* Línea 2: Nivel · Responsable (sin repetir la unidad física que ya está arriba a la derecha) */}
                                            <div style={{
                                                fontSize: '12px',
                                                color: '#64748B',
                                                marginTop: '4px',
                                                marginBottom: '6px',
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                alignItems: 'center',
                                                gap: '4px',
                                                lineHeight: '1.4'
                                            }}>
                                                <span style={{ fontWeight: 600, color: '#334155' }}>{levelName}</span>
                                                <span style={{ color: '#94A3B8' }}>·</span>
                                                <span style={{ fontWeight: 500 }}>{respName}</span>
                                            </div>

                                            {/* Línea 3, de izquierda a derecha y con 6px de separación: */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                {/* 1. A la izquierda, el botón Protocolo (tonal) */}
                                                <button
                                                    type="button"
                                                    className="kl-proto-btn"
                                                    onClick={() => {
                                                        if (appliesProtocol) {
                                                            setMobileProtocolTarget({
                                                                taskId: alloc.taskId,
                                                                unitId: alloc.unitId,
                                                                taskName: taskDisplayName,
                                                                taskCode: ganttNumber ? String(ganttNumber) : undefined
                                                            });
                                                        } else {
                                                            alert('Esta partida no tiene protocolo de calidad configurado.');
                                                        }
                                                    }}
                                                    style={{
                                                        opacity: appliesProtocol ? 1 : 0.6
                                                    }}
                                                >
                                                    <span className="material-symbols-outlined" style={{ fontSize: '15px' }}>
                                                        {isProtocolClosed ? 'verified' : 'fact_check'}
                                                    </span>
                                                    <span>Protocolo</span>
                                                </button>

                                                {/* 2. Botón ⋮ de 28x28 (área táctil de 40x40), borde #E2E8F0, radio 8 */}
                                                <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', margin: '0 -6px' }}>
                                                    <button
                                                        type="button"
                                                        style={{
                                                            width: '28px',
                                                            height: '28px',
                                                            borderRadius: '8px',
                                                            border: '1px solid #E2E8F0',
                                                            background: '#ffffff',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: 'pointer',
                                                            padding: 0
                                                        }}
                                                        aria-label="Acciones de tarea"
                                                        onClick={(e) => {
                                                            setMobileWeeklyCardActionTarget({ alloc, allocIndex: idx, task, unit, levelName, respName });
                                                            setMobileWeeklyCardActionAnchor(e.currentTarget);
                                                        }}
                                                    >
                                                        <span className="material-symbols-outlined" style={{ fontSize: '18px', color: '#64748B' }}>more_vert</span>
                                                    </button>
                                                </div>

                                                {/* 3. ID correlativo de la fila de la carta Gantt (#65) - Oculto si no tiene número */}
                                                {ganttNumber ? (
                                                    <span style={{
                                                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                                        fontSize: '11px',
                                                        fontWeight: 700,
                                                        color: '#64748B',
                                                        background: '#F8FAFC',
                                                        border: '1px solid #E2E8F0',
                                                        borderRadius: '6px',
                                                        padding: '2px 6px',
                                                        lineHeight: '1.4',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        #{ganttNumber}
                                                    </span>
                                                ) : null}

                                                {/* 4. A la derecha, Insignia de estado ▾ */}
                                                <div style={{ position: 'relative', marginLeft: 'auto' }}>
                                                    <div
                                                        className="kl-st-badge"
                                                        onClick={(e) => {
                                                            if (canEditWeekly) {
                                                                setMobileActiveWeeklyMenuKey(itemKey);
                                                                setMobileActiveWeeklyMenuAnchor(e.currentTarget);
                                                            } else {
                                                                alert('No tienes permisos para modificar el estado o la planificación semanal está cerrada.');
                                                            }
                                                        }}
                                                    >
                                                        <span>{displayStatus}</span>
                                                        <svg viewBox="0 0 24 24" style={{ width: '12px', height: '12px', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5 }}>
                                                            <path d="M6 9l6 6 6-6" />
                                                        </svg>
                                                    </div>

                                                    <KlMenu
                                                        open={mobileActiveWeeklyMenuKey === itemKey}
                                                        onClose={() => setMobileActiveWeeklyMenuKey(null)}
                                                        anchorRef={{ current: mobileActiveWeeklyMenuAnchor }}
                                                        items={[
                                                            {
                                                                id: 'pendiente',
                                                                label: 'Pendiente',
                                                                iconName: 'schedule',
                                                                iconColor: '#D97706',
                                                                onClick: () => {
                                                                    setMobileActiveWeeklyMenuKey(null);
                                                                    updateMainAllocationField(activePlan.id, idx, 'executed', null);
                                                                    updateMainAllocationField(activePlan.id, idx, 'percentCumplimiento', 0);
                                                                }
                                                            },
                                                            {
                                                                id: 'cumplida',
                                                                label: 'Cumplida (Ejecutado)',
                                                                iconName: 'check_circle',
                                                                iconColor: '#16A34A',
                                                                onClick: () => {
                                                                    setMobileActiveWeeklyMenuKey(null);
                                                                    if (appliesProtocol && (accumulatedCompliance + pctComprometido >= 99.9) && !isProtocolClosed) {
                                                                        setMobileQualityBlockedModalData({ alloc, task: task! });
                                                                    } else {
                                                                        updateMainAllocationField(activePlan.id, idx, 'executed', true);
                                                                        updateMainAllocationField(activePlan.id, idx, 'percentCumplimiento', 100);
                                                                    }
                                                                }
                                                            },
                                                            {
                                                                id: 'no_cumplida',
                                                                label: 'No cumplida (No Ejecutado)',
                                                                iconName: 'cancel',
                                                                iconColor: '#DC2626',
                                                                onClick: () => {
                                                                    setMobileActiveWeeklyMenuKey(null);
                                                                    setMobileUnexecutedModalData({
                                                                        allocIndex: idx,
                                                                        alloc,
                                                                        cncId: alloc.cncId || '',
                                                                        comentario: alloc.comentario || '',
                                                                        pctCumplimiento: alloc.percentCumplimiento ?? 0,
                                                                        pctStr: String(alloc.percentCumplimiento ?? 0),
                                                                        taskName: cleanTaskName(task?.name || alloc.taskId)
                                                                    });
                                                                }
                                                            }
                                                        ]}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                    </div>
                ) : (
                    // VISTA MAESTRA LISTA DE PLANES (02-plan-intermedia.html / 03-plan-semanal.html)
                    <>
                        {/* 1. MobileTitleBar */}
                        <MobileTitleBar
                            title="Planificación"
                            projectName={projectInfo.name}
                            projectCode={projectInfo.code}
                            delayDays={projectStatus.delay}
                            delayStatusText={projectStatus.delay > 0 ? `Atrasado · ${projectStatus.delay}d` : (projectStatus.label || 'Al día')}
                        />

                        {/* 2. Switcher de pestañas y Chips de Filtro */}
                        <div style={{ background: '#fff', padding: '8px 12px 10px', boxShadow: '0 2px 4px rgba(0,0,0,.08)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {/* Switcher Intermedia / Semanal */}
                            <div style={{ display: 'flex', background: '#E2E8F0', borderRadius: '10px', padding: '3px', height: '36px', boxSizing: 'border-box' }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setActiveTab('intermedia');
                                        setActiveLookaheadId(null);
                                        setActivePlanId(null);
                                    }}
                                    style={{
                                        flex: 1,
                                        border: 0,
                                        borderRadius: '8px',
                                        background: activeTab === 'intermedia' ? '#fff' : 'none',
                                        fontSize: '13px',
                                        fontWeight: activeTab === 'intermedia' ? 700 : 600,
                                        color: activeTab === 'intermedia' ? '#1D4ED8' : '#475569',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Intermedia
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setActiveTab('semanal');
                                        setActiveLookaheadId(null);
                                        setActivePlanId(null);
                                    }}
                                    style={{
                                        flex: 1,
                                        border: 0,
                                        borderRadius: '8px',
                                        background: activeTab === 'semanal' ? '#fff' : 'none',
                                        fontSize: '13px',
                                        fontWeight: activeTab === 'semanal' ? 700 : 600,
                                        color: activeTab === 'semanal' ? '#1D4ED8' : '#475569',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Semanal
                                </button>
                            </div>

                            {/* Chips de filtro por estado */}
                            <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }} className="no-scrollbar">
                                {(['Todas', 'En ejecución', 'En revisión', 'En planificación', 'Cerrado'] as const).map(chip => {
                                    const isSelected = mobilePlanFilter === chip;
                                    return (
                                        <button
                                            key={chip}
                                            type="button"
                                            onClick={() => setMobilePlanFilter(chip)}
                                            style={{
                                                height: '30px',
                                                padding: '0 12px',
                                                borderRadius: '15px',
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                whiteSpace: 'nowrap',
                                                border: isSelected ? '1px solid #2563EB' : '1px solid #CBD5E1',
                                                background: isSelected ? '#2563EB' : '#fff',
                                                color: isSelected ? '#fff' : '#475569',
                                                cursor: 'pointer',
                                                flexShrink: 0
                                            }}
                                        >
                                            {chip}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* 3. Listado de tarjetas */}
                        <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {activeTab === 'intermedia' ? (
                                // VISTA INTERMEDIA (02-plan-intermedia.html)
                                filteredLookaheadPlans.length === 0 ? (
                                    <div style={{ background: '#fff', borderRadius: '12px', padding: '32px 16px', textAlign: 'center', color: '#64748B', fontSize: '13px', boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>
                                        No hay planificaciones intermedias {mobilePlanFilter !== 'Todas' ? `con estado "${mobilePlanFilter}"` : 'registradas'}.
                                    </div>
                                ) : (
                                    filteredLookaheadPlans.map((plan) => {
                                        const planIdx = getLookaheadPlanNumber(plan.id);
                                        const pendingCount = (plan.constraints || []).filter(c => c.status === 'Pendiente' || c.status === 'NO LIBERADO').length;
                                        const badge = getMobileStatusBadge(plan.status);
                                        const isHighlighted = highlightedPlanId === String(plan.id);

                                        return (
                                            <div
                                                key={plan.id}
                                                id={`plan-card-intermedia-${plan.id}`}
                                                onClick={() => {
                                                    recordPlanScroll('intermedia', plan.id);
                                                    setLookaheadPlansBackup(JSON.parse(JSON.stringify(lookaheadPlans)));
                                                    setActiveLookaheadId(plan.id);
                                                }}
                                                style={{
                                                    background: isHighlighted ? '#EFF6FF' : '#fff',
                                                    transition: 'background-color 1.2s ease',
                                                    borderRadius: '12px',
                                                    boxShadow: isHighlighted ? '0 0 0 2px #3B82F6, 0 2px 6px rgba(0,0,0,.1)' : '0 1px 3px rgba(0,0,0,.08)',
                                                    height: '58px',
                                                    boxSizing: 'border-box',
                                                    padding: '0 4px 0 12px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#6D28D9' }}>
                                                        Planificación #{planIdx}
                                                    </div>
                                                    <div style={{ fontSize: '11px', color: '#64748B', marginTop: '3px' }}>
                                                        {pendingCount === 0 ? (
                                                            <span style={{ color: '#047857', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                <svg className="i" viewBox="0 0 24 24" style={{ width: '14px', height: '14px' }}>
                                                                    <circle cx="12" cy="12" r="9" />
                                                                    <path d="M8 12l3 3 5-5" />
                                                                </svg>
                                                                Liberadas
                                                            </span>
                                                        ) : (
                                                            <span style={{ color: '#B45309', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                <svg className="i" viewBox="0 0 24 24" style={{ width: '14px', height: '14px' }}>
                                                                    <path d="M12 3l10 18H2z" />
                                                                    <path d="M12 10v5M12 18v.5" />
                                                                </svg>
                                                                {pendingCount} Pendientes
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                <span
                                                    style={{
                                                        background: badge.bg,
                                                        color: badge.color,
                                                        fontSize: '11px',
                                                        fontWeight: 700,
                                                        padding: '3px 8px',
                                                        borderRadius: '10px',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {badge.label}
                                                </span>

                                                <button
                                                    type="button"
                                                    className="ib"
                                                    aria-label="Ver o editar"
                                                    style={{ width: '40px', height: '40px', color: '#64748B' }}
                                                >
                                                    <svg className="i" viewBox="0 0 24 24"><path d="M9 6l6 6-6 6" /></svg>
                                                </button>
                                            </div>
                                        );
                                    })
                                )
                            ) : (
                                // VISTA SEMANAL (03-plan-semanal.html)
                                filteredWeeklyPlans.length === 0 ? (
                                    <div style={{ background: '#fff', borderRadius: '12px', padding: '32px 16px', textAlign: 'center', color: '#64748B', fontSize: '13px', boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>
                                        No hay planificaciones semanales {mobilePlanFilter !== 'Todas' ? `con estado "${mobilePlanFilter}"` : 'registradas'}.
                                    </div>
                                ) : (
                                    filteredWeeklyPlans.map((plan) => {
                                        const dateRangeStr = formatMobilePlanDateRange(plan.startDate, plan.endDate);
                                        const badge = getMobileStatusBadge(plan.status);
                                        const isHighlighted = highlightedPlanId === String(plan.id);

                                        return (
                                            <div
                                                key={plan.id}
                                                id={`plan-card-semanal-${plan.id}`}
                                                onClick={() => {
                                                    recordPlanScroll('semanal', plan.id);
                                                    setWeeklyPlansBackup(JSON.parse(JSON.stringify(weeklyPlans)));
                                                    setActivePlanId(plan.id);
                                                    setIsWeeklyEditing(plan.status !== 'Cerrada');
                                                }}
                                                style={{
                                                    background: isHighlighted ? '#EFF6FF' : '#fff',
                                                    transition: 'background-color 1.2s ease',
                                                    borderRadius: '12px',
                                                    boxShadow: isHighlighted ? '0 0 0 2px #3B82F6, 0 2px 6px rgba(0,0,0,.1)' : '0 1px 3px rgba(0,0,0,.08)',
                                                    height: '58px',
                                                    boxSizing: 'border-box',
                                                    padding: '0 4px 0 12px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#1D4ED8' }}>
                                                        Planificación #{plan.id}
                                                    </div>
                                                    <div style={{ fontSize: '11px', color: '#64748B', marginTop: '3px' }}>
                                                        {dateRangeStr}
                                                    </div>
                                                </div>

                                                <span
                                                    style={{
                                                        background: badge.bg,
                                                        color: badge.color,
                                                        fontSize: '11px',
                                                        fontWeight: 700,
                                                        padding: '3px 8px',
                                                        borderRadius: '10px',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {badge.label}
                                                </span>

                                                {/* Botón: Asignar / Registro de Asistencia (personas con check) */}
                                                <button
                                                    type="button"
                                                    className="ib"
                                                    aria-label="Asignar"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setAttendanceModalPlan(plan);
                                                        setAttendanceModalOpen(true);
                                                    }}
                                                    style={{
                                                        width: '40px',
                                                        height: '40px',
                                                        border: '1px solid #E2E8F0',
                                                        borderRadius: '10px',
                                                        background: '#F8FAFC',
                                                        color: '#475569'
                                                    }}
                                                    title="Registro de Asistencia"
                                                >
                                                    <svg className="i" viewBox="0 0 24 24" style={{ width: '18px', height: '18px' }}>
                                                        <path d="M15 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
                                                        <circle cx="8.5" cy="7" r="4" />
                                                        <path d="M17 11l2 2 4-4" />
                                                    </svg>
                                                </button>

                                                {/* Botón Ver o Editar */}
                                                <button
                                                    type="button"
                                                    className="ib"
                                                    aria-label="Ver o editar"
                                                    onClick={() => {
                                                        recordPlanScroll('semanal', plan.id);
                                                        setWeeklyPlansBackup(JSON.parse(JSON.stringify(weeklyPlans)));
                                                        setActivePlanId(plan.id);
                                                        setIsWeeklyEditing(plan.status !== 'Cerrada');
                                                    }}
                                                    style={{ width: '40px', height: '40px', color: '#64748B' }}
                                                >
                                                    <svg className="i" viewBox="0 0 24 24"><path d="M9 6l6 6-6 6" /></svg>
                                                </button>
                                            </div>
                                        );
                                    })
                                )
                            )}
                        </div>

                        {/* Espaciador inferior para scroll cómodo sobre bottom nav */}
                        <div className="kl-list-end" />
                    </>
                )}
            </PullToRefresh>

            {/* ======================================================== */}
            {/* VISTA ESCRITORIO (PRESERVADA 100% IDÉNTICA)              */}
            {/* ======================================================== */}
            <div className="hidden md:flex flex-col flex-1 h-full overflow-hidden relative">
                {/* Header */}
                <div className="bg-[#1a73e8] dark:bg-blue-900 text-white px-4 md:px-6 pt-4 pb-0 flex flex-col gap-4 shrink-0 shadow-md z-30 overflow-visible">
                    <div className="hidden md:flex items-center gap-6">
                        <div className="header-dropdown-area flex items-center gap-2 text-sm text-blue-100 relative">
                            <button
                                onClick={(e) => { e.stopPropagation(); setCncMenuOpen(!cncMenuOpen); setResponsiblesMenuOpen(false); setConstraintsMenuOpen(false); setIsAdicionalesMenuOpen(false); }}
                                className={`font-semibold text-white flex items-center gap-1 hover:bg-white/10 px-2 py-1 rounded transition-colors ${cncMenuOpen ? 'bg-white/10' : ''}`}
                            >
                                Causas de No Cumplimiento
                                <span className="material-symbols-outlined text-[16px]">expand_more</span>
                            </button>
                            {cncMenuOpen && (
                                <div className="absolute top-full left-0 mt-1 bg-white dark:bg-slate-800 rounded shadow-lg py-1 w-48 z-[9999] text-slate-800 dark:text-white border border-slate-200 dark:border-slate-700 animate-in fade-in zoom-in duration-75">
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setCncMenuOpen(false); setCncModalOpen(true); }}
                                        className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 text-sm cursor-pointer"
                                    >
                                        Crear C.N.C
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="header-dropdown-area flex items-center gap-2 text-sm text-blue-100 relative">
                            <button
                                onClick={(e) => { e.stopPropagation(); setConstraintsMenuOpen(!constraintsMenuOpen); setCncMenuOpen(false); setResponsiblesMenuOpen(false); setIsAdicionalesMenuOpen(false); }}
                                className={`font-semibold text-white flex items-center gap-1 hover:bg-white/10 px-2 py-1 rounded transition-colors ${constraintsMenuOpen ? 'bg-white/10' : ''}`}
                            >
                                Restricciones
                                <span className="material-symbols-outlined text-[16px]">expand_more</span>
                            </button>
                            {constraintsMenuOpen && (
                                <div className="absolute top-full left-0 mt-1 bg-white dark:bg-slate-800 rounded shadow-lg py-1 w-48 z-[9999] text-slate-800 dark:text-white border border-slate-200 dark:border-slate-700 animate-in fade-in zoom-in duration-75">
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setConstraintsMenuOpen(false); setConstraintsModalOpen(true); }}
                                        className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 text-sm cursor-pointer"
                                    >
                                        Crear restricciones
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="header-dropdown-area flex items-center gap-2 text-sm text-blue-100 relative">
                            <button
                                onClick={(e) => { e.stopPropagation(); setResponsiblesMenuOpen(!responsiblesMenuOpen); setCncMenuOpen(false); setConstraintsMenuOpen(false); setIsAdicionalesMenuOpen(false); }}
                                className={`font-semibold text-white flex items-center gap-1 hover:bg-white/10 px-2 py-1 rounded transition-colors ${responsiblesMenuOpen ? 'bg-white/10' : ''}`}
                            >
                                Responsables
                                <span className="material-symbols-outlined text-[16px]">expand_more</span>
                            </button>
                            {responsiblesMenuOpen && (
                                <div className="absolute top-full left-0 mt-1 bg-white dark:bg-slate-800 rounded shadow-lg py-1 w-48 z-[9999] text-slate-800 dark:text-white border border-slate-200 dark:border-slate-700 animate-in fade-in zoom-in duration-75">
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setResponsiblesMenuOpen(false); setIsAddResponsibleModalOpen(true); }}
                                        className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 text-sm cursor-pointer"
                                    >
                                        Añadir Responsable
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setResponsiblesMenuOpen(false); setIsResponsiblesListModalOpen(true); }}
                                        className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 text-sm border-t border-slate-100 dark:border-slate-700 cursor-pointer"
                                    >
                                        Lista de Responsables
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setResponsiblesMenuOpen(false); setIsWorkGroupModalOpen(true); }}
                                        className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 text-sm border-t border-slate-100 dark:border-slate-700 cursor-pointer flex items-center justify-between font-medium text-primary dark:text-blue-400"
                                    >
                                        <span>Grupos de Trabajo</span>
                                        <span className="material-symbols-outlined text-[16px]">groups</span>
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="header-dropdown-area flex items-center gap-2 text-sm text-blue-100 relative">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsAdicionalesMenuOpen(prev => !prev);
                                    setCncMenuOpen(false);
                                    setConstraintsMenuOpen(false);
                                    setResponsiblesMenuOpen(false);
                                }}
                                className={`font-semibold text-white flex items-center gap-1 hover:bg-white/10 px-2 py-1 rounded transition-colors ${isAdicionalesMenuOpen ? 'bg-white/10' : ''}`}
                            >
                                Adicionales
                                <span className="material-symbols-outlined text-[16px]">expand_more</span>
                            </button>
                            {isAdicionalesMenuOpen && (
                                <div className="absolute top-full left-0 mt-1 bg-white dark:bg-slate-800 rounded shadow-lg py-1 w-56 z-[9999] text-slate-800 dark:text-white border border-slate-200 dark:border-slate-700 animate-in fade-in zoom-in duration-75">
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsAdicionalesMenuOpen(false);
                                            setPresentationEditorMode('config');
                                            setShowPresentationEditor(true);
                                        }}
                                        className="w-full text-left px-4 py-2.5 hover:bg-slate-100 dark:hover:bg-slate-700 text-sm flex items-center gap-2.5 cursor-pointer"
                                    >
                                        <span className="material-symbols-outlined text-[18px] text-blue-500">slideshow</span>
                                        Conf. Presentación
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsAdicionalesMenuOpen(false);
                                            setShowPresentationListModal(true);
                                        }}
                                        className="w-full text-left px-4 py-2.5 hover:bg-slate-100 dark:hover:bg-slate-700 text-sm flex items-center gap-2.5 cursor-pointer border-t border-slate-100 dark:border-slate-700/60"
                                    >
                                        <span className="material-symbols-outlined text-[18px] text-indigo-500">list_alt</span>
                                        Lista Presentaciones
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 md:gap-0">
                        <div className="flex items-center justify-between w-full md:w-auto pb-2 md:pb-6">
                            <div className="flex items-center gap-3 md:gap-4 truncate">
                                <div className="hidden md:flex size-10 bg-white/20 rounded-lg items-center justify-center backdrop-blur-sm shrink-0">
                                    <span className="material-symbols-outlined text-[24px]">apartment</span>
                                </div>
                                <div className="truncate">
                                    <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3">
                                        <h1 className="text-lg md:text-xl font-bold truncate">{projectInfo.name}</h1>
                                        <div className={`flex items-center gap-1 px-2.5 py-0.5 md:px-3 md:py-1.5 rounded-full border text-[11px] md:text-sm font-semibold shadow-sm whitespace-nowrap shrink-0 w-fit ${projectStatus.color}`}>
                                            <span className="material-symbols-outlined text-[14px] md:text-[18px]">{projectStatus.icon}</span>
                                            <span>{projectStatus.label} {projectStatus.delay > 0 && `(${projectStatus.delay}d desvío)`}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>


                        </div>

                        <div className="flex items-end gap-1 overflow-x-auto w-full md:w-auto hide-scrollbar pb-0">
                            {[
                                { id: 'maestra', label: 'Maestra', show: hasPermission('mod_plan_maestra', 'view') },
                                { id: 'intermedia', label: 'Intermedia', show: hasPermission('mod_plan_intermedia', 'view') },
                                { id: 'semanal', label: 'Semanal', show: hasPermission('mod_plan_semanal', 'view') }
                            ].filter(t => t.show).map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => {
                                        setActiveTab(tab.id as any);
                                        setIsBreakdownMode(false);
                                        if (tab.id === 'maestra') { setIsWeeklyEditing(false); }
                                        else if (tab.id === 'semanal') { setIsMasterEditing(false); }
                                    }}
                                    className={`
                                    flex items-center gap-2 px-4 py-3 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap
                                    ${activeTab === tab.id
                                            ? 'bg-white dark:bg-[#101822] text-[#1a73e8] dark:text-blue-400 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]'
                                            : 'bg-white/10 text-white hover:bg-white/20'}
                                    ${tab.id === 'maestra' ? 'hidden md:flex' : ''}
                                `}
                                >
                                    <span className="material-symbols-outlined text-[18px]">
                                        {tab.id === 'maestra' ? 'calendar_month' : tab.id === 'intermedia' ? 'calendar_view_week' : 'view_week'}
                                    </span>
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Toolbar */}
                <div className="hidden md:flex bg-white dark:bg-[#101822] border-b border-slate-200 dark:border-slate-700 px-6 py-3 items-center justify-between shrink-0 shadow-sm z-20">
                    {activeTab === 'maestra' ? (
                        <>
                            {/* 1. Gestión Gantt Dropdown */}
                            <div className="relative z-30">
                                <button
                                    onClick={() => setIsGanttMenuOpen(!isGanttMenuOpen)}
                                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-200 bg-white dark:bg-[#1a2634] hover:bg-slate-50 dark:hover:bg-slate-800 transition-all shadow-sm"
                                >
                                    <span className="material-symbols-outlined text-[20px]">menu_open</span>
                                    Gestión Gantt
                                    <span className="material-symbols-outlined text-[16px] transition-transform duration-205">keyboard_arrow_down</span>
                                </button>

                                {isGanttMenuOpen && (
                                    <>
                                        <div className="fixed inset-0 z-30" onClick={() => setIsGanttMenuOpen(false)} />
                                        <div className="absolute left-0 mt-2 w-64 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#1a2634] shadow-lg py-1.5 z-40 animate-fade-in-up">
                                            {/* Option: Editar Carta Gantt */}
                                            {hasPermission('pm_gantt_edit', 'edit') && (
                                                <button
                                                    onClick={() => {
                                                        setIsGanttMenuOpen(false);
                                                        if (isMasterEditing) {
                                                            if (forceSyncTasks) {
                                                                forceSyncTasks(processedTasks).catch(alert);
                                                            }
                                                            addActivity({
                                                                type: 'planning',
                                                                title: 'Planificación modificada',
                                                                description: 'Se han guardado cambios en la Carta Gantt Maestra',
                                                                user: currentUser?.name || currentUser?.email || 'Usuario'
                                                            });
                                                        }
                                                        setIsMasterEditing(!isMasterEditing);
                                                    }}
                                                    className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-sm font-bold transition-colors ${isMasterEditing
                                                        ? 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-950/20'
                                                        : 'text-slate-700 dark:text-slate-205 hover:bg-slate-100 dark:hover:bg-slate-800'
                                                        }`}
                                                >
                                                    <span className="material-symbols-outlined text-[18px]">{isMasterEditing ? 'save' : 'edit'}</span>
                                                    {isMasterEditing ? 'Guardar Modificaciones' : 'Editar Carta Gantt'}
                                                </button>
                                            )}

                                            {/* Option: Configuración de Calendario (Only visible when editing and pm_gantt_edit edit permission is granted) */}
                                            {isMasterEditing && hasPermission('pm_gantt_edit', 'edit') && (
                                                <button
                                                    onClick={() => {
                                                        setIsGanttMenuOpen(false);
                                                        setCalendarModalOpen(true);
                                                    }}
                                                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-sm font-bold text-slate-700 dark:text-slate-205 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                                >
                                                    <span className="material-symbols-outlined text-[18px]">calendar_today</span>
                                                    Configurar Calendario
                                                </button>
                                            )}

                                            {/* Option: Importar Carta Gantt */}
                                            {hasPermission('pm_gantt_imp', 'edit') && (
                                                <button
                                                    onClick={() => {
                                                        setIsGanttMenuOpen(false);
                                                        setImportModalOpen(true);
                                                    }}
                                                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-sm font-bold text-slate-700 dark:text-slate-205 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                                >
                                                    <span className="material-symbols-outlined text-[18px]">upload_file</span>
                                                    Importar Carta Gantt
                                                </button>
                                            )}

                                            {/* Option: Cargar Reprogramación */}
                                            {hasPermission('pm_reprog', 'edit') && (
                                                <button
                                                    onClick={() => {
                                                        setIsGanttMenuOpen(false);
                                                        setReprogrammingModalOpen(true);
                                                    }}
                                                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-sm font-bold text-slate-700 dark:text-slate-205 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                                >
                                                    <span className="material-symbols-outlined text-[18px]">update</span>
                                                    Cargar Reprogramación
                                                </button>
                                            )}

                                            {/* Option: Exportar Carta Gantt */}
                                            <button
                                                onClick={() => {
                                                    setIsGanttMenuOpen(false);
                                                    setExportModalOpen(true);
                                                }}
                                                disabled={!hasPermission("pm_gantt_exp", "edit")}
                                                className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-sm font-bold transition-colors ${hasPermission("pm_gantt_exp", "edit")
                                                    ? 'text-slate-700 dark:text-slate-205 hover:bg-slate-100 dark:hover:bg-slate-800'
                                                    : 'text-slate-400 dark:text-slate-500 cursor-not-allowed opacity-50'
                                                    }`}
                                            >
                                                <span className="material-symbols-outlined text-[18px]">download</span>
                                                Exportar Carta Gantt
                                            </button>

                                            {/* Option: Historial de Versiones */}
                                            <button
                                                onClick={() => {
                                                    setIsGanttMenuOpen(false);
                                                    setHistoryModalOpen(true);
                                                }}
                                                disabled={!hasPermission("pm_historial", "view")}
                                                className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-sm font-bold transition-colors ${hasPermission("pm_historial", "view")
                                                    ? 'text-slate-700 dark:text-slate-205 hover:bg-slate-100 dark:hover:bg-slate-800'
                                                    : 'text-slate-400 dark:text-slate-500 cursor-not-allowed opacity-50'
                                                    }`}
                                            >
                                                <span className="material-symbols-outlined text-[18px]">history</span>
                                                Historial de Versiones
                                            </button>

                                            {/* Option: Conf. Presentación */}
                                            <button
                                                onMouseDown={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    setIsGanttMenuOpen(false);
                                                    setShowPresentationEditor(true);
                                                }}
                                                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-sm font-bold text-slate-700 dark:text-slate-205 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors border-t border-slate-100 dark:border-slate-800 text-blue-600 dark:text-blue-400"
                                            >
                                                <span className="material-symbols-outlined text-[18px]">slideshow</span>
                                                Conf. Presentación
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* 2. Unified View Mode (Segmented Control) */}
                            <div className="flex bg-slate-100 dark:bg-slate-800/80 p-1 rounded-xl border border-slate-250/20 shadow-inner">
                                <button
                                    onClick={() => setGraphicMode(false)}
                                    className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-bold transition-all duration-200 ${!graphicMode
                                        ? 'bg-white dark:bg-[#1a2634] text-blue-600 dark:text-blue-400 shadow-sm'
                                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                                        }`}
                                >
                                    <span className="material-symbols-outlined text-[16px]">table_chart</span>
                                    Carta Gantt
                                </button>
                                <button
                                    onClick={() => setGraphicMode(true)}
                                    className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-bold transition-all duration-200 ${graphicMode
                                        ? 'bg-white dark:bg-[#1a2634] text-blue-600 dark:text-blue-400 shadow-sm'
                                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                                        }`}
                                >
                                    <span className="material-symbols-outlined text-[16px]">map</span>
                                    Avance Gráfico
                                </button>
                            </div>

                            {/* Toggle Ruta Crítica */}
                            <button
                                onClick={() => setShowCriticalPath(!showCriticalPath)}
                                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-xs font-bold border transition-all duration-200 ${showCriticalPath
                                    ? 'bg-red-50 dark:bg-red-950/20 border-red-250 dark:border-red-900/40 text-red-600 dark:text-red-400 shadow-sm'
                                    : 'bg-white dark:bg-[#1a2634] border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                                    }`}
                            >
                                <span className={`material-symbols-outlined text-[16px] ${showCriticalPath ? 'text-red-500 animate-pulse' : ''}`}>alt_route</span>
                                Ruta Crítica
                            </button>

                            {/* 3. Main CTA Button */}
                            <button
                                onClick={() => { setSyncProgressModalOpen(true); setExtraWeeksCount(0); }}
                                disabled={!hasPermission("pm_sync", "edit")}
                                className={`flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold bg-[#1a73e8] hover:bg-blue-600 text-white shadow-md shadow-blue-500/10 transition-all duration-150 active:scale-95 ml-auto ${!hasPermission("pm_sync", "edit") ? "opacity-50 pointer-events-none" : ""
                                    }`}
                            >
                                <span className="material-symbols-outlined text-[20px]">history_toggle_off</span>
                                Sincronizar Avance Semanal
                            </button>
                        </>
                    ) : activeTab === 'intermedia' ? (
                        <div className="flex gap-2 w-full">
                            {!activeLookaheadId ? (
                                <>
                                    <button
                                        onClick={() => setActiveLookaheadId(null)}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold border border-slate-300 text-slate-600 hover:bg-slate-50 transition-all ${!hasPermission("pi_historial", "view") ? "opacity-50 pointer-events-none" : ""}`}
                                    >
                                        <span className="material-symbols-outlined text-[20px]">history</span>
                                        Historial
                                    </button>
                                    <button
                                        onClick={() => setIsITEView(!isITEView)}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold border transition-all ${isITEView ? 'bg-amber-100 text-amber-700 border-amber-300' : 'border-slate-300 text-slate-600 hover:bg-slate-50'} ${!hasPermission("pi_ite", "view") ? "opacity-50 pointer-events-none" : ""}`}
                                    >
                                        <span className="material-symbols-outlined text-[20px]">assignment_turned_in</span>
                                        I.T.E
                                    </button>
                                    {!isMobile && (
                                        <button
                                            onClick={handleOpenLookaheadModal}
                                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-purple-600 text-white border border-purple-600 hover:bg-purple-700 transition-all ml-auto ${!hasPermission("pi_generar", "edit") ? "opacity-50 pointer-events-none" : ""}`}
                                        >
                                            <span className="material-symbols-outlined text-[20px]">add_road</span>
                                            Generar Planificación Intermedia
                                        </button>
                                    )}
                                </>
                            ) : (
                                <>
                                    <button
                                        onClick={() => setIsITEView(!isITEView)}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold border transition-all ${isITEView ? 'bg-amber-100 text-amber-700 border-amber-300' : 'border-slate-300 text-slate-600 hover:bg-slate-50'} ${!hasPermission("pi_ite", "view") ? "opacity-50 pointer-events-none" : ""}`}
                                    >
                                        <span className="material-symbols-outlined text-[20px]">assignment_turned_in</span>
                                        I.T.E
                                    </button>
                                    {activeLookahead?.status !== 'Cerrada' && (
                                        <button
                                            onClick={handleSaveLookaheadPlan}
                                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-green-600 text-white border border-green-600 hover:bg-green-700 transition-all ml-auto ${!hasPermission("pi_generar", "edit") ? "opacity-50 pointer-events-none" : ""}`}
                                        >
                                            <span className="material-symbols-outlined text-[20px]">save</span>
                                            Guardar Planificación
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    ) : activeTab === 'semanal' && (isWeeklyEditing || (activePlanId !== null && activePlan?.status !== 'Cerrada')) ? (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setIsBreakdownMode(!isBreakdownMode)}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-bold border transition-all ${isBreakdownMode
                                        ? 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-700'
                                        : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50'
                                    } ${!hasPermission("ps_desglose", "edit") ? "opacity-50 pointer-events-none" : ""}`}
                                title="Activar o desactivar el modo de desglose para crear o editar subtareas directamente en la planificación"
                            >
                                <span className="material-symbols-outlined text-[20px]">account_tree</span>
                                <span>Desglose</span>
                                {isBreakdownMode && <span className="text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded-full uppercase ml-1">Activo</span>}
                            </button>

                            {/* Dropdown Avance Gráfico (Barra Superior) */}
                            <div className="relative">
                                <button
                                    onClick={() => setIsWeeklyHeaderDropdownOpen(prev => !prev)}
                                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-bold border transition-all ${weeklyGraphicMode
                                            ? 'bg-blue-100 text-blue-800 border-blue-400 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-700'
                                            : 'border-blue-500 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30'
                                        } shadow-sm`}
                                    title="Opciones de Avance Gráfico"
                                >
                                    <span className="material-symbols-outlined text-[20px]">map</span>
                                    <span>Avance Gráfico</span>
                                    {weeklyGraphicMode && (
                                        <span className="text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded-full uppercase ml-1">
                                            {weeklyGraphicViewType === 'planning' ? 'Plan' : 'Tarea'}
                                        </span>
                                    )}
                                    <span className="material-symbols-outlined text-[16px] text-slate-400">arrow_drop_down</span>
                                </button>

                                {isWeeklyHeaderDropdownOpen && (
                                    <div className="absolute right-0 mt-1 w-64 bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800 py-1.5 z-50 animate-in fade-in zoom-in-95 duration-100">
                                        <button
                                            onClick={() => {
                                                setWeeklyGraphicViewType('task');
                                                setWeeklyGraphicMode(true);
                                                setIsWeeklyHeaderDropdownOpen(false);
                                            }}
                                            className={`w-full px-3.5 py-2.5 text-left text-xs flex items-center gap-2.5 transition-colors ${weeklyGraphicMode && weeklyGraphicViewType === 'task'
                                                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300 font-bold'
                                                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                                                }`}
                                        >
                                            <span className="material-symbols-outlined text-[18px] text-blue-600">assignment</span>
                                            <div>
                                                <div className="font-bold">Avance Gráfico Tarea</div>
                                                <div className="text-[10px] text-slate-500">Visualizar avance acumulado por partida</div>
                                            </div>
                                        </button>

                                        <button
                                            onClick={() => {
                                                setWeeklyGraphicViewType('planning');
                                                setWeeklyGraphicMode(true);
                                                setIsWeeklyHeaderDropdownOpen(false);
                                            }}
                                            className={`w-full px-3.5 py-2.5 text-left text-xs flex items-center gap-2.5 transition-colors ${weeklyGraphicMode && weeklyGraphicViewType === 'planning'
                                                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300 font-bold'
                                                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                                                }`}
                                        >
                                            <span className="material-symbols-outlined text-[18px] text-indigo-600">view_quilt</span>
                                            <div>
                                                <div className="font-bold">Avance Gráfico Planificación</div>
                                                <div className="text-[10px] text-slate-500">Visor espacial por Nivel y Semana activa</div>
                                            </div>
                                        </button>

                                        {weeklyGraphicMode && (
                                            <div className="border-t border-slate-100 dark:border-slate-800 mt-1 pt-1">
                                                <button
                                                    onClick={() => {
                                                        setWeeklyGraphicMode(false);
                                                        setIsWeeklyHeaderDropdownOpen(false);
                                                    }}
                                                    className="w-full px-3.5 py-1.5 text-left text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 flex items-center gap-2"
                                                >
                                                    <span className="material-symbols-outlined text-[16px]">visibility_off</span>
                                                    <span>Cerrar Visor Gráfico</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Botón Ver Fechas en barra superior de edición */}
                            <button
                                onClick={() => setShowWeeklyDates(prev => !prev)}
                                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold border transition-all ${showWeeklyDates
                                        ? 'bg-blue-100 text-blue-800 border-blue-400 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-700 font-extrabold'
                                        : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50'
                                    } shadow-sm`}
                                title="Mostrar u ocultar columnas de Fecha Inicio y Término"
                            >
                                <span className="material-symbols-outlined text-[18px]">calendar_month</span>
                                <span>Ver Fechas</span>
                                {showWeeklyDates && <span className="text-[10px] bg-blue-600 text-white px-1.5 py-0.2 rounded-full uppercase ml-0.5">Activo</span>}
                            </button>

                            <button
                                onClick={handleSaveWeeklyPlanFromToolbar}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-green-600 text-white border border-green-600 hover:bg-green-700 transition-all ${!(hasPermission("ps_crear", "edit") || hasPermission("ps_editar", "edit")) ? "opacity-50 pointer-events-none" : ""}`}
                            >
                                <span className="material-symbols-outlined text-[20px]">save</span>
                                Guardar Planificación
                            </button>
                        </div>
                    ) : activeTab === 'semanal' && isBreakdownMode ? (
                        <div className="flex items-center gap-2">
                            {/* Botón Ver Fechas en modo desglose */}
                            <button
                                onClick={() => setShowWeeklyDates(prev => !prev)}
                                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold border transition-all ${showWeeklyDates
                                        ? 'bg-blue-100 text-blue-800 border-blue-400 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-700 font-extrabold'
                                        : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50'
                                    } shadow-sm`}
                                title="Mostrar u ocultar columnas de Fecha Inicio y Término"
                            >
                                <span className="material-symbols-outlined text-[18px]">calendar_month</span>
                                <span>Ver Fechas</span>
                                {showWeeklyDates && <span className="text-[10px] bg-blue-600 text-white px-1.5 py-0.2 rounded-full uppercase ml-0.5">Activo</span>}
                            </button>

                            <button
                                onClick={() => setIsBreakdownMode(false)}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-green-600 text-white border border-green-600 hover:bg-green-700 transition-all"
                            >
                                <span className="material-symbols-outlined text-[20px]">save</span>
                                Guardar Desglose
                            </button>
                        </div>
                    ) : (
                        <div className="text-sm text-slate-500 italic flex items-center gap-2">
                            <span className="material-symbols-outlined text-lg">visibility</span>
                            Modo visualización
                        </div>
                    )}
                </div>

                {/* Main Content */}
                <div className="flex-1 flex overflow-hidden relative">
                    {/* Left Side: Table (Task List) */}
                    <div
                        id="planning-left-pane"
                        className={`
                        ${isWeeklyGraphicExpanded && weeklyGraphicMode && activeTab === 'semanal' ? '!hidden' : ''}
                        ${leftPaneWidth === null ? (activeTab === 'semanal' && showWeeklyDates ? 'w-[52%]' : 'w-[45%]') : ''} 
                        hidden md:flex flex-col border-r border-slate-200 dark:border-slate-700 bg-white dark:bg-[#101822] z-10 shadow-[4px_0_10px_rgba(0,0,0,0.02)] shrink-0 ${activeTab === 'semanal' && showWeeklyDates ? 'min-w-[540px]' : 'min-w-[420px]'}
                    `}
                        style={leftPaneWidth !== null && !(isWeeklyGraphicExpanded && weeklyGraphicMode && activeTab === 'semanal') ? { width: `${leftPaneWidth}px`, minWidth: `${leftPaneWidth}px`, maxWidth: `${leftPaneWidth}px` } : undefined}
                    >
                        {/* Sub-header actions bar for weekly mode */}
                        {activeTab === 'semanal' && !isWeeklyEditing && !isBreakdownMode && (
                            <div className="p-2 bg-slate-50 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-800 flex flex-wrap items-center gap-2 shrink-0">
                                <button
                                    onClick={() => {
                                        if (activePlanId !== null && (isWeeklyEditing || activePlan?.status !== 'Cerrada')) {
                                            setExitWarningTarget('semanal');
                                            setShowExitWarningModal(true);
                                        } else {
                                            setActivePlanId(null);
                                            setIsWeeklyEditing(false);
                                        }
                                    }}
                                    className={`flex items-center gap-1 px-2.5 py-1 rounded-lg border transition-colors normal-case font-medium text-xs ${activePlanId === null ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white border-slate-300 dark:border-slate-600 font-bold' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:text-slate-900'} ${!hasPermission("ps_historial", "view") ? "opacity-50 pointer-events-none" : ""}`}
                                >
                                    <span className="material-symbols-outlined text-[16px]">history</span>
                                    <span>Historial</span>
                                </button>
                                {!isMobile && (
                                    <button onClick={() => setNewPlanModalOpen(true)} className={`flex items-center gap-1 px-2.5 py-1 rounded-lg bg-primary text-white hover:bg-blue-600 transition-colors normal-case font-medium text-xs shadow-sm ${!hasPermission("ps_crear", "edit") ? "opacity-50 pointer-events-none" : ""}`}>
                                        <span className="material-symbols-outlined text-[16px]">add</span>
                                        <span>Crear Planificación</span>
                                    </button>
                                )}
                                <button onClick={handleBreakdownClick} className={`flex items-center gap-1 px-2.5 py-1 rounded-lg border border-primary text-primary hover:bg-primary/5 transition-colors normal-case font-medium text-xs shadow-sm ${!hasPermission("ps_desglose", "edit") ? "opacity-50 pointer-events-none" : ""}`}>
                                    <span className="material-symbols-outlined text-[16px]">account_tree</span>
                                    <span>Desglose</span>
                                </button>

                                {/* Dropdown Avance Gráfico (Barra Inferior Izquierda) */}
                                <div className="relative">
                                    <button
                                        onClick={() => setIsWeeklyGraphicDropdownOpen(prev => !prev)}
                                        className={`flex items-center gap-1 px-2.5 py-1 rounded-lg border transition-colors normal-case font-bold text-xs shadow-sm ${weeklyGraphicMode
                                                ? 'bg-blue-100 text-blue-800 border-blue-400 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-700'
                                                : 'border-blue-500 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30'
                                            }`}
                                        title="Opciones de Avance Gráfico"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">map</span>
                                        <span>Avance Gráfico</span>
                                        {weeklyGraphicMode && <span className="text-[9px] bg-blue-600 text-white px-1 py-0.2 rounded-full uppercase ml-0.5">{weeklyGraphicViewType === 'planning' ? 'Plan' : 'Tarea'}</span>}
                                        <span className="material-symbols-outlined text-[14px]">arrow_drop_down</span>
                                    </button>

                                    {isWeeklyGraphicDropdownOpen && (
                                        <div className="absolute left-0 mt-1 w-64 bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800 py-1.5 z-50 animate-in fade-in zoom-in-95 duration-100">
                                            <button
                                                onClick={() => {
                                                    setWeeklyGraphicViewType('task');
                                                    setWeeklyGraphicMode(true);
                                                    setIsWeeklyGraphicDropdownOpen(false);
                                                }}
                                                className={`w-full px-3.5 py-2.5 text-left text-xs flex items-center gap-2.5 transition-colors ${weeklyGraphicMode && weeklyGraphicViewType === 'task'
                                                        ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300 font-bold'
                                                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                                                    }`}
                                            >
                                                <span className="material-symbols-outlined text-[18px] text-blue-600">assignment</span>
                                                <div>
                                                    <div className="font-bold">Avance Gráfico Tarea</div>
                                                    <div className="text-[10px] text-slate-500">Visualizar avance acumulado por partida</div>
                                                </div>
                                            </button>

                                            <button
                                                onClick={() => {
                                                    setWeeklyGraphicViewType('planning');
                                                    setWeeklyGraphicMode(true);
                                                    setIsWeeklyGraphicDropdownOpen(false);
                                                }}
                                                className={`w-full px-3.5 py-2.5 text-left text-xs flex items-center gap-2.5 transition-colors ${weeklyGraphicMode && weeklyGraphicViewType === 'planning'
                                                        ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300 font-bold'
                                                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                                                    }`}
                                            >
                                                <span className="material-symbols-outlined text-[18px] text-indigo-600">view_quilt</span>
                                                <div>
                                                    <div className="font-bold">Avance Gráfico Planificación</div>
                                                    <div className="text-[10px] text-slate-500">Visor espacial por Nivel y Semana activa</div>
                                                </div>
                                            </button>

                                            {weeklyGraphicMode && (
                                                <div className="border-t border-slate-100 dark:border-slate-800 mt-1 pt-1">
                                                    <button
                                                        onClick={() => {
                                                            setWeeklyGraphicMode(false);
                                                            setIsWeeklyGraphicDropdownOpen(false);
                                                        }}
                                                        className="w-full px-3.5 py-1.5 text-left text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 flex items-center gap-2"
                                                    >
                                                        <span className="material-symbols-outlined text-[16px]">visibility_off</span>
                                                        <span>Cerrar Visor Gráfico</span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Botón Ver Fechas en barra inferior izquierda */}
                                <button
                                    onClick={() => setShowWeeklyDates(prev => !prev)}
                                    className={`flex items-center gap-1 px-2.5 py-1 rounded-lg border transition-colors normal-case font-medium text-xs shadow-sm ${showWeeklyDates
                                            ? 'bg-blue-100 text-blue-800 border-blue-400 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-700 font-bold'
                                            : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:text-slate-900'
                                        }`}
                                    title="Mostrar u ocultar fechas de inicio y término en la tabla"
                                >
                                    <span className="material-symbols-outlined text-[16px]">calendar_month</span>
                                    <span>Ver Fechas</span>
                                    {showWeeklyDates && <span className="text-[9px] bg-blue-600 text-white px-1 py-0.2 rounded-full uppercase ml-0.5 font-bold">Activo</span>}
                                </button>
                            </div>
                        )}
                        <div
                            className={`flex-1 ${activeTab === 'semanal' && !showWeeklyDates ? 'overflow-y-auto overflow-x-hidden' : 'overflow-auto'}`}
                            ref={leftScrollRef}
                            onScroll={handleLeftScroll}
                            onClick={() => setSelectedGanttTaskId(null)}
                        >
                            <table className={`w-full text-left border-collapse ${activeTab === 'semanal' && !showWeeklyDates ? 'table-fixed' : 'min-w-[420px]'}`}>
                                <thead className="sticky top-0 z-10 bg-white dark:bg-[#101822] shadow-sm">
                                    <tr className="border-b border-slate-200 dark:border-slate-700 text-[11px] font-bold text-slate-400 uppercase tracking-wider h-[45px] whitespace-nowrap">
                                        <th className={`px-4 py-2 bg-slate-50/50 dark:bg-slate-900/50 text-center align-middle ${activeTab === 'semanal' ? 'w-10' : 'w-12'}`}>#</th>
                                        <th
                                            id="planning-tarea-header"
                                            className="px-4 py-2 bg-slate-50/50 dark:bg-slate-900/50 align-middle relative group"
                                            style={activeTab !== 'semanal' && tareaColumnWidth !== null ? { width: `${tareaColumnWidth}px`, minWidth: `${tareaColumnWidth}px`, maxWidth: `${tareaColumnWidth}px` } : undefined}
                                        >
                                            {activeTab === 'semanal' ? (
                                                <div className="flex items-center justify-between gap-2 w-full pr-1">
                                                    <span className="shrink-0">
                                                        Tarea {isBreakdownMode && <span className="text-amber-600 text-[10px] ml-1 font-bold">(Desglose)</span>} {isWeeklyEditing && (
                                                            <span className="text-primary text-[10px] ml-1 font-bold">
                                                                {activePlan?.status === 'En Ejecución' ? '(Ejecutando)' :
                                                                    activePlan?.status === 'En Revisión' ? '(Revisando)' :
                                                                        '(Planificando)'}
                                                            </span>
                                                        )}
                                                    </span>
                                                    <div className="relative flex items-center min-w-[130px] max-w-[220px] flex-1">
                                                        <span className="material-symbols-outlined absolute left-2 text-slate-400 text-[14px] pointer-events-none">search</span>
                                                        <input
                                                            type="text"
                                                            value={weeklyTaskSearchTerm}
                                                            onChange={(e) => setWeeklyTaskSearchTerm(e.target.value)}
                                                            placeholder="Buscar partida..."
                                                            className="w-full pl-6 pr-6 py-0.5 text-[11px] bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-md text-slate-800 dark:text-slate-200 placeholder-slate-400 font-normal outline-none focus:border-primary focus:ring-1 focus:ring-primary/50 transition-all shadow-none"
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                        {weeklyTaskSearchTerm && (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setWeeklyTaskSearchTerm('');
                                                                }}
                                                                className="absolute right-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 flex items-center"
                                                                title="Limpiar búsqueda"
                                                            >
                                                                <span className="material-symbols-outlined text-[14px]">close</span>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : activeTab === 'intermedia' ? (
                                                <div className="flex items-center justify-between">
                                                    <span>Tarea (Análisis de Restricciones)</span>
                                                    {activeLookaheadId && (
                                                        <button onClick={() => {
                                                            if (activeLookahead?.status === 'En Planificación') {
                                                                setExitWarningTarget('intermedia');
                                                                setShowExitWarningModal(true);
                                                            } else {
                                                                setActiveLookaheadId(null);
                                                            }
                                                        }} className="text-xs flex items-center gap-1 text-slate-500 hover:text-purple-600">
                                                            <span className="material-symbols-outlined text-[14px]">arrow_back</span> Volver a Lista
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-between">
                                                    <span>Tarea</span>
                                                    {isMasterEditing && (
                                                        <button onClick={() => handleCreateFolderClick(null)} className="flex items-center gap-1 text-[10px] bg-primary/10 text-primary px-2 py-1 rounded hover:bg-primary/20">
                                                            <span className="material-symbols-outlined text-[14px]">create_new_folder</span>
                                                            Crear Carpeta
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                            {activeTab !== 'semanal' && (
                                                <div
                                                    onMouseDown={handleResizeMouseDown}
                                                    className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-primary bg-slate-200/20 dark:bg-slate-700/20 transition-colors z-20"
                                                    title="Arrastra para cambiar el ancho de la columna Tarea"
                                                />
                                            )}
                                        </th>
                                        {activeTab === 'semanal' || activeTab === 'intermedia' || (activeTab === 'maestra' && !isMasterEditing) ? (
                                            <th className={`px-4 py-2 bg-slate-50/50 dark:bg-slate-900/50 text-center align-middle ${activeTab === 'semanal' ? 'w-28' : 'w-32'}`}>% Avance</th>
                                        ) : null}
                                        {activeTab === 'semanal' && showWeeklyDates && (
                                            <>
                                                <th className="px-3 py-2 bg-slate-50/50 dark:bg-slate-900/50 text-center w-24 align-middle">Fecha Inicio</th>
                                                <th className="px-3 py-2 bg-slate-50/50 dark:bg-slate-900/50 text-center w-24 align-middle border-r dark:border-slate-700">Fecha Término</th>
                                            </>
                                        )}
                                        {activeTab !== 'semanal' && activeTab !== 'intermedia' && (
                                            <>
                                                <th className="px-4 py-2 bg-slate-50/50 dark:bg-slate-900/50 text-center w-24 align-middle">Duración</th>
                                                <th className="px-4 py-2 bg-slate-50/50 dark:bg-slate-900/50 w-36 align-middle border-r dark:border-slate-700">Fecha Inicio</th>
                                                <th className="px-4 py-2 bg-slate-50/50 dark:bg-slate-900/50 w-36 align-middle border-r dark:border-slate-700">Fecha Termino</th>
                                                {(isMasterEditing || flattenedTasks.some(t => (t.predecessors && t.predecessors.length > 0) || (t.successors && t.successors.length > 0))) && (
                                                    <>
                                                        <th className="px-4 py-2 bg-slate-50/50 dark:bg-slate-900/50 w-24 align-middle border-r dark:border-slate-700">Predecesor</th>
                                                        <th className="px-4 py-2 bg-slate-50/50 dark:bg-slate-900/50 w-24 align-middle">Sucesor</th>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {tasksToDisplayInLeftTable.length === 0 ? (
                                        <tr>
                                            <td colSpan={10} className="p-10 text-center text-slate-500 italic dark:text-slate-400 font-medium text-xs">
                                                {weeklyTaskSearchTerm.trim() ? `No se encontraron partidas que coincidan con "${weeklyTaskSearchTerm}"` : "Se debe importar o crear una planificación"}
                                            </td>
                                        </tr>
                                    ) : (
                                        renderTableRows(tasksToDisplayInLeftTable)
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Splitter Bar between Tasks and Gantt */}
                    <div
                        onMouseDown={handleSplitterMouseDown}
                        className={`${isWeeklyGraphicExpanded && weeklyGraphicMode && activeTab === 'semanal' ? 'hidden' : 'hidden md:block'} w-1.5 hover:w-2 bg-slate-200/80 hover:bg-primary dark:bg-slate-700 dark:hover:bg-blue-500 cursor-col-resize transition-all shrink-0 z-20 border-l border-r border-slate-300 dark:border-slate-600`}
                        title="Arrastra para ajustar la división"
                    />

                    {/* Right Side: Content Area (Gantt, Weekly Plan Detail, or Lookahead) */}
                    <div
                        className="flex-1 overflow-auto bg-slate-50 dark:bg-[#0d131b] relative gantt-scroll"
                        ref={rightScrollRef}
                        onScroll={handleRightScroll}
                        onClick={() => setSelectedGanttTaskId(null)}
                    >
                        {activeTab === 'maestra' && graphicMode ? (
                            <div className="h-full w-full p-4 flex flex-col">
                                <div className="flex justify-between items-center mb-2 px-2">
                                    <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200">Avance Gráfico</h2>
                                    <button onClick={() => setGraphicMode(false)} className="px-3 py-1.5 border border-slate-300 rounded text-slate-600 text-xs font-bold hover:bg-slate-50">
                                        Cerrar Visor
                                    </button>
                                </div>
                                <div className="flex-1 relative">
                                    <GraphicProgressViewer
                                        activeProjectId={projectInfo.id}
                                        tasks={processedTasks}
                                        units={units}
                                        levels={levels}
                                        matrixData={matrixData}
                                        protocolConfigs={protocolConfigs}
                                        projectInfo={projectInfo}
                                        savedProtocols={savedProtocols}
                                        weeklyPlans={weeklyPlans}
                                        selectedTaskId={graphicSelectedTaskId}
                                        activePlanId={activePlanId}
                                        onClose={() => setGraphicMode(false)}
                                    />
                                </div>
                            </div>
                        ) : activeTab === 'intermedia' ? (
                            <div className="h-full p-2 md:p-6 flex flex-col">
                                {activeLookaheadId ? (
                                    // Lookahead Plan Detail (Constraints Analysis)
                                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 flex-1 min-h-0 flex flex-col overflow-hidden">
                                        <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-purple-50/50 dark:bg-purple-900/10">
                                            <div className="flex justify-between items-center">
                                                <div>
                                                    <div className="flex items-center gap-2 md:gap-3">
                                                        <button
                                                            onClick={() => {
                                                                if (activeLookahead?.status !== 'Cerrada') {
                                                                    setExitWarningTarget('intermedia');
                                                                    setShowExitWarningModal(true);
                                                                } else {
                                                                    setActiveLookaheadId(null);
                                                                }
                                                            }}
                                                            className="text-slate-400 hover:text-slate-600 flex items-center justify-center"
                                                        >
                                                            <span className="material-symbols-outlined text-[20px] md:text-[24px]">arrow_back</span>
                                                        </button>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-purple-600 font-bold text-sm uppercase tracking-wide">{getLookaheadPlanTitle(activeLookahead?.id)}</span>
                                                            <PlanStatusDot status={activeLookahead?.status} title={activeLookahead?.status} />
                                                        </div>
                                                        <span className="text-slate-400 text-xs hidden md:inline">•</span>
                                                        <span className="text-slate-600 dark:text-slate-300 font-bold text-sm hidden md:inline">Semana: {formatDateToShow(activeLookahead?.startDate)} al {formatDateToShow(activeLookahead?.endDate)}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 mt-1 ml-7 md:ml-9">
                                                        <p className="hidden md:block text-xs text-slate-500">Análisis de restricciones para tareas proyectadas a {activeLookahead?.weeksAhead} semanas.</p>
                                                        <span className="hidden md:inline text-slate-400 text-xs">•</span>
                                                        <span className={`hidden md:inline text-xs font-bold ${activeLookahead?.status === 'En Planificación' ? 'text-blue-600' :
                                                            activeLookahead?.status === 'En Ejecución' ? 'text-green-600' :
                                                                activeLookahead?.status === 'En Revisión' ? 'text-amber-500' :
                                                                    'text-slate-600'
                                                            }`}>Estado: {activeLookahead?.status}</span>
                                                    </div>
                                                </div>
                                                <div className="flex gap-2">
                                                    {/* En Planificación */}
                                                    {activeLookahead?.status === 'En Planificación' && hasPermission('ps_publicar', 'edit') && (
                                                        <button
                                                            onClick={() => handlePublishLookahead(activeLookahead.id)}
                                                            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-xs font-bold transition-colors flex items-center gap-1"
                                                        >
                                                            <span className="material-symbols-outlined text-[16px]">send</span>
                                                            <span>Publicar Planificación</span>
                                                        </button>
                                                    )}

                                                    {/* En Ejecución */}
                                                    {activeLookahead?.status === 'En Ejecución' && (
                                                        <>
                                                            {((isMobile ? hasPermission("movil_ps_revisar", "view") : hasPermission("ps_revisar", "view"))) && (
                                                                <button
                                                                    onClick={() => {
                                                                        const canEdit = isMobile ? hasPermission("movil_ps_revisar", "edit") : hasPermission("ps_revisar", "edit");
                                                                        if (!canEdit) return;
                                                                        handleStartLookaheadReview(activeLookahead.id);
                                                                    }}
                                                                    disabled={!(isMobile ? hasPermission("movil_ps_revisar", "edit") : hasPermission("ps_revisar", "edit"))}
                                                                    className={`bg-amber-50 text-amber-600 hover:bg-amber-100 border border-amber-200 px-3 py-1.5 rounded text-xs font-bold transition-colors ${!(isMobile ? hasPermission("movil_ps_revisar", "edit") : hasPermission("ps_revisar", "edit")) ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                                                                        }`}
                                                                >
                                                                    Revisar Planificación Intermedia
                                                                </button>
                                                            )}
                                                            {!isMobile && (
                                                                <button
                                                                    onClick={() => handleRevertLookaheadToDraft(activeLookahead.id)}
                                                                    className="bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-300 px-3 py-1.5 rounded text-xs font-bold transition-colors flex items-center gap-1"
                                                                >
                                                                    <span className="material-symbols-outlined text-[16px]">edit_note</span>
                                                                    <span>Editar Planificación</span>
                                                                </button>
                                                            )}
                                                        </>
                                                    )}

                                                    {/* En Revisión */}
                                                    {activeLookahead?.status === 'En Revisión' && (
                                                        <>
                                                            <button
                                                                onClick={() => handleBackToLookaheadExecution(activeLookahead.id)}
                                                                className="bg-white text-slate-600 hover:bg-slate-100 border border-slate-300 px-3 py-1.5 rounded text-xs font-bold transition-colors flex items-center gap-1"
                                                            >
                                                                <span className="material-symbols-outlined text-[16px]">arrow_back</span>
                                                                <span>Volver a Ejecución</span>
                                                            </button>
                                                            {hasMissingLookaheadComments && (
                                                                <div className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-[10px] md:text-xs font-bold flex items-center gap-1 animate-pulse border border-red-200">
                                                                    <span className="material-symbols-outlined text-[14px]">warning</span>
                                                                    <span>Faltan comentarios de justificación</span>
                                                                </div>
                                                            )}
                                                            <button
                                                                onClick={() => handleCloseLookahead(activeLookahead.id)}
                                                                disabled={hasMissingLookaheadComments}
                                                                className={`px-3 py-1.5 rounded text-xs font-bold transition-colors flex items-center gap-1 border ${hasMissingLookaheadComments
                                                                    ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                                                                    : 'bg-green-50 text-green-600 hover:bg-green-100 border-green-200'
                                                                    }`}
                                                                title={hasMissingLookaheadComments ? "No se puede finalizar la revisión porque hay restricciones sin liberar que no cuentan con observaciones/justificaciones de incumplimiento" : "Finalizar Revisión"}
                                                            >
                                                                <span className="material-symbols-outlined text-[16px]">check_circle</span>
                                                                <span>Finalizar Revisión</span>
                                                            </button>
                                                        </>
                                                    )}

                                                    {/* Cerrada */}
                                                    {activeLookahead?.status === 'Cerrada' && (
                                                        <button
                                                            onClick={() => handleReopenLookahead(activeLookahead.id)}
                                                            className="bg-amber-50 text-amber-600 hover:bg-amber-100 border border-amber-200 px-3 py-1.5 rounded text-xs font-bold transition-colors flex items-center gap-1"
                                                        >
                                                            <span className="material-symbols-outlined text-[16px]">lock_open</span>
                                                            <span>Volver a Abrir</span>
                                                        </button>
                                                    )}

                                                    {/* Exportar (Oculto en En Planificación) */}
                                                    {activeLookahead?.status !== 'En Planificación' && (
                                                        <button onClick={() => exportLookaheadToExcel(activeLookahead.id)} className="px-3 py-1.5 border border-green-300 bg-green-50 text-green-700 rounded text-xs font-bold hover:bg-green-100 flex items-center gap-1">
                                                            <span className="material-symbols-outlined text-[16px]">download</span>
                                                            <span>Exportar tareas con Restricciones</span>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex-1 overflow-auto bg-slate-50 dark:bg-slate-950">
                                            <table className={`${isMobile ? 'hidden' : 'table'} w-full text-left border-collapse`}>
                                                <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10">
                                                    <tr>
                                                        <th className="px-3 py-3 text-xs font-bold text-slate-500 uppercase w-12 text-center">#</th>
                                                        <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Tarea Vigente</th>
                                                        <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase w-32">Inicio</th>
                                                        <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase w-32">Termino</th>
                                                        <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Restricciones Identificadas</th>
                                                        <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase w-32 text-center">No Aplica Restricción</th>
                                                        <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase w-10"></th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                                    {(() => {
                                                        if (!activeLookahead) return null;
                                                        let tasksInWindow = getTasksForLookahead(activeLookahead.startDate, activeLookahead.endDate, activeLookahead.constraints);

                                                        // Filter for ITE Mode
                                                        if (isITEView) {
                                                            tasksInWindow = tasksInWindow.filter(task => {
                                                                const isCleared = activeLookahead.clearedTaskIds?.includes(task.id);
                                                                const constraints = activeLookahead.constraints.filter(c => c.taskId === task.id);
                                                                const hasConstraints = constraints.length > 0;
                                                                const allConstraintsReleased = hasConstraints && constraints.every(c => c.status === 'Liberado');

                                                                // Task is executable if manually cleared OR has all constraints released
                                                                return isCleared || allConstraintsReleased;
                                                            });
                                                        }

                                                        if (tasksInWindow.length === 0) {
                                                            return <tr><td colSpan={7} className="p-8 text-center text-slate-400 italic">No hay tareas programadas para esta semana futura (o no cumplen criterio I.T.E).</td></tr>;
                                                        }

                                                        return tasksInWindow.map(task => {
                                                            const constraints = activeLookahead.constraints.filter(c => c.taskId === task.id);
                                                            const isCleared = activeLookahead.clearedTaskIds?.includes(task.id);
                                                            const hasBreakdown = task.children?.some(c => c.isWeeklySubtask && !c.isDeleted);
                                                            const isSubtask = !!task.isWeeklySubtask;

                                                            return (
                                                                <React.Fragment key={task.id}>
                                                                    <tr className={`${hasBreakdown ? 'bg-slate-100/80 dark:bg-slate-800/60 font-semibold' : isSubtask ? 'bg-white dark:bg-slate-900/50' : 'bg-slate-50/50 dark:bg-slate-800/30'}`}>
                                                                        <td className={`px-3 py-3 text-xs font-mono text-center font-bold ${isSubtask ? 'text-slate-400 dark:text-slate-500' : 'text-slate-500 dark:text-slate-400'}`}>
                                                                            {task.id_original_project || "-"}
                                                                        </td>
                                                                        <td className="px-4 py-3 text-sm font-bold text-slate-700 dark:text-slate-200">
                                                                            <div className={`flex items-center gap-2 ${isSubtask ? 'pl-6' : ''}`}>
                                                                                {isSubtask && (
                                                                                    <span className="material-symbols-outlined text-[16px] text-slate-400">
                                                                                        subdirectory_arrow_right
                                                                                    </span>
                                                                                )}
                                                                                <span>{cleanTaskName(task.name)}</span>
                                                                                {hasBreakdown && (() => {
                                                                                    const weeklySubtasks = (task.children || []).filter(c => c.isWeeklySubtask && !c.isDeleted);
                                                                                    const sumWeight = weeklySubtasks.reduce((sum, c) => sum + (c.weight !== undefined ? Number(c.weight) : 0), 0);
                                                                                    const roundedSum = Math.round(sumWeight * 100) / 100;
                                                                                    if (roundedSum < 100) {
                                                                                        return (
                                                                                            <span
                                                                                                className="text-[10px] bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400 px-1.5 py-0.5 rounded font-bold"
                                                                                                title="Falta distribuir ponderaciones (Saldo pendiente)"
                                                                                            >
                                                                                                Peso: {roundedSum}%
                                                                                            </span>
                                                                                        );
                                                                                    }
                                                                                    return null;
                                                                                })()}
                                                                                {isSubtask && task.weight !== undefined && (
                                                                                    <span className="text-[10px] bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-400 px-1.5 py-0.5 rounded font-bold">
                                                                                        Peso: {task.weight}%
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </td>
                                                                        <td className="px-4 py-3 text-xs text-slate-500">{formatDateToShow(task.start)}</td>
                                                                        <td className="px-4 py-3 text-xs text-slate-500">{formatDateToShow(task.end)}</td>
                                                                        <td className="px-4 py-3">
                                                                            {hasBreakdown ? (
                                                                                <span className="text-xs text-slate-400 dark:text-slate-500 italic flex items-center gap-1 font-normal">
                                                                                    <span className="material-symbols-outlined text-[14px]">account_tree</span>
                                                                                    Desglosada en subtareas
                                                                                </span>
                                                                            ) : (
                                                                                <button
                                                                                    onClick={() => handleOpenAddConstraintModal(task.id)}
                                                                                    disabled={activeLookahead.status === 'Cerrada'}
                                                                                    className={`text-xs font-bold flex items-center gap-1 ${activeLookahead.status === 'Cerrada' ? 'text-slate-300 cursor-not-allowed' : 'text-purple-600 hover:text-purple-700'}`}
                                                                                >
                                                                                    <span className="material-symbols-outlined text-[16px]">add_circle</span>
                                                                                    {isCleared ? 'Reabrir Restricción' : 'Agregar Restricción'}
                                                                                </button>
                                                                            )}
                                                                        </td>
                                                                        <td className="px-4 py-3 text-center">
                                                                            {hasBreakdown ? (
                                                                                <span className="text-xs text-slate-300 dark:text-slate-600 font-bold">-</span>
                                                                            ) : (
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={!!isCleared}
                                                                                    disabled={activeLookahead.status === 'Cerrada'}
                                                                                    onChange={() => handleToggleTaskCleared(activeLookahead.id, task.id)}
                                                                                    className="rounded border-slate-300 text-green-600 focus:ring-green-500 cursor-pointer size-4 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                />
                                                                            )}
                                                                        </td>
                                                                        <td></td>
                                                                    </tr>
                                                                    {constraints.length > 0 && (
                                                                        <tr className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                                                                            <td colSpan={7} className="p-0">
                                                                                <div className="pl-4 pr-4 py-2 bg-slate-50/50 dark:bg-slate-800/20">
                                                                                    <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm">
                                                                                        <table className="w-full text-left">
                                                                                            <thead className="bg-slate-100 dark:bg-slate-700/50 text-[10px] uppercase text-slate-500 font-bold tracking-wider border-b border-slate-200 dark:border-slate-700">
                                                                                                <tr>
                                                                                                    <th className="px-3 py-2 w-32">Tipo</th>
                                                                                                    <th className="px-3 py-2 w-48">Responsable</th>
                                                                                                    <th className="px-3 py-2 w-32">Fecha Requerida</th>
                                                                                                    <th className="px-3 py-2">Detalle</th>
                                                                                                    <th className="px-3 py-2 w-48 text-center">Unidades Físicas</th>
                                                                                                    <th className="px-3 py-2 w-32 text-center">Estado</th>
                                                                                                    <th className="px-3 py-2 w-10"></th>
                                                                                                </tr>
                                                                                            </thead>
                                                                                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                                                                                {constraints.map(c => {
                                                                                                    let typeName = 'General';
                                                                                                    constraintGroups.forEach(g => {
                                                                                                        const def = g.definitions.find(d => d.id === c.typeId);
                                                                                                        if (def) typeName = def.name;
                                                                                                    });
                                                                                                    const respName = responsibles.find(r => r.id === c.responsibleId)?.name || 'Sin asignar';

                                                                                                    return (
                                                                                                        <tr key={c.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                                                                                                            <td className="px-3 py-2">
                                                                                                                <span className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 text-[10px] font-bold px-2 py-0.5 rounded border border-purple-200 dark:border-purple-800 truncate block text-center">
                                                                                                                    {typeName}
                                                                                                                </span>
                                                                                                            </td>
                                                                                                            <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300 truncate" title={respName}>
                                                                                                                {respName}
                                                                                                            </td>
                                                                                                            <td className="px-3 py-2">
                                                                                                                <input
                                                                                                                    type="date"
                                                                                                                    disabled={activeLookahead.status === 'Cerrada'}
                                                                                                                    className={`text-xs border border-slate-300 dark:border-slate-600 rounded p-1 w-full text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-900 focus:ring-1 focus:ring-purple-500 outline-none ${activeLookahead.status === 'Cerrada' ? 'opacity-60 cursor-not-allowed' : ''}`}
                                                                                                                    value={c.requiredDate}
                                                                                                                    onChange={(e) => handleUpdateConstraint(activeLookahead.id, c.id, 'requiredDate', e.target.value)}
                                                                                                                />
                                                                                                            </td>
                                                                                                            <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">
                                                                                                                <div className="flex flex-col gap-1">
                                                                                                                    <span className="font-semibold">{c.description}</span>
                                                                                                                    <div className="flex flex-wrap gap-1.5 items-center mt-1">
                                                                                                                        {c.isReprogrammed && (
                                                                                                                            <span className="inline-flex items-center gap-0.5 bg-amber-50 text-amber-700 dark:bg-amber-950/20 dark:text-amber-400 text-[9px] font-black uppercase px-2 py-0.5 rounded border border-amber-200 dark:border-amber-800">
                                                                                                                                <span className="material-symbols-outlined text-[10px]">sync</span>
                                                                                                                                Reprogramada
                                                                                                                            </span>
                                                                                                                        )}
                                                                                                                        {c.isOverdue && (
                                                                                                                            <span className="inline-flex items-center gap-0.5 bg-red-50 text-red-700 dark:bg-red-950/20 dark:text-red-400 text-[9px] font-black uppercase px-2 py-0.5 rounded border border-red-200 dark:border-red-800">
                                                                                                                                <span className="material-symbols-outlined text-[10px]">error</span>
                                                                                                                                Vencida
                                                                                                                            </span>
                                                                                                                        )}
                                                                                                                        {c.originWeekName && (
                                                                                                                            <span className="text-[10px] text-slate-400 dark:text-slate-500 font-medium italic">
                                                                                                                                (Origen: {c.originWeekName})
                                                                                                                            </span>
                                                                                                                        )}
                                                                                                                    </div>
                                                                                                                </div>
                                                                                                            </td>
                                                                                                            <td className="px-3 py-2 text-xs text-center">
                                                                                                                {(!c.unitsType || c.unitsType === 'na') ? (
                                                                                                                    <span className="text-slate-400 italic">No aplica Unidades físicas</span>
                                                                                                                ) : (
                                                                                                                    <button
                                                                                                                        onClick={() => { setActiveConstraintForUnits(c.id); setUnitsViewModalOpen(true); }}
                                                                                                                        className="text-primary hover:underline font-bold"
                                                                                                                    >
                                                                                                                        Si aplica a Unidades Fisicas
                                                                                                                    </button>
                                                                                                                )}
                                                                                                            </td>
                                                                                                            <td className="px-3 py-2 text-center">
                                                                                                                <select
                                                                                                                    onChange={(e) => {
                                                                                                                        const newVal = e.target.value;
                                                                                                                        if (newVal === 'Liberado' || newVal === 'NO LIBERADO') {
                                                                                                                            setConstraintObsData({ planId: activeLookahead.id, constraintId: c.id, currentStatus: newVal, observations: c.observations || '', description: c.description });
                                                                                                                            setConstraintObsModalOpen(true);
                                                                                                                        } else {
                                                                                                                            handleUpdateConstraint(activeLookahead.id, c.id, 'status', newVal);
                                                                                                                            handleUpdateConstraint(activeLookahead.id, c.id, 'observations', '');
                                                                                                                        }
                                                                                                                    }}
                                                                                                                    disabled={activeLookahead.status === 'Cerrada' || !(() => {
                                                                                                                        const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager' || currentUser?.globalRole === 'Administrador';
                                                                                                                        const isAssigned = responsibles.find(r => r.id === c.responsibleId)?.email === currentUser?.email;
                                                                                                                        return isAdmin || isAssigned;
                                                                                                                    })()}
                                                                                                                    className={`w-full text-[10px] border rounded p-1 outline-none font-bold uppercase ${(activeLookahead.status !== 'Cerrada' && (() => {
                                                                                                                        const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager' || currentUser?.globalRole === 'Administrador';
                                                                                                                        const isAssigned = responsibles.find(r => r.id === c.responsibleId)?.email === currentUser?.email;
                                                                                                                        return isAdmin || isAssigned;
                                                                                                                    })()) ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
                                                                                                                        } ${c.status === 'Liberado' ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800' : 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800'}`}
                                                                                                                    value={c.status}
                                                                                                                >
                                                                                                                    <option value="Pendiente">Pendiente</option>
                                                                                                                    <option value="Liberado">Liberado</option>
                                                                                                                    <option value="NO LIBERADO">NO LIBERADO</option>
                                                                                                                </select>
                                                                                                            </td>
                                                                                                            <td className="px-3 py-2 text-center">
                                                                                                                <div className="flex items-center justify-center gap-1">
                                                                                                                    <button
                                                                                                                        onClick={() => handleOpenEditConstraintModal(c)}
                                                                                                                        disabled={activeLookahead.status === 'Cerrada' || !(() => {
                                                                                                                            const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager' || currentUser?.globalRole === 'Administrador';
                                                                                                                            const isAssigned = responsibles.find(r => r.id === c.responsibleId)?.email === currentUser?.email;
                                                                                                                            return isAdmin || isAssigned;
                                                                                                                        })()}
                                                                                                                        title="Editar Restricción"
                                                                                                                        className={`text-slate-400 hover:text-purple-600 ${(activeLookahead.status === 'Cerrada' || !(() => {
                                                                                                                            const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager' || currentUser?.globalRole === 'Administrador';
                                                                                                                            const isAssigned = responsibles.find(r => r.id === c.responsibleId)?.email === currentUser?.email;
                                                                                                                            return isAdmin || isAssigned;
                                                                                                                        })()) ? 'opacity-30 cursor-not-allowed' : ''}`}
                                                                                                                    >
                                                                                                                        <span className="material-symbols-outlined text-[16px]">edit</span>
                                                                                                                    </button>
                                                                                                                    <button
                                                                                                                        onClick={() => handleDeleteConstraint(activeLookahead.id, c.id)}
                                                                                                                        disabled={activeLookahead.status === 'Cerrada' || !(() => {
                                                                                                                            const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager' || currentUser?.globalRole === 'Administrador';
                                                                                                                            const isAssigned = responsibles.find(r => r.id === c.responsibleId)?.email === currentUser?.email;
                                                                                                                            return isAdmin || isAssigned;
                                                                                                                        })()}
                                                                                                                        title="Eliminar Restricción"
                                                                                                                        className={`text-slate-400 hover:text-red-500 ${(activeLookahead.status === 'Cerrada' || !(() => {
                                                                                                                            const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager' || currentUser?.globalRole === 'Administrador';
                                                                                                                            const isAssigned = responsibles.find(r => r.id === c.responsibleId)?.email === currentUser?.email;
                                                                                                                            return isAdmin || isAssigned;
                                                                                                                        })()) ? 'opacity-30 cursor-not-allowed' : ''}`}
                                                                                                                    >
                                                                                                                        <span className="material-symbols-outlined text-[16px]">delete</span>
                                                                                                                    </button>
                                                                                                                </div>
                                                                                                            </td>
                                                                                                        </tr>
                                                                                                    );
                                                                                                })}
                                                                                            </tbody>
                                                                                        </table>
                                                                                    </div>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    )}
                                                                </React.Fragment>
                                                            );
                                                        });
                                                    })()}
                                                </tbody>
                                            </table>

                                            {/* --- VISTA MÓVIL (PLANIFICACIÓN INTERMEDIA / ITE) --- */}
                                            <div className={`${isMobile ? 'block' : 'hidden'} p-4 space-y-4 pb-24`}>
                                                {/* Filtro Móvil ITE */}
                                                <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
                                                    <button
                                                        onClick={() => setMobileFilterITE('all')}
                                                        className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-colors ${mobileFilterITE === 'all' ? 'bg-white dark:bg-slate-700 shadow text-slate-900 dark:text-white flex items-center justify-center gap-1' : 'text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700/50 flex items-center justify-center gap-1'}`}
                                                    >
                                                        <span className="material-symbols-outlined text-[14px]">public</span> Todas
                                                    </button>
                                                    <button
                                                        onClick={() => setMobileFilterITE('mine')}
                                                        className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-colors ${mobileFilterITE === 'mine' ? 'bg-primary text-white shadow flex items-center justify-center gap-1' : 'text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700/50 flex items-center justify-center gap-1'}`}
                                                    >
                                                        <span className="material-symbols-outlined text-[14px]">person</span> Mías
                                                    </button>
                                                </div>

                                                {(() => {
                                                    if (!activeLookahead) return null;
                                                    let tasksInWindow = getTasksForLookahead(activeLookahead.startDate, activeLookahead.endDate, activeLookahead.constraints);

                                                    if (isITEView) {
                                                        tasksInWindow = tasksInWindow.filter(task => {
                                                            const isCleared = activeLookahead.clearedTaskIds?.includes(task.id);
                                                            const constraints = activeLookahead.constraints.filter(c => c.taskId === task.id);
                                                            const hasConstraints = constraints.length > 0;
                                                            const allConstraintsReleased = hasConstraints && constraints.every(c => c.status === 'Liberado');
                                                            return isCleared || allConstraintsReleased;
                                                        });
                                                    }

                                                    if (tasksInWindow.length === 0) {
                                                        return <div className="text-center text-slate-400 italic py-8 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">No hay tareas programadas para esta semana futura.</div>;
                                                    }

                                                    // Extract all constraints mapped with tasks
                                                    let mobileConstraints = tasksInWindow.flatMap(task => {
                                                        const constraints = activeLookahead.constraints.filter(c => c.taskId === task.id);
                                                        return constraints.map(c => ({ task, constraint: c }));
                                                    });

                                                    if (mobileFilterITE === 'mine' && currentUser) {
                                                        mobileConstraints = mobileConstraints.filter(mc => {
                                                            const resp = responsibles.find(r => r.id === mc.constraint.responsibleId);
                                                            return resp && resp.email === currentUser.email;
                                                        });
                                                    }

                                                    if (mobileConstraints.length === 0) {
                                                        return <div className="text-center text-slate-400 italic py-8 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">No se han registrado restricciones en las tareas vigentes, o no tienes restricciones asignadas.</div>;
                                                    }

                                                    return mobileConstraints.map(({ task, constraint: c }) => {
                                                        let typeName = 'General';
                                                        constraintGroups.forEach(g => {
                                                            const def = g.definitions.find(d => d.id === c.typeId);
                                                            if (def) typeName = def.name;
                                                        });
                                                        const respName = responsibles.find(r => r.id === c.responsibleId)?.name || 'Sin asignar';
                                                        const isLiberado = c.status === 'Liberado';

                                                        return (<div key={c.id} className={`rounded-xl shadow-sm flex items-center justify-between p-3 gap-3 border transition-colors ${isLiberado
                                                            ? 'bg-[#52be80] border-[#229954] text-slate-900 dark:bg-[#27ae60] dark:text-white dark:border-[#1e8449]'
                                                            : 'bg-[#85c1e9] border-[#2e86c1] text-slate-900 dark:bg-[#3498db] dark:text-white dark:border-[#2874a6]'
                                                            }`}>
                                                            <div className="flex-1 text-[11px] font-bold leading-snug">
                                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                                    {task.isWeeklySubtask && (
                                                                        <span className="material-symbols-outlined text-[13px] text-slate-400">subdirectory_arrow_right</span>
                                                                    )}
                                                                    <span>{cleanTaskName(task.name)}</span>
                                                                    {task.isWeeklySubtask && task.weight !== undefined && (
                                                                        <span className="text-[9px] bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300 px-1 py-0.2 rounded font-bold">
                                                                            Peso: {task.weight}%
                                                                        </span>
                                                                    )}
                                                                    <span className="text-slate-500 font-normal">| {c.requiredDate} | {typeName} | {respName}</span>
                                                                </div>
                                                                <div className="text-slate-700 dark:text-slate-200 mt-1">{c.description}</div>
                                                                <div className="flex flex-wrap gap-1.5 items-center mt-1">
                                                                    {c.isReprogrammed && (
                                                                        <span className="bg-amber-100 text-amber-850 dark:bg-amber-950/40 dark:text-amber-300 text-[9px] font-bold px-1.5 py-0.5 rounded border border-amber-300">
                                                                            Reprogramada
                                                                        </span>
                                                                    )}
                                                                    {c.isOverdue && (
                                                                        <span className="bg-red-100 text-red-850 dark:bg-red-950/40 dark:text-red-300 text-[9px] font-bold px-1.5 py-0.5 rounded border border-red-300">
                                                                            Vencida
                                                                        </span>
                                                                    )}
                                                                    {c.originWeekName && (
                                                                        <span className="text-[9px] text-slate-700 dark:text-slate-200 italic font-normal">
                                                                            (Origen: {c.originWeekName})
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-1.5 shrink-0">
                                                                <button
                                                                    onClick={() => {
                                                                        const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager' || currentUser?.globalRole === 'Administrador';
                                                                        const isAssigned = responsibles.find(r => r.id === c.responsibleId)?.email === currentUser?.email;
                                                                        if (!isAdmin && !isAssigned) return;

                                                                        if (!isLiberado) {
                                                                            setConstraintObsData({ planId: activeLookahead.id, constraintId: c.id, currentStatus: 'Liberado', observations: c.observations || '', description: c.description });
                                                                            setConstraintObsModalOpen(true);
                                                                        } else {
                                                                            handleUpdateConstraint(activeLookahead.id, c.id, 'status', 'Pendiente');
                                                                            handleUpdateConstraint(activeLookahead.id, c.id, 'observations', '');
                                                                        }
                                                                    }}
                                                                    disabled={activeLookahead.status === 'Cerrada' || !(() => {
                                                                        const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager' || currentUser?.globalRole === 'Administrador';
                                                                        const isAssigned = responsibles.find(r => r.id === c.responsibleId)?.email === currentUser?.email;
                                                                        return isAdmin || isAssigned;
                                                                    })()}
                                                                    className={`flex items-center justify-center font-black text-[10px] w-8 h-8 rounded shrink-0 border uppercase transition-colors ${(activeLookahead.status !== 'Cerrada' && (() => {
                                                                        const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager' || currentUser?.globalRole === 'Administrador';
                                                                        const isAssigned = responsibles.find(r => r.id === c.responsibleId)?.email === currentUser?.email;
                                                                        return isAdmin || isAssigned;
                                                                    })()) ? 'active:scale-95' : 'opacity-50 cursor-not-allowed'
                                                                        } ${isLiberado
                                                                            ? 'bg-[#d5f5e3] border-[#229954] text-[#1e8449] hover:bg-[#abebc6]'
                                                                            : 'bg-[#d6eaf8] border-[#2e86c1] text-[#2874a6] hover:bg-[#aed6f1]'
                                                                        }`}
                                                                >
                                                                    {isLiberado ? 'SI' : 'NO'}
                                                                </button>
                                                                <button
                                                                    onClick={() => handleOpenEditConstraintModal(c)}
                                                                    disabled={activeLookahead.status === 'Cerrada' || !(() => {
                                                                        const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager' || currentUser?.globalRole === 'Administrador';
                                                                        const isAssigned = responsibles.find(r => r.id === c.responsibleId)?.email === currentUser?.email;
                                                                        return isAdmin || isAssigned;
                                                                    })()}
                                                                    className={`flex items-center justify-center w-8 h-8 rounded shrink-0 hover:bg-black/10 transition-colors ${(activeLookahead.status === 'Cerrada' || !(() => {
                                                                        const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager' || currentUser?.globalRole === 'Administrador';
                                                                        const isAssigned = responsibles.find(r => r.id === c.responsibleId)?.email === currentUser?.email;
                                                                        return isAdmin || isAssigned;
                                                                    })()) ? 'opacity-30 cursor-not-allowed' : ''}`}
                                                                    title="Editar Restricción"
                                                                >
                                                                    <span className="material-symbols-outlined text-[18px]">edit</span>
                                                                </button>
                                                                <button className="flex items-center justify-center w-8 h-8 rounded shrink-0 hover:bg-black/10 transition-colors">
                                                                    <span className="material-symbols-outlined text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>chat_bubble</span>
                                                                </button>
                                                            </div>
                                                        </div>);
                                                    });
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    // Lookahead History View
                                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 flex-1 min-h-0 flex flex-col overflow-hidden">
                                        <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex justify-between items-center">
                                            <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider">Historial de Planificaciones</h3>
                                        </div>
                                        <div className="flex-1 overflow-auto">
                                            {lookaheadPlans.length === 0 ? (
                                                <div className="flex flex-col items-center justify-center h-full text-slate-400">
                                                    <span className="material-symbols-outlined text-4xl mb-2">calendar_month</span>
                                                    <p className="text-sm">No hay planificaciones intermedias generadas.</p>
                                                    <p className="text-xs mt-2 max-w-xs text-center">Utilice el botón "Generar Planificación Intermedia" para proyectar restricciones futuras.</p>
                                                </div>
                                            ) : (
                                                <>
                                                    <table className={`${isMobile ? 'hidden' : 'table'} w-full text-left border-collapse`}>
                                                        <thead className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                                                            <tr>
                                                                <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">N° Planificación</th>
                                                                <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Semana Objetivo</th>
                                                                <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Horizonte</th>
                                                                <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Estado</th>
                                                                <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Restricciones</th>
                                                                <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider text-center">PCR</th>
                                                                <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider text-right">Acciones</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                                            {[...lookaheadPlans].sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()).map((plan, index) => {
                                                                const pendingConstraints = plan.constraints.filter(c => c.status === 'Pendiente').length;
                                                                const notLiberadoConstraints = plan.constraints.filter(c => c.status === 'NO LIBERADO').length;
                                                                const totalConstraints = plan.constraints.length;
                                                                const closedConstraints = plan.constraints.filter(c => c.status === 'Liberado').length;
                                                                const pcr = totalConstraints > 0 ? Math.round((closedConstraints / totalConstraints) * 100) : 100;

                                                                return (
                                                                    <tr key={plan.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                                                        <td className="px-6 py-5">
                                                                            <span className="font-bold text-sm text-purple-700 dark:text-purple-400">Planificación Intermedia #{index + 1}</span>
                                                                        </td>
                                                                        <td className="px-6 py-5">
                                                                            <div className="flex flex-col">
                                                                                <span className="font-bold text-sm text-slate-700 dark:text-slate-300">{plan.startDate}</span>
                                                                                <span className="text-xs text-slate-500">al {plan.endDate}</span>
                                                                            </div>
                                                                        </td>
                                                                        <td className="px-6 py-5 text-sm text-slate-600 dark:text-slate-400">
                                                                            <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs font-bold">Vista a {plan.weeksAhead} semanas</span>
                                                                        </td>
                                                                        <td className="px-6 py-5">
                                                                            <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold shadow-sm ${plan.status === 'En Planificación' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                                                                plan.status === 'En Ejecución' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                                                                    plan.status === 'En Revisión' ? 'bg-yellow-400 text-slate-900 border border-yellow-500/20' :
                                                                                        'bg-slate-300 text-slate-600 border border-slate-400/20'
                                                                                }`}>
                                                                                {plan.status === 'Cerrada' ? 'Cerrado' : plan.status}
                                                                            </span>
                                                                        </td>
                                                                        <td className="px-6 py-5">
                                                                            {pendingConstraints > 0 || notLiberadoConstraints > 0 ? (
                                                                                <div className="flex flex-col gap-1">
                                                                                    {pendingConstraints > 0 && (
                                                                                        <span className="text-amber-600 font-bold text-xs flex items-center gap-1">
                                                                                            <span className="material-symbols-outlined text-[16px]">warning</span>
                                                                                            {pendingConstraints} Pendientes
                                                                                        </span>
                                                                                    )}
                                                                                    {notLiberadoConstraints > 0 && (
                                                                                        <span className="text-red-600 font-bold text-xs flex items-center gap-1">
                                                                                            <span className="material-symbols-outlined text-[16px]">cancel</span>
                                                                                            {notLiberadoConstraints} No Liberadas
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            ) : (
                                                                                <span className="text-green-600 font-bold text-xs flex items-center gap-1">
                                                                                    <span className="material-symbols-outlined text-[16px]">check_circle</span>
                                                                                    Todo Liberado
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                        <td className="px-6 py-5 text-center">
                                                                            <span className="font-bold text-sm text-slate-700 dark:text-slate-300">{pcr}%</span>
                                                                        </td>
                                                                        <td className="px-6 py-5 text-right flex justify-end">
                                                                            <button
                                                                                onClick={() => {
                                                                                    setEditingLookaheadId(plan.id);
                                                                                    setLookaheadRange({ start: plan.startDate, end: plan.endDate });
                                                                                    setLookaheadModalOpen(true);
                                                                                }}
                                                                                disabled={plan.status === 'Cerrada'}
                                                                                className={`text-slate-400 hover:text-purple-600 transition-colors p-2 hover:bg-slate-100 rounded-full ${plan.status === 'Cerrada' ? 'opacity-30 cursor-not-allowed hover:text-slate-400 hover:bg-transparent' : ''}`}
                                                                                title="Editar fechas de la planificación"
                                                                            >
                                                                                <span className="material-symbols-outlined text-[20px]">edit_calendar</span>
                                                                            </button>
                                                                            <button
                                                                                onClick={() => {
                                                                                    setLookaheadPlansBackup(JSON.parse(JSON.stringify(lookaheadPlans)));
                                                                                    setActiveLookaheadId(plan.id);
                                                                                }}
                                                                                className="text-slate-400 hover:text-purple-600 transition-colors p-2 hover:bg-slate-100 rounded-full"
                                                                                title="Ver Plan"
                                                                            >
                                                                                <span className="material-symbols-outlined text-[22px]">search</span>
                                                                            </button>
                                                                            {(hasPermission('pi_eliminar', 'view') || hasPermission('pi_eliminar', 'edit')) && (
                                                                                <button
                                                                                    onClick={() => {
                                                                                        if (hasPermission('pi_eliminar', 'edit')) {
                                                                                            handleDeleteLookahead(plan.id);
                                                                                        }
                                                                                    }}
                                                                                    disabled={!hasPermission('pi_eliminar', 'edit') || plan.status === 'Cerrada'}
                                                                                    className={`p-2 rounded-full transition-colors ${(!hasPermission('pi_eliminar', 'edit') || plan.status === 'Cerrada')
                                                                                        ? 'text-slate-200 cursor-not-allowed dark:text-slate-800'
                                                                                        : 'text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20'
                                                                                        }`}
                                                                                    title={plan.status === 'Cerrada' ? "Planificación cerrada (No se puede eliminar)" : !hasPermission('pi_eliminar', 'edit') ? "Eliminar Programación (Solo Lectura)" : "Eliminar Programación"}
                                                                                >
                                                                                    <span className="material-symbols-outlined text-[20px]">delete</span>
                                                                                </button>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                    <div className={`${isMobile ? 'block' : 'hidden'} p-3 space-y-2.5 pb-24`}>
                                                        {[...lookaheadPlans]
                                                            .filter(plan => !isMobile || isPlanVisibleInMobile(plan.status))
                                                            .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
                                                            .map((plan) => {
                                                                const pendingConstraints = plan.constraints.filter(c => c.status === 'Pendiente').length;
                                                                const notLiberadoConstraints = plan.constraints.filter(c => c.status === 'NO LIBERADO').length;

                                                                let lookaheadStatusLabel = 'En Planificación';
                                                                let lookaheadStatusClasses = 'bg-blue-100 text-slate-900 border border-blue-200/50';
                                                                if (plan.status === 'En Ejecución') {
                                                                    lookaheadStatusLabel = 'En Ejecución';
                                                                    lookaheadStatusClasses = 'bg-green-100 text-slate-900 border border-green-200/50';
                                                                } else if (plan.status === 'En Revisión') {
                                                                    lookaheadStatusLabel = 'En Revisión';
                                                                    lookaheadStatusClasses = 'bg-yellow-400 text-slate-900 border border-yellow-500/20';
                                                                } else if (plan.status === 'Cerrada' || (plan.status as any) === 'Cerrado') {
                                                                    lookaheadStatusLabel = 'Cerrado';
                                                                    lookaheadStatusClasses = 'bg-slate-300 text-slate-900 border border-slate-400/20';
                                                                }

                                                                return (
                                                                    <div key={plan.id} className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-2.5 flex flex-col gap-1.5">
                                                                        <div className="flex justify-between items-center min-h-[24px]">
                                                                            <h4 className="font-bold text-purple-700 dark:text-purple-400 text-sm leading-none flex items-center">{getLookaheadPlanTitle(plan.id)}</h4>
                                                                            <span className={`inline-flex items-center justify-center px-2.5 py-1 rounded-full text-[10px] font-bold shadow-sm leading-none translate-y-[2.5px] ${lookaheadStatusClasses}`}>
                                                                                {lookaheadStatusLabel}
                                                                            </span>
                                                                        </div>

                                                                        {/* Fila 2: Botón Ver / Editar a la izquierda (subido) | Restricciones apegadas a la derecha (bajadas) */}
                                                                        <div className="flex justify-between items-center">
                                                                            <div className="flex items-center gap-1.5 shrink-0 -translate-y-[2px]">
                                                                                <button
                                                                                    onClick={() => {
                                                                                        setLookaheadPlansBackup(JSON.parse(JSON.stringify(lookaheadPlans)));
                                                                                        setActiveLookaheadId(plan.id);
                                                                                    }}
                                                                                    className="w-auto h-[31px] bg-purple-600 hover:bg-purple-700 text-white font-bold px-3 rounded-lg text-xs flex items-center justify-center gap-1 shadow-sm active:scale-95 transition-all cursor-pointer"
                                                                                    title="Ver o editar planificación intermedia"
                                                                                >
                                                                                    <span className="material-symbols-outlined text-[15px]">touch_app</span>
                                                                                    <span>Ver / Editar</span>
                                                                                </button>
                                                                            </div>

                                                                            <div className="flex items-center gap-1.5 justify-end h-[31px] shrink-0 translate-y-[2px]">
                                                                                {pendingConstraints > 0 || notLiberadoConstraints > 0 ? (
                                                                                    <>
                                                                                        {pendingConstraints > 0 && (
                                                                                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-600 dark:text-amber-400">
                                                                                                <span className="material-symbols-outlined text-[14px]">warning</span> {pendingConstraints} Pendientes
                                                                                            </span>
                                                                                        )}
                                                                                        {notLiberadoConstraints > 0 && (
                                                                                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-red-600 dark:text-red-400">
                                                                                                <span className="material-symbols-outlined text-[14px]">cancel</span> {notLiberadoConstraints} No Liberadas
                                                                                            </span>
                                                                                        )}
                                                                                    </>
                                                                                ) : (
                                                                                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
                                                                                        <span className="material-symbols-outlined text-[14px]">check_circle</span> Liberadas
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : activeTab === 'semanal' ? (
                            weeklyGraphicMode ? (
                                <div className="h-full w-full p-4 flex flex-col">
                                    <div className="flex justify-between items-center mb-2 px-2">
                                        <div className="flex items-center gap-2">
                                            <span className="material-symbols-outlined text-blue-600">map</span>
                                            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200">
                                                Avance Gráfico {activePlan ? `(Semana #${activePlan.id}: ${activePlan.startDate} al ${activePlan.endDate})` : '(Historial / General)'}
                                            </h2>
                                        </div>
                                        <button
                                            onClick={() => setWeeklyGraphicMode(false)}
                                            className="px-3 py-1.5 border border-slate-300 dark:border-slate-700 rounded text-slate-600 dark:text-slate-300 text-xs font-bold hover:bg-slate-50 dark:hover:bg-slate-800 transition-all flex items-center gap-1"
                                        >
                                            <span className="material-symbols-outlined text-[16px]">close</span>
                                            <span>Cerrar Visor</span>
                                        </button>
                                    </div>
                                    <div className="flex-1 min-h-0 relative overflow-hidden">
                                        <GraphicProgressViewer
                                            activeProjectId={projectInfo.id}
                                            tasks={processedTasks}
                                            units={units}
                                            levels={levels}
                                            matrixData={matrixData}
                                            protocolConfigs={protocolConfigs}
                                            projectInfo={projectInfo}
                                            savedProtocols={savedProtocols}
                                            weeklyPlans={weeklyPlans}
                                            responsibles={responsibles}
                                            selectedTaskId={graphicSelectedTaskId}
                                            activePlanId={activePlanId}
                                            initialMode={weeklyGraphicViewType}
                                            isFullscreen={isWeeklyGraphicExpanded}
                                            onToggleFullscreen={() => setIsWeeklyGraphicExpanded(prev => !prev)}
                                            onClose={() => {
                                                setWeeklyGraphicMode(false);
                                                setIsWeeklyGraphicExpanded(false);
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="h-full p-2 md:p-6 flex flex-col">
                                    {/* ... (Existing Weekly Plan View Code) ... */}
                                    {activePlanId ? (
                                        // Plan Detail View
                                        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 flex-1 min-h-0 flex flex-col overflow-hidden">
                                            <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50">
                                                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                                                    <div className="w-full md:w-auto">
                                                        <div className="flex flex-col md:flex-row md:items-center gap-x-6 gap-y-2">
                                                            <div>
                                                                <div className="flex items-center gap-2 md:gap-3">
                                                                    <button
                                                                        onClick={() => {
                                                                            if (isWeeklyEditing || (activePlanId !== null && activePlan?.status !== 'Cerrada')) {
                                                                                setExitWarningTarget('semanal');
                                                                                setShowExitWarningModal(true);
                                                                            } else {
                                                                                setActivePlanId(null);
                                                                            }
                                                                        }}
                                                                        className="text-slate-400 hover:text-slate-600"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[20px] md:text-[24px]">arrow_back</span>
                                                                    </button>
                                                                    <div className="flex items-center gap-2">
                                                                        <h2 className="text-base md:text-lg font-bold text-slate-800 dark:text-white">Planificación #{activePlan?.id}</h2>
                                                                        <PlanStatusDot status={activePlan?.status} title={activePlan?.status} />
                                                                    </div>
                                                                    {activePlan && (
                                                                        <button
                                                                            onClick={() => {
                                                                                setAttendanceModalPlan(activePlan);
                                                                                setAttendanceModalOpen(true);
                                                                            }}
                                                                            className={`p-1.5 rounded-lg border transition-all flex items-center justify-center cursor-pointer ${isPlanAttendanceDone(activePlan.id)
                                                                                    ? 'bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800'
                                                                                    : 'bg-white dark:bg-slate-800 text-slate-500 border-slate-200 dark:border-slate-700 hover:text-slate-800'
                                                                                }`}
                                                                            title={isPlanAttendanceDone(activePlan.id) ? "Registro de Asistencia (Completado)" : "Registro de Asistencia (Pendiente)"}
                                                                        >
                                                                            <span className="material-symbols-outlined text-[18px]">how_to_reg</span>
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        onClick={() => {
                                                                            window.dispatchEvent(new CustomEvent('klyd_sync_refresh'));
                                                                        }}
                                                                        className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white transition-all flex items-center justify-center cursor-pointer active:scale-95 shadow-sm"
                                                                        title="Actualizar datos"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[18px]">refresh</span>
                                                                    </button>
                                                                </div>
                                                                <div className="flex items-center mt-1 text-xs md:text-sm ml-7 md:ml-9">
                                                                    <p className="text-slate-500 dark:text-slate-400">Fecha: <span className="font-medium text-slate-900 dark:text-white">{activePlan?.startDate}</span> - <span className="font-medium text-slate-900 dark:text-white">{activePlan?.endDate}</span></p>
                                                                </div>
                                                            </div>
                                                            {activePlan && (() => {
                                                                const progVal = calculateWeeklyGlobalProgrammedProgress();
                                                                const metaGantt = calculateWeeklyGlobalGanttMeta();
                                                                const recoveryMeta = calculateWeeklyRecoveryMeta(metaGantt);
                                                                const hasDelay = projectStatus.delay > 0;
                                                                const targetMeta = hasDelay ? recoveryMeta : metaGantt;
                                                                const meetsTarget = progVal >= targetMeta;

                                                                const dotColorClass = meetsTarget ? 'bg-emerald-500' : 'bg-rose-500';

                                                                return (
                                                                    <div
                                                                        id="weekly-progress-vertical-panel"
                                                                        className="hidden md:flex flex-col gap-1 text-xs font-medium text-slate-500 dark:text-slate-400 ml-7 md:ml-0 md:border-l border-slate-200 dark:border-slate-700 md:pl-6 pl-0 select-none"
                                                                        title="Muestra la relación entre lo que estás comprometiendo esta semana vs. lo exigido por la Carta Gantt Maestra para cumplir los plazos contractuales."
                                                                    >
                                                                        <div className="flex items-center gap-2">
                                                                            <span className={`w-2 h-2 rounded-full ${dotColorClass}`} />
                                                                            <span>Programado: <span className="font-bold text-slate-800 dark:text-white">{truncateProgress(progVal, 2)}%</span></span>
                                                                        </div>
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="w-2 h-2 rounded-full bg-blue-500" />
                                                                            <span>Meta Gantt: <span className="font-bold text-slate-800 dark:text-white">{truncateProgress(metaGantt, 2)}%</span></span>
                                                                        </div>
                                                                        {hasDelay && (
                                                                            <div className="flex items-center gap-2">
                                                                                <span className="w-2 h-2 rounded-full bg-amber-500" />
                                                                                <span>Meta Recuperación: <span className="font-bold text-slate-800 dark:text-white">{truncateProgress(recoveryMeta, 2)}%</span></span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-3 md:gap-4 w-full md:w-auto justify-end">
                                                        <div className="hidden md:block text-right">
                                                            <p className="text-[10px] md:text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Estado</p>
                                                            <p className={`text-xs md:text-sm font-bold ${isPlanningStatus(activePlan?.status) ? 'text-blue-600' :
                                                                isExecutionStatus(activePlan?.status) ? 'text-green-600' :
                                                                    isRevisionStatus(activePlan?.status) ? 'text-amber-500 font-extrabold' :
                                                                        'text-slate-600'
                                                                }`}>{isRevisionStatus(activePlan?.status) ? 'Revisión de Planificación' : activePlan?.status}</p>
                                                        </div>

                                                        {hasMissingCNC && (
                                                            <div className="bg-red-100 text-red-700 px-2 py-1 md:px-3 rounded-full text-[10px] md:text-xs font-bold flex items-center gap-1 animate-pulse border border-red-200">
                                                                <span className="material-symbols-outlined text-[14px] md:text-[16px]">warning</span>
                                                                <span className="hidden md:inline">Falta completar C.N.C</span>
                                                                <span className="inline md:hidden">C.N.C pendiente</span>
                                                            </div>
                                                        )}

                                                        {/* En Planificación */}
                                                        {isPlanningStatus(activePlan?.status) && (
                                                            <>
                                                                {!isMobile && (hasPermission('ps_gantt_avance', 'view') || hasPermission('ps_gantt_avance', 'edit')) && (
                                                                    <button
                                                                        onClick={toggleGanttSuggestions}
                                                                        className={`px-2 md:px-3 py-1.5 rounded text-[11px] md:text-sm font-bold transition-all border flex items-center gap-1.5 ${!hasPermission('ps_gantt_avance', 'edit')
                                                                            ? 'bg-slate-100/70 dark:bg-slate-800/30 text-slate-400 border-slate-200 dark:border-slate-800 cursor-pointer opacity-75 shadow-none'
                                                                            : showGanttSuggestions
                                                                                ? 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600 shadow-md animate-pulse'
                                                                                : 'bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-200'
                                                                            }`}
                                                                        title={!hasPermission('ps_gantt_avance', 'edit') ? "Visualizar sugerencias (Solo Lectura)" : "Avance por Carta Gantt"}
                                                                    >
                                                                        <span className="material-symbols-outlined text-[16px] md:text-[18px]">
                                                                            {!hasPermission('ps_gantt_avance', 'edit') ? 'visibility' : 'auto_awesome'}
                                                                        </span>
                                                                        <span>Avance por Carta Gantt</span>
                                                                        {!hasPermission('ps_gantt_avance', 'edit') && (
                                                                            <span className="text-[9px] uppercase font-black bg-slate-200 dark:bg-slate-700 px-1 py-0.5 rounded text-slate-500">Solo Ver</span>
                                                                        )}
                                                                    </button>
                                                                )}
                                                                <button
                                                                    onClick={handlePlanAllReprogrammed}
                                                                    className="bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 hover:bg-amber-100 border border-amber-300 dark:border-amber-700 px-2 md:px-3 py-1.5 rounded text-[11px] md:text-sm font-bold transition-colors flex items-center gap-1.5"
                                                                    title="Ver y programar tareas no ejecutadas de planificaciones anteriores"
                                                                >
                                                                    <span className="material-symbols-outlined text-[16px] md:text-[18px] text-amber-600 dark:text-amber-400">update</span>
                                                                    <span>Re-Programadas</span>
                                                                    {(() => {
                                                                        const count = getReprogrammedAllocations().length;
                                                                        return count > 0 ? (
                                                                            <span className="ml-1 bg-amber-500 text-white text-[10px] font-black px-1.5 py-0.2 rounded-full">
                                                                                {count}
                                                                            </span>
                                                                        ) : null;
                                                                    })()}
                                                                </button>
                                                                {!isMobile && (
                                                                    <button
                                                                        onClick={() => {
                                                                            setEditingAdditionalIndex(null);
                                                                            setAdditionalTaskRows([createEmptyAdditionalRow()]);
                                                                            setAdditionalModalOpen(true);
                                                                        }}
                                                                        className="bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200 px-2 md:px-3 py-1.5 rounded text-[11px] md:text-sm font-bold transition-colors flex items-center gap-1.5"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[16px] md:text-[18px]">add_circle</span>
                                                                        <span>Tareas adicionales</span>
                                                                    </button>
                                                                )}
                                                                {hasPermission('ps_publicar', 'edit') && (
                                                                    <button
                                                                        onClick={() => handlePublishPlan(activePlan.id)}
                                                                        className="bg-green-600 hover:bg-green-700 text-white px-2 md:px-3 py-1.5 rounded text-[11px] md:text-sm font-bold transition-colors flex items-center gap-1 cursor-pointer"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[16px] md:text-[18px]">send</span>
                                                                        <span>Publicar Planificación</span>
                                                                    </button>
                                                                )}
                                                            </>
                                                        )}

                                                        {/* En Ejecución */}
                                                        {isExecutionStatus(activePlan?.status) && (
                                                            <>
                                                                {((isMobile ? hasPermission("movil_ps_revisar", "view") : hasPermission("ps_revisar", "view"))) && (
                                                                    <button
                                                                        onClick={() => {
                                                                            const canEdit = isMobile ? hasPermission("movil_ps_revisar", "edit") : hasPermission("ps_revisar", "edit");
                                                                            if (!canEdit) return;
                                                                            handleStartReview(activePlan.id);
                                                                        }}
                                                                        disabled={!(isMobile ? hasPermission("movil_ps_revisar", "edit") : hasPermission("ps_revisar", "edit"))}
                                                                        title={!(isMobile ? hasPermission("movil_ps_revisar", "edit") : hasPermission("ps_revisar", "edit")) ? "No tienes permisos para modificar o iniciar la revisión" : "Revisar Planificación Semanal"}
                                                                        className={`bg-amber-50 text-amber-600 hover:bg-amber-100 border border-amber-200 px-2 md:px-3 py-1.5 rounded text-[11px] md:text-sm font-bold transition-colors ${!(isMobile ? hasPermission("movil_ps_revisar", "edit") : hasPermission("ps_revisar", "edit")) ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                                                                            }`}
                                                                    >
                                                                        Revisar Planificación Semanal
                                                                    </button>
                                                                )}
                                                                {!isMobile && (
                                                                    <button
                                                                        onClick={() => handleRevertToDraft(activePlan.id)}
                                                                        className="hidden md:flex bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-300 px-2 md:px-3 py-1.5 rounded text-[11px] md:text-sm font-bold transition-colors items-center gap-1 cursor-pointer"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[16px] md:text-[18px]">edit_note</span>
                                                                        <span>Editar Planificación</span>
                                                                    </button>
                                                                )}
                                                            </>
                                                        )}

                                                        {/* En Revisión */}
                                                        {isRevisionStatus(activePlan?.status) && (
                                                            <div className="flex flex-wrap gap-2 items-center">
                                                                <button onClick={() => handleBackToExecution(activePlan.id)} className="bg-white text-slate-600 hover:bg-slate-100 border border-slate-300 px-2 md:px-3 py-1.5 rounded text-[11px] md:text-sm font-bold transition-colors flex items-center gap-1 cursor-pointer">
                                                                    <span className="material-symbols-outlined text-[16px] md:text-[18px]">arrow_back</span>
                                                                    <span className="hidden md:inline">Volver a Ejecución</span>
                                                                </button>
                                                                {!isMobile && (
                                                                    <button onClick={() => handleRevertToDraft(activePlan.id)} className="hidden md:flex bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-300 px-2 md:px-3 py-1.5 rounded text-[11px] md:text-sm font-bold transition-colors items-center gap-1 cursor-pointer">
                                                                        <span className="material-symbols-outlined text-[16px] md:text-[18px]">edit_note</span>
                                                                        <span>Editar Planificación</span>
                                                                    </button>
                                                                )}
                                                                <button onClick={() => handleSaveReview(activePlan.id)} className="bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 px-2 md:px-3 py-1.5 rounded text-[11px] md:text-sm font-bold transition-colors flex items-center gap-1 cursor-pointer">
                                                                    <span className="material-symbols-outlined text-[16px] md:text-[18px]">save</span>
                                                                    <span className="hidden md:inline">Guardar</span>
                                                                </button>
                                                                <button
                                                                    onClick={() => handleFinalClosePlan(activePlan.id)}
                                                                    disabled={hasMissingCNC}
                                                                    className={`
                                                                px-2 md:px-3 py-1.5 rounded text-[11px] md:text-sm font-bold transition-all border flex items-center gap-1 cursor-pointer
                                                                ${hasMissingCNC
                                                                            ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                                                                            : 'bg-green-50 text-green-600 hover:bg-green-100 border-green-200 hover:shadow-sm'
                                                                        }
                                                            `}
                                                                >
                                                                    <span className="material-symbols-outlined text-[16px] md:text-[18px]">check_circle</span>
                                                                    Finalizar Revisión
                                                                </button>
                                                            </div>
                                                        )}

                                                        {/* Cerrada */}
                                                        {isClosedStatus(activePlan?.status) && (
                                                            <button
                                                                onClick={() => handleReopenPlan(activePlan.id)}
                                                                className="bg-amber-50 text-amber-600 hover:bg-amber-100 border border-amber-200 px-2 md:px-3 py-1.5 rounded text-[11px] md:text-sm font-bold transition-colors flex items-center gap-1 cursor-pointer"
                                                            >
                                                                <span className="material-symbols-outlined text-[16px] md:text-[18px]">lock_open</span>
                                                                <span>Volver a Abrir</span>
                                                            </button>
                                                        )}

                                                        {/* Botón de Exportar (Oculto en En Planificación y en App/Móvil) */}
                                                        {!isPlanningStatus(activePlan?.status) && !isMobile && (
                                                            <button
                                                                onClick={() => exportWeeklyPlanToExcel(activePlan!.id)}
                                                                className="hidden md:flex bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 px-2 md:px-3 py-1.5 rounded text-[11px] md:text-sm font-bold transition-colors items-center gap-1 cursor-pointer"
                                                            >
                                                                <span className="material-symbols-outlined text-[16px] md:text-[18px]">download</span>
                                                                <span>Exportar las Tareas</span>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            {(() => {
                                                return (
                                                    <div className="flex-1 overflow-auto bg-slate-50 dark:bg-slate-950">
                                                        {showGanttSuggestions && activePlan && (hasPermission('ps_gantt_avance', 'view') || hasPermission('ps_gantt_avance', 'edit')) && (
                                                            <div className="p-4 bg-blue-50/75 dark:bg-blue-950/20 border-b border-blue-200 dark:border-blue-900/50 animate-in slide-in-from-top-4 duration-300">
                                                                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="material-symbols-outlined text-blue-600 dark:text-blue-400 text-[20px] md:text-[24px]">auto_awesome</span>
                                                                        <div>
                                                                            <h3 className="text-xs md:text-sm font-extrabold text-blue-800 dark:text-blue-400 uppercase tracking-wide">
                                                                                Partidas Sugeridas por Carta Gantt
                                                                            </h3>
                                                                            <p className="text-[10px] md:text-xs text-blue-600/80 dark:text-blue-400/80 font-medium">
                                                                                Programadas según Carta Gantt para esta semana y pendientes de asignación.
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                    <button
                                                                        onClick={handleOpenAutoPlanModal}
                                                                        disabled={!hasPermission('ps_gantt_avance', 'edit')}
                                                                        className={`px-3 md:px-4 py-2 text-[11px] font-black uppercase tracking-wider rounded-xl transition-all flex items-center gap-1.5 self-stretch sm:self-auto justify-center ${!hasPermission('ps_gantt_avance', 'edit')
                                                                            ? 'bg-slate-200 dark:bg-slate-800 text-slate-400 border border-slate-300 dark:border-slate-700 cursor-not-allowed opacity-50 shadow-none pointer-events-none'
                                                                            : 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500 text-white shadow-sm hover:shadow active:scale-95'
                                                                            }`}
                                                                    >
                                                                        <span className="material-symbols-outlined text-[16px]">checklist</span>
                                                                        Auto-Programar Sugeridas
                                                                    </button>
                                                                </div>

                                                                {(() => {
                                                                    const suggestedTasks = getGanttSuggestedTasks();

                                                                    if (suggestedTasks.length === 0) {
                                                                        return (
                                                                            <div className="flex items-center gap-2 text-xs text-slate-555 italic p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                                                                                <span className="material-symbols-outlined text-slate-400 text-[18px]">info</span>
                                                                                No hay partidas proyectadas o rezagadas pendientes de programar.
                                                                            </div>
                                                                        );
                                                                    }

                                                                    return (
                                                                        <div className="columns-1 sm:columns-2 lg:columns-3 gap-3">
                                                                            {suggestedTasks.map(t => {
                                                                                const isAlreadyScheduled = activePlan.allocations.some(a => a.taskId === t.id);
                                                                                const isRezagada = t.end && activePlan && t.end < activePlan.startDate;
                                                                                const ganttNumber = t.id_original_project;

                                                                                let cardBgClass = "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-blue-400 dark:hover:border-blue-500 shadow-sm";
                                                                                if (isAlreadyScheduled) {
                                                                                    cardBgClass = "bg-emerald-50/50 dark:bg-emerald-950/10 border-emerald-200 dark:border-emerald-900/30";
                                                                                } else if (isRezagada) {
                                                                                    cardBgClass = "bg-red-50/40 dark:bg-red-950/10 border-red-200 dark:border-red-900/30 hover:border-red-400 dark:hover:border-red-555 shadow-sm";
                                                                                }

                                                                                const hasGanttNumber = ganttNumber !== undefined && ganttNumber !== null && ganttNumber !== '';

                                                                                return (
                                                                                    <div
                                                                                        key={t.id}
                                                                                        className={`p-3 rounded-xl border inline-flex w-full justify-between items-center gap-3 transition-all mb-3 break-inside-avoid ${cardBgClass}`}
                                                                                    >
                                                                                        <div className="min-w-0 flex-1">
                                                                                            <div className="flex items-center gap-1.5 mb-0.5">
                                                                                                {hasGanttNumber && (
                                                                                                    <span className={`flex-shrink-0 size-5 rounded-full text-[10px] font-black flex items-center justify-center border ${isAlreadyScheduled
                                                                                                            ? 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-955/40 dark:text-emerald-400 dark:border-emerald-900/30'
                                                                                                            : isRezagada
                                                                                                                ? 'bg-red-100 text-red-800 border-red-200 dark:bg-red-955/40 dark:text-red-400 dark:border-red-900/30'
                                                                                                                : 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-955/40 dark:text-blue-400 dark:border-blue-900/30'
                                                                                                        }`} title={`Línea Gantt: ${ganttNumber}`}>
                                                                                                        {ganttNumber}
                                                                                                    </span>
                                                                                                )}
                                                                                                <span className={`text-xs font-bold truncate ${isRezagada && !isAlreadyScheduled ? 'text-red-750 dark:text-red-400 font-extrabold' : 'text-slate-800 dark:text-slate-200'}`} title={t.name}>
                                                                                                    {t.name}
                                                                                                </span>
                                                                                                {t.isWeeklySubtask && (() => {
                                                                                                    const parent = findParentTask(t.id, tasks);
                                                                                                    return parent ? (
                                                                                                        <span className="text-[9px] bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 px-1 py-0.5 rounded font-semibold truncate max-w-[110px]" title={`Partida: ${parent.name}`}>
                                                                                                            {parent.name}
                                                                                                        </span>
                                                                                                    ) : null;
                                                                                                })()}
                                                                                            </div>
                                                                                            <span className={`text-[10px] text-slate-450 dark:text-slate-500 block mt-0.5 font-medium ${hasGanttNumber ? 'pl-[26px]' : ''}`}>
                                                                                                {isRezagada ? (
                                                                                                    <span className="text-red-650 dark:text-red-455 font-bold flex items-center gap-1">
                                                                                                        <span className="material-symbols-outlined text-[13px] text-red-500">warning</span>
                                                                                                        ATRASADA (Fin: {t.end})
                                                                                                    </span>
                                                                                                ) : (
                                                                                                    `Proyectada: ${t.start} al ${t.end}`
                                                                                                )}
                                                                                            </span>
                                                                                        </div>
                                                                                        <div className="flex-shrink-0">
                                                                                            {isAlreadyScheduled ? (
                                                                                                <span className="text-[10px] font-black uppercase text-emerald-600 dark:text-emerald-400 flex items-center gap-0.5">
                                                                                                    <span className="material-symbols-outlined text-[14px]">check</span> Programada
                                                                                                </span>
                                                                                            ) : (
                                                                                                <button
                                                                                                    onClick={() => openPlanTaskModal(t.id)}
                                                                                                    disabled={!hasPermission('ps_gantt_avance', 'edit')}
                                                                                                    className={`px-2.5 py-1.5 text-[10px] font-bold uppercase rounded-lg border transition-colors ${!hasPermission('ps_gantt_avance', 'edit')
                                                                                                        ? 'bg-slate-100 dark:bg-slate-800/50 text-slate-400 border-slate-200 dark:border-slate-800 cursor-not-allowed opacity-50 pointer-events-none shadow-none'
                                                                                                        : isRezagada
                                                                                                            ? 'bg-red-50 hover:bg-red-100 dark:bg-red-955/40 dark:hover:bg-red-900/60 text-red-700 dark:text-red-400 border-red-200 dark:border-red-900/50'
                                                                                                            : 'bg-blue-50 hover:bg-blue-100 dark:bg-blue-955/40 dark:hover:bg-blue-900/60 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-900/50'
                                                                                                        }`}
                                                                                                >
                                                                                                    Programar
                                                                                                </button>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </div>
                                                        )}
                                                        {(() => {
                                                            const filteredAndSortedAllocations = (() => {
                                                                if (!activePlan?.allocations) return [];
                                                                let list = activePlan.allocations.map((alloc, originalIdx) => {
                                                                    const unit = units.find(u => u.id === alloc.unitId);
                                                                    const unitName = unit?.name || 'N/A';
                                                                    const levelName = levels.find(l => l.id === unit?.levelId)?.name || '-';
                                                                    const taskName = alloc.isAdditional ? (alloc.additionalTaskName || 'Tarea Adicional') : (findTaskName(alloc.taskId, tasks) || '[Partida Obsoleta/Fuera de Gantt]');
                                                                    const responsibleName = responsibles.find(r => r.id === alloc.responsibleId)?.name || '-';
                                                                    const taskObjForRow = !alloc.isAdditional ? findTaskById(alloc.taskId, tasks) : undefined;
                                                                    const parentTaskForRow = !alloc.isAdditional ? findParentTask(alloc.taskId, tasks) : null;
                                                                    const targetTaskIdForRow = (taskObjForRow?.isWeeklySubtask && parentTaskForRow) ? parentTaskForRow.id : alloc.taskId;
                                                                    const config = !alloc.isAdditional ? (protocolConfigs[alloc.taskId] || protocolConfigs[targetTaskIdForRow]) : null;
                                                                    const appliesProtocol = config?.applies ?? false;
                                                                    const accumulatedCompliance = !alloc.isAdditional
                                                                        ? getTaskUnitAccumulatedCompliance(alloc.taskId, alloc.unitId, tasks, weeklyPlans, protocolConfigs, savedProtocols, undefined, activePlan.id)
                                                                        : 0;
                                                                    const isProtocolClosed = !alloc.isAdditional && appliesProtocol
                                                                        ? (isProtocolProgressedLocal(alloc.taskId, alloc.unitId) || isProtocolProgressedLocal(targetTaskIdForRow, alloc.unitId))
                                                                        : false;
                                                                    const pctComprometido = Number((alloc.percentComprometido ?? (alloc.executed ? 100 : 100)).toFixed(2));
                                                                    let pctCumplimiento = getAllocationCompliancePct(alloc);

                                                                    const statusVal = getStatusValue(alloc);
                                                                    const statusLabel = statusVal === 'si' ? 'Ejecutado' : statusVal === 'no' ? 'No Ejecutado' : 'Pendiente';

                                                                    let cncLabel = 'No Aplica';
                                                                    if (statusVal !== 'si') {
                                                                        const cause = cncGroups.flatMap(g => g.causes).find(c => c.id === alloc.cncId);
                                                                        cncLabel = cause?.name || 'Sin C.N.C';
                                                                    }

                                                                    return {
                                                                        alloc,
                                                                        originalIdx,
                                                                        values: {
                                                                            level: levelName,
                                                                            unit: unitName,
                                                                            task: taskName,
                                                                            responsible: responsibleName,
                                                                            comprometido: `${pctComprometido}%`,
                                                                            comprometidoNum: pctComprometido,
                                                                            estado: statusLabel,
                                                                            cumplimiento: `${pctCumplimiento}%`,
                                                                            cumplimientoNum: pctCumplimiento,
                                                                            cnc: cncLabel
                                                                        }
                                                                    };
                                                                });

                                                                // Aplicar filtros por columna
                                                                list = list.filter(item => {
                                                                    for (const colKey of Object.keys(weeklyColFilters) as WeeklyAllocColKey[]) {
                                                                        const filter = weeklyColFilters[colKey];
                                                                        if (!filter) continue;
                                                                        const cellVal = item.values[colKey] || '';
                                                                        if (filter.search && !cellVal.toLowerCase().includes(filter.search.toLowerCase())) {
                                                                            return false;
                                                                        }
                                                                        if (filter.selected !== undefined && !filter.selected.includes(cellVal)) {
                                                                            return false;
                                                                        }
                                                                    }
                                                                    return true;
                                                                });

                                                                // Aplicar ordenamiento
                                                                if (weeklySortColumn && weeklySortDirection) {
                                                                    list.sort((a, b) => {
                                                                        if (weeklySortColumn === 'comprometido') {
                                                                            const diff = a.values.comprometidoNum - b.values.comprometidoNum;
                                                                            return weeklySortDirection === 'asc' ? diff : -diff;
                                                                        }
                                                                        if (weeklySortColumn === 'cumplimiento') {
                                                                            const diff = a.values.cumplimientoNum - b.values.cumplimientoNum;
                                                                            return weeklySortDirection === 'asc' ? diff : -diff;
                                                                        }
                                                                        const valA = a.values[weeklySortColumn] || '';
                                                                        const valB = b.values[weeklySortColumn] || '';
                                                                        const comp = valA.localeCompare(valB, 'es', { numeric: true, sensitivity: 'base' });
                                                                        return weeklySortDirection === 'asc' ? comp : -comp;
                                                                    });
                                                                }

                                                                return list;
                                                            })();

                                                            return (
                                                                <table className={`${isMobile ? 'hidden' : 'table'} w-full text-left border-collapse`}>
                                                                    <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10">
                                                                        <tr>
                                                                            {renderWeeklyHeaderCell('level', 'Nivel')}
                                                                            {renderWeeklyHeaderCell('unit', 'Unidades Físicas')}
                                                                            {renderWeeklyHeaderCell('task', 'Tarea')}
                                                                            {renderWeeklyHeaderCell('responsible', 'Responsable')}
                                                                            {dynamicDayColumns.map((col) => (
                                                                                <th
                                                                                    key={col.key}
                                                                                    className="px-2 py-1.5 text-xs font-semibold text-slate-500 text-center w-10 border-x border-slate-200/60 dark:border-slate-700/60 bg-slate-50 dark:bg-slate-800/90 select-none"
                                                                                >
                                                                                    <div className="flex flex-col items-center justify-center leading-tight">
                                                                                        <span className="text-[11px] font-black uppercase text-slate-700 dark:text-slate-200">{col.dayLetter}</span>
                                                                                        <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 font-mono mt-0.5">{col.dateNum}</span>
                                                                                    </div>
                                                                                </th>
                                                                            ))}
                                                                            {renderWeeklyHeaderCell('comprometido', '% Comprometido', 'w-32 text-center bg-slate-50 dark:bg-slate-800')}
                                                                            {(isExecutionStatus(activePlan?.status) || isRevisionStatus(activePlan?.status) || isClosedStatus(activePlan?.status)) && (
                                                                                renderWeeklyHeaderCell('estado', 'Estado', 'w-32 text-center bg-slate-50 dark:bg-slate-800')
                                                                            )}
                                                                            {renderWeeklyHeaderCell('cumplimiento', '% Cumplimiento', 'w-36 text-center bg-slate-50 dark:bg-slate-800')}
                                                                            {(isRevisionStatus(activePlan?.status) || isClosedStatus(activePlan?.status)) && (
                                                                                renderWeeklyHeaderCell('cnc', 'C.N.C', 'w-48 bg-amber-50 dark:bg-amber-900/10')
                                                                            )}
                                                                            <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase w-10 text-right"></th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                                                        {activePlan && activePlan.allocations.length === 0 ? (
                                                                            <tr>
                                                                                <td colSpan={4 + dynamicDayColumns.length + ((isExecutionStatus(activePlan?.status) || isRevisionStatus(activePlan?.status) || isClosedStatus(activePlan?.status)) ? 3 : 2) + ((isRevisionStatus(activePlan?.status) || isClosedStatus(activePlan?.status)) ? 1 : 0)} className="px-4 py-12 text-center text-slate-400 italic">
                                                                                    No hay tareas programadas. Utilice "Click Derecho" en una tarea del menú izquierdo y seleccione "Programar tarea".
                                                                                </td>
                                                                            </tr>
                                                                        ) : filteredAndSortedAllocations.length === 0 ? (
                                                                            <tr>
                                                                                <td colSpan={4 + dynamicDayColumns.length + ((isExecutionStatus(activePlan?.status) || isRevisionStatus(activePlan?.status) || isClosedStatus(activePlan?.status)) ? 3 : 2) + ((isRevisionStatus(activePlan?.status) || isClosedStatus(activePlan?.status)) ? 1 : 0)} className="px-4 py-12 text-center text-slate-400 italic">
                                                                                    No se encontraron tareas con los filtros seleccionados.
                                                                                </td>
                                                                            </tr>
                                                                        ) : (
                                                                            filteredAndSortedAllocations.map(({ alloc, originalIdx: idx }) => {
                                                                                const unit = units.find(u => u.id === alloc.unitId);
                                                                                const unitName = unit?.name || 'N/A';
                                                                                const levelName = levels.find(l => l.id === unit?.levelId)?.name || '-';
                                                                                const taskName = alloc.isAdditional ? (alloc.additionalTaskName || 'Tarea Adicional') : (findTaskName(alloc.taskId, tasks) || '[Partida Obsoleta/Fuera de Gantt]');
                                                                                const responsibleName = responsibles.find(r => r.id === alloc.responsibleId)?.name || '-';
                                                                                const taskObjForRow = !alloc.isAdditional ? findTaskById(alloc.taskId, tasks) : undefined;
                                                                                const parentTaskForRow = !alloc.isAdditional ? findParentTask(alloc.taskId, tasks) : null;
                                                                                const targetTaskIdForRow = (taskObjForRow?.isWeeklySubtask && parentTaskForRow) ? parentTaskForRow.id : alloc.taskId;
                                                                                const config = !alloc.isAdditional ? (protocolConfigs[alloc.taskId] || protocolConfigs[targetTaskIdForRow]) : null;
                                                                                const appliesProtocol = config?.applies ?? false;

                                                                                // Calculate accumulated compliance from previous closed weekly plans and base/reconciled progress
                                                                                const accumulatedCompliance = !alloc.isAdditional
                                                                                    ? getTaskUnitAccumulatedCompliance(alloc.taskId, alloc.unitId, tasks, weeklyPlans, protocolConfigs, savedProtocols, undefined, activePlan.id)
                                                                                    : 0;
                                                                                const isProtocolClosed = !alloc.isAdditional && appliesProtocol
                                                                                    ? (isProtocolProgressedLocal(alloc.taskId, alloc.unitId) || isProtocolProgressedLocal(targetTaskIdForRow, alloc.unitId))
                                                                                    : false;

                                                                                const pctComprometido = Number((alloc.percentComprometido ?? (alloc.executed ? 100 : 100)).toFixed(2));
                                                                                let pctCumplimiento = getAllocationCompliancePct(alloc);

                                                                                const isExecuted = !!alloc.executed;

                                                                                const isManualEntryAllowed = !appliesProtocol || (accumulatedCompliance + pctComprometido < 99.9);

                                                                                return (
                                                                                    <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                                                                        <td className="px-4 py-2 text-sm text-slate-500 dark:text-slate-400">{levelName}</td>
                                                                                        <td className="px-4 py-2 text-sm text-slate-700 dark:text-slate-300 font-medium">{unitName}</td>
                                                                                        <td className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 relative">
                                                                                            <div className="flex items-center gap-2">
                                                                                                {alloc.isAdditional ? (
                                                                                                    <span className="flex items-center gap-1.5">
                                                                                                        <span className="bg-purple-100 text-purple-700 text-[10px] font-bold px-1.5 py-0.5 rounded dark:bg-purple-900/30 dark:text-purple-300">Adicional</span>
                                                                                                        <span>{taskName}</span>
                                                                                                    </span>
                                                                                                ) : (
                                                                                                    <span className="flex items-center gap-1.5">
                                                                                                        <span>{taskName}</span>
                                                                                                        {appliesProtocol && (
                                                                                                            <span
                                                                                                                className={`material-symbols-outlined text-[17px] cursor-pointer transition-colors ${isProtocolClosed
                                                                                                                        ? 'text-emerald-500 hover:text-emerald-600'
                                                                                                                        : 'text-slate-400 hover:text-slate-600'
                                                                                                                    }`}
                                                                                                                title={isProtocolClosed ? "Protocolo aprobado" : "Protocolo sin aprobar"}
                                                                                                                onClick={(e) => {
                                                                                                                    e.stopPropagation();
                                                                                                                    navigate('/quality', {
                                                                                                                        state: {
                                                                                                                            fromPlanning: true,
                                                                                                                            openProtocolTaskId: alloc.taskId,
                                                                                                                            openProtocolUnitId: alloc.unitId
                                                                                                                        }
                                                                                                                    });
                                                                                                                }}
                                                                                                            >
                                                                                                                fact_check
                                                                                                            </span>
                                                                                                        )}
                                                                                                    </span>
                                                                                                )}
                                                                                                {getStatusValue(alloc) === 'no' && alloc.comentario && (
                                                                                                    <div className="relative inline-block">
                                                                                                        <button
                                                                                                            type="button"
                                                                                                            onClick={() => setActiveTooltipAllocIdx(activeTooltipAllocIdx === idx ? null : idx)}
                                                                                                            className="text-amber-500 hover:text-amber-600 flex items-center transition-colors focus:outline-none"
                                                                                                            title="Ver justificación de incumplimiento"
                                                                                                        >
                                                                                                            <span className="material-symbols-outlined text-[16px]">chat</span>
                                                                                                        </button>
                                                                                                        {activeTooltipAllocIdx === idx && (
                                                                                                            <div className="absolute z-[999] bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 bg-slate-900 text-white text-xs rounded-lg p-2.5 shadow-xl border border-slate-700 animate-in fade-in slide-in-from-bottom-2">
                                                                                                                <div className="flex justify-between items-start mb-1 font-bold text-amber-400">
                                                                                                                    <span>Justificación</span>
                                                                                                                    <button type="button" onClick={(e) => { e.stopPropagation(); setActiveTooltipAllocIdx(null); }} className="text-slate-400 hover:text-white"><span className="material-symbols-outlined text-[12px]">close</span></button>
                                                                                                                </div>
                                                                                                                <p className="font-normal text-slate-200 break-words">{alloc.comentario}</p>
                                                                                                                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900"></div>
                                                                                                            </div>
                                                                                                        )}
                                                                                                    </div>
                                                                                                )}
                                                                                            </div>
                                                                                        </td>
                                                                                        <td className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400">{responsibleName}</td>
                                                                                        {dynamicDayColumns.map((col) => {
                                                                                            const isChecked = !!(alloc.days?.[col.dayLetter] || alloc.days?.[col.fullDate]);
                                                                                            return (
                                                                                                <td key={col.key} className="px-2 py-2 text-center border-x border-slate-100/60 dark:border-slate-800/60">
                                                                                                    {isChecked && <span className="text-xs font-bold text-slate-600 dark:text-slate-400">x</span>}
                                                                                                </td>
                                                                                            );
                                                                                        })}
                                                                                        <td className="px-4 py-2 text-sm text-center text-slate-600 dark:text-slate-400">
                                                                                            {pctComprometido}%
                                                                                        </td>
                                                                                        {(isExecutionStatus(activePlan.status) || isRevisionStatus(activePlan.status) || isClosedStatus(activePlan.status)) && (
                                                                                            <td className="px-4 py-2 text-center">
                                                                                                <div className="flex flex-col gap-1.5 items-center">
                                                                                                    <select
                                                                                                        value={getStatusValue(alloc)}
                                                                                                        disabled={getDisabledStatus(alloc)}
                                                                                                        onChange={(e) => {
                                                                                                            const val = e.target.value;
                                                                                                            if (val === 'pendiente') {
                                                                                                                updateMainAllocationField(activePlan.id, idx, 'executed', null);
                                                                                                                updateMainAllocationField(activePlan.id, idx, 'percentCumplimiento', 0);
                                                                                                            } else if (val === 'si') {
                                                                                                                if (appliesProtocol && (accumulatedCompliance + pctComprometido >= 99.9) && !isProtocolClosed) {
                                                                                                                    setMobileQualityBlockedModalData({
                                                                                                                        alloc,
                                                                                                                        task: findTaskById(alloc.taskId, tasks)!
                                                                                                                    });
                                                                                                                    return;
                                                                                                                }
                                                                                                                updateMainAllocationField(activePlan.id, idx, 'executed', true);
                                                                                                                updateMainAllocationField(activePlan.id, idx, 'percentCumplimiento', 100);
                                                                                                            } else if (val === 'no') {
                                                                                                                handleSetUnexecuted(activePlan.id, idx, alloc);
                                                                                                            }
                                                                                                        }}
                                                                                                        className={`text-xs border rounded p-1.5 transition-all font-bold focus:outline-none focus:ring-1 ${getStatusValue(alloc) === 'si'
                                                                                                            ? 'bg-emerald-100 border-emerald-300 text-emerald-800 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-300 focus:ring-emerald-500'
                                                                                                            : getStatusValue(alloc) === 'no'
                                                                                                                ? 'bg-red-100 border-red-300 text-red-800 dark:bg-red-950/40 dark:border-red-800 dark:text-red-300 focus:ring-red-500'
                                                                                                                : 'bg-amber-100 border-amber-300 text-amber-800 dark:bg-amber-950/40 dark:border-amber-800 dark:text-amber-300 focus:ring-amber-500'
                                                                                                            }`}
                                                                                                    >
                                                                                                        <option value="pendiente" className="bg-white text-slate-900 dark:bg-slate-800 dark:text-white font-normal">Pendiente</option>
                                                                                                        <option value="si" className="bg-white text-slate-900 dark:bg-slate-800 dark:text-white font-normal">Ejecutado</option>
                                                                                                        <option value="no" className="bg-white text-slate-900 dark:bg-slate-800 dark:text-white font-normal">No Ejecutado</option>
                                                                                                    </select>
                                                                                                    {getStatusValue(alloc) === 'no' && (
                                                                                                        <JustificationInput
                                                                                                            placeholder="Justificación (obligatoria)"
                                                                                                            disabled={getDisabledStatus(alloc)}
                                                                                                            className="w-full min-w-[120px] max-w-[200px] text-xs p-1 border border-slate-300 rounded dark:bg-slate-800 dark:border-slate-600 dark:text-white placeholder:italic"
                                                                                                            value={alloc.comentario || ''}
                                                                                                            onChange={(val) => updateMainAllocationField(activePlan.id, idx, 'comentario', val)}
                                                                                                        />
                                                                                                    )}
                                                                                                </div>
                                                                                            </td>
                                                                                        )}
                                                                                        <td className="px-4 py-2 text-sm text-center">
                                                                                            {(() => {
                                                                                                const status = getStatusValue(alloc);
                                                                                                if (status === 'si') {
                                                                                                    return (
                                                                                                        <span className="text-emerald-600 dark:text-emerald-400 font-bold text-xs bg-emerald-50 dark:bg-emerald-950/20 px-2 py-1 rounded border border-emerald-200 dark:border-emerald-850 flex items-center justify-center gap-1 mx-auto w-fit">
                                                                                                            100%
                                                                                                        </span>
                                                                                                    );
                                                                                                } else if (status === 'no') {
                                                                                                    const isDisabled = getDisabledStatus(alloc);
                                                                                                    if (isClosedStatus(activePlan.status)) {
                                                                                                        return <span className="text-red-600 dark:text-red-400 font-bold">{pctCumplimiento}%</span>;
                                                                                                    }
                                                                                                    return (
                                                                                                        <div className="flex items-center justify-center gap-1">
                                                                                                            <input
                                                                                                                type="number"
                                                                                                                min="0"
                                                                                                                max={Math.min(99, roundToTwoDecimals(100 - accumulatedCompliance))}
                                                                                                                step="0.01"
                                                                                                                disabled={isDisabled}
                                                                                                                className="w-16 text-center text-xs p-1 border border-slate-300 rounded dark:bg-slate-800 dark:border-slate-600 dark:text-white font-bold text-red-650 dark:text-red-400 bg-red-50/30"
                                                                                                                value={pctCumplimiento >= 100 ? 0 : roundToTwoDecimals(pctCumplimiento)}
                                                                                                                onChange={(e) => {
                                                                                                                    const rawVal = parseFloat(e.target.value) || 0;
                                                                                                                    let val = roundToTwoDecimals(rawVal);
                                                                                                                    const maxVal = Math.min(99, roundToTwoDecimals(100 - accumulatedCompliance));
                                                                                                                    if (val > maxVal) {
                                                                                                                        alert(`Para tareas en estado 'No Ejecutado', el avance máximo es ${maxVal}%.`);
                                                                                                                        val = maxVal;
                                                                                                                    }
                                                                                                                    if (val < 0) val = 0;
                                                                                                                    updateMainAllocationField(activePlan.id, idx, 'percentCumplimiento', val);
                                                                                                                }}
                                                                                                            />
                                                                                                            <span className="text-xs text-slate-500">%</span>
                                                                                                        </div>
                                                                                                    );
                                                                                                } else {
                                                                                                    return (
                                                                                                        <span className="text-amber-655 dark:text-amber-400 font-bold text-xs bg-amber-50 dark:bg-amber-950/20 px-2 py-1 rounded border border-amber-200 dark:border-amber-850 flex items-center justify-center gap-1 mx-auto w-fit">
                                                                                                            0%
                                                                                                        </span>
                                                                                                    );
                                                                                                }
                                                                                            })()}
                                                                                        </td>
                                                                                        {(isRevisionStatus(activePlan.status) || isClosedStatus(activePlan.status)) && (
                                                                                            <td className="px-4 py-2 bg-amber-50/30 dark:bg-amber-900/5">
                                                                                                {getStatusValue(alloc) === 'si' ? (
                                                                                                    <span className="text-xs text-slate-300 italic">No Aplica</span>
                                                                                                ) : (
                                                                                                    <div className="relative">
                                                                                                        <SearchableCNCSelect
                                                                                                            value={alloc.cncId || ''}
                                                                                                            onChange={(val) => updateMainAllocationField(activePlan.id, idx, 'cncId', val)}
                                                                                                            groups={cncGroups}
                                                                                                            disabled={isClosedStatus(activePlan.status) || !isJefaturaOrOficina}
                                                                                                        />
                                                                                                    </div>
                                                                                                )}
                                                                                            </td>
                                                                                        )}
                                                                                        <td className="px-4 py-2 text-right">
                                                                                            <div className="flex items-center justify-end gap-1.5">
                                                                                                {activePlan.status === 'En Planificación' && (
                                                                                                    <button
                                                                                                        onClick={() => {
                                                                                                            if (alloc.isAdditional) {
                                                                                                                setEditingAdditionalIndex(idx);
                                                                                                                setAdditionalTaskRows([{
                                                                                                                    id: 'edit_0',
                                                                                                                    levelId: units.find(u => u.id === alloc.unitId)?.levelId || '',
                                                                                                                    unitId: alloc.unitId,
                                                                                                                    additionalTaskName: alloc.additionalTaskName || '',
                                                                                                                    responsibleId: alloc.responsibleId,
                                                                                                                    days: { ...alloc.days }
                                                                                                                }]);
                                                                                                                setAdditionalModalOpen(true);
                                                                                                            } else {
                                                                                                                setEditingAllocationIndex(idx);
                                                                                                                const allocCopy = JSON.parse(JSON.stringify(alloc));
                                                                                                                allocCopy.percentComprometido = allocCopy.percentComprometido ?? 100;
                                                                                                                setEditingAllocationData(allocCopy);
                                                                                                                setIsEditingAllocation(true);
                                                                                                            }
                                                                                                        }}
                                                                                                        className="text-slate-400 hover:text-purple-600 transition-colors"
                                                                                                        title="Editar asignación"
                                                                                                    >
                                                                                                        <span className="material-symbols-outlined text-[18px]">edit</span>
                                                                                                    </button>
                                                                                                )}
                                                                                                {activePlan.status === 'En Planificación' && (
                                                                                                    <button
                                                                                                        onClick={() => handleDeleteAllocation(activePlan.id, idx)}
                                                                                                        className="text-slate-400 hover:text-red-500 transition-colors"
                                                                                                        title="Eliminar actividad"
                                                                                                    >
                                                                                                        <span className="material-symbols-outlined text-[18px]">delete</span>
                                                                                                    </button>
                                                                                                )}
                                                                                            </div>
                                                                                        </td>
                                                                                    </tr>
                                                                                );
                                                                            })
                                                                        )}
                                                                    </tbody>
                                                                </table>
                                                            );
                                                        })()}

                                                        {/* --- VISTA MÓVIL (PLANIFICACIÓN SEMANAL) --- */}
                                                        <div className={`${isMobile ? 'block' : 'hidden'} p-4 space-y-4 pb-24`}>
                                                            <div className="h-full">
                                                                {/* Filtro Móvil Semanal y Botón de Filtros */}
                                                                <div className="flex items-center gap-2 mb-4">
                                                                    <div className="flex-1 flex bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
                                                                        <button
                                                                            onClick={() => setMobileFilterWeekly('all')}
                                                                            className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-colors ${mobileFilterWeekly === 'all' ? 'bg-white dark:bg-slate-700 shadow text-slate-900 dark:text-white flex items-center justify-center gap-1' : 'text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700/50 flex items-center justify-center gap-1'}`}
                                                                        >
                                                                            <span className="material-symbols-outlined text-[14px]">public</span> General
                                                                        </button>
                                                                        <button
                                                                            onClick={() => setMobileFilterWeekly('mine')}
                                                                            className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-colors ${mobileFilterWeekly === 'mine' ? 'bg-primary text-white shadow flex items-center justify-center gap-1' : 'text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700/50 flex items-center justify-center gap-1'}`}
                                                                        >
                                                                            <span className="material-symbols-outlined text-[14px]">person</span> Mis Tareas
                                                                        </button>
                                                                    </div>
                                                                    <button
                                                                        onClick={() => setIsMobileFilterOpen(true)}
                                                                        className={`px-3 py-1.5 text-xs font-bold rounded-lg border flex items-center gap-1.5 transition-colors cursor-pointer ${mobileActiveFiltersCount > 0
                                                                                ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                                                                                : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50'
                                                                            }`}
                                                                    >
                                                                        <span className="material-symbols-outlined text-[16px]">filter_list</span>
                                                                        <span>Filtro</span>
                                                                        {mobileActiveFiltersCount > 0 && (
                                                                            <span className="w-4 h-4 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">
                                                                                {mobileActiveFiltersCount}
                                                                            </span>
                                                                        )}
                                                                    </button>
                                                                </div>

                                                                {(() => {
                                                                    if (!activePlan || activePlan.allocations.length === 0) {
                                                                        return (
                                                                            <div className="text-center text-slate-400 italic py-8 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
                                                                                No hay tareas programadas.
                                                                            </div>
                                                                        );
                                                                    }

                                                                    let allocationsForMobile = activePlan.allocations;

                                                                    // Filtro 'mine'
                                                                    if (mobileFilterWeekly === 'mine' && currentUser) {
                                                                        allocationsForMobile = allocationsForMobile.filter(alloc => {
                                                                            const resp = responsibles.find(r => r.id === alloc.responsibleId);
                                                                            return resp && resp.email === currentUser.email;
                                                                        });
                                                                    }

                                                                    // Filtros avanzados móviles
                                                                    if (mobileFilters.levelId) {
                                                                        allocationsForMobile = allocationsForMobile.filter(alloc => {
                                                                            const unit = units.find(u => u.id === alloc.unitId);
                                                                            return unit && unit.levelId === mobileFilters.levelId;
                                                                        });
                                                                    }

                                                                    if (mobileFilters.unitId) {
                                                                        allocationsForMobile = allocationsForMobile.filter(alloc => alloc.unitId === mobileFilters.unitId);
                                                                    }

                                                                    if (mobileFilters.responsibleId) {
                                                                        allocationsForMobile = allocationsForMobile.filter(alloc => alloc.responsibleId === mobileFilters.responsibleId);
                                                                    }

                                                                    if (mobileFilters.taskSearch && mobileFilters.taskSearch.trim() !== '') {
                                                                        const q = mobileFilters.taskSearch.trim().toLowerCase();
                                                                        allocationsForMobile = allocationsForMobile.filter(alloc => {
                                                                            const taskName = alloc.isAdditional ? (alloc.additionalTaskName || 'Tarea Adicional') : (findTaskName(alloc.taskId, tasks) || '');
                                                                            return taskName.toLowerCase().includes(q);
                                                                        });
                                                                    }

                                                                    if (mobileFilters.status && mobileFilters.status !== 'all') {
                                                                        allocationsForMobile = allocationsForMobile.filter(alloc => {
                                                                            const config = !alloc.isAdditional ? protocolConfigs[alloc.taskId] : null;
                                                                            const appliesProtocol = config?.applies ?? false;
                                                                            const isExecuted = !!alloc.executed;
                                                                            if (mobileFilters.status === 'executed') {
                                                                                return isExecuted;
                                                                            } else if (mobileFilters.status === 'pending') {
                                                                                return !isExecuted;
                                                                            } else if (mobileFilters.status === 'protocol') {
                                                                                return appliesProtocol;
                                                                            }
                                                                            return true;
                                                                        });
                                                                    }

                                                                    if (allocationsForMobile.length === 0) {
                                                                        return (
                                                                            <div className="text-center text-slate-400 italic py-8 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 px-4">
                                                                                <p className="mb-2">
                                                                                    {mobileActiveFiltersCount > 0
                                                                                        ? 'No se encontraron tareas con los filtros seleccionados.'
                                                                                        : mobileFilterWeekly === 'mine'
                                                                                            ? 'No tienes tareas asignadas en esta planificación.'
                                                                                            : 'No hay tareas registradas.'}
                                                                                </p>
                                                                                {mobileActiveFiltersCount > 0 && (
                                                                                    <button
                                                                                        onClick={() => setMobileFilters({ levelId: '', unitId: '', responsibleId: '', taskSearch: '', status: 'all' })}
                                                                                        className="text-xs font-bold text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1 cursor-pointer"
                                                                                    >
                                                                                        <span className="material-symbols-outlined text-[14px]">restart_alt</span>
                                                                                        Restablecer filtros
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    }

                                                                    return allocationsForMobile.map((alloc, idx) => {
                                                                        const targetIdx = activePlan.allocations.findIndex(a => a.taskId === alloc.taskId && a.unitId === alloc.unitId);
                                                                        const actualIdx = targetIdx >= 0 ? targetIdx : idx;
                                                                        const unit = units.find(u => u.id === alloc.unitId);
                                                                        const unitName = unit?.name || 'N/A';
                                                                        const levelName = levels.find(l => l.id === unit?.levelId)?.name || '-';
                                                                        const taskName = alloc.isAdditional ? (alloc.additionalTaskName || 'Tarea Adicional') : (findTaskName(alloc.taskId, tasks) || '[Partida Obsoleta/Fuera de Gantt]');
                                                                        const responsibleName = responsibles.find(r => r.id === alloc.responsibleId)?.name || 'Sin Asignar';
                                                                        const taskObjForRow = !alloc.isAdditional ? findTaskById(alloc.taskId, tasks) : undefined;
                                                                        const parentTaskForRow = !alloc.isAdditional ? findParentTask(alloc.taskId, tasks) : null;
                                                                        const targetTaskIdForRow = (taskObjForRow?.isWeeklySubtask && parentTaskForRow) ? parentTaskForRow.id : alloc.taskId;
                                                                        const config = !alloc.isAdditional ? (protocolConfigs[alloc.taskId] || protocolConfigs[targetTaskIdForRow]) : null;
                                                                        const appliesProtocol = config?.applies ?? false;

                                                                        // Calculate accumulated compliance from previous closed weekly plans and base/reconciled progress
                                                                        const accumulatedCompliance = !alloc.isAdditional
                                                                            ? getTaskUnitAccumulatedCompliance(alloc.taskId, alloc.unitId, tasks, weeklyPlans, protocolConfigs, savedProtocols, undefined, activePlan.id)
                                                                            : 0;

                                                                        const isProtocolClosed = !alloc.isAdditional && appliesProtocol
                                                                            ? (isProtocolProgressedLocal(alloc.taskId, alloc.unitId) || isProtocolProgressedLocal(targetTaskIdForRow, alloc.unitId))
                                                                            : false;

                                                                        const pctComprometido = Number((alloc.percentComprometido ?? (alloc.executed ? 100 : 100)).toFixed(2));
                                                                        let pctCumplimiento = getAllocationCompliancePct(alloc);

                                                                        const isExecuted = !!alloc.executed;
                                                                        const isManualEntryAllowed = !appliesProtocol || (accumulatedCompliance + pctComprometido < 99.9);
                                                                        const activeDays = dynamicDayColumns
                                                                            .filter(col => alloc.days?.[col.dayLetter] || alloc.days?.[col.fullDate])
                                                                            .map(col => col.dateNum !== '--' ? `${col.dayLetter} ${col.dateNum}` : col.dayLetter);

                                                                        return (
                                                                            <div key={`${alloc.taskId}_${alloc.unitId}`} className={`rounded-xl shadow-sm flex flex-col p-3 gap-2 border transition-colors relative ${getStatusValue(alloc) === 'si'
                                                                                ? 'bg-[#52be80] border-[#229954] text-slate-900 dark:bg-[#27ae60] dark:text-white dark:border-[#1e8449]'
                                                                                : getStatusValue(alloc) === 'no'
                                                                                    ? 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-900 text-red-900 dark:text-red-300'
                                                                                    : 'bg-[#fcf3cf] border-[#f4d03f] text-slate-900 dark:bg-[#f1c40f]/20 dark:text-white dark:border-[#f1c40f]/40'
                                                                                }`}>
                                                                                <div className="flex items-center justify-between gap-3">
                                                                                    <div className="flex flex-col flex-1 gap-1.5 pr-2">
                                                                                        <div className="text-[13px] font-black leading-tight drop-shadow-sm flex items-center gap-2">
                                                                                            {alloc.isAdditional && (
                                                                                                <span className="bg-purple-100 text-purple-700 text-[9px] font-bold px-1 py-0.5 rounded mr-1.5 dark:bg-purple-900/30 dark:text-purple-300">ADICIONAL</span>
                                                                                            )}
                                                                                            <span className="flex-1">{taskName}</span>
                                                                                            {getStatusValue(alloc) === 'no' && alloc.comentario && (
                                                                                                <div className="relative">
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        onClick={() => setActiveTooltipAllocIdx(activeTooltipAllocIdx === actualIdx ? null : actualIdx)}
                                                                                                        className="text-amber-500 dark:text-amber-400 p-1 flex items-center focus:outline-none"
                                                                                                        title="Ver justificación"
                                                                                                    >
                                                                                                        <span className="material-symbols-outlined text-[16px]">chat</span>
                                                                                                    </button>
                                                                                                    {activeTooltipAllocIdx === actualIdx && (
                                                                                                        <div className="absolute z-[999] right-0 bottom-full mb-2 w-56 bg-slate-950 text-white text-xs rounded-lg p-2.5 shadow-xl border border-slate-800 animate-in fade-in slide-in-from-bottom-2">
                                                                                                            <div className="flex justify-between items-start mb-1 font-bold text-amber-400">
                                                                                                                <span>Justificación</span>
                                                                                                                <button type="button" onClick={(e) => { e.stopPropagation(); setActiveTooltipAllocIdx(null); }} className="text-slate-400 hover:text-white"><span className="material-symbols-outlined text-[12px]">close</span></button>
                                                                                                            </div>
                                                                                                            <p className="font-normal text-slate-200 break-words">{alloc.comentario}</p>
                                                                                                            <div className="absolute top-full right-3 border-4 border-transparent border-t-slate-950"></div>
                                                                                                        </div>
                                                                                                    )}
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                        <div className="flex flex-wrap gap-1 text-[10px] font-bold opacity-90">
                                                                                            <span className="bg-black/10 dark:bg-white/10 px-1.5 py-0.5 rounded shadow-sm">{levelName} - {unitName}</span>
                                                                                            <span className="bg-black/10 dark:bg-white/10 px-1.5 py-0.5 rounded shadow-sm">{responsibleName}</span>
                                                                                            <span className="bg-black/10 dark:bg-white/10 px-1.5 py-0.5 rounded shadow-sm flex items-center gap-0.5"><span className="material-symbols-outlined text-[10px]">calendar_today</span>{activeDays.join(', ')}</span>
                                                                                        </div>
                                                                                        <div className="flex flex-wrap gap-1.5 text-[10px] font-bold mt-1 text-slate-800 dark:text-slate-200">
                                                                                            <span className="bg-white/20 dark:bg-black/20 px-1.5 py-0.5 rounded shadow-sm">Meta: {pctComprometido}%</span>
                                                                                            <span className="bg-white/20 dark:bg-black/20 px-1.5 py-0.5 rounded shadow-sm">Acum: {accumulatedCompliance}%</span>
                                                                                            {appliesProtocol && (
                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        setMobileProtocolTarget({
                                                                                                            taskId: alloc.taskId,
                                                                                                            unitId: alloc.unitId,
                                                                                                            taskName: taskName
                                                                                                        });
                                                                                                    }}
                                                                                                    className={`px-2 py-0.5 rounded shadow-sm text-[9px] uppercase font-bold tracking-wide flex items-center gap-1 transition-all active:scale-95 cursor-pointer ${isProtocolClosed
                                                                                                            ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                                                                                                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                                                                                                        }`}
                                                                                                    title={isProtocolClosed ? "Protocolo aprobado" : "Protocolo de calidad"}
                                                                                                >
                                                                                                    <span className="material-symbols-outlined text-[12px]">
                                                                                                        {isProtocolClosed ? 'verified' : 'fact_check'}
                                                                                                    </span>
                                                                                                    <span>Protocolo</span>
                                                                                                </button>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>

                                                                                <div className="flex flex-col gap-2 shrink-0 bg-white/50 dark:bg-black/20 p-2 rounded-lg border border-black/10 text-slate-800 dark:text-slate-200 w-full mt-2">
                                                                                    <div className="flex items-center justify-between gap-2">
                                                                                        <span className="text-[10px] font-black opacity-85 uppercase">Estado</span>
                                                                                        <select
                                                                                            value={getStatusValue(alloc)}
                                                                                            disabled={getDisabledStatus(alloc)}
                                                                                            onChange={(e) => {
                                                                                                const val = e.target.value;
                                                                                                if (val === 'pendiente') {
                                                                                                    updateMainAllocationField(activePlan.id, actualIdx, 'executed', null);
                                                                                                    updateMainAllocationField(activePlan.id, actualIdx, 'percentCumplimiento', 0);
                                                                                                } else if (val === 'si') {
                                                                                                    if (appliesProtocol && (accumulatedCompliance + pctComprometido >= 99.9) && !isProtocolClosed) {
                                                                                                        setMobileQualityBlockedModalData({
                                                                                                            alloc,
                                                                                                            task: findTaskById(alloc.taskId, tasks)!
                                                                                                        });
                                                                                                        return;
                                                                                                    }
                                                                                                    updateMainAllocationField(activePlan.id, actualIdx, 'executed', true);
                                                                                                    updateMainAllocationField(activePlan.id, actualIdx, 'percentCumplimiento', 100);
                                                                                                } else if (val === 'no') {
                                                                                                    handleSetUnexecuted(activePlan.id, actualIdx, alloc);
                                                                                                }
                                                                                            }}
                                                                                            className={`text-xs border rounded p-1.5 transition-all font-bold focus:outline-none ${getStatusValue(alloc) === 'si'
                                                                                                ? 'bg-emerald-100 border-emerald-300 text-emerald-800 dark:bg-emerald-950/40 dark:border-emerald-850 dark:text-emerald-300'
                                                                                                : getStatusValue(alloc) === 'no'
                                                                                                    ? 'bg-red-100 border-red-300 text-red-800 dark:bg-red-950/40 dark:border-red-850 dark:text-red-300'
                                                                                                    : 'bg-amber-100 border-amber-300 text-amber-800 dark:bg-amber-950/40 dark:border-amber-850 dark:text-amber-300'
                                                                                                }`}
                                                                                        >
                                                                                            <option value="pendiente" className="bg-white text-slate-900 dark:bg-slate-800 dark:text-white font-normal">Pendiente</option>
                                                                                            <option value="si" className="bg-white text-slate-900 dark:bg-slate-800 dark:text-white font-normal">Ejecutado</option>
                                                                                            <option value="no" className="bg-white text-slate-900 dark:bg-slate-800 dark:text-white font-normal">No Ejecutado</option>
                                                                                        </select>
                                                                                    </div>
                                                                                    {getStatusValue(alloc) === 'si' && (
                                                                                        <div className="flex items-center justify-between text-xs">
                                                                                            <span className="font-bold">Cumplimiento:</span>
                                                                                            <span className="text-green-600 font-bold">100%</span>
                                                                                        </div>
                                                                                    )}
                                                                                    {getStatusValue(alloc) === 'no' && (
                                                                                        <>
                                                                                            <div className="flex items-center justify-between gap-2">
                                                                                                <span className="text-[10px] font-black opacity-85 uppercase">Cumplimiento</span>
                                                                                                <div className="flex items-center gap-1">
                                                                                                    <input
                                                                                                        type="number"
                                                                                                        min="0"
                                                                                                        max={Math.min(99, roundToTwoDecimals(100 - accumulatedCompliance))}
                                                                                                        step="0.01"
                                                                                                        disabled={getDisabledStatus(alloc)}
                                                                                                        className="w-16 text-center text-xs p-1 border border-slate-300 rounded dark:bg-slate-800 dark:border-slate-600 dark:text-white font-bold"
                                                                                                        value={pctCumplimiento >= 100 ? 0 : roundToTwoDecimals(pctCumplimiento)}
                                                                                                        onChange={(e) => {
                                                                                                            const rawVal = parseFloat(e.target.value) || 0;
                                                                                                            let val = roundToTwoDecimals(rawVal);
                                                                                                            const maxVal = Math.min(99, roundToTwoDecimals(100 - accumulatedCompliance));
                                                                                                            if (val > maxVal) {
                                                                                                                alert(`Para tareas en estado 'No Ejecutado', el avance máximo es ${maxVal}%.`);
                                                                                                                val = maxVal;
                                                                                                            }
                                                                                                            if (val < 0) val = 0;
                                                                                                            updateMainAllocationField(activePlan.id, actualIdx, 'percentCumplimiento', val);
                                                                                                        }}
                                                                                                    />
                                                                                                    <span className="text-xs">%</span>
                                                                                                </div>
                                                                                            </div>
                                                                                            <div className="flex flex-col gap-1">
                                                                                                <span className="text-[10px] font-black opacity-85 uppercase">Justificación</span>
                                                                                                <JustificationInput
                                                                                                    placeholder="Justificación (obligatoria)"
                                                                                                    disabled={getDisabledStatus(alloc)}
                                                                                                    className="w-full text-xs p-1.5 border border-slate-300 rounded dark:bg-slate-800 dark:border-slate-600 dark:text-white placeholder:italic"
                                                                                                    value={alloc.comentario || ''}
                                                                                                    onChange={(val) => updateMainAllocationField(activePlan.id, actualIdx, 'comentario', val)}
                                                                                                />
                                                                                            </div>
                                                                                        </>
                                                                                    )}
                                                                                    {getStatusValue(alloc) === 'pendiente' && (
                                                                                        <div className="flex items-center justify-between text-xs text-slate-400">
                                                                                            <span>Cumplimiento:</span>
                                                                                            <span>0%</span>
                                                                                        </div>
                                                                                    )}
                                                                                </div>

                                                                                {(activePlan.status === 'En Revisión' || activePlan.status === 'Cerrada') && getStatusValue(alloc) !== 'si' && (
                                                                                    <div className="mt-2 pt-2 border-t border-black/10 dark:border-white/10 w-full">
                                                                                        <label className="block text-[10px] uppercase font-black opacity-70 mb-1 ml-1 text-slate-900 dark:text-white">Causas de No Cumplimiento</label>
                                                                                        <div className="bg-white/80 dark:bg-[#101822]/80 rounded p-1">
                                                                                            <SearchableCNCSelect
                                                                                                value={alloc.cncId || ''}
                                                                                                onChange={(val) => updateMainAllocationField(activePlan.id, actualIdx, 'cncId', val)}
                                                                                                groups={cncGroups}
                                                                                                disabled={activePlan.status === 'Cerrada' || !isJefaturaOrOficina}
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                )}

                                                                                {activePlan.status === 'En Planificación' && (
                                                                                    <div className="absolute top-1 right-1 flex items-center gap-1 opacity-60">
                                                                                        {!alloc.isAdditional && (
                                                                                            <button
                                                                                                onClick={() => {
                                                                                                    setEditingAllocationIndex(actualIdx);
                                                                                                    const allocCopy = JSON.parse(JSON.stringify(alloc));
                                                                                                    allocCopy.percentComprometido = allocCopy.percentComprometido ?? 100;
                                                                                                    setEditingAllocationData(allocCopy);
                                                                                                    setIsEditingAllocation(true);
                                                                                                }}
                                                                                                className="hover:text-purple-700 dark:hover:text-purple-300 transition-colors p-1"
                                                                                                title="Editar asignación"
                                                                                            >
                                                                                                <span className="material-symbols-outlined text-[14px]">edit</span>
                                                                                            </button>
                                                                                        )}
                                                                                        {!alloc.isAdditional && (
                                                                                            <button
                                                                                                onClick={() => handleDeleteAllocation(activePlan.id, actualIdx)}
                                                                                                className="hover:text-red-700 dark:hover:text-red-400 transition-colors p-1"
                                                                                                title="Eliminar asignación"
                                                                                            >
                                                                                                <span className="material-symbols-outlined text-[14px]">close</span>
                                                                                            </button>
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })
                                                                })()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    ) : (
                                        // History View (Default)
                                        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 flex-1 min-h-0 flex flex-col overflow-hidden">
                                            <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                                                <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider">Historial de Planificaciones</h3>
                                            </div>
                                            <div className="flex-1 overflow-auto">
                                                {weeklyPlans.length === 0 ? (
                                                    <div className="flex flex-col items-center justify-center h-full text-slate-400">
                                                        <span className="material-symbols-outlined text-4xl mb-2">history</span>
                                                        <p className="text-sm">No hay planificaciones registradas.</p>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <table className={`${isMobile ? 'hidden' : 'table'} w-full text-left border-collapse`}>
                                                            <thead className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                                                                <tr>
                                                                    <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">#</th>
                                                                    <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider leading-tight">
                                                                        <div>FECHA DE INICIO</div>
                                                                        <div>FECHA DE TERMINO</div>
                                                                    </th>
                                                                    <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Estado</th>
                                                                    <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider text-center">Adicionales</th>
                                                                    <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider text-center">PPC</th>
                                                                    <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider text-right">Acciones</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                                                {[...weeklyPlans].sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()).map(plan => {
                                                                    let statusLabel: string = plan.status;
                                                                    let statusClasses = '';

                                                                    if (plan.status === 'En Planificación') {
                                                                        statusLabel = 'En Planificación';
                                                                        statusClasses = 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
                                                                    } else if (plan.status === 'En Ejecución') {
                                                                        statusLabel = 'En Ejecución';
                                                                        statusClasses = 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
                                                                    } else if (plan.status === 'En Revisión') {
                                                                        statusLabel = 'En Revisión';
                                                                        statusClasses = 'bg-yellow-400 text-slate-900 border border-yellow-500/20';
                                                                    } else if (plan.status === 'Cerrada') {
                                                                        statusLabel = 'Cerrado';
                                                                        statusClasses = 'bg-slate-300 text-slate-600 border border-slate-400/20';
                                                                    }

                                                                    const totalAllocations = plan.allocations ? plan.allocations.length : 0;
                                                                    const executedAllocations = plan.allocations ? plan.allocations.filter(a => a.executed).length : 0;
                                                                    const ppc = totalAllocations > 0 ? Math.round((executedAllocations / totalAllocations) * 100) : 0;

                                                                    return (
                                                                        <tr key={plan.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                                                            {/* 1. # */}
                                                                            <td className="px-6 py-4 font-bold text-sm text-slate-700 dark:text-slate-300">Planificación #{plan.id}</td>

                                                                            {/* 2. FECHA DE INICIO / FECHA DE TERMINO */}
                                                                            <td className="px-6 py-4">
                                                                                <div className="flex flex-col">
                                                                                    <span className="font-bold text-xs text-slate-800 dark:text-slate-200">{plan.startDate}</span>
                                                                                    <span className="text-[11px] text-slate-500 dark:text-slate-400 font-medium">{plan.endDate}</span>
                                                                                </div>
                                                                            </td>

                                                                            {/* 3. Estado */}
                                                                            <td className="px-6 py-4">
                                                                                <span className={`inline-flex px-4 py-1.5 rounded-full text-xs font-bold shadow-sm ${statusClasses}`}>
                                                                                    {statusLabel}
                                                                                </span>
                                                                            </td>

                                                                            {/* 4. Adicionales (4 Iconos) */}
                                                                            <td className="px-6 py-4 text-center">
                                                                                <div className="flex items-center justify-center gap-1">
                                                                                    {/* Ícono 1: Evaluación SC */}
                                                                                    {(() => {
                                                                                        const isDone = isPlanEvaluationDone(plan.id);
                                                                                        return (
                                                                                            <button
                                                                                                onClick={() => {
                                                                                                    setEvalModalPlanId(plan.id);
                                                                                                    setEvalModalOpen(true);
                                                                                                }}
                                                                                                className={`p-1.5 rounded-lg transition-all active:scale-95 cursor-pointer ${isDone
                                                                                                        ? 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 font-bold'
                                                                                                        : 'text-slate-400 hover:text-slate-600 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'
                                                                                                    }`}
                                                                                                title={isDone ? "Evaluación de Subcontratos (Completada)" : "Evaluación de Subcontratos (Pendiente)"}
                                                                                            >
                                                                                                <span className="material-symbols-outlined text-[20px]">fact_check</span>
                                                                                            </button>
                                                                                        );
                                                                                    })()}

                                                                                    {/* Ícono 2: Registro Asistencia */}
                                                                                    {(() => {
                                                                                        const isDone = isPlanAttendanceDone(plan.id);
                                                                                        return (
                                                                                            <button
                                                                                                onClick={() => {
                                                                                                    setAttendanceModalPlan(plan);
                                                                                                    setAttendanceModalOpen(true);
                                                                                                }}
                                                                                                className={`p-1.5 rounded-lg transition-all active:scale-95 cursor-pointer ${isDone
                                                                                                        ? 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 font-bold'
                                                                                                        : 'text-slate-400 hover:text-slate-600 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'
                                                                                                    }`}
                                                                                                title={isDone ? "Registro de Asistencia (Completado)" : "Registro de Asistencia (Pendiente)"}
                                                                                            >
                                                                                                <span className="material-symbols-outlined text-[20px]">how_to_reg</span>
                                                                                            </button>
                                                                                        );
                                                                                    })()}

                                                                                    {/* Ícono 3: Acta de Reunión */}
                                                                                    {(() => {
                                                                                        const isDone = isPlanMeetingMinutesDone(plan.id);
                                                                                        return (
                                                                                            <button
                                                                                                onClick={() => {
                                                                                                    setMeetingMinutesModalPlan(plan);
                                                                                                    setMeetingMinutesModalOpen(true);
                                                                                                }}
                                                                                                className={`p-1.5 rounded-lg transition-all active:scale-95 cursor-pointer ${isDone
                                                                                                        ? 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 font-bold'
                                                                                                        : 'text-slate-400 hover:text-slate-600 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'
                                                                                                    }`}
                                                                                                title={isDone ? "Acta de Reunión (Completada)" : "Acta de Reunión (Pendiente)"}
                                                                                            >
                                                                                                <span className="material-symbols-outlined text-[20px]">description</span>
                                                                                            </button>
                                                                                        );
                                                                                    })()}

                                                                                    {/* Ícono 4: Presentación */}
                                                                                    {(() => {
                                                                                        const isDone = isPlanPresentationDone(plan.id);
                                                                                        return (
                                                                                            <button
                                                                                                onClick={() => handleOpenPresentationForPlan(plan)}
                                                                                                className={`p-1.5 rounded-lg transition-all active:scale-95 cursor-pointer ${isDone
                                                                                                        ? 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 font-bold'
                                                                                                        : 'text-slate-400 hover:text-slate-600 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'
                                                                                                    }`}
                                                                                                title="Presentación"
                                                                                            >
                                                                                                <span className="material-symbols-outlined text-[20px]">co_present</span>
                                                                                            </button>
                                                                                        );
                                                                                    })()}
                                                                                </div>
                                                                            </td>

                                                                            {/* 5. PPC */}
                                                                            <td className="px-6 py-5 text-center">
                                                                                <span className="font-bold text-sm text-slate-700 dark:text-slate-300">{ppc}%</span>
                                                                            </td>

                                                                            {/* 6. Acciones */}
                                                                            <td className="px-6 py-5 text-right flex justify-end">
                                                                                <button
                                                                                    onClick={() => {
                                                                                        setEditingWeeklyPlanId(plan.id);
                                                                                        setWeeklyPlanDates({ start: plan.startDate, end: plan.endDate });
                                                                                        setNewPlanModalOpen(true);
                                                                                    }}
                                                                                    className="text-slate-400 hover:text-primary transition-colors p-2 hover:bg-slate-100 rounded-full cursor-pointer"
                                                                                    title="Editar fechas de la planificación"
                                                                                    disabled={plan.status === 'Cerrada'}
                                                                                >
                                                                                    <span className="material-symbols-outlined text-[20px]">edit_calendar</span>
                                                                                </button>
                                                                                <button
                                                                                    onClick={() => {
                                                                                        recordPlanScroll('semanal', plan.id);
                                                                                        setWeeklyPlansBackup(JSON.parse(JSON.stringify(weeklyPlans)));
                                                                                        setActivePlanId(plan.id);
                                                                                        setIsWeeklyEditing(plan.status !== 'Cerrada');
                                                                                    }}
                                                                                    className="text-slate-400 hover:text-primary transition-colors p-2 hover:bg-slate-100 rounded-full cursor-pointer"
                                                                                    title="Ver Plan"
                                                                                >
                                                                                    <span className="material-symbols-outlined text-[22px]">search</span>
                                                                                </button>
                                                                                {(hasPermission('ps_eliminar', 'view') || hasPermission('ps_eliminar', 'edit')) && (
                                                                                    <button
                                                                                        onClick={() => {
                                                                                            if (hasPermission('ps_eliminar', 'edit')) {
                                                                                                handleDeleteWeeklyPlan(plan.id);
                                                                                            }
                                                                                        }}
                                                                                        disabled={!hasPermission('ps_eliminar', 'edit')}
                                                                                        className={`p-2 rounded-full transition-colors ${!hasPermission('ps_eliminar', 'edit')
                                                                                            ? 'text-slate-200 cursor-not-allowed dark:text-slate-800'
                                                                                            : 'text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 cursor-pointer'
                                                                                            }`}
                                                                                        title={!hasPermission('ps_eliminar', 'edit') ? "Eliminar Programación (Solo Lectura)" : "Eliminar Programación"}
                                                                                    >
                                                                                        <span className="material-symbols-outlined text-[20px]">delete</span>
                                                                                    </button>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                        <div className={`${isMobile ? 'block' : 'hidden'} p-3 space-y-2.5 pb-24`}>
                                                            {[...weeklyPlans]
                                                                .filter(plan => !isMobile || isPlanVisibleInMobile(plan.status))
                                                                .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
                                                                .map(plan => {
                                                                    const isAttDone = isPlanAttendanceDone(plan.id);

                                                                    let weeklyStatusLabel = 'En Planificación';
                                                                    let weeklyStatusClasses = 'bg-blue-100 text-slate-900 border border-blue-200/50';
                                                                    if (plan.status === 'En Ejecución') {
                                                                        weeklyStatusLabel = 'En Ejecución';
                                                                        weeklyStatusClasses = 'bg-green-100 text-slate-900 border border-green-200/50';
                                                                    } else if (plan.status === 'En Revisión') {
                                                                        weeklyStatusLabel = 'En Revisión';
                                                                        weeklyStatusClasses = 'bg-yellow-400 text-slate-900 border border-yellow-500/20';
                                                                    } else if (plan.status === 'Cerrada' || (plan.status as any) === 'Cerrado') {
                                                                        weeklyStatusLabel = 'Cerrado';
                                                                        weeklyStatusClasses = 'bg-slate-300 text-slate-900 border border-slate-400/20';
                                                                    }

                                                                    return (
                                                                        <div key={plan.id} className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-2.5 flex flex-col gap-1.5">
                                                                            <div className="flex justify-between items-center min-h-[24px]">
                                                                                <h4 className="font-bold text-[#1a73e8] dark:text-blue-400 text-sm leading-none flex items-center">Planificación #{plan.id}</h4>
                                                                                <span className={`inline-flex items-center justify-center px-2.5 py-1 rounded-full text-[10px] font-bold shadow-sm leading-none translate-y-[2.5px] ${weeklyStatusClasses}`}>
                                                                                    {weeklyStatusLabel}
                                                                                </span>
                                                                            </div>

                                                                            {/* Fila única de acciones: Botones a la izquierda (subidos) | Fecha a la derecha (bajada) */}
                                                                            <div className="flex justify-between items-center">
                                                                                <div className="flex items-center gap-1.5 shrink-0 -translate-y-[2px]">
                                                                                    {hasPermission('movil_ps_revisar', 'view') && (
                                                                                        <button
                                                                                            onClick={() => {
                                                                                                if (!hasPermission('movil_ps_revisar', 'edit')) return;
                                                                                                recordPlanScroll('semanal', plan.id);
                                                                                                setWeeklyPlansBackup(JSON.parse(JSON.stringify(weeklyPlans)));
                                                                                                setActivePlanId(plan.id);
                                                                                                setIsWeeklyEditing(plan.status !== 'Cerrada');
                                                                                            }}
                                                                                            disabled={!hasPermission('movil_ps_revisar', 'edit')}
                                                                                            title={!hasPermission('movil_ps_revisar', 'edit') ? "Solo lectura: No tienes permisos para revisar desde la app" : "Ver o editar planificación semanal"}
                                                                                            className={`w-auto h-[31px] font-bold px-3 rounded-lg text-xs flex items-center justify-center gap-1 shadow-sm transition-all ${!hasPermission('movil_ps_revisar', 'edit')
                                                                                                    ? 'bg-slate-300 dark:bg-slate-700 text-slate-500 dark:text-slate-400 opacity-60 cursor-not-allowed'
                                                                                                    : 'bg-[#1a73e8] hover:bg-blue-600 text-white active:scale-95 cursor-pointer'
                                                                                                }`}
                                                                                        >
                                                                                            <span className="material-symbols-outlined text-[15px]">touch_app</span>
                                                                                            <span>Ver / Editar</span>
                                                                                        </button>
                                                                                    )}

                                                                                    <button
                                                                                        onClick={() => {
                                                                                            setAttendanceModalPlan(plan);
                                                                                            setAttendanceModalOpen(true);
                                                                                        }}
                                                                                        className={`h-[31px] px-2.5 rounded-lg border flex items-center justify-center transition-all cursor-pointer active:scale-95 shadow-sm ${isAttDone
                                                                                                ? 'bg-emerald-50 border-emerald-200 text-emerald-600 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-400'
                                                                                                : 'bg-slate-50 border-slate-200 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400'
                                                                                            }`}
                                                                                        title={isAttDone ? "Registro de Asistencia (Completado)" : "Registro de Asistencia (Pendiente)"}
                                                                                    >
                                                                                        <span className="material-symbols-outlined text-[17px]">how_to_reg</span>
                                                                                    </button>
                                                                                </div>

                                                                                <div className="flex items-center justify-end h-[31px] shrink-0 translate-y-[2px]">
                                                                                    <span className="text-[11px] text-slate-500 dark:text-slate-400 font-medium whitespace-nowrap text-right">
                                                                                        {plan.startDate} al {plan.endDate}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )
                        ) : (
                            // Standard Gantt View for Master
                            <>
                                <div className="min-w-max">
                                    {/* ... (Existing Gantt code) ... */}
                                    <div className="sticky top-0 z-30 flex flex-col border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-[#101822] shadow-sm h-[45px] box-border">
                                        {/* Top Header */}
                                        <div className="flex h-[24px] border-b border-slate-100 dark:border-slate-800">
                                            {timelineHeaders.map((h, i) => (
                                                <div
                                                    key={i}
                                                    className="flex items-center justify-center border-r border-slate-100 dark:border-slate-800 text-xs font-bold text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900 uppercase tracking-wide px-1 overflow-hidden text-ellipsis whitespace-nowrap shrink-0"
                                                    style={{ width: `${h.width}px` }}
                                                >
                                                    {h.label}
                                                </div>
                                            ))}
                                        </div>
                                        {/* Bottom Header */}
                                        <div className="flex h-[20px] bg-white dark:bg-[#101822]">
                                            {timelineColumns.map((date, i) => {
                                                let widthClass = "w-[100px]";
                                                let label: React.ReactNode = "";

                                                if (zoomLevel === 'days') {
                                                    widthClass = "w-[30px]";
                                                    const weekdayLetters = ["D", "L", "M", "M", "J", "V", "S"];
                                                    label = (
                                                        <div className="flex flex-col items-center justify-center text-[10px] text-slate-500 font-semibold h-full leading-none">
                                                            <span>{date.getDate()}</span>
                                                            <span className="text-[8px] text-slate-400 font-medium mt-0.5">{weekdayLetters[date.getDay()]}</span>
                                                        </div>
                                                    );
                                                } else if (zoomLevel === 'weeks') {
                                                    widthClass = "w-[100px]";
                                                    label = <span>{date.getDate()}</span>;
                                                } else if (zoomLevel === 'months') {
                                                    widthClass = "w-[120px]";
                                                    const shortMonths = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
                                                    label = <span>{shortMonths[date.getMonth()]}</span>;
                                                } else { // 'years'
                                                    widthClass = "w-[250px]";
                                                    label = <span>{date.getFullYear()}</span>;
                                                }

                                                return (
                                                    <div
                                                        key={i}
                                                        className={`${widthClass} shrink-0 border-r border-slate-100 dark:border-slate-800 flex items-center justify-center text-[11px] text-slate-500 font-medium`}
                                                    >
                                                        {label}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div className="relative">
                                        <DependencyArrows
                                            viewStartDate={gridStartDate}
                                            visibleTasks={visibleFlattenedTasks}
                                            zoomLevel={zoomLevel}
                                            workingDays={workingDays}
                                            holidays={holidays}
                                            timelineColumns={timelineColumns}
                                            selectedGanttTaskId={selectedGanttTaskId}
                                            taskPathInfo={taskPathInfo}
                                        />
                                        {renderGanttBars(processedTasks)}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* NEW Lookahead Modal */}
            {
                lookaheadModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-2xl w-[400px] border border-slate-200 dark:border-slate-800 overflow-hidden">
                            <div className="flex justify-between items-center p-4 border-b border-slate-100 dark:border-slate-800">
                                <h3 className="text-lg font-bold text-slate-800 dark:text-white">{editingLookaheadId ? 'Editar Fechas' : 'Planificación Intermedia'}</h3>
                                <button onClick={() => { setLookaheadModalOpen(false); setEditingLookaheadId(null); }} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>
                            <div className="p-6 flex flex-col gap-6">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Fecha Inicio</label>
                                        <input
                                            type="date"
                                            className="w-full rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white p-2.5 border focus:ring-1 focus:ring-purple-500 outline-none"
                                            value={lookaheadRange.start}
                                            onChange={(e) => setLookaheadRange({ ...lookaheadRange, start: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Fecha Término</label>
                                        <input
                                            type="date"
                                            className="w-full rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white p-2.5 border focus:ring-1 focus:ring-purple-500 outline-none"
                                            value={lookaheadRange.end}
                                            onChange={(e) => setLookaheadRange({ ...lookaheadRange, end: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-100 dark:border-purple-800 flex items-center justify-between">
                                    <div>
                                        <p className="text-xs text-purple-600 dark:text-purple-300 font-medium mb-1 uppercase">Horizonte</p>
                                        <div className="text-sm font-bold text-slate-800 dark:text-white">
                                            {calculatedWeeksAhead} Semana{calculatedWeeksAhead !== 1 ? 's' : ''}
                                        </div>
                                    </div>
                                    <span className="material-symbols-outlined text-purple-300 text-3xl">date_range</span>
                                </div>
                            </div>
                            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3">
                                <button
                                    onClick={() => { setLookaheadModalOpen(false); setEditingLookaheadId(null); }}
                                    className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleCreateLookaheadPlan}
                                    className="px-6 py-2 text-sm font-bold text-white bg-purple-600 hover:bg-purple-700 rounded transition-colors shadow-sm"
                                >
                                    {editingLookaheadId ? 'Guardar Cambios' : 'Generar'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* NEW ADD CONSTRAINT MODAL (ISOLATED & MEMOIZED) */}
            <ConstraintModal
                isOpen={constraintModalOpen}
                editingConstraintId={editingConstraintId}
                initialData={newConstraintData}
                constraintGroups={constraintGroups}
                sortedResponsibles={sortedResponsibles}
                onClose={() => setConstraintModalOpen(false)}
                onSave={(data) => {
                    setNewConstraintData(data);
                    handleSaveConstraint(data);
                }}
                onOpenUnitSelection={(currentData) => {
                    setNewConstraintData(currentData);
                    setUnitSelectionModalOpen(true);
                }}
            />

            {/* Context Menu */}
            {contextMenu.visible && (
                <div
                    className="fixed z-[100] bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 py-1 w-52 overflow-y-auto"
                    style={{ top: contextMenu.y, left: contextMenu.x, maxHeight: 'calc(100vh - 20px)' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Weekly View Context Menu */}
                    {activeTab === 'semanal' && isWeeklyEditing && (() => {
                        const taskObj = contextMenu.taskId ? findTaskById(contextMenu.taskId, tasks) : null;
                        if (!taskObj) return null;

                        let isCompleted = false;
                        try {
                            isCompleted = calculateTaskProgressLocal(taskObj) >= 100;
                        } catch {
                            isCompleted = (taskObj.progress ?? 0) >= 100;
                        }

                        const hasWeeklySubtasks = Boolean(taskObj.children && taskObj.children.some(c => c.isWeeklySubtask && !c.isDeleted));
                        const isDisabled = isCompleted || hasWeeklySubtasks;

                        let label = 'Programar tarea';
                        if (hasWeeklySubtasks) {
                            label = 'Desglosada en subtareas';
                        } else if (isCompleted) {
                            label = 'Tarea Completada (100%)';
                        }

                        return (
                            <button
                                onClick={() => contextMenu.taskId && !isDisabled && openPlanTaskModal(contextMenu.taskId)}
                                disabled={isDisabled}
                                className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${isDisabled
                                    ? 'text-slate-400 dark:text-slate-500 cursor-not-allowed opacity-60'
                                    : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700'
                                    }`}
                            >
                                <span className="material-symbols-outlined text-[18px]">
                                    {hasWeeklySubtasks ? 'account_tree' : 'calendar_today'}
                                </span>
                                {label}
                            </button>
                        );
                    })()}

                    {/* Breakdown (Desglose) Mode Context Menu */}
                    {isBreakdownMode && (() => {
                        const taskObj = contextMenu.taskId ? findTaskById(contextMenu.taskId, tasks) : null;
                        const isWeeklySubtask = taskObj?.isWeeklySubtask === true;
                        const hasWeeklyChildren = taskObj?.children && taskObj.children.some(c => c.isWeeklySubtask && !c.isDeleted);

                        return (
                            <>
                                {!isWeeklySubtask ? (
                                    <button
                                        onClick={() => contextMenu.taskId && openBreakdownModal(contextMenu.taskId)}
                                        className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                                    >
                                        <span className="material-symbols-outlined text-[18px] text-amber-500">
                                            {hasWeeklyChildren ? 'tune' : 'add_task'}
                                        </span>
                                        {hasWeeklyChildren ? 'Subtareas y Ponderadores' : 'Crear Subtarea'}
                                    </button>
                                ) : (
                                    <>
                                        <button
                                            onClick={() => contextMenu.taskId && openBreakdownModal(contextMenu.taskId)}
                                            className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                                        >
                                            <span className="material-symbols-outlined text-[18px] text-amber-500">
                                                {hasWeeklyChildren ? 'tune' : 'add_task'}
                                            </span>
                                            {hasWeeklyChildren ? 'Subtareas y Ponderadores' : 'Crear Subtarea'}
                                        </button>
                                        <button
                                            onClick={() => {
                                                const task = findTaskById(contextMenu.taskId, tasks);
                                                if (task) handleEditNameClick(task);
                                            }}
                                            className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                                        >
                                            <span className="material-symbols-outlined text-[18px]">edit</span>
                                            Editar Subtarea
                                        </button>
                                        <button
                                            onClick={() => contextMenu.taskId && handleDeleteItemClick(contextMenu.taskId)}
                                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                                        >
                                            <span className="material-symbols-outlined text-[18px]">delete</span>
                                            Eliminar Subtarea
                                        </button>
                                    </>
                                )}
                            </>
                        );
                    })()}

                    {/* Master Plan (Carta Gantt) Editing Context Menu */}
                    {activeTab === 'maestra' && isMasterEditing && (
                        <>
                            {(() => {
                                const t = findTaskById(contextMenu.taskId, tasks);
                                return t?.type === 'folder' ? (
                                    <>
                                        <button onClick={() => contextMenu.taskId && handleCreateSubtaskClick(contextMenu.taskId)} className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2">
                                            <span className="material-symbols-outlined text-[18px]">add_task</span>
                                            Crear Tarea
                                        </button>
                                        <button onClick={() => contextMenu.taskId && handleCreateFolderClick(contextMenu.taskId)} className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2">
                                            <span className="material-symbols-outlined text-[18px]">create_new_folder</span>
                                            Crear Subcarpeta
                                        </button>
                                    </>
                                ) : null;
                            })()}
                            <button
                                onClick={() => {
                                    const task = findTaskById(contextMenu.taskId, tasks);
                                    if (task) handleEditNameClick(task);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                            >
                                <span className="material-symbols-outlined text-[18px]">edit</span>
                                Editar
                            </button>
                            <button
                                onClick={() => contextMenu.taskId && handleMoveItemClick(contextMenu.taskId)}
                                className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                            >
                                <span className="material-symbols-outlined text-[18px]">drive_file_move</span>
                                Mover
                            </button>
                            <button
                                onClick={() => contextMenu.taskId && handleDeleteItemClick(contextMenu.taskId)}
                                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                            >
                                <span className="material-symbols-outlined text-[18px]">delete</span>
                                Eliminar
                            </button>
                            <div className="border-t border-slate-100 dark:border-slate-700 my-1"></div>
                            <button
                                onClick={() => contextMenu.taskId && handleToggleCritical(contextMenu.taskId)}
                                className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center gap-2 font-bold"
                            >
                                <span className="material-symbols-outlined text-[18px]">priority_high</span>
                                Marcar/Desmarcar Ruta Crítica
                            </button>
                        </>
                    )}
                </div>
            )}

            {/* Custom Delete Confirmation Modal */}
            {deleteConfirmModalOpen && itemToDeleteId && (() => {
                const taskToDelete = findTaskById(itemToDeleteId, tasks);
                const taskToDeleteName = taskToDelete ? cleanTaskName(taskToDelete.name) : 'Elemento seleccionado';

                return (
                    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-900/60 dark:bg-black/80 backdrop-blur-sm animate-in fade-in duration-200" onClick={() => setDeleteConfirmModalOpen(false)}>
                        <div className="bg-white dark:bg-[#1a2634] rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-200 dark:border-slate-700 animate-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
                            <div className="p-6 text-center space-y-4">
                                <div className="size-14 rounded-full bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-400 flex items-center justify-center mx-auto shadow-inner">
                                    <span className="material-symbols-outlined text-[32px]">delete_forever</span>
                                </div>
                                <div>
                                    <h3 className="text-lg font-black text-slate-800 dark:text-white">¿Eliminar {taskToDelete?.isWeeklySubtask ? 'Subtarea' : 'Elemento'}?</h3>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 font-medium">
                                        ¿Estás seguro de que deseas eliminar <strong className="text-slate-700 dark:text-slate-200">"{taskToDeleteName}"</strong>?
                                    </p>
                                    <p className="text-[11px] text-red-500 dark:text-red-400 font-bold mt-1">
                                        Esta acción no se puede deshacer.
                                    </p>
                                </div>
                            </div>
                            <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3">
                                <button
                                    onClick={() => {
                                        setDeleteConfirmModalOpen(false);
                                        setItemToDeleteId(null);
                                    }}
                                    className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={confirmDeleteTask}
                                    className="px-5 py-2 rounded-xl text-xs font-black text-white bg-red-600 hover:bg-red-700 transition-all shadow-md shadow-red-500/20 active:scale-95"
                                >
                                    Sí, Eliminar
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Input Modal */}
            {
                modalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl p-6 w-full max-w-md border border-slate-200 dark:border-slate-800">
                            <h3 className="text-lg font-bold mb-4 text-slate-900 dark:text-white">
                                {editingItemId
                                    ? (isBreakdownMode && modalMode === 'subtask' ? "Editar Subtarea" : "Editar Nombre")
                                    : (modalMode === 'folder' ? "Nueva Carpeta" : (isBreakdownMode ? "Nueva Subtarea" : "Nueva Tarea"))}
                            </h3>
                            <div className="mb-4">
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Nombre</label>
                                <input autoFocus type="text" value={inputName} onChange={(e) => setInputName(e.target.value)} className="w-full rounded-lg border-slate-300 dark:border-slate-700 p-2 border" onKeyDown={(e) => e.key === 'Enter' && confirmInputModal()} />
                            </div>
                            {isBreakdownMode && modalMode === 'subtask' && (
                                <div className="mb-4">
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Ponderación / Peso (%)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="any"
                                        value={inputWeight}
                                        onChange={(e) => setInputWeight(e.target.value)}
                                        placeholder="Ej: 50"
                                        className="w-full rounded-lg border-slate-300 dark:border-slate-700 p-2 border"
                                        onKeyDown={(e) => e.key === 'Enter' && confirmInputModal()}
                                    />
                                    <span className="text-[10px] text-slate-400 mt-1 block">Peso porcentual asignado a esta subtarea dentro de la tarea madre.</span>
                                </div>
                            )}
                            <div className="flex justify-end gap-2">
                                <button onClick={() => setModalOpen(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">Cancelar</button>
                                <button onClick={confirmInputModal} className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary hover:bg-blue-600">{editingItemId ? "Guardar" : "Crear"}</button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Breakdown & Subtasks Weight Management Modal */}
            {
                breakdownModalOpen && breakdownParentTaskId && (() => {
                    const parentTask = findTaskById(breakdownParentTaskId, tasks);
                    const parentName = parentTask ? cleanTaskName(parentTask.name) : 'Tarea Madre';
                    const parentCode = parentTask?.id_original_project !== undefined ? String(parentTask.id_original_project) : '';

                    // Compute distributed sum and remaining
                    const totalDistributed = draftSubtasks.reduce((sum, sub) => {
                        const val = sub.weight;
                        const num = val !== undefined && val.trim() !== '' ? parseFloat(val) : 0;
                        return sum + (!isNaN(num) ? num : 0);
                    }, 0);

                    const roundedDistributed = Math.round(totalDistributed * 100) / 100;
                    const remainingPercent = Math.round((100 - roundedDistributed) * 100) / 100;

                    // Remaining badge styling
                    let remainingClass = "bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800";
                    let remainingText = `${remainingPercent}%`;
                    if (remainingPercent === 0) {
                        remainingClass = "bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800";
                        remainingText = "0% (100% Asignado)";
                    } else if (remainingPercent < 0) {
                        remainingClass = "bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800";
                        remainingText = `${remainingPercent}% (Excedido)`;
                    }

                    return (
                        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200" onClick={() => setBreakdownModalOpen(false)}>
                            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
                                {/* Header */}
                                <div className="p-5 border-b border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-800/50 flex items-start justify-between">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="material-symbols-outlined text-amber-500 text-2xl">account_tree</span>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <h3 className="text-base font-bold text-slate-900 dark:text-white">
                                                        {draftSubtasks.length > 0 ? "Subtareas y Ponderadores" : "Crear Subtarea"}
                                                    </h3>
                                                    {parentCode && (
                                                        <span className="px-2 py-0.5 text-xs font-mono font-bold bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded">
                                                            #{parentCode}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-slate-600 dark:text-slate-300 font-semibold mt-0.5 truncate max-w-md" title={parentName}>
                                                    {parentName}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <button onClick={() => setBreakdownModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                                        <span className="material-symbols-outlined text-xl">close</span>
                                    </button>
                                </div>

                                {/* Summary Indicators (Ventana de Ponderadores) */}
                                <div className="grid grid-cols-2 gap-3 p-4 bg-slate-100/60 dark:bg-slate-800/40 border-b border-slate-200 dark:border-slate-800">
                                    <div className="p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center justify-between shadow-xs">
                                        <div>
                                            <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase block tracking-wider">% Asignado</span>
                                            <span className="text-xl font-extrabold text-slate-800 dark:text-white mt-0.5 block">{roundedDistributed}%</span>
                                        </div>
                                        <div className="size-10 rounded-full bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                                            <span className="material-symbols-outlined text-xl">pie_chart</span>
                                        </div>
                                    </div>

                                    <div className={`p-3 rounded-xl border flex items-center justify-between shadow-xs ${remainingClass}`}>
                                        <div>
                                            <span className="text-[11px] font-bold uppercase block tracking-wider opacity-80">% Restante</span>
                                            <span className="text-xl font-extrabold mt-0.5 block">
                                                {remainingText}
                                            </span>
                                        </div>
                                        <div className="size-10 rounded-full bg-white/60 dark:bg-slate-900/60 flex items-center justify-center">
                                            <span className="material-symbols-outlined text-xl">
                                                {remainingPercent === 0 ? 'check_circle' : remainingPercent < 0 ? 'error' : 'hourglass_empty'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Toolbar with "Crear Subtarea" button */}
                                <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-800/40 flex items-center justify-between">
                                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                                        <span className="material-symbols-outlined text-[16px] text-amber-500">list</span>
                                        Subtareas ({draftSubtasks.length})
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setNewSubtaskName("");
                                            setNewSubtaskWeight("");
                                            setIsAddingSubtask(true);
                                            setTimeout(() => newSubtaskInputRef.current?.focus(), 50);
                                        }}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-amber-600 hover:bg-amber-700 shadow-sm transition-all active:scale-95 cursor-pointer"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">add_task</span>
                                        <span>Crear Subtarea</span>
                                    </button>
                                </div>

                                {/* Form to create new subtask (appears when clicking "Crear Subtarea") */}
                                {isAddingSubtask && (
                                    <div className="p-4 bg-amber-50/70 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800 animate-in fade-in duration-150">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-xs font-bold text-amber-900 dark:text-amber-200 flex items-center gap-1.5">
                                                <span className="material-symbols-outlined text-[16px]">add_circle</span>
                                                Nueva Subtarea
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setIsAddingSubtask(false)}
                                                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-xs font-medium"
                                            >
                                                Cancelar
                                            </button>
                                        </div>
                                        <div className="flex flex-col sm:flex-row gap-2">
                                            <div className="flex-1">
                                                <input
                                                    ref={newSubtaskInputRef}
                                                    type="text"
                                                    autoFocus
                                                    value={newSubtaskName}
                                                    onChange={(e) => setNewSubtaskName(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            handleAddDraftSubtask();
                                                        }
                                                    }}
                                                    placeholder="Nombre de la subtarea (ej: Enfierradura, Moldaje...)"
                                                    className="w-full text-xs px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500 outline-none"
                                                />
                                            </div>
                                            <div className="w-full sm:w-28 flex items-center gap-1 shrink-0">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    step="any"
                                                    value={newSubtaskWeight}
                                                    onChange={(e) => setNewSubtaskWeight(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            handleAddDraftSubtask();
                                                        }
                                                    }}
                                                    placeholder={remainingPercent > 0 ? String(remainingPercent) : "0"}
                                                    className="w-full text-xs px-2.5 py-2 text-right font-bold rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500 outline-none"
                                                />
                                                <span className="text-xs font-bold text-slate-500">%</span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={handleAddDraftSubtask}
                                                disabled={!newSubtaskName.trim()}
                                                className="px-4 py-2 rounded-lg text-xs font-bold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:pointer-events-none transition-all flex items-center justify-center gap-1 shadow-sm active:scale-95 cursor-pointer"
                                            >
                                                <span className="material-symbols-outlined text-[16px]">check</span>
                                                <span>Agregar</span>
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* List of Subtasks and Ponderadores with Drag & Drop Reorder */}
                                <div className="p-4 flex-1 overflow-y-auto space-y-2 min-h-[160px] max-h-[320px]">
                                    {draftSubtasks.length === 0 ? (
                                        <div className="text-center py-8 text-slate-400 border border-dashed border-slate-200 dark:border-slate-800 rounded-xl flex flex-col items-center justify-center gap-2">
                                            <span className="material-symbols-outlined text-3xl text-slate-300 dark:text-slate-600">playlist_add</span>
                                            <p className="text-xs italic">Aún no hay subtareas en esta partida.</p>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setNewSubtaskName("");
                                                    setNewSubtaskWeight("");
                                                    setIsAddingSubtask(true);
                                                    setTimeout(() => newSubtaskInputRef.current?.focus(), 50);
                                                }}
                                                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-950/40 hover:bg-amber-200 transition-colors cursor-pointer"
                                            >
                                                <span className="material-symbols-outlined text-[16px]">add_task</span>
                                                <span>Crear primera subtarea</span>
                                            </button>
                                        </div>
                                    ) : (
                                        draftSubtasks.map((sub, index) => {
                                            const correlative = sub.id_original_project || (parentCode ? `${parentCode}.${index + 1}` : String(index + 1));
                                            return (
                                                <div
                                                    key={sub.id}
                                                    draggable={true}
                                                    onDragStart={(e) => {
                                                        setDraggedSubtaskIndex(index);
                                                        e.dataTransfer.effectAllowed = 'move';
                                                    }}
                                                    onDragOver={(e) => {
                                                        e.preventDefault();
                                                        e.dataTransfer.dropEffect = 'move';
                                                    }}
                                                    onDrop={(e) => {
                                                        e.preventDefault();
                                                        if (draggedSubtaskIndex === null || draggedSubtaskIndex === index) return;
                                                        setDraftSubtasks(prev => {
                                                            const updated = [...prev];
                                                            const [movedItem] = updated.splice(draggedSubtaskIndex, 1);
                                                            updated.splice(index, 0, movedItem);

                                                            const pTask = breakdownParentTaskId ? findTaskById(breakdownParentTaskId, tasks) : null;
                                                            const pCode = pTask?.id_original_project !== undefined ? String(pTask.id_original_project) : '';
                                                            return updated.map((it, idx) => ({
                                                                ...it,
                                                                id_original_project: pCode ? `${pCode}.${idx + 1}` : String(idx + 1)
                                                            }));
                                                        });
                                                        setDraggedSubtaskIndex(null);
                                                    }}
                                                    className={`flex items-center justify-between gap-2.5 p-2.5 bg-slate-50 dark:bg-slate-800/50 rounded-xl border transition-all ${draggedSubtaskIndex === index
                                                            ? 'border-amber-400 opacity-40 scale-[0.99]'
                                                            : 'border-slate-200 dark:border-slate-700/80 hover:border-slate-300 dark:hover:border-slate-600'
                                                        }`}
                                                >
                                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                                        <span className="text-xs font-mono font-bold text-slate-400 dark:text-slate-500 w-10 shrink-0">
                                                            {correlative}
                                                        </span>
                                                        <input
                                                            type="text"
                                                            value={sub.name}
                                                            onChange={(e) => handleUpdateDraftName(sub.id, e.target.value)}
                                                            className="text-xs font-semibold text-slate-800 dark:text-slate-100 bg-transparent hover:bg-white dark:hover:bg-slate-800 focus:bg-white dark:focus:bg-slate-800 px-2 py-1 rounded border border-transparent hover:border-slate-300 dark:hover:border-slate-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none w-full transition-all"
                                                            placeholder="Nombre de subtarea"
                                                            title="Haz clic para editar el nombre de la subtarea"
                                                        />
                                                    </div>

                                                    <div className="flex items-center gap-2 shrink-0">
                                                        <div className="flex items-center gap-1">
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                max="100"
                                                                step="any"
                                                                value={sub.weight}
                                                                placeholder="0"
                                                                onChange={(e) => handleUpdateDraftWeight(sub.id, e.target.value)}
                                                                className="w-20 px-2 py-1 text-right font-bold text-xs bg-white dark:bg-slate-900 rounded-lg border border-slate-300 dark:border-slate-600 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                                                                title="Ponderador porcentual"
                                                            />
                                                            <span className="text-xs font-bold text-slate-400 dark:text-slate-500">%</span>
                                                        </div>

                                                        <button
                                                            type="button"
                                                            onClick={() => handleDeleteDraftSubtask(sub.id)}
                                                            className="p-1.5 text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors cursor-pointer"
                                                            title="Eliminar subtarea del desglose"
                                                        >
                                                            <span className="material-symbols-outlined text-[18px]">delete</span>
                                                        </button>

                                                        <div
                                                            className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 flex items-center transition-colors select-none"
                                                            title="Arrastra para cambiar el orden de esta subtarea"
                                                        >
                                                            <span className="material-symbols-outlined text-[20px]">drag_indicator</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>

                                {/* Footer Actions */}
                                <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-800/50 flex items-center justify-between gap-3">
                                    <div className="text-[11px] text-slate-500 dark:text-slate-400">
                                        {remainingPercent < 0 ? (
                                            <span className="text-red-500 font-bold flex items-center gap-1">
                                                <span className="material-symbols-outlined text-[14px]">warning</span>
                                                Atención: la suma supera el 100%.
                                            </span>
                                        ) : remainingPercent > 0 ? (
                                            <span className="text-amber-600 dark:text-amber-400 font-medium">
                                                Saldo sin asignar: {remainingPercent}%
                                            </span>
                                        ) : (
                                            <span className="text-green-600 dark:text-green-400 font-bold flex items-center gap-1">
                                                <span className="material-symbols-outlined text-[14px]">check_circle</span>
                                                Ponderación 100% distribuida.
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setBreakdownModalOpen(false)}
                                            className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleSaveBreakdownModal}
                                            className="px-5 py-2 rounded-xl text-xs font-bold text-white bg-primary hover:bg-blue-600 transition-all shadow-md shadow-primary/20 active:scale-95"
                                        >
                                            Guardar Desglose
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })()
            }


            {/* Move Item Modal */}
            {
                moveModalOpen && movingItemId && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-slate-800 flex flex-col max-h-[80vh]">
                            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                                <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                    <span className="material-symbols-outlined text-primary">drive_file_move</span>
                                    Mover Elemento
                                </h3>
                                <button onClick={() => setMoveModalOpen(false)} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
                            </div>

                            <div className="p-4 bg-slate-50 dark:bg-slate-800/30 border-b border-slate-200 dark:border-slate-700 text-xs text-slate-500">
                                Seleccione la carpeta o ubicación de destino para el elemento:
                                <strong className="block text-slate-700 dark:text-slate-200 mt-1 text-sm font-semibold truncate">
                                    {findTaskById(movingItemId, tasks)?.name || 'Elemento'}
                                </strong>
                            </div>

                            <div className="flex-1 overflow-y-auto p-4 space-y-2">
                                {/* Option 1: Root Level */}
                                <button
                                    onClick={() => handleMoveItem(null)}
                                    className="w-full text-left p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-primary/5 hover:border-primary transition-all flex items-center gap-2.5 group"
                                >
                                    <span className="material-symbols-outlined text-slate-400 group-hover:text-primary">home_work</span>
                                    <div>
                                        <span className="font-bold text-slate-800 dark:text-white text-sm block">[ Raíz del Proyecto ]</span>
                                        <span className="text-[10px] text-slate-400">Mover al nivel principal de la Carta Gantt</span>
                                    </div>
                                </button>

                                <div className="border-b border-slate-100 dark:border-slate-800 my-3"></div>

                                {/* Option 2: Folders list */}
                                {(() => {
                                    const validFolders = getValidTargetFolders(tasks, movingItemId);
                                    if (validFolders.length === 0) {
                                        return (
                                            <p className="text-center text-slate-400 italic py-4 text-xs">
                                                No hay otras carpetas de destino válidas disponibles.
                                            </p>
                                        );
                                    }
                                    return (
                                        <div className="space-y-1.5">
                                            <p className="text-[10px] uppercase font-black tracking-wider text-slate-400 mb-2">Carpetas Disponibles</p>
                                            {validFolders.map(folder => (
                                                <button
                                                    key={folder.id}
                                                    onClick={() => handleMoveItem(folder.id)}
                                                    style={{ paddingLeft: `${folder.level * 16 + 12}px` }}
                                                    className="w-full text-left p-2.5 rounded-lg border border-slate-100 dark:border-slate-800 hover:bg-primary/5 hover:border-primary transition-all flex items-center gap-2 group"
                                                >
                                                    <span className="material-symbols-outlined text-amber-500 group-hover:text-primary text-[18px]">folder</span>
                                                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{folder.name}</span>
                                                </button>
                                            ))}
                                        </div>
                                    );
                                })()}
                            </div>

                            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3 border-t border-slate-200 dark:border-slate-700">
                                <button
                                    onClick={() => setMoveModalOpen(false)}
                                    className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                >
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }


            {/* CNC Import Modal */}
            {
                cncModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-2xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[85vh]">
                            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                                <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                    <span className="material-symbols-outlined text-orange-500">warning</span>
                                    Importar Causas de No Cumplimiento desde Maestro Global
                                </h3>
                                <button onClick={() => setCncModalOpen(false)} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                {globalCNCGroups.length === 0 ? (
                                    <div className="text-center py-10 text-slate-400">
                                        <p>No hay grupos en el Maestro Global.</p>
                                    </div>
                                ) : globalCNCGroups.map(group => (
                                    <div key={group.id} className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                                        <div className="bg-slate-50 dark:bg-slate-800/80 px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                                            <span className="font-bold text-slate-800 dark:text-white">{group.name}</span>
                                            <button
                                                onClick={() => {
                                                    const causeIds = group.causes.map(c => c.id);
                                                    const allIncluded = causeIds.every(id => cncGroups.some(g => g.causes.some(c => c.id === id)));

                                                    let newGroups = [...cncGroups];

                                                    if (allIncluded) {
                                                        newGroups = newGroups.map(g => ({
                                                            ...g,
                                                            causes: g.causes.filter(c => !causeIds.includes(c.id))
                                                        })).filter(g => g.causes.length > 0);
                                                    } else {
                                                        let targetGroup = newGroups.find(g => g.id === group.id);
                                                        if (!targetGroup) {
                                                            targetGroup = { id: group.id, name: group.name, causes: [] };
                                                            newGroups.push(targetGroup);
                                                        }

                                                        causeIds.forEach(id => {
                                                            if (!targetGroup.causes.some(c => c.id === id)) {
                                                                const cause = group.causes.find(c => c.id === id);
                                                                targetGroup.causes.push(cause);
                                                            }
                                                        });
                                                    }
                                                    setCncGroups([...newGroups]);
                                                }}
                                                className="text-xs bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 px-2 py-1 rounded hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                                            >
                                                Seleccionar Todo
                                            </button>
                                        </div>
                                        <div className="p-2 bg-white dark:bg-slate-900 grid grid-cols-2 gap-2">
                                            {group.causes.map(cause => {
                                                const isIncluded = cncGroups.some(g => g.causes.some(c => c.id === cause.id));
                                                return (
                                                    <label key={cause.id} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            className="rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                                                            checked={isIncluded}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    let targetGroup = cncGroups.find(g => g.id === group.id);
                                                                    if (targetGroup) {
                                                                        setCncGroups(cncGroups.map(g => g.id === group.id ? { ...g, causes: [...g.causes, cause] } : g));
                                                                    } else {
                                                                        setCncGroups([...cncGroups, { id: group.id, name: group.name, causes: [cause] }]);
                                                                    }
                                                                } else {
                                                                    setCncGroups(cncGroups.map(g => {
                                                                        if (g.id === group.id) {
                                                                            return { ...g, causes: g.causes.filter(c => c.id !== cause.id) };
                                                                        }
                                                                        return g;
                                                                    }).filter(g => g.causes.length > 0));
                                                                }
                                                            }}
                                                        />
                                                        <span className="text-sm text-slate-700 dark:text-slate-300">{cause.name}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex justify-end">
                                <button onClick={() => setCncModalOpen(false)} className="bg-primary text-white px-4 py-2 rounded-lg font-bold hover:bg-blue-600 transition-colors">
                                    Cerrar y Guardar
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }


            {/* Constraints Import Modal */}
            {
                constraintsModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-2xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[85vh]">
                            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                                <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                    <span className="material-symbols-outlined text-purple-500">block</span>
                                    Importar Restricciones desde Maestro Global
                                </h3>
                                <button onClick={() => setConstraintsModalOpen(false)} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                {globalConstraintGroups.length === 0 ? (
                                    <div className="text-center py-10 text-slate-400">
                                        <p>No hay grupos en el Maestro Global.</p>
                                    </div>
                                ) : globalConstraintGroups.map(group => (
                                    <div key={group.id} className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                                        <div className="bg-slate-50 dark:bg-slate-800/80 px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                                            <span className="font-bold text-slate-800 dark:text-white">{group.name}</span>
                                            <button
                                                onClick={() => {
                                                    const defIds = group.definitions.map(d => d.id);
                                                    const allIncluded = defIds.every(id => constraintGroups.some(g => g.definitions.some(d => d.id === id)));

                                                    let newGroups = [...constraintGroups];

                                                    if (allIncluded) {
                                                        newGroups = newGroups.map(g => ({
                                                            ...g,
                                                            definitions: g.definitions.filter(d => !defIds.includes(d.id))
                                                        })).filter(g => g.definitions.length > 0);
                                                    } else {
                                                        let targetGroup = newGroups.find(g => g.id === group.id);
                                                        if (!targetGroup) {
                                                            targetGroup = { id: group.id, name: group.name, definitions: [] };
                                                            newGroups.push(targetGroup);
                                                        }

                                                        defIds.forEach(id => {
                                                            if (!targetGroup.definitions.some(d => d.id === id)) {
                                                                const def = group.definitions.find(d => d.id === id);
                                                                targetGroup.definitions.push(def);
                                                            }
                                                        });
                                                    }
                                                    setConstraintGroups([...newGroups]);
                                                }}
                                                className="text-xs bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 px-2 py-1 rounded hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                                            >
                                                Seleccionar Todo
                                            </button>
                                        </div>
                                        <div className="p-2 bg-white dark:bg-slate-900 grid grid-cols-2 gap-2">
                                            {group.definitions.map(def => {
                                                const isIncluded = constraintGroups.some(g => g.definitions.some(d => d.id === def.id));
                                                return (
                                                    <label key={def.id} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            className="rounded border-slate-300 text-purple-500 focus:ring-purple-500"
                                                            checked={isIncluded}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    let targetGroup = constraintGroups.find(g => g.id === group.id);
                                                                    if (targetGroup) {
                                                                        setConstraintGroups(constraintGroups.map(g => g.id === group.id ? { ...g, definitions: [...g.definitions, def] } : g));
                                                                    } else {
                                                                        setConstraintGroups([...constraintGroups, { id: group.id, name: group.name, definitions: [def] }]);
                                                                    }
                                                                } else {
                                                                    setConstraintGroups(constraintGroups.map(g => {
                                                                        if (g.id === group.id) {
                                                                            return { ...g, definitions: g.definitions.filter(d => d.id !== def.id) };
                                                                        }
                                                                        return g;
                                                                    }).filter(g => g.definitions.length > 0));
                                                                }
                                                            }}
                                                        />
                                                        <span className="text-sm text-slate-700 dark:text-slate-300">{def.name}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex justify-end">
                                <button onClick={() => setConstraintsModalOpen(false)} className="bg-primary text-white px-4 py-2 rounded-lg font-bold hover:bg-blue-600 transition-colors">
                                    Cerrar y Guardar
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Plan Task Modal */}
            <PlanTaskModal
                isOpen={planTaskModalOpen}
                planningTaskId={planningTaskId}
                initialAllocations={currentAllocations}
                activePlanId={activePlanId}
                showGanttSuggestions={showGanttSuggestions}
                units={units}
                levels={levels}
                tasks={tasks}
                sortedResponsibles={sortedResponsibles}
                dynamicDayColumns={dynamicDayColumns}
                weeklyPlans={weeklyPlans}
                protocolConfigs={protocolConfigs}
                savedProtocols={savedProtocols}
                onClose={() => setPlanTaskModalOpen(false)}
                onSave={handleSaveAllocations}
                onExportExcel={exportWeeklyPlanToExcel}
            />

            {/* Nueva Planificación Modal (Weekly) */}
            {
                newPlanModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-2xl w-[400px] border border-slate-200 dark:border-slate-800 overflow-hidden">
                            <div className="flex justify-between items-center p-4 border-b border-slate-100 dark:border-slate-800">
                                <h3 className="text-lg font-bold text-slate-800 dark:text-white">{editingWeeklyPlanId ? 'Editar Fechas Semanales' : 'Nueva Planificación Semanal'}</h3>
                                <button onClick={() => { setNewPlanModalOpen(false); setEditingWeeklyPlanId(null); }} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>
                            <div className="p-6 flex flex-col gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Fecha Inicio</label>
                                    <input
                                        type="date"
                                        value={weeklyPlanDates.start}
                                        onChange={(e) => setWeeklyPlanDates({ ...weeklyPlanDates, start: e.target.value })}
                                        className="w-full rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white p-2.5 border focus:ring-1 focus:ring-primary outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Fecha Término</label>
                                    <input
                                        type="date"
                                        value={weeklyPlanDates.end}
                                        onChange={(e) => setWeeklyPlanDates({ ...weeklyPlanDates, end: e.target.value })}
                                        className="w-full rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white p-2.5 border focus:ring-1 focus:ring-primary outline-none"
                                    />
                                </div>
                            </div>
                            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3">
                                <button
                                    onClick={() => { setNewPlanModalOpen(false); setEditingWeeklyPlanId(null); }}
                                    className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleCreateWeeklyPlan}
                                    className="px-6 py-2 text-sm font-bold text-white bg-primary hover:bg-blue-600 rounded transition-colors shadow-sm"
                                >
                                    {editingWeeklyPlanId ? 'Guardar Cambios' : 'Comenzar'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }


            {/* MOCK Document Manager Modal */}
            {
                docManagerModalOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-fade-in">
                        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-[600px] border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col animate-scale-up">
                            <div className="flex justify-between items-center p-5 border-b border-slate-100 dark:border-slate-800 bg-emerald-500 text-white">
                                <div>
                                    <h3 className="text-lg font-bold flex items-center gap-2">
                                        <span className="material-symbols-outlined">folder_managed</span>
                                        Seleccionar desde Gestor Documental
                                    </h3>
                                    <p className="text-emerald-100 text-xs mt-1">
                                        Mostrando archivos vinculados a {projectInfo.name}
                                    </p>
                                </div>
                                <button onClick={() => setDocManagerModalOpen(false)} className="hover:bg-white/20 p-2 rounded-full transition-colors">
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>

                            <div className="p-0 max-h-[400px] overflow-y-auto">
                                <table className="w-full text-left">
                                    <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0">
                                        <tr>
                                            <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Archivo</th>
                                            <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Estado</th>
                                            <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Acción</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {[
                                            { id: 1, name: 'Cronograma_Maestro_V2.mpp', date: 'Hoy, 10:30', version: 2, status: 'Vigente' },
                                            { id: 2, name: 'Cronograma_Maestro_V1.mpp', date: '20 Mar 2026', version: 1, status: 'Histórico' }
                                        ].map(doc => (
                                            <tr key={doc.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                                <td className="p-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-xl flex items-center justify-center">
                                                            <span className="material-symbols-outlined">account_tree</span>
                                                        </div>
                                                        <div>
                                                            <p className="font-bold text-sm text-slate-800 dark:text-white">{doc.name}</p>
                                                            <p className="text-xs text-slate-500 mt-0.5">Subido el {doc.date} • Versión {doc.version}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="p-4">
                                                    <span className={`px-2 py-1 rounded text-xs font-bold ${doc.status === 'Vigente'
                                                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                                                        : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                                                        }`}>
                                                        {doc.status}
                                                    </span>
                                                </td>
                                                <td className="p-4 text-right">
                                                    <button
                                                        onClick={() => {
                                                            alert(`Simulación: Archivo "${doc.name}" seleccionado correctamente del Gestor Documental.\n\nEn la versión de producción (Supabase), esto descargará e inyectará el MS Project transparente para el Project Manager.`);
                                                            setDocManagerModalOpen(false);
                                                        }}
                                                        className="px-4 py-2 bg-slate-100 hover:bg-emerald-500 text-slate-700 hover:text-white dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-emerald-600 text-sm font-bold rounded-lg transition-colors"
                                                    >
                                                        Importar
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="p-4 bg-slate-50 dark:bg-[#1e2c3c] flex justify-between items-center border-t border-slate-100 dark:border-slate-800">
                                <span className="text-xs text-slate-500 flex items-center gap-1">
                                    <span className="material-symbols-outlined text-[14px]">info</span>
                                    Mostrando datos simulados
                                </span>
                                <button
                                    onClick={() => setDocManagerModalOpen(false)}
                                    className="px-5 py-2 text-sm font-bold text-slate-500 hover:text-slate-800 dark:hover:text-white transition-colors"
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Calendar Configuration Modal */}
            {calendarModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-[#1a2634] rounded-2xl shadow-2xl w-full max-w-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col animate-in zoom-in-95 duration-200 max-h-[85vh]">
                        {/* Header */}
                        <div className="flex justify-between items-center p-6 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-[#1e2c3c]">
                            <div>
                                <h3 className="text-xl font-black text-slate-800 dark:text-white tracking-tight">Configuración de Calendario</h3>
                                <p className="text-sm text-slate-500 font-medium mt-1">Configura la jornada laboral y los días festivos del proyecto (estilo MS Project)</p>
                            </div>
                            <button onClick={() => setCalendarModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors bg-white dark:bg-slate-800 p-2 rounded-full shadow-sm border border-slate-200 dark:border-slate-700">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-6 overflow-y-auto space-y-6 bg-white dark:bg-[#1a2634] flex-1">
                            {/* Standard Work Week */}
                            <div>
                                <h4 className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-3">Jornada Laboral Estándar</h4>
                                <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-800">
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">Selecciona los días hábiles de la semana para la ejecución de las tareas:</p>
                                    <div className="grid grid-cols-7 gap-2">
                                        {['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'].map((day, idx) => {
                                            const isChecked = tempWorkingDays[idx];
                                            return (
                                                <label
                                                    key={day}
                                                    className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 cursor-pointer transition-all ${isChecked
                                                        ? 'bg-blue-50/70 border-blue-500 text-blue-700 dark:bg-blue-900/20 dark:border-blue-500 dark:text-blue-400 font-bold'
                                                        : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-400 dark:hover:bg-slate-800/50'
                                                        }`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="sr-only"
                                                        checked={isChecked}
                                                        onChange={(e) => {
                                                            const updated = [...tempWorkingDays];
                                                            updated[idx] = e.target.checked;
                                                            setTempWorkingDays(updated);
                                                        }}
                                                    />
                                                    <span className="text-xs uppercase font-semibold">{day.slice(0, 3)}</span>
                                                    <span className="material-symbols-outlined text-lg mt-1">
                                                        {isChecked ? 'check_circle' : 'circle'}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* Daily Working Hours */}
                            <div>
                                <h4 className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-3">Horas de jornada laboral diaria</h4>
                                <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-800 flex items-center gap-4">
                                    <p className="text-xs text-slate-500 dark:text-slate-400 flex-1">
                                        Ingresa la cantidad de horas laborables equivalentes a un día de trabajo (por defecto 8 horas, útil para sincronizar con MS Project):
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="number"
                                            min="1"
                                            max="24"
                                            step="0.5"
                                            value={tempHoursPerDay}
                                            onChange={(e) => setTempHoursPerDay(parseFloat(e.target.value) || 8)}
                                            className="w-24 text-sm font-bold text-center p-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <span className="text-xs font-bold text-slate-500">horas/día</span>
                                    </div>
                                </div>
                            </div>

                            {/* Exceptions */}
                            <div>
                                <div className="flex justify-between items-center mb-3">
                                    <h4 className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Excepciones (Días Festivos / No Laborables)</h4>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setTempHolidays([...tempHolidays, { date: getLocalDateStr(new Date()), name: 'Nuevo Festivo' }]);
                                        }}
                                        className="flex items-center gap-1 text-xs font-bold text-blue-600 dark:text-blue-400 hover:underline bg-blue-50 dark:bg-blue-950/30 px-3 py-1.5 rounded-lg border border-blue-100 dark:border-blue-900/40"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">add</span>
                                        + Agregar Día Festivo
                                    </button>
                                </div>

                                <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden bg-white dark:bg-slate-900 shadow-sm">
                                    <div className="max-h-64 overflow-y-auto">
                                        {tempHolidays.length === 0 ? (
                                            <div className="p-8 text-center text-slate-400 dark:text-slate-500 flex flex-col items-center">
                                                <span className="material-symbols-outlined text-3xl mb-1">event_busy</span>
                                                <p className="text-xs font-medium">No hay días festivos configurados.</p>
                                            </div>
                                        ) : (
                                            <table className="w-full text-left border-collapse">
                                                <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10">
                                                    <tr>
                                                        <th className="px-4 py-3 text-[11px] font-bold text-slate-400 uppercase tracking-wider w-2/3">Descripción del Festivo</th>
                                                        <th className="px-4 py-3 text-[11px] font-bold text-slate-400 uppercase tracking-wider w-1/3">Fecha</th>
                                                        <th className="px-4 py-3 w-12 text-center"></th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                                    {tempHolidays.map((holiday, idx) => (
                                                        <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20">
                                                            <td className="px-4 py-2">
                                                                <input
                                                                    type="text"
                                                                    value={holiday.name}
                                                                    onChange={(e) => {
                                                                        const updated = [...tempHolidays];
                                                                        updated[idx].name = e.target.value;
                                                                        setTempHolidays(updated);
                                                                    }}
                                                                    className="w-full text-xs p-2 border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 dark:text-white focus:border-blue-500 outline-none"
                                                                    placeholder="Nombre del festivo (ej. Año Nuevo)"
                                                                />
                                                            </td>
                                                            <td className="px-4 py-2">
                                                                <input
                                                                    type="date"
                                                                    value={holiday.date}
                                                                    onChange={(e) => {
                                                                        const updated = [...tempHolidays];
                                                                        updated[idx].date = e.target.value;
                                                                        setTempHolidays(updated);
                                                                    }}
                                                                    className="w-full text-xs p-2 border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 dark:text-white focus:border-blue-500 outline-none"
                                                                />
                                                            </td>
                                                            <td className="px-4 py-2 text-center">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setTempHolidays(tempHolidays.filter((_, i) => i !== idx));
                                                                    }}
                                                                    className="text-slate-400 hover:text-red-500 transition-colors p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
                                                                >
                                                                    <span className="material-symbols-outlined text-[18px]">delete</span>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-6 bg-slate-50 dark:bg-[#1e2c3c] border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3 shrink-0">
                            <button
                                onClick={() => setCalendarModalOpen(false)}
                                className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSaveCalendar}
                                className="px-6 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm flex items-center gap-1.5"
                            >
                                <span className="material-symbols-outlined text-[18px]">save</span>
                                Guardar Configuración
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Import Gantt Modal */}
            {
                importModalOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-fade-in">
                        <div className="bg-white dark:bg-[#1a2634] rounded-2xl shadow-2xl w-full max-w-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col animate-scale-up">
                            <div className="flex justify-between items-center p-6 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-[#1e2c3c]">
                                <div>
                                    <h3 className="text-xl font-black text-slate-800 dark:text-white tracking-tight">Importar Carta Gantt</h3>
                                    <p className="text-sm text-slate-500 font-medium mt-1">Selecciona el origen del archivo de planificación</p>
                                </div>
                                <button onClick={() => setImportModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors bg-white dark:bg-slate-800 p-2 rounded-full shadow-sm border border-slate-200 dark:border-slate-700">
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>

                            <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50/50 dark:bg-[#0d131b]/50">
                                {/* Option 1: Local PC */}
                                <label className="relative group flex flex-col items-center justify-center p-8 bg-white dark:bg-[#1a2634] border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-2xl hover:border-primary dark:hover:border-primary hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-all cursor-pointer overflow-hidden">
                                    <input
                                        type="file"
                                        accept=".xml,.mpp"
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                        onChange={handleFileImport}
                                    />
                                    <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 text-primary rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-inner">
                                        <span className="material-symbols-outlined text-3xl">upload_file</span>
                                    </div>
                                    <h4 className="text-lg font-bold text-slate-800 dark:text-white mb-2 text-center group-hover:text-primary transition-colors">Subir desde mi PC</h4>
                                    <p className="text-xs text-slate-500 text-center font-medium px-4">Archivos locales .xml o .mpp</p>

                                    <div className="absolute inset-0 border-2 border-primary rounded-2xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity"></div>
                                </label>

                                {/* Option 2: Document Manager */}
                                <div
                                    onClick={() => {
                                        setDocManagerModalType('import');
                                        setDocManagerModalOpen(true);
                                        setImportModalOpen(false);
                                    }}
                                    className="relative group flex flex-col items-center justify-center p-8 bg-white dark:bg-[#1a2634] border-2 border-slate-200 dark:border-slate-800 rounded-2xl hover:border-emerald-500 dark:hover:border-emerald-500 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10 transition-all cursor-pointer shadow-sm"
                                >
                                    <div className="absolute top-4 right-4 flex h-6 w-6">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-6 w-6 bg-emerald-500 text-white items-center justify-center">
                                            <span className="material-symbols-outlined text-[14px]">star</span>
                                        </span>
                                    </div>

                                    <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-inner">
                                        <span className="material-symbols-outlined text-3xl">folder_managed</span>
                                    </div>
                                    <h4 className="text-lg font-bold text-slate-800 dark:text-white mb-2 text-center group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">Gestor Documental</h4>
                                    <p className="text-xs text-slate-500 text-center font-medium px-4">Seleccionar un archivo previamente cargado o vigente</p>
                                </div>
                            </div>

                            <div className="p-4 bg-slate-50 dark:bg-[#1e2c3c] flex justify-end gap-3 border-t border-slate-100 dark:border-slate-800">
                                <button
                                    onClick={() => setImportModalOpen(false)}
                                    className="px-6 py-2.5 text-sm font-bold text-slate-500 hover:text-slate-800 dark:hover:text-white transition-colors"
                                >
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Gantt Import Wizard Modal */}
            {importWizardModalOpen && wizardData && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200">
                    <div className={`bg-white dark:bg-slate-900 shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 duration-200 flex flex-col transition-all duration-200 ${isWizardFullscreen
                        ? 'fixed inset-0 w-screen h-screen z-[100] max-w-none max-h-none rounded-none'
                        : 'rounded-2xl w-full max-w-4xl max-h-[90vh]'
                        }`}>
                        <div className="bg-[#1a73e8] p-6 text-white flex justify-between items-center">
                            <div>
                                <h2 className="text-xl font-bold flex items-center gap-2">
                                    <span className="material-symbols-outlined">auto_awesome_motion</span>
                                    Asistente de Configuración de Importación
                                </h2>
                                <h3 className="text-xs text-blue-100 font-semibold uppercase tracking-wider mt-1">
                                    Paso {wizardStep} de {wizardSteps.length}
                                </h3>
                                <p className="text-xs text-blue-50/80 mt-0.5">
                                    {activeStepType === 'distribution' && "Valida si la Carta Gantt importada trae avances físicos y realiza su distribución."}
                                    {activeStepType === 'reconciliation' && "Asigna y concilia manualmente el % de avance en los desgloses semanales de las tareas con avance parcial."}
                                    {activeStepType === 'calendar' && "Define los días laborables, feriados y jornada de trabajo diario del proyecto."}
                                </p>
                            </div>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setIsWizardFullscreen(!isWizardFullscreen)}
                                    className="hover:bg-white/20 p-2 rounded-full transition-colors flex items-center justify-center"
                                    title={isWizardFullscreen ? "Restaurar tamaño" : "Pantalla completa"}
                                >
                                    <span className="material-symbols-outlined text-white text-[20px]">
                                        {isWizardFullscreen ? 'fullscreen_exit' : 'fullscreen'}
                                    </span>
                                </button>
                                <button
                                    onClick={() => {
                                        setImportWizardModalOpen(false);
                                        setWizardData(null);
                                    }}
                                    className="hover:bg-white/20 p-2 rounded-full transition-colors flex items-center justify-center"
                                >
                                    <span className="material-symbols-outlined text-white">close</span>
                                </button>
                            </div>
                        </div>

                        <div className="p-6 overflow-y-auto flex-1 space-y-6">
                            {activeStepType === 'distribution' && (
                                <div className="space-y-6">
                                    {wizardData.importedProgress === 0 ? (
                                        <div className="bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/40 p-4 rounded-xl flex items-center gap-3">
                                            <span className="material-symbols-outlined text-emerald-600 dark:text-emerald-450 text-2xl">check_circle</span>
                                            <div>
                                                <h4 className="text-xs font-bold text-emerald-800 dark:text-emerald-400">Sin avance detectado</h4>
                                                <p className="text-[11px] text-emerald-600 dark:text-emerald-450/90 mt-0.5">La Carta Gantt importada no cuenta con avance, listo para trabajar directamente.</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            <div className="bg-amber-50 dark:bg-amber-950/15 border border-amber-200 dark:border-amber-900/35 p-4 rounded-xl flex items-start gap-3">
                                                <span className="material-symbols-outlined text-amber-600 dark:text-amber-450 text-2xl mt-0.5">warning</span>
                                                <div>
                                                    <h4 className="text-xs font-bold text-amber-800 dark:text-amber-400">Avance Detectado ({truncateProgress(wizardData.importedProgress)}%)</h4>
                                                    <p className="text-[11px] text-amber-600 dark:text-amber-450/90 leading-relaxed mt-0.5">
                                                        El archivo importado contiene un avance físico real registrado. Para mantener la consistencia histórica del avance acumulado en K-LyDapp, por favor distribuye este avance en las semanas históricas correspondientes.
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="space-y-3">
                                                <div className="flex justify-between items-center flex-wrap gap-3">
                                                    <div className="flex items-center gap-3">
                                                        <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">Distribución Semanal de Avance</h4>
                                                        <div className="flex items-center gap-1.5 bg-slate-100 dark:bg-slate-800 px-2.5 py-1 rounded-lg border border-slate-200 dark:border-slate-700">
                                                            <span className="material-symbols-outlined text-[16px] text-slate-500">sync</span>
                                                            <span className="text-[10px] font-bold text-slate-600 dark:text-slate-400">CORTE:</span>
                                                            <select
                                                                value={wizardData.syncDay ?? 1}
                                                                onChange={(e) => {
                                                                    const newDay = parseInt(e.target.value);
                                                                    setWizardData({
                                                                        ...wizardData,
                                                                        syncDay: newDay
                                                                    });
                                                                }}
                                                                className="bg-transparent text-[10px] font-bold text-slate-850 dark:text-slate-200 outline-none border-none cursor-pointer pr-1"
                                                            >
                                                                <option value={1} className="dark:bg-slate-900">Lunes</option>
                                                                <option value={2} className="dark:bg-slate-900">Martes</option>
                                                                <option value={3} className="dark:bg-slate-900">Miércoles</option>
                                                                <option value={4} className="dark:bg-slate-900">Jueves</option>
                                                                <option value={5} className="dark:bg-slate-900">Viernes</option>
                                                                <option value={6} className="dark:bg-slate-900">Sábado</option>
                                                                <option value={0} className="dark:bg-slate-900">Domingo</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => {
                                                            let minStart = "";
                                                            flattenTasks(wizardData.tasks).filter(t => t.type === 'task' && !t.isWeeklySubtask).forEach(t => {
                                                                if (t.start && (!minStart || t.start < minStart)) minStart = t.start;
                                                            });
                                                            const projectStartDate = minStart || projectInfo.startDate;
                                                            const weeks = generateHistoricalWeeks(projectStartDate, wizardData.cutOffDate, wizardData.tasks, wizardData.syncDay);
                                                            const distributed = autoDistributeProportionally(wizardData.importedProgress, weeks);

                                                            const newProgressMap: Record<number, string> = {};
                                                            weeks.forEach((w, idx) => {
                                                                newProgressMap[w.index] = distributed[idx].toFixed(2);
                                                            });
                                                            setWizardData({
                                                                ...wizardData,
                                                                weeklyProgress: newProgressMap
                                                            });
                                                        }}
                                                        className="px-3 py-1.5 text-[10px] font-black uppercase bg-blue-50 text-[#1a73e8] hover:bg-blue-100 rounded-lg transition-all flex items-center gap-1.5 border border-blue-200 dark:border-blue-800"
                                                    >
                                                        <span className="material-symbols-outlined text-[14px]">auto_awesome</span>
                                                        Autodistribuir Proporcionalmente
                                                    </button>
                                                </div>

                                                <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden max-h-[300px] overflow-y-auto">
                                                    <table className="w-full text-left">
                                                        <thead className="bg-slate-50 dark:bg-slate-800/50 text-[10px] font-bold text-slate-500 uppercase tracking-wider sticky top-0 border-b border-slate-200 dark:border-slate-800">
                                                            <tr>
                                                                <th className="px-4 py-3">Semana</th>
                                                                <th className="px-4 py-3 text-center">Avance Planificado</th>
                                                                <th className="px-4 py-3 text-right">Avance Real Semanal (%)</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-xs">
                                                            {(() => {
                                                                let minStart = "";
                                                                flattenTasks(wizardData.tasks).filter(t => t.type === 'task' && !t.isWeeklySubtask).forEach(t => {
                                                                    if (t.start && (!minStart || t.start < minStart)) minStart = t.start;
                                                                });
                                                                const projectStartDate = minStart || projectInfo.startDate;
                                                                const weeks = generateHistoricalWeeks(projectStartDate, wizardData.cutOffDate, wizardData.tasks, wizardData.syncDay);

                                                                const parsedWeeklyVals = Object.keys(wizardData.weeklyProgress).reduce((acc, key) => {
                                                                    const val = parseFloat(wizardData.weeklyProgress[Number(key)]);
                                                                    acc[Number(key)] = isNaN(val) ? 0 : val;
                                                                    return acc;
                                                                }, {} as Record<number, number>);

                                                                return weeks.map((w, idx) => {
                                                                    const val = wizardData.weeklyProgress[w.index] || "";
                                                                    return (
                                                                        <tr key={w.index} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                                                                            <td className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">{w.label}</td>
                                                                            <td className="px-4 py-3 text-center text-slate-500 font-medium">{w.plannedWeekly.toFixed(2)}%</td>
                                                                            <td className="px-4 py-3 text-right">
                                                                                <input
                                                                                    type="number"
                                                                                    step="0.01"
                                                                                    placeholder="0.00"
                                                                                    value={val}
                                                                                    onChange={(e) => {
                                                                                        const newWeekly = { ...wizardData.weeklyProgress };
                                                                                        newWeekly[w.index] = e.target.value;
                                                                                        setWizardData({
                                                                                            ...wizardData,
                                                                                            weeklyProgress: newWeekly
                                                                                        });
                                                                                    }}
                                                                                    className="w-24 text-right bg-slate-50 dark:bg-slate-850 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 focus:ring-2 focus:ring-[#1a73e8]/30 outline-none font-bold"
                                                                                />
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                });
                                                            })()}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeStepType === 'reconciliation' && (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 min-h-[400px]">
                                    {/* Left Panel: Parent Tasks */}
                                    <div className="bg-slate-50 dark:bg-slate-850 p-4 rounded-xl border border-slate-100 dark:border-slate-800 flex flex-col">
                                        <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">Tareas con Avance Parcial</h4>
                                        <div className="space-y-2 flex-1 overflow-y-auto max-h-[350px]">
                                            {tasksToReconcile.length === 0 ? (
                                                <p className="text-slate-400 italic text-xs py-4 text-center">No hay tareas que requieran conciliación.</p>
                                            ) : (
                                                tasksToReconcile.map(item => {
                                                    const isParentSelected = selectedReconcileParentId === item.parentTask.id;

                                                    // Check if this specific task is balanced
                                                    const target = item.targetProgress;
                                                    const achieved = item.existingSubtasks.reduce((sum, sub) => {
                                                        const progress = wizardData.reconciliationProgress[sub.id] ?? sub.progress ?? 0;
                                                        const weight = sub.weight !== undefined ? sub.weight : (100 / item.existingSubtasks.length);
                                                        return sum + progress * (weight / 100);
                                                    }, 0);
                                                    const isBalanced = Math.abs(target - achieved) < 0.01;

                                                    return (
                                                        <div
                                                            key={item.parentTask.id}
                                                            onClick={() => setSelectedReconcileParentId(item.parentTask.id)}
                                                            className={`p-3 rounded-lg border cursor-pointer transition-all ${isParentSelected
                                                                ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800'
                                                                : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:bg-slate-50'
                                                                }`}
                                                        >
                                                            <div className="flex justify-between items-start gap-2">
                                                                <div className="flex-1 min-w-0">
                                                                    <span className="text-xs font-bold text-slate-800 dark:text-slate-250 line-clamp-2 block">
                                                                        {item.parentTask.name}
                                                                    </span>
                                                                    {(() => {
                                                                        const path = getTaskPath(item.parentTask.id, wizardData?.tasks || []);
                                                                        return path ? (
                                                                            <span className="text-[10px] text-slate-400 dark:text-slate-500 block truncate mt-0.5" title={path}>
                                                                                {path}
                                                                            </span>
                                                                        ) : null;
                                                                    })()}
                                                                </div>
                                                                {isBalanced ? (
                                                                    <span className="shrink-0 px-1.5 py-0.5 text-[9px] font-black uppercase rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400">
                                                                        Conciliado
                                                                    </span>
                                                                ) : (
                                                                    <span className="shrink-0 px-1.5 py-0.5 text-[9px] font-black uppercase rounded bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-400">
                                                                        Pendiente
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="flex justify-between items-center mt-2 text-[10px] text-slate-500">
                                                                <span>Gantt: <strong>{target.toFixed(2)}%</strong></span>
                                                                <span>Asignado: <strong>{achieved.toFixed(2)}%</strong></span>
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>

                                    {/* Right Panel: Subtasks Details */}
                                    <div className="md:col-span-2 flex flex-col bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800">
                                        {(() => {
                                            const selectedItem = tasksToReconcile.find(t => t.parentTask.id === selectedReconcileParentId);
                                            if (!selectedItem) {
                                                return (
                                                    <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-8 text-center">
                                                        <span className="material-symbols-outlined text-4xl mb-2">assignment_ind</span>
                                                        <p className="text-xs font-semibold">Selecciona una tarea de la izquierda para conciliar sus desgloses.</p>
                                                    </div>
                                                );
                                            }

                                            const target = selectedItem.targetProgress;
                                            const achieved = selectedItem.existingSubtasks.reduce((sum, sub) => {
                                                const progress = wizardData.reconciliationProgress[sub.id] ?? sub.progress ?? 0;
                                                const weight = sub.weight !== undefined ? sub.weight : (100 / selectedItem.existingSubtasks.length);
                                                return sum + progress * (weight / 100);
                                            }, 0);
                                            const diff = target - achieved;
                                            const isBalanced = Math.abs(diff) < 0.01;

                                            return (
                                                <div className="flex flex-col flex-1 space-y-4">
                                                    <div className="bg-slate-50 dark:bg-slate-850 p-4 rounded-xl border border-slate-100 dark:border-slate-800 flex justify-between items-center gap-4 flex-wrap">
                                                        <div className="flex-1 min-w-[200px]">
                                                            <h4 className="text-xs font-extrabold text-slate-800 dark:text-slate-200">{selectedItem.parentTask.name}</h4>
                                                            {(() => {
                                                                const path = getTaskPath(selectedItem.parentTask.id, wizardData?.tasks || []);
                                                                return path ? (
                                                                    <p className="text-[9px] font-semibold text-slate-400 dark:text-slate-500 mt-0.5 truncate" title={path}>
                                                                        {path}
                                                                    </p>
                                                                ) : null;
                                                            })()}
                                                            <p className="text-[10px] text-slate-500 mt-1">Asigna el avance a cada desglose para equiparar el % de la Gantt.</p>
                                                        </div>
                                                        <button
                                                            onClick={() => handleReconcileSequentially(selectedItem.parentTask.id)}
                                                            className="px-3 py-1.5 text-[10px] font-black uppercase bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-900 rounded-lg transition-colors flex items-center gap-1 shrink-0"
                                                        >
                                                            <span className="material-symbols-outlined text-[14px]">checklist_rtl</span>
                                                            Completar Secuencialmente
                                                        </button>
                                                    </div>

                                                    {/* Real-time stats bar */}
                                                    <div className="grid grid-cols-3 gap-4 bg-slate-50 dark:bg-slate-850/60 p-3 rounded-lg border border-slate-100 dark:border-slate-800 text-center">
                                                        <div>
                                                            <div className="text-[10px] uppercase font-bold text-slate-500">Objetivo (Gantt)</div>
                                                            <div className="text-sm font-black text-slate-800 dark:text-white mt-0.5">{target.toFixed(2)}%</div>
                                                        </div>
                                                        <div>
                                                            <div className="text-[10px] uppercase font-bold text-slate-500">Logrado (Desgloses)</div>
                                                            <div className="text-sm font-black text-slate-800 dark:text-white mt-0.5">{achieved.toFixed(2)}%</div>
                                                        </div>
                                                        <div>
                                                            <div className="text-[10px] uppercase font-bold text-slate-500">Diferencia</div>
                                                            <div className={`text-sm font-black mt-0.5 ${isBalanced ? 'text-emerald-500' : 'text-amber-500 animate-pulse'}`}>
                                                                {diff.toFixed(2)}%
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Subtasks table */}
                                                    {/* Subtasks table */}
                                                    <div className={`flex-1 border border-slate-100 dark:border-slate-800 rounded-xl overflow-hidden overflow-y-auto ${isWizardFullscreen ? 'max-h-[60vh]' : 'max-h-[300px]'}`}>
                                                        <table className="w-full text-left">
                                                            <thead className="bg-slate-50 dark:bg-slate-800/80 text-[10px] font-bold text-slate-500 uppercase tracking-wider border-b border-slate-200 dark:border-slate-800 sticky top-0">
                                                                <tr>
                                                                    <th className="px-4 py-2.5">Nivel / Unidad Física</th>
                                                                    <th className="px-4 py-2.5">Subtarea / Partida</th>
                                                                    <th className="px-4 py-2.5 text-center">Acción</th>
                                                                    <th className="px-4 py-2.5 text-right w-24">% Peso</th>
                                                                    <th className="px-4 py-2.5 text-right w-28">% Avance</th>
                                                                    <th className="px-4 py-2.5 text-right">% Aporte</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-xs">
                                                                {selectedItem.existingSubtasks.flatMap(sub => {
                                                                    const isExpanded = !!expandedSubtasks[sub.id];
                                                                    const subUnits = getSubtaskUnits(sub.id, sub.name, selectedItem.parentTask.id);
                                                                    const subProgressVal = wizardData.reconciliationProgress[sub.id] ?? sub.progress ?? 0;
                                                                    const weight = sub.weight !== undefined ? sub.weight : (100 / selectedItem.existingSubtasks.length);
                                                                    const subAporte = subProgressVal * (weight / 100);

                                                                    const rows = [];

                                                                    // Subtask Parent Row
                                                                    rows.push(
                                                                        <tr key={sub.id} className="bg-slate-50/50 dark:bg-slate-800/20 hover:bg-slate-50 dark:hover:bg-slate-800/40 border-b border-slate-100 dark:border-slate-800">
                                                                            <td className="px-4 py-2.5 font-bold text-slate-700 dark:text-slate-350">
                                                                                <button
                                                                                    onClick={() => toggleSubtaskExpanded(sub.id)}
                                                                                    className="flex items-center gap-1.5 focus:outline-none"
                                                                                >
                                                                                    <span className="material-symbols-outlined text-[18px] text-slate-400">
                                                                                        {isExpanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right'}
                                                                                    </span>
                                                                                    <span>Desglose Semanal</span>
                                                                                </button>
                                                                            </td>
                                                                            <td className="px-4 py-2.5 font-bold text-slate-850 dark:text-slate-200">
                                                                                {sub.name}
                                                                            </td>
                                                                            <td className="px-4 py-2.5 text-center">
                                                                                <button
                                                                                    disabled={subUnits.length === 0}
                                                                                    onClick={() => {
                                                                                        subUnits.forEach(u => {
                                                                                            handleUpdateUnitProgress(sub.id, u.id, 100);
                                                                                        });
                                                                                    }}
                                                                                    className={`px-2 py-1 text-[9px] font-black uppercase rounded transition-colors border ${subUnits.length === 0
                                                                                        ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed dark:bg-slate-800 dark:text-slate-550 dark:border-slate-700"
                                                                                        : "bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200 dark:bg-blue-950/40 dark:text-blue-400 dark:border-blue-900/60"
                                                                                        }`}
                                                                                >
                                                                                    Todo Completo
                                                                                </button>
                                                                            </td>
                                                                            <td className="px-4 py-2.5 text-right font-bold">
                                                                                <div className="flex items-center justify-end gap-1">
                                                                                    <input
                                                                                        type="number"
                                                                                        step="0.01"
                                                                                        min="0"
                                                                                        max="100"
                                                                                        value={weight}
                                                                                        onChange={(e) => {
                                                                                            const val = parseFloat(e.target.value);
                                                                                            handleUpdateSubtaskWeight(selectedItem.parentTask.id, sub.id, isNaN(val) ? 0 : val);
                                                                                        }}
                                                                                        className="w-16 text-right bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-1.5 py-1 focus:ring-2 focus:ring-blue-500 outline-none font-bold text-xs text-slate-850 dark:text-slate-200 shadow-sm"
                                                                                    />
                                                                                    <span className="text-xs font-bold text-slate-400">%</span>
                                                                                </div>
                                                                            </td>
                                                                            <td className="px-4 py-2.5 text-right font-bold text-slate-850 dark:text-slate-200">
                                                                                {subProgressVal.toFixed(2)}%
                                                                            </td>
                                                                            <td className="px-4 py-2.5 text-right font-mono font-bold text-slate-850 dark:text-slate-200">
                                                                                {subAporte.toFixed(2)}%
                                                                            </td>
                                                                        </tr>
                                                                    );

                                                                    // Units Child Rows (rendered if expanded)
                                                                    if (isExpanded) {
                                                                        if (subUnits.length === 0) {
                                                                            rows.push(
                                                                                <tr key={`${sub.id}_warning`} className="bg-orange-50/20 dark:bg-orange-950/10 border-b border-slate-100 dark:border-slate-800">
                                                                                    <td colSpan={6} className="px-8 py-3 text-xs text-orange-650 dark:text-orange-400 font-medium">
                                                                                        <div className="flex items-center gap-2 pl-3 border-l-2 border-orange-500">
                                                                                            <span className="material-symbols-outlined text-[18px]">warning</span>
                                                                                            <span>Falta completar EDT para esta tarea</span>
                                                                                        </div>
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        } else {
                                                                            subUnits.forEach(u => {
                                                                                const levelName = levels.find(l => l.id === u.levelId)?.name || '-';
                                                                                const unitName = u.name || '-';

                                                                                const progressVal = wizardData.reconciliationUnitProgress[sub.id]?.[u.id] ?? getInitialUnitProgress(sub, u.id);

                                                                                // Unit's weight inside the parent task is (weight / subUnits.length)
                                                                                const unitWeight = weight / subUnits.length;
                                                                                const unitAporte = progressVal * (unitWeight / 100);

                                                                                rows.push(
                                                                                    <tr key={`${sub.id}_${u.id}`} className="bg-white dark:bg-slate-900 hover:bg-slate-50/50 dark:hover:bg-slate-800/10 border-b border-slate-50 dark:border-slate-850">
                                                                                        <td className="px-8 py-2 text-xs">
                                                                                            <div className="flex flex-col border-l-2 border-slate-200 dark:border-slate-700 pl-3">
                                                                                                <span className="font-bold text-slate-700 dark:text-slate-350">{levelName}</span>
                                                                                                <span className="text-[10px] text-slate-455 dark:text-slate-400">{unitName}</span>
                                                                                            </div>
                                                                                        </td>
                                                                                        <td className="px-4 py-2 text-xs text-slate-400 italic">
                                                                                            Unidad Física
                                                                                        </td>
                                                                                        <td className="px-4 py-2 text-center">
                                                                                            <button
                                                                                                onClick={() => handleUpdateUnitProgress(sub.id, u.id, 100)}
                                                                                                className="px-2 py-1 text-[9px] font-semibold uppercase rounded bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-950/40 dark:text-green-400 transition-colors border border-green-200 dark:border-green-900/60"
                                                                                            >
                                                                                                Completado
                                                                                            </button>
                                                                                        </td>
                                                                                        <td className="px-4 py-2 text-right text-xs font-semibold text-slate-400 font-mono">
                                                                                            {unitWeight.toFixed(2)}%
                                                                                        </td>
                                                                                        <td className="px-4 py-2 text-right">
                                                                                            <input
                                                                                                type="number"
                                                                                                min="0"
                                                                                                max="100"
                                                                                                step="1"
                                                                                                value={progressVal}
                                                                                                onChange={(e) => handleUpdateUnitProgress(sub.id, u.id, parseFloat(e.target.value))}
                                                                                                className="w-16 text-right bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-1.5 py-1 focus:ring-1 focus:ring-blue-500 outline-none font-semibold text-xs text-slate-800 dark:text-slate-200"
                                                                                            />
                                                                                        </td>
                                                                                        <td className="px-4 py-2 text-right font-mono text-slate-455 text-[11px]">
                                                                                            {unitAporte.toFixed(2)}%
                                                                                        </td>
                                                                                    </tr>
                                                                                );
                                                                            });
                                                                        }
                                                                    }

                                                                    return rows;
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            )}

                            {activeStepType === 'calendar' && (
                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="bg-slate-50 dark:bg-slate-850 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 space-y-4">
                                            <h4 className="text-xs font-bold text-slate-700 dark:text-slate-350 uppercase tracking-widest flex items-center gap-1.5">
                                                <span className="material-symbols-outlined text-[16px] text-blue-600 dark:text-blue-400">calendar_month</span>
                                                Días Laborables de Obra
                                            </h4>
                                            <div className="grid grid-cols-2 gap-3 text-xs font-medium">
                                                {['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'].map((day, idx) => {
                                                    const apiIndex = idx === 6 ? 0 : idx + 1;
                                                    const checked = wizardData.workingDays[apiIndex];
                                                    return (
                                                        <label key={day} className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-slate-200/50 dark:hover:bg-slate-800 rounded-lg transition-colors">
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                onChange={(e) => {
                                                                    const copy = [...wizardData.workingDays];
                                                                    copy[apiIndex] = e.target.checked;
                                                                    setWizardData({
                                                                        ...wizardData,
                                                                        workingDays: copy
                                                                    });
                                                                }}
                                                                className="rounded text-[#1a73e8] focus:ring-[#1a73e8]/40 border-slate-300"
                                                            />
                                                            <span className="text-slate-800 dark:text-slate-300">{day}</span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        <div className="bg-slate-50 dark:bg-slate-850 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 space-y-4">
                                            <h4 className="text-xs font-bold text-slate-700 dark:text-slate-350 uppercase tracking-widest flex items-center gap-1.5">
                                                <span className="material-symbols-outlined text-[16px] text-blue-600 dark:text-blue-400">schedule</span>
                                                Jornada Laboral Diaria
                                            </h4>
                                            <div>
                                                <select
                                                    value={wizardData.hoursPerDay}
                                                    onChange={(e) => setWizardData({ ...wizardData, hoursPerDay: parseInt(e.target.value) })}
                                                    className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs font-semibold outline-none focus:ring-2 focus:ring-[#1a73e8]/30"
                                                >
                                                    {[6, 7, 8, 9, 10, 11, 12].map(h => (
                                                        <option key={h} value={h}>{h} horas diarias</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-slate-50 dark:bg-slate-850 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 space-y-4">
                                        <h4 className="text-xs font-bold text-slate-700 dark:text-slate-350 uppercase tracking-widest flex items-center gap-1.5">
                                            <span className="material-symbols-outlined text-[16px] text-blue-600 dark:text-blue-400">event_busy</span>
                                            Feriados No Laborables del Proyecto
                                        </h4>
                                        <div className="flex gap-3">
                                            <input
                                                type="date"
                                                value={newHolidayDate}
                                                onChange={(e) => setNewHolidayDate(e.target.value)}
                                                className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs outline-none focus:ring-2 focus:ring-[#1a73e8]/30 font-medium"
                                            />
                                            <input
                                                type="text"
                                                placeholder="Nombre del Feriado (ej. Fiestas Patrias)"
                                                value={newHolidayName}
                                                onChange={(e) => setNewHolidayName(e.target.value)}
                                                className="flex-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs outline-none focus:ring-2 focus:ring-[#1a73e8]/30 font-semibold"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (!newHolidayDate) return;
                                                    const hName = newHolidayName.trim() || "Feriado";
                                                    const updatedHols = [...wizardData.tempHolidays, { date: newHolidayDate, name: hName }];
                                                    setWizardData({
                                                        ...wizardData,
                                                        tempHolidays: updatedHols
                                                    });
                                                    setNewHolidayDate("");
                                                    setNewHolidayName("");
                                                }}
                                                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all"
                                            >
                                                Agregar
                                            </button>
                                        </div>

                                        <div className="max-h-[150px] overflow-y-auto space-y-2 border border-slate-200 dark:border-slate-800 rounded-xl p-3 bg-white dark:bg-slate-900">
                                            {wizardData.tempHolidays.length === 0 ? (
                                                <p className="text-slate-400 italic text-center py-2 text-xs">No hay feriados agregados para este proyecto.</p>
                                            ) : wizardData.tempHolidays.map((h, idx) => (
                                                <div key={idx} className="flex justify-between items-center text-xs bg-slate-50 dark:bg-slate-850 p-2 rounded-lg border border-slate-100 dark:border-slate-800/50">
                                                    <span className="font-semibold text-slate-700 dark:text-slate-300">{h.name}</span>
                                                    <div className="flex items-center gap-3">
                                                        <span className="text-slate-500 font-bold">{h.date}</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const updatedHols = wizardData.tempHolidays.filter((_, i) => i !== idx);
                                                                setWizardData({
                                                                    ...wizardData,
                                                                    tempHolidays: updatedHols
                                                                });
                                                            }}
                                                            className="text-red-500 hover:text-red-600 transition-colors p-1"
                                                        >
                                                            <span className="material-symbols-outlined text-[16px]">delete</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 flex justify-between items-center">
                            {activeStepType === 'distribution' ? (
                                <>
                                    {wizardData.importedProgress > 0 ? (
                                        (() => {
                                            let minStart = "";
                                            flattenTasks(wizardData.tasks).filter(t => t.type === 'task' && !t.isWeeklySubtask).forEach(t => {
                                                if (t.start && (!minStart || t.start < minStart)) minStart = t.start;
                                            });
                                            const projectStartDate = minStart || projectInfo.startDate;
                                            const weeks = generateHistoricalWeeks(projectStartDate, wizardData.cutOffDate, wizardData.tasks, wizardData.syncDay);
                                            const totalInput = Object.keys(wizardData.weeklyProgress).reduce((sum, key) => {
                                                const val = parseFloat(wizardData.weeklyProgress[Number(key)]);
                                                return sum + (isNaN(val) ? 0 : val);
                                            }, 0);
                                            const diff = wizardData.importedProgress - totalInput;
                                            const isExactMatch = Math.abs(diff) < 0.01;

                                            return (
                                                <div className="text-xs">
                                                    <span className="text-slate-500 font-bold uppercase tracking-wider mr-2">Falta por Distribuir:</span>
                                                    <span className={`font-black text-sm ${isExactMatch ? 'text-emerald-500' : 'text-amber-500 animate-pulse'}`}>
                                                        {diff.toFixed(2)}%
                                                    </span>
                                                </div>
                                            );
                                        })()
                                    ) : (
                                        <div />
                                    )}

                                    {(() => {
                                        let isExactMatch = true;
                                        if (wizardData.importedProgress > 0) {
                                            const totalInput = Object.keys(wizardData.weeklyProgress).reduce((sum, key) => {
                                                const val = parseFloat(wizardData.weeklyProgress[Number(key)]);
                                                return sum + (isNaN(val) ? 0 : val);
                                            }, 0);
                                            isExactMatch = Math.abs(wizardData.importedProgress - totalInput) < 0.01;
                                        }

                                        return (
                                            <div
                                                className="relative group inline-block"
                                                title={!isExactMatch ? "Se debe completar la distribución de avance" : undefined}
                                            >
                                                <button
                                                    disabled={!isExactMatch}
                                                    onClick={() => setWizardStep(wizardStep + 1)}
                                                    className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-xl text-xs font-bold shadow-md transition-all active:scale-95 disabled:pointer-events-none flex items-center gap-1"
                                                >
                                                    Siguiente
                                                    <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
                                                </button>
                                            </div>
                                        );
                                    })()}
                                </>
                            ) : activeStepType === 'reconciliation' ? (
                                <>
                                    <button
                                        onClick={() => setWizardStep(wizardStep - 1)}
                                        className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold transition-all flex items-center gap-1"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">arrow_back</span>
                                        Atrás
                                    </button>

                                    <div
                                        className="relative group inline-block"
                                        title={!isReconciliationBalanced ? "Todas las tareas con avance parcial deben estar conciliadas" : undefined}
                                    >
                                        <button
                                            disabled={!isReconciliationBalanced}
                                            onClick={() => setWizardStep(wizardStep + 1)}
                                            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-xl text-xs font-bold shadow-md transition-all active:scale-95 disabled:pointer-events-none flex items-center gap-1"
                                        >
                                            Siguiente
                                            <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <button
                                        onClick={() => setWizardStep(wizardStep - 1)}
                                        className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold transition-all flex items-center gap-1"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">arrow_back</span>
                                        Atrás
                                    </button>
                                    <button
                                        onClick={handleConfirmImportWizard}
                                        className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-md transition-all active:scale-95"
                                    >
                                        Finalizar Importación
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Conflict Resolution Modal */}
            {conflictModalOpen && conflictData && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 duration-200">
                        <div className="bg-amber-600 p-6 text-white flex justify-between items-center">
                            <div>
                                <h3 className="text-lg font-bold flex items-center gap-2">
                                    <span className="material-symbols-outlined">warning_amber</span>
                                    {conflictType === 'incompatibility' && "⚠️ Incompatibilidad de Proyecto"}
                                    {conflictType === 'mode_change' && "📊 Detección de Avance en Importación"}
                                    {conflictType === 'changes_detected' && "🔄 Cambios Detectados en el Cronograma"}
                                </h3>
                            </div>
                            <button
                                onClick={() => {
                                    setConflictModalOpen(false);
                                    setConflictData(null);
                                    setConflictType(null);
                                }}
                                className="text-slate-100 hover:text-white transition-colors bg-amber-700/50 p-2 rounded-full border border-amber-500/30"
                            >
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            {conflictType === 'incompatibility' && (
                                <div className="space-y-4 text-xs">
                                    <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                                        El proyecto de la Carta Gantt que intentas importar difiere del nombre actual configurado en el sistema:
                                    </p>
                                    <div className="bg-slate-50 dark:bg-slate-850 p-4 rounded-xl border border-slate-100 dark:border-slate-800 space-y-2">
                                        <p className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Proyecto en Sistema:</p>
                                        <p className="text-slate-800 dark:text-white font-black text-sm">{conflictData.currentProjectName}</p>
                                        <div className="border-t border-slate-200 dark:border-slate-700 my-2" />
                                        <p className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Proyecto a Importar:</p>
                                        <p className="text-slate-850 dark:text-amber-500 font-black text-sm">{conflictData.importedProjectName}</p>
                                    </div>
                                    <p className="text-amber-600 font-bold leading-relaxed">
                                        ⚠️ Continuar podría sobreescribir el cronograma actual. ¿Deseas proceder de todas formas?
                                    </p>
                                </div>
                            )}

                            {conflictType === 'mode_change' && (
                                <div className="space-y-4 text-xs leading-relaxed">
                                    <p className="text-slate-600 dark:text-slate-400">
                                        El proyecto actual está configurado sin avances físicos (0%), pero el archivo cargado contiene avances físicos registrados. Selecciona cómo deseas proceder:
                                    </p>
                                    <div className="bg-slate-50 dark:bg-slate-850 p-4 rounded-xl border border-slate-100 dark:border-slate-800 flex items-center justify-between">
                                        <div>
                                            <p className="text-slate-500 font-bold uppercase text-[10px]">Avance en XML:</p>
                                            <p className="text-slate-850 dark:text-white font-black text-sm">{truncateProgress(calculateWeightedProgress(conflictData.processed))}%</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {conflictType === 'changes_detected' && (
                                <div className="space-y-4 text-xs leading-relaxed">
                                    <p className="text-slate-600 dark:text-slate-400">
                                        Se han detectado modificaciones en la estructura de partidas del cronograma respecto al archivo original. Selecciona cómo deseas importarlo:
                                    </p>
                                    <div className="bg-slate-50 dark:bg-slate-850 p-4 rounded-xl border border-slate-100 dark:border-slate-800/80 space-y-2">
                                        <p className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Efectos de Modificación:</p>
                                        <p className="text-slate-800 dark:text-white leading-relaxed">
                                            Una <strong>Nueva Importación Completa</strong> reemplazará la estructura actual y reiniciará todos los desgloses en K-LyDapp.<br />
                                            Una <strong>Reprogramación</strong> integrará la nueva estructura respetando intactos todos los desgloses y avances reportados en terreno.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
                            {conflictType === 'incompatibility' && (
                                <>
                                    <button
                                        onClick={() => {
                                            setConflictModalOpen(false);
                                            setConflictData(null);
                                        }}
                                        className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-350 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                                    >
                                        Cancelar Importación
                                    </button>
                                    <button
                                        onClick={handleProceedIncompatibility}
                                        className="px-5 py-2 text-xs font-bold text-white bg-amber-600 hover:bg-amber-700 rounded shadow-md transition-colors"
                                    >
                                        Sí, Proceder
                                    </button>
                                </>
                            )}

                            {conflictType === 'mode_change' && (
                                <>
                                    <button
                                        onClick={() => handleModeChangeChoice(false)}
                                        className="px-4 py-2 text-xs font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 rounded transition-colors border border-slate-300 dark:border-slate-700"
                                    >
                                        Importar SIN Avance (0%)
                                    </button>
                                    <button
                                        onClick={() => handleModeChangeChoice(true)}
                                        className="px-5 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded shadow transition-colors"
                                    >
                                        Importar CON Avance (Wizard)
                                    </button>
                                </>
                            )}

                            {conflictType === 'changes_detected' && (
                                <>
                                    <button
                                        onClick={() => handleChangesDetectedChoice('full_import')}
                                        className="px-4 py-2 text-xs font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-850 rounded transition-colors border border-slate-300 dark:border-slate-700"
                                    >
                                        Nueva Importación Completa
                                    </button>
                                    <button
                                        onClick={() => handleChangesDetectedChoice('reprogram')}
                                        className="px-5 py-2 text-xs font-bold text-white bg-[#1a73e8] hover:bg-blue-600 rounded shadow-md transition-colors"
                                    >
                                        Reprogramación
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Reprogram Compare Modal */}
            {reprogramCompareModalOpen && reprogramCompareData && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
                        <div className="bg-[#1a73e8] p-6 text-white flex justify-between items-center">
                            <div>
                                <h2 className="text-xl font-bold flex items-center gap-2">
                                    <span className="material-symbols-outlined">published_with_changes</span>
                                    Comparativa de Modificaciones de Reprogramación
                                </h2>
                                <p className="text-xs text-blue-100 font-semibold mt-1">
                                    Revisa las diferencias respecto al cronograma anterior. Los avances reales y desgloses de terreno se preservan.
                                </p>
                            </div>
                            <button
                                onClick={() => {
                                    setReprogramCompareModalOpen(false);
                                    setReprogramCompareData(null);
                                }}
                                className="hover:bg-white/20 p-2 rounded-full transition-colors"
                            >
                                <span className="material-symbols-outlined text-white">close</span>
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto flex-1 grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50/40 dark:bg-slate-950/20">
                            <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-250 dark:border-slate-800 flex flex-col max-h-[450px]">
                                <h4 className="text-xs font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest mb-3 flex items-center gap-1">
                                    <span className="material-symbols-outlined text-[16px]">add_circle</span>
                                    Partidas Agregadas ({reprogramCompareData.addedTasks.length})
                                </h4>
                                <div className="overflow-y-auto flex-1 space-y-2 pr-1 text-xs">
                                    {reprogramCompareData.addedTasks.length === 0 ? (
                                        <p className="text-slate-400 italic text-center py-6">No se agregaron nuevas partidas.</p>
                                    ) : reprogramCompareData.addedTasks.map(t => (
                                        <div key={t.id} className="p-3 bg-emerald-50/40 dark:bg-emerald-950/10 border border-emerald-100 dark:border-emerald-950/30 rounded-xl">
                                            <p className="font-bold text-slate-850 dark:text-slate-200">{t.name}</p>
                                            <div className="flex gap-4 text-[10px] text-slate-500 font-semibold mt-1">
                                                <span>Inicio: {t.start}</span>
                                                <span>Fin: {t.end}</span>
                                                <span>Duración: {t.duration}d</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-250 dark:border-slate-800 flex flex-col max-h-[450px]">
                                <h4 className="text-xs font-black text-red-500 dark:text-red-400 uppercase tracking-widest mb-3 flex items-center gap-1">
                                    <span className="material-symbols-outlined text-[16px]">remove_circle</span>
                                    Partidas Eliminadas ({reprogramCompareData.deletedTasks.length})
                                </h4>
                                <div className="overflow-y-auto flex-1 space-y-2 pr-1 text-xs">
                                    {reprogramCompareData.deletedTasks.length === 0 ? (
                                        <p className="text-slate-400 italic text-center py-6">No se eliminaron partidas.</p>
                                    ) : reprogramCompareData.deletedTasks.map(t => (
                                        <div key={t.id} className="p-3 bg-red-50/40 dark:bg-red-950/10 border border-red-100 dark:border-red-950/30 rounded-xl">
                                            <p className="font-bold text-slate-850 dark:text-slate-200">{t.name}</p>
                                            <div className="flex gap-4 text-[10px] text-slate-500 font-semibold mt-1">
                                                <span>Inicio: {t.start}</span>
                                                <span>Fin: {t.end}</span>
                                                <span>Duración: {t.duration}d</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setReprogramCompareModalOpen(false);
                                    setReprogramCompareData(null);
                                }}
                                className="px-4 py-2 text-xs font-bold text-slate-655 dark:text-slate-350 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleReprogramConfirm}
                                className="px-6 py-2.5 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-md transition-all active:scale-95"
                            >
                                Aceptar y Reprogramar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* NEW Unit Selection Modal (ISOLATED & MEMOIZED) */}
            <UnitSelectionModal
                isOpen={unitSelectionModalOpen}
                unitsType={newConstraintData.unitsType}
                unitIds={newConstraintData.unitIds}
                activeConstraintTask={activeConstraintTask}
                units={units}
                levels={levels}
                matrixData={matrixData}
                tasks={tasks}
                onClose={() => setUnitSelectionModalOpen(false)}
                onConfirm={(uType, uIds) => {
                    setNewConstraintData(prev => ({
                        ...prev,
                        unitsType: uType,
                        unitIds: uIds
                    }));
                    setUnitSelectionModalOpen(false);
                }}
            />

            {/* NEW Units View Modal (Read Only) */}
            {
                unitsViewModalOpen && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-[450px] border border-slate-200 dark:border-slate-800 flex flex-col max-h-[70vh]">
                            <div className="flex justify-between items-center p-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                                <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                    <span className="material-symbols-outlined text-primary">visibility</span>
                                    Unidades Físicas Asignadas
                                </h3>
                                <button onClick={() => setUnitsViewModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4">
                                {(() => {
                                    const constraint = lookaheadPlans.flatMap(p => p.constraints).find(c => c.id === activeConstraintForUnits);
                                    if (!constraint) return null;

                                    if (constraint.unitsType === 'all') {
                                        return <div className="text-sm font-bold text-primary p-4 bg-primary/5 rounded-lg border border-primary/10 text-center">Aplica a TODAS las Unidades Físicas</div>;
                                    }

                                    return (
                                        <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                                            <table className="w-full text-left border-collapse">
                                                <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                                                    <tr>
                                                        <th className="px-4 py-2.5 text-xs font-bold text-slate-500 uppercase w-1/2">Nivel</th>
                                                        <th className="px-4 py-2.5 text-xs font-bold text-slate-500 uppercase w-1/2">Unidades Físicas</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700 bg-white dark:bg-slate-900">
                                                    {constraint.unitIds && constraint.unitIds.length > 0 ? (
                                                        constraint.unitIds.map(uid => {
                                                            const unit = units.find(u => u.id === uid);
                                                            const level = unit ? levels.find(l => l.id === unit.levelId) : null;
                                                            return (
                                                                <tr key={uid} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/50 transition-colors">
                                                                    <td className="px-4 py-2.5 text-xs text-slate-500 dark:text-slate-400 font-medium">
                                                                        {level ? level.name : '-'}
                                                                    </td>
                                                                    <td className="px-4 py-2.5 text-sm font-semibold text-slate-700 dark:text-slate-200">
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="material-symbols-outlined text-[16px] text-primary">check_circle</span>
                                                                            {unit?.name || uid}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })
                                                    ) : (
                                                        <tr>
                                                            <td colSpan={2} className="px-4 py-6 text-center text-xs text-slate-400 italic">
                                                                No hay unidades físicas seleccionadas.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    );
                                })()}
                            </div>
                            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 flex justify-end">
                                <button onClick={() => setUnitsViewModalOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors">Cerrar</button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Export Gantt Modal */}
            {
                exportModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-2xl w-[400px] border border-slate-200 dark:border-slate-800 overflow-hidden">
                            <div className="flex justify-between items-center p-4 border-b border-slate-100 dark:border-slate-800">
                                <h3 className="text-lg font-bold text-slate-800 dark:text-white">Exportar Carta Gantt</h3>
                                <button onClick={() => setExportModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>
                            <div className="p-6 flex flex-col gap-4">
                                <p className="text-sm text-slate-600 dark:text-slate-400">Generar archivo compatible con:</p>
                                <div className="flex flex-col gap-2">
                                    <button
                                        onClick={handleExportToMSProject}
                                        className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left group w-full"
                                    >
                                        <span className="material-symbols-outlined text-blue-600 group-hover:scale-110 transition-transform">calendar_month</span>
                                        <div>
                                            <p className="text-sm font-bold text-slate-800 dark:text-white">Microsoft Project (.xml)</p>
                                            <p className="text-xs text-slate-500">Formato de intercambio estándar</p>
                                        </div>
                                    </button>
                                    <button
                                        onClick={handleExportToMSProject}
                                        className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left group w-full"
                                    >
                                        <span className="material-symbols-outlined text-indigo-600 group-hover:scale-110 transition-transform">article</span>
                                        <div>
                                            <p className="text-sm font-bold text-slate-800 dark:text-white">Primavera P6 (.xml)</p>
                                            <p className="text-xs text-slate-500">Esquema XML compatible (MSPDI)</p>
                                        </div>
                                    </button>
                                </div>
                            </div>
                            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3">
                                <button
                                    onClick={() => setExportModalOpen(false)}
                                    className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                                >
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }


            {/* Reprogramming History Modal */}
            {historyModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-4xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[85vh]">
                        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between bg-slate-50 dark:bg-slate-800/50">
                            <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                <span className="material-symbols-outlined text-blue-600">history</span>
                                Historial de Reprogramaciones
                            </h3>
                            <button onClick={() => setHistoryModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6">
                            {baselines.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                                    <span className="material-symbols-outlined text-5xl mb-2">history</span>
                                    <p>No hay reprogramaciones guardadas.</p>
                                </div>
                            ) : (
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="border-b border-slate-200 dark:border-slate-700">
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">N° / Revisión</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Fecha Carga</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Inicio-Término</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Comentario</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase text-center">Vigente</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase text-center">Ver en Gráfico</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase text-right">Gantt</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {baselines.map(b => (
                                            <tr key={b.id} className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 ${comparisonBaselineId === b.id ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`}>
                                                <td className="px-4 py-4">
                                                    <span className="font-bold text-blue-600 block">#{b.versionNumber}</span>
                                                    <span className="text-[10px] text-slate-400 dark:text-slate-500 font-medium">
                                                        {b.versionNumber === 0 ? 'Línea Base Original' : `Rev ${b.versionNumber}`}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-4 text-xs text-slate-600 dark:text-slate-400">{new Date(b.uploadDate).toLocaleString()}</td>
                                                <td className="px-4 py-4 text-xs font-medium text-slate-700 dark:text-slate-300">
                                                    {b.startDate} al {b.endDate}
                                                </td>
                                                <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-400 max-w-xs truncate" title={b.comment}>
                                                    {b.comment}
                                                </td>
                                                <td className="px-4 py-4 text-center">
                                                    <input
                                                        type="radio"
                                                        name="activeBaselineRadio"
                                                        checked={!!b.vigente}
                                                        onChange={() => {
                                                            const updated = baselines.map(x => ({
                                                                ...x,
                                                                vigente: x.id === b.id
                                                            }));
                                                            setBaselines(updated);
                                                        }}
                                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 dark:border-slate-700 cursor-pointer"
                                                    />
                                                </td>
                                                <td className="px-4 py-4 text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!b.selectedForChart}
                                                        onChange={() => {
                                                            const updated = baselines.map(x => ({
                                                                ...x,
                                                                selectedForChart: x.id === b.id ? !x.selectedForChart : x.selectedForChart
                                                            }));
                                                            setBaselines(updated);
                                                        }}
                                                        className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500 border-slate-300 dark:border-slate-700 cursor-pointer"
                                                    />
                                                </td>
                                                <td className="px-4 py-4 text-right">
                                                    <button
                                                        onClick={() => {
                                                            if (comparisonBaselineId === b.id) {
                                                                setComparisonBaselineId(null);
                                                                setShowComparison(false);
                                                            } else {
                                                                setComparisonBaselineId(b.id);
                                                                setShowComparison(true);
                                                                setHistoryModalOpen(false);
                                                            }
                                                        }}
                                                        className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${comparisonBaselineId === b.id ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-blue-50 hover:text-blue-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-blue-900/30'}`}
                                                    >
                                                        {comparisonBaselineId === b.id ? 'Comparando' : 'Comparar'}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        <div className="p-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 flex justify-end">
                            {showComparison && (
                                <button
                                    onClick={() => { setShowComparison(false); setComparisonBaselineId(null); }}
                                    className="mr-auto text-red-600 hover:text-red-700 text-sm font-bold flex items-center gap-1"
                                >
                                    <span className="material-symbols-outlined text-lg">visibility_off</span>
                                    Quitar Comparación
                                </button>
                            )}
                            <button onClick={() => setHistoryModalOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors">Cerrar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reprogramming Upload Modal */}
            {reprogrammingModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-slate-800 overflow-hidden">
                        <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-blue-50 dark:bg-blue-900/20">
                            <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                <span className="material-symbols-outlined text-blue-600">update</span>
                                Cargar Reprogramación
                            </h3>
                        </div>
                        <div className="p-6 flex flex-col gap-4">
                            <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg border border-amber-200 dark:border-amber-800 flex gap-3 text-amber-700 dark:text-amber-400">
                                <span className="material-symbols-outlined shrink-0 text-xl">info</span>
                                <p className="text-xs font-medium">Esta acción guardará una copia de la Carta Gantt actual en el historial y fusionará el nuevo archivo respetando los avances existentes.</p>
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-slate-500 uppercase">Motivo / Comentario</label>
                                <textarea
                                    className="w-full border rounded-lg p-3 text-sm dark:bg-slate-800 dark:text-white outline-none focus:ring-1 focus:ring-blue-500 h-24"
                                    placeholder="Describa el motivo de esta reprogramación..."
                                    value={reprogrammingComment}
                                    onChange={(e) => setReprogrammingComment(e.target.value)}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3 mt-2">
                                {/* Opción 1: PC Local */}
                                <div className="relative group">
                                    <input
                                        type="file"
                                        accept=".xml,.mpp"
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                        id="reprogramming-file-input"
                                    />
                                    <div className="flex flex-col items-center justify-center p-4 bg-slate-50 dark:bg-slate-800 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl group-hover:bg-blue-50 dark:group-hover:bg-blue-900/20 group-hover:border-blue-500 transition-all text-center">
                                        <span className="material-symbols-outlined text-2xl text-slate-400 group-hover:text-blue-500 mb-1">upload_file</span>
                                        <span className="text-xs font-bold text-slate-600 dark:text-slate-300 group-hover:text-blue-600">Mi PC Local</span>
                                    </div>
                                </div>
                                {/* Opción 2: Gestor Documental */}
                                <div
                                    onClick={() => {
                                        setDocManagerModalType('reprogram');
                                        setDocManagerModalOpen(true);
                                        setReprogrammingModalOpen(false);
                                    }}
                                    className="flex flex-col items-center justify-center p-4 bg-slate-50 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-xl hover:bg-emerald-50 dark:hover:bg-emerald-900/20 hover:border-emerald-500 transition-all cursor-pointer text-center relative"
                                >
                                    <span className="absolute top-2 right-2 flex h-3 w-3">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                                    </span>
                                    <span className="material-symbols-outlined text-2xl text-slate-400 hover:text-emerald-500 mb-1">folder_managed</span>
                                    <span className="text-xs font-bold text-slate-600 dark:text-slate-300">Gestor Documental</span>
                                </div>
                            </div>
                        </div>
                        <div className="p-4 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3 border-t border-slate-200 dark:border-slate-700">
                            <button
                                onClick={() => setReprogrammingModalOpen(false)}
                                className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => {
                                    if (!reprogrammingComment.trim()) {
                                        alert("Por favor, ingrese un motivo para la reprogramación.");
                                        return;
                                    }
                                    const input = document.getElementById('reprogramming-file-input') as HTMLInputElement;
                                    if (input?.files?.[0]) {
                                        (window as any).__REPROGRAMMING_COMMENT = reprogrammingComment;
                                        handleFileImport({ target: input } as any);
                                    } else {
                                        alert("Por favor, seleccione un archivo.");
                                    }
                                }}
                                className="px-6 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors shadow-sm"
                            >
                                Procesar Reprogramación
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Sync Progress Modal */}
            {syncProgressModalOpen && (() => {
                const startStr = tasks[0]?.start || projectInfo.startDate;
                const endStr = tasks[0]?.end || projectInfo.endDate;

                const maxHistoryDateStr = progressHistory && progressHistory.length > 0
                    ? progressHistory.reduce((max, h) => (h.date > max ? h.date : max), progressHistory[0].date)
                    : "";

                const limitDate = (maxHistoryDateStr && startStr && endStr && maxHistoryDateStr > endStr)
                    ? new Date(maxHistoryDateStr + "T12:00:00")
                    : (endStr ? new Date(endStr + "T12:00:00") : new Date());

                const allSyncWeeksList: { index: number; dateStr: string }[] = [];
                if (startStr && endStr) {
                    const current = new Date(startStr + "T12:00:00");
                    while (current.getDay() !== (syncDay % 7)) {
                        current.setDate(current.getDate() + 1);
                    }

                    let idx = 1;
                    while (current <= limitDate) {
                        allSyncWeeksList.push({
                            index: idx++,
                            dateStr: current.toISOString().split('T')[0]
                        });
                        current.setDate(current.getDate() + 7);
                    }

                    for (let i = 0; i < extraWeeksCount; i++) {
                        allSyncWeeksList.push({
                            index: idx++,
                            dateStr: current.toISOString().split('T')[0]
                        });
                        current.setDate(current.getDate() + 7);
                    }
                }

                return (
                    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200">
                        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-6xl overflow-hidden border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 duration-200">
                            <div className="bg-[#1a73e8] p-6 text-white flex justify-between items-center">
                                <div>
                                    <h2 className="text-xl font-bold flex items-center gap-2">
                                        <span className="material-symbols-outlined">history_toggle_off</span>
                                        Sincronizar Avance Semanal
                                    </h2>
                                    <p className="text-blue-100 text-sm mt-1">Registre el avance fotografiado para una fecha específica.</p>
                                    <div className="mt-3 flex items-center gap-2 bg-blue-800/40 px-3 py-1.5 rounded-lg border border-blue-400/30 w-fit">
                                        <span className="material-symbols-outlined text-[16px]">info</span>
                                        <span className="text-[10px] font-medium leading-tight">
                                            Cada sincronización captura el avance <b>calculado en vivo</b> del proyecto.<br />
                                            Este valor puede diferir de la curva S histórica. Puedes usar "Re-Sincronizar" para actualizar una fecha.
                                        </span>
                                    </div>
                                </div>
                                <button onClick={() => { setSyncProgressModalOpen(false); setExtraWeeksCount(0); setIsTableEditMode(false); }} className="hover:bg-white/20 p-2 rounded-full transition-colors">
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>

                            <div className="p-6">
                                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl mb-6 flex items-center justify-between border border-blue-100 dark:border-blue-900/30">
                                    <div>
                                        <label className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider block mb-1">Día de Sincronización</label>
                                        <select
                                            className="bg-transparent font-bold text-slate-800 dark:text-white outline-none cursor-pointer text-sm"
                                            value={syncDay}
                                            onChange={(e) => handleMigrateSyncDayHistory(parseInt(e.target.value))}
                                        >
                                            <option value={1}>Lunes</option>
                                            <option value={2}>Martes</option>
                                            <option value={3}>Miércoles</option>
                                            <option value={4}>Jueves</option>
                                            <option value={5}>Viernes</option>
                                            <option value={6}>Sábado</option>
                                            <option value={0}>Domingo</option>
                                        </select>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        {(() => {
                                            const isAdmin = currentUser?.role === 'admin' ||
                                                currentUser?.role === 'manager' ||
                                                currentUser?.globalRole === 'Administrador' ||
                                                currentUser?.globalRole === 'admin' ||
                                                (typeof hasPermission === 'function' && hasPermission('planning', 'edit'));
                                            if (!isAdmin) return null;

                                            const maxProjectProgress = calculateTotalProjectProgress();
                                            const currentAccumulatedProgress = isTableEditMode
                                                ? allSyncWeeksList.reduce((sum, w) => {
                                                    const val = parseFloat(draftWeeklyGains[w.dateStr] ?? '0');
                                                    return sum + (isNaN(val) ? 0 : val);
                                                }, 0)
                                                : (progressHistory && progressHistory.length > 0 ? Math.max(...progressHistory.map(h => h.progress)) : 0);
                                            const diffToMax = Number((maxProjectProgress - currentAccumulatedProgress).toFixed(2));

                                            return isTableEditMode ? (
                                                <div className="flex items-center gap-2 animate-in fade-in duration-200">
                                                    <span className="text-[10px] font-bold text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-950/60 px-2 py-1 rounded border border-amber-300 dark:border-amber-800 flex items-center gap-1" title="Límite máximo permitido para el avance acumulado">
                                                        <span className="material-symbols-outlined text-[14px]">lock</span>
                                                        Máx: {truncateProgress(maxProjectProgress)}%
                                                    </span>
                                                    <span className={`text-[10px] font-bold px-2 py-1 rounded border flex items-center gap-1 ${diffToMax > 0
                                                            ? 'text-emerald-700 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-950/60 border-emerald-300 dark:border-emerald-800'
                                                            : diffToMax < 0
                                                                ? 'text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-950/60 border-red-300 dark:border-red-800'
                                                                : 'text-blue-700 dark:text-blue-400 bg-blue-100 dark:bg-blue-950/60 border-blue-300 dark:border-blue-800'
                                                        }`} title="Diferencia entre el máximo permitido y el avance acumulado">
                                                        <span className="material-symbols-outlined text-[14px]">calculate</span>
                                                        Diferencia: {diffToMax > 0 ? '+' : ''}{truncateProgress(diffToMax)}%
                                                    </span>
                                                    <button
                                                        onClick={() => {
                                                            const maxLimit = calculateTotalProjectProgress();
                                                            const totalAccum = allSyncWeeksList.reduce((sum, w) => {
                                                                const val = parseFloat(draftWeeklyGains[w.dateStr] ?? '0');
                                                                return sum + (isNaN(val) ? 0 : val);
                                                            }, 0);
                                                            const diff = Number((maxLimit - totalAccum).toFixed(2));

                                                            if (diff > 0) {
                                                                alert("Falta completar avance");
                                                                return;
                                                            }

                                                            if (diff < 0) {
                                                                alert("El avance acumulado no puede ser mayor que el máximo permitido.");
                                                                return;
                                                            }

                                                            handleSaveGlobalTableEdit(allSyncWeeksList, maxLimit);
                                                        }}
                                                        className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-all shadow-md flex items-center gap-1.5"
                                                    >
                                                        <span className="material-symbols-outlined text-[16px]">check_circle</span>
                                                        Guardar Cambios
                                                    </button>
                                                    <button
                                                        onClick={() => setIsTableEditMode(false)}
                                                        className="px-3 py-1.5 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-lg text-xs font-bold transition-all"
                                                    >
                                                        Cancelar
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => {
                                                        handleEnterTableEditMode(allSyncWeeksList);
                                                    }}
                                                    className="px-3.5 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:hover:bg-amber-900/50 dark:text-amber-300 border border-amber-300 dark:border-amber-700/60 rounded-lg text-xs font-bold transition-all shadow-sm flex items-center gap-1.5"
                                                    title="Permite editar manualmente el % Semanal Real para todas las semanas (Solo Administradores)"
                                                >
                                                    <span className="material-symbols-outlined text-[16px]">edit_note</span>
                                                    Editar Tabla
                                                </button>
                                            );
                                        })()}
                                        <div className="text-right pl-4 border-l border-blue-200 dark:border-blue-800/40">
                                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Avance Real Acumulado</p>
                                            <p className="text-xl font-black text-blue-600 dark:text-blue-400">{truncateProgress(calculateTotalProjectProgress())}%</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="max-h-[400px] overflow-auto rounded-xl border border-slate-200 dark:border-slate-800">
                                    <table className="w-full text-left">
                                        <thead className="bg-slate-50 dark:bg-slate-800/50 sticky top-0 z-10 text-[9px] font-black text-slate-500 uppercase tracking-wider select-none">
                                            <tr className="border-b border-slate-200 dark:border-slate-700">
                                                <th colSpan={3} className="px-2 py-1.5 text-slate-400"></th>
                                                <th colSpan={3} className="px-2 py-1.5 text-center bg-blue-50/50 dark:bg-blue-950/20 border-x border-slate-200 dark:border-slate-700 text-blue-600 dark:text-blue-400 font-bold">
                                                    DESEMPEÑO SEMANAL
                                                </th>
                                                <th colSpan={3} className="px-2 py-1.5 text-center bg-emerald-50/50 dark:bg-emerald-950/20 border-r border-slate-200 dark:border-slate-700 text-emerald-600 dark:text-emerald-400 font-bold">
                                                    DESEMPEÑO ACUMULADO
                                                </th>
                                                <th colSpan={2} className="px-2 py-1.5 text-slate-400"></th>
                                            </tr>
                                            <tr className="border-b border-slate-200 dark:border-slate-700">
                                                <th className="px-2 py-2 text-left">Semana</th>
                                                <th className="px-2 py-2 text-left">Fecha</th>
                                                <th className="px-2 py-2 text-left">Estado</th>

                                                <th className="px-2 py-2 text-center bg-blue-50/20 dark:bg-blue-950/10 border-l border-slate-200 dark:border-slate-700">% Sem. Prog.</th>
                                                <th className="px-2 py-2 text-center bg-blue-50/20 dark:bg-blue-950/10">% Sem. Real</th>
                                                <th className="px-2 py-2 text-center bg-blue-50/20 dark:bg-blue-950/10 border-r border-slate-200 dark:border-slate-700">Diff. Semanal</th>

                                                <th className="px-2 py-2 text-center bg-emerald-50/10 dark:bg-emerald-950/5 border-l border-slate-200 dark:border-slate-700">% Acum. Prog.</th>
                                                <th className="px-2 py-2 text-center bg-emerald-50/10 dark:bg-emerald-950/5">% Acum. Real</th>
                                                <th className="px-2 py-2 text-center bg-emerald-50/10 dark:bg-emerald-950/5 border-r border-slate-200 dark:border-slate-700">Diff. Acumulada</th>

                                                <th className="px-2 py-2 text-center">Atraso C.S / R.C</th>
                                                <th className="px-2 py-2 text-right">Acción</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                            {(() => {
                                                const getProgPct = (dateStr: string): number => {
                                                    return calculateProgrammedProgressAtDate(tasks, dateStr);
                                                };

                                                const generateAllSyncWeeks = () => {
                                                    const startStr = tasks[0]?.start || projectInfo.startDate;
                                                    const endStr = tasks[0]?.end || projectInfo.endDate;
                                                    if (!startStr || !endStr) return [];

                                                    const maxHistoryDateStr = progressHistory && progressHistory.length > 0
                                                        ? progressHistory.reduce((max, h) => (h.date > max ? h.date : max), progressHistory[0].date)
                                                        : "";

                                                    const limitDate = (maxHistoryDateStr && maxHistoryDateStr > endStr)
                                                        ? new Date(maxHistoryDateStr + "T12:00:00")
                                                        : new Date(endStr + "T12:00:00");

                                                    // Generate base weeks
                                                    const weeksList: { index: number; dateStr: string }[] = [];
                                                    const current = new Date(startStr + "T12:00:00");
                                                    while (current.getDay() !== (syncDay % 7)) {
                                                        current.setDate(current.getDate() + 1);
                                                    }

                                                    let idx = 1;
                                                    while (current <= limitDate) {
                                                        weeksList.push({
                                                            index: idx++,
                                                            dateStr: current.toISOString().split('T')[0]
                                                        });
                                                        current.setDate(current.getDate() + 7);
                                                    }

                                                    // Add extra weeks from state
                                                    for (let i = 0; i < extraWeeksCount; i++) {
                                                        weeksList.push({
                                                            index: idx++,
                                                            dateStr: current.toISOString().split('T')[0]
                                                        });
                                                        current.setDate(current.getDate() + 7);
                                                    }

                                                    return weeksList;
                                                };

                                                const allWeeks = generateAllSyncWeeks();

                                                return allWeeks.map((week, index) => {
                                                    const date = week.dateStr;
                                                    const record = progressHistory.find(r => (r.weekIndex ? r.weekIndex === week.index : r.date === date));

                                                    // Programmed accumulated and weekly
                                                    const currentPlan = getProgPct(date);
                                                    const prevWeek = allWeeks[index - 1];
                                                    const prevPlan = prevWeek ? getProgPct(prevWeek.dateStr) : 0;
                                                    const programmedWeekly = Math.max(0, currentPlan - prevPlan);

                                                    const prevRecord = prevWeek ? progressHistory.find(r => (r.weekIndex ? r.weekIndex === prevWeek.index : r.date === prevWeek.dateStr)) : null;

                                                    let weeklyGain: number | null = null;
                                                    let diffWeekly: number | null = null;
                                                    let diffAccumulated: number | null = null;

                                                    if (record) {
                                                        if (prevRecord) {
                                                            weeklyGain = record.progress - prevRecord.progress;
                                                        } else if (index === 0) {
                                                            weeklyGain = record.progress;
                                                        } else {
                                                            let lastSyncedRecord = null;
                                                            for (let i = index - 1; i >= 0; i--) {
                                                                const r = progressHistory.find(h => (h.weekIndex ? h.weekIndex === allWeeks[i].index : h.date === allWeeks[i].dateStr));
                                                                if (r) {
                                                                    lastSyncedRecord = r;
                                                                    break;
                                                                }
                                                            }
                                                            weeklyGain = record.progress - (lastSyncedRecord ? lastSyncedRecord.progress : 0);
                                                        }

                                                        diffWeekly = weeklyGain - programmedWeekly;
                                                        diffAccumulated = record.progress - currentPlan;
                                                    }

                                                    // Calculate cumulative draft real progress for this row in global edit mode
                                                    let draftAccumReal: number | null = null;
                                                    let draftWeeklyGainVal: number | null = null;

                                                    if (isTableEditMode) {
                                                        let accum = 0;
                                                        for (let i = 0; i <= index; i++) {
                                                            const dStr = allWeeks[i].dateStr;
                                                            const inputVal = draftWeeklyGains[dStr];
                                                            const numVal = inputVal !== undefined && inputVal !== '' ? parseFloat(inputVal) : 0;
                                                            accum += !isNaN(numVal) ? numVal : 0;
                                                            if (i === index) {
                                                                draftWeeklyGainVal = !isNaN(numVal) ? numVal : 0;
                                                            }
                                                        }
                                                        const maxLimit = calculateTotalProjectProgress();
                                                        draftAccumReal = Number(Math.min(maxLimit, Math.max(0, accum)).toFixed(2));
                                                    }

                                                    const isSynced = isTableEditMode
                                                        ? (parseFloat(draftWeeklyGains[date] ?? '0') > 0)
                                                        : (record !== undefined && record.progress > 0);

                                                    const currentWeeklyGain = isTableEditMode
                                                        ? draftWeeklyGainVal
                                                        : (isSynced && weeklyGain !== null ? weeklyGain : null);
                                                    const currentDiffWeekly = isTableEditMode
                                                        ? (draftWeeklyGainVal !== null ? draftWeeklyGainVal - programmedWeekly : null)
                                                        : (isSynced && diffWeekly !== null ? diffWeekly : null);
                                                    const currentAccumReal = isTableEditMode
                                                        ? draftAccumReal
                                                        : (isSynced && record ? record.progress : null);
                                                    const currentDiffAccumulated = isTableEditMode
                                                        ? (draftAccumReal !== null ? draftAccumReal - currentPlan : null)
                                                        : (isSynced && record ? record.progress - currentPlan : null);

                                                    // Dynamic delay metrics for this week (Curva S vs Ruta Crítica)
                                                    const accumRealVal = currentAccumReal !== null ? currentAccumReal : (record ? record.progress : 0);
                                                    const curveData = calculateSCurve(processedTasks, progressHistory, syncDay);

                                                    let projectTotalDays = 0;
                                                    if (processedTasks && processedTasks.length > 0) {
                                                        const flat = flattenTasks(processedTasks).filter(t => t.type === 'task' && !t.isWeeklySubtask);
                                                        let minStart = "", maxEnd = "";
                                                        flat.forEach(t => {
                                                            if (t.start && (!minStart || t.start < minStart)) minStart = t.start;
                                                            if (t.end && (!maxEnd || t.end > maxEnd)) maxEnd = t.end;
                                                        });
                                                        if (minStart && maxEnd) {
                                                            const sTime = new Date(minStart + "T12:00:00").getTime();
                                                            const eTime = new Date(maxEnd + "T12:00:00").getTime();
                                                            projectTotalDays = Math.max(1, Math.ceil(Math.abs(eTime - sTime) / (1000 * 60 * 60 * 24)) + 1);
                                                        }
                                                    }
                                                    if (projectTotalDays <= 0) projectTotalDays = 100;

                                                    const calculatedCS = calculateDelayDays(curveData, accumRealVal, date, projectTotalDays, currentPlan);
                                                    const calculatedRC = getCriticalPathDelayAtDate(date);
                                                    const delayCSVal = (record && record.delayCurvaS && record.delayCurvaS > 0) ? record.delayCurvaS : calculatedCS;
                                                    const delayRCVal = calculatedRC;

                                                    return (
                                                        <tr key={date} className={`transition-colors border-b border-slate-100 dark:border-slate-800 ${isTableEditMode ? 'bg-blue-50/20 dark:bg-blue-950/10' : 'hover:bg-slate-50 dark:hover:bg-slate-800/30'}`}>
                                                            <td className="px-2 py-2 text-xs font-bold text-slate-500 uppercase tracking-tight">Semana {week.index}</td>
                                                            <td className="px-2 py-2 text-xs font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">{date}</td>
                                                            <td className="px-2 py-2">
                                                                {isSynced ? (
                                                                    <span className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-[9px] font-bold px-2 py-0.5 rounded-full border border-emerald-200 dark:border-emerald-800 flex items-center gap-0.5 w-fit whitespace-nowrap">
                                                                        <span className="material-symbols-outlined text-[12px]">check_circle</span>
                                                                        Sincronizado
                                                                    </span>
                                                                ) : (
                                                                    <span className="bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 text-[9px] font-bold px-2 py-0.5 rounded-full border border-amber-200 dark:border-amber-700 flex items-center gap-0.5 w-fit whitespace-nowrap italic">
                                                                        <span className="material-symbols-outlined text-[12px]">pending</span>
                                                                        Pendiente
                                                                    </span>
                                                                )}
                                                            </td>

                                                            {/* DESEMPEÑO SEMANAL */}
                                                            {/* % Sem. Prog. */}
                                                            <td className="px-2 py-2 text-center text-xs text-slate-600 dark:text-slate-400 bg-blue-50/5 dark:bg-blue-950/5 border-l border-slate-150 dark:border-slate-800">
                                                                {truncateProgress(programmedWeekly)}%
                                                            </td>
                                                            {/* % Sem. Real */}
                                                            <td className="px-2 py-2 text-center text-xs font-semibold bg-blue-50/5 dark:bg-blue-950/5">
                                                                {isTableEditMode ? (
                                                                    <div className="flex items-center justify-center gap-1">
                                                                        <input
                                                                            type="number"
                                                                            step="0.01"
                                                                            min="0"
                                                                            max="100"
                                                                            value={draftWeeklyGains[date] ?? ''}
                                                                            onChange={(e) => {
                                                                                const val = e.target.value;
                                                                                const numVal = parseFloat(val) || 0;
                                                                                if (numVal < 0) return;

                                                                                const maxLimit = calculateTotalProjectProgress();
                                                                                const testDrafts = { ...draftWeeklyGains, [date]: val };
                                                                                const newAccum = allWeeks.reduce((sum, w) => {
                                                                                    const vStr = w.dateStr === date ? val : (testDrafts[w.dateStr] ?? '0');
                                                                                    const vNum = parseFloat(vStr) || 0;
                                                                                    return sum + (vNum > 0 ? vNum : 0);
                                                                                }, 0);

                                                                                if (Number(newAccum.toFixed(2)) > Number(maxLimit.toFixed(2))) {
                                                                                    alert(`El avance acumulado (${truncateProgress(newAccum)}%) no puede ser mayor que el máximo permitido (${truncateProgress(maxLimit)}%).`);
                                                                                    return;
                                                                                }

                                                                                setDraftWeeklyGains(prev => ({ ...prev, [date]: val }));
                                                                            }}
                                                                            className="w-16 px-1.5 py-0.5 text-xs text-center font-bold border-2 border-blue-500 rounded bg-white dark:bg-slate-900 dark:text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                        />
                                                                        <span className="text-xs font-bold text-blue-600 dark:text-blue-400">%</span>
                                                                    </div>
                                                                ) : currentWeeklyGain !== null ? (
                                                                    <span className="text-blue-600 dark:text-blue-400">
                                                                        {truncateProgress(currentWeeklyGain)}%
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-slate-400">—</span>
                                                                )}
                                                            </td>
                                                            {/* Diff. Semanal */}
                                                            <td className="px-2 py-2 text-center text-xs font-bold bg-blue-50/5 dark:bg-blue-950/5 border-r border-slate-150 dark:border-slate-800">
                                                                {currentDiffWeekly !== null ? (
                                                                    <span className={currentDiffWeekly >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}>
                                                                        {currentDiffWeekly >= 0 ? '+' : ''}{truncateProgress(currentDiffWeekly)}%
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-slate-400">—</span>
                                                                )}
                                                            </td>

                                                            {/* DESEMPEÑO ACUMULADO */}
                                                            {/* % Acum. Prog. */}
                                                            <td className="px-2 py-2 text-center text-xs text-slate-600 dark:text-slate-400 bg-emerald-50/5 dark:bg-emerald-950/5 border-l border-slate-150 dark:border-slate-800">
                                                                {truncateProgress(currentPlan)}%
                                                            </td>
                                                            {/* % Acum. Real */}
                                                            <td className="px-2 py-2 text-center text-xs font-semibold bg-emerald-50/5 dark:bg-emerald-950/5">
                                                                {currentAccumReal !== null ? (
                                                                    <span className="text-emerald-600 dark:text-emerald-400 font-bold">
                                                                        {truncateProgress(currentAccumReal)}%
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-slate-400">—</span>
                                                                )}
                                                            </td>
                                                            {/* Diff. Acumulada */}
                                                            <td className="px-2 py-2 text-center text-xs font-bold bg-emerald-50/5 dark:bg-emerald-950/5 border-r border-slate-150 dark:border-slate-800">
                                                                {currentDiffAccumulated !== null ? (
                                                                    <span className={currentDiffAccumulated >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}>
                                                                        {currentDiffAccumulated >= 0 ? '+' : ''}{truncateProgress(currentDiffAccumulated)}%
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-slate-400">—</span>
                                                                )}
                                                            </td>

                                                            {/* ATRASO C.S / R.C */}
                                                            <td className="px-2 py-2 text-center">
                                                                <div className="flex items-center justify-center gap-1 select-none">
                                                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${delayCSVal > 0 ? 'bg-red-50 text-red-600 dark:bg-red-950/20 dark:text-red-400' : 'bg-slate-50 text-slate-400 dark:bg-slate-800'}`}>
                                                                        {delayCSVal}d
                                                                    </span>
                                                                    <span className="text-slate-300 dark:text-slate-700">/</span>
                                                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${delayRCVal > 0 ? 'bg-orange-50 text-orange-600 dark:bg-orange-950/20 dark:text-orange-400' : 'bg-slate-50 text-slate-400 dark:bg-slate-800'}`}>
                                                                        {delayRCVal}d
                                                                    </span>
                                                                </div>
                                                            </td>

                                                            {/* ACCIÓN */}
                                                            <td className="px-2 py-2 text-right">
                                                                <button
                                                                    onClick={() => {
                                                                        setSyncConfirmData({ date, delayCS: delayCSVal, delayRC: delayRCVal, weekIndex: week.index });
                                                                        setSyncConfirmOpen(true);
                                                                    }}
                                                                    className={`px-2.5 py-1 rounded-md text-[9px] font-bold uppercase tracking-wider transition-all shadow-sm whitespace-nowrap ${isSynced ? 'bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400' : 'bg-blue-600 text-white hover:bg-blue-700 font-bold'}`}
                                                                >
                                                                    {isSynced ? 'Re-Sincronizar' : 'Sincronizar'}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                });
                                            })()}
                                        </tbody>
                                    </table>
                                    <div className="p-4 bg-slate-50 dark:bg-slate-800/30 border-t border-slate-200 dark:border-slate-700 flex justify-center">
                                        <button
                                            type="button"
                                            onClick={() => setExtraWeeksCount(prev => prev + 1)}
                                            className="px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 dark:text-blue-400 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
                                        >
                                            <span className="material-symbols-outlined text-[16px]">add_circle</span>
                                            + Agregar Semana
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 flex justify-end">
                                <button
                                    onClick={() => { setSyncProgressModalOpen(false); setExtraWeeksCount(0); setIsTableEditMode(false); }}
                                    className="px-6 py-2 text-sm font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl transition-colors"
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Sync Confirmation Modal */}
            {syncConfirmOpen && syncConfirmData && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[110] flex items-center justify-center p-4 animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 duration-200">
                        <div className="p-6">
                            <h3 className="text-base font-bold text-slate-800 dark:text-white mb-2">Confirmar Sincronización</h3>
                            <p className="text-slate-600 dark:text-slate-400 text-xs leading-relaxed">
                                ¿Está seguro de que desea realizar/actualizar la sincronización de avance para esta semana? Esta acción actualizará la foto de progreso y las métricas históricas del proyecto.
                            </p>
                        </div>
                        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setSyncConfirmOpen(false);
                                    setSyncConfirmData(null);
                                }}
                                className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => {
                                    handleSyncProgressToDate(syncConfirmData.date, syncConfirmData.delayCS, syncConfirmData.delayRC, syncConfirmData.weekIndex);
                                    setSyncConfirmOpen(false);
                                    setSyncConfirmData(null);
                                }}
                                className="px-4 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded shadow transition-colors"
                            >
                                Confirmar Sincronización
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: Observaciones al Liberar / No Liberar Restricción */}
            {constraintObsModalOpen && constraintObsData && (
                isMobile ? (
                    <KlSheet
                        open={constraintObsModalOpen}
                        onClose={() => setConstraintObsModalOpen(false)}
                        title={constraintObsData.currentStatus === 'NO LIBERADO' ? 'No Liberar Restricción' : 'Liberar Restricción'}
                        subtitle={constraintObsData.description}
                        footer={
                            <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
                                <button
                                    type="button"
                                    onClick={() => setConstraintObsModalOpen(false)}
                                    className="b b-o"
                                    style={{ flex: 1 }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleReleaseConstraintWithObservation(constraintObsData.planId, constraintObsData.constraintId, constraintObsData.observations, constraintObsData.currentStatus as any)}
                                    className={constraintObsData.currentStatus === 'NO LIBERADO' ? 'b b-r' : 'b b-g'}
                                    style={{ flex: 1 }}
                                >
                                    {constraintObsData.currentStatus === 'NO LIBERADO' ? 'Confirmar No Liberado' : 'Confirmar Liberación'}
                                </button>
                            </div>
                        }
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '10px 0' }}>
                            <div style={{ fontSize: '13px', color: '#475569', lineHeight: '1.4' }}>
                                Has marcado como <strong style={{ color: '#0F172A' }}>{constraintObsData.currentStatus}</strong> la restricción: <strong style={{ color: '#0F172A' }}>{constraintObsData.description}</strong>. Ingresa alguna observación o evidencia si es necesario.
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', marginBottom: '6px' }}>
                                    Observaciones
                                </label>
                                <BufferedTextArea
                                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-primary/50 text-slate-900 dark:text-white text-xs h-24 resize-none"
                                    placeholder={constraintObsData.currentStatus === 'NO LIBERADO' ? "Justifica por qué no se liberó esta restricción..." : "Justifica la liberación de esta restricción..."}
                                    value={constraintObsData.observations}
                                    onValueChange={(val) => setConstraintObsData({ ...constraintObsData, observations: val })}
                                />
                            </div>
                        </div>
                    </KlSheet>
                ) : (
                    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-[70]">
                        <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden animate-fade-in-up">
                            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800">
                                <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase">
                                    {constraintObsData.currentStatus === 'NO LIBERADO' ? 'No Liberar Restricción' : 'Liberar Restricción'}
                                </h3>
                                <button onClick={() => setConstraintObsModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors">
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>
                            <div className="p-5 space-y-4 text-xs">
                                <div className="text-slate-500 mb-2">
                                    Has marcado como <strong className="text-slate-700 dark:text-slate-300">{constraintObsData.currentStatus}</strong> la restricción: <strong className="text-slate-700 dark:text-slate-300">{constraintObsData.description}</strong>. Ingresa alguna observación o evidencia si es necesario.
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Observaciones</label>
                                    <BufferedTextArea
                                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-primary/50 text-slate-900 dark:text-white text-xs h-24 resize-none"
                                        placeholder={constraintObsData.currentStatus === 'NO LIBERADO' ? "Justifica por qué no se liberó esta restricción..." : "Justifica la liberación de esta restricción..."}
                                        value={constraintObsData.observations}
                                        onValueChange={(val) => setConstraintObsData({ ...constraintObsData, observations: val })}
                                    />
                                </div>
                            </div>
                            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3 border-t border-slate-200 dark:border-slate-800">
                                <button
                                    onClick={() => setConstraintObsModalOpen(false)}
                                    className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={() => handleReleaseConstraintWithObservation(constraintObsData.planId, constraintObsData.constraintId, constraintObsData.observations, constraintObsData.currentStatus as any)}
                                    className={`px-5 py-2 text-white rounded-xl text-xs font-bold shadow-md active:scale-95 transition-all ${constraintObsData.currentStatus === 'NO LIBERADO' ? 'bg-amber-600 hover:bg-amber-700 shadow-amber-500/20' : 'bg-green-600 hover:bg-green-700 shadow-green-500/20'}`}
                                >
                                    {constraintObsData.currentStatus === 'NO LIBERADO' ? 'Confirmar No Liberado' : 'Confirmar Liberación'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            )}

            {/* Modal Móvil: Menú ⋮ de Acciones Secundarias para Restricción */}
            {mobileCardActionTarget && (
                <KlMenu
                    open={!!mobileCardActionTarget}
                    onClose={() => setMobileCardActionTarget(null)}
                    anchorRef={{ current: mobileCardActionAnchor }}
                    items={[
                        {
                            id: 'details',
                            label: 'Ver Detalles',
                            iconName: 'info',
                            onClick: () => {
                                const target = mobileCardActionTarget;
                                setMobileCardActionTarget(null);
                                setMobileConstraintDetailTarget(target);
                            }
                        },
                        ...(activeLookahead?.status !== 'Cerrada' && (
                            currentUser?.role === 'admin' ||
                            currentUser?.role === 'manager' ||
                            currentUser?.globalRole === 'Administrador' ||
                            responsibles.find(r => r.id === mobileCardActionTarget.constraint?.responsibleId)?.email === currentUser?.email
                        ) ? [
                            {
                                id: 'edit_obs',
                                label: 'Editar Observaciones',
                                iconName: 'edit_note',
                                onClick: () => {
                                    const target = mobileCardActionTarget;
                                    setMobileCardActionTarget(null);
                                    setConstraintObsData({
                                        planId: activeLookahead!.id,
                                        constraintId: target.constraint.id,
                                        currentStatus: target.constraint.status || 'Pendiente',
                                        observations: target.constraint.observations || '',
                                        description: target.constraint.description
                                    });
                                    setConstraintObsModalOpen(true);
                                }
                            }
                        ] : [])
                    ]}
                />
            )}

            {/* Modal Móvil: Detalle Completo de Restricción */}
            {mobileConstraintDetailTarget && (
                <KlDialog
                    open={!!mobileConstraintDetailTarget}
                    onClose={() => setMobileConstraintDetailTarget(null)}
                    title="Detalle de Restricción"
                    confirmText="Cerrar"
                    onConfirm={() => setMobileConstraintDetailTarget(null)}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13px', maxHeight: '72vh', overflowY: 'auto', paddingRight: '4px' }}>
                        <div>
                            <span style={{ fontSize: '10px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.03em' }}>Tarea</span>
                            <div style={{ fontWeight: 700, color: '#0F172A', marginTop: '2px' }}>
                                {cleanTaskName(mobileConstraintDetailTarget.task.name)}
                                {mobileConstraintDetailTarget.ganttNumber ? ` (#${mobileConstraintDetailTarget.ganttNumber})` : ''}
                            </div>
                        </div>
                        <div>
                            <span style={{ fontSize: '10px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.03em' }}>Tipo de Restricción</span>
                            <div style={{ fontWeight: 600, color: '#0F172A', marginTop: '2px' }}>{mobileConstraintDetailTarget.typeName || 'General'}</div>
                        </div>
                        <div>
                            <span style={{ fontSize: '10px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.03em' }}>Restricción (Detalle)</span>
                            <div style={{ color: '#334155', marginTop: '2px', background: '#F8FAFC', padding: '8px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', whiteSpace: 'pre-wrap' }}>
                                {mobileConstraintDetailTarget.constraint.description || 'Sin detalle ingresado'}
                            </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '4px' }}>
                            <div>
                                <span style={{ fontSize: '10px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>Nivel</span>
                                <div style={{ fontWeight: 600, color: '#0F172A' }}>{mobileConstraintDetailTarget.levelName || 'General'}</div>
                            </div>
                            <div>
                                <span style={{ fontSize: '10px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>Sitio / Unidades</span>
                                <div style={{ fontWeight: 600, color: '#0F172A' }}>
                                    {(() => {
                                        const c = mobileConstraintDetailTarget.constraint;
                                        if (c.unitsType === 'all') return 'Todas las unidades';
                                        if (c.unitIds && c.unitIds.length > 0) {
                                            const names = c.unitIds.map((uId: string) => units.find(u => u.id === uId)?.name || uId);
                                            return names.join(', ');
                                        }
                                        if ((mobileConstraintDetailTarget.task as any)?.unitId) {
                                            return units.find(u => u.id === (mobileConstraintDetailTarget.task as any).unitId)?.name || 'General';
                                        }
                                        return 'No aplica';
                                    })()}
                                </div>
                            </div>
                            <div>
                                <span style={{ fontSize: '10px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>Responsable</span>
                                <div style={{ fontWeight: 600, color: '#0F172A' }}>{mobileConstraintDetailTarget.respName || 'Sin asignar'}</div>
                            </div>
                            <div>
                                <span style={{ fontSize: '10px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>Fecha Requerida</span>
                                <div style={{ fontWeight: 600, color: '#0F172A' }}>
                                    {mobileConstraintDetailTarget.constraint.requiredDate ? formatDateToShow(mobileConstraintDetailTarget.constraint.requiredDate) : (mobileConstraintDetailTarget.constraint.requiredDate || 'Sin fecha')}
                                </div>
                            </div>
                            <div>
                                <span style={{ fontSize: '10px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>Estado</span>
                                <div style={{ fontWeight: 700, color: mobileConstraintDetailTarget.constraint.status === 'Liberado' ? '#16A34A' : mobileConstraintDetailTarget.constraint.status === 'NO LIBERADO' ? '#DC2626' : '#D97706' }}>
                                    {mobileConstraintDetailTarget.constraint.status || 'Pendiente'}
                                </div>
                            </div>
                        </div>
                        {mobileConstraintDetailTarget.constraint.observations && (
                            <div style={{ marginTop: '6px', background: '#F8FAFC', padding: '8px 10px', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                                <span style={{ fontSize: '10px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>Observación (Cierre / Seguimiento)</span>
                                <div style={{ color: '#334155', fontStyle: 'italic', marginTop: '2px', whiteSpace: 'pre-wrap' }}>{mobileConstraintDetailTarget.constraint.observations}</div>
                            </div>
                        )}
                    </div>
                </KlDialog>
            )}

            {/* Modal Móvil: Hoja de Filtros para Planificación Semanal (Punto 2) */}
            {isWeeklyFilterSheetOpen && activePlan && (() => {
                const allocs = (activePlan.allocations || []).filter(Boolean);

                // Helper para desambiguar unidades físicas repetidas en diferentes niveles/manzanas
                const getDisambiguatedUnitName = (uId: string) => {
                    const unit = units.find(u => u.id === uId);
                    if (!unit) return uId || '-';
                    const isRepeated = units.filter(u2 => u2.name && u2.name.trim().toLowerCase() === unit.name.trim().toLowerCase()).length > 1;
                    if (isRepeated) {
                        const lvl = levels.find(l => l.id === unit.levelId);
                        return `${unit.name} · ${lvl?.name || 'General'}`;
                    }
                    return unit.name;
                };

                // Orden natural (1, 2, 3 ... 10, 12)
                const naturalSort = (a: string, b: string) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });

                // Niveles con conteo
                const levelCounts: Record<string, number> = {};
                allocs.forEach(a => {
                    const lvl = levels.find(l => l.id === (findTaskById(a.taskId, tasks) as any)?.levelId)?.name || 'General';
                    levelCounts[lvl] = (levelCounts[lvl] || 0) + 1;
                });
                const distinctLevels = Object.keys(levelCounts).sort(naturalSort);

                // Unidades físicas desambiguadas con conteo
                const unitCounts: Record<string, number> = {};
                allocs.forEach(a => {
                    const uName = getDisambiguatedUnitName(a.unitId);
                    unitCounts[uName] = (unitCounts[uName] || 0) + 1;
                });
                const distinctUnits = Object.keys(unitCounts).sort(naturalSort);

                // Responsables con conteo
                const respCounts: Record<string, number> = {};
                allocs.forEach(a => {
                    const rName = responsibles.find(r => r.id === a.responsibleId)?.name || 'Sin asignar';
                    respCounts[rName] = (respCounts[rName] || 0) + 1;
                });
                const distinctResponsibles = Object.keys(respCounts).sort(naturalSort);

                // Tareas con conteo
                const taskCounts: Record<string, number> = {};
                allocs.forEach(a => {
                    const t = findTaskById(a.taskId, tasks);
                    const rowNum = getTaskGanttRowNumber(t, tasks);
                    const rawName = cleanTaskName(t?.name || (a.isAdditional ? (a.additionalTaskName || 'Tarea Adicional') : ''));
                    const taskLabel = rowNum ? `#${rowNum} ${rawName}`.trim() : (rawName || 'Tarea');
                    taskCounts[taskLabel] = (taskCounts[taskLabel] || 0) + 1;
                });
                const distinctTasks = Object.keys(taskCounts).sort(naturalSort);

                // Estados con conteo
                const statusCounts: Record<string, number> = { 'Cumplida': 0, 'Pendiente': 0, 'No cumplida': 0 };
                allocs.forEach(a => {
                    const st = a.executed === true ? 'Cumplida' : a.executed === false ? 'No cumplida' : 'Pendiente';
                    statusCounts[st] = (statusCounts[st] || 0) + 1;
                });
                const distinctStatuses = ['Cumplida', 'Pendiente', 'No cumplida'];

                const categories = [
                    { id: 'levels', label: 'Nivel', values: distinctLevels, counts: levelCounts },
                    { id: 'units', label: 'Unidad Física', values: distinctUnits, counts: unitCounts },
                    { id: 'responsibles', label: 'Responsable', values: distinctResponsibles, counts: respCounts },
                    { id: 'tasks', label: 'Tarea', values: distinctTasks, counts: taskCounts },
                    { id: 'statuses', label: 'Estado', values: distinctStatuses, counts: statusCounts }
                ];

                const toggleValue = (catId: keyof WeeklyMobileFilterState, val: string) => {
                    setDraftWeeklyMobileFilters(prev => {
                        const current = prev[catId] || [];
                        const exists = current.includes(val);
                        return {
                            ...prev,
                            [catId]: exists ? current.filter(x => x !== val) : [...current, val]
                        };
                    });
                };

                // Total de filtros activos en el borrador
                const totalActiveFilters = (
                    draftWeeklyMobileFilters.levels.length +
                    draftWeeklyMobileFilters.units.length +
                    draftWeeklyMobileFilters.responsibles.length +
                    draftWeeklyMobileFilters.tasks.length +
                    draftWeeklyMobileFilters.statuses.length
                );

                // Cálculo dinámico de tareas que cumplen con los filtros del borrador
                const matchingTasksCount = allocs.filter(alloc => {
                    if (mobileFilterWeekly === 'mine' && currentUser) {
                        const resp = responsibles.find(r => r.id === alloc.responsibleId);
                        if (!resp || resp.email !== currentUser.email) return false;
                    }
                    if (draftWeeklyMobileFilters.levels.length > 0) {
                        const t = findTaskById(alloc.taskId, tasks);
                        const lvl = levels.find(l => l.id === (t as any)?.levelId)?.name || 'General';
                        if (!draftWeeklyMobileFilters.levels.includes(lvl)) return false;
                    }
                    if (draftWeeklyMobileFilters.units.length > 0) {
                        const u = units.find(u => u.id === alloc.unitId);
                        const uDisplayName = getDisambiguatedUnitName(alloc.unitId);
                        const match = draftWeeklyMobileFilters.units.includes(uDisplayName) ||
                            (u && draftWeeklyMobileFilters.units.includes(u.name)) ||
                            draftWeeklyMobileFilters.units.includes(alloc.unitId);
                        if (!match) return false;
                    }
                    if (draftWeeklyMobileFilters.responsibles.length > 0) {
                        const resp = responsibles.find(r => r.id === alloc.responsibleId);
                        const rName = resp?.name || 'Sin asignar';
                        if (!draftWeeklyMobileFilters.responsibles.includes(rName)) return false;
                    }
                    if (draftWeeklyMobileFilters.tasks.length > 0) {
                        const t = findTaskById(alloc.taskId, tasks);
                        const rowNum = getTaskGanttRowNumber(t, tasks);
                        const rawName = cleanTaskName(t?.name || (alloc.isAdditional ? (alloc.additionalTaskName || 'Tarea Adicional') : ''));
                        const taskLabel = rowNum ? `#${rowNum} ${rawName}`.trim() : (rawName || 'Tarea');
                        if (!draftWeeklyMobileFilters.tasks.includes(taskLabel) && !(rawName && draftWeeklyMobileFilters.tasks.includes(rawName))) return false;
                    }
                    if (draftWeeklyMobileFilters.statuses.length > 0) {
                        const st = alloc.executed === true ? 'Cumplida' : alloc.executed === false ? 'No cumplida' : 'Pendiente';
                        if (!draftWeeklyMobileFilters.statuses.includes(st)) return false;
                    }
                    return true;
                }).length;

                return (
                    <KlSheet
                        open={isWeeklyFilterSheetOpen}
                        onClose={() => setIsWeeklyFilterSheetOpen(false)}
                        title="Filtrar Tareas"
                        subtitle={`Semanal #${activePlan.id} · ${totalActiveFilters} ${totalActiveFilters === 1 ? 'filtro activo' : 'filtros activos'}`}
                        footer={
                            <div style={{ display: 'flex', gap: '10px', width: '100%', alignItems: 'center' }}>
                                <button
                                    type="button"
                                    onClick={() => setDraftWeeklyMobileFilters(initialWeeklyMobileFilters)}
                                    className="b b-o"
                                    style={{
                                        width: '40%',
                                        flex: 'none',
                                        background: '#F1F5F9',
                                        color: '#475569',
                                        borderColor: '#CBD5E1',
                                        fontWeight: 600,
                                        height: '42px',
                                        borderRadius: '10px'
                                    }}
                                >
                                    Limpiar
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setWeeklyMobileFilters(draftWeeklyMobileFilters);
                                        setIsWeeklyFilterSheetOpen(false);
                                    }}
                                    className="b b-p"
                                    style={{
                                        width: '60%',
                                        flex: 1,
                                        background: '#2563EB',
                                        color: '#ffffff',
                                        fontWeight: 700,
                                        height: '42px',
                                        borderRadius: '10px'
                                    }}
                                >
                                    Ver {matchingTasksCount} {matchingTasksCount === 1 ? 'tarea' : 'tareas'}
                                </button>
                            </div>
                        }
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px', padding: '10px 0 20px' }}>
                            {categories.map(cat => {
                                const isExpanded = !!expandedFilterCategories[cat.id];
                                const search = (weeklyFilterCategorySearch[cat.id] || '').toLowerCase();
                                const filteredVals = search ? cat.values.filter(v => v.toLowerCase().includes(search)) : cat.values;
                                const showSearch = cat.values.length > 6;
                                const hasMany = filteredVals.length > 8 && !search;
                                const displayVals = (!isExpanded && hasMany) ? filteredVals.slice(0, 6) : filteredVals;
                                const remainingCount = filteredVals.length - 6;
                                const selectedCount = draftWeeklyMobileFilters[cat.id as keyof WeeklyMobileFilterState].length;

                                return (
                                    <div key={cat.id}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                            <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.03em' }}>
                                                {cat.label}
                                            </span>
                                            {selectedCount > 0 && (
                                                <span style={{ fontSize: '11px', fontWeight: 700, color: '#2563EB' }}>
                                                    {selectedCount} {selectedCount === 1 ? 'elegida' : 'elegidas'}
                                                </span>
                                            )}
                                        </div>

                                        {showSearch && (
                                            <div style={{ marginBottom: '8px' }}>
                                                <input
                                                    type="text"
                                                    value={weeklyFilterCategorySearch[cat.id] || ''}
                                                    onChange={(e) => setWeeklyFilterCategorySearch(prev => ({ ...prev, [cat.id]: e.target.value }))}
                                                    placeholder={`Buscar ${cat.label.toLowerCase()}...`}
                                                    style={{
                                                        width: '100%',
                                                        height: '34px',
                                                        borderRadius: '8px',
                                                        border: '1px solid #CBD5E1',
                                                        background: '#F8FAFC',
                                                        padding: '0 10px',
                                                        fontSize: '12px',
                                                        boxSizing: 'border-box',
                                                        outline: 'none'
                                                    }}
                                                />
                                            </div>
                                        )}

                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                            {displayVals.map(val => {
                                                const isSel = draftWeeklyMobileFilters[cat.id as keyof WeeklyMobileFilterState].includes(val);
                                                const count = cat.counts[val] || 0;
                                                return (
                                                    <button
                                                        key={val}
                                                        type="button"
                                                        onClick={() => toggleValue(cat.id as keyof WeeklyMobileFilterState, val)}
                                                        className={`kl-pill ${isSel ? 'is-on' : ''}`}
                                                        style={{
                                                            cursor: 'pointer',
                                                            background: isSel ? '#2563EB' : '#F8FAFC',
                                                            color: isSel ? '#ffffff' : '#334155',
                                                            borderColor: isSel ? '#2563EB' : '#E2E8F0',
                                                            fontWeight: isSel ? 700 : 500,
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            padding: '5px 10px',
                                                            borderRadius: '8px',
                                                            fontSize: '12px',
                                                            transition: 'all 0.15s ease'
                                                        }}
                                                    >
                                                        <span>{val}</span>
                                                        <small style={{ opacity: 0.65, marginLeft: '6px', fontWeight: 600 }}>
                                                            {count}
                                                        </small>
                                                    </button>
                                                );
                                            })}

                                            {!isExpanded && hasMany && (
                                                <button
                                                    type="button"
                                                    onClick={() => setExpandedFilterCategories(prev => ({ ...prev, [cat.id]: true }))}
                                                    className="kl-pill"
                                                    style={{
                                                        cursor: 'pointer',
                                                        background: '#EFF6FF',
                                                        color: '#2563EB',
                                                        borderColor: '#BFDBFE',
                                                        fontWeight: 700,
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        padding: '5px 10px',
                                                        borderRadius: '8px',
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    Ver {remainingCount} más
                                                </button>
                                            )}

                                            {isExpanded && hasMany && (
                                                <button
                                                    type="button"
                                                    onClick={() => setExpandedFilterCategories(prev => ({ ...prev, [cat.id]: false }))}
                                                    className="kl-pill"
                                                    style={{
                                                        cursor: 'pointer',
                                                        background: '#F1F5F9',
                                                        color: '#64748B',
                                                        borderColor: '#CBD5E1',
                                                        fontWeight: 600,
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        padding: '5px 10px',
                                                        borderRadius: '8px',
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    Ver menos
                                                </button>
                                            )}

                                            {filteredVals.length === 0 && (
                                                <span style={{ fontSize: '12px', color: '#94A3B8', fontStyle: 'italic' }}>
                                                    Sin coincidencias
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </KlSheet>
                );
            })()}

            {/* Modal Móvil: Bloqueo de Ejecución por Protocolo de Calidad Pendiente */}
            {mobileQualityBlockedModalData && (
                <KlDialog
                    open={!!mobileQualityBlockedModalData}
                    onClose={() => setMobileQualityBlockedModalData(null)}
                    title="Protocolo de Calidad Pendiente"
                    message="No se puede marcar como Ejecutado (Cumplida) porque la partida aplica protocolo de calidad y este no ha sido enviado al siguiente revisor por la persona responsable de generar avance. ¿Deseas abrir el protocolo para revisarlo o enviarlo?"
                    confirmText="Abrir Protocolo"
                    cancelText="Cancelar"
                    onConfirm={() => {
                        const { alloc, task } = mobileQualityBlockedModalData;
                        setMobileQualityBlockedModalData(null);
                        setMobileProtocolTarget({
                            taskId: alloc.taskId,
                            unitId: alloc.unitId,
                            taskName: cleanTaskName(task?.name || (alloc.isAdditional ? (alloc.additionalTaskName || 'Tarea Adicional') : 'Tarea')),
                            taskCode: getTaskGanttRowNumber(task, tasks) ? String(getTaskGanttRowNumber(task, tasks)) : undefined
                        });
                    }}
                />
            )}

            {/* Modal Móvil: Formulario de Tarea No Cumplida (Justificación, CNC y % Cumplimiento) */}
            {mobileUnexecutedModalData && activePlan && (
                <KlSheet
                    open={!!mobileUnexecutedModalData}
                    onClose={() => setMobileUnexecutedModalData(null)}
                    title="Marcar como No Cumplida"
                    subtitle={mobileUnexecutedModalData.taskName}
                    footer={
                        <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
                            <button
                                type="button"
                                onClick={() => setMobileUnexecutedModalData(null)}
                                className="b b-o"
                                style={{ flex: 1 }}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    const { allocIndex, comentario, cncId, pctStr } = mobileUnexecutedModalData;
                                    if (!comentario.trim()) {
                                        alert('Debe ingresar una justificación para declarar la tarea como No Cumplida.');
                                        return;
                                    }
                                    const finalPct = Math.min(99, Math.max(0, roundToTwoDecimals(parseFloat(pctStr) || 0)));
                                    updateMainAllocationField(activePlan.id, allocIndex, 'executed', false);
                                    updateMainAllocationField(activePlan.id, allocIndex, 'percentCumplimiento', finalPct);
                                    updateMainAllocationField(activePlan.id, allocIndex, 'comentario', comentario);
                                    if (isRevisionStatus(activePlan.status) || isClosedStatus(activePlan.status)) {
                                        updateMainAllocationField(activePlan.id, allocIndex, 'cncId', cncId || '');
                                    }
                                    setMobileUnexecutedModalData(null);
                                }}
                                className="b b-r"
                                style={{ flex: 1 }}
                            >
                                Confirmar No Cumplida
                            </button>
                        </div>
                    }
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', padding: '10px 0 20px' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', marginBottom: '6px' }}>
                                Justificación / Comentario (Obligatorio)
                            </label>
                            <BufferedTextArea
                                value={mobileUnexecutedModalData.comentario}
                                onValueChange={(val) => setMobileUnexecutedModalData({ ...mobileUnexecutedModalData, comentario: val })}
                                placeholder="Escribe el motivo del no cumplimiento..."
                                style={{
                                    width: '100%',
                                    height: '80px',
                                    borderRadius: '10px',
                                    border: '1px solid #CBD5E1',
                                    background: '#F8FAFC',
                                    padding: '10px 12px',
                                    fontSize: '13px',
                                    color: '#0F172A',
                                    boxSizing: 'border-box',
                                    outline: 'none',
                                    resize: 'none'
                                }}
                            />
                        </div>

                        {(isRevisionStatus(activePlan.status) || isClosedStatus(activePlan.status)) && (
                            <div>
                                <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', marginBottom: '6px' }}>
                                    Causa de No Cumplimiento (CNC)
                                </label>
                                <select
                                    value={mobileUnexecutedModalData.cncId}
                                    onChange={(e) => setMobileUnexecutedModalData({ ...mobileUnexecutedModalData, cncId: e.target.value })}
                                    style={{
                                        width: '100%',
                                        height: '40px',
                                        borderRadius: '10px',
                                        border: '1px solid #CBD5E1',
                                        background: '#F8FAFC',
                                        padding: '0 10px',
                                        fontSize: '13px',
                                        color: '#0F172A',
                                        boxSizing: 'border-box',
                                        outline: 'none'
                                    }}
                                >
                                    <option value="">Seleccionar Causa...</option>
                                    {cncGroups.map(grp => (
                                        <optgroup key={grp.id} label={grp.name}>
                                            {grp.causes.map(c => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>
                                    % Cumplimiento Logrado (0 a 99%)
                                </label>
                                <span style={{ fontSize: '13px', fontWeight: 700, color: '#2563EB' }}>
                                    {mobileUnexecutedModalData.pctStr || '0'}%
                                </span>
                            </div>

                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={mobileUnexecutedModalData.pctStr}
                                    placeholder="0.00"
                                    onChange={(e) => {
                                        let raw = e.target.value.replace(',', '.').replace(/[^0-9.]/g, '');
                                        // Allow max 1 decimal dot and max 2 decimal digits
                                        const parts = raw.split('.');
                                        if (parts.length > 2) {
                                            raw = parts[0] + '.' + parts.slice(1).join('');
                                        }
                                        if (parts[1] && parts[1].length > 2) {
                                            raw = parts[0] + '.' + parts[1].slice(0, 2);
                                        }
                                        if (raw === '') {
                                            setMobileUnexecutedModalData({
                                                ...mobileUnexecutedModalData,
                                                pctStr: '',
                                                pctCumplimiento: 0
                                            });
                                            return;
                                        }
                                        const num = parseFloat(raw);
                                        if (!isNaN(num) && num > 99) {
                                            setMobileUnexecutedModalData({
                                                ...mobileUnexecutedModalData,
                                                pctStr: '99',
                                                pctCumplimiento: 99
                                            });
                                        } else {
                                            setMobileUnexecutedModalData({
                                                ...mobileUnexecutedModalData,
                                                pctStr: raw,
                                                pctCumplimiento: isNaN(num) ? 0 : roundToTwoDecimals(num)
                                            });
                                        }
                                    }}
                                    onBlur={() => {
                                        const num = Math.min(99, Math.max(0, roundToTwoDecimals(parseFloat(mobileUnexecutedModalData.pctStr) || 0)));
                                        setMobileUnexecutedModalData({
                                            ...mobileUnexecutedModalData,
                                            pctStr: num === 0 && !mobileUnexecutedModalData.pctStr ? '' : String(num),
                                            pctCumplimiento: num
                                        });
                                    }}
                                    style={{
                                        width: '74px',
                                        height: '42px',
                                        borderRadius: '10px',
                                        border: '1px solid #CBD5E1',
                                        background: '#F8FAFC',
                                        padding: '0 10px',
                                        fontSize: '15px',
                                        fontWeight: 700,
                                        color: '#0F172A',
                                        textAlign: 'center',
                                        boxSizing: 'border-box',
                                        outline: 'none'
                                    }}
                                />

                                <input
                                    type="range"
                                    min="0"
                                    max="99"
                                    value={mobileUnexecutedModalData.pctCumplimiento || 0}
                                    onChange={(e) => {
                                        const val = parseInt(e.target.value, 10) || 0;
                                        setMobileUnexecutedModalData({
                                            ...mobileUnexecutedModalData,
                                            pctStr: String(val),
                                            pctCumplimiento: val
                                        });
                                    }}
                                    style={{
                                        flex: 1,
                                        accentColor: '#2563EB',
                                        cursor: 'pointer',
                                        height: '24px'
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </KlSheet>
            )}

            {/* Modal Móvil: Menú ⋮ de Acciones Secundarias para Tarea Semanal */}
            {mobileWeeklyCardActionTarget && (
                <KlMenu
                    open={!!mobileWeeklyCardActionTarget}
                    onClose={() => setMobileWeeklyCardActionTarget(null)}
                    anchorRef={{ current: mobileWeeklyCardActionAnchor }}
                    items={[
                        {
                            id: 'details',
                            label: 'Ver Detalles',
                            iconName: 'info',
                            onClick: () => {
                                const target = mobileWeeklyCardActionTarget;
                                setMobileWeeklyCardActionTarget(null);
                                setMobileWeeklyDetailTarget(target);
                            }
                        },
                        ...((!mobileWeeklyCardActionTarget.alloc.isAdditional && protocolConfigs[mobileWeeklyCardActionTarget.alloc.taskId]?.applies) ? [
                            {
                                id: 'protocol',
                                label: 'Protocolo de Calidad',
                                iconName: 'fact_check',
                                onClick: () => {
                                    const target = mobileWeeklyCardActionTarget;
                                    setMobileWeeklyCardActionTarget(null);
                                    setMobileProtocolTarget({
                                        taskId: target.alloc.taskId,
                                        unitId: target.alloc.unitId,
                                        taskName: cleanTaskName(target.task?.name || (target.alloc.isAdditional ? (target.alloc.additionalTaskName || 'Tarea Adicional') : 'Tarea')),
                                        taskCode: getTaskGanttRowNumber(target.task, tasks) ? String(getTaskGanttRowNumber(target.task, tasks)) : undefined
                                    });
                                }
                            }
                        ] : [])
                    ]}
                />
            )}

            {/* Modal Móvil: Detalle Completo de Tarea Semanal (Punto 3) */}
            {mobileWeeklyDetailTarget && (() => {
                const alloc = mobileWeeklyDetailTarget.alloc;
                const targetIdx = mobileWeeklyDetailTarget.allocIndex ?? activePlan?.allocations?.findIndex(a => a?.taskId === alloc.taskId && a?.unitId === alloc.unitId) ?? 0;
                const task = mobileWeeklyDetailTarget.task || findTaskById(alloc.taskId, tasks);
                const unit = mobileWeeklyDetailTarget.unit || units.find(u => u.id === alloc.unitId);
                const ganttNumber = getTaskGanttRowNumber(task, tasks);
                const levelName = mobileWeeklyDetailTarget.levelName || levels.find(l => l.id === (task as any)?.levelId)?.name || 'General';
                const unitName = unit?.name || alloc.unitId;
                const respName = mobileWeeklyDetailTarget.respName || responsibles.find(r => r.id === alloc.responsibleId)?.name || 'Sin asignar';
                const taskName = cleanTaskName(task?.name || (alloc.isAdditional ? (alloc.additionalTaskName || 'Tarea Adicional') : 'Tarea'));

                const isCumplida = alloc.executed === true;
                const isNoCumplida = alloc.executed === false;
                const isPendiente = alloc.executed === null || alloc.executed === undefined;
                const statusClass = isCumplida ? 'st-done' : isNoCumplida ? 'st-fail' : 'st-pending';
                const displayStatus = isCumplida ? 'Cumplida' : isNoCumplida ? 'No cumplida' : 'Pendiente';

                const config = !alloc.isAdditional ? protocolConfigs[alloc.taskId] : null;
                const appliesProtocol = config?.applies ?? false;
                const isProtocolClosed = !alloc.isAdditional && appliesProtocol ? isProtocolProgressedLocal(alloc.taskId, alloc.unitId) : false;

                const compliancePct = getAllocationCompliancePct(alloc);
                const rawComprometido = (alloc as any).pctComprometido ?? ((alloc as any).comprometido ? ((alloc as any).customPercentComprometido ?? 100) : (alloc.percentComprometido ?? 0));
                const pctComprometido = Number(Number(rawComprometido).toFixed(2));
                const accumulatedCompliance = !alloc.isAdditional
                    ? getTaskUnitAccumulatedCompliance(alloc.taskId, alloc.unitId, tasks, weeklyPlans, protocolConfigs, savedProtocols, undefined, activePlan?.id)
                    : 0;

                const cncName = alloc.cncId ? (cncGroups.flatMap(g => g.causes).find(c => c.id === alloc.cncId)?.name || alloc.cncId) : 'Sin causa especificada';

                const canEditWeekly = (activePlan?.status === 'En Ejecución' || activePlan?.status === 'En Revisión' || activePlan?.status === 'En Planificación') &&
                    (currentUser?.role === 'admin' || currentUser?.role === 'manager' || currentUser?.globalRole === 'Administrador' || hasPermission('ps_gantt_avance', 'edit') || hasPermission('movil_ps_gantt_avance', 'edit') || responsibles.find(r => r.id === alloc.responsibleId)?.email === currentUser?.email);

                const itemKey = `${alloc.taskId}_${alloc.unitId}`;

                return (
                    <KlSheet
                        open={!!mobileWeeklyDetailTarget}
                        onClose={() => setMobileWeeklyDetailTarget(null)}
                        title={taskName}
                        subtitle={`${ganttNumber ? `#${ganttNumber} · ` : ''}${unitName} · ${levelName}`}
                        footer={
                            <div style={{ display: 'flex', gap: '10px', width: '100%', alignItems: 'center' }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (appliesProtocol) {
                                            setMobileWeeklyDetailTarget(null);
                                            setMobileProtocolTarget({
                                                taskId: alloc.taskId,
                                                unitId: alloc.unitId,
                                                taskName: taskName,
                                                taskCode: ganttNumber ? String(ganttNumber) : undefined
                                            });
                                        } else {
                                            alert('Esta partida no tiene protocolo de calidad configurado.');
                                        }
                                    }}
                                    className="b b-o"
                                    style={{
                                        width: '100%',
                                        flex: 1,
                                        background: '#F1F5F9',
                                        color: appliesProtocol ? '#0F172A' : '#94A3B8',
                                        borderColor: '#CBD5E1',
                                        fontWeight: 600,
                                        height: '42px',
                                        borderRadius: '10px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '6px'
                                    }}
                                >
                                    <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>
                                        {isProtocolClosed ? 'verified' : 'fact_check'}
                                    </span>
                                    <span>Ver protocolo</span>
                                </button>
                            </div>
                        }
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', padding: '6px 0 16px' }}>
                            {/* Bajo la cabecera: la insignia de estado en color (tocable) y "Cumplimiento N%" */}
                            <div className={statusClass} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '4px' }}>
                                <div style={{ position: 'relative' }}>
                                    <div
                                        className="kl-st-badge"
                                        onClick={(e) => {
                                            if (canEditWeekly) {
                                                setMobileActiveWeeklyMenuKey(`detail_${itemKey}`);
                                                setMobileActiveWeeklyMenuAnchor(e.currentTarget);
                                            } else {
                                                alert('No tienes permisos para modificar el estado o la planificación semanal está cerrada.');
                                            }
                                        }}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <span>{displayStatus}</span>
                                        <svg viewBox="0 0 24 24" style={{ width: '12px', height: '12px', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5 }}>
                                            <path d="M6 9l6 6 6-6" />
                                        </svg>
                                    </div>

                                    <KlMenu
                                        open={mobileActiveWeeklyMenuKey === `detail_${itemKey}`}
                                        onClose={() => setMobileActiveWeeklyMenuKey(null)}
                                        anchorRef={{ current: mobileActiveWeeklyMenuAnchor }}
                                        items={[
                                            {
                                                id: 'pendiente',
                                                label: 'Pendiente',
                                                iconName: 'schedule',
                                                iconColor: '#D97706',
                                                onClick: () => {
                                                    setMobileActiveWeeklyMenuKey(null);
                                                    updateMainAllocationField(activePlan.id, targetIdx, 'executed', null);
                                                    updateMainAllocationField(activePlan.id, targetIdx, 'percentCumplimiento', 0);
                                                    setMobileWeeklyDetailTarget((prev: any) => prev ? {
                                                        ...prev,
                                                        alloc: { ...prev.alloc, executed: null, percentCumplimiento: 0 }
                                                    } : null);
                                                }
                                            },
                                            {
                                                id: 'cumplida',
                                                label: 'Cumplida (Ejecutado)',
                                                iconName: 'check_circle',
                                                iconColor: '#16A34A',
                                                onClick: () => {
                                                    setMobileActiveWeeklyMenuKey(null);
                                                    if (appliesProtocol && (accumulatedCompliance + pctComprometido >= 99.9) && !isProtocolClosed) {
                                                        setMobileWeeklyDetailTarget(null);
                                                        setMobileQualityBlockedModalData({ alloc, task: task! });
                                                    } else {
                                                        updateMainAllocationField(activePlan.id, targetIdx, 'executed', true);
                                                        updateMainAllocationField(activePlan.id, targetIdx, 'percentCumplimiento', 100);
                                                        setMobileWeeklyDetailTarget((prev: any) => prev ? {
                                                            ...prev,
                                                            alloc: { ...prev.alloc, executed: true, percentCumplimiento: 100 }
                                                        } : null);
                                                    }
                                                }
                                            },
                                            {
                                                id: 'no_cumplida',
                                                label: 'No cumplida (No Ejecutado)',
                                                iconName: 'cancel',
                                                iconColor: '#DC2626',
                                                onClick: () => {
                                                    setMobileActiveWeeklyMenuKey(null);
                                                    setMobileWeeklyDetailTarget(null);
                                                    setMobileUnexecutedModalData({
                                                        allocIndex: targetIdx,
                                                        alloc,
                                                        cncId: alloc.cncId || '',
                                                        comentario: alloc.comentario || '',
                                                        pctCumplimiento: alloc.percentCumplimiento ?? 0,
                                                        pctStr: String(alloc.percentCumplimiento ?? 0),
                                                        taskName: cleanTaskName(task?.name || alloc.taskId)
                                                    });
                                                }
                                            }
                                        ]}
                                    />
                                </div>

                                <div style={{ fontSize: '13px', fontWeight: 700, color: '#2563EB', background: '#EFF6FF', padding: '4px 10px', borderRadius: '8px', border: '1px solid #BFDBFE' }}>
                                    Cumplimiento {compliancePct}%
                                </div>
                            </div>

                            {/* Datos en 2 columnas: Nivel, Unidad física, Responsable y Protocolo */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '12px 14px',
                                background: '#F8FAFC',
                                padding: '12px 14px',
                                borderRadius: '12px',
                                border: '1px solid #E2E8F0'
                            }}>
                                <div>
                                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.03em', display: 'block', marginBottom: '2px' }}>
                                        Nivel
                                    </span>
                                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#0F172A', wordBreak: 'break-word' }}>
                                        {levelName}
                                    </div>
                                </div>
                                <div>
                                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.03em', display: 'block', marginBottom: '2px' }}>
                                        Unidad Física
                                    </span>
                                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#0F172A', wordBreak: 'break-word' }}>
                                        {unitName}
                                    </div>
                                </div>
                                <div>
                                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.03em', display: 'block', marginBottom: '2px' }}>
                                        Responsable
                                    </span>
                                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#0F172A', wordBreak: 'break-word' }}>
                                        {respName}
                                    </div>
                                </div>
                                <div>
                                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.03em', display: 'block', marginBottom: '2px' }}>
                                        Protocolo
                                    </span>
                                    <div>
                                        {!appliesProtocol ? (
                                            <span style={{
                                                fontSize: '11px',
                                                fontWeight: 600,
                                                color: '#64748B',
                                                background: '#F1F5F9',
                                                border: '1px solid #E2E8F0',
                                                padding: '2px 8px',
                                                borderRadius: '6px',
                                                display: 'inline-block'
                                            }}>
                                                No aplica
                                            </span>
                                        ) : isProtocolClosed ? (
                                            <span style={{
                                                fontSize: '11px',
                                                fontWeight: 700,
                                                color: '#16A34A',
                                                background: '#DCFCE7',
                                                border: '1px solid #BBF7D0',
                                                padding: '2px 8px',
                                                borderRadius: '6px',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}>
                                                <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>verified</span>
                                                Cerrado
                                            </span>
                                        ) : (
                                            <span style={{
                                                fontSize: '11px',
                                                fontWeight: 700,
                                                color: '#D97706',
                                                background: '#FEF3C7',
                                                border: '1px solid #FDE68A',
                                                padding: '2px 8px',
                                                borderRadius: '6px',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}>
                                                <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>pending</span>
                                                Abierto
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Justificación y causa de no cumplimiento */}
                            {isNoCumplida ? (
                                <div style={{
                                    background: '#FEF2F2',
                                    border: '1px solid #FECACA',
                                    borderRadius: '10px',
                                    padding: '10px 12px'
                                }}>
                                    <span style={{
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        color: '#DC2626',
                                        textTransform: 'uppercase',
                                        letterSpacing: '.03em',
                                        display: 'block',
                                        marginBottom: '4px'
                                    }}>
                                        {(isRevisionStatus(activePlan?.status) || isClosedStatus(activePlan?.status)) && alloc.cncId
                                            ? `CAUSA DE NO CUMPLIMIENTO · ${cncName}`
                                            : 'JUSTIFICACIÓN DE NO CUMPLIMIENTO'}
                                    </span>
                                    {alloc.comentario ? (
                                        <div style={{ fontSize: '12px', color: '#991B1B', lineHeight: '1.4' }}>
                                            {alloc.comentario}
                                        </div>
                                    ) : (
                                        <div style={{ fontSize: '12px', color: '#DC2626', fontStyle: 'italic' }}>
                                            Sin justificación detallada
                                        </div>
                                    )}
                                </div>
                            ) : (alloc.comentario ? (
                                <div style={{
                                    background: '#F8FAFC',
                                    border: '1px solid #E2E8F0',
                                    borderRadius: '10px',
                                    padding: '10px 12px'
                                }}>
                                    <span style={{
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        color: '#64748B',
                                        textTransform: 'uppercase',
                                        letterSpacing: '.03em',
                                        display: 'block',
                                        marginBottom: '2px'
                                    }}>
                                        Justificación anterior
                                    </span>
                                    <div style={{ fontSize: '12px', color: '#64748B', fontStyle: 'italic', lineHeight: '1.4' }}>
                                        {alloc.comentario}
                                    </div>
                                </div>
                            ) : null)}
                        </div>
                    </KlSheet>
                );
            })()}

            {/* Modal: Asignación Masiva de Responsable (Gantt Auto-Plan) */}
            {ganttAutoPlanModalOpen && (() => {
                return (
                    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
                        <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-5xl shadow-2xl overflow-hidden animate-fade-in-up border border-slate-200 dark:border-slate-800 flex flex-col max-h-[90vh]">
                            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800">
                                <div className="flex items-center gap-2">
                                    <span className="material-symbols-outlined text-blue-600 dark:text-blue-400">group</span>
                                    <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider">
                                        Planificación Masiva Sugerida
                                    </h3>
                                </div>
                                <button onClick={() => setGanttAutoPlanModalOpen(false)} className="text-slate-400 hover:text-slate-655 dark:hover:text-white transition-colors">
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>

                            <div className="p-5 space-y-4 overflow-y-auto flex-1">
                                <div className="space-y-2">
                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider">
                                        Propuesta de Asignación por Partida:
                                    </p>
                                    <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-x-auto bg-slate-50 dark:bg-slate-950/50">
                                        <table className="w-full text-left border-collapse min-w-[760px]">
                                            <thead>
                                                <tr className="bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                                                    <th className="px-4 py-2.5 text-[11px] font-bold text-slate-500 dark:text-slate-450 uppercase">Tarea / Subtarea</th>
                                                    <th className="px-4 py-2.5 text-[11px] font-bold text-slate-500 dark:text-slate-450 uppercase">Responsable(s)</th>
                                                    <th className="px-4 py-2.5 text-[11px] font-bold text-slate-500 dark:text-slate-450 uppercase text-center w-28">Cant. / Unidades</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                                                {ganttSuggestionsData.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={3} className="px-4 py-8 text-xs text-slate-400 italic text-center">
                                                            No hay unidades disponibles al 0% de avance para programar.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    ganttSuggestionsData.map((s, taskIdx) => {
                                                        // Calculate unique responsibles list for selected units
                                                        const activeUnits = s.scheduledUnits.filter((u: any) => u.selected);
                                                        const uniqueRespIds = Array.from(new Set(activeUnits.map((u: any) => u.responsibleId)));
                                                        const respNames = uniqueRespIds.map(id => responsibles.find(r => r.id === id)?.name || 'Sin Asignar').filter(Boolean);

                                                        let responsibleText = '-';
                                                        if (respNames.length > 0) {
                                                            if (respNames.length === 1) {
                                                                responsibleText = respNames[0];
                                                            } else {
                                                                responsibleText = `${respNames[0]} +${respNames.length - 1}`;
                                                            }
                                                        }

                                                        const isExpanded = expandedTaskIdx === taskIdx;

                                                        return (
                                                            <React.Fragment key={taskIdx}>
                                                                <tr
                                                                    onClick={() => s.selected && setExpandedTaskIdx(isExpanded ? null : taskIdx)}
                                                                    className={`hover:bg-slate-100 dark:hover:bg-slate-800/80 cursor-pointer transition-colors ${!s.selected ? 'opacity-55 bg-slate-100/30' : ''}`}
                                                                >
                                                                    <td className="px-4 py-3 text-xs">
                                                                        <div className="flex items-center gap-2">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={s.selected}
                                                                                onChange={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleToggleTaskSelection(taskIdx);
                                                                                }}
                                                                                className="rounded border-slate-350 text-primary focus:ring-primary size-4 cursor-pointer mr-2"
                                                                            />
                                                                            <span className={`material-symbols-outlined text-[16px] text-slate-455 transition-transform ${isExpanded ? 'rotate-90' : ''} ${!s.selected ? 'pointer-events-none opacity-50' : ''}`}>
                                                                                chevron_right
                                                                            </span>
                                                                            <span className="font-semibold text-slate-800 dark:text-slate-200 block truncate max-w-[320px]" title={s.taskName}>
                                                                                {s.taskName}
                                                                            </span>
                                                                            {s.isRezagada && (
                                                                                <span className="text-[9px] bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-400 px-1.5 py-0.5 rounded font-black tracking-wider uppercase">Rezagada</span>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-4 py-3 text-xs text-slate-600 dark:text-slate-400 font-medium">
                                                                        {responsibleText}
                                                                    </td>
                                                                    <td className="px-4 py-3 text-xs font-bold text-center text-slate-700 dark:text-slate-350">
                                                                        {s.unitsToSuggest}
                                                                    </td>
                                                                </tr>
                                                                {isExpanded && s.selected && (
                                                                    <tr>
                                                                        <td colSpan={3} className="bg-slate-100/60 dark:bg-slate-900/40 px-4 py-3.5 border-t border-b border-slate-200 dark:border-slate-800">
                                                                            <div className="space-y-2 border-l-2 border-primary/50 pl-3 py-1">
                                                                                <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Desglose de Unidades y Responsables:</p>
                                                                                <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                                                                                    <table className="w-full text-left border-collapse min-w-[700px]">
                                                                                        <thead>
                                                                                            <tr className="bg-slate-50 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700 text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase">
                                                                                                <th className="px-3 py-2 w-48">Unidad Física</th>
                                                                                                <th className="px-3 py-2 w-48">Responsable</th>
                                                                                                <th className="px-2 py-2 text-center w-24">% Cumplimiento</th>
                                                                                                <th className="px-2 py-2 text-center w-28">% Comprometido</th>
                                                                                                <th className="px-3 py-2 text-center w-48">Días de Ejecución</th>
                                                                                            </tr>
                                                                                        </thead>
                                                                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60 text-xs">
                                                                                            {s.scheduledUnits.map((u: any, unitIdx: number) => {
                                                                                                const maxBalance = Math.max(0, 100 - (u.accumulatedCompliance ?? 0));
                                                                                                return (
                                                                                                    <tr key={unitIdx} className={`hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors ${!u.selected ? 'opacity-40 bg-slate-50/50' : ''}`}>
                                                                                                        {/* Unidad */}
                                                                                                        <td className="px-3 py-2">
                                                                                                            <div className="flex items-center gap-2">
                                                                                                                <input
                                                                                                                    type="checkbox"
                                                                                                                    checked={u.selected}
                                                                                                                    onChange={() => handleToggleUnitSelection(taskIdx, unitIdx)}
                                                                                                                    className="rounded border-slate-300 text-primary focus:ring-primary size-3.5 cursor-pointer"
                                                                                                                />
                                                                                                                <span className={`font-semibold text-slate-700 dark:text-slate-200 ${!u.selected ? 'line-through text-slate-400 dark:text-slate-500' : ''}`}>
                                                                                                                    {u.unitName} <span className="text-[11px] font-normal text-slate-400 dark:text-slate-500">({u.levelName})</span>
                                                                                                                </span>
                                                                                                            </div>
                                                                                                        </td>

                                                                                                        {/* Responsable */}
                                                                                                        <td className="px-3 py-2">
                                                                                                            <div className="flex items-center gap-1.5">
                                                                                                                {u.hasInheritedResp && (
                                                                                                                    <span className="text-[9px] bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 px-1 py-0.5 rounded font-bold" title="Heredado de Revisor 1 en Calidad">CC</span>
                                                                                                                )}
                                                                                                                <select
                                                                                                                    value={u.responsibleId}
                                                                                                                    onChange={(e) => handleIndividualRespChange(taskIdx, unitIdx, e.target.value)}
                                                                                                                    disabled={!u.selected}
                                                                                                                    className="w-full text-[11px] p-1.5 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 outline-none font-medium focus:ring-1 focus:ring-primary/50 disabled:bg-slate-100 dark:disabled:bg-slate-900 disabled:cursor-not-allowed"
                                                                                                                >
                                                                                                                    <option value="">Seleccionar...</option>
                                                                                                                    {sortedResponsibles.map(r => (
                                                                                                                        <option key={r.id} value={r.id}>{r.name} ({r.companyName || 'Sin Asignar'})</option>
                                                                                                                    ))}
                                                                                                                </select>
                                                                                                            </div>
                                                                                                        </td>

                                                                                                        {/* % Cumplimiento (Acumulado) */}
                                                                                                        <td className="px-2 py-2 text-center">
                                                                                                            <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold ${(u.accumulatedCompliance ?? 0) >= 100
                                                                                                                    ? 'bg-green-100 text-green-700 dark:bg-green-950/50 dark:text-green-400'
                                                                                                                    : (u.accumulatedCompliance ?? 0) > 0
                                                                                                                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400'
                                                                                                                        : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
                                                                                                                }`}>
                                                                                                                {u.accumulatedCompliance ?? 0}%
                                                                                                            </span>
                                                                                                        </td>

                                                                                                        {/* % Comprometido (Editable con tope de saldo restante) */}
                                                                                                        <td className="px-2 py-2 text-center">
                                                                                                            <div className="flex flex-col items-center justify-center">
                                                                                                                <div className="relative flex items-center">
                                                                                                                    <input
                                                                                                                        type="number"
                                                                                                                        min="0"
                                                                                                                        max={maxBalance}
                                                                                                                        step="0.01"
                                                                                                                        value={roundToTwoDecimals(u.percentComprometido ?? maxBalance)}
                                                                                                                        disabled={!u.selected}
                                                                                                                        onChange={(e) => handleUnitPercentComprometidoChange(taskIdx, unitIdx, roundToTwoDecimals(parseFloat(e.target.value) || 0))}
                                                                                                                        className="w-16 text-center text-xs p-1 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 outline-none font-bold focus:ring-1 focus:ring-primary/50 disabled:bg-slate-100 dark:disabled:bg-slate-900 disabled:cursor-not-allowed"
                                                                                                                    />
                                                                                                                    <span className="text-[11px] font-bold text-slate-400 ml-1">%</span>
                                                                                                                </div>
                                                                                                                <span className="text-[9px] text-slate-400 mt-0.5 font-medium">
                                                                                                                    máx {maxBalance}%
                                                                                                                </span>
                                                                                                            </div>
                                                                                                        </td>

                                                                                                        {/* Días seleccionables de la semana */}
                                                                                                        <td className="px-3 py-2 text-center">
                                                                                                            <div className="flex items-center justify-center gap-1">
                                                                                                                {dynamicDayColumns.map(col => {
                                                                                                                    const dayKey = col.dayLetter;
                                                                                                                    const isChecked = !!(u.days && u.days[dayKey]);
                                                                                                                    return (
                                                                                                                        <button
                                                                                                                            key={col.key}
                                                                                                                            type="button"
                                                                                                                            disabled={!u.selected}
                                                                                                                            onClick={() => handleToggleUnitDay(taskIdx, unitIdx, dayKey)}
                                                                                                                            className={`flex flex-col items-center justify-center w-7 h-8 rounded text-[10px] font-black transition-all ${!u.selected
                                                                                                                                    ? 'opacity-30 cursor-not-allowed bg-slate-100 dark:bg-slate-800 text-slate-400'
                                                                                                                                    : isChecked
                                                                                                                                        ? 'bg-blue-600 text-white shadow-sm shadow-blue-500/30'
                                                                                                                                        : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:border-blue-400'
                                                                                                                                }`}
                                                                                                                            title={`${col.dayLetter} (${col.dateNum})`}
                                                                                                                        >
                                                                                                                            <span className="leading-none">{col.dayLetter}</span>
                                                                                                                            <span className="text-[8px] font-medium leading-none opacity-80 mt-0.5">{col.dateNum}</span>
                                                                                                                        </button>
                                                                                                                    );
                                                                                                                })}
                                                                                                            </div>
                                                                                                        </td>
                                                                                                    </tr>
                                                                                                );
                                                                                            })}
                                                                                        </tbody>
                                                                                    </table>
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </React.Fragment>
                                                        );
                                                    })
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            <div className="p-4 bg-slate-50 dark:bg-slate-855/50 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-3">
                                <button
                                    onClick={() => setGanttAutoPlanModalOpen(false)}
                                    className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={() => {
                                        handleAutoPlanGanttTasks();
                                        setGanttAutoPlanModalOpen(false);
                                    }}
                                    disabled={ganttSuggestionsData.length === 0}
                                    className="px-5 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-xl text-xs font-bold shadow-md shadow-blue-500/20 active:scale-95 transition-all disabled:opacity-50 disabled:pointer-events-none"
                                >
                                    Auto-Programar
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Modal: Editar Asignación Semanal Individual (Icono de lápiz) */}
            {isEditingAllocation && editingAllocationData && (() => {
                const taskObjForModal = findTaskById(editingAllocationData.taskId, tasks);
                const parentTaskForModal = findParentTask(editingAllocationData.taskId, tasks);
                const targetTaskIdForModal = (taskObjForModal?.isWeeklySubtask && parentTaskForModal) ? parentTaskForModal.id : editingAllocationData.taskId;
                const appliesProtocolForModal = protocolConfigs[targetTaskIdForModal]?.applies ?? false;

                let accumulatedComplianceForModal = 0;
                weeklyPlans
                    .filter(p => p.status === 'Cerrada')
                    .flatMap(p => p.allocations)
                    .forEach(a => {
                        if (a.taskId === editingAllocationData.taskId && a.unitId === editingAllocationData.unitId) {
                            accumulatedComplianceForModal += getAllocationCompliancePct(a);
                        }
                    });
                accumulatedComplianceForModal = Math.min(100, accumulatedComplianceForModal);

                if (appliesProtocolForModal && isProtocolProgressedLocal(targetTaskIdForModal, editingAllocationData.unitId)) {
                    accumulatedComplianceForModal = 100;
                }

                const balanceForModal = Math.max(0, 100 - accumulatedComplianceForModal);

                return (
                    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
                        <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden animate-fade-in-up border border-slate-200 dark:border-slate-800">
                            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800">
                                <div className="flex items-center gap-2">
                                    <span className="material-symbols-outlined text-purple-600 dark:text-purple-400">edit</span>
                                    <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider">
                                        Editar Programación
                                    </h3>
                                </div>
                                <button onClick={() => { setIsEditingAllocation(false); setEditingAllocationData(null); }} className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors">
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>

                            <div className="p-5 space-y-4">
                                <div>
                                    <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Partida / Tarea</h4>
                                    <p className="text-xs font-bold text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-850 p-2.5 rounded-xl border border-slate-200 dark:border-slate-800">
                                        {findTaskName(editingAllocationData.taskId, tasks) || '[Partida Obsoleta/Fuera de Gantt]'}
                                    </p>
                                </div>
                                <div>
                                    <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Unidad Física</h4>
                                    <p className="text-xs font-bold text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-850 p-2.5 rounded-xl border border-slate-200 dark:border-slate-800">
                                        {units.find(u => u.id === editingAllocationData.unitId)?.name || 'N/A'}
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Responsable</label>
                                    <select
                                        value={editingAllocationData.responsibleId}
                                        onChange={(e) => setEditingAllocationData(prev => prev ? { ...prev, responsibleId: e.target.value } : null)}
                                        className="w-full text-xs p-2.5 border border-slate-300 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none"
                                    >
                                        <option value="">-- Sin asignar --</option>
                                        {sortedResponsibles.map(r => (
                                            <option key={r.id} value={r.id}>
                                                {r.name} ({r.companyName || 'Sin Asignar'})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5 ml-1">% Comprometido</label>
                                    <input
                                        type="number"
                                        min="0"
                                        max={balanceForModal}
                                        step="0.01"
                                        value={roundToTwoDecimals(editingAllocationData.percentComprometido ?? 100)}
                                        disabled={activePlan?.status === 'Cerrada'}
                                        onChange={(e) => {
                                            const val = roundToTwoDecimals(Math.min(balanceForModal, Math.max(0, parseFloat(e.target.value) || 0)));
                                            setEditingAllocationData(prev => prev ? { ...prev, percentComprometido: val } : null);
                                        }}
                                        className="w-full text-xs p-2.5 border border-slate-300 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none font-bold"
                                    />
                                    <p className="text-[10px] text-slate-400 mt-1 ml-1">
                                        Saldo restante: {balanceForModal}% (Acumulado anterior: {accumulatedComplianceForModal}%)
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Días de Ejecución</label>
                                    <div className="flex justify-between bg-slate-50 dark:bg-slate-800 p-2.5 rounded-xl border border-slate-200 dark:border-slate-700">
                                        {dynamicDayColumns.map(col => {
                                            const dayKey = col.dayLetter;
                                            const isChecked = !!editingAllocationData.days[dayKey as keyof typeof editingAllocationData.days];
                                            return (
                                                <button
                                                    key={col.key}
                                                    type="button"
                                                    onClick={() => {
                                                        setEditingAllocationData(prev => {
                                                            if (!prev) return null;
                                                            return {
                                                                ...prev,
                                                                days: { ...prev.days, [dayKey]: !prev.days[dayKey as keyof typeof prev.days] }
                                                            };
                                                        });
                                                    }}
                                                    className={`flex flex-col items-center justify-center w-8 h-9 rounded-lg text-xs font-black transition-all ${isChecked
                                                            ? 'bg-purple-600 text-white shadow-md shadow-purple-500/20 scale-105'
                                                            : 'bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-600 hover:border-purple-500'
                                                        }`}
                                                    title={col.fullDate || col.dayLetter}
                                                >
                                                    <span className="text-[11px] leading-none">{col.dayLetter}</span>
                                                    <span className="text-[9px] font-medium leading-none opacity-80 mt-0.5">{col.dateNum}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            <div className="p-4 bg-slate-50 dark:bg-slate-850/50 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-3">
                                <button
                                    onClick={() => { setIsEditingAllocation(false); setEditingAllocationData(null); }}
                                    className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={() => {
                                        if (!editingAllocationData.responsibleId) {
                                            alert("Debe seleccionar un responsable.");
                                            return;
                                        }
                                        if (!Object.values(editingAllocationData.days).some(d => d)) {
                                            alert("Debe seleccionar al menos un día de ejecución.");
                                            return;
                                        }

                                        const nowIso = new Date().toISOString();
                                        const userName = currentUser?.name || currentUser?.email || 'Usuario';

                                        if (draftWeeklyPlan && draftWeeklyPlan.id === activePlanId) {
                                            setDraftWeeklyPlan(prev => {
                                                if (!prev) return prev;
                                                const newAllocations = [...prev.allocations];
                                                newAllocations[editingAllocationIndex] = {
                                                    ...newAllocations[editingAllocationIndex],
                                                    responsibleId: editingAllocationData.responsibleId,
                                                    days: editingAllocationData.days,
                                                    percentComprometido: editingAllocationData.percentComprometido,
                                                    updatedAt: nowIso,
                                                    updatedBy: userName
                                                };
                                                return { ...prev, allocations: newAllocations };
                                            });
                                        }

                                        setWeeklyPlans(prevPlans => prevPlans.map(plan => {
                                            if (plan.id === activePlanId) {
                                                const newAllocations = [...plan.allocations];
                                                newAllocations[editingAllocationIndex] = {
                                                    ...newAllocations[editingAllocationIndex],
                                                    responsibleId: editingAllocationData.responsibleId,
                                                    days: editingAllocationData.days,
                                                    percentComprometido: editingAllocationData.percentComprometido,
                                                    updatedAt: nowIso,
                                                    updatedBy: userName
                                                };
                                                return { ...plan, allocations: newAllocations };
                                            }
                                            return plan;
                                        }));

                                        setIsEditingAllocation(false);
                                        setEditingAllocationData(null);
                                    }}
                                    className="px-5 py-2 text-white bg-purple-600 hover:bg-purple-700 rounded-xl text-xs font-bold shadow-md shadow-purple-500/20 active:scale-95 transition-all"
                                >
                                    Guardar
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
            {/* Modal: Tareas adicionales (Batch Table) */}
            {additionalModalOpen && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-2 sm:p-4 animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-[#1a2634] rounded-2xl w-full max-w-[96vw] xl:max-w-7xl shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-800 flex flex-col max-h-[92vh] animate-in zoom-in-95 duration-200">
                        {/* Modal Header */}
                        <div className="p-4 px-6 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-900/80">
                            <div className="flex items-center gap-3">
                                <div className="size-9 rounded-xl bg-purple-100 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 flex items-center justify-center shadow-inner">
                                    <span className="material-symbols-outlined text-[22px]">playlist_add</span>
                                </div>
                                <div>
                                    <h3 className="text-base font-black text-slate-800 dark:text-white uppercase tracking-wide">
                                        {editingAdditionalIndex !== null ? 'Editar Tarea Adicional' : 'Tareas Adicionales de la Semana'}
                                    </h3>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                        Agregue una o varias partidas adicionales fuera de la Carta Gantt para la programación semanal.
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                {editingAdditionalIndex === null && (
                                    <button
                                        type="button"
                                        onClick={handleAddAdditionalRow}
                                        className="bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs px-3.5 py-2 rounded-xl shadow-md shadow-purple-500/20 flex items-center gap-1.5 transition-all active:scale-95"
                                    >
                                        <span className="material-symbols-outlined text-[18px]">add</span>
                                        <span>+ Agregar Tarea</span>
                                    </button>
                                )}
                                <button
                                    onClick={() => {
                                        setAdditionalModalOpen(false);
                                        setSelectedAdditionalRowIds(new Set());
                                        setAdditionalBulkResponsibleId('');
                                        setActiveLevelPickerRowId(null);
                                        setActiveUnitPickerRowId(null);
                                    }}
                                    className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors p-1 rounded-lg"
                                >
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>
                        </div>

                        {/* Modal Subheader / Bulk Toolbar */}
                        {editingAdditionalIndex === null && (
                            <div className="px-6 py-3 bg-slate-50 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between flex-wrap gap-4 text-xs">
                                <div className="flex items-center gap-3">
                                    <div className="size-7 rounded-lg bg-purple-100 dark:bg-purple-950/60 text-purple-600 dark:text-purple-400 flex items-center justify-center shadow-sm">
                                        <span className="material-symbols-outlined text-[17px]">assignment_ind</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <label className="font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider text-[11px]">
                                            Asignar Responsable en Lote:
                                        </label>
                                        <select
                                            value={additionalBulkResponsibleId}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setAdditionalBulkResponsibleId(val);
                                                if (val) {
                                                    handleApplyAdditionalBulkResponsible(val);
                                                }
                                            }}
                                            className="text-xs py-1 px-2.5 border border-purple-200 dark:border-purple-800/60 rounded-lg bg-white dark:bg-slate-800 font-bold text-purple-700 dark:text-purple-300 outline-none focus:ring-2 focus:ring-purple-500/40 shadow-sm"
                                        >
                                            <option value="">Seleccionar Responsable...</option>
                                            {sortedResponsibles.map(r => (
                                                <option key={r.id} value={r.id}>{r.name} ({r.companyName || 'Sin Asignar'})</option>
                                            ))}
                                        </select>
                                    </div>
                                    {selectedAdditionalRowIds.size > 0 ? (
                                        <span className="text-[11px] text-purple-600 dark:text-purple-400 font-bold bg-purple-50 dark:bg-purple-950/50 px-2 py-0.5 rounded-md border border-purple-200/60 dark:border-purple-800/40">
                                            Se aplicará a las {selectedAdditionalRowIds.size} filas seleccionadas
                                        </span>
                                    ) : (
                                        <span className="text-[11px] text-slate-400 dark:text-slate-500 italic">
                                            (Aplica a todas las filas si no hay ninguna seleccionada)
                                        </span>
                                    )}
                                </div>

                                <div className="flex items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400">
                                    <span className="inline-flex items-center gap-1 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded border border-amber-200/60 dark:border-amber-800/50 font-medium">
                                        <span className="material-symbols-outlined text-[14px]">info</span>
                                        Selecciona múltiples niveles o unidades para desplegar tareas en masa
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Modal Body: Table of Additional Tasks */}
                        <div className="p-4 md:p-6 overflow-y-auto overflow-x-auto flex-1 space-y-4">
                            <table className="w-full text-left border-collapse min-w-[1100px]">
                                <thead>
                                    <tr className="border-b border-slate-200 dark:border-slate-800 text-[10px] font-black text-slate-400 uppercase tracking-widest bg-slate-50/50 dark:bg-slate-900/30">
                                        {editingAdditionalIndex === null && (
                                            <th className="py-2.5 px-3 text-center w-10">
                                                <input
                                                    type="checkbox"
                                                    checked={additionalTaskRows.length > 0 && selectedAdditionalRowIds.size === additionalTaskRows.length}
                                                    onChange={handleToggleSelectAllAdditionalRows}
                                                    className="rounded border-slate-300 text-purple-600 focus:ring-purple-500 size-4 cursor-pointer"
                                                    title={selectedAdditionalRowIds.size === additionalTaskRows.length ? "Desmarcar todas" : "Seleccionar todas"}
                                                />
                                            </th>
                                        )}
                                        <th className="py-2.5 px-2 text-center w-10">#</th>
                                        <th className="py-2.5 px-3 min-w-[220px] w-[240px]">Nivel</th>
                                        <th className="py-2.5 px-3 min-w-[220px] w-[240px]">Unidad Física (Sitio)</th>
                                        <th className="py-2.5 px-3 min-w-[240px]">Tarea (Nombre)</th>
                                        <th className="py-2.5 px-3 min-w-[210px] w-[230px]">Responsable</th>
                                        <th className="py-2.5 px-3 text-center min-w-[270px] w-[280px]">Días de Ejecución</th>
                                        {editingAdditionalIndex === null && <th className="py-2.5 px-3 text-center w-12">Acción</th>}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                    {additionalTaskRows.map((row, idx) => {
                                        const availableUnits = (units || []).filter(u => u && u.levelId === row?.levelId);
                                        const isRowChecked = selectedAdditionalRowIds.has(row.id);

                                        return (
                                            <AdditionalTaskRowItem
                                                key={row.id}
                                                row={row}
                                                idx={idx}
                                                isEditing={editingAdditionalIndex !== null}
                                                isRowChecked={isRowChecked}
                                                levels={levels}
                                                availableUnits={availableUnits}
                                                sortedResponsibles={sortedResponsibles}
                                                dynamicDayColumns={dynamicDayColumns}
                                                canDelete={additionalTaskRows.length > 1}
                                                isLevelPickerOpen={activeLevelPickerRowId === row.id}
                                                isUnitPickerOpen={activeUnitPickerRowId === row.id}
                                                onToggleSelectRow={handleToggleSelectAdditionalRow}
                                                onToggleLevelPicker={handleToggleLevelPicker}
                                                onToggleUnitPicker={handleToggleUnitPicker}
                                                onUpdateRow={handleUpdateAdditionalRow}
                                                onToggleDay={handleToggleAdditionalRowDay}
                                                onRemoveRow={handleRemoveAdditionalRow}
                                                onDeployMultiLevel={handleDeployMultiLevelRow}
                                                onDeployMultiUnit={handleDeployMultiUnitRow}
                                                onSelectLevelsBatch={handleSelectLevelsBatch}
                                                onSelectUnitsBatch={handleSelectUnitsBatch}
                                            />
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Modal Footer */}
                        <div className="p-4 px-6 bg-slate-50 dark:bg-slate-900/80 border-t border-slate-200 dark:border-slate-800 flex justify-between items-center">
                            <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                                {additionalTaskRows.length} {additionalTaskRows.length === 1 ? 'tarea configurada' : 'tareas configuradas'}
                            </span>
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setAdditionalModalOpen(false);
                                        setSelectedAdditionalRowIds(new Set());
                                        setAdditionalBulkResponsibleId('');
                                        setActiveLevelPickerRowId(null);
                                        setActiveUnitPickerRowId(null);
                                    }}
                                    className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-xl transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSaveAdditionalTasksBatch}
                                    className="px-6 py-2.5 text-white bg-purple-600 hover:bg-purple-700 rounded-xl text-xs font-black shadow-lg shadow-purple-500/25 active:scale-95 transition-all"
                                >
                                    {editingAdditionalIndex !== null ? 'Guardar Cambios' : 'Crear Tareas'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: Añadir Responsable */}
            {isAddResponsibleModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-[#1a2634] rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-200 dark:border-slate-700 animate-in zoom-in-95 duration-200">
                        <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-[#1e2c3c]">
                            <div>
                                <h3 className="text-lg font-black text-slate-800 dark:text-white tracking-tight uppercase">Añadir Responsable</h3>
                            </div>
                            <button onClick={() => setIsAddResponsibleModalOpen(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        <div className="p-6 space-y-5">
                            <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded-r-xl">
                                <div className="flex gap-3">
                                    <span className="material-symbols-outlined text-blue-500">mark_email_read</span>
                                    <div className="text-xs text-blue-800 dark:text-blue-300 leading-relaxed">
                                        <strong>Mensaje Automático a enviar:</strong><br />
                                        "Hola,<br />
                                        Has sido invitado a registrarte y colaborar en la plataforma de control de gestión K-LyDapp.<br />
                                        Para darte de alta solo debes hacer clic en el botón a continuación e ingresar con tu credencial de Microsoft Office365."
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Nombre Completo</label>
                                <input
                                    type="text"
                                    value={newRespName}
                                    onChange={e => setNewRespName(e.target.value)}
                                    placeholder="Ej: Juan Pérez"
                                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 text-slate-850 dark:text-white"
                                />
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Correo (Microsoft 365)</label>
                                <input
                                    type="email"
                                    value={newRespEmail}
                                    onChange={e => setNewRespEmail(e.target.value)}
                                    placeholder="ejemplo@ldconstructora.cl"
                                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 text-slate-850 dark:text-white"
                                />
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Perfil</label>
                                <select
                                    value={newRespProfileId}
                                    onChange={e => setNewRespProfileId(e.target.value)}
                                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 appearance-none text-slate-850 dark:text-white"
                                >
                                    <option value="" className="text-slate-400">Selecciona un perfil...</option>
                                    {profiles.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                    {profiles.length === 0 && (
                                        <option value="" disabled>Debes crear perfiles primero</option>
                                    )}
                                </select>
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Empresa</label>
                                <select
                                    value={newRespCompanyName}
                                    onChange={e => setNewRespCompanyName(e.target.value)}
                                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 appearance-none text-slate-850 dark:text-white"
                                >
                                    <option value="" className="text-slate-400">Selecciona la empresa...</option>
                                    <option value={companyConfig?.name || 'Constructora LyD S.A.'}>
                                        🏢 {companyConfig?.name || 'Constructora LyD S.A.'} (Empresa Principal)
                                    </option>
                                    {subcontractors.filter(s => (s.status || 'Activo') === 'Activo').map(sub => (
                                        <option key={sub.id} value={sub.razonSocial}>
                                            🤝 {sub.razonSocial} {sub.rut ? `(${sub.rut})` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <button
                                onClick={handleAddResponsible}
                                disabled={!newRespEmail || !newRespName || !newRespProfileId || !newRespCompanyName || isInvitingResponsible}
                                className="w-full py-4 bg-[#1a73e8] text-white rounded-xl font-bold hover:bg-blue-600 transition-all shadow-md disabled:opacity-50 flex items-center justify-center gap-2 mt-2"
                            >
                                <span className="material-symbols-outlined">{isInvitingResponsible ? 'hourglass_empty' : 'send'}</span>
                                {isInvitingResponsible ? 'Enviando...' : 'Enviar Invitación'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: Lista de Responsables */}
            {isResponsiblesListModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-[#1a2634] rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-200 dark:border-slate-700 animate-in zoom-in-95 duration-200 flex flex-col max-h-[80vh]">
                        <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-[#1e2c3c] shrink-0">
                            <div>
                                <h3 className="text-lg font-black text-slate-800 dark:text-white tracking-tight uppercase">Lista de Responsables</h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Responsables asignados a la obra activa: {projectInfo.name}</p>
                            </div>
                            <button onClick={() => setIsResponsiblesListModalOpen(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto flex-1 bg-white dark:bg-[#1a2634]">
                            {responsibles.length === 0 ? (
                                <div className="text-center py-10 text-slate-400">
                                    <span className="material-symbols-outlined text-4xl block mb-2">group_off</span>
                                    No hay responsables asignados a este proyecto.
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse text-xs">
                                        <thead>
                                            <tr className="border-b border-slate-100 dark:border-slate-850">
                                                <th className="py-3 font-bold text-slate-450 uppercase tracking-wider">Nombre</th>
                                                <th className="py-3 font-bold text-slate-450 uppercase tracking-wider">Correo</th>
                                                <th className="py-3 font-bold text-slate-450 uppercase tracking-wider">Empresa</th>
                                                <th className="py-3 font-bold text-slate-450 uppercase tracking-wider">Perfil / Rol</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                            {sortedResponsibles.map(r => {
                                                const pName = profiles.find(p => p.id === r.profileId)?.name || r.role || 'Usuario';
                                                const userCompany = globalUsers.find(u => u.email.toLowerCase() === r.email.toLowerCase())?.companyName || companyConfig?.name || 'Constructora LyD S.A.';
                                                return (
                                                    <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                                                        <td className="py-3.5 font-semibold text-slate-800 dark:text-slate-200">{r.name}</td>
                                                        <td className="py-3.5 text-slate-650 dark:text-slate-400">{r.email}</td>
                                                        <td className="py-3.5 text-slate-650 dark:text-slate-400">
                                                            <span className="px-2 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-md text-[10px] font-bold inline-flex items-center gap-1">
                                                                <span className="material-symbols-outlined text-[12px]">domain</span>
                                                                {userCompany}
                                                            </span>
                                                        </td>
                                                        <td className="py-3.5 text-slate-650 dark:text-slate-400">
                                                            <span className="px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-full font-medium">
                                                                {pName}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                        <div className="p-4 bg-slate-50 dark:bg-slate-850/50 border-t border-slate-200 dark:border-slate-800 flex justify-end shrink-0">
                            <button
                                onClick={() => setIsResponsiblesListModalOpen(false)}
                                className="px-5 py-2 text-white bg-[#1a73e8] hover:bg-blue-600 rounded-xl text-xs font-bold shadow-md transition-all active:scale-95"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: Conciliación de Partidas (Split-screen) */}
            {reconciliationModalOpen && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-900/80 dark:bg-black/90 backdrop-blur-md animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-[#1a2634] rounded-3xl shadow-2xl w-full max-w-5xl h-[85vh] overflow-hidden border border-slate-200 dark:border-slate-800 flex flex-col animate-in zoom-in-95 duration-200">
                        {/* Header */}
                        <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-900/40">
                            <div>
                                <h3 className="text-lg font-black text-slate-800 dark:text-white uppercase tracking-tight flex items-center gap-2">
                                    <span className="material-symbols-outlined text-blue-500 text-[24px]">sync_alt</span>
                                    Conciliación y Re-mapeo de Partidas
                                </h3>
                                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                                    Vincule manualmente las partidas antiguas (que contienen datos) con sus equivalentes en el nuevo cronograma para conservar su historial.
                                </p>
                            </div>
                            <button
                                onClick={handleCancelReconciliation}
                                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                            >
                                <span className="material-symbols-outlined text-[24px]">close</span>
                            </button>
                        </div>

                        {/* Split-screen panels */}
                        <div className="flex-1 flex overflow-hidden">
                            {/* Left Panel: Old Unmatched Tasks */}
                            <div className="w-1/2 border-r border-slate-150 dark:border-slate-800 flex flex-col p-6 overflow-hidden">
                                <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-1.5">
                                    <span className="material-symbols-outlined text-[16px] text-amber-500">warning</span>
                                    Partidas Antiguas con Datos Históricos ({unmatchedOldTasks.filter(t => !reconciliationMappings[t.id]).length} sin vincular)
                                </h4>
                                <div className="flex-1 overflow-y-auto space-y-3 pr-2">
                                    {unmatchedOldTasks.map(task => {
                                        const isSelected = selectedOldTaskId === task.id;
                                        const mappedNewId = reconciliationMappings[task.id];
                                        const mappedNewTask = mappedNewId ? reconciliationNewTasks.find(nt => nt.id === mappedNewId) : null;

                                        // Count protocols and check weekly status
                                        const protCount = Object.values(savedProtocols).filter(p => p.taskId === task.id && p.status !== 'Inactivo').length;
                                        const hasPlan = weeklyPlans.some(wp => wp.allocations.some(alloc => alloc.taskId === task.id));

                                        return (
                                            <div
                                                key={task.id}
                                                onClick={() => {
                                                    if (!mappedNewId) {
                                                        setSelectedOldTaskId(isSelected ? null : task.id);
                                                    }
                                                }}
                                                className={`p-4 rounded-2xl border transition-all ${mappedNewId
                                                    ? 'bg-slate-50/50 dark:bg-slate-900/30 border-emerald-500/30 opacity-75'
                                                    : isSelected
                                                        ? 'bg-blue-500/5 dark:bg-blue-500/10 border-blue-500 shadow-sm ring-1 ring-blue-500'
                                                        : 'bg-white dark:bg-slate-850 hover:bg-slate-50 dark:hover:bg-slate-800/50 border-slate-200 dark:border-slate-800 cursor-pointer'
                                                    }`}
                                            >
                                                <div className="flex justify-between items-start gap-3">
                                                    <div className="flex-1 min-w-0">
                                                        <span className="text-xs font-black text-slate-800 dark:text-white block truncate">
                                                            {cleanTaskName(task.name)}
                                                        </span>
                                                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                                            {protCount > 0 && (
                                                                <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[9px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                                                    📜 {protCount} Protocolo(s)
                                                                </span>
                                                            )}
                                                            {hasPlan && (
                                                                <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[9px] font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                                                    📈 Avance Planif.
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {mappedNewId ? (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setReconciliationMappings(prev => {
                                                                    const updated = { ...prev };
                                                                    delete updated[task.id];
                                                                    return updated;
                                                                });
                                                            }}
                                                            className="p-1 hover:bg-red-50 dark:hover:bg-red-950/20 text-red-500 hover:text-red-700 rounded-lg transition-colors flex items-center justify-center"
                                                            title="Desvincular"
                                                        >
                                                            <span className="material-symbols-outlined text-[18px]">link_off</span>
                                                        </button>
                                                    ) : (
                                                        isSelected && (
                                                            <span className="material-symbols-outlined text-blue-500 text-[20px] shrink-0 animate-pulse">radio_button_checked</span>
                                                        )
                                                    )}
                                                </div>

                                                {mappedNewTask && (
                                                    <div className="mt-2.5 pt-2 border-t border-slate-100 dark:border-slate-800 text-[10px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1 font-bold">
                                                        <span className="material-symbols-outlined text-[14px]">link</span>
                                                        Vinculada a: {mappedNewTask.name}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Right Panel: New Gantt Schedule (Searchable list) */}
                            <div className="w-1/2 flex flex-col p-6 overflow-hidden bg-slate-50/50 dark:bg-slate-900/10">
                                <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                                    <span className="material-symbols-outlined text-[16px] text-blue-500">view_week</span>
                                    Nuevo Cronograma de Tareas ({reconciliationNewTasks.length} partidas)
                                </h4>

                                {/* Search bar */}
                                <div className="relative mb-4">
                                    <span className="material-symbols-outlined absolute left-3.5 top-2.5 text-[20px] text-slate-400">search</span>
                                    <input
                                        type="text"
                                        placeholder="Buscar partida equivalente en nuevo cronograma..."
                                        value={reconciliationSearch || ''}
                                        onChange={(e) => setReconciliationSearch(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 text-xs border border-slate-200 dark:border-slate-850 bg-white dark:bg-slate-800 rounded-xl outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 text-slate-800 dark:text-white"
                                    />
                                </div>

                                <div className="flex-1 overflow-y-auto space-y-2 pr-2">
                                    {reconciliationNewTasks
                                        .filter(t => {
                                            if (!reconciliationSearch) return true;
                                            return normalizeTaskName(t.name).includes(normalizeTaskName(reconciliationSearch));
                                        })
                                        .map(task => {
                                            const isSelected = selectedNewTaskId === task.id;
                                            const isAlreadyMapped = Object.values(reconciliationMappings).includes(task.id);
                                            const isOldSelected = !!selectedOldTaskId;

                                            return (
                                                <div
                                                    key={task.id}
                                                    onClick={() => {
                                                        if (!isAlreadyMapped && isOldSelected) {
                                                            setSelectedNewTaskId(isSelected ? null : task.id);
                                                        }
                                                    }}
                                                    className={`p-3 rounded-xl border text-xs transition-all ${isAlreadyMapped
                                                        ? 'bg-slate-100 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 text-slate-400 cursor-not-allowed'
                                                        : isSelected
                                                            ? 'bg-blue-500/5 dark:bg-blue-500/10 border-blue-500 ring-1 ring-blue-500 font-bold'
                                                            : isOldSelected
                                                                ? 'bg-white dark:bg-slate-850 hover:bg-slate-50 dark:hover:bg-slate-800/50 border-slate-200 dark:border-slate-805 cursor-pointer text-slate-800 dark:text-slate-200'
                                                                : 'bg-white dark:bg-slate-850 border-slate-150 dark:border-slate-800 opacity-60 text-slate-500 cursor-not-allowed'
                                                        }`}
                                                >
                                                    <div className="flex justify-between items-center gap-2">
                                                        <span className="truncate">{cleanTaskName(task.name)}</span>
                                                        {isAlreadyMapped ? (
                                                            <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-0.5 shrink-0">
                                                                <span className="material-symbols-outlined text-[14px]">check_circle</span>
                                                                Vinculada
                                                            </span>
                                                        ) : (
                                                            isSelected && (
                                                                <span className="material-symbols-outlined text-blue-500 text-[16px] shrink-0">radio_button_checked</span>
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </div>
                            </div>
                        </div>

                        {/* Mid Action bar to link selected */}
                        {selectedOldTaskId && selectedNewTaskId && (
                            <div className="px-6 py-3 bg-blue-500/5 dark:bg-blue-500/20 border-t border-b border-blue-500/20 flex justify-between items-center">
                                <span className="text-[11px] font-bold text-blue-500">
                                    ¿Vincular "{unmatchedOldTasks.find(ot => ot.id === selectedOldTaskId)?.name}" con "{reconciliationNewTasks.find(nt => nt.id === selectedNewTaskId)?.name}"?
                                </span>
                                <button
                                    onClick={() => {
                                        setReconciliationMappings(prev => ({
                                            ...prev,
                                            [selectedOldTaskId!]: selectedNewTaskId!
                                        }));
                                        setSelectedOldTaskId(null);
                                        setSelectedNewTaskId(null);
                                    }}
                                    className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-black flex items-center gap-1 shadow-md shadow-blue-500/25"
                                >
                                    <span className="material-symbols-outlined text-[16px]">link</span>
                                    Vincular Partidas
                                </button>
                            </div>
                        )}

                        {/* Footer Actions */}
                        <div className="p-6 bg-slate-50 dark:bg-slate-900/40 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center">
                            <span className="text-[11px] text-slate-500 dark:text-slate-400 font-bold">
                                {Object.keys(reconciliationMappings).length} partidas vinculadas manualmente.
                            </span>
                            <div className="flex gap-3">
                                <button
                                    onClick={handleCancelReconciliation}
                                    className="px-5 py-2.5 bg-slate-200 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-white rounded-xl font-bold transition-all text-xs"
                                >
                                    Cancelar Importación
                                </button>
                                <button
                                    onClick={handleConfirmReconciliation}
                                    className="px-6 py-2.5 bg-blue-600 hover:bg-blue-750 text-white rounded-xl font-black transition-all text-xs shadow-md shadow-blue-500/20"
                                >
                                    Confirmar y Proceder
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: Alerta de Partidas Faltantes en Importación */}
            {missingTasksModalOpen && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/70 dark:bg-black/85 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-[#1a2634] rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-200 dark:border-slate-700 animate-in zoom-in-95 duration-200">
                        <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3 bg-red-50 dark:bg-red-950/20 text-red-600 dark:text-red-400">
                            <span className="material-symbols-outlined text-[28px]">warning</span>
                            <h3 className="text-base font-black uppercase tracking-tight">Atención: Partidas Faltantes</h3>
                        </div>
                        <div className="p-6 space-y-4">
                            <p className="text-xs text-slate-600 dark:text-slate-350 leading-relaxed font-bold">
                                Atención: Las siguientes partidas no se encontraron en la nueva Carta Gantt:
                            </p>
                            <div className="bg-slate-50 dark:bg-slate-900/50 border border-slate-150 dark:border-slate-800 rounded-2xl p-4 max-h-[200px] overflow-y-auto space-y-2">
                                {missingTasksList.map(task => (
                                    <div key={task.id} className="text-[11px] text-slate-700 dark:text-slate-300 flex justify-between gap-4 font-mono">
                                        <span className="truncate">{cleanTaskName(task.name)}</span>
                                        <span className="text-slate-400 shrink-0">{task.key}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="text-xs text-red-600 dark:text-red-400 leading-relaxed font-bold mt-2 space-y-1">
                                <p>¿Desea continuar eliminando estas partidas?</p>
                                <p>⚠️ ADVERTENCIA: Si estas partidas contienen desgloses semanales o avances registrados en terreno, SE PERDERÁN PERMANENTEMENTE.</p>
                            </div>
                        </div>
                        <div className="p-6 bg-slate-50 dark:bg-slate-900/40 border-t border-slate-100 dark:border-slate-800 flex gap-3">
                            <button
                                onClick={() => {
                                    setPendingImportData(null);
                                    setMissingTasksList([]);
                                    setMissingTasksModalOpen(false);
                                }}
                                className="flex-1 py-3 bg-slate-200 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-white rounded-xl font-bold transition-all text-xs"
                            >
                                Cancelar Importación
                            </button>
                            <button
                                onClick={confirmMissingTasksImport}
                                className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold transition-all text-xs shadow-md shadow-red-500/10"
                            >
                                Confirmar y Eliminar Partidas
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Exit Warning Modal */}
            {showExitWarningModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 dark:bg-black/80 backdrop-blur-sm transition-all duration-300">
                    <div className="bg-white dark:bg-slate-900 rounded-3xl max-w-md w-full shadow-2xl border border-slate-100 dark:border-slate-800 overflow-hidden transform scale-100 animate-in fade-in zoom-in duration-200">
                        <div className="p-6 text-center space-y-4">
                            <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-amber-50 dark:bg-amber-950/30 text-amber-500 dark:text-amber-400">
                                <span className="material-symbols-outlined text-[32px]">warning</span>
                            </div>
                            <div className="space-y-2">
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white">¿Desea salir de la Planificación sin guardar?</h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                                    Tiene cambios sin guardar en esta planificación.
                                </p>
                            </div>
                            <div className="flex flex-col gap-2 pt-2">
                                <button
                                    onClick={handleExitWithoutSaving}
                                    className="w-full bg-red-50 hover:bg-red-100 dark:bg-red-950/20 dark:hover:bg-red-950/40 text-red-600 dark:text-red-400 font-bold py-2.5 px-4 rounded-xl text-xs uppercase flex items-center justify-center gap-1.5 transition-all"
                                >
                                    <span className="material-symbols-outlined text-[16px]">delete_forever</span>
                                    Salir sin Guardar
                                </button>
                                <button
                                    onClick={handleExitWithSaving}
                                    className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2.5 px-4 rounded-xl text-xs uppercase flex items-center justify-center gap-1.5 shadow-sm active:scale-95 transition-all"
                                >
                                    <span className="material-symbols-outlined text-[16px]">save</span>
                                    Guardar Planificación
                                </button>
                                <button
                                    onClick={() => {
                                        setShowExitWarningModal(false);
                                        setExitWarningTarget(null);
                                    }}
                                    className="w-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-bold py-2.5 px-4 rounded-xl text-xs uppercase flex items-center justify-center gap-1.5 shadow-sm active:scale-95 transition-all"
                                >
                                    <span className="material-symbols-outlined text-[16px]">edit</span>
                                    Seguir Editando
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Subcontractor Evaluation Modal Triggered from History */}
            {evalModalOpen && (
                <SubcontractorEvaluationModal
                    isOpen={evalModalOpen}
                    onClose={() => {
                        setEvalModalOpen(false);
                        setEvalModalPlanId(null);
                        setAdditionalsUpdateTrigger(prev => prev + 1);
                    }}
                    weeklyPlans={weeklyPlans}
                    subcontractors={subcontractors}
                    contracts={contracts}
                    tasks={tasks}
                    savedProtocols={savedProtocols}
                    lookaheadPlans={lookaheadPlans}
                    evaluationConfig={evaluationConfig}
                    initialPlanId={evalModalPlanId}
                    hasEditPermission={hasPermission('ps_editar', 'edit') || hasPermission('mod_subcontratos', 'edit')}
                    projectId={projectInfo?.name || 'default'}
                    globalUsers={globalUsers}
                    responsibles={responsibles}
                />
            )}

            {/* Attendance Sheet Modal Triggered from History */}
            {attendanceModalOpen && attendanceModalPlan && (
                <AttendanceModal
                    isOpen={attendanceModalOpen}
                    onClose={() => {
                        setAttendanceModalOpen(false);
                        setAttendanceModalPlan(null);
                        setAdditionalsUpdateTrigger(prev => prev + 1);
                    }}
                    plan={attendanceModalPlan}
                    projectInfo={projectInfo}
                    globalUsers={globalUsers}
                    responsibles={responsibles}
                    profiles={profiles}
                    companyConfig={companyConfig}
                    hasEditPermission={hasPermission('ps_editar', 'edit')}
                />
            )}

            {/* Meeting Minutes (Acta de Reunión) Modal Triggered from History */}
            {meetingMinutesModalOpen && meetingMinutesModalPlan && (
                <MeetingMinutesModal
                    isOpen={meetingMinutesModalOpen}
                    onClose={() => {
                        setMeetingMinutesModalOpen(false);
                        setMeetingMinutesModalPlan(null);
                        setAdditionalsUpdateTrigger(prev => prev + 1);
                    }}
                    plan={meetingMinutesModalPlan}
                    projectInfo={projectInfo}
                    globalUsers={globalUsers}
                    responsibles={responsibles}
                    profiles={profiles}
                    companyConfig={companyConfig}
                    hasEditPermission={hasPermission('ps_editar', 'edit')}
                />
            )}

            {/* Master Work Group Templates & Assignment Modal */}
            {isWorkGroupModalOpen && (
                <WorkGroupManagerModal
                    isOpen={isWorkGroupModalOpen}
                    onClose={() => setIsWorkGroupModalOpen(false)}
                    tasks={tasks}
                    units={units}
                    levels={levels}
                    sortedResponsibles={sortedResponsibles}
                    protocolConfigs={protocolConfigs}
                    setProtocolConfigs={setProtocolConfigs || (() => { })}
                    workGroupTemplates={workGroupTemplates}
                    setWorkGroupTemplates={setWorkGroupTemplates || (() => { })}
                />
            )}

            {/* Modal: Decisión de Protocolo al Marcar No Ejecutado */}
            {protocolReviewPromptOpen && protocolPromptTarget && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-150">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200">
                        {/* Header */}
                        <div className="bg-amber-50 dark:bg-amber-950/30 px-6 py-4 border-b border-amber-200/60 dark:border-amber-800/40 flex items-center gap-3">
                            <div className="size-10 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center flex-shrink-0">
                                <span className="material-symbols-outlined text-24px">warning</span>
                            </div>
                            <div className="min-w-0 flex-1">
                                <h3 className="text-base font-bold text-slate-800 dark:text-white leading-tight">
                                    Protocolo de Calidad Asociado
                                </h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                    {protocolPromptTarget.taskName} • {protocolPromptTarget.unitName}
                                </p>
                            </div>
                        </div>

                        {/* Body */}
                        <div className="p-6 space-y-4">
                            <p className="text-sm text-slate-700 dark:text-slate-300 font-medium leading-relaxed">
                                Esta tarea cuenta con un Protocolo de Calidad previamente enviado o en curso (<span className="font-bold text-amber-700 dark:text-amber-400">{protocolPromptTarget.protocolId}</span>). ¿Qué desea hacer?
                            </p>

                            {/* Opciones */}
                            <div className="space-y-3 pt-1">
                                {/* Opción 1: Rechazar Protocolo con Observación */}
                                <div className="p-3.5 rounded-xl border border-rose-200 dark:border-rose-900/50 bg-rose-50/50 dark:bg-rose-950/20 space-y-2.5">
                                    <div className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-rose-600 dark:text-rose-400 text-lg">cancel</span>
                                        <span className="text-xs font-bold uppercase tracking-wider text-rose-700 dark:text-rose-300">
                                            Opción 1: Rechazar Protocolo con Observación
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-600 dark:text-slate-400">
                                        El protocolo regresará al revisor anterior (o supervisor) con estado <span className="font-bold text-rose-600 dark:text-rose-400">Rechazado</span> y la observación ingresada.
                                    </p>
                                    <textarea
                                        value={rejectionObservation}
                                        onChange={(e) => setRejectionObservation(e.target.value)}
                                        placeholder="Ingrese el motivo u observación de rechazo (obligatorio para rechazar)..."
                                        rows={2}
                                        className="w-full text-xs p-2.5 rounded-lg border border-rose-300 dark:border-rose-800 bg-white dark:bg-slate-800 text-slate-800 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-rose-500 resize-none"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleConfirmRejectProtocol}
                                        disabled={!rejectionObservation.trim()}
                                        className="w-full py-2 px-3 text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg shadow-sm transition-all flex items-center justify-center gap-1.5"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">assignment_late</span>
                                        Rechazar Protocolo con esta Observación
                                    </button>
                                </div>

                                {/* Opción 2: Solo ajustar avance de producción */}
                                <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 space-y-2">
                                    <div className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-slate-500 text-lg">tune</span>
                                        <span className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                                            Opción 2: Solo ajustar avance de producción
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-600 dark:text-slate-400">
                                        Modifica únicamente el avance en la planificación semanal sin alterar el estado ni las firmas del protocolo de calidad existente.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={handleConfirmAdjustProductionOnly}
                                        className="w-full py-2 px-3 text-xs font-bold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-700 hover:bg-slate-100 dark:hover:bg-slate-600 border border-slate-300 dark:border-slate-600 rounded-lg shadow-sm transition-all flex items-center justify-center gap-1.5"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">check_circle</span>
                                        Solo Ajustar Avance de Producción
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-800 flex justify-end">
                            <button
                                type="button"
                                onClick={() => {
                                    setProtocolReviewPromptOpen(false);
                                    setProtocolPromptTarget(null);
                                    setRejectionObservation('');
                                }}
                                className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-all"
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Presentation Editor Fullscreen Overlay */}
            {showPresentationEditor && (
                <PresentationEditor
                    projectInfo={projectInfo}
                    mode={presentationEditorMode}
                    onClose={() => {
                        setShowPresentationEditor(false);
                        setPresentationTargetPlan(null);
                        setPresentationInitialTemplate(undefined);
                        setPresentationEditorMode('config');
                    }}
                    initialTemplate={presentationInitialTemplate}
                    currentUserName={currentUser?.name || currentUser?.email}
                    weeklyPlanData={{
                        weeklyPlans,
                        tasks,
                        units,
                        levels,
                        responsibles,
                        cncGroups,
                        savedProtocols,
                        protocolConfigs,
                        activePlanId: presentationTargetPlan ? presentationTargetPlan.id : activePlanId,
                        matrixData,
                        projectInfo,
                        subcontractors,
                        contracts,
                        baselines,
                        progressHistory,
                        evaluationConfig,
                        lookaheadPlans,
                        globalUsers
                    }}
                />
            )}

            {/* Presentation List Modal */}
            <PresentationListModal
                isOpen={showPresentationListModal}
                onClose={() => setShowPresentationListModal(false)}
                projectInfo={projectInfo}
                onViewPresentation={(tmpl) => {
                    setPresentationTargetPlan(null);
                    setPresentationInitialTemplate(tmpl);
                    setPresentationEditorMode('presentation');
                    setShowPresentationEditor(true);
                }}
                onEditPresentation={(tmpl) => {
                    setPresentationTargetPlan(null);
                    setPresentationInitialTemplate(tmpl);
                    setPresentationEditorMode('config');
                    setShowPresentationEditor(true);
                }}
            />

            {/* Mobile Filter Modal */}
            {isMobileFilterOpen && (
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-t-2xl sm:rounded-2xl max-w-lg w-full max-h-[85vh] flex flex-col shadow-2xl border border-slate-200 dark:border-slate-800 animate-in slide-in-from-bottom duration-200">
                        {/* Header */}
                        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="material-symbols-outlined text-blue-600 dark:text-blue-400">filter_list</span>
                                <h3 className="font-bold text-base text-slate-800 dark:text-white">Filtros de Tareas</h3>
                            </div>
                            <button
                                onClick={() => setIsMobileFilterOpen(false)}
                                className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                            >
                                <span className="material-symbols-outlined text-[20px]">close</span>
                            </button>
                        </div>

                        {/* Body */}
                        <div className="p-4 overflow-y-auto space-y-4 flex-1">
                            {/* Buscar por Tarea */}
                            <div>
                                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">
                                    Buscar Tarea
                                </label>
                                <div className="relative">
                                    <span className="material-symbols-outlined absolute left-3 top-2.5 text-slate-400 text-[18px]">search</span>
                                    <input
                                        type="text"
                                        placeholder="Nombre de la tarea..."
                                        value={mobileFilters.taskSearch || ''}
                                        onChange={(e) => setMobileFilters(prev => ({ ...prev, taskSearch: e.target.value }))}
                                        className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                                    />
                                </div>
                            </div>

                            {/* Nivel */}
                            <div>
                                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">
                                    Nivel
                                </label>
                                <select
                                    value={mobileFilters.levelId || ''}
                                    onChange={(e) => {
                                        const newLevelId = e.target.value;
                                        setMobileFilters(prev => ({
                                            ...prev,
                                            levelId: newLevelId,
                                            unitId: prev.unitId && newLevelId && units.find(u => u.id === prev.unitId)?.levelId !== newLevelId ? '' : prev.unitId
                                        }));
                                    }}
                                    className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                                >
                                    <option value="">Todos los niveles</option>
                                    {levels.map(lvl => (
                                        <option key={lvl.id} value={lvl.id}>{lvl.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Unidad Física */}
                            <div>
                                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">
                                    Unidad Física
                                </label>
                                <select
                                    value={mobileFilters.unitId || ''}
                                    onChange={(e) => setMobileFilters(prev => ({ ...prev, unitId: e.target.value }))}
                                    className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                                >
                                    <option value="">Todas las unidades</option>
                                    {units
                                        .filter(u => !mobileFilters.levelId || u.levelId === mobileFilters.levelId)
                                        .map(u => (
                                            <option key={u.id} value={u.id}>{u.name}</option>
                                        ))}
                                </select>
                            </div>

                            {/* Responsable */}
                            <div>
                                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">
                                    Responsable
                                </label>
                                <select
                                    value={mobileFilters.responsibleId || ''}
                                    onChange={(e) => setMobileFilters(prev => ({ ...prev, responsibleId: e.target.value }))}
                                    className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                                >
                                    <option value="">Todos los responsables</option>
                                    {responsibles.map(r => (
                                        <option key={r.id} value={r.id}>{r.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Estado */}
                            <div>
                                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">
                                    Estado de Tarea
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    {[
                                        { key: 'all', label: 'Todos' },
                                        { key: 'executed', label: 'Cumplida' },
                                        { key: 'pending', label: 'Pendiente' },
                                        { key: 'protocol', label: 'Con Protocolo' }
                                    ].map(st => (
                                        <button
                                            key={st.key}
                                            type="button"
                                            onClick={() => setMobileFilters(prev => ({ ...prev, status: st.key as any }))}
                                            className={`py-2 px-3 text-xs font-bold rounded-lg border transition-colors cursor-pointer ${(mobileFilters.status || 'all') === st.key
                                                    ? 'bg-primary text-white border-primary shadow-sm'
                                                    : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-100'
                                                }`}
                                        >
                                            {st.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between gap-3 bg-slate-50/50 dark:bg-slate-800/50">
                            <button
                                type="button"
                                onClick={() => {
                                    setMobileFilters({
                                        levelId: '',
                                        unitId: '',
                                        responsibleId: '',
                                        taskSearch: '',
                                        status: 'all'
                                    });
                                }}
                                className="px-3 py-2 text-xs font-bold text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white flex items-center gap-1 transition-colors cursor-pointer"
                            >
                                <span className="material-symbols-outlined text-[16px]">restart_alt</span>
                                Restablecer
                            </button>
                            <button
                                type="button"
                                onClick={() => setIsMobileFilterOpen(false)}
                                className="px-5 py-2 text-xs font-bold bg-primary text-white rounded-lg shadow hover:bg-primary-dark transition-colors cursor-pointer"
                            >
                                Aplicar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Protocolo Móvil KlFullModal */}
            {mobileProtocolTarget && (
                <MobileProtocolModal
                    open={!!mobileProtocolTarget}
                    onClose={() => setMobileProtocolTarget(null)}
                    taskId={mobileProtocolTarget.taskId}
                    unitId={mobileProtocolTarget.unitId}
                    taskName={mobileProtocolTarget.taskName}
                    taskCode={mobileProtocolTarget.taskCode}
                    tasks={tasks}
                    units={units}
                    levels={levels}
                    protocolConfigs={protocolConfigs}
                    savedProtocols={savedProtocols}
                    setSavedProtocols={setSavedProtocols}
                    currentUser={currentUser}
                    hasPermission={hasPermission}
                    responsibles={responsibles}
                    addActivity={addActivity}
                    collectionName={projectInfo?.id ? ('project_' + projectInfo.id) : 'data'}
                    matrixData={matrixData}
                />
            )}

        </div>
    );
};

export default PlanningScreen;